import React, { useState, useEffect, useRef, useCallback, useMemo, useImperativeHandle, forwardRef } from 'react';
import ReactDOM from 'react-dom';
import http, { resetHttpAuth, invalidateCache, getCached, prefetch } from './api/http';
import { toast, Toaster as SonnerToaster } from 'sonner';
import { initAuthSync, broadcastLogout, forceLogout, clearAuthStorage, recordUserActivity, isSessionExpired, SESSION_IDLE_TIMEOUT_MS } from './auth/authSync';
import { supabase } from './lib/supabase';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';
import MFASetup from './components/security/MFASetup';
import MFAVerifyModal from './components/security/MFAVerifyModal';
import ErrorBoundary from './components/ErrorBoundary';
import PlanningManagement from './PlanningComponent';
import MissionComments from './components/MissionComments';
import WorksitePhotos from './components/WorksitePhotos';
import { groupTechnicianMissions, fetchPlanningSchedules } from './lib/planningSchedules';
import InventoryScanner from './InventoryScanner';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import useProgressiveList from './hooks/useProgressiveList';
import './App.css';
// Import shadcn components
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from './components/ui/select';
import { Badge } from './components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './components/ui/dialog';
import { useConfirm } from './components/ui/confirm-dialog';
import { validatePasswordStrength, PASSWORD_MIN_LENGTH } from './lib/passwordPolicy';
import PasswordStrengthMeter from './components/security/PasswordStrengthMeter';
// Import AI Orb - Premier Logiciel BTP Intelligent en France
import AIOrb from './components/AIOrb/AIOrb';
import { Annotator } from './components/ImageAnnotator';
import ChangelogPage from './pages/ChangelogPage';
import LegalPage from './pages/LegalPage';

import {
  AlertCircle,
  AlertTriangle,
  Archive,
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Briefcase,
  Building,
  Building2,
  Calendar,
  Camera,
  Calculator,
  Check,
  CheckCheck,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  Clock,
  DollarSign,
  Download,
  Edit,
  Edit2,
  Eye,
  FileBarChart,
  FileText,
  Filter,
  FolderOpen,
  Globe,
  Grid,
  GripVertical,
  Headphones,
  History,
  Image as ImageIcon,
  Info,
  Kanban,
  Layers,
  Lightbulb,
  List,
  Loader2,
  Lock,
  LogOut,
  Mail,
  Map,
  MapPin,
  MapPin as MapPinIcon,
  Menu,
  MoreVertical,
  MoreHorizontal,
  CreditCard,
  RefreshCw,
  Navigation,
  Phone,
  Play,
  Plus,
  Package,
  Save,
  Search,
  Settings,
  Share2,
  Shield,
  Sparkles,
  Star,
  Target,
  Timer,
  Trash2,
  TrendingUp,
  Upload,
  User,
  UserPlus,
  Users,
  Wifi,
  Wrench,
  X,
  XCircle
} from 'lucide-react';

// Backend URL and API base with safe fallbacks
// En dev/LAN: utilise le hostname du navigateur pour que ça marche depuis n'importe quel PC du réseau
// En production: utilise la variable d'environnement (URL Render)
const BACKEND_URL = (() => {
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  // Si c'est une URL de production (https), l'utiliser telle quelle
  if (envUrl.startsWith('https://')) return envUrl;
  // Sinon (dev local), utiliser le hostname du navigateur pour supporter le LAN
  const host = (typeof window !== 'undefined') ? window.location.hostname : 'localhost';
  return `http://${host}:8001`;
})();
const API = `${BACKEND_URL}/api`;
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://wursductnatclwrqvgua.supabase.co';

// ⚡ Performance tracker — mesure page structure + data load times
const PERF_ENABLED = true;
const _perfTimers = {};
const perf = {
  start: (label) => { if (PERF_ENABLED) _perfTimers[label] = performance.now(); },
  end: (label, threshold = 1000) => {
    if (!PERF_ENABLED || !_perfTimers[label]) return;
    const ms = Math.round(performance.now() - _perfTimers[label]);
    const icon = ms > threshold ? '⚠️' : ms > 500 ? '⏱' : '⚡';
    console.log(`${icon} [PERF] ${label}: ${ms}ms`);
    delete _perfTimers[label];
    return ms;
  }
};

const FOUNDER_EMAIL = (process.env.REACT_APP_FOUNDER_EMAIL || 'contact@skyapp.fr').toLowerCase();

// Debug mode (désactivé en production)
const DEBUG_MODE = process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG === 'true';

// Fonction pour nettoyer le formatage Markdown des observations
const cleanObservations = (text) => {
  if (!text) return '';
  
  // Si c'est du JSON avec customSections, extraire uniquement le texte
  try {
    const data = JSON.parse(text);
    if (data && typeof data === 'object' && 'text' in data) {
      return data.text || '';
    }
  } catch (e) {
    // Pas du JSON, continuer avec le nettoyage classique
  }
  
  // Enlever les **text:** et garder seulement le contenu après
  return text
    .replace(/\*\*[^:]+:\*\*/g, '') // Enlever **titre:**
    .replace(/\*\*[^*]+\*\*/g, '') // Enlever **texte**
    .replace(/\s+/g, ' ') // Remplacer multiples espaces par un seul
    .trim();
};

// ─── Restauration session Supabase au démarrage ───────────────
// Le client Supabase (persistSession: true, autoRefreshToken: true) restaure
// automatiquement sa session depuis sb-*-auth-token.
// On synchronise aussi depuis nos clés localStorage (token/refresh_token)
// au cas où le sb-*-auth-token aurait été vidé.
//
// Règle métier SkyApp : un refresh token Supabase valide ne suffit PAS à
// restaurer la session si l'inactivité SkyApp (skyapp:lastActivity) dépasse
// SESSION_IDLE_TIMEOUT_MS — sinon fermer Chrome puis le rouvrir après 10 min
// contournerait le timeout (le refresh token survit, lui, indéfiniment).
(async () => {
  const token = localStorage.getItem('token');
  const refreshToken = localStorage.getItem('refresh_token');
  if (!token || !refreshToken) return;

  let expired = false;
  try {
    const savedUser = localStorage.getItem('user');
    const userData = savedUser ? JSON.parse(savedUser) : null;
    expired = userData ? isSessionExpired(userData.id) : false;
  } catch {
    // Parsing impossible : on laisse la restauration se faire normalement
  }

  if (expired) {
    clearAuthStorage();
    try { await supabase.auth.signOut(); } catch { /* déjà invalide, ignorer */ }
    return;
  }

  try {
    await supabase.auth.setSession({ access_token: token, refresh_token: refreshToken });
  } catch (e) {
    console.warn('Session Supabase non restaurée:', e);
  }
})();

// ─── Sync Supabase → localStorage (TOKEN_REFRESHED) ──────────
// Quand le client Supabase auto-refresh les tokens, on met à jour
// localStorage.token et localStorage.refresh_token pour que l'intercepteur
// Axios ait TOUJOURS le token courant.
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'TOKEN_REFRESHED' && session) {
    localStorage.setItem('token', session.access_token);
    if (session.refresh_token) {
      localStorage.setItem('refresh_token', session.refresh_token);
    }
    if (process.env.NODE_ENV === 'development') {
      console.log('[authSync] Supabase TOKEN_REFRESHED → localStorage mis à jour');
    }
  }
  if (event === 'SIGNED_OUT') {
    // Le client Supabase a signed out (timeout, invalidation côté serveur)
    // → nettoyer nos clés et forcer le logout React
    clearAuthStorage();
    forceLogout('supabase_signed_out');
  }
});

// Les interceptors axios sont centralisés dans src/api/http.js
// Plus besoin de configurer axios ici — utiliser http.get/post/... partout

// Auth Context
const AuthContext = React.createContext(null);

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const useFounderOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOverview = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await http.get(`${API}/founder/overview`);
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error('Erreur chargement vue fondateur:', err);
      const status = err?.response?.status;
      if (status === 403) {
        setData(null);
        setError(null);
      } else {
        setError('Impossible de charger la vue fondateur');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  return { data, loading, error, refresh: fetchOverview };
};

// Real-time Statistics Hook (avec filtre entreprise pour fondateur)
const useRealTimeStats = (companyFilter = null, enabled = true) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);

  const fallback = React.useMemo(() => ({
    total_users: 0,
    total_companies: 0,
    total_searches: 0,
    total_reports: 0,
    active_sessions: 0,
    server_uptime: 'N/A'
  }), []);

  const fetchStats = React.useCallback(async () => {
    // Si le hook est désactivé, ne rien faire
    if (!enabled) {
      setStats(fallback);
      setLoading(false);
      setError(null);
      return;
    }
    
    // Vérifier si l'utilisateur est connecté
    const token = localStorage.getItem('token');
    if (!token) {
      setStats(fallback);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    try {
      const url = companyFilter ? `${API}/founder/overview?company=${companyFilter}` : `${API}/founder/overview`;
      const response = await http.get(url);
      setStats(response.data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      const status = err?.response?.status;
      setStats(prev => prev ?? fallback);
      
      if (status === 403 || status === 401) {
        // Accès refusé ou non autorisé - normal si pas fondateur ou pas connecté
        setError(null);
      } else {
        // Autres erreurs (réseau, serveur, etc.)
        console.error('Erreur chargement statistiques:', err);
        setError('Impossible de charger les statistiques');
      }
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  }, [companyFilter, fallback]);

  useEffect(() => {
    if (!enabled) return;
    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [fetchStats, enabled]);

  return { stats, loading, error, lastUpdated, refresh: fetchStats };
};

// User Profile Modal Component (Editable)
const UserProfileModal = ({ user, isOpen, onClose, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    company_name: ''
  });

  useEffect(() => {
    if (user && isOpen) {
      http.get(`${API}/users/me`)
      .then(response => {
        setProfileData({
          first_name: response.data.first_name || '',
          last_name: response.data.last_name || '',
          phone: response.data.phone || '',
          address: response.data.address || '',
          company_name: response.data.company_name || ''
        });
      })
      .catch(err => console.error('Erreur chargement profil:', err));
    }
  }, [user, isOpen]);

  const handleSave = async () => {
    setLoading(true);
    try {
      let dataToSend = Object.fromEntries(
        Object.entries(profileData).filter(([_, value]) => value && value.trim() !== '')
      );
      if (user?.role !== 'ADMIN' && 'company_name' in dataToSend) {
        const { company_name, ...rest } = dataToSend;
        dataToSend = rest;
      }
      await http.put(`${API}/users/me`, dataToSend);
      const updatedProfile = await http.get(`${API}/users/me`);
      localStorage.setItem('user', JSON.stringify(updatedProfile.data));
      if (onUpdate) onUpdate(updatedProfile.data);
      setIsEditing(false);
      toast.success('Profil mis à jour !');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du profil');
    } finally {
      setLoading(false);
    }
  };

  const initials = (() => {
    const f = profileData.first_name || user?.first_name || '';
    const l = profileData.last_name || user?.last_name || '';
    return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U';
  })();

  const roleLabel = user?.role?.toUpperCase();
  const roleColors = {
    ADMIN: 'bg-red-100 text-red-700',
    BUREAU: 'bg-blue-100 text-blue-700',
    TECHNICIEN: 'bg-green-100 text-green-700',
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl p-0 overflow-hidden max-h-[92vh] flex flex-col">
        {/* Titre caché pour accessibilité (exigé par Radix UI) */}
        <DialogTitle className="sr-only">Profil Utilisateur</DialogTitle>
        <DialogDescription className="sr-only">Gérez vos informations personnelles et la sécurité de votre compte</DialogDescription>

        {/* Header avec dégradé — fixe */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 pt-6 pb-10 shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center border-2 border-white/40">
                <span className="text-white font-bold text-2xl">{initials}</span>
              </div>
              <div>
                <h2 className="text-white font-bold text-xl">
                  {profileData.first_name || user?.first_name || ''} {profileData.last_name || user?.last_name || ''}
                </h2>
                <p className="text-blue-100 text-sm">{user?.email}</p>
                <div className="flex gap-2 mt-1">
                  {user?.is_fondateur && (
                    <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-yellow-400 text-yellow-900">FONDATEUR</span>
                  )}
                  {roleLabel && (
                    <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-white/20 text-white">{roleLabel}</span>
                  )}
                </div>
              </div>
            </div>
            {!isEditing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="text-white hover:bg-white/20"
              >
                <Edit className="h-4 w-4 mr-1" />Modifier
              </Button>
            )}
          </div>
        </div>

        {/* Corps — scrollable */}
        <div className="-mt-6 mx-4 mb-4 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-y-auto flex-1">
          {/* Section Informations */}
          <div className="p-5 space-y-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Informations personnelles</p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500">Prénom</label>
                {isEditing ? (
                  <Input value={profileData.first_name} onChange={(e) => setProfileData({...profileData, first_name: e.target.value})} placeholder="Prénom" className="mt-1 h-8" />
                ) : (
                  <p className="font-medium text-gray-900">{profileData.first_name || <span className="text-gray-400 italic text-sm">Non renseigné</span>}</p>
                )}
              </div>
              <div>
                <label className="text-xs text-gray-500">Nom</label>
                {isEditing ? (
                  <Input value={profileData.last_name} onChange={(e) => setProfileData({...profileData, last_name: e.target.value})} placeholder="Nom" className="mt-1 h-8" />
                ) : (
                  <p className="font-medium text-gray-900">{profileData.last_name || <span className="text-gray-400 italic text-sm">Non renseigné</span>}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <Mail className="w-4 h-4 text-gray-400 shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="font-medium text-gray-900 text-sm">{user?.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <Phone className="w-4 h-4 text-gray-400 shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">Téléphone</p>
                {isEditing ? (
                  <Input value={profileData.phone} onChange={(e) => setProfileData({...profileData, phone: e.target.value})} placeholder="Numéro" className="mt-1 h-8" />
                ) : (
                  <p className="font-medium text-gray-900 text-sm">{profileData.phone || <span className="text-gray-400 italic">Non renseigné</span>}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-gray-500">Adresse</p>
                {isEditing ? (
                  <Input value={profileData.address} onChange={(e) => setProfileData({...profileData, address: e.target.value})} placeholder="Adresse" className="mt-1 h-8" />
                ) : (
                  <p className="font-medium text-gray-900 text-sm">{profileData.address || <span className="text-gray-400 italic">Non renseigné</span>}</p>
                )}
              </div>
            </div>

            {(user?.role === 'ADMIN' || profileData.company_name) && (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Entreprise</p>
                  {isEditing && user?.role === 'ADMIN' ? (
                    <Input value={profileData.company_name} onChange={(e) => setProfileData({...profileData, company_name: e.target.value})} placeholder="Entreprise" className="mt-1 h-8" />
                  ) : (
                    <p className="font-medium text-gray-900 text-sm">{profileData.company_name || user?.entreprise || <span className="text-gray-400 italic">Non renseigné</span>}</p>
                  )}
                </div>
              </div>
            )}

            {isEditing && (
              <div className="flex gap-2 pt-2">
                <Button onClick={handleSave} disabled={loading} className="flex-1">
                  {loading ? 'Sauvegarde...' : 'Sauvegarder'}
                </Button>
                <Button variant="outline" onClick={() => setIsEditing(false)} className="flex-1">Annuler</Button>
              </div>
            )}
          </div>

          {/* Séparateur + infos compte (ID complet) */}
          <div className="border-t border-gray-100 px-5 py-4 bg-gray-50/50">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Compte</p>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-gray-500 mb-1">ID Utilisateur</p>
                <p className="font-mono text-xs text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2 break-all select-all">
                  {user?.id || 'N/A'}
                </p>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">Licence</span>
                <span className="text-xs font-medium text-gray-700">{user?.licence || 'Non configurée'}</span>
              </div>
            </div>
          </div>

          {/* Section MFA */}
          {!isEditing && (
            <div className="border-t border-gray-100 px-5 py-4">
              <MFASetup />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Real-time Stats Display Component
const RealTimeStatsDisplay = ({ stats, className = "", showIndicator = true }) => {
  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <span>{stats}</span>
      {showIndicator && (
        <div className="flex items-center space-x-1">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-xs text-gray-500">temps réel</span>
        </div>
      )}
    </div>
  );
};

// Animated Background Component
const CosmicBackground = () => {
  return (
    <>
      {/* Main cosmic background */}
      <div className="cosmic-background"></div>
      
      {/* Particle system */}
      <div className="particle-system">
        {[...Array(20)].map((_, i) => (
          <div 
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${8 + Math.random() * 4}s`
            }}
          />
        ))}
      </div>
      
      {/* Morphing blobs */}
      <div className="fixed inset-0 pointer-events-none z-[-1] overflow-hidden">
        <div className="morphing-blob" style={{ top: '10%', left: '10%' }}></div>
        <div className="morphing-blob" style={{ top: '60%', right: '10%' }}></div>
        <div className="morphing-blob" style={{ bottom: '20%', left: '20%' }}></div>
      </div>
    </>
  );
};

// Page dédiée Fondateur (route /fondateur)
const FounderDashboard = () => {
  const { data, loading, error } = useFounderOverview();
  const navigate = useNavigate();

  // Si pas fondateur (hook ne renvoie rien) => écran d'accès refusé
  if (!loading && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <nav className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/role-selection')} className="p-2 rounded-lg hover:bg-gray-100">
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">Accès Fondateur</h1>
            </div>
          </div>
        </nav>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <Shield className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Accès refusé</h2>
            <p className="text-gray-600 mb-4">Cette zone est réservée au fondateur de la plateforme.</p>
            <Button onClick={() => navigate('/role-selection')}>Retour</Button>
          </div>
        </div>
      </div>
    );
  }

  const totals = data?.totals || {};
  const last7 = data?.last_7d || {};
  const ratio = (a, b) => (b ? (a / b * 100).toFixed(1) + '%' : '—');
  const metrics = [
    { label: 'Utilisateurs', value: totals.users },
    { label: 'Entreprises', value: totals.companies },
    { label: 'Recherches', value: totals.searches },
    { label: 'Clients', value: totals.clients },
    { label: 'Devis', value: totals.quotes },
    { label: 'Chantiers', value: totals.worksites },
    { label: 'Matériels', value: totals.materials },
    { label: 'Abonnements', value: totals.subscriptions }
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/role-selection')} className="p-2 rounded-lg hover:bg-gray-100">
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              Vue Fondateur <Badge className="bg-indigo-600 text-white">Global</Badge>
            </h1>
          </div>
          {data?.generated_at && (
            <div className="text-xs text-gray-500">Maj: {new Date(data.generated_at).toLocaleTimeString()}</div>
          )}
        </div>
      </nav>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
          {/* Etat chargement / erreur */}
          {loading && (
            <div className="p-4 bg-white border rounded-xl text-sm text-gray-600">Chargement des métriques…</div>
          )}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{error}</div>
          )}

          {/* Grille Totaux */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
            {metrics.map(m => (
              <div key={m.label} className="p-3 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="text-xs uppercase tracking-wide text-gray-500">{m.label}</div>
                <div className="text-xl font-semibold text-gray-900">{m.value}</div>
              </div>
            ))}
          </div>

            {/* Derniers 7 jours */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2"><History className="h-5 w-5" /> Derniers 7 jours</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
              {[
                { label: 'Utilisateurs (7j)', value: last7.users },
                { label: 'Recherches (7j)', value: last7.searches },
                { label: 'Clients (7j)', value: last7.clients },
                { label: 'Devis (7j)', value: last7.quotes }
              ].map(x => (
                <div key={x.label} className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                  <div className="text-xs text-indigo-700">{x.label}</div>
                  <div className="text-lg font-semibold text-indigo-900">{x.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Ratios simples */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2"><Target className="h-5 w-5" /> Ratios / Conversion</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-white rounded-xl border border-gray-200">
                <div className="text-xs text-gray-500">Clients / Recherches</div>
                <div className="text-2xl font-semibold text-gray-900">{ratio(totals.clients, totals.searches)}</div>
              </div>
              <div className="p-4 bg-white rounded-xl border border-gray-200">
                <div className="text-xs text-gray-500">Devis / Clients</div>
                <div className="text-2xl font-semibold text-gray-900">{ratio(totals.quotes, totals.clients)}</div>
              </div>
              <div className="p-4 bg-white rounded-xl border border-gray-200">
                <div className="text-xs text-gray-500">Chantiers / Devis</div>
                <div className="text-2xl font-semibold text-gray-900">{ratio(totals.worksites, totals.quotes)}</div>
              </div>
              <div className="p-4 bg-white rounded-xl border border-gray-200">
                <div className="text-xs text-gray-500">Abonnements / Entreprises</div>
                <div className="text-2xl font-semibold text-gray-900">{ratio(totals.subscriptions, totals.companies)}</div>
              </div>
            </div>
          </div>

          <div className="text-xs text-gray-500">Cache backend 60s - actualisation automatique.</div>
        </div>
      </div>
    </div>
  );
};


/* ──────────────────────────────────────────────────────────────
   Hero : carousel de captures produit avec pile en profondeur
   ────────────────────────────────────────────────────────────── */
const HERO_SCREENSHOTS = [
  { src: '/Dashbord%20stat.png',                label: 'Tableau de bord' },
  { src: '/Gestion%20planing.png',              label: 'Planning équipe' },
  { src: '/Menu%20bureau.png',                  label: 'Gestion bureau' },
  { src: '/Centre%20gestion%20commercial.png',  label: 'Gestion commerciale' },
  { src: '/Menu%20tech.png',                    label: 'Interface terrain' }
];

const HeroProductCarousel = ({ index: controlledIndex, onIndexChange, autoPlay = false }) => {
  const total = HERO_SCREENSHOTS.length;
  const [internalIndex, setInternalIndex] = useState(0);
  const isControlled = controlledIndex != null;
  const index = isControlled ? controlledIndex : internalIndex;
  const setIndex = (next) => {
    const value = typeof next === 'function' ? next(index) : next;
    if (!isControlled) setInternalIndex(value);
    if (onIndexChange) onIndexChange(value);
  };
  const mod = (n, m) => ((n % m) + m) % m;

  // Défilement automatique (doux et lent)
  const [paused, setPaused] = useState(false);
  const [imgHover, setImgHover] = useState(false);
  // Sur mobile/tablette, on réduit l'éventail 3D pour éviter tout débordement
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const check = () => setCompact(window.innerWidth < 1280);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  useEffect(() => {
    if (!autoPlay || paused) return;
    const t = setTimeout(() => setIndex(mod(index + 1, total)), 3500);
    return () => clearTimeout(t);
  }, [autoPlay, paused, index, total]);

  // On affiche TOUTES les vues en éventail 3D vers la droite.
  // L'image active est devant, les suivantes reculent en perspective.
  const cards = HERO_SCREENSHOTS.map((s, i) => {
    const depth = mod(i - index, total); // 0 = active, 1, 2, 3, …
    return { ...s, originalIndex: i, depth };
  });

  return (
    <div
      className="relative flex flex-col items-center lg:items-start select-none"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Éventail 3D : cartes décalées vers la droite, en perspective */}
      <div
        className="relative w-full max-w-sm sm:max-w-md lg:max-w-xl mx-auto"
        style={{
          aspectRatio: '16 / 10',
          perspective: '1800px',
          perspectiveOrigin: '20% 50%',
          overflow: 'visible'
        }}
      >
        {/* Halo doux derrière le screen (profondeur premium) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -inset-8"
          style={{
            zIndex: 0,
            background:
              'radial-gradient(ellipse at 45% 45%, rgba(37, 99, 235, 0.16), transparent 60%),' +
              'radial-gradient(ellipse at 62% 60%, rgba(124, 58, 237, 0.14), transparent 65%)',
            filter: 'blur(38px)'
          }}
        />
        {cards.map((c) => {
          const isTop = c.depth === 0;
          // Décalages progressifs en perspective vers la droite
          // (réduits en mode compact pour rester dans le bloc sur tablette/mobile)
          const offsetX = c.depth * (compact ? 7 : 11);  // % de la largeur (décalage horizontal)
          const offsetY = c.depth * 2.4;         // % léger décalage vertical
          const baseScale = Math.max(0.55, 1 - c.depth * 0.07);
          const scale = isTop ? 1 : baseScale;
          const rotateY = c.depth === 0 ? 0 : (compact ? -14 : -22);   // tilt 3D (cartes "fuient" vers la droite)
          const opacity = c.depth === 0 ? 1 : Math.max(0.25, 0.55 - c.depth * 0.12);
          const zIndex = 100 - c.depth;
          const blur = c.depth === 0 ? 0 : Math.min(3.5, 0.8 + c.depth * 0.7);

          const shadow = isTop
            ? (imgHover
                ? '0 72px 150px -24px rgba(15, 23, 42, 0.50), 0 32px 70px -20px rgba(37, 99, 235, 0.32)'
                : '0 56px 120px -22px rgba(15, 23, 42, 0.40), 0 24px 54px -18px rgba(15, 23, 42, 0.24)')
            : '0 24px 60px -28px rgba(15, 23, 42, 0.30)';

          return (
            <div
              key={c.originalIndex}
              onMouseEnter={isTop ? () => setImgHover(true) : undefined}
              onMouseLeave={isTop ? () => setImgHover(false) : undefined}
              className="absolute inset-0 rounded-[24px] overflow-hidden bg-white"
              style={{
                border: '1px solid rgba(226, 232, 240, 0.9)',
                boxShadow: shadow,
                transform: `translate3d(${offsetX}%, ${offsetY}%, 0) rotateY(${rotateY}deg) scale(${scale})`,
                transformOrigin: 'left center',
                transformStyle: 'preserve-3d',
                transition: 'transform 700ms cubic-bezier(0.22, 1, 0.36, 1), opacity 600ms ease, filter 600ms ease, box-shadow 600ms ease',
                opacity,
                filter: blur ? `blur(${blur}px)` : 'none',
                zIndex,
                willChange: 'transform, opacity',
                backfaceVisibility: 'hidden'
              }}
            >
              <img
                key={isTop ? `top-${index}` : c.originalIndex}
                src={c.src}
                alt={isTop ? `Capture SkyApp - ${c.label}` : ''}
                draggable={false}
                loading={isTop ? 'eager' : 'lazy'}
                className={`absolute inset-0 w-full h-full object-contain pointer-events-none ${isTop ? 'hero-screen-in' : ''}`}
                style={{ backgroundColor: '#FFFFFF' }}
              />

              {/* Voile dégradé pour donner du contraste 3D aux cartes du fond */}
              {!isTop && (
                <div
                  aria-hidden="true"
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.35) 60%, rgba(255,255,255,0.55) 100%)'
                  }}
                />
              )}

              {/* Pastille label dynamique uniquement sur la carte active */}
              {isTop && (
                <div
                  className="absolute bottom-3 right-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium pointer-events-none"
                  style={{
                    background: 'rgba(255,255,255,0.92)',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    border: '1px solid rgba(229, 231, 235, 0.9)',
                    color: '#1E293B',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.10)'
                  }}
                  aria-live="polite"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                  {c.label}
                </div>
              )}
            </div>
          );
        })}

        {/* Pastille flottante "Temps gagné" (bas-gauche, hors carte) */}
        <div
          className="hidden sm:flex absolute -bottom-5 -left-5 bg-white rounded-2xl px-4 py-3 items-center gap-3 pointer-events-none"
          style={{
            border: '1px solid #F3F4F6',
            boxShadow: '0 16px 32px -12px rgba(15, 23, 42, 0.18)',
            zIndex: 200
          }}
        >
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#DCFCE7' }}>
            <CheckCircle className="h-5 w-5" style={{ color: '#16A34A' }} />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-900 leading-tight">Bureau &amp; terrain</p>
            <p className="text-xs leading-tight" style={{ color: '#6B7280' }}>toujours synchronisés</p>
          </div>
        </div>
      </div>

      {/* Boutons précédent / suivant + points indicateurs */}
      <div className="relative z-10 mt-8 flex items-center justify-center gap-4">
        <button
          type="button"
          onClick={() => setIndex((i) => mod(i - 1, total))}
          aria-label="Vue précédente"
          className="w-10 h-10 rounded-full flex items-center justify-center bg-white transition-all duration-200 hover:-translate-y-0.5 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          style={{
            border: '1px solid #E5E7EB',
            boxShadow: '0 6px 16px -8px rgba(15, 23, 42, 0.18)',
            color: '#1E293B'
          }}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2" role="tablist" aria-label="Vues du produit">
          {HERO_SCREENSHOTS.map((s, i) => {
            const isActive = i === mod(index, total);
            return (
              <button
                key={s.src}
                role="tab"
                aria-selected={isActive}
                aria-label={`Afficher : ${s.label}`}
                onClick={() => setIndex(i)}
                className="rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                style={{
                  width: isActive ? 22 : 8,
                  height: 8,
                  transform: isActive ? 'scale(1.15)' : 'scale(1)',
                  backgroundColor: isActive ? '#2563EB' : '#CBD5E1',
                  boxShadow: isActive ? '0 2px 8px -1px rgba(37, 99, 235, 0.55)' : 'none'
                }}
              />
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => setIndex((i) => mod(i + 1, total))}
          aria-label="Vue suivante"
          className="w-10 h-10 rounded-full flex items-center justify-center bg-white transition-all duration-200 hover:-translate-y-0.5 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          style={{
            border: '1px solid #E5E7EB',
            boxShadow: '0 6px 16px -8px rgba(15, 23, 42, 0.18)',
            color: '#1E293B'
          }}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Liste des noms de vues (cliquables, sans miniatures) */}
      <div className="relative z-10 mt-5 w-full overflow-x-auto sm:overflow-visible">
        <div className="flex items-center justify-center flex-wrap gap-x-5 gap-y-2 sm:gap-x-7 px-2">
          {HERO_SCREENSHOTS.map((s, i) => {
            const isActive = i === mod(index, total);
            return (
              <button
                key={s.src}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={s.label}
                className="relative text-[12px] sm:text-[14px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
                style={{ color: isActive ? '#2563EB' : '#64748B' }}
              >
                {s.label}
                <span
                  aria-hidden="true"
                  className="block mx-auto mt-1 rounded-full transition-all duration-300"
                  style={{
                    width: isActive ? 18 : 0,
                    height: 2,
                    backgroundColor: '#2563EB'
                  }}
                />
              </button>
            );
          })}
        </div>
      </div>

      {/* Animation de transition d'écran (fade + slide) */}
      <style>{`
        @keyframes heroScreenIn {
          from { opacity: 0; transform: translateY(30px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .hero-screen-in { animation: heroScreenIn 0.4s cubic-bezier(0.22, 1, 0.36, 1) both; }
        @media (prefers-reduced-motion: reduce) {
          .hero-screen-in { animation: none; }
        }
      `}</style>
    </div>
  );
};
const HeroAboutBubble = () => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="absolute z-20 left-4 sm:left-6 bottom-4 sm:bottom-6"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* Bulle fermée (toujours rendue, sert d'ancre) */}
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((o) => !o)}
        onFocus={() => setOpen(true)}
        className="inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-xs sm:text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-colors"
        style={{
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid #E5E7EB',
          color: '#374151',
          boxShadow: '0 6px 16px -6px rgba(15, 23, 42, 0.18)'
        }}
      >
        <Info className="h-4 w-4" style={{ color: '#FB923C' }} />
        <span>À propos</span>
      </button>

      {/* Carte ouverte */}
      <div
        role="dialog"
        aria-label="À propos de SkyApp"
        className="absolute left-0 bottom-full mb-3 origin-bottom-left"
        style={{
          width: 'min(92vw, 320px)',
          opacity: open ? 1 : 0,
          transform: open ? 'scale(1) translateY(0)' : 'scale(0.96) translateY(6px)',
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 200ms ease, transform 200ms ease'
        }}
      >
        <div
          className="relative rounded-[22px] p-5"
          style={{
            background: 'rgba(255,255,255,0.92)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid #E5E7EB',
            boxShadow: '0 24px 70px rgba(15, 23, 42, 0.14)'
          }}
        >
          {/* Bouton fermer discret */}
          <button
            type="button"
            aria-label="Fermer"
            onClick={() => setOpen(false)}
            className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full inline-flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <div className="flex items-center gap-2 mb-2 pr-6">
            <span
              className="inline-flex items-center justify-center w-7 h-7 rounded-lg"
              style={{ background: 'rgba(251, 146, 60, 0.14)' }}
            >
              <Info className="h-4 w-4" style={{ color: '#FB923C' }} />
            </span>
            <h3 className="text-sm font-semibold text-gray-900">À propos de SkyApp</h3>
          </div>
          <p className="text-xs sm:text-sm leading-relaxed mb-3" style={{ color: '#475569' }}>
            SkyApp est conçu pour aider les entreprises du BTP à mieux organiser leurs devis, leurs équipes, leurs chantiers et leurs rapports d'intervention.
          </p>
          <ul className="space-y-1.5 mb-3">
            {['Pensé pour le terrain', 'Simple à prendre en main', 'Adapté aux équipes bureau et techniciens'].map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-xs sm:text-sm" style={{ color: '#1E293B' }}>
                <span
                  className="mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: '#FB923C' }}
                ></span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <a
            href="#solution"
            className="inline-flex items-center gap-1 text-xs sm:text-sm font-medium hover:underline"
            style={{ color: '#2563EB' }}
            onClick={() => setOpen(false)}
          >
            En savoir plus
            <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
};

// Données des 5 blocs produit (section "Tout ce dont vous avez besoin")
const FEATURES_SHOWCASE = [
  {
    img: '/Menu tech.png',
    alt: 'Interface Technicien - SkyApp',
    icon: Search,
    iconGradient: 'from-blue-500 to-cyan-500',
    color: '#2563EB',
    title: 'Interface Technicien Mobile',
    label: 'Interface mobile terrain',
    desc: "Menu intuitif optimisé pour les techniciens terrain. Accès rapide aux recherches, missions, planning et rapports. Interface tactile adaptée à tous les appareils mobiles avec navigation simplifiée.",
    points: ['Navigation simplifiée pour le terrain', 'Accès rapide aux missions en cours', 'Interface tactile optimisée']
  },
  {
    img: '/Menu bureau.png',
    alt: 'Interface Bureau - SkyApp',
    icon: FileText,
    iconGradient: 'from-green-500 to-emerald-500',
    color: '#10B981',
    title: 'Centre de Gestion Bureau',
    label: 'Centre de gestion bureau',
    desc: "Interface administrative complète pour gérer toute votre activité. Tableau de bord centralisé avec accès aux recherches, devis, clients, planning et rapports. Statistiques globales et navigation rapide.",
    points: ['Tableau de bord centralisé', "Statistiques d'activité globales", 'Navigation intuitive entre modules']
  },
  {
    img: '/Centre gestion commercial.png',
    alt: 'Centre de Gestion Commerciale - SkyApp',
    icon: DollarSign,
    iconGradient: 'from-purple-500 to-violet-500',
    color: '#7C3AED',
    title: 'Centre de Gestion Commerciale',
    label: 'Gestion commerciale',
    desc: "Module complet pour la gestion commerciale : devis, clients, projets et chantiers. Suivez l'ensemble du cycle de vente depuis la prospection jusqu'à la réalisation. Génération automatique de documents et suivi d'activité.",
    points: ['Gestion complète clients et projets', 'Cycle devis → chantier automatisé', 'Documents professionnels personnalisés']
  },
  {
    img: '/Gestion planing.png',
    alt: "Planning d'Équipe - SkyApp",
    icon: Calendar,
    iconGradient: 'from-orange-500 to-red-500',
    color: '#FB923C',
    title: "Planning d'Équipe Intelligent",
    label: 'Planning intelligent',
    desc: "Organisez et visualisez le planning de vos équipes en temps réel. Assignation drag & drop, gestion des disponibilités, vue calendrier et liste. Synchronisation automatique avec les missions terrain et notifications.",
    points: ['Vue calendrier et planning hebdomadaire', 'Assignation rapide par drag & drop', 'Gestion des disponibilités techniciens']
  },
  {
    img: '/Dashbord stat.png',
    alt: 'Analytics Avancées - SkyApp',
    icon: BarChart3,
    iconGradient: 'from-indigo-500 to-blue-500',
    color: '#6366F1',
    title: 'Analytics & Statistiques Avancées',
    label: 'Analyse en temps réel',
    desc: "Analysez votre performance avec des graphiques détaillés et KPI en temps réel. Évolution du chiffre d'affaires, nombre de projets, taux de conversion, répartition géographique. Prenez des décisions éclairées basées sur vos données.",
    points: ['Graphiques interactifs et KPI visuels', 'Suivi CA et taux de conversion', 'Analyse géographique par région']
  }
];

// Une ligne produit (image + texte) avec effet focus au scroll + interactions premium
const FeatureRow = ({ feature, reverse }) => {
  const { img, alt, icon: Icon, iconGradient, color, title, label, desc, points } = feature;
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.35, rootMargin: '-10% 0px -10% 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`feature-row ${visible ? 'is-focus' : ''} flex flex-col ${reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 md:gap-12`}
    >
      {/* Visuel */}
      <div className="lg:w-1/2 w-full">
        <div className="feature-screen relative">
          {/* Glow coloré derrière le screen */}
          <span
            aria-hidden="true"
            className="feature-glow pointer-events-none absolute -inset-6 -z-10"
            style={{
              background: `radial-gradient(ellipse at 50% 50%, ${color}40, transparent 65%)`,
              filter: 'blur(80px)'
            }}
          />
          <div
            className="feature-screen-card relative bg-white rounded-3xl overflow-hidden"
            style={{
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 40px 90px -28px rgba(15, 23, 42, 0.34), 0 16px 40px -20px rgba(15, 23, 42, 0.20)'
            }}
          >
            <img src={img} alt={alt} className="w-full h-auto object-cover" loading="lazy" />
          </div>
          {/* Pill label discret sous le screen */}
          <div className="flex justify-center -mt-3 relative z-10">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
              style={{
                background: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(226, 232, 240, 0.9)',
                color: '#475569',
                boxShadow: '0 6px 16px -8px rgba(15, 23, 42, 0.18)'
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
              {label}
            </span>
          </div>
        </div>
      </div>

      {/* Texte */}
      <div className="lg:w-1/2 w-full">
        <div
          className={`feature-icon w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br ${iconGradient} rounded-2xl flex items-center justify-center mb-4 md:mb-6 shadow-lg`}
        >
          <Icon className="h-6 w-6 md:h-8 md:w-8 text-white" />
        </div>
        <h3 className="feature-title text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-4">{title}</h3>
        <p className="feature-desc text-base md:text-lg text-gray-600 leading-relaxed mb-4 md:mb-6">{desc}</p>
        <ul className="feature-points space-y-2 md:space-y-3 text-sm md:text-base text-gray-700">
          {points.map((p, i) => (
            <li
              key={i}
              className="feature-point group relative flex items-center rounded-xl py-3 px-4 transition-all duration-300"
              style={{ '--feat-color': color }}
            >
              {/* Barre verticale colorée */}
              <span
                aria-hidden="true"
                className="feature-point-bar absolute left-0 top-1/2 -translate-y-1/2 rounded-full transition-all duration-300"
                style={{ width: 3, backgroundColor: color }}
              />
              <span
                className="feature-point-icon flex-shrink-0 mr-2 md:mr-3 flex items-center justify-center transition-transform duration-300"
              >
                <Check className="h-4 w-4 md:h-5 md:w-5" style={{ color }} />
              </span>
              <span className="font-medium">{p}</span>
            </li>
          ))}
        </ul>
      </div>

      <style>{`
        .feature-row {
          opacity: 0.5;
          transform: scale(0.97);
          filter: blur(1px);
          transition: opacity 0.5s ease, transform 0.5s ease, filter 0.5s ease;
        }
        .feature-row.is-focus {
          opacity: 1;
          transform: scale(1);
          filter: blur(0);
        }
        .feature-screen-card {
          transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.4s ease;
        }
        .feature-screen:hover .feature-screen-card {
          transform: scale(1.03);
          box-shadow: 0 56px 120px -28px rgba(15, 23, 42, 0.44), 0 24px 56px -22px rgba(15, 23, 42, 0.26);
        }
        .feature-glow { opacity: 0.18; transition: opacity 0.5s ease; }
        .feature-row.is-focus .feature-glow { opacity: 0.25; }
        .feature-icon, .feature-title, .feature-desc, .feature-point {
          opacity: 0;
          transform: translateY(20px);
        }
        .feature-row.is-focus .feature-icon,
        .feature-row.is-focus .feature-title,
        .feature-row.is-focus .feature-desc,
        .feature-row.is-focus .feature-point {
          opacity: 1;
          transform: translateY(0);
          transition: opacity 0.5s ease, transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .feature-row.is-focus .feature-icon { transition-delay: 0.05s; }
        .feature-row.is-focus .feature-title { transition-delay: 0.12s; }
        .feature-row.is-focus .feature-desc { transition-delay: 0.20s; }
        .feature-row.is-focus .feature-point:nth-child(1) { transition-delay: 0.28s; }
        .feature-row.is-focus .feature-point:nth-child(2) { transition-delay: 0.36s; }
        .feature-row.is-focus .feature-point:nth-child(3) { transition-delay: 0.44s; }
        .feature-point-bar { height: 0%; opacity: 0; }
        .feature-point:hover {
          background: linear-gradient(90deg, color-mix(in srgb, var(--feat-color) 10%, transparent) 0%, transparent 100%);
          box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--feat-color) 28%, transparent), 0 8px 20px -14px var(--feat-color);
        }
        .feature-point:hover .feature-point-bar { height: 62%; opacity: 1; }
        .feature-point:hover .feature-point-icon { transform: scale(1.18); }
        @media (prefers-reduced-motion: reduce) {
          .feature-row, .feature-icon, .feature-title, .feature-desc, .feature-point {
            opacity: 1 !important; transform: none !important; filter: none !important; transition: none !important;
          }
        }
      `}</style>
    </div>
  );
};


// FAQ interactive en accordéon (DA premium, cohérent hero)
const FAQ_ITEMS = [
  {
    icon: Layers,
    color: '#2563EB',
    bg: '#DBEAFE',
    q: 'Puis-je changer de formule ?',
    a: "Oui, vous pouvez changer d'offre ou passer à une offre inférieure à tout moment depuis votre tableau de bord."
  },
  {
    icon: Shield,
    color: '#7C3AED',
    bg: '#EDE9FE',
    q: 'Y a-t-il des frais cachés ?',
    a: 'Aucun frais caché. Tous nos tarifs sont transparents et affichés TTC.'
  },
  {
    icon: Clock,
    color: '#FB923C',
    bg: '#FFEDD5',
    q: 'Essai gratuit disponible ?',
    a: "Oui, 14 jours d'essai gratuit pour toutes les formules, sans engagement ni carte bancaire."
  },
  {
    icon: Headphones,
    color: '#10B981',
    bg: '#D1FAE5',
    q: 'Support technique inclus ?',
    a: 'Support 24/7 inclus dans toutes nos formules, avec priorité pour les formules Pro et Entreprise.'
  }
];

const FaqAccordion = ({ onContact }) => {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="max-w-3xl mx-auto space-y-3 md:space-y-4 text-left">
      {FAQ_ITEMS.map((item, i) => {
        const Icon = item.icon;
        const isOpen = openIndex === i;
        return (
          <div
            key={i}
            className="faq-item rounded-2xl overflow-hidden"
            style={{
              background: 'rgba(255, 255, 255, 0.92)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              boxShadow: isOpen
                ? '0 18px 40px -24px rgba(15, 23, 42, 0.28)'
                : '0 8px 24px -20px rgba(15, 23, 42, 0.18)'
            }}
          >
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => setOpenIndex(isOpen ? -1 : i)}
              className="faq-trigger w-full flex items-center gap-3 md:gap-4 px-5 md:px-6 py-4 md:py-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded-2xl"
            >
              <span className="flex-shrink-0 w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: item.bg }}>
                <Icon className="h-5 w-5" style={{ color: item.color, width: '20px', height: '20px' }} />
              </span>
              <span className="flex-1 font-semibold text-gray-900 text-base md:text-lg">{item.q}</span>
              <ChevronDown
                className="flex-shrink-0 h-5 w-5 text-gray-400 transition-transform duration-300"
                style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              />
            </button>
            <div
              className="faq-panel"
              style={{
                display: 'grid',
                gridTemplateRows: isOpen ? '1fr' : '0fr',
                transition: 'grid-template-rows 0.3s ease'
              }}
            >
              <div style={{ overflow: 'hidden' }}>
                <p
                  className="px-5 md:px-6 pb-5 md:pb-6 pl-[68px] md:pl-[72px] text-sm md:text-base text-gray-600 leading-relaxed"
                  style={{
                    opacity: isOpen ? 1 : 0,
                    transform: isOpen ? 'translateY(0)' : 'translateY(-6px)',
                    transition: 'opacity 0.3s ease, transform 0.3s ease'
                  }}
                >
                  {item.a}
                </p>
              </div>
            </div>
          </div>
        );
      })}

      <style>{`
        .faq-item { transition: transform 0.25s ease, box-shadow 0.25s ease; }
        .faq-item:hover { transform: translateY(-2px); box-shadow: 0 20px 44px -24px rgba(15, 23, 42, 0.30); }
        @media (prefers-reduced-motion: reduce) {
          .faq-item, .faq-panel, .faq-trigger * { transition: none !important; }
        }
      `}</style>
    </div>
  );
};


const LandingPage = () => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [contactError, setContactError] = useState('');
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const [emailVerifiedBanner, setEmailVerifiedBanner] = useState(false);
  const [sessionExpiredBanner, setSessionExpiredBanner] = useState(false); // false | 'expired' | 'inactivity'
  // Filtre entreprise pour le fondateur
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [productScreen, setProductScreen] = useState(0);
  const [showAiPacksModal, setShowAiPacksModal] = useState(false);
  // Révélation au scroll du bloc produit (fade-in + stagger)
  const productRevealRef = useRef(null);
  const [productRevealed, setProductRevealed] = useState(false);
  useEffect(() => {
    const el = productRevealRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setProductRevealed(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  
  // Check if user is logged in
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const founderEmail = FOUNDER_EMAIL;
  const isFounder = (user?.is_fondateur === true) || ((user?.email || '').toLowerCase() === founderEmail);
  const isAdmin = isFounder || (user?.role === 'ADMIN');
  
  // Ne charger les stats que pour les fondateurs
  const shouldLoadStats = isFounder && isLoggedIn;
  const { stats, loading } = useRealTimeStats(selectedCompany, shouldLoadStats);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setIsLoggedIn(true);
      setUser(JSON.parse(userData));
      
      // Recharger le profil depuis l'API pour avoir les données à jour
      http.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(response => {
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      })
      .catch(err => console.error('Erreur rechargement profil:', err));
    }

    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [resetLinkExpiredBanner, setResetLinkExpiredBanner] = useState(false);

  // Détecter ?verified=true ou ?session=expired dans l'URL + erreur lien reset expiré (#error=)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('verified') === 'true') {
      setEmailVerifiedBanner(true);
      setShowLoginModal(true);
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => setEmailVerifiedBanner(false), 10000);
    }
    if (params.get('session') === 'expired' || params.get('session') === 'inactivity' || params.get('session') === 'deactivated') {
      setSessionExpiredBanner(params.get('session'));
      setShowLoginModal(true);
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => setSessionExpiredBanner(false), 10000);
    }
    // Détecter lien de reset expiré renvoyé par Supabase (#error=access_denied&error_code=otp_expired)
    const hash = window.location.hash;
    if (hash.includes('error=access_denied') || hash.includes('otp_expired')) {
      setResetLinkExpiredBanner(true);
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => setResetLinkExpiredBanner(false), 10000);
    }
    // Ouverture directe des modals depuis une redirection (VerifyEmailPage, EmailConfirmedPage)
    if (params.get('openLogin') === 'true') {
      setShowLoginModal(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    if (params.get('openRegister') === 'true') {
      setShowRegisterModal(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Charger liste entreprises si fondateur
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (isFounder && token) {
      http.get(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setCompanies(r.data || []))
        .catch(() => setCompanies([]));
    }
  }, [isFounder]);

  // Charger la liste des entreprises si fondateur
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (isFounder && token) {
      http.get(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setCompanies(res.data || []))
        .catch(() => setCompanies([]));
    }
  }, [isFounder]);

  // Animation counter function
  const animateCounter = (element, target, duration = 2000) => {
    const start = 0;
    const startTime = performance.now();
    
    const updateCounter = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = Math.floor(progress * target);
      
      element.textContent = current;
      
      if (progress < 1) {
        requestAnimationFrame(updateCounter);
      }
    };
    
    requestAnimationFrame(updateCounter);
  };

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            setIsVisible(prev => ({
              ...prev,
              [id]: true
            }));

            // Animate counters in about section
            if (id === 'apropos') {
              // Reveal left-column blocks with a light stagger
              const revealItems = entry.target.querySelectorAll('.apropos-reveal');
              revealItems.forEach((item, index) => {
                setTimeout(() => {
                  item.classList.add('animate');
                }, index * 120);
              });

              setTimeout(() => {
                const counterElements = entry.target.querySelectorAll('.counter-value');
                counterElements.forEach((el, index) => {
                  const target = el.dataset.target;
                  if (el.classList.contains('user-counter')) {
                    animateCounter(el, 108, 1500); // Default user count
                  } else {
                    animateCounter(el, parseInt(target), 1500);
                  }
                });

                // Animate values
                const valueItems = entry.target.querySelectorAll('.value-item');
                valueItems.forEach((item, index) => {
                  setTimeout(() => {
                    item.classList.add('animate');
                  }, index * 200);
                });
              }, 500);
            }

            // Animate pricing cards
            if (id === 'tarifs') {
              setTimeout(() => {
                const pricingCards = entry.target.querySelectorAll('.pricing-card');
                pricingCards.forEach((card, index) => {
                  setTimeout(() => {
                    card.classList.add('animate');
                  }, index * 200);
                });
              }, 300);
            }

            // Animate testimonial cards
            if (id === 'testimonials') {
              const cards = entry.target.querySelectorAll('.testimonial-card');
              cards.forEach((card, index) => {
                setTimeout(() => {
                  card.classList.add('animate');
                }, index * 120);
              });
            }
          }
        });
      },
      { threshold: 0.3 }
    );

    // Observe sections
    const sections = document.querySelectorAll('#apropos, #tarifs, #testimonials');
    sections.forEach((section) => {
      observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  // Cursor animation for pricing section
  useEffect(() => {
    const handleMouseMove = (e) => {
      const pricingSection = document.getElementById('tarifs');
      if (!pricingSection) return;

      const cursorEffect = pricingSection.querySelector('.pricing-cursor-effect');
      if (!cursorEffect) return;

      const rect = pricingSection.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Check if mouse is within pricing section
      if (x >= 0 && x <= rect.width && y >= 0 && y <= rect.height) {
        cursorEffect.style.left = x + 'px';
        cursorEffect.style.top = y + 'px';
        cursorEffect.style.opacity = '1';
      } else {
        cursorEffect.style.opacity = '0';
      }
    };

    const handleMouseLeave = () => {
      const pricingSection = document.getElementById('tarifs');
      if (!pricingSection) return;

      const cursorEffect = pricingSection.querySelector('.pricing-cursor-effect');
      if (!cursorEffect) return;

      cursorEffect.style.opacity = '0';
    };

    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove);
    
    const pricingSection = document.getElementById('tarifs');
    if (pricingSection) {
      pricingSection.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (pricingSection) {
        pricingSection.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  const handleLogout = () => {
    setIsLoggedIn(false);
    setUser(null);
    broadcastLogout('user_action');
    forceLogout('user_action');
  };

  const goToDashboard = () => {
    window.location.href = '/role-selection';
  };

  const goToStatistics = () => {
    window.location.href = '/statistiques';
  };

  // Advanced scroll effect
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(prev => ({
            ...prev,
            [entry.target.id]: entry.isIntersecting
          }));
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[id]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: Search,
      title: "Recherche Terrain",
      description: "Interface mobile optimisée pour les techniciens sur le terrain avec géolocalisation GPS précise et hors-ligne."
    },
    {
      icon: FileBarChart,
      title: "Rapports Automatisés", 
      description: "Génération automatique de rapports PDF professionnels avec photos géolocalisées et observations détaillées."
    },
    {
      icon: Calculator,
      title: "Gestion des Devis",
      description: "Création et suivi des devis avec intégration catalogue produits et calculs automatiques."
    },
    {
      icon: MapPinIcon,
      title: "Cartographie Interactive",
      description: "Visualisation des chantiers sur carte avec informations en temps réel et navigation GPS."
    },
    {
      icon: Users,
      title: "Gestion Clients",
      description: "Base de données clients complète avec historique des interventions et suivi commercial."
    },
    {
      icon: BarChart3,
      title: "Analytics Avancés",
      description: "Tableau de bord analytique avec métriques de performance et insights prédictifs."
    }
  ];

  const testimonials = [
    {
      name: "Marie Dubois",
      role: "Technicienne BTP",
      company: "Constructions Modernes",
      content: "L'interface terrain est simple, on trouve rapidement ce qu'on cherche même sur chantier. On gagne du temps au quotidien.",
      badge: "Technicien terrain",
      avatar: "MD",
      grad: "linear-gradient(135deg, #2563EB, #7C3AED)"
    },
    {
      name: "Pierre Martin",
      role: "Chef de Bureau",
      company: "BTP Solutions",
      content: "Les rapports automatiques nous évitent pas mal de saisie. On peut se concentrer sur le chantier plutôt que sur l'administratif.",
      badge: "Équipe bureau",
      avatar: "PM",
      grad: "linear-gradient(135deg, #7C3AED, #FB923C)"
    },
    {
      name: "Sophie Rousseau",
      role: "Directrice Technique",
      company: "Terrassements Pro",
      content: "On a enfin une vue claire sur les interventions. Avant on jonglait entre plusieurs outils, maintenant tout est au même endroit.",
      badge: "Direction / gestion",
      avatar: "SR",
      grad: "linear-gradient(135deg, #2563EB, #10B981)"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Bandeau de confirmation email vérifiée */}
      {emailVerifiedBanner && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-green-500 text-white py-3 px-4 text-center shadow-lg animate-in slide-in-from-top" style={{animation: 'slideDown 0.4s ease-out'}}>
          <div className="flex items-center justify-center gap-2">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Email vérifié avec succès ! Vous pouvez maintenant vous connecter.</span>
            <button onClick={() => setEmailVerifiedBanner(false)} className="ml-4 hover:bg-green-600 rounded-full p-1 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {/* Bandeau de session expirée */}
      {sessionExpiredBanner && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-orange-500 text-white py-3 px-4 text-center shadow-lg" style={{animation: 'slideDown 0.4s ease-out'}}>
          <div className="flex items-center justify-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">
              {sessionExpiredBanner === 'inactivity'
                ? 'Session expirée pour inactivité (10 min), veuillez vous reconnecter.'
                : sessionExpiredBanner === 'deactivated'
                ? 'Votre compte a été désactivé par un administrateur. Contactez votre entreprise pour plus d\'informations.'
                : 'Votre session a expiré, veuillez vous reconnecter.'}
            </span>
            <button onClick={() => setSessionExpiredBanner(false)} className="ml-4 hover:bg-orange-600 rounded-full p-1 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {/* Bandeau lien reset expiré */}
      {resetLinkExpiredBanner && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-red-500 text-white py-3 px-4 text-center shadow-lg" style={{animation: 'slideDown 0.4s ease-out'}}>
          <div className="flex items-center justify-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">Ce lien de réinitialisation a expiré. Veuillez en demander un nouveau.</span>
            <button onClick={() => setResetLinkExpiredBanner(false)} className="ml-4 hover:bg-red-600 rounded-full p-1 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      {/* Apple-style Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrollY > 50 
          ? 'bg-white/80 backdrop-blur-xl border-b border-gray-200/50' 
          : 'bg-transparent'
      }`}>
        <div className="navigation-menu max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="logo-container flex items-center space-x-2">
              <img 
                src="/Logo skyapp v.2.png" 
                alt="SkyApp Logo" 
                className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-lg object-cover logo-neon-effect"
              />
            </div>
            
            {/* Conditional Navigation */}
            {isLoggedIn ? (
              // Simplified logged-in navigation
              <div className="flex items-center space-x-4">
                <button 
                  onClick={() => setShowUserProfile(true)}
                  className="flex items-center space-x-3 hover:bg-gray-100 rounded-lg p-2 transition-colors"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-gray-800 to-black rounded-full flex items-center justify-center text-white font-medium text-sm">
                    {(() => {
                      const firstName = user?.first_name || user?.prenom || '';
                      const lastName = user?.last_name || user?.nom || '';
                      const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
                      return initials || user?.email?.charAt(0)?.toUpperCase() || 'U';
                    })()}
                  </div>
                  <div className="hidden md:block text-sm">
                    <span className="font-medium text-gray-900">
                      {user?.first_name || user?.prenom} {user?.last_name || user?.nom}
                    </span>
                  </div>
                </button>
                <Button
                  onClick={goToDashboard}
                  className="bg-black hover:bg-gray-800 text-white text-sm font-medium px-4 py-2 rounded-full transition-colors duration-200"
                >
                  Tableau de bord
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  className="text-gray-500 hover:text-gray-700 p-2"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              // Original navigation for non-logged users
              <>
                <div className="flex items-center space-x-2 md:space-x-4 lg:space-x-8">
                  <div className="hidden lg:flex items-center space-x-8">
                    {[
                      { label: 'Fonctionnalités', id: 'features' },
                      { label: 'À propos', id: 'apropos' },
                      { label: 'Tarifs', id: 'tarifs' },
                      { label: 'Contact', id: 'contact' }
                    ].map((item) => (
                      <a
                        key={item.label}
                        href={`#${item.id}`}
                        className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors duration-200"
                      >
                        {item.label}
                      </a>
                    ))}
                    <a
                      href="/changelog"
                      className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors duration-200"
                    >
                      Mises à jour
                    </a>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowLoginModal(true)}
                    className="border-2 border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white text-xs md:text-sm font-medium px-3 md:px-4 py-2 rounded-full transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105 notranslate"
                    translate="no"
                  >
                    Connexion
                  </Button>
                  <Button
                    onClick={() => setShowRegisterModal(true)}
                    className="bg-black hover:bg-gray-800 text-white text-xs md:text-sm font-medium px-3 md:px-4 py-2 rounded-full transition-colors duration-200 notranslate"
                    translate="no"
                  >
                    Inscription
                  </Button>
                </div>
                
                {/* Mobile menu button */}
                <div className="lg:hidden ml-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="p-2"
                  >
                    {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                  </Button>
                </div>
              </>
            )}

            {/* Desktop menu hidden placeholder */}
            <div className="hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className={`lg:hidden border-t border-gray-200/50 bg-white/95 backdrop-blur-xl overflow-hidden transition-all duration-300 ease-in-out ${mobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
            <div className="py-4 space-y-4">
                {isLoggedIn ? (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 px-4 py-2">
                      <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center text-white font-medium text-sm">
                        {(user?.first_name || user?.prenom)?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {user?.first_name || user?.prenom} {user?.last_name || user?.nom}
                        </div>
                        <div className="text-xs text-gray-500">{user?.email}</div>
                      </div>
                    </div>
                    <Button
                      onClick={goToDashboard}
                      className="w-full mx-4 bg-black hover:bg-gray-800 text-white text-sm font-medium py-2 rounded-full"
                    >
                      Tableau de bord
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={handleLogout}
                      className="w-full mx-4 text-red-600 hover:bg-red-50 text-sm"
                    >
                      Déconnexion
                    </Button>
                  </div>
                ) : (
                  <>
                    {[
                      { label: 'Fonctionnalités', id: 'features' },
                      { label: 'À propos', id: 'apropos' },
                      { label: 'Tarifs', id: 'tarifs' },
                      { label: 'Contact', id: 'contact' }
                    ].map((item) => (
                      <a
                        key={item.label}
                        href={`#${item.id}`}
                        className="block px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                      >
                        {item.label}
                      </a>
                    ))}
                    <a
                      href="/changelog"
                      className="block px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                    >
                      Mises à jour
                    </a>
                  </>
                )}
              </div>
            </div>
        </div>
      </nav>

      {/* Scène sticky : le hero reste épinglé, la section "Problèmes" remonte par-dessus */}
      <div className="hero-problemes-stage" style={{ position: 'relative' }}>

      {/* Hero Section - premium Apple-like, 2 colonnes, compact */}
      <section
        className="hero-section hero-animated-bg relative overflow-hidden flex items-center"
        style={{
          background:
            'radial-gradient(circle at 15% 70%, rgba(96, 165, 250, 0.40), transparent 32%),' +
            'radial-gradient(circle at 82% 65%, rgba(251, 146, 60, 0.38), transparent 36%),' +
            'radial-gradient(circle at 55% 25%, rgba(168, 85, 247, 0.28), transparent 34%),' +
            'radial-gradient(circle at 90% 15%, rgba(251, 146, 60, 0.22), transparent 30%),' +
            'radial-gradient(circle at 10% 20%, rgba(99, 102, 241, 0.22), transparent 30%),' +
            'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 40%, #FFF0E6 100%)',
          minHeight: '100vh',
          padding: 'clamp(80px, 12vh, 100px) clamp(16px, 4vw, 24px) clamp(56px, 8vh, 70px)',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}
      >
        {/* Blob violet décoratif (bas gauche) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-40 -bottom-40 w-[520px] h-[520px] rounded-full"
          style={{
            background:
              'radial-gradient(circle at 50% 50%, rgba(167, 139, 250, 0.42) 0%, rgba(167, 139, 250, 0.16) 45%, transparent 70%)',
            filter: 'blur(2px)'
          }}
        ></div>

        {/* Blob orange décoratif (bas droite) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-32 -bottom-32 w-[420px] h-[420px] rounded-full"
          style={{
            background:
              'radial-gradient(circle at 50% 50%, rgba(251, 146, 60, 0.30) 0%, rgba(251, 146, 60, 0.12) 50%, transparent 75%)',
            filter: 'blur(2px)'
          }}
        ></div>

        <div className="relative z-10 max-w-3xl w-full mx-auto">
          <div className="text-center">
            {/* Texte hero centré (impact marketing, sans démonstration produit) */}
            <div className="space-y-6 md:space-y-7">
              {/* Badge crédibilité terrain */}
              <div className="flex justify-center">
                <span
                  className="hero-badge inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs sm:text-sm font-semibold"
                  style={{
                    color: '#4338CA',
                    backgroundColor: 'rgba(99, 102, 241, 0.10)',
                    border: '1px solid rgba(99, 102, 241, 0.22)',
                    backdropFilter: 'blur(4px)'
                  }}
                >
                  <Wrench className="h-3.5 w-3.5" style={{ color: '#7C3AED' }} />
                  Pensé pour les professionnels du terrain
                </span>
              </div>

              <h1 className="hero-title futuristic-text relative text-4xl sm:text-5xl lg:text-[68px] font-extrabold text-gray-900 tracking-tight" style={{ lineHeight: 1.08, color: '#0F172A' }}>
                {/* Halo lumineux derrière le titre (profondeur premium) */}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[160%]"
                  style={{
                    zIndex: -1,
                    background:
                      'radial-gradient(ellipse at 30% 40%, rgba(37, 99, 235, 0.35), transparent 70%),' +
                      'radial-gradient(ellipse at 70% 55%, rgba(124, 58, 237, 0.28), transparent 70%),' +
                      'radial-gradient(ellipse at 80% 75%, rgba(251, 146, 60, 0.22), transparent 70%)',
                    filter: 'blur(80px)',
                    opacity: 0.18
                  }}
                />
                Gérez vos chantiers
                <br />
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 50%, #FB923C 100%)',
                    WebkitBackgroundClip: 'text'
                  }}
                >
                  sans perdre de temps
                </span>
              </h1>

              <p className="hero-subtitle text-base sm:text-lg max-w-2xl mx-auto" style={{ color: '#475569', lineHeight: 1.7 }}>
                Une seule application pour gérer vos devis, vos équipes et vos rapports — conçue avec ceux qui sont vraiment sur le terrain.
              </p>

              <ul className="hero-bullets space-y-3 text-sm sm:text-base max-w-xl mx-auto inline-flex flex-col items-start text-left" style={{ color: '#1E293B' }}>
                {[
                  'Des informations claires pour chaque équipe',
                  'Une vision claire de vos équipes et chantiers',
                  'Des rapports prêts en quelques minutes'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span
                      className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: '#DBEAFE',
                        boxShadow: 'inset 0 0 0 1px rgba(37, 99, 235, 0.18)'
                      }}
                    >
                      <Check className="h-3 w-3" strokeWidth={3} style={{ color: '#2563EB' }} />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="hero-cta flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                <button
                  onClick={() => setShowRegisterModal(true)}
                  className="hero-cta-primary w-full sm:w-auto text-white font-semibold px-7 py-3.5 rounded-full transition-all duration-300 flex items-center justify-center gap-2 hover:scale-[1.05]"
                  style={{
                    backgroundImage: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 48%, #FB923C 100%)',
                    backgroundSize: '200% 100%',
                    boxShadow: '0 12px 28px -10px rgba(124, 58, 237, 0.60), 0 6px 14px -4px rgba(37, 99, 235, 0.40), 0 4px 12px -4px rgba(251, 146, 60, 0.30)'
                  }}
                >
                  <span>Essayer gratuitement</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
                <a
                  href="#solution"
                  className="w-full sm:w-auto bg-white hover:bg-gray-50 text-gray-900 font-semibold px-7 py-3.5 rounded-full transition-transform duration-200 hover:scale-[1.03] flex items-center justify-center gap-2"
                  style={{
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
                  }}
                >
                  <Play className="h-4 w-4" />
                  <span>Voir le produit</span>
                </a>
              </div>

              {/* Proof bar avec icônes */}
              <div className="hero-proof flex flex-wrap items-center justify-center gap-x-5 gap-y-2 pt-1 text-xs sm:text-sm" style={{ color: '#64748B' }}>
                <span className="inline-flex items-center gap-1.5">
                  <Shield className="h-4 w-4" style={{ color: '#16A34A' }} />
                  <span className="font-medium">Essai gratuit 14 jours</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Lock className="h-4 w-4" style={{ color: '#6B7280' }} />
                  <span className="font-medium">Sans engagement</span>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Headphones className="h-4 w-4" style={{ color: '#2563EB' }} />
                  <span className="font-medium">Support réactif</span>
                </span>
              </div>


            </div>
          </div>
        </div>

        {/* Bulle "À propos de SkyApp" — bas-gauche du hero */}
        <HeroAboutBubble />

        {/* Flèche "scroll" en bas du hero */}
        <button
          type="button"
          onClick={() => {
            const el = document.getElementById('problemes');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          aria-label="Faire défiler vers la section suivante"
          className="hero-scroll-arrow absolute left-1/2 -translate-x-1/2 bottom-6 md:bottom-8 z-20 flex items-center justify-center rounded-full bg-white transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
          style={{
            width: 48,
            height: 48,
            border: '1px solid #E5E7EB',
            boxShadow: '0 10px 24px -10px rgba(15, 23, 42, 0.22)',
            color: '#1E293B'
          }}
        >
          <ChevronDown className="w-6 h-6" />
        </button>

        {/* Animation flottement */}
        <style>{`
          @keyframes heroFloat {
            0%, 100% { transform: translateY(0); }
            50%      { transform: translateY(-8px); }
          }
          .hero-float { animation: heroFloat 7s ease-in-out infinite; }
          @keyframes heroScrollBounce {
            0%, 100% { transform: translate(-50%, 0); }
            50%      { transform: translate(-50%, 6px); }
          }
          .hero-scroll-arrow { animation: heroScrollBounce 2.2s ease-in-out infinite; }
          @keyframes heroFadeUp {
            from { opacity: 0; transform: translateY(14px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes heroFadeIn {
            from { opacity: 0; transform: translateY(6px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          @keyframes heroGlowShift {
            0%, 100% { background-position: 0% 50%; }
            50%      { background-position: 100% 50%; }
          }
          .hero-badge { opacity: 0; animation: heroFadeIn 0.6s ease-out 0.05s forwards; }
          .hero-title { opacity: 0; animation: heroFadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.15s forwards; }
          .hero-subtitle { opacity: 0; animation: heroFadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.32s forwards; }
          .hero-bullets { opacity: 0; animation: heroFadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.45s forwards; }
          .hero-cta { opacity: 0; animation: heroFadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.58s forwards; }
          .hero-proof { opacity: 0; animation: heroFadeIn 0.7s ease-out 0.72s forwards; }
          .hero-context-card {
            background: rgba(255, 255, 255, 0.88);
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
            border: 1px solid rgba(124, 58, 237, 0.16);
            box-shadow: 0 18px 40px -18px rgba(15, 23, 42, 0.30), 0 4px 12px -6px rgba(124, 58, 237, 0.20);
            opacity: 0;
            transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease;
            animation: heroFadeUp 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.85s forwards;
          }
          .hero-context-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 24px 50px -18px rgba(15, 23, 42, 0.34), 0 6px 16px -6px rgba(124, 58, 237, 0.26);
          }
          .hero-cta-primary { animation: heroGlowShift 6s ease-in-out infinite; }
          .hero-cta-primary:hover {
            box-shadow: 0 0 0 1px rgba(124, 58, 237, 0.25),
                        0 14px 34px -10px rgba(124, 58, 237, 0.65),
                        0 0 28px -4px rgba(37, 99, 235, 0.55);
          }
          @keyframes heroBgShift {
            0%, 100% { background-position: 0% 0%, 100% 100%, 50% 0%, 0% 50%; }
            50%      { background-position: 10% 20%, 90% 80%, 40% 30%, 100% 50%; }
          }
          .hero-animated-bg { /* animation désactivée — évite les coutures de gradient */ }
          @media (prefers-reduced-motion: reduce) {
            .hero-float { animation: none; }
            .hero-scroll-arrow { animation: none; }
            .hero-badge, .hero-title, .hero-subtitle, .hero-bullets, .hero-cta, .hero-proof { opacity: 1; animation: none; }
            .hero-context-card { opacity: 1; animation: none; }
            .hero-cta-primary { animation: none; }
            .hero-animated-bg { animation: none; }
          }
        `}</style>
      </section>

      {/* Section : Tout votre chantier, au même endroit — feature split (image + texte) */}
      <section
        id="solution"
        className="relative overflow-hidden py-20 md:py-28"
        style={{
          backgroundColor: '#FFFFFF',
          backgroundImage:
            'radial-gradient(ellipse at 18% 35%, rgba(96, 165, 250, 0.22) 0%, rgba(96, 165, 250, 0) 55%),' +
            'radial-gradient(ellipse at 42% 78%, rgba(251, 146, 60, 0.16) 0%, rgba(251, 146, 60, 0) 50%),' +
            'radial-gradient(ellipse at 5% 5%, rgba(124, 58, 237, 0.12) 0%, rgba(124, 58, 237, 0) 45%),' +
            'linear-gradient(180deg, #FFFFFF 0%, #FBFCFF 100%)',
          borderTopLeftRadius: '40px',
          borderTopRightRadius: '40px',
          boxShadow: '0 -28px 70px -20px rgba(15, 23, 42, 0.22)',
          position: 'relative',
          zIndex: 2
        }}
      >
        <div ref={productRevealRef} className={`relative z-10 max-w-6xl mx-auto px-4 sm:px-6 product-reveal ${productRevealed ? 'is-revealed' : ''}`}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Colonne animation produit (carousel déplacé depuis le hero) */}
            <div className="relative order-1 z-0 lg:-ml-12 xl:-ml-16 lg:pr-6 product-reveal-item">
              <HeroProductCarousel index={productScreen} onIndexChange={setProductScreen} autoPlay />
            </div>

            {/* Colonne texte */}
            <div className="order-2 relative z-10 lg:pl-10 xl:pl-16">
              {/* Badge section produit */}
              <div className="product-reveal-item mb-4">
                <span
                  className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold"
                  style={{
                    color: '#2563EB',
                    backgroundColor: 'rgba(37, 99, 235, 0.10)',
                    border: '1px solid rgba(37, 99, 235, 0.20)'
                  }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#2563EB' }} />
                  Vue d'ensemble SkyApp
                </span>
              </div>
              <h2 className="product-reveal-item text-3xl md:text-4xl lg:text-5xl font-bold mb-4" style={{ color: '#0F172A', letterSpacing: '-0.02em' }}>
                Tout votre chantier, au même endroit
              </h2>
              <p className="product-reveal-item text-base md:text-lg mb-4" style={{ color: '#475569', lineHeight: 1.7 }}>
                Devis, planning et rapports réunis dans une seule application.
              </p>
              <p className="product-reveal-item text-sm md:text-[15px] mb-8 md:mb-10" style={{ color: '#64748B', lineHeight: 1.6 }}>
                Chaque module est connecté pour éviter les doubles saisies et garder une vision claire.
              </p>

              <ul className="space-y-4 md:space-y-5">
                {[
                  { icon: FileText, label: 'Créez et suivez vos devis', bg: '#DBEAFE', color: '#2563EB', screen: 3 },
                  { icon: Calendar, label: 'Planifiez vos équipes en quelques clics', bg: '#EDE9FE', color: '#7C3AED', screen: 1 },
                  { icon: ClipboardList, label: 'Réalisez vos comptes-rendus sur le terrain', bg: '#FFEDD5', color: '#FB923C', screen: 4 },
                  { icon: Users, label: 'Gérez clients, devis et chantiers en un clic', bg: '#D1FAE5', color: '#10B981', screen: 2 }
                ].map(({ icon: Icon, label, bg, color, screen }, idx) => {
                  const active = productScreen === screen;
                  return (
                    <li
                      key={idx}
                      onMouseEnter={() => setProductScreen(screen)}
                      onFocus={() => setProductScreen(screen)}
                      tabIndex={0}
                      className="group product-reveal-item relative flex items-center gap-4 rounded-2xl p-3.5 md:p-4 pl-5 md:pl-6 transition-all duration-300 cursor-pointer hover:-translate-y-0.5 focus:outline-none"
                      style={{
                        backgroundImage: active
                          ? `linear-gradient(90deg, ${color}1F 0%, ${color}08 60%, transparent 100%)`
                          : 'none',
                        backgroundColor: active ? '#FBFCFF' : 'transparent',
                        transform: active ? 'translateX(2px)' : 'translateX(0)',
                        boxShadow: active
                          ? `inset 0 0 0 1px ${color}40, 0 14px 32px -16px ${color}AA`
                          : 'inset 0 0 0 1px transparent'
                      }}
                    >
                      {/* Barre verticale colorée (feature active) */}
                      <span
                        aria-hidden="true"
                        className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full transition-all duration-300"
                        style={{
                          width: 4,
                          height: active ? '70%' : '0%',
                          backgroundColor: color,
                          opacity: active ? 1 : 0,
                          boxShadow: active ? `0 0 10px -1px ${color}AA` : 'none'
                        }}
                      />
                      <span
                        className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                        style={{
                          backgroundColor: bg,
                          transform: active ? 'scale(1.1)' : 'scale(1)',
                          boxShadow: active ? `0 8px 18px -10px ${color}AA` : 'none'
                        }}
                      >
                        <Icon className="h-5 w-5" style={{ color }} strokeWidth={2.2} />
                      </span>
                      <span
                        className="font-semibold text-base md:text-[17px] transition-colors duration-300"
                        style={{ color: active ? '#0F172A' : '#334155' }}
                      >
                        {label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        {/* Révélation au scroll (fade-in + stagger) du bloc produit */}
        <style>{`
          .product-reveal .product-reveal-item {
            opacity: 0;
            transform: translateY(22px);
          }
          .product-reveal.is-revealed .product-reveal-item {
            opacity: 1;
            transform: translateY(0);
            transition: opacity 0.6s ease, transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
          }
          .product-reveal.is-revealed .product-reveal-item:nth-child(1) { transition-delay: 0.05s; }
          .product-reveal.is-revealed .product-reveal-item:nth-child(2) { transition-delay: 0.14s; }
          .product-reveal.is-revealed .product-reveal-item:nth-child(3) { transition-delay: 0.22s; }
          .product-reveal.is-revealed .product-reveal-item:nth-child(4) { transition-delay: 0.30s; }
          .product-reveal.is-revealed .product-reveal-item:nth-child(5) { transition-delay: 0.38s; }
          .product-reveal.is-revealed .product-reveal-item:nth-child(6) { transition-delay: 0.46s; }
          @media (prefers-reduced-motion: reduce) {
            .product-reveal .product-reveal-item { opacity: 1; transform: none; transition: none; }
          }
        `}</style>
      </section>

      </div>
      {/* /scène sticky hero + tout votre chantier */}

      {/* Section : Problèmes rencontrés dans le BTP — bulles organiques */}
      <section
        id="problemes"
        className="relative overflow-hidden py-20 md:py-28"
        style={{
          backgroundColor: '#FFFFFF',
          backgroundImage:
            'radial-gradient(ellipse at 100% 60%, rgba(254, 215, 170, 0.35) 0%, rgba(254, 215, 170, 0) 45%),' +
            'radial-gradient(ellipse at 0% 90%, rgba(199, 210, 254, 0.18) 0%, rgba(199, 210, 254, 0) 45%),' +
            'linear-gradient(180deg, #FFFFFF 0%, #FDFCFB 100%)'
        }}
      >
        {/* Vagues décoratives SVG (très subtiles, comme dans la maquette) */}
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 w-full opacity-50"
          style={{ height: '60%' }}
          viewBox="0 0 1440 500"
          preserveAspectRatio="none"
        >
          <path d="M0,260 C200,200 380,340 620,290 C880,235 1100,360 1440,280" stroke="#CBD5E1" strokeOpacity="0.45" strokeWidth="1" fill="none" />
          <path d="M0,320 C240,260 420,400 700,340 C980,280 1200,400 1440,330" stroke="#C7D2FE" strokeOpacity="0.45" strokeWidth="1" fill="none" />
          <path d="M0,380 C280,330 460,460 760,400 C1040,350 1240,440 1440,400" stroke="#FED7AA" strokeOpacity="0.55" strokeWidth="1" fill="none" />
          <path d="M0,440 C320,400 520,510 820,460 C1100,420 1280,490 1440,460" stroke="#FBCFE8" strokeOpacity="0.35" strokeWidth="1" fill="none" />
        </svg>

        {/* Points colorés flottants dispersés */}
        <span aria-hidden="true" className="pointer-events-none absolute hidden md:block" style={{ top: '52%', left: '28%', width: 8, height: 8, borderRadius: '50%', background: '#60A5FA' }}></span>
        <span aria-hidden="true" className="pointer-events-none absolute hidden md:block" style={{ top: '46%', left: '22%', width: 6, height: 6, borderRadius: '50%', background: '#A78BFA', opacity: 0.7 }}></span>
        <span aria-hidden="true" className="pointer-events-none absolute hidden md:block" style={{ top: '70%', left: '46%', width: 8, height: 8, borderRadius: '50%', background: '#A78BFA' }}></span>
        <span aria-hidden="true" className="pointer-events-none absolute hidden md:block" style={{ top: '68%', left: '60%', width: 8, height: 8, borderRadius: '50%', background: '#34D399' }}></span>
        <span aria-hidden="true" className="pointer-events-none absolute hidden md:block" style={{ top: '78%', left: '70%', width: 7, height: 7, borderRadius: '50%', background: '#FB923C' }}></span>
        <span aria-hidden="true" className="pointer-events-none absolute hidden md:block" style={{ top: '88%', left: '90%', width: 6, height: 6, borderRadius: '50%', background: '#FB923C', opacity: 0.7 }}></span>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-20 md:mb-28">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" style={{ color: '#0F172A', letterSpacing: '-0.02em' }}>
              Problèmes rencontrés dans le BTP
            </h2>
            <p className="text-lg md:text-xl max-w-3xl mx-auto" style={{ color: '#475569', lineHeight: 1.7 }}>
              Les entreprises du BTP perdent souvent du temps à cause des devis dispersés, des plannings difficiles à suivre, des informations terrain mal transmises et des rapports d'intervention longs à produire.
            </p>
          </div>

          {/* 5 bulles pebbles organiques avec halo de couleur interne offset */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-y-6 sm:gap-y-16 md:gap-y-20 gap-x-4 sm:gap-x-8 lg:gap-x-6 items-start">
            {[
              {
                icon: FileText,
                title: 'Devis dispersés',
                desc: 'Versions multiples, fichiers perdus, suivi commercial flou.',
                iconBg: '#DBEAFE',
                iconRing: 'rgba(96, 165, 250, 0.20)',
                iconColor: '#2563EB',
                haloColor: 'rgba(96, 165, 250, 0.32)',
                haloCx: 110, haloCy: 120, haloR: 170,
                // pebble ovale, large au centre
                path: 'M16,170 C10,80 90,18 195,22 C310,26 354,100 348,200 C342,290 250,326 140,318 C30,310 22,260 16,170 Z',
                offsetY: 'xl:translate-y-0',
                dot: { color: '#2563EB', cx: 306, cy: 286 },
                delay: '0s'
              },
              {
                icon: Calendar,
                title: 'Planning difficile à suivre',
                desc: 'Affectations à la volée, manque de visibilité par équipe.',
                iconBg: '#EDE9FE',
                iconRing: 'rgba(167, 139, 250, 0.22)',
                iconColor: '#7C3AED',
                haloColor: 'rgba(167, 139, 250, 0.30)',
                haloCx: 250, haloCy: 240, haloR: 175,
                // pebble avec bump haut-gauche
                path: 'M22,180 C8,90 88,18 188,30 C300,42 356,108 350,210 C342,308 254,330 144,320 C32,310 34,260 22,180 Z',
                offsetY: 'xl:translate-y-20',
                dot: { color: '#A78BFA', cx: 302, cy: 294 },
                delay: '0.6s'
              },
              {
                icon: MapPin,
                title: 'Infos terrain mal transmises',
                desc: 'Photos, mesures et observations qui restent sur les téléphones.',
                iconBg: '#D1FAE5',
                iconRing: 'rgba(52, 211, 153, 0.22)',
                iconColor: '#10B981',
                haloColor: 'rgba(52, 211, 153, 0.28)',
                haloCx: 130, haloCy: 110, haloR: 180,
                // pebble plus rond
                path: 'M18,150 C28,60 116,16 210,28 C312,42 352,118 342,220 C332,310 240,332 138,320 C28,308 8,238 18,150 Z',
                offsetY: 'xl:translate-y-4',
                dot: { color: '#10B981', cx: 178, cy: 322 },
                delay: '1.2s'
              },
              {
                icon: ClipboardList,
                title: 'Rapports longs à produire',
                desc: 'Comptes-rendus refaits le soir, mise en page chronophage.',
                iconBg: '#FFEDD5',
                iconRing: 'rgba(251, 146, 60, 0.22)',
                iconColor: '#FB923C',
                haloColor: 'rgba(251, 146, 60, 0.30)',
                haloCx: 250, haloCy: 230, haloR: 180,
                // pebble horizontal allongé
                path: 'M10,168 C8,82 100,18 200,24 C308,30 354,98 348,212 C342,296 258,328 146,318 C36,308 16,236 10,168 Z',
                offsetY: 'xl:translate-y-24',
                dot: { color: '#FB923C', cx: 312, cy: 296 },
                delay: '1.8s'
              },
              {
                icon: Wrench,
                title: 'Suivi du matériel',
                desc: 'Outils égarés, retours non tracés, stock mal connu en temps réel.',
                iconBg: '#FEE2E2',
                iconRing: 'rgba(248, 113, 113, 0.22)',
                iconColor: '#EF4444',
                haloColor: 'rgba(248, 113, 113, 0.30)',
                haloCx: 140, haloCy: 220, haloR: 175,
                // pebble vertical doux
                path: 'M20,160 C18,72 110,20 208,30 C310,42 350,112 344,214 C336,300 246,330 142,322 C38,314 22,242 20,160 Z',
                offsetY: 'xl:translate-y-8',
                dot: { color: '#EF4444', cx: 304, cy: 290 },
                delay: '2.4s'
              }
            ].map(({ icon: Icon, title, desc, iconBg, iconRing, iconColor, haloColor, haloCx, haloCy, haloR, path, offsetY, dot, delay }, idx) => {
              const haloId = `problem-halo-${idx}`;
              const shadowId = `problem-shadow-${idx}`;
              return (
                <div
                  key={idx}
                  className={`relative ${offsetY} transition-transform duration-300 hover:-translate-y-0.5`}
                >
                  <div
                    className="problem-blob relative w-full"
                    style={{
                      aspectRatio: '360 / 380',
                      animation: 'problemFloat 7s ease-in-out infinite',
                      animationDelay: delay
                    }}
                  >
                    {/* Forme SVG pebble (fond + halo interne + point coloré) */}
                    <svg
                      className="absolute inset-0 w-full h-full"
                      viewBox="0 0 360 350"
                      preserveAspectRatio="none"
                      aria-hidden="true"
                    >
                      <defs>
                        <radialGradient id={haloId} cx={haloCx} cy={haloCy} r={haloR} gradientUnits="userSpaceOnUse">
                          <stop offset="0%" stopColor={haloColor} />
                          <stop offset="100%" stopColor={haloColor.replace(/[\d.]+\)$/, '0)')} />
                        </radialGradient>
                        <filter id={shadowId} x="-20%" y="-20%" width="140%" height="140%">
                          <feGaussianBlur in="SourceAlpha" stdDeviation="7" />
                          <feOffset dx="0" dy="12" result="offsetblur" />
                          <feComponentTransfer>
                            <feFuncA type="linear" slope="0.14" />
                          </feComponentTransfer>
                          <feMerge>
                            <feMergeNode />
                            <feMergeNode in="SourceGraphic" />
                          </feMerge>
                        </filter>
                      </defs>
                      {/* Fond blanc de la bulle (avec ombre douce) */}
                      <path d={path} fill="rgba(255,255,255,0.96)" stroke="rgba(226, 232, 240, 0.75)" strokeWidth="1" filter={`url(#${shadowId})`} />
                      {/* Halo de couleur interne offset */}
                      <path d={path} fill={`url(#${haloId})`} />
                      {/* Petit point coloré sur le bord */}
                      <circle cx={dot.cx} cy={dot.cy} r="5" fill={dot.color} />
                    </svg>

                    {/* Contenu — centré horizontalement et verticalement dans la zone large du blob */}
                    <div
                      className="absolute inset-0 flex flex-col items-center justify-center text-center"
                      style={{ padding: '14% 12%' }}
                    >
                      {/* Icône circulaire */}
                      <div
                        className="w-10 h-10 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mb-2 sm:mb-4 flex-shrink-0"
                        style={{
                          backgroundColor: iconBg,
                          boxShadow: `0 0 0 6px ${iconRing}`
                        }}
                      >
                        <Icon className="h-4 w-4 sm:h-7 sm:w-7" style={{ color: iconColor }} strokeWidth={2.2} />
                      </div>

                      <h3 className="font-semibold mb-1 sm:mb-2.5 text-[11px] sm:text-base md:text-lg" style={{ color: '#0F172A', lineHeight: 1.3 }}>
                        {title}
                      </h3>
                      <p className="text-[10px] sm:text-sm md:text-[15px]" style={{ color: '#64748B', lineHeight: 1.5 }}>
                        {desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Animation flottante respectant prefers-reduced-motion */}
        <style>{`
          @keyframes problemFloat {
            0%, 100% { transform: translateY(0px); }
            50%      { transform: translateY(-6px); }
          }
          @media (max-width: 639px) {
            .problem-blob { animation: none !important; }
          }
          @media (prefers-reduced-motion: reduce) {
            .problem-blob { animation: none !important; }
          }
        `}</style>
      </section>

      {/* Features Section - Apple Style */}
      <section
        id="features"
        className="py-12 md:py-20"
        style={{
          backgroundImage:
            'linear-gradient(180deg, rgba(37, 99, 235, 0) 0%, rgba(37, 99, 235, 0.04) 50%, rgba(251, 146, 60, 0.05) 100%),' +
            'linear-gradient(180deg, #F9FAFB 0%, #F9FAFB 100%)'
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Tout ce dont vous avez besoin
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto">
              Des fonctionnalités révolutionnaires pensées par et pour les professionnels du terrain
            </p>
          </div>

          <div className="space-y-16 md:space-y-24">
            {FEATURES_SHOWCASE.map((feature, idx) => (
              <FeatureRow key={feature.title} feature={feature} reverse={idx % 2 === 1} />
            ))}
          </div>

          {/* Real-time Stats Section */}
          {stats?.cached && (
            <div className="mb-4 text-xs text-gray-500">Données mises en cache (60s)</div>
          )}
          {isFounder && (
            <div className="mb-12">
              <div className="p-4 rounded-xl border border-indigo-200 bg-indigo-50/60 backdrop-blur-sm">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow">
                      Fondateur — Statistiques {selectedCompany ? 'filtrées entreprise' : 'globales'}
                    </Badge>
                    {selectedCompany && (
                      <Button size="sm" variant="outline" onClick={() => setSelectedCompany(null)}>
                        Vue globale
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <Select value={selectedCompany || ''} onValueChange={(v) => setSelectedCompany(v)}>
                      <SelectTrigger className="w-64">
                        <SelectValue placeholder="Filtrer par entreprise" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-600">
                  {selectedCompany ? 'Affichage limité à cette entreprise. Cache 60s.' : 'Vue globale agrégée. Cache 60s.'}
                </p>
              </div>
            </div>
          )}
          <div className="mt-12 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            <div className="text-center">
              <RealTimeStatsDisplay 
                stats={loading ? "..." : `${stats.total_users}`} 
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" 
              />
              <div className="text-xs md:text-sm text-gray-600">Utilisateurs actifs</div>
            </div>
            <div className="text-center">
              <RealTimeStatsDisplay 
                stats={loading ? "..." : `${Math.floor(stats.total_searches / 1000)}K+`} 
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-2"
                showIndicator={false}
              />
              <div className="text-xs md:text-sm text-gray-600">Recherches traitées</div>
            </div>
            <div className="text-center">
              <RealTimeStatsDisplay 
                stats={loading ? "..." : stats.uptime} 
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-2"
                showIndicator={false}
              />
              <div className="text-xs md:text-sm text-gray-600">Temps de disponibilité</div>
            </div>
            <div className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">24/7</div>
              <div className="text-xs md:text-sm text-gray-600">Support technique</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section - Apple Style */}
      <section id="apropos" className="apropos-section py-12 md:py-20 relative overflow-hidden" style={{
        backgroundImage:
          'radial-gradient(ellipse at 12% 10%, rgba(37, 99, 235, 0.06), transparent 45%),' +
          'radial-gradient(ellipse at 88% 90%, rgba(251, 146, 60, 0.06), transparent 45%),' +
          'linear-gradient(180deg, #FFFFFF 0%, #FBFCFF 100%)'
      }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <div className="flex justify-center mb-4">
              <span className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold" style={{ color: '#2563EB', backgroundColor: 'rgba(37, 99, 235, 0.10)', border: '1px solid rgba(37, 99, 235, 0.20)' }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#2563EB' }} />
                Notre vision
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Pourquoi{' '}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 50%, #FB923C 100%)', WebkitBackgroundClip: 'text' }}>
                SkyApp
              </span>{' '}existe
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              SkyApp est née d'un constat simple : les outils actuels sont souvent trop complexes, trop dispersés, et pas toujours adaptés à la réalité du terrain.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
            <div className="space-y-7 md:space-y-9">
              <div className="apropos-reveal relative pl-5" style={{ opacity: 0, transform: 'translateY(20px)' }}>
                <span aria-hidden="true" className="absolute left-0 top-1 bottom-1 w-1 rounded-full" style={{ background: 'linear-gradient(180deg, #2563EB, #7C3AED)' }} />
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex-shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #EFF4FF 0%, #DBEAFE 100%)', boxShadow: '0 8px 20px -8px rgba(37, 99, 235, 0.45)' }}>
                    <Lightbulb className="h-5 w-5" style={{ color: '#2563EB', width: '22px', height: '22px' }} />
                  </span>
                  <h3 className="text-xl md:text-2xl font-semibold text-gray-900">Notre constat</h3>
                </div>
                <div className="space-y-3 text-sm md:text-base text-gray-600" style={{ lineHeight: 1.75 }}>
                  <p>Sur le terrain, les informations circulent vite, parfois trop vite.</p>
                  <p>Entre les devis, les plannings, les photos et les échanges avec le bureau, il devient facile de perdre du temps ou de passer à côté d'une information importante.</p>
                </div>
              </div>

              <div className="apropos-reveal relative pl-5" style={{ opacity: 0, transform: 'translateY(20px)' }}>
                <span aria-hidden="true" className="absolute left-0 top-1 bottom-1 w-1 rounded-full" style={{ background: 'linear-gradient(180deg, #7C3AED, #FB923C)' }} />
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex-shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F4EFFF 0%, #EDE9FE 100%)', boxShadow: '0 8px 20px -8px rgba(124, 58, 237, 0.45)' }}>
                    <Target className="h-5 w-5" style={{ color: '#7C3AED', width: '22px', height: '22px' }} />
                  </span>
                  <h3 className="text-xl md:text-2xl font-semibold text-gray-900">Notre mission</h3>
                </div>
                <p className="text-sm md:text-base text-gray-600" style={{ lineHeight: 1.75 }}>
                  Notre objectif est simple : proposer une application claire, rapide et vraiment utile au quotidien, pour les équipes terrain comme pour le bureau.
                </p>
              </div>

              {/* Signature terrain */}
              <div className="apropos-reveal apropos-signature flex items-start gap-4 rounded-2xl p-5 md:p-6 transition-all duration-300" style={{
                opacity: 0,
                transform: 'translateY(20px)',
                background: 'rgba(255, 255, 255, 0.85)',
                border: '1px solid rgba(124, 58, 237, 0.16)',
                boxShadow: '0 10px 30px rgba(124, 58, 237, 0.15)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)'
              }}>
                <span className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2563EB, #7C3AED)', boxShadow: '0 8px 18px -6px rgba(124, 58, 237, 0.55)' }}>
                  <Wrench className="h-5 w-5 text-white" />
                </span>
                <div>
                  <p className="text-sm md:text-base font-semibold text-gray-900">Créé avec une logique terrain</p>
                  <p className="text-xs md:text-sm text-gray-600 mt-1" style={{ lineHeight: 1.6 }}>Pas un outil de plus. Un outil pensé pour correspondre à votre façon de travailler.</p>
                </div>
              </div>

              {/* Badges qualitatifs */}
              <div className="apropos-reveal flex flex-wrap gap-3" style={{ opacity: 0, transform: 'translateY(20px)' }}>
                {[
                  { icon: Wrench, label: 'Conçu pour le terrain', color: '#2563EB', bg: 'rgba(37, 99, 235, 0.08)', border: 'rgba(37, 99, 235, 0.18)' },
                  { icon: Clock, label: 'Interface simple & rapide', color: '#7C3AED', bg: 'rgba(124, 58, 237, 0.08)', border: 'rgba(124, 58, 237, 0.18)' },
                  { icon: Users, label: 'Adapté aux équipes BTP', color: '#FB923C', bg: 'rgba(251, 146, 60, 0.10)', border: 'rgba(251, 146, 60, 0.22)' }
                ].map(({ icon: Icon, label, color, bg, border }, i) => (
                  <span key={i} className="apropos-badge inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs md:text-sm font-medium transition-all duration-300" style={{ color, backgroundColor: bg, border: `1px solid ${border}` }}>
                    <Icon className="h-4 w-4" style={{ color }} />
                    {label}
                  </span>
                ))}
              </div>
            </div>

            <div className="apropos-reveal apropos-values relative rounded-3xl p-7 md:p-9 lg:sticky lg:top-24 overflow-hidden" style={{
              opacity: 0,
              transform: 'translateY(20px)',
              background: 'linear-gradient(160deg, rgba(255, 255, 255, 0.96) 0%, rgba(248, 250, 255, 0.96) 100%)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              boxShadow: '0 40px 90px -34px rgba(15, 23, 42, 0.32)'
            }}>
              <span aria-hidden="true" className="absolute -top-16 -right-16 w-48 h-48 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(124, 58, 237, 0.12), transparent 70%)', filter: 'blur(10px)' }} />
              <h3 className="relative flex items-center gap-2.5 text-xl md:text-2xl font-semibold text-gray-900 mb-5 md:mb-6">
                <span className="w-1.5 h-6 rounded-full" style={{ background: 'linear-gradient(180deg, #2563EB, #FB923C)' }} />
                Nos valeurs
              </h3>
              <div className="relative space-y-3 md:space-y-4">
                {[
                  { icon: Lightbulb, color: '#2563EB', bg: '#DBEAFE', title: "Simplicité d'usage", desc: 'Une interface claire, pensée pour aller vite.' },
                  { icon: Wrench, color: '#10B981', bg: '#D1FAE5', title: 'Logique terrain', desc: 'Des fonctionnalités utiles, sans complexité inutile.' },
                  { icon: Lock, color: '#7C3AED', bg: '#EDE9FE', title: 'Données sécurisées', desc: 'Une base fiable pour centraliser les informations.' },
                  { icon: Headphones, color: '#FB923C', bg: '#FFEDD5', title: 'Support réactif', desc: 'Un accompagnement humain pour avancer sereinement.' }
                ].map(({ icon: Icon, color, bg, title, desc }, i) => (
                  <div
                    key={i}
                    className="apropos-value-row flex items-start gap-3 md:gap-4 rounded-2xl p-3 value-item transition-all duration-300"
                    style={{ opacity: 0, transform: 'translateY(20px)' }}
                  >
                    <span className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: bg }}>
                      <Icon className="h-5 w-5" style={{ color }} />
                    </span>
                    <div>
                      <h4 className="font-semibold text-sm md:text-base text-gray-900">{title}</h4>
                      <p className="text-xs md:text-sm text-gray-600">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <style>{`
          .apropos-reveal {
            transition: opacity 0.6s cubic-bezier(0.22, 1, 0.36, 1), transform 0.6s cubic-bezier(0.22, 1, 0.36, 1);
          }
          .apropos-reveal.animate {
            opacity: 1 !important;
            transform: translateY(0) !important;
          }
          .apropos-value-row:hover {
            background: #FFFFFF;
            box-shadow: inset 0 0 0 1px rgba(226, 232, 240, 0.9), 0 12px 26px -16px rgba(15, 23, 42, 0.20);
            transform: translateY(-2px) !important;
          }
          .apropos-signature:hover {
            background: #FFFFFF;
            box-shadow: 0 24px 50px -26px rgba(124, 58, 237, 0.45);
            transform: translateY(-2px);
          }
          .apropos-badge:hover {
            transform: translateY(-2px) scale(1.03);
            box-shadow: 0 10px 22px -12px rgba(15, 23, 42, 0.28);
          }
          @media (prefers-reduced-motion: reduce) {
            .apropos-reveal { transition: none; opacity: 1 !important; transform: none !important; }
          }
        `}</style>
      </section>

      {/* Testimonials Section - Apple Style */}
      <section id="testimonials" className="py-12 md:py-20 relative overflow-hidden" style={{
        backgroundImage:
          'radial-gradient(circle at 15% 20%, rgba(96, 165, 250, 0.12), transparent 35%),' +
          'radial-gradient(circle at 82% 80%, rgba(251, 146, 60, 0.10), transparent 35%),' +
          'radial-gradient(circle at 55% 50%, rgba(168, 85, 247, 0.07), transparent 40%),' +
          'linear-gradient(135deg, #F8FAFC 0%, #EEF4FF 55%, #FFF7ED 100%)'
      }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Une seule plateforme pour toute votre équipe
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-2xl mx-auto">
              De la planification à la facturation, tout est centralisé pour vos équipes terrain et bureau.
            </p>
            <div className="flex justify-center mt-5">
              <span className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs md:text-sm font-medium" style={{ color: '#7C3AED', backgroundColor: 'rgba(124, 58, 237, 0.08)', border: '1px solid rgba(124, 58, 237, 0.18)' }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: '#7C3AED' }} />
                Testé en conditions réelles sur le terrain
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="testimonial-card group flex flex-col rounded-2xl p-7 md:p-8"
                style={{
                  opacity: 0,
                  transform: 'translateY(20px)',
                  background: '#FFFFFF',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  boxShadow: '0 14px 40px -28px rgba(15, 23, 42, 0.30)',
                  transition: 'all 0.3s ease',
                  transitionDelay: `${index * 0.1}s`
                }}
              >
                <span className="inline-flex self-start items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium mb-6" style={{ color: '#2563EB', backgroundColor: 'rgba(37, 99, 235, 0.07)', border: '1px solid rgba(37, 99, 235, 0.16)' }}>
                  <CheckCircle className="h-3.5 w-3.5" />
                  {testimonial.badge}
                </span>

                <blockquote className="text-gray-700 mb-8 flex-1" style={{ lineHeight: 1.8 }}>
                  «&nbsp;{testimonial.content}&nbsp;»
                </blockquote>

                <div className="flex items-center space-x-4 pt-5" style={{ borderTop: '1px solid rgba(0, 0, 0, 0.05)' }}>
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold" style={{ background: testimonial.grad }}>
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900 leading-tight">{testimonial.name}</div>
                    <div className="text-sm text-gray-600 leading-tight">{testimonial.role}</div>
                    <div className="text-sm text-gray-500 leading-tight">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <style>{`
          .testimonial-card.animate {
            opacity: 1 !important;
            transform: translateY(0) !important;
          }
          .testimonial-card:hover {
            transform: translateY(-4px) !important;
            box-shadow: 0 28px 60px -28px rgba(15, 23, 42, 0.30);
            border-color: rgba(37, 99, 235, 0.18);
          }
          @media (prefers-reduced-motion: reduce) {
            .testimonial-card { opacity: 1 !important; transform: none !important; }
          }
        `}</style>
      </section>

      {/* Pricing Section - Animated Background */}
      <section id="tarifs" className="py-12 md:py-20 relative overflow-hidden">
        {/* Animated Cursor Background */}
        <div className="absolute inset-0 pricing-bg-container">
          <div className="pricing-cursor-effect"></div>
          <div className="pricing-grid-overlay"></div>
        </div>

        <div className="pricing-container max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 futuristic-text">
              Tarifs
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto pricing-text">
              Des formules adaptées à chaque besoin, avec un support technique inclus dans toutes les formules
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Plan Essentiel */}
            <div className="pricing-card bg-white/90 backdrop-blur-sm rounded-3xl p-6 md:p-8 border-2 relative overflow-hidden transition-all duration-300" style={{ borderColor: 'rgba(37, 99, 235, 0.25)' }}>
              <div className="absolute top-0 left-0 w-full h-1" style={{ background: 'linear-gradient(90deg, #2563EB, #60A5FA)' }}></div>
              
              <div className="text-center">
                <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2 font-space-grotesk">Essentiel</h3>
                <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">Pour démarrer avec une petite équipe</p>
                <div className="mb-6 md:mb-8">
                  <span className="text-4xl md:text-5xl font-bold price-bounce" style={{ color: '#2563EB' }}>39€</span>
                  <span className="text-gray-600 ml-1">HT/mois</span>
                  <p className="text-xs md:text-sm text-gray-500 mt-2">390€ HT/an (2 mois offerts)</p>
                </div>
              </div>
              
              <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                {['3 utilisateurs inclus', 'Gestion des clients et chantiers', 'Création de devis et factures', 'Planning et gestion des missions', 'Comptes rendus avec photos et signatures', 'Génération des rapports PDF', 'Support par e-mail'].map((f, i) => (
                  <li key={i} className="flex items-center text-sm md:text-base text-gray-600 pricing-feature">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center mr-3 flex-shrink-0" style={{ background: 'rgba(37, 99, 235, 0.12)' }}>
                      <Check className="h-3 w-3" style={{ color: '#2563EB' }} />
                    </div>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-400 text-center mb-4 md:mb-6">+7€ HT/mois par utilisateur supplémentaire</p>
              
              <button 
                onClick={() => setShowRegisterModal(true)}
                className="w-full text-white font-medium py-4 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-2xl"
                style={{ background: 'linear-gradient(90deg, #2563EB, #60A5FA)', boxShadow: '0 12px 28px -10px rgba(37, 99, 235, 0.45)' }}
              >
                Commencer
              </button>
            </div>

            {/* Plan Équipe - Recommandé */}
            <div className="pricing-card bg-white/90 backdrop-blur-sm rounded-3xl p-6 md:p-8 border-2 relative overflow-hidden lg:transform lg:scale-105 shadow-2xl lg:mt-0" style={{ borderColor: 'rgba(124, 58, 237, 0.55)' }}>
              <div className="absolute top-0 left-0 w-full h-1" style={{ background: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 48%, #FB923C 100%)' }}></div>
              <div className="absolute -top-5 md:-top-6 left-1/2 transform -translate-x-1/2">
                <span className="text-white px-4 md:px-6 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium shadow-xl" style={{ background: 'linear-gradient(90deg, #2563EB, #7C3AED)' }}>
                  ⭐ Recommandé
                </span>
              </div>
              
              <div className="text-center mt-4 md:mt-6">
                <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2 font-space-grotesk">Équipe</h3>
                <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">Pour connecter le terrain et le bureau</p>
                <div className="mb-6 md:mb-8">
                  <span className="text-4xl md:text-5xl font-bold price-bounce bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg, #2563EB, #7C3AED)', WebkitBackgroundClip: 'text' }}>69€</span>
                  <span className="text-gray-600 ml-1">HT/mois</span>
                  <p className="text-xs md:text-sm text-gray-500 mt-2">690€ HT/an (2 mois offerts)</p>
                </div>
              </div>
              
              <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                {['8 utilisateurs inclus', 'Toutes les fonctionnalités Essentiel', 'Rôles ADMIN, BUREAU et TECHNICIEN', 'Planning partagé semaine et mois', 'Gestion de l\u2019inventaire et du matériel', 'Identification du matériel par QR code', 'Statistiques et tableaux de bord', 'Support prioritaire'].map((f, i) => (
                  <li key={i} className="flex items-center text-sm md:text-base text-gray-600 pricing-feature">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center mr-3 flex-shrink-0" style={{ background: 'rgba(124, 58, 237, 0.12)' }}>
                      <Check className="h-3 w-3" style={{ color: '#7C3AED' }} />
                    </div>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-400 text-center mb-4 md:mb-6">+7€ HT/mois par utilisateur supplémentaire</p>
              
              <button 
                onClick={() => setShowRegisterModal(true)}
                className="w-full text-white font-medium py-4 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-2xl"
                style={{ background: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 48%, #FB923C 100%)', boxShadow: '0 14px 32px -12px rgba(124, 58, 237, 0.6)' }}
              >
                Commencer maintenant
              </button>
            </div>

            {/* Plan Entreprise */}
            <div className="pricing-card bg-white/90 backdrop-blur-sm rounded-3xl p-6 md:p-8 border-2 relative overflow-hidden transition-all duration-300" style={{ borderColor: 'rgba(251, 146, 60, 0.30)' }}>
              <div className="absolute top-0 left-0 w-full h-1" style={{ background: 'linear-gradient(90deg, #7C3AED, #FB923C)' }}></div>
              
              <div className="text-center">
                <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2 font-space-grotesk">Entreprise</h3>
                <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">Pour piloter plusieurs équipes</p>
                <div className="mb-6 md:mb-8">
                  <span className="text-4xl md:text-5xl font-bold text-gray-900 price-bounce">99€</span>
                  <span className="text-gray-600 ml-1">HT/mois</span>
                  <p className="text-xs md:text-sm text-gray-500 mt-2">990€ HT/an (2 mois offerts)</p>
                </div>
              </div>
              
              <ul className="space-y-3 md:space-y-4 mb-6 md:mb-8">
                {['15 utilisateurs inclus', 'Toutes les fonctionnalités Équipe', 'Configuration personnalisée du compte', 'Assistance pour importer les données', 'Formation de prise en main', 'Accompagnement au déploiement', 'Support premium prioritaire'].map((f, i) => (
                  <li key={i} className="flex items-center text-sm md:text-base text-gray-600 pricing-feature">
                    <div className="w-5 h-5 rounded-full flex items-center justify-center mr-3 flex-shrink-0" style={{ background: 'rgba(251, 146, 60, 0.12)' }}>
                      <Check className="h-3 w-3" style={{ color: '#FB923C' }} />
                    </div>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-400 text-center mb-4 md:mb-6">Tarif personnalisé au-delà de 15 utilisateurs</p>
              
              <button
                onClick={() => setShowRegisterModal(true)}
                className="w-full text-white font-medium py-4 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                style={{ background: 'linear-gradient(90deg, #7C3AED, #FB923C)', boxShadow: '0 12px 28px -10px rgba(251, 146, 60, 0.45)' }}
              >
                Nous contacter
              </button>
            </div>
          </div>

          {/* Option Intelligence artificielle - encart commun (expansion inline) */}
          <div className="mt-10 md:mt-14">
            <div className="max-w-3xl mx-auto rounded-2xl border-2 overflow-hidden transition-all duration-300" style={{ borderColor: 'rgba(124, 58, 237, 0.25)' }}>
              <button
                type="button"
                onClick={() => setShowAiPacksModal(prev => !prev)}
                className="w-full bg-white/90 backdrop-blur-sm p-6 md:p-8 text-center transition-all duration-300 hover:bg-white cursor-pointer"
              >
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #2563EB, #7C3AED)' }}>
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 font-space-grotesk">Option Intelligence artificielle</h3>
                <p className="text-sm md:text-base text-gray-600 max-w-xl mx-auto">
                  Disponible avec toutes les offres à partir de <span className="font-semibold text-gray-900">9€ HT/mois</span>. Utilisable uniquement par les rôles ADMIN et BUREAU. Packs de 100, 300 ou 1 000 crédits.
                </p>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium mt-4" style={{ color: '#7C3AED' }}>
                  {showAiPacksModal ? 'Masquer le détail des packs' : 'Voir le détail des packs'}
                  <ChevronDown className="h-4 w-4 transition-transform duration-300" style={{ transform: showAiPacksModal ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                </span>
              </button>

              {/* Panneau de détail animé (accordion) */}
              <div
                className="ai-packs-panel"
                style={{
                  maxHeight: showAiPacksModal ? '900px' : '0px',
                  opacity: showAiPacksModal ? 1 : 0,
                  transition: 'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.35s ease',
                  overflow: 'hidden',
                  background: 'linear-gradient(180deg, rgba(124,58,237,0.03), rgba(37,99,235,0.03))',
                }}
              >
                <div className="p-6 md:p-8 pt-2 border-t" style={{ borderColor: 'rgba(124, 58, 237, 0.15)' }}>
                  <div className="space-y-3 mt-4">
                    {[
                      { name: 'IA 100', price: '9€', credits: '100 crédits', desc: 'Pour un usage occasionnel de l\'IA' },
                      { name: 'IA 300', price: '19€', credits: '300 crédits', desc: 'Pour un usage régulier' },
                      { name: 'IA 1 000', price: '39€', credits: '1 000 crédits', desc: 'Pour un usage intensif' },
                      { name: 'Recharge', price: '9€', credits: '100 crédits supplémentaires', desc: 'À ajouter à tout moment sur votre pack actif' },
                    ].map((pack, i) => (
                      <div
                        key={i}
                        className="ai-pack-row flex items-center justify-between gap-4 p-4 rounded-2xl border-2 bg-white transition-all duration-300 hover:-translate-y-0.5"
                        style={{
                          borderColor: 'rgba(124, 58, 237, 0.15)',
                          transitionDelay: showAiPacksModal ? `${i * 60}ms` : '0ms',
                          opacity: showAiPacksModal ? 1 : 0,
                          transform: showAiPacksModal ? 'translateY(0)' : 'translateY(8px)',
                        }}
                      >
                        <div>
                          <h4 className="text-base md:text-lg font-semibold text-gray-900 font-space-grotesk">{pack.name}</h4>
                          <p className="text-xs md:text-sm text-gray-500">{pack.desc}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div>
                            <span className="text-xl md:text-2xl font-bold bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg, #2563EB, #7C3AED)', WebkitBackgroundClip: 'text' }}>{pack.price}</span>
                            <span className="text-gray-500 text-xs ml-1">HT/mois</span>
                          </div>
                          <p className="text-xs text-gray-400">{pack.credits}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="text-xs text-gray-400 text-center mt-5">
                    Les crédits sont consommés à l'utilisation des fonctionnalités IA. Une recharge peut être ajoutée à tout moment sans changer de pack.
                  </p>

                  <button
                    onClick={() => setShowRegisterModal(true)}
                    className="w-full text-white font-medium py-3.5 rounded-2xl mt-6 transition-all duration-300 transform hover:scale-[1.02] shadow-lg"
                    style={{ background: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 100%)' }}
                  >
                    Commencer
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div className="mt-16 md:mt-20" style={{
            backgroundImage: 'radial-gradient(circle at 50% 0%, rgba(124, 58, 237, 0.08), transparent 60%)'
          }}>
            <h3 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-8 md:mb-12 text-center futuristic-text">Questions fréquentes</h3>
            <FaqAccordion />

            {/* CTA sous FAQ */}
            <div className="mt-10 md:mt-14 text-center">
              <p className="text-base md:text-lg text-gray-600 mb-5">Une question ? Notre équipe vous répond rapidement.</p>
              <button
                type="button"
                onClick={() => setShowRegisterModal(true)}
                className="faq-cta-btn inline-flex items-center justify-center gap-2 text-white font-semibold text-base md:text-lg px-7 md:px-8 py-3.5 md:py-4 rounded-full transition-all duration-300"
                style={{
                  background: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 48%, #FB923C 100%)',
                  boxShadow: '0 18px 40px -16px rgba(124, 58, 237, 0.5)'
                }}
              >
                Demander une démo
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <style>{`
          .faq-cta-btn:hover {
            transform: scale(1.05) translateY(-2px);
            box-shadow: 0 26px 56px -18px rgba(124, 58, 237, 0.65);
          }
          @media (prefers-reduced-motion: reduce) {
            .faq-cta-btn:hover { transform: none; }
          }
        `}</style>
      </section>

      {/* CTA Section - Apple Style */}
      <section id="cta" className="py-12 md:py-20 relative overflow-hidden" style={{
        backgroundImage:
          'radial-gradient(circle at 20% 30%, rgba(37, 99, 235, 0.15), transparent 40%),' +
          'radial-gradient(circle at 80% 70%, rgba(124, 58, 237, 0.15), transparent 40%),' +
          'linear-gradient(180deg, #0B1220 0%, #0B1220 100%)'
      }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
            Simplifiez enfin la gestion de vos chantiers
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-gray-300 mb-8 md:mb-10 max-w-2xl mx-auto">
            Planning, devis, rapports… tout est centralisé dans un seul outil, pensé pour vos équipes terrain et bureau.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowRegisterModal(true)}
              className="cta-final-btn text-white font-semibold text-lg px-8 py-4 rounded-full transition-all duration-300 flex items-center justify-center gap-2"
              style={{
                background: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 48%, #FB923C 100%)',
                boxShadow: '0 18px 40px -14px rgba(124, 58, 237, 0.55)'
              }}
            >
              Essayer gratuitement
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 text-xs md:text-sm text-gray-400 mt-6 md:mt-8">
            <div className="flex items-center space-x-2">
              <Check className="h-4 w-4" style={{ color: '#34D399' }} />
              <span>14 jours gratuits</span>
            </div>
            <div className="flex items-center space-x-2">
              <Shield className="h-4 w-4" style={{ color: '#34D399' }} />
              <span>Sans engagement</span>
            </div>
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-4 w-4" style={{ color: '#34D399' }} />
              <span>Aucune carte bancaire requise</span>
            </div>
          </div>
        </div>

        <style>{`
          .cta-final-btn:hover {
            transform: scale(1.05) translateY(-2px);
            box-shadow: 0 26px 56px -16px rgba(124, 58, 237, 0.7);
          }
          @media (prefers-reduced-motion: reduce) {
            .cta-final-btn:hover { transform: none; }
          }
        `}</style>
      </section>

      {/* Section Journal des mises à jour */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 border border-gray-200 rounded-2xl p-8 md:p-12 shadow-sm">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <History className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              SkyApp évolue chaque semaine
            </h3>
            <p className="text-gray-500 text-base md:text-lg mb-6 max-w-xl mx-auto">
              Consultez les dernières nouveautés, améliorations et corrections apportées à la plateforme.
            </p>
            <a
              href="/changelog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white font-semibold rounded-xl hover:bg-gray-800 transition-all shadow-sm hover:shadow-md"
            >
              <History className="h-4 w-4" />
              Voir les mises à jour
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section - Simple */}
      <section id="contact" className="py-20 md:py-32 relative overflow-hidden text-white" style={{
        backgroundImage:
          'radial-gradient(circle at 20% 30%, rgba(37, 99, 235, 0.15), transparent 40%),' +
          'radial-gradient(circle at 80% 70%, rgba(124, 58, 237, 0.15), transparent 40%),' +
          'linear-gradient(180deg, #0B1220 0%, #0B1220 100%)'
      }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Contactez-nous
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 mb-12 md:mb-16">
            Notre équipe est à votre écoute
          </p>

          <div className="max-w-lg mx-auto">
            <div className="contact-card rounded-3xl p-8 md:p-10 transition-all duration-300" style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.10)'
            }}>
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ background: 'linear-gradient(135deg, #2563EB, #7C3AED)' }}>
                <Headphones className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-semibold mb-2">Envie de voir SkyApp en action&nbsp;?</h3>
              <p className="text-gray-300 text-sm md:text-base mb-6 max-w-sm mx-auto">
                Écrivez-nous directement, nous vous répondons sous 24h.
              </p>

              {/* Formulaire de contact */}
              {showContactPopup ? (
                <div className="space-y-3 text-left">
                  <input
                    type="text"
                    placeholder="Votre nom"
                    value={contactForm.name}
                    onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  <input
                    type="email"
                    placeholder="Votre email"
                    value={contactForm.email}
                    onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  <textarea
                    rows={3}
                    placeholder="Votre message..."
                    value={contactForm.message}
                    onChange={e => setContactForm(p => ({ ...p, message: e.target.value }))}
                    className="w-full px-4 py-2.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none"
                  />
                  {contactError && <p className="text-red-400 text-xs">{contactError}</p>}
                  <div className="flex gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => { setShowContactPopup(false); setContactForm({ name: '', email: '', message: '' }); setContactError(''); setContactSent(false); }}
                      className="flex-1 py-2.5 rounded-xl border border-white/20 text-gray-300 text-sm hover:bg-white/10 transition-colors"
                    >Annuler</button>
                    <button
                      type="button"
                      disabled={contactSending}
                      onClick={async () => {
                        setContactError('');
                        if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
                          setContactError('Veuillez remplir tous les champs');
                          return;
                        }
                        try {
                          setContactSending(true);
                          const BACKEND = (window._env_?.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001/api').replace(/\/api$/, '');
                          await fetch(`${BACKEND}/api/contact`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(contactForm),
                          }).then(r => { if (!r.ok) throw new Error(); });
                          setContactSent(true);
                          setContactForm({ name: '', email: '', message: '' });
                        } catch {
                          setContactError("Erreur d'envoi, réessayez ou écrivez à contact@skyapp.fr");
                        } finally {
                          setContactSending(false);
                        }
                      }}
                      className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-60 transition-all"
                      style={{ background: 'linear-gradient(90deg,#2563EB,#7C3AED)' }}
                    >{contactSending ? 'Envoi...' : 'Envoyer'}</button>
                  </div>
                  {contactSent && <p className="text-green-400 text-sm text-center pt-1">✓ Message envoyé ! Nous vous répondrons sous 24h.</p>}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowContactPopup(true)}
                  className="cta-final-btn inline-flex items-center justify-center gap-2 text-white font-semibold text-base md:text-lg px-7 py-3.5 rounded-full transition-all duration-300"
                  style={{
                    background: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 48%, #FB923C 100%)',
                    boxShadow: '0 18px 40px -14px rgba(124, 58, 237, 0.55)'
                  }}
                >
                  Prendre contact
                  <ArrowRight className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        <style>{`
          .contact-card:hover {
            transform: translateY(-4px) scale(1.01);
            box-shadow: 0 30px 70px -30px rgba(124, 58, 237, 0.45);
            border-color: rgba(255, 255, 255, 0.20);
          }
          @media (prefers-reduced-motion: reduce) {
            .contact-card:hover { transform: none; }
          }
        `}</style>
      </section>

      {/* Footer légal */}
      <footer className="bg-[#07101E] border-t border-white/5 py-6 px-4">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <span>© {new Date().getFullYear()} SkyApp — Tous droits réservés</span>
          <div className="flex items-center gap-5">
            <a href="/mentions-legales" className="hover:text-gray-300 transition-colors">Mentions légales</a>
            <span className="text-gray-700">·</span>
            <a href="/cgu" className="hover:text-gray-300 transition-colors">CGU</a>
            <span className="text-gray-700">·</span>
            <a href="/cgv" className="hover:text-gray-300 transition-colors">CGV</a>
          </div>
        </div>
      </footer>

      {/* Login Modal */}
      <LoginModal 
        open={showLoginModal} 
        onClose={() => setShowLoginModal(false)}
      />

      {/* Register Modal */}
      <RegisterModal 
        open={showRegisterModal} 
        onClose={() => setShowRegisterModal(false)}
      />

      {/* User Profile Modal */}
      <UserProfileModal 
        user={user}
        isOpen={showUserProfile}
        onClose={() => setShowUserProfile(false)}
        onUpdate={(updatedUser) => setUser(updatedUser)}
      />

    </div>
  );
};

// Register Modal Component
const RegisterModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    companyName: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      setLoading(false);
      return;
    }

    const passwordError = validatePasswordStrength(formData.password);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    try {
      const response = await http.post(`${API}/auth/register`, {
        company_name: formData.companyName,
        email: formData.email,
        nom: formData.lastName,
        prenom: formData.firstName,
        password: formData.password
      });

      const { access_token, user, requires_email_verification, message, email_sent, email_debug } = response.data;
      
      if (access_token) {
        // Compte actif → connexion directe
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(user));
        recordUserActivity(user.id);
        onClose();
        window.location.href = '/role-selection';
      } else if (requires_email_verification) {
        // Vérification email requise → page dédiée (jamais de connexion automatique)
        sessionStorage.setItem('pendingVerificationEmail', formData.email);
        onClose();
        navigate('/auth/verifier-email', { state: { justRegistered: true } });
        if (!email_sent) {
          toast.error(`L'email n'a pas pu être envoyé automatiquement (${email_debug || 'erreur inconnue'}). Utilisez le bouton "Renvoyer l'e-mail" sur la page suivante.`);
        }
      } else {
        setSuccessMessage(message || `Compte créé ! Vous pouvez maintenant vous connecter.`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Inscription</DialogTitle>
          <DialogDescription>
            Créez votre compte SearchApp
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              name="firstName"
              placeholder="Prénom"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
            <Input
              name="lastName"
              placeholder="Nom"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
          </div>
          
          <Input
            name="email"
            type="email"
            placeholder="Adresse email"
            value={formData.email}
            onChange={handleChange}
            required
          />

          <Input
            name="companyName"
            placeholder="Nom de l'entreprise"
            value={formData.companyName}
            onChange={handleChange}
            required
          />

          <Input
            name="password"
            type="password"
            placeholder="Mot de passe"
            value={formData.password}
            onChange={handleChange}
            required
            minLength={PASSWORD_MIN_LENGTH}
          />
          <PasswordStrengthMeter password={formData.password} />

          <Input
            name="confirmPassword"
            type="password"
            placeholder="Confirmer le mot de passe"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
          />

          {error && (
            <div className="text-red-500 text-sm">{error}</div>
          )}

          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <div className="flex justify-center mb-2">
                <Mail className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-green-800 font-medium text-sm">{successMessage}</p>
              <p className="text-green-600 text-xs mt-2">Pensez à vérifier vos spams si vous ne trouvez pas l'email.</p>
              <Button onClick={onClose} variant="outline" className="mt-3 w-full">
                Fermer
              </Button>
            </div>
          )}

          {!successMessage && (
            <Button type="submit" disabled={loading} className="w-full">
              {loading ? 'Création du compte...' : 'Créer mon compte'}
            </Button>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Apple-style Role Selection with Back Button
const RoleSelection = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const founderEmail = FOUNDER_EMAIL;
  const isFounder = (user?.is_fondateur === true) || ((user?.email || '').toLowerCase() === founderEmail);
  const isAdmin = isFounder || (user?.role === 'ADMIN');

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsed = JSON.parse(userData);
      setUser(parsed);
    }
  }, []);

  const leaveCompany = async () => {
    if (!confirm("Voulez-vous vraiment quitter votre entreprise ?")) return;
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      const res = await http.post(`${API}/auth/leave-company`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data?.user) {
        localStorage.setItem('user', JSON.stringify(res.data.user));
        setUser(res.data.user);
      }
    } catch (e) {
      alert(`Erreur lors de la sortie: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRoleSelect = (role) => {
    switch(role) {
      case 'technicien':
        navigate('/technicien');
        break;
      case 'bureau':
        navigate('/bureau');
        break;
      case 'statistiques':
        navigate('/statistiques');
        break;
      case 'fondateur':
        navigate('/fondateur');
        break;
      case 'admin':
        navigate('/admin');
        break;
      default:
        navigate('/');
    }
  };

  const handleLogout = () => {
    broadcastLogout('user_action');
    forceLogout('user_action');
  };

  const goBack = () => {
    navigate('/');
  };

  const goToStatistics = () => {
    navigate('/statistiques');
  };

  return (
    <div className="min-h-screen skyapp-page-bg flex flex-col">
      {/* Apple-style Navigation with Back Button */}
      <nav className="bg-white/70 backdrop-blur border-b border-white/60">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={goBack}
                className="mr-2 sm:mr-4 p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              >
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
              <img 
                src="/Logo skyapp v.2.png" 
                alt="SkyApp Logo" 
                className="w-28 h-28 sm:w-28 sm:h-28 md:w-24 md:h-24 lg:w-28 lg:h-28 rounded-lg object-cover logo-neon-effect"
              />
            </div>
            
            <div className="flex items-center space-x-4">
              <Button
                onClick={goToStatistics}
                variant="outline"
                className="text-gray-900 border-gray-300 hover:bg-gray-50"
              >
                Statistiques
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="text-gray-500 hover:text-gray-700 p-2"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md space-y-8">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl md:text-4xl font-extrabold mb-2 bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 50%, #FB923C 100%)' }}>
              Choisissez votre interface
            </h1>
            <p className="text-lg text-gray-600">Sélectionnez l'interface adaptée à votre rôle</p>
          </div>

          {/* Role Cards */}
          <div className="space-y-4">
            {/* Badge Fondateur */}
            {user?.is_fondateur && (
              <div className="mb-6 p-4 bg-gradient-to-r from-gray-900 to-black border-2 border-gray-800 rounded-2xl shadow-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border-2 border-gray-300">
                    <Shield className="h-6 w-6 text-black" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white flex items-center gap-2">
                      FONDATEUR SkyApp
                      <span className="px-2 py-0.5 text-xs bg-white text-black rounded-full font-bold">Privilèges Maximum</span>
                    </h4>
                    <p className="text-sm text-gray-300">Accès complet à tout le système multi-tenant</p>
                  </div>
                </div>
              </div>
            )}

            {/* Badge Accès Admin */}
            {isAdmin && !user?.is_fondateur && (
              <div className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-300 rounded-2xl">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Accès Admin</h4>
                    <p className="text-sm text-gray-600">Tous les privilèges administrateur disponibles</p>
                  </div>
                </div>
              </div>
            )}

            {/* Badge Accès Bureau */}
            {user?.role === 'BUREAU' && !isAdmin && (
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-300 rounded-2xl">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                    <Briefcase className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900">Accès Bureau</h4>
                    <p className="text-sm text-blue-700">Gestion administrative et planning</p>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={() => handleRoleSelect('technicien')}
              className="w-full bg-white/90 backdrop-blur border border-white/70 rounded-2xl p-6 text-left hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 group shadow-lg"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-violet-600 rounded-xl flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
                    <Search className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Technicien</h3>
                    <p className="text-sm text-gray-600">Interface terrain - Recherches et partage</p>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
              </div>
            </button>

            {(isAdmin || user?.role === 'BUREAU') && (
              <button
                onClick={() => handleRoleSelect('bureau')}
                className="w-full bg-white/90 backdrop-blur border border-white/70 rounded-2xl p-6 text-left hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 group shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-violet-600 rounded-xl flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
                      <Briefcase className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Bureau</h3>
                      <p className="text-sm text-gray-600">Interface administrative - Rapports et gestion</p>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                </div>
              </button>
            )}

            {isAdmin && (
              <button
                onClick={() => handleRoleSelect('statistiques')}
                className="w-full bg-white/90 backdrop-blur border border-white/70 rounded-2xl p-6 text-left hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 group shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
                      <BarChart3 className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Statistiques</h3>
                      <p className="text-sm text-gray-600">Interface analytique - Métriques et analyses</p>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-orange-500 group-hover:translate-x-1 transition-all" />
                </div>
              </button>
            )}

            {/* Allow any user to leave company at any time */}
            {user && user.company_id && (
              <div className="pt-2">
                <Button disabled={actionLoading} onClick={leaveCompany} variant="ghost" className="text-red-600 hover:text-red-700">Quitter mon entreprise</Button>
              </div>
            )}

            {/* Admin Panel réservé au Fondateur */}
            {isFounder && (
              <button
                onClick={() => {
                  navigate('/admin');
                }}
                className="w-full bg-gradient-to-r from-gray-800 to-gray-900 border-0 rounded-2xl p-6 text-left hover:from-gray-700 hover:to-gray-800 transition-all duration-200 group shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Shield className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Administration Fondateur</h3>
                      <p className="text-sm text-gray-300">Gestion globale du système et des utilisateurs</p>
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-white" />
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Menu de sélection du type de recherche
const SearchTypeSelector = ({ onSelect, onCancel }) => {
  const [pendingReportsCount, setPendingReportsCount] = useState(0);

  useEffect(() => {
    const fetchPendingReports = async () => {
      if (!localStorage.getItem('token')) return;
      try {
        const token = localStorage.getItem('token');
        // Récupérer les missions sans rapport
        const response = await http.get(`${API}/planning/my-missions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const missions = response.data || [];
        
        // Récupérer les comptes-rendus existants (le backend renvoie ceux du
        // technicien connecté) pour ne compter que les missions SANS rapport
        const reportsResponse = await http.get(`${API}/mission-reports`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const reports = Array.isArray(reportsResponse.data) ? reportsResponse.data : [];
        
        // Compte les missions éligibles (terminées / en cours / en pause / partielles)
        // sans rapport existant. Règle alignée sur le sélecteur de chantier :
        // tout technicien assigné (chef d'équipe OU collaborateur) peut créer un
        // compte-rendu — /planning/my-missions ne renvoie déjà que les missions
        // assignées à l'utilisateur connecté.
        const eligibleStatuses = new Set(['completed', 'in_progress', 'paused', 'partially_completed']);
        const missionsWithReports = new Set(reports.map(r => r.mission_id).filter(Boolean));
        const missionsWithoutReports = missions.filter(m =>
          eligibleStatuses.has((m.status || '').toLowerCase())
          && !missionsWithReports.has(m.id)
        );
        
        setPendingReportsCount(missionsWithoutReports.length);
      } catch (error) {
        console.error('Erreur chargement compteur rapports:', error);
        setPendingReportsCount(0);
      }
    };
    
    fetchPendingReports();
  }, []);

  const searchTypes = [
    {
      id: 'rapport',
      title: 'Compte-Rendu Chantier',
      description: 'Créer un rapport d\'intervention pour une mission planifiée',
      icon: ClipboardList,
      gradient: 'from-blue-600 to-violet-600',
      softBg: 'from-blue-50 to-violet-50/40',
      hoverBorder: 'hover:border-blue-400',
      accentText: 'group-hover:text-blue-600',
      badgeCount: pendingReportsCount,
      badgeLabel: pendingReportsCount === 1 ? 'compte-rendu à faire' : 'comptes-rendus à faire'
    },
    {
      id: 'infiltration',
      title: "Recherche d'Infiltration",
      description: 'Test et détection des infiltrations d\'eau',
      icon: Layers,
      gradient: 'from-orange-400 to-orange-500',
      softBg: 'from-orange-50 to-amber-50/40',
      hoverBorder: 'hover:border-orange-400',
      accentText: 'group-hover:text-orange-500',
      badgeCount: 0,
      badgeLabel: ''
    }
  ];

  return (
    <div className="max-w-4xl mx-auto p-3 sm:p-6">
      <Card className="bg-white/90 backdrop-blur rounded-3xl border-0 shadow-2xl overflow-hidden">
        {/* Bandeau décoratif — gradient SkyApp */}
        <div className="h-1.5" style={{ background: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 50%, #FB923C 100%)' }}></div>

        <CardHeader className="pb-4 sm:pb-6 text-center px-4 sm:px-6 pt-6 sm:pt-8">
          <div className="relative w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-violet-600 rounded-2xl rotate-6 opacity-20"></div>
            <div className="relative w-full h-full bg-gradient-to-br from-blue-600 to-violet-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Search className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-xl sm:text-3xl font-bold text-gray-900 tracking-tight">
            Choisir le type d'action
          </CardTitle>
          <CardDescription className="text-gray-500 mt-1 sm:mt-2 text-xs sm:text-sm">
            Sélectionnez l'action que vous souhaitez effectuer
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-3 sm:space-y-4 pb-6 sm:pb-8 px-3 sm:px-8">
          {/* Options de recherche */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-5">
            {searchTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => onSelect(type.id)}
                className={`group relative bg-gradient-to-br ${type.softBg} border-2 border-gray-200 ${type.hoverBorder} rounded-2xl sm:rounded-3xl p-4 sm:p-6 text-left transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 active:scale-[0.99] overflow-visible`}
              >
                {/* Badge compteur — visible uniquement s'il y a des tâches */}
                {type.badgeCount > 0 && (
                  <div className="absolute -top-2.5 -right-2.5 flex items-center gap-1 bg-gradient-to-r from-blue-600 to-violet-600 text-white text-[11px] sm:text-xs font-bold rounded-full px-2.5 sm:px-3 py-1 shadow-lg animate-pulse">
                    <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
                    {type.badgeCount} {type.badgeLabel}
                  </div>
                )}

                <div className="flex items-start gap-3 sm:block">
                  {/* Icône */}
                  <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br ${type.gradient} rounded-xl sm:rounded-2xl flex items-center justify-center mb-0 sm:mb-4 shadow-md group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300 flex-shrink-0`}>
                    <type.icon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>

                  {/* Contenu */}
                  <div className="min-w-0 flex-1">
                    <h3 className={`text-base sm:text-xl font-bold text-gray-900 ${type.accentText} mb-1 sm:mb-2 leading-tight transition-colors`}>
                      {type.title}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 leading-snug">
                      {type.description}
                    </p>

                    {/* Flèche */}
                    <div className={`mt-3 sm:mt-5 inline-flex items-center gap-2 text-xs sm:text-sm font-semibold text-gray-400 ${type.accentText} transition-colors`}>
                      <span>Continuer</span>
                      <span className="w-6 h-6 rounded-full bg-white shadow-sm border border-gray-200 flex items-center justify-center group-hover:translate-x-1 transition-transform">
                        <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Bouton Annuler */}
          <div className="pt-2 sm:pt-4 flex justify-center">
            <Button
              onClick={onCancel}
              variant="ghost"
              className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl"
            >
              <X className="h-4 w-4 mr-2" />
              Annuler
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Composant de sélection de mission pour compte-rendu
const MissionSelector = ({ onSelect, onCancel, token }) => {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportedMissionIds, setReportedMissionIds] = useState(new Set());
  const [reportStatusByMission, setReportStatusByMission] = useState({});

  useEffect(() => {
    const fetchMissions = async () => {
      if (!localStorage.getItem('token')) return;
      try {
        setLoading(true);
        // Récupérer les missions planifiées pour l'utilisateur
        const response = await http.get(`${API}/planning/my-missions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Récupérer les comptes-rendus déjà réalisés par le technicien pour
        // marquer les missions concernées (badge "déjà effectué").
        try {
          const reportsRes = await http.get(`${API}/mission-reports`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const reportsList = Array.isArray(reportsRes.data) ? reportsRes.data : [];
          setReportedMissionIds(new Set(reportsList.map(r => r.mission_id).filter(Boolean)));
          const statusMap = {};
          reportsList.forEach(r => { if (r.mission_id) statusMap[r.mission_id] = r.report_status; });
          setReportStatusByMission(statusMap);
        } catch (repErr) {
          console.warn('Impossible de charger les comptes-rendus existants:', repErr);
        }
        
        // Regrouper les missions par titre et location pour éviter les doublons (missions multi-jours)
        const missionsData = response.data || [];
        const uniqueMissions = {};
        
        missionsData.forEach(mission => {
          // Nettoyer le titre : retirer "RDV - Client" et autres préfixes
          let cleanTitle = mission.title || '';
          cleanTitle = cleanTitle.replace(/^RDV\s*-\s*Client\s*/i, '').trim();
          
          // Si le titre est vide ou générique, construire un titre pertinent
          if (!cleanTitle || cleanTitle === 'Mission sans titre') {
            // Utiliser description en priorité, sinon intervention_category, sinon "Chantier chez client"
            if (mission.description) {
              cleanTitle = mission.description;
            } else if (mission.intervention_category) {
              cleanTitle = mission.intervention_category.charAt(0).toUpperCase() + mission.intervention_category.slice(1);
              if (mission.client_name) {
                cleanTitle += ` - ${mission.client_name}`;
              }
            } else if (mission.client_name) {
              cleanTitle = `Chantier chez ${mission.client_name}`;
            } else {
              cleanTitle = 'Chantier';
            }
          }
          
          // Créer une clé unique basée sur le titre nettoyé et la location
          const key = `${cleanTitle}_${mission.location}`;
          
          // Garder uniquement la première occurrence de chaque mission
          if (!uniqueMissions[key]) {
            uniqueMissions[key] = {
              ...mission,
              title: cleanTitle
            };
          }
        });
        
        setMissions(
          Object.values(uniqueMissions).filter(m => {
            // Compte-Rendu Chantier : accessible au technicien assigné (chef
            // d'équipe OU collaborateur — l'endpoint /planning/my-missions ne
            // renvoie que les missions assignées à l'utilisateur connecté),
            // pour des missions terminées / clôturées / en cours / en pause / partielles.
            const status = (m.status || '').toLowerCase();
            const eligibleStatus = ['completed', 'in_progress', 'paused', 'partially_completed'].includes(status);
            return eligibleStatus;
          })
        );
      } catch (err) {
        console.error('Erreur chargement missions:', err);
        setError('Impossible de charger les missions');
      } finally {
        setLoading(false);
      }
    };
    
    fetchMissions();
  }, [token]);

  return (
    <div className="max-w-5xl mx-auto p-6">
      <Card className="bg-white rounded-2xl border-0 shadow-lg">
        <CardHeader className="pb-6 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Calendar className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-semibold text-gray-900">
            Sélectionner un chantier
          </CardTitle>
          <CardDescription className="text-gray-600 mt-2">
            Choisissez le chantier pour lequel créer un compte-rendu
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4 pb-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-green-500" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600">{error}</p>
            </div>
          ) : missions.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">Aucune mission planifiée</p>
              <p className="text-sm text-gray-500">Les missions planifiées apparaîtront ici</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {missions.map((mission) => {
                const alreadyReported = reportedMissionIds.has(mission.id);
                const needsCorrection = reportStatusByMission[mission.id] === 'correction_requested';
                return (
                <button
                  key={mission._segKey || mission.id}
                  onClick={() => onSelect(mission)}
                  className={`relative w-full bg-white border-2 rounded-xl p-4 text-left transition-all duration-200 hover:shadow-lg group ${needsCorrection ? 'border-amber-300 bg-amber-50/40' : alreadyReported ? 'border-green-300 bg-green-50/40' : 'border-gray-200 hover:border-green-500'}`}
                >
                  {/* Badge statut du compte-rendu */}
                  {needsCorrection ? (
                    <div className="mb-3 inline-flex items-center gap-1.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-[11px] font-bold rounded-full px-3 py-1 shadow-sm">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      Correction demandée
                    </div>
                  ) : alreadyReported && (
                    <div className="mb-3 inline-flex items-center gap-1.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-[11px] font-bold rounded-full px-3 py-1 shadow-sm">
                      <CheckCircle className="h-3.5 w-3.5" />
                      Compte-rendu déjà effectué
                    </div>
                  )}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${alreadyReported ? 'bg-gradient-to-br from-green-400 to-emerald-500' : 'bg-gradient-to-br from-green-500 to-green-600'}`}>
                          <MapPin className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 group-hover:text-green-600">
                            {mission.title || 'Mission sans titre'}
                          </h3>
                          <p className="text-sm text-gray-500">{mission.location || 'Localisation non spécifiée'}</p>
                        </div>
                      </div>
                      
                      {mission.description && (
                        <p className="text-sm text-gray-600 ml-13 mb-2">{mission.description}</p>
                      )}
                      
                      <div className="flex items-center space-x-4 ml-13 text-xs text-gray-500">
                        {mission.date && (
                          <span className="flex items-center">
                            <Calendar className="h-3 w-3 mr-1" />
                            {new Date(mission.date).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                        {mission.client_name && (
                          <span className="flex items-center">
                            <User className="h-3 w-3 mr-1" />
                            {mission.client_name}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-green-600 group-hover:translate-x-1 transition-all ml-4 mt-1" />
                  </div>
                </button>
                );
              })}
            </div>
          )}

          {/* Bouton Annuler */}
          <div className="pt-4 flex justify-center">
            <Button
              onClick={onCancel}
              variant="ghost"
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-4 w-4 mr-2" />
              Annuler
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Apple-style Main Layout for Technicien with Back Button
const TechnicienLayout = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('search');

  // Garde auth : si pas de token, tenter refresh ou rediriger
  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    if (!savedToken) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        http.post(`${API}/auth/refresh`, { refresh_token: refreshToken })
          .then(res => {
            localStorage.setItem('token', res.data.access_token);
            if (res.data.refresh_token) localStorage.setItem('refresh_token', res.data.refresh_token);
            window.location.reload();
          })
          .catch(() => {
            broadcastLogout('no_token');
            forceLogout('no_token');
            window.location.href = '/?session=expired';
          });
      } else {
        broadcastLogout('no_token');
        forceLogout('no_token');
        window.location.href = '/?session=expired';
      }
    }
  }, []);
  const [showSearchTypeSelector, setShowSearchTypeSelector] = useState(false);
  const [showMissionSelector, setShowMissionSelector] = useState(false);
  const [selectedSearchType, setSelectedSearchType] = useState(null);
  const [selectedMission, setSelectedMission] = useState(null);
  const searchFormRef = useRef(null);
  const [drafts, setDrafts] = useState([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [draftError, setDraftError] = useState(null);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [discardingDraftId, setDiscardingDraftId] = useState(null);
  const [isTabTransitioning, setIsTabTransitioning] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [searchToEdit, setSearchToEdit] = useState(null);
  // Set partagé pour tracker les recherches récemment finalisées
  const recentlyFinalizedRef = useRef(new Set());

  const handleLogout = () => {
    broadcastLogout('user_action');
    forceLogout('user_action');
  };

  const goBack = () => {
    navigate('/role-selection');
  };

  const refreshDrafts = useCallback(async ({ silent = false } = {}) => {
    const authToken = token || localStorage.getItem('token');
    if (!authToken) {
      setDrafts([]);
      if (!silent) {
        setDraftsLoading(false);
      }
      return;
    }
    if (!silent) {
      setDraftsLoading(true);
    }
    try {
      setDraftError(null);
      const response = await http.get(`${API}/searches`, {
        params: { status: 'DRAFT', page: 1, page_size: 50, sort_by: 'updated_at', sort_dir: 'desc' },
        headers: { Authorization: `Bearer ${authToken}` }
      });
      // Format standardisé : { data: [...] } ou { data: [...], page: ..., has_more: ... }
      // Filtre de sécurité : ne jamais afficher les recherches ACTIVE/SHARED dans les brouillons
      const allSearches = response.data.data || [];
      if (DEBUG_MODE) console.log('📊 [Brouillons] Recherches reçues du backend:', allSearches.map(s => ({ location: s.location, status: s.status })));
      
      // Double filtrage : 
      // 1. Status doit être DRAFT
      // 2. Ne doit PAS être dans la liste des recherches récemment finalisées
      if (DEBUG_MODE) console.log('🔍 [Brouillons] Set de finalisées:', Array.from(recentlyFinalizedRef.current));
      const draftsOnly = allSearches.filter(search => {
        const isDraft = search.status === 'DRAFT';
        const isInFinalizedSet = recentlyFinalizedRef.current.has(search.id);
        const notRecentlyFinalized = !isInFinalizedSet;
        
        if (DEBUG_MODE) {
          console.log(`🔎 [Brouillons] Recherche ${search.id}:`, { 
            status: search.status, 
            isDraft, 
            isInFinalizedSet,
            willBeIncluded: isDraft && notRecentlyFinalized 
          });
          
          if (isDraft && !notRecentlyFinalized) {
            console.log('🚫 [Brouillons] Exclusion de', search.id, '(récemment finalisée)');
          }
        }
        return isDraft && notRecentlyFinalized;
      });
      
      if (DEBUG_MODE) console.log('✅ [Brouillons] Après filtrage DRAFT uniquement:', draftsOnly.length, 'brouillons');
      setDrafts(draftsOnly);
    } catch (error) {
      console.error('Erreur lors du chargement des brouillons:', error);
      setDraftError('Impossible de charger les brouillons.');
    } finally {
      if (!silent) {
        setDraftsLoading(false);
      }
    }
  }, [token]);

  useEffect(() => {
    refreshDrafts({ silent: true });
  }, [refreshDrafts]);

  useEffect(() => {
    if (activeTab === 'search') {
      searchFormRef.current?.ensureDraft?.();
    }
  }, [activeTab]);

  // Écouter l'événement de finalisation pour basculer vers "Mes Recherches"
  useEffect(() => {
    const handleSearchFinalized = (event) => {
      if (DEBUG_MODE) {
        console.log('🔔 [TechnicienLayout] Événement searchFinalized reçu!', event.detail);
        console.log('🔄 Bascule vers l\'onglet "Mes Recherches"...');
      }
      setActiveTab('history');
    };
    
    if (DEBUG_MODE) console.log('✅ [TechnicienLayout] Écouteur d\'événement searchFinalized installé');
    window.addEventListener('searchFinalized', handleSearchFinalized);
    return () => {
      if (DEBUG_MODE) console.log('❌ [TechnicienLayout] Écouteur d\'événement searchFinalized retiré');
      window.removeEventListener('searchFinalized', handleSearchFinalized);
    };
  }, []);

  const handleDraftEvent = useCallback((event) => {
    if (!event) return;
    
    if (event.type === 'finalized') {
      // Retirer immédiatement la recherche finalisée de la liste des brouillons
      setDrafts(prevDrafts => prevDrafts.filter(draft => draft.id !== event.draftId));
      if (DEBUG_MODE) console.log(`🗑️ Brouillon ${event.draftId} retiré de la liste après finalisation`);
      return;
    }
    
    const impactfulTypes = new Set(['created', 'saved', 'discarded']);
    if (impactfulTypes.has(event.type)) {
      refreshDrafts({ silent: true });
    }
  }, [refreshDrafts]);

  const openDraftModal = useCallback(async () => {
    // Rafraîchir la liste avant d'ouvrir pour avoir les dernières données
    if (DEBUG_MODE) console.log('🔄 [openDraftModal] Rafraîchissement des brouillons avant ouverture...');
    await refreshDrafts();
    // Petit délai pour laisser le state se mettre à jour
    setTimeout(() => {
      if (DEBUG_MODE) console.log('✅ [openDraftModal] Ouverture de la modal avec données à jour');
      setShowDraftModal(true);
    }, 300);
  }, [refreshDrafts]);

  const closeDraftModal = useCallback(() => {
    setShowDraftModal(false);
    setDraftError(null);
    setDiscardingDraftId(null);
  }, []);

  useEffect(() => {
    if (showDraftModal) {
      setDraftError(null);
      refreshDrafts();
    }
  }, [showDraftModal, refreshDrafts]);

  const resumeDraft = useCallback(async (draft) => {
    if (!draft) {
      if (DEBUG_MODE) console.log('❌ [resumeDraft] Pas de brouillon fourni');
      return;
    }
    
    if (DEBUG_MODE) console.log('🔄 [resumeDraft] Reprise du brouillon:', draft.id);
    setShowDraftModal(false);
    
    // Restaurer le type de recherche AVANT de basculer vers l'onglet search
    // Mapper l'ancien type 'terrain' vers 'infiltration' car 'rapport' nécessite une mission
    let searchType = draft.search_type?.toLowerCase() || 'infiltration';
    if (searchType === 'terrain') {
      searchType = 'infiltration'; // Migration: anciens brouillons terrain deviennent infiltration
    }
    if (DEBUG_MODE) console.log('📋 [resumeDraft] Type de recherche:', searchType);
    
    // Récupération fraîche du draft depuis l'API pour éviter les données périmées
    try {
      const authToken = token || localStorage.getItem('token');
      if (DEBUG_MODE) console.log('🌐 [resumeDraft] Chargement depuis l\'API...');
      const resp = await http.get(`${API}/searches/${draft.id}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (DEBUG_MODE) console.log('✅ [resumeDraft] Brouillon chargé depuis l\'API:', resp.data);
      
      // Mettre à jour le type de recherche depuis la réponse API si disponible
      if (resp.data?.search_type) {
        searchType = resp.data.search_type.toLowerCase();
        // Mapper l'ancien type 'terrain' vers 'infiltration'
        if (searchType === 'terrain') {
          searchType = 'infiltration';
        }
      }
      
      // Définir le type AVANT de basculer vers l'onglet
      setSelectedSearchType(searchType);
      
      // Petit délai pour laisser React monter le formulaire
      setTimeout(() => {
        setActiveTab('search');
        // Attendre que le formulaire soit monté avant de charger les données
        setTimeout(() => {
          if (DEBUG_MODE) console.log('📤 [resumeDraft] Chargement des données dans le formulaire...');
          searchFormRef.current?.loadDraft?.(resp.data || draft);
        }, 100);
      }, 50);
      
    } catch (e) {
      console.error('❌ [resumeDraft] Erreur chargement API:', e);
      // En cas d'erreur réseau, on retombe sur la version passée en paramètre
      setSelectedSearchType(searchType);
      setTimeout(() => {
        setActiveTab('search');
        setTimeout(() => {
          searchFormRef.current?.loadDraft?.(draft);
        }, 100);
      }, 50);
    }
  }, [token]);

  const discardDraft = useCallback(async (draftId) => {
    if (!draftId) {
      return;
    }
    const authToken = token || localStorage.getItem('token');
    if (!authToken) {
      setDraftError('Authentification requise pour supprimer un brouillon.');
      return;
    }

    setDiscardingDraftId(draftId);
    try {
      setDraftError(null);
      // Suppression définitive si brouillon (DELETE) sinon fallback archivage côté backend
      await http.delete(`${API}/searches/${draftId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      await refreshDrafts();
    } catch (error) {
      console.error('Erreur suppression brouillon:', error);
      setDraftError('Impossible de supprimer le brouillon pour le moment.');
    } finally {
      setDiscardingDraftId(null);
    }
  }, [refreshDrafts, token]);

  const handleTabChange = useCallback((value) => {
    if (value === activeTab || isTabTransitioning) {
      return;
    }
    const applyChange = () => {
      setActiveTab(value);
      // Reset search type when leaving search tab
      if (activeTab === 'search' && value !== 'search') {
        setSelectedSearchType(null);
      }
      if (value === 'search') {
        refreshDrafts({ silent: true });
      }
    };

    if (activeTab === 'search' && searchFormRef.current?.forceAutoSave) {
      setIsTabTransitioning(true);
      Promise.resolve(searchFormRef.current.forceAutoSave())
        .catch((err) => {
          console.error('Erreur sauvegarde brouillon avant changement d\'onglet:', err);
        })
        .finally(() => {
          setIsTabTransitioning(false);
          applyChange();
          if (value !== 'search') {
            refreshDrafts({ silent: true });
          }
        });
    } else {
      applyChange();
      if (value !== 'search') {
        refreshDrafts({ silent: true });
      }
    }
  }, [activeTab, isTabTransitioning, refreshDrafts]);

  return (
    <div className="min-h-screen skyapp-page-bg">
      {/* Apple-style Navigation with Back Button */}
      <nav className="bg-white/80 backdrop-blur border-b border-white/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center space-x-2 sm:space-x-0">
              <button
                onClick={goBack}
                className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              >
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
              <img 
                src="/Logo skyapp v.2.png" 
                alt="SkyApp Logo" 
                className="w-28 h-28 sm:w-28 sm:h-28 md:w-24 md:h-24 lg:w-28 lg:h-28 rounded-lg object-cover logo-neon-effect"
              />
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Button
                variant="outline"
                onClick={openDraftModal}
                className="relative flex items-center text-xs sm:text-sm px-2 sm:px-4"
              >
                {draftsLoading ? (
                  <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 animate-spin" />
                ) : (
                  <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                )}
                <span className="hidden sm:inline">Brouillon en attente</span>
                <span className="sm:hidden">Brouillon</span>
                {drafts.length > 0 && (
                  <Badge
                    variant="destructive"
                    className="ml-1 sm:ml-2 px-1.5 sm:px-2 py-0 text-xs font-semibold"
                  >
                    {drafts.length}
                  </Badge>
                )}
              </Button>
              <span className="hidden md:inline text-sm font-medium text-gray-700">Technicien</span>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="text-gray-500 hover:text-gray-700 p-1.5 sm:p-2 hidden md:flex"
              >
                <LogOut className="h-4 w-4" />
              </Button>
              
              {/* Mobile menu burger */}
              <Button
                onClick={() => setMobileNavOpen(!mobileNavOpen)}
                variant="ghost"
                className="md:hidden text-gray-500 hover:text-gray-700 p-1.5 sm:p-2"
              >
                {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <Dialog open={showDraftModal} onOpenChange={(open) => (open ? setShowDraftModal(true) : closeDraftModal())}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Brouillons en attente</DialogTitle>
            <DialogDescription>
              Reprenez vos recherches non finalisées. Les modifications sont sauvegardées automatiquement.
            </DialogDescription>
          </DialogHeader>
          {draftError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-xl text-sm">
              {draftError}
            </div>
          )}
          {draftsLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
            </div>
          ) : drafts.length === 0 ? (
            <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl p-6 text-center text-sm text-gray-600">
              Aucun brouillon détecté pour le moment.
            </div>
          ) : (
            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {drafts.map((draft) => {
                const updatedAt = draft.updated_at || draft.created_at;
                const dateLabel = updatedAt ? new Date(updatedAt).toLocaleString('fr-FR') : 'Date inconnue';
                const locationLabel = draft.location?.trim() ? draft.location : 'Localisation à définir';
                return (
                  <div key={draft.id} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{locationLabel}</p>
                        <p className="text-xs text-gray-500 mt-1">Dernière sauvegarde&nbsp;: {dateLabel}</p>
                        {draft.description && (
                          <p className="text-xs text-gray-500 mt-2 line-clamp-2">{draft.description}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end space-y-2">
                        <Button size="sm" onClick={() => resumeDraft(draft)}>
                          Reprendre
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                          disabled={discardingDraftId === draft.id}
                          onClick={() => discardDraft(draft.id)}
                        >
                          {discardingDraftId === draft.id ? (
                            <span className="inline-flex items-center">
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Suppression…
                            </span>
                          ) : (
                            <span className="inline-flex items-center">
                              <Trash2 className="h-4 w-4 mr-2" />
                              Supprimer
                            </span>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex justify-end">
            <Button variant="ghost" onClick={closeDraftModal}>Fermer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-8 px-6">
        {showMissionSelector ? (
          <MissionSelector
            token={token}
            onSelect={(mission) => {
              setSelectedMission(mission);
              setShowMissionSelector(false);
              setSelectedSearchType('rapport');
              setActiveTab('search');
            }}
            onCancel={() => {
              setShowMissionSelector(false);
            }}
          />
        ) : showSearchTypeSelector ? (
          <SearchTypeSelector
            onSelect={(type) => {
              if (type === 'rapport') {
                // Pour compte-rendu, afficher d'abord le sélecteur de mission
                setShowSearchTypeSelector(false);
                setShowMissionSelector(true);
              } else {
                setSelectedSearchType(type);
                setShowSearchTypeSelector(false);
                setActiveTab('search');
              }
            }}
            onCancel={() => {
              setShowSearchTypeSelector(false);
            }}
          />
        ) : (
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            {/* Apple-style Tab Navigation - Desktop only */}
            <div className="hidden md:flex justify-center mb-6 sm:mb-8 overflow-x-auto">
              <TabsList className="bg-white border border-gray-200 p-1 rounded-xl shadow-sm min-w-max">
                <TabsTrigger 
                  value="search" 
                  className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg data-[state=active]:bg-gray-100 data-[state=active]:shadow-sm transition-all duration-200"
                  disabled={isTabTransitioning}
                >
                  <Search className="h-4 w-4" />
                  <span className="font-medium text-xs sm:text-sm md:text-base">Nouvelle</span>
                  <span className="hidden md:inline font-medium">Recherche</span>
                </TabsTrigger>
              <TabsTrigger 
                value="history" 
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg data-[state=active]:bg-gray-100 data-[state=active]:shadow-sm transition-all duration-200 whitespace-nowrap"
                disabled={isTabTransitioning}
              >
                <History className="h-4 w-4 flex-shrink-0" />
                <span className="font-medium text-xs sm:text-sm md:text-base whitespace-nowrap">
                  <span className="md:hidden">Mes</span>
                  <span className="hidden md:inline">Mes Recherches</span>
                </span>
              </TabsTrigger>
              <TabsTrigger 
                value="planning" 
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg data-[state=active]:bg-gray-100 data-[state=active]:shadow-sm transition-all duration-200"
                disabled={isTabTransitioning}
              >
                <Calendar className="h-4 w-4" />
                <span className="font-medium text-xs sm:text-sm md:text-base">Missions</span>
              </TabsTrigger>
              <TabsTrigger 
                value="inventory" 
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg data-[state=active]:bg-gray-100 data-[state=active]:shadow-sm transition-all duration-200"
                disabled={isTabTransitioning}
              >
                <Package className="h-4 w-4" />
                <span className="font-medium text-xs sm:text-sm md:text-base">Inventaire</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Mobile Navigation Menu */}
          <div className={`md:hidden transition-all duration-300 ease-in-out overflow-hidden ${
            mobileNavOpen ? 'max-h-96 opacity-100 mb-6' : 'max-h-0 opacity-0'
          }`}>
            <div className="bg-white border-2 border-black rounded-xl shadow-lg p-2 space-y-2">
              <button
                onClick={() => {
                  handleTabChange('search');
                  setMobileNavOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === 'search'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Search className="h-5 w-5" />
                <span className="font-medium">Nouvelle Recherche</span>
              </button>
              <button
                onClick={() => {
                  handleTabChange('history');
                  setMobileNavOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === 'history'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <History className="h-5 w-5" />
                <span className="font-medium">Mes Recherches</span>
              </button>
              <button
                onClick={() => {
                  handleTabChange('planning');
                  setMobileNavOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === 'planning'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Calendar className="h-5 w-5" />
                <span className="font-medium">Missions</span>
              </button>
              <button
                onClick={() => {
                  handleTabChange('inventory');
                  setMobileNavOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === 'inventory'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Package className="h-5 w-5" />
                <span className="font-medium">Inventaire</span>
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  setMobileNavOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg bg-white text-red-600 hover:bg-red-50 transition-all"
              >
                <LogOut className="h-5 w-5" />
                <span className="font-medium">Déconnexion</span>
              </button>
            </div>
          </div>

          <TabsContent value="search" className="mt-4 sm:mt-6 md:mt-8">
            {!selectedSearchType ? (
              <SearchTypeSelector
                onSelect={(type) => {
                  if (type === 'rapport') {
                    setSelectedSearchType('rapport');
                    // Ne pas afficher immédiatement le form, attendre la sélection de mission
                  } else {
                    setSelectedSearchType(type);
                  }
                }}
                onCancel={() => {
                  setActiveTab('history');
                }}
              />
            ) : selectedSearchType === 'rapport' && !selectedMission ? (
              <MissionSelector
                token={token}
                onSelect={(mission) => {
                  setSelectedMission(mission);
                }}
                onCancel={() => {
                  setSelectedSearchType(null);
                  setSelectedMission(null);
                }}
              />
            ) : selectedSearchType === 'rapport' && selectedMission ? (
              <MissionReportForm 
                mission={selectedMission}
                token={token}
                onCancel={() => {
                  setSelectedMission(null);
                }}
                onComplete={() => {
                  setSelectedSearchType(null);
                  setSelectedMission(null);
                  setActiveTab('history');
                }}
              />
            ) : (
              <SearchForm 
                ref={searchFormRef} 
                onDraftEvent={handleDraftEvent} 
                searchType={selectedSearchType}
                initialSearch={searchToEdit}
                onSearchLoaded={() => setSearchToEdit(null)}
                recentlyFinalizedRef={recentlyFinalizedRef}
              />
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-4 sm:mt-6 md:mt-8">
            <SearchHistory 
              onEditSearch={(search) => {
                setSearchToEdit(search);
                setActiveTab('search');
                setSelectedSearchType('terrain');
              }}
            />
          </TabsContent>

          <TabsContent value="planning" className="mt-8">
            <TechnicianPlanning />
          </TabsContent>

          <TabsContent value="inventory" className="mt-4 sm:mt-6 md:mt-8">
            <InventoryScanner />
          </TabsContent>
        </Tabs>
        )}
      </main>
    </div>
  );
};

// Technician Planning Component - Vue Calendrier Hebdomadaire
const TechnicianPlanning = () => {
  const confirm = useConfirm();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(new Date()));
  const [selectedMission, setSelectedMission] = useState(null);
  const [quoteData, setQuoteData] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [searchData, setSearchData] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [missionPhotos, setMissionPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [currentTechId, setCurrentTechId] = useState(null);

  // Obtenir le début de semaine (lundi)
  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  // Obtenir les 7 jours de la semaine
  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      days.push(date);
    }
    return days;
  };

  // Navigation semaine
  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeekStart(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeekStart(newDate);
  };

  const goToToday = () => {
    setCurrentWeekStart(getWeekStart(new Date()));
  };

  // Fetch missions where current user is assigned
  useEffect(() => {
    fetchMissions();
    let refreshTimer;
    const refresh = () => {
      clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => fetchMissions(), 300);
    };
    const onChange = (event) => {
      if (['schedules', 'planning_overrides', 'worksites'].includes(event.detail?.table)) refresh();
    };
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    const interval = setInterval(onVisible, 30000);
    window.addEventListener('realtime-update', onChange);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(refreshTimer);
      clearInterval(interval);
      window.removeEventListener('realtime-update', onChange);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const fetchMissions = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Décoder le token pour obtenir l'ID du technicien
      const payload = JSON.parse(atob(token.split('.')[1]));
      const technicianId = payload.sub;
      setCurrentTechId(technicianId);

      const response = await http.get(`${API}/technicians/${technicianId}/missions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200) {
        setMissions(groupTechnicianMissions(response.data || [], technicianId));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des missions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'in_progress': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'completed': return 'bg-green-100 text-green-700 border-green-300';
      case 'cancelled': return 'bg-red-100 text-red-700 border-red-300';
      case 'paused': return 'bg-amber-100 text-amber-700 border-amber-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'scheduled': return 'Planifiée';
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminée';
      case 'cancelled': return 'Annulée';
      case 'paused': return 'En pause';
      default: return status;
    }
  };

  const formatTime = (timeString) => {
    return timeString ? timeString.substring(0, 5) : '';
  };

  // Obtenir toutes les missions de la semaine groupées par chantier
  const getWeekMissionsGrouped = () => {
    const grouped = [];
    const processed = new Set();
    const weekStart = getWeekStart(currentWeekStart);
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    weekEnd.setHours(23, 59, 59, 999);

    missions.forEach(mission => {
      const segmentKey = mission._segKey || `${mission.id}_${mission.start_date}_${mission.end_date}`;
      if (processed.has(segmentKey)) return;
      processed.add(segmentKey);

      const startDate = new Date(mission.start_date || mission.date);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(mission.end_date || mission.date || mission.start_date);
      endDate.setHours(0, 0, 0, 0);

      // Vérifier si la mission chevauche la semaine affichée
      if (endDate < weekStart || startDate > weekEnd) return;

      // Calculer la durée totale en jours
      const totalDays = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      const isMultiDay = totalDays > 1;

      // Jour d'affichage = jour de début (clampé au début de la semaine)
      const displayDate = startDate < weekStart ? weekStart : startDate;
      const dayIndex = Math.round((displayDate.getTime() - weekStart.getTime()) / (1000 * 60 * 60 * 24));

      // Nombre de colonnes à couvrir dans la semaine visible
      const displayEnd = endDate > weekEnd ? weekEnd : endDate;
      const visibleSpan = Math.round((displayEnd.getTime() - displayDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

      grouped.push({
        ...mission,
        isMultiDay,
        dayCount: totalDays,
        dayIndex: Math.max(0, Math.min(dayIndex, 6)),
        columnSpan: Math.max(1, Math.min(visibleSpan, 7 - Math.max(0, Math.min(dayIndex, 6)))),
        startDate: mission.start_date || mission.date,
        endDate: mission.end_date || mission.date || mission.start_date
      });
    });

    return grouped;
  };

  const loadMissionPhotos = async (missionId) => {
    try {
      setPhotosLoading(true);
      const token = localStorage.getItem('token');
      const res = await http.get(`${API}/schedules/${missionId}/photos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMissionPhotos(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Erreur chargement photos mission:', err);
      setMissionPhotos([]);
    } finally {
      setPhotosLoading(false);
    }
  };

  const handleUploadMissionPhotos = async (event) => {
    const filesList = Array.from(event.target.files || []);
    event.target.value = '';
    if (!filesList.length || !selectedMission) return;
    try {
      setUploadingPhotos(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      filesList.forEach((file) => formData.append('files', file));
      formData.append('category', 'mission_progress');
      const res = await http.post(
        `${API}/schedules/${selectedMission.id}/photos`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      const newPhotos = res.data?.photos || [];
      setMissionPhotos((prev) => [...prev, ...newPhotos]);
    } catch (err) {
      console.error('Erreur upload photos mission:', err);
      const detail = err?.response?.data?.detail || "Impossible d'ajouter les photos";
      alert(detail);
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleDeleteMissionPhoto = async (photoId) => {
    if (!selectedMission || !photoId) return;
    if (!(await confirm('Supprimer cette photo ?'))) return;
    try {
      const token = localStorage.getItem('token');
      await http.delete(`${API}/schedules/${selectedMission.id}/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMissionPhotos((prev) => prev.filter((p) => p.id !== photoId));
    } catch (err) {
      console.error('Erreur suppression photo mission:', err);
      const detail = err?.response?.data?.detail || 'Suppression impossible';
      alert(detail);
    }
  };

  const fetchQuoteForMission = async (mission) => {
    setLoadingQuote(true);
    setSelectedMission(mission);
    setQuoteData(null);
    setSearchData(null);
    setMissionPhotos([]);
    if (mission?.id) loadMissionPhotos(mission.id);
    
    try {
      const token = localStorage.getItem('token');
      const worksiteId = mission.worksite_id || mission.worksites?.id;
      
      console.log('🔍 Récupération des données pour worksite:', worksiteId);
      
      // Récupérer le chantier pour avoir le quote_id et project_id
      // NB : peut échouer (mission clôturée / droits RLS technicien limités).
      // Dans ce cas on garde le détail ouvert avec les infos déjà en main.
      let worksiteRes = null;
      try {
        worksiteRes = await http.get(`${API}/worksites/${worksiteId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (wErr) {
        console.warn('⚠️ Chantier inaccessible, détail affiché sans enrichissement:', wErr?.response?.status);
      }

      if (!worksiteRes) {
        return; // le finally remet loadingQuote à false
      }
      
      console.log('📋 Données du chantier:', worksiteRes.data);
      
      const quoteId = worksiteRes.data.quote_id;
      const projectId = worksiteRes.data.project_id;
      const clientId = worksiteRes.data.client_id;
      const address = worksiteRes.data.address;
      
      console.log('🎯 Quote ID:', quoteId, '| Project ID:', projectId);
      
      // Récupérer le devis si disponible
      if (quoteId) {
        try {
          const quoteRes = await http.get(`${API}/quotes/${quoteId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('📄 Devis chargé:', quoteRes.data);
          setQuoteData(quoteRes.data);
        } catch (err) {
          console.error('❌ Erreur chargement devis:', err);
        }
      }
      
      // Récupérer la recherche d'infiltration via le projet
      if (projectId) {
        try {
          console.log('🔎 Récupération du projet:', projectId);
          const projectRes = await http.get(`${API}/projects/${projectId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          console.log('📦 Données du projet:', projectRes.data);
          
          const searchId = projectRes.data.search_id;
          console.log('🔍 Search ID trouvé:', searchId);
          
          if (searchId) {
            const searchRes = await http.get(`${API}/searches/${searchId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            console.log('✅ Recherche chargée via projet:', searchRes.data);
            setSearchData(searchRes.data);
          } else {
            console.log('⚠️ Aucun search_id dans le projet');
          }
        } catch (err) {
          console.error('❌ Erreur chargement recherche via projet:', err);
        }
      } else {
        console.log('⚠️ Aucun project_id dans le chantier');
        
        // Si pas de project_id, chercher la recherche via le client et l'adresse
        if (clientId || address || worksiteRes.data.client_name) {
          try {
            console.log('🔎 Recherche d\'infiltration via client/adresse/nom...');
            console.log('🎯 Client ID du chantier:', clientId);
            console.log('📍 Adresse du chantier:', address);
            console.log('👤 Nom client du chantier:', worksiteRes.data.client_name);
            
            const searchesRes = await http.get(`${API}/searches`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            console.log('📊 Toutes les recherches:', searchesRes.data);
            
            // L'API retourne {data: [...], count: ...}
            const searchesList = searchesRes.data.data || searchesRes.data;
            console.log('📋 Liste des recherches:', searchesList);
            
            const clientName = worksiteRes.data.client_name?.toLowerCase() || '';
            
            // Chercher une recherche qui correspond
            const matchingSearch = searchesList.find(search => {
              console.log(`🔍 Analyse recherche ${search.id}:`, {
                client_id: search.client_id,
                address: search.address || search.location,
                prenom: search.prenom,
                nom: search.nom
              });
              
              // Match par client_id
              const clientMatch = clientId && search.client_id === clientId;
              
              // Match par adresse
              const searchAddress = search.address || search.location || '';
              const addressMatch = address && searchAddress && 
                                   (searchAddress.toLowerCase().includes(address.toLowerCase().substring(0, 20)) ||
                                    address.toLowerCase().includes(searchAddress.toLowerCase().substring(0, 20)));
              
              // Match par nom (prenom + nom de la recherche contient le nom du client)
              const searchFullName = `${search.prenom || ''} ${search.nom || ''}`.toLowerCase().trim();
              const nameMatch = clientName && searchFullName && 
                               (searchFullName.includes(clientName) || 
                                clientName.includes(search.nom?.toLowerCase() || '') ||
                                clientName.includes(search.prenom?.toLowerCase() || ''));
              
              console.log(`   → clientMatch=${clientMatch}, addressMatch=${addressMatch}, nameMatch=${nameMatch}`);
              return clientMatch || addressMatch || nameMatch;
            });
            
            if (matchingSearch) {
              console.log('✅ Recherche trouvée par correspondance:', matchingSearch);
              setSearchData(matchingSearch);
            } else {
              console.log('⚠️ Aucune recherche correspondante trouvée');
            }
          } catch (err) {
            console.error('❌ Erreur recherche d\'infiltration:', err);
          }
        }
      }
    } catch (error) {
      console.error('❌ Erreur chargement données mission:', error);
      // On garde le détail ouvert même si l'enrichissement échoue
    } finally {
      setLoadingQuote(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
        <CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de vos missions...</p>
        </CardContent>
      </Card>
    );
  }

  const weekDays = getWeekDays();
  const weekDayNames = ['LUN.', 'MAR.', 'MER.', 'JEU.', 'VEN.', 'SAM.', 'DIM.'];
  const monthNames = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

  // Statistiques de la semaine - utiliser les missions groupées pour compter correctement
  const weekMissionsGrouped = getWeekMissionsGrouped();

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header avec navigation */}
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 text-white">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 lg:gap-0">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold flex items-center gap-2 sm:gap-3">
              <Calendar className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 flex-shrink-0" />
              <span className="truncate">Mes Missions Planifiées</span>
            </h2>
            <p className="text-blue-100 mt-1 sm:mt-2 text-xs sm:text-sm">Semaine du {weekDays[0].getDate()} au {weekDays[6].getDate()} {monthNames[weekDays[0].getMonth()]} {weekDays[0].getFullYear()}</p>
          </div>

          {/* Navigation semaine */}
          <div className="flex items-center gap-2 sm:gap-3 self-end lg:self-auto">
            <button
              onClick={goToPreviousWeek}
              className="p-2 sm:p-3 bg-white/20 hover:bg-white/30 rounded-xl transition-all backdrop-blur-sm"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            <button
              onClick={goToToday}
              className="px-3 sm:px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl transition-all backdrop-blur-sm font-medium text-xs sm:text-sm"
            >
              Aujourd'hui
            </button>
            <button
              onClick={goToNextWeek}
              className="p-2 sm:p-3 bg-white/20 hover:bg-white/30 rounded-xl transition-all backdrop-blur-sm"
            >
              <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* Stats rapides de la semaine */}
        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4 mt-4 sm:mt-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
            <p className="text-blue-100 text-[11px] sm:text-sm">Total</p>
            <p className="text-2xl sm:text-3xl font-bold">{weekMissionsGrouped.length}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
            <p className="text-blue-100 text-[11px] sm:text-sm">Planifiées</p>
            <p className="text-2xl sm:text-3xl font-bold">{weekMissionsGrouped.filter(m => m.status === 'scheduled').length}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
            <p className="text-blue-100 text-[11px] sm:text-sm">En cours</p>
            <p className="text-2xl sm:text-3xl font-bold">{weekMissionsGrouped.filter(m => m.status === 'in_progress').length}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4">
            <p className="text-amber-200 text-[11px] sm:text-sm">En pause</p>
            <p className="text-2xl sm:text-3xl font-bold">{weekMissionsGrouped.filter(m => m.status === 'paused').length}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 col-span-3 sm:col-span-1">
            <p className="text-blue-100 text-[11px] sm:text-sm">Terminées</p>
            <p className="text-2xl sm:text-3xl font-bold">{weekMissionsGrouped.filter(m => m.status === 'completed').length}</p>
          </div>
        </div>
      </div>

      {/* Vue Agenda Mobile (md:hidden) */}
      <div className="md:hidden space-y-3">
        {weekDays.map((day, dayIndex) => {
          const isToday = day.toDateString() === new Date().toDateString();
          const dayMissions = weekMissionsGrouped.filter(m =>
            m.dayIndex === dayIndex || (m.isMultiDay && dayIndex >= m.dayIndex && dayIndex < m.dayIndex + m.columnSpan)
          );
          if (dayMissions.length === 0) return null;
          return (
            <div key={`mob-day-${dayIndex}`} className={`bg-white rounded-2xl shadow border ${isToday ? 'border-blue-400 ring-2 ring-blue-200' : 'border-gray-100'} overflow-hidden`}>
              <div className={`px-4 py-2 flex items-center justify-between ${isToday ? 'bg-blue-50' : 'bg-gray-50'}`}>
                <div className={`text-sm font-bold uppercase ${isToday ? 'text-blue-700' : 'text-gray-700'}`}>
                  {weekDayNames[dayIndex]} {day.getDate()} {monthNames[day.getMonth()]}
                </div>
                {isToday && <span className="text-[10px] font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">Aujourd'hui</span>}
              </div>
              <div className="p-2 space-y-2">
                {dayMissions.map(mission => {
                  const hours = parseInt(mission.hours) || 8;
                  const startTime = mission.time || '08:00';
                  const [startHour, startMin] = startTime.split(':').map(Number);
                  const endHour = startHour + hours;
                  const endTime = `${endHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`;
                  const grad = mission.status === 'paused'
                    ? 'from-amber-400 to-amber-600 border-amber-400'
                    : mission.isMultiDay
                      ? 'from-purple-500 to-indigo-600 border-purple-400'
                      : 'from-blue-500 to-indigo-600 border-blue-400';
                  return (
                    <button
                      key={`mob-m-${mission._segKey || mission.id}-${dayIndex}`}
                      onClick={() => fetchQuoteForMission(mission)}
                      className={`w-full text-left rounded-xl bg-gradient-to-br ${grad} border shadow-md p-3 text-white active:scale-[0.98] transition`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold bg-white/20 px-2 py-0.5 rounded-full">{startTime} → {endTime}</span>
                        {mission.isMultiDay && (
                          <span className="text-[10px] font-bold bg-white/30 px-2 py-0.5 rounded-full">{mission.dayCount}j</span>
                        )}
                        {mission.status === 'paused' && (
                          <span className="text-[10px] font-bold bg-white/30 px-2 py-0.5 rounded-full">⏸ Pause</span>
                        )}
                      </div>
                      <p className="font-bold text-sm leading-tight line-clamp-2">
                        {mission.worksites?.title || mission.worksite_name || 'Chantier'}
                      </p>
                      {(mission.client_name || mission.worksites?.clients?.name) && (
                        <p className="text-xs text-white/90 mt-1 truncate">
                          {mission.client_name || mission.worksites?.clients?.name}
                        </p>
                      )}
                      {(mission.worksites?.address || mission.worksites?.clients?.address) && (
                        <div className="flex items-center gap-1 text-[11px] text-white/80 mt-1">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{mission.worksites?.address || mission.worksites?.clients?.address}</span>
                        </div>
                      )}
                      {mission.team_leader_name && (
                        <div className="flex items-center gap-1 text-[11px] text-white/80 mt-1">
                          <User className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{mission.team_leader_name}</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        {weekMissionsGrouped.length === 0 && (
          <div className="bg-white rounded-2xl shadow border border-gray-100 p-8 text-center text-gray-500 text-sm">
            Aucune mission cette semaine
          </div>
        )}
      </div>

      {/* Vue Calendrier Hebdomadaire (md+) */}
      <div className="hidden md:block bg-white rounded-3xl shadow-xl border border-gray-100 overflow-visible">
        {/* En-têtes des jours */}
        <div className="grid grid-cols-7 gap-px bg-gray-200">
          {weekDays.map((day, index) => {
            const isToday = day.toDateString() === new Date().toDateString();
            return (
              <div 
                key={`header-${index}`}
                className={`bg-white p-4 text-center border-b-2 ${isToday ? 'border-blue-500 bg-blue-100' : 'border-gray-200'}`}
              >
                <div className={`text-xs font-bold uppercase tracking-wide ${isToday ? 'text-blue-700' : 'text-gray-500'}`}>
                  {weekDayNames[index]}
                </div>
                <div className={`text-2xl font-bold mt-1 ${isToday ? 'text-blue-700' : 'text-gray-900'}`}>
                  {day.getDate()}
                </div>
                <div className={`text-xs ${isToday ? 'text-blue-600' : 'text-gray-500'}`}>
                  {monthNames[day.getMonth()]}
                </div>
              </div>
            );
          })}
        </div>

        {/* Bandeau missions multi-jours (CSS Grid natif avec gridColumn) */}
        {weekMissionsGrouped.filter(m => m.isMultiDay).length > 0 && (
          <div className="grid grid-cols-7 gap-px bg-gray-200 border-b border-gray-200">
            {weekMissionsGrouped.filter(m => m.isMultiDay).map(mission => {
              const hours = parseInt(mission.hours) || 8;
              const startTime = mission.time || '08:00';
              const [startHour, startMin] = startTime.split(':').map(Number);
              const endHour = startHour + hours;
              const endTime = `${endHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`;

              return (
                <div
                  key={`md-${mission._segKey || mission.id}`}
                  onClick={() => fetchQuoteForMission(mission)}
                  className={`rounded-xl cursor-pointer hover:shadow-xl transition-all group overflow-hidden
                    shadow-lg border m-1 ${
                      mission.status === 'paused'
                        ? 'bg-gradient-to-r from-amber-400 to-amber-600 border-amber-400'
                        : 'bg-gradient-to-r from-purple-500 to-indigo-600 border-purple-400'
                    }`}
                  style={{ gridColumn: `${mission.dayIndex + 1} / span ${mission.columnSpan}` }}
                >
                  {/* En-tête */}
                  <div className="px-3 py-2 border-b bg-white/10 border-white/20">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center font-bold text-xs text-white flex-shrink-0">
                          {mission.worksites?.clients?.name
                            ? mission.worksites.clients.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                            : '?'}
                        </div>
                        <span className="font-semibold text-xs text-white">
                          {startTime} - {endTime}
                        </span>
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/30 text-white flex-shrink-0">
                        {mission.dayCount}j
                      </span>
                    </div>
                  </div>
                  {/* Contenu */}
                  <div className="p-3">
                    <p className="font-bold text-sm mb-1.5 leading-tight line-clamp-2 text-white">
                      {mission.worksites?.title || mission.worksite_name || 'Chantier'}
                    </p>
                    {(mission.client_name || mission.worksites?.clients?.name) && (
                      <div className="mb-1.5">
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold truncate max-w-full bg-white/20 text-white inline-block">
                          {mission.client_name || mission.worksites?.clients?.name}
                        </span>
                      </div>
                    )}
                    <div className="mb-1.5">
                      <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium text-white bg-white/20">
                        <Calendar className="w-3 h-3 flex-shrink-0" />
                        <span>
                          {new Date(mission.startDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                          {' → '}
                          {new Date(mission.endDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </span>
                      </div>
                    </div>
                    {(mission.worksites?.address || mission.worksites?.clients?.address) && (
                      <div className="mb-1.5">
                        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-white bg-white/20">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{mission.worksites?.address || mission.worksites?.clients?.address}</span>
                        </div>
                      </div>
                    )}
                    {mission.team_leader_name && (
                      <div className="flex items-center gap-1 text-xs text-white/80">
                        <User className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{mission.team_leader_name}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Grille missions single-day */}
        <div className="grid grid-cols-7 gap-px bg-gray-200" style={{ minHeight: '400px' }}>
          {weekDays.map((day, dayIndex) => {
            const isToday = day.toDateString() === new Date().toDateString();
            const dayMissions = weekMissionsGrouped.filter(m => !m.isMultiDay && m.dayIndex === dayIndex);

            return (
              <div
                key={`day-col-${dayIndex}`}
                className={`${isToday ? 'bg-blue-50/30' : 'bg-white'} p-1.5 flex flex-col gap-1.5 overflow-y-auto`}
                style={{ maxHeight: '500px', minHeight: '400px' }}
              >
                {dayMissions.map((mission) => {
                  const hours = parseInt(mission.hours) || 8;
                  const startTime = mission.time || '08:00';
                  const [startHour, startMin] = startTime.split(':').map(Number);
                  const endHour = startHour + hours;
                  const endTime = `${endHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`;

                  return (
                    <div
                      key={`mission-${mission.id}-${dayIndex}`}
                      onClick={() => fetchQuoteForMission(mission)}
                      className={`rounded-xl cursor-pointer hover:shadow-xl transition-all group overflow-hidden shrink-0
                        shadow-lg border ${
                          mission.status === 'paused'
                            ? 'bg-gradient-to-br from-amber-400 to-amber-600 border-amber-400'
                            : 'bg-gradient-to-br from-blue-500 to-indigo-600 border-blue-400'
                        }`}
                    >
                      {/* Badge statut */}
                      {mission.status === 'paused' && (
                        <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-white/30 text-white text-[10px] font-bold z-20">
                          ⏸ En pause
                        </div>
                      )}
                      {mission.status !== 'scheduled' && mission.status !== 'paused' && (
                        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-orange-500 border-2 border-white shadow-md z-20 flex items-center justify-center">
                          <span className="text-white text-xs font-bold">!</span>
                        </div>
                      )}
                      
                      {/* En-tête avec icône et horaire */}
                      <div className="px-3 py-2 border-b bg-white/10 border-white/20">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center font-bold text-xs text-white flex-shrink-0">
                              {mission.worksites?.clients?.name 
                                ? mission.worksites.clients.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                                : '?'}
                            </div>
                            <span className="font-semibold text-xs text-white">
                              {startTime} - {endTime}
                            </span>
                          </div>
                          {(mission.client_name || mission.worksites?.clients?.name || mission.worksites?.clients?.prenom) && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-bold truncate max-w-[120px] bg-white/20 text-white"
                              title={mission.client_name || mission.worksites?.clients?.name || ''}>
                              {mission.client_name || mission.worksites?.clients?.name || mission.worksites?.clients?.prenom}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Contenu principal */}
                      <div className="p-3">
                        <p className="font-bold text-sm mb-1.5 leading-tight line-clamp-2 text-white">
                          {mission.worksites?.title || mission.worksite_name || 'Chantier'}
                        </p>

                        {(mission.worksites?.address || mission.worksites?.clients?.address) && (
                          <div className="mb-1.5">
                            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-white bg-white/20">
                              <MapPin className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">
                                {mission.worksites?.address || mission.worksites?.clients?.address}
                              </span>
                            </div>
                          </div>
                        )}

                        <div className="flex flex-col gap-0.5 text-xs text-white/80">
                          {mission.team_leader_name && (
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{mission.team_leader_name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {dayMissions.length === 0 && (
                  <div className="flex-1" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Modale de devis sans prix */}
      {selectedMission && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center sm:p-4 z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex justify-between items-center z-10">
              <div className="min-w-0 pr-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Détail de la Mission</h2>
                  {['completed', 'partially_completed'].includes((selectedMission.status || '').toLowerCase()) && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-300 flex-shrink-0">
                      <CheckCircle className="w-3.5 h-3.5" />
                      Clôturée
                    </span>
                  )}
                </div>
                <p className="text-xs sm:text-sm text-gray-600 mt-0.5 sm:mt-1 truncate">
                  {selectedMission.worksites?.title || selectedMission.worksite_name || 'Chantier'}
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedMission(null);
                  setQuoteData(null);
                  setMissionPhotos([]);
                }}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors flex-shrink-0"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            <div className="p-4 sm:p-6">
              {loadingQuote ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Chargement des détails...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Informations mission */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
                    <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      Informations Mission
                    </h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <div>
                          <span className="text-gray-600">Date:</span>
                          <span className="ml-2 font-semibold text-gray-900">
                            {(() => {
                              const sd = selectedMission.start_date || selectedMission.period_start;
                              const ed = selectedMission.end_date || selectedMission.period_end;
                              if (!sd) return 'Non définie';
                              const opts = { day: 'numeric', month: 'short', year: 'numeric' };
                              const startStr = new Date(sd + 'T00:00:00').toLocaleDateString('fr-FR', opts);
                              if (ed && ed !== sd) {
                                const endStr = new Date(ed + 'T00:00:00').toLocaleDateString('fr-FR', opts);
                                return `Du ${startStr} au ${endStr}`;
                              }
                              return new Date(sd + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
                            })()}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <div>
                          <span className="text-gray-600">Horaire:</span>
                          <span className="ml-2 font-semibold text-gray-900">{selectedMission.time || 'Non défini'}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <div>
                          <span className="text-gray-600">Chef d'équipe:</span>
                          <span className="ml-2 font-semibold text-gray-900">{selectedMission.team_leader_name || 'Non défini'}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Timer className="w-4 h-4 text-gray-500" />
                        <div>
                          <span className="text-gray-600">Durée:</span>
                          <span className="ml-2 font-semibold text-gray-900">{selectedMission.hours || 8}h</span>
                        </div>
                      </div>
                      {selectedMission.intervention_category && (
                        <div className="flex items-center gap-2">
                          <Info className="w-4 h-4 text-gray-500" />
                          <div>
                            <span className="text-gray-600">Catégorie:</span>
                            <span className="ml-2 font-semibold text-gray-900">{selectedMission.intervention_category}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    {selectedMission.description && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <span className="text-gray-600 text-sm font-medium flex items-center gap-2">
                          <Info className="w-4 h-4" />
                          Description:
                        </span>
                        <p className="mt-2 text-gray-900">{selectedMission.description}</p>
                      </div>
                    )}
                    
                    {/* Si mission multi-jours */}
                    {selectedMission.isMultiDay && selectedMission.groupedMissions && (
                      <div className="mt-4 pt-4 border-t border-blue-200">
                        <p className="text-sm font-medium text-blue-700 mb-2">Mission sur plusieurs jours:</p>
                        <div className="space-y-1">
                          {selectedMission.groupedMissions.map((m, idx) => {
                            const mDate = m.start_date || m.date;
                            const dateStr = mDate ? new Date(mDate + (mDate.length <= 10 ? 'T00:00:00' : '')).toLocaleDateString('fr-FR') : '—';
                            return (
                              <div key={idx} className="text-sm text-gray-700 flex items-center gap-2">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                {dateStr} - {m.time || '—'} ({m.hours || 8}h)
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Adresse du chantier */}
                  {(() => {
                    const addr = selectedMission.client_address || selectedMission.worksites?.address;
                    const clientData = selectedMission.worksites?.clients;
                    const ville = clientData?.ville;
                    const cp = clientData?.code_postal;
                    const fullAddr = addr
                      ? [addr, cp && ville ? `${cp} ${ville}` : (cp || ville || '')].filter(Boolean).join(', ')
                      : (ville ? [cp, ville].filter(Boolean).join(' ') : null);
                    return (
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
                        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <MapPin className="w-5 h-5 text-green-600" />
                          Adresse du chantier
                        </h3>
                        <p className="text-sm text-gray-900">
                          {fullAddr || 'Adresse non renseignée'}
                        </p>
                      </div>
                    );
                  })()}

                  {/* Informations client */}
                  {(() => {
                    const clientData = selectedMission.worksites?.clients;
                    const clientName = clientData
                      ? [clientData.prenom, clientData.nom].filter(Boolean).join(' ') || clientData.name
                      : selectedMission.client_name;
                    if (!clientName && !clientData?.telephone && !clientData?.email) return null;
                    return (
                      <div className="bg-gradient-to-br from-purple-50 to-fuchsia-50 rounded-xl p-5 border border-purple-200">
                        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <Users className="w-5 h-5 text-purple-600" />
                          Client
                        </h3>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {clientName && (
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-gray-500" />
                              <span className="text-gray-900 font-medium">{clientName}</span>
                            </div>
                          )}
                          {clientData?.telephone && (
                            <div className="flex items-center gap-2">
                              <Phone className="w-4 h-4 text-gray-500" />
                              <a href={`tel:${clientData.telephone}`} className="text-blue-700 hover:underline font-medium">{clientData.telephone}</a>
                            </div>
                          )}
                          {clientData?.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="w-4 h-4 text-gray-500" />
                              <a href={`mailto:${clientData.email}`} className="text-blue-700 hover:underline font-medium">{clientData.email}</a>
                            </div>
                          )}
                          {clientData?.adresse && !selectedMission.client_address && !selectedMission.worksites?.address && (
                            <div className="flex items-center gap-2 col-span-2">
                              <MapPin className="w-4 h-4 text-gray-500" />
                              <span className="text-gray-700">{clientData.adresse}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Photos libres ajoutées en cours de mission (MED-001) */}
                  {(() => {
                    const status = (selectedMission.status || '').toLowerCase();
                    const endStr = selectedMission.end_date || selectedMission.period_end || selectedMission.start_date;
                    let canUpload = false;
                    if (status !== 'cancelled') {
                      if (['scheduled', 'in_progress', 'paused', 'partially_completed'].includes(status)) {
                        canUpload = true;
                      } else if (endStr) {
                        const today = new Date(); today.setHours(0, 0, 0, 0);
                        const endD = new Date(String(endStr).slice(0, 10) + 'T00:00:00');
                        canUpload = endD >= today;
                      }
                    }
                    return (
                      <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-5 border border-cyan-200">
                        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                          <h3 className="font-bold text-gray-900 flex items-center gap-2">
                            <Camera className="w-5 h-5 text-cyan-600" />
                            Photos de la mission ({missionPhotos.length})
                          </h3>
                          {canUpload && (
                            <>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                id="tech-mission-photos-upload"
                                className="hidden"
                                onChange={handleUploadMissionPhotos}
                                disabled={uploadingPhotos}
                              />
                              <label
                                htmlFor="tech-mission-photos-upload"
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold shadow-md transition-all cursor-pointer ${
                                  uploadingPhotos
                                    ? 'bg-cyan-400 cursor-wait'
                                    : 'bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700'
                                }`}
                              >
                                {uploadingPhotos ? (
                                  <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Envoi...
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-4 h-4" />
                                    Ajouter des photos
                                  </>
                                )}
                              </label>
                            </>
                          )}
                        </div>

                        {!canUpload && (
                          <p className="text-sm text-cyan-800 bg-white/70 rounded-lg p-3 mb-4">
                            Lecture seule — la mission est {status === 'cancelled' ? 'annulée' : 'terminée'}.
                          </p>
                        )}

                        {photosLoading ? (
                          <div className="flex items-center justify-center py-6">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
                          </div>
                        ) : missionPhotos.length === 0 ? (
                          <div className="text-center py-6 text-gray-500">
                            <Camera className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                            <p className="text-sm">Aucune photo pour le moment.</p>
                            {canUpload && (
                              <p className="text-xs mt-1">Cliquez sur « Ajouter des photos » pour en envoyer.</p>
                            )}
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {missionPhotos.map((photo) => {
                              const canDelete = canUpload && photo.uploaded_by === currentTechId;
                              return (
                                <div key={photo.id} className="relative group">
                                  <a href={photo.file_url} target="_blank" rel="noopener noreferrer" className="block">
                                    <img
                                      src={photo.file_url}
                                      alt={photo.file_name || 'Photo mission'}
                                      className="w-full h-32 object-cover rounded-lg shadow-sm group-hover:shadow-md transition-all bg-gray-100"
                                    />
                                  </a>
                                  {canDelete && (
                                    <button
                                      onClick={() => handleDeleteMissionPhoto(photo.id)}
                                      className="absolute top-1.5 right-1.5 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow"
                                      title="Supprimer"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded-b-lg truncate">
                                    {new Date(photo.created_at).toLocaleDateString('fr-FR')}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <MissionComments scheduleId={selectedMission.id} currentUserId={currentTechId} />

                  {/* Bandeau pause (lecture seule pour technicien) */}
                  {selectedMission.status === 'paused' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                      <h3 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                        <span className="text-lg">⏸</span>
                        Mission en pause
                      </h3>
                      {selectedMission.pause_reason && (
                        <p className="text-sm text-amber-700 leading-relaxed">{selectedMission.pause_reason}</p>
                      )}
                      {selectedMission.paused_at && (
                        <p className="text-xs text-amber-500 mt-2">
                          Depuis le {new Date(selectedMission.paused_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Devis associé */}
                  {quoteData && (
                    <div>
                      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-gray-600" />
                        Devis associé
                      </h3>
                      
                      {/* Info devis */}
                      <div className="bg-gray-50 rounded-xl p-4 mb-4">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Numéro:</span>
                            <span className="ml-2 font-semibold text-gray-900">{quoteData.quote_number || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Date:</span>
                            <span className="ml-2 font-semibold text-gray-900">
                              {quoteData.created_at ? new Date(quoteData.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                            </span>
                          </div>
                          {quoteData.client_name && (
                            <div>
                              <span className="text-gray-600">Client:</span>
                              <span className="ml-2 font-semibold text-gray-900">{quoteData.client_name}</span>
                            </div>
                          )}
                          {quoteData.status && (
                            <div>
                              <span className="text-gray-600">Statut:</span>
                              <span className="ml-2 font-semibold text-gray-900">{quoteData.status}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Articles du devis (sans prix) */}
                      <div className="space-y-3">
                        <h4 className="font-semibold text-gray-700">Articles à réaliser:</h4>
                        {(quoteData.items || []).map((item, index) => (
                          <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-all">
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                <span className="text-blue-700 font-bold text-sm">{index + 1}</span>
                              </div>
                              <div className="flex-1">
                                <h5 className="font-semibold text-gray-900 mb-1">{item.name || item.description}</h5>
                                {item.name && item.description && (
                                  <p className="text-sm text-gray-500 mb-2">{item.description}</p>
                                )}
                                <div className="grid grid-cols-2 gap-3 text-sm text-gray-600">
                                  <div className="flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                    <span><span className="font-medium">Quantité:</span> {item.quantity} {item.unit || 'u'}</span>
                                  </div>
                                  {item.reference && (
                                    <div className="flex items-center gap-2">
                                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                                      <span><span className="font-medium">Réf:</span> {item.reference}</span>
                                    </div>
                                  )}
                                </div>
                                {item.notes && (
                                  <div className="mt-3 pt-3 border-t border-gray-100">
                                    <p className="text-sm text-gray-600">
                                      <span className="font-medium text-gray-700">Notes:</span> {item.notes}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Total articles */}
                      <div className="mt-4 bg-blue-50 rounded-xl p-4 border border-blue-200">
                        <p className="text-sm font-semibold text-blue-900">
                          Total: {(quoteData.items || []).length} article(s) à réaliser
                        </p>
                      </div>
                    </div>
                  )}

                  {!quoteData && !loadingQuote && (
                    <div className="text-center py-8 bg-gray-50 rounded-xl">
                      <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600">Aucun devis associé à cette mission</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6">
              <div className="flex justify-between items-center gap-3">
                {/* Boutons d'accès aux documents */}
                <div className="flex gap-3">
                  {quoteData && (
                    <button
                      onClick={() => setShowQuoteModal(true)}
                      className="px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-medium flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4" />
                      Voir le Devis
                    </button>
                  )}
                  {searchData && (
                    <button
                      onClick={() => setShowSearchModal(true)}
                      className="px-6 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all font-medium flex items-center gap-2"
                    >
                      <Search className="w-4 h-4" />
                      Voir la Recherche
                    </button>
                  )}
                </div>
                
                <button
                  onClick={() => {
                    setSelectedMission(null);
                    setQuoteData(null);
                    setSearchData(null);
                    setMissionPhotos([]);
                  }}
                  className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-medium"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modale Devis complet (sans prix) */}
      {showQuoteModal && quoteData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 flex justify-between items-center z-10">
              <div>
                <h2 className="text-2xl font-bold">Devis #{quoteData.quote_number}</h2>
                <p className="text-blue-100 text-sm mt-1">Informations techniques (sans prix)</p>
              </div>
              <button
                onClick={() => setShowQuoteModal(false)}
                className="p-2 hover:bg-white/20 rounded-xl transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Informations générales */}
              <div className="bg-gray-50 rounded-xl p-5">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Informations Générales
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Date création:</span>
                    <span className="ml-2 font-semibold text-gray-900">
                      {quoteData.created_at ? new Date(quoteData.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                    </span>
                  </div>
                  {quoteData.client_name && (
                    <div>
                      <span className="text-gray-600">Client:</span>
                      <span className="ml-2 font-semibold text-gray-900">{quoteData.client_name}</span>
                    </div>
                  )}
                  {quoteData.client_email && (
                    <div>
                      <span className="text-gray-600">Email:</span>
                      <span className="ml-2 font-semibold text-gray-900">{quoteData.client_email}</span>
                    </div>
                  )}
                  {quoteData.client_phone && (
                    <div>
                      <span className="text-gray-600">Téléphone:</span>
                      <span className="ml-2 font-semibold text-gray-900">{quoteData.client_phone}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Adresse */}
              {quoteData.address && (
                <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-blue-600" />
                    Adresse du chantier
                  </h3>
                  <p className="text-gray-900">{quoteData.address}</p>
                </div>
              )}

              {/* Articles détaillés */}
              <div>
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-gray-600" />
                  Articles à réaliser ({(quoteData.items || []).length})
                </h3>
                <div className="space-y-3">
                  {(quoteData.items || []).map((item, index) => (
                    <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-blue-300 transition-all">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg">
                          <span className="text-white font-bold">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h5 className="font-bold text-gray-900 mb-3 text-lg">{item.description}</h5>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="bg-gray-50 rounded-lg p-3">
                              <span className="text-gray-600 block mb-1">Quantité</span>
                              <span className="font-bold text-gray-900 text-lg">{item.quantity} {item.unit || 'u'}</span>
                            </div>
                            {item.reference && (
                              <div className="bg-gray-50 rounded-lg p-3">
                                <span className="text-gray-600 block mb-1">Référence</span>
                                <span className="font-bold text-gray-900">{item.reference}</span>
                              </div>
                            )}
                          </div>
                          {item.notes && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <p className="text-sm text-gray-700 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                                <span className="font-bold text-yellow-800">📝 Notes:</span> {item.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Résumé */}
              <div className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-xl p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">Total articles</p>
                    <p className="text-3xl font-bold">{(quoteData.items || []).length}</p>
                  </div>
                  <CheckCircle className="w-12 h-12 text-blue-200" />
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6">
              <button
                onClick={() => setShowQuoteModal(false)}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-medium"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Recherche d'infiltration */}
      {showSearchModal && searchData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 flex justify-between items-center z-10">
              <div>
                <h2 className="text-2xl font-bold">Recherche d'Infiltration</h2>
                <p className="text-purple-100 text-sm mt-1">
                  {searchData.search_type === 'terrain' ? 'Recherche Terrain' : 
                   searchData.search_type === 'pre_search' ? 'Pré-recherche' : 'Recherche'}
                </p>
              </div>
              <button
                onClick={() => setShowSearchModal(false)}
                className="p-2 hover:bg-white/20 rounded-xl transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Informations générales */}
              <div className="bg-gray-50 rounded-xl p-5">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Info className="w-5 h-5 text-purple-600" />
                  Informations Recherche
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Date:</span>
                    <span className="ml-2 font-semibold text-gray-900">
                      {searchData.created_at ? new Date(searchData.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Type:</span>
                    <span className="ml-2 font-semibold text-gray-900">
                      {searchData.search_type === 'terrain' ? 'Terrain' : 
                       searchData.search_type === 'pre_search' ? 'Pré-recherche' : 'Standard'}
                    </span>
                  </div>
                  {searchData.client_name && (
                    <div>
                      <span className="text-gray-600">Client:</span>
                      <span className="ml-2 font-semibold text-gray-900">{searchData.client_name}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-600">Statut:</span>
                    <span className="ml-2 font-semibold text-gray-900">{searchData.status || 'En cours'}</span>
                  </div>
                </div>
              </div>

              {/* Adresse */}
              {searchData.address && (
                <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
                  <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-purple-600" />
                    Adresse
                  </h3>
                  <p className="text-gray-900">{searchData.address}</p>
                </div>
              )}

              {/* Description de la recherche */}
              {searchData.description && (
                <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Description de la recherche
                  </h3>
                  <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">{searchData.description}</p>
                  
                  {/* Photo principale si elle existe */}
                  {searchData.photos && searchData.photos.length > 0 && searchData.photos[0] && (
                    <div className="mt-4">
                      <p className="text-xs text-gray-600 mb-2 flex items-center gap-1">
                        <ImageIcon className="w-3 h-3" />
                        PHOTOS (1)
                      </p>
                      <img
                        src={searchData.photos[0].url || searchData.photos[0]}
                        alt="Photo principale"
                        className="w-48 h-auto rounded-lg border-2 border-gray-200 shadow-sm"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Observations et sections personnalisées */}
              {(() => {
                let observationsData = null;
                if (searchData.observations) {
                  try {
                    // Tenter de parser les observations si c'est du JSON
                    observationsData = typeof searchData.observations === 'string' 
                      ? JSON.parse(searchData.observations) 
                      : searchData.observations;
                  } catch (e) {
                    // Si ce n'est pas du JSON, utiliser tel quel
                    observationsData = { text: searchData.observations };
                  }
                }

                if (!observationsData) return null;

                return (
                  <>
                    {/* Observations et remarques */}
                    {observationsData.text && (
                      <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-200">
                        <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <Eye className="w-5 h-5 text-yellow-600" />
                          Observations et remarques
                        </h3>
                        <p className="text-gray-900 leading-relaxed whitespace-pre-wrap mb-4">
                          {observationsData.text}
                        </p>
                        
                        {/* Photos des observations (indices 1 à 5 par exemple) */}
                        {searchData.photos && searchData.photos.length > 1 && (
                          <div>
                            <p className="text-xs text-gray-600 mb-2 flex items-center gap-1">
                              <ImageIcon className="w-3 h-3" />
                              Photos ({Math.min(5, searchData.photos.length - 1)})
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              {searchData.photos.slice(1, 6).map((photo, index) => (
                                <div key={index} className="relative group">
                                  <img
                                    src={photo.url || photo}
                                    alt={`Photo ${index + 1}`}
                                    className="w-full h-32 object-cover rounded-lg border-2 border-gray-200 hover:border-yellow-400 transition-all shadow-sm"
                                  />
                                  <div className="absolute top-1 left-1 bg-black bg-opacity-70 text-white text-xs font-bold px-2 py-1 rounded">
                                    #{index + 1}
                                  </div>
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center">
                                    <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Sections personnalisées (Inspection Extérieure, Conclusion, etc.) */}
                    {observationsData.customSections && observationsData.customSections.length > 0 && (
                      <>
                        {observationsData.customSections.map((section, index) => {
                          // Déterminer les photos pour cette section (par exemple photos 6 à 13 pour la première section)
                          const startPhotoIndex = 6 + (index * 8);
                          const endPhotoIndex = startPhotoIndex + 8;
                          const sectionPhotos = searchData.photos ? searchData.photos.slice(startPhotoIndex, endPhotoIndex) : [];
                          
                          return (
                            <div key={section.id || index} className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-5 border-2 border-purple-200">
                              <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                                <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-lg">
                                  <span className="text-white font-bold text-sm">+</span>
                                </div>
                                {section.title || `Section ${index + 1}`}
                              </h3>
                              <p className="text-gray-900 leading-relaxed whitespace-pre-wrap mb-4">
                                {section.value}
                              </p>
                              
                              {/* Photos de la section */}
                              {sectionPhotos.length > 0 && (
                                <div>
                                  <p className="text-xs text-purple-700 mb-2 flex items-center gap-1">
                                    <ImageIcon className="w-3 h-3" />
                                    Photos ({sectionPhotos.length})
                                  </p>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {sectionPhotos.map((photo, photoIndex) => (
                                      <div key={photoIndex} className="relative group">
                                        <img
                                          src={photo.url || photo}
                                          alt={`${section.title} - Photo ${photoIndex + 1}`}
                                          className="w-full h-32 object-cover rounded-lg border-2 border-purple-200 hover:border-purple-400 transition-all shadow-sm"
                                        />
                                        <div className="absolute top-1 left-1 bg-black bg-opacity-70 text-white text-xs font-bold px-2 py-1 rounded">
                                          #{photoIndex + 1}
                                        </div>
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center">
                                          <Eye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </>
                );
              })()}

              {/* Notes et observations générales */}
              {searchData.notes && (
                <div className="bg-orange-50 rounded-xl p-5 border border-orange-200">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-orange-600" />
                    Notes complémentaires
                  </h3>
                  <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">{searchData.notes}</p>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6">
              <button
                onClick={() => setShowSearchModal(false)}
                className="w-full px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all font-medium"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant de formulaire de compte-rendu de chantier
const MissionReportForm = ({ mission, token, onCancel, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    works_performed: '',
    materials_used: '',
    duration_hours: '',
    observations: '',
    issues_encountered: ''
  });
  const [photosBefore, setPhotosBefore] = useState([]);
  const [photosAfter, setPhotosAfter] = useState([]);
  const [photosBeforePreview, setPhotosBeforePreview] = useState([]);
  const [photosAfterPreview, setPhotosAfterPreview] = useState([]);
  const [existingReport, setExistingReport] = useState(null);
  const [existingPhotosBefore, setExistingPhotosBefore] = useState([]);
  const [existingPhotosAfter, setExistingPhotosAfter] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [worksiteTeam, setWorksiteTeam] = useState([]);
  const [realDurationDays, setRealDurationDays] = useState(null);

  // Charger l'équipe (techniciens) et la durée réelle depuis les interventions du chantier
  useEffect(() => {
    let cancelled = false;
    if (!mission.worksite_id) { setWorksiteTeam([]); setRealDurationDays(null); return; }
    (async () => {
      try {
        const res = await http.get(`${API}/schedules?worksite_id=${mission.worksite_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const scheds = (res.data || []).filter(s => s.worksite_id === mission.worksite_id && s.status !== 'cancelled');
        // Équipe : chefs d'équipe d'abord, puis collaborateurs, dédupliqués
        const seenLeaders = new Set();
        const seenCollabs = new Set();
        const leaders = [];
        const collabs = [];
        scheds.forEach(s => {
          const leader = s.planning_team_leaders;
          const leaderName = leader ? `${leader.first_name || ''} ${leader.last_name || ''}`.trim() : '';
          if (leaderName && !seenLeaders.has(leaderName)) { seenLeaders.add(leaderName); leaders.push({ name: leaderName, role: 'Chef d\'équipe' }); }
          const collabName = `${s.collaborator_first_name || s.users?.first_name || ''} ${s.collaborator_last_name || s.users?.last_name || ''}`.trim();
          if (collabName && !seenCollabs.has(collabName)) { seenCollabs.add(collabName); collabs.push({ name: collabName, role: 'Collaborateur' }); }
        });
        const team = [...leaders, ...collabs];
        // Durée réelle (jours) = du 1er jour au jour de clôture bureau (completed_at, en date seule pour éviter le décalage horaire)
        const starts = scheds.map(s => s.start_date).filter(Boolean);
        const completions = scheds.map(s => (s.completed_at || '').slice(0, 10)).filter(Boolean);
        let days = null;
        if (starts.length && completions.length) {
          const realStart = starts.reduce((a, b) => (a < b ? a : b));
          const realEnd = completions.reduce((a, b) => (a > b ? a : b));
          days = Math.max(1, Math.round((new Date(realEnd) - new Date(realStart)) / 86400000) + 1);
        }
        if (!cancelled) { setWorksiteTeam(team); setRealDurationDays(days); }
      } catch (err) {
        console.warn('Impossible de charger l\'équipe du chantier:', err);
        if (!cancelled) { setWorksiteTeam([]); setRealDurationDays(null); }
      }
    })();
    return () => { cancelled = true; };
  }, [mission.worksite_id, token]);

  // Charger un éventuel compte-rendu déjà enregistré pour cette mission
  // afin de réafficher les informations précédemment saisies.
  useEffect(() => {
    let cancelled = false;
    const loadExisting = async () => {
      try {
        const res = await http.get(`${API}/mission-reports`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const list = Array.isArray(res.data) ? res.data : [];
        const report = list.find(r => r.mission_id === mission.id);
        if (report && !cancelled) {
          setExistingReport(report);
          setFormData({
            works_performed: report.works_performed || '',
            materials_used: report.materials_used || '',
            duration_hours: report.duration_hours != null ? String(report.duration_hours) : '',
            observations: report.observations || '',
            issues_encountered: report.issues_encountered || ''
          });
          setExistingPhotosBefore(Array.isArray(report.photos_before_urls) ? report.photos_before_urls : (Array.isArray(report.photos_before) ? report.photos_before : []));
          setExistingPhotosAfter(Array.isArray(report.photos_after_urls) ? report.photos_after_urls : (Array.isArray(report.photos_after) ? report.photos_after : []));
        }
      } catch (err) {
        console.warn('Impossible de charger le compte-rendu existant:', err);
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    };
    loadExisting();
    return () => { cancelled = true; };
  }, [mission.id, token]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = (e, type) => {
    const files = Array.from(e.target.files);
    if (type === 'before') {
      setPhotosBefore(prev => [...prev, ...files]);
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotosBeforePreview(prev => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      });
    } else {
      setPhotosAfter(prev => [...prev, ...files]);
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPhotosAfterPreview(prev => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removePhoto = (index, type) => {
    if (type === 'before') {
      setPhotosBefore(prev => prev.filter((_, i) => i !== index));
      setPhotosBeforePreview(prev => prev.filter((_, i) => i !== index));
    } else {
      setPhotosAfter(prev => prev.filter((_, i) => i !== index));
      setPhotosAfterPreview(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Durée initiale (prévue) = nombre de jours entre les dates planifiées de la mission
  const initialDurationDays = (mission.start_date && mission.end_date)
    ? Math.max(1, Math.round((new Date(mission.end_date) - new Date(mission.start_date)) / 86400000) + 1)
    : null;

  const handleSubmit = async () => {
    if (!formData.works_performed) {
      alert('Veuillez renseigner les travaux effectués');
      return;
    }

    setLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('mission_id', mission.id);
      formDataToSend.append('works_performed', formData.works_performed);
      formDataToSend.append('materials_used', formData.materials_used);
      formDataToSend.append('duration_hours', formData.duration_hours);
      formDataToSend.append('observations', formData.observations);
      formDataToSend.append('issues_encountered', formData.issues_encountered);
      
      photosBefore.forEach(photo => {
        formDataToSend.append('photos_before', photo);
      });
      
      photosAfter.forEach(photo => {
        formDataToSend.append('photos_after', photo);
      });

      await http.post(`${API}/mission-reports`, formDataToSend, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      alert('Compte-rendu enregistré avec succès !');
      onComplete();
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement:', error);
      alert('Erreur lors de l\'enregistrement du compte-rendu');
    } finally {
      setLoading(false);
    }
  };

  // VAL-002 — Statut du CR (indépendant du statut mission)
  const isCorrectionRequested = existingReport?.report_status === 'correction_requested';
  // Champs verrouillés seulement si un CR existe ET qu'aucune correction n'est demandée
  const isLocked = !!existingReport && !isCorrectionRequested;

  // VAL-002 — Le technicien renvoie son CR corrigé
  const handleResubmit = async () => {
    if (!existingReport?.id) return;
    if (!formData.works_performed || !formData.works_performed.trim()) {
      alert('Veuillez renseigner les travaux effectués');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('works_performed', formData.works_performed);
      fd.append('materials_used', formData.materials_used || '');
      fd.append('duration_hours', formData.duration_hours || '');
      fd.append('observations', formData.observations || '');
      fd.append('issues_encountered', formData.issues_encountered || '');
      photosBefore.forEach(photo => fd.append('photos_before', photo));
      photosAfter.forEach(photo => fd.append('photos_after', photo));
      await http.patch(`${API}/mission-reports/${existingReport.id}/resubmit`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      alert('Compte-rendu corrigé et renvoyé au bureau !');
      onComplete();
    } catch (error) {
      const status = error?.response?.status;
      const msg = status === 409
        ? "Ce compte-rendu ne peut plus être renvoyé (état modifié). Veuillez rafraîchir."
        : (error?.response?.data?.detail || "Erreur lors du renvoi du compte-rendu");
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Card className="bg-white rounded-2xl border-0 shadow-lg">
        <CardHeader className="pb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                <ClipboardList className="h-7 w-7 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl font-semibold text-gray-900">
                  Compte-Rendu de Chantier
                </CardTitle>
                <CardDescription className="text-gray-600 mt-1">
                  {mission.title}
                </CardDescription>
              </div>
            </div>
            <Button variant="ghost" onClick={onCancel}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Bandeau : compte-rendu déjà effectué (soumis / renvoyé / validé) */}
          {existingReport && !isCorrectionRequested && (
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl p-4 flex items-start gap-3 shadow-sm">
              <CheckCircle className="h-6 w-6 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Compte-rendu déjà effectué</p>
                <p className="text-sm text-white/90">
                  Ce compte-rendu a déjà été enregistré et transmis au bureau
                  {existingReport.created_at ? ` le ${new Date(existingReport.created_at).toLocaleDateString('fr-FR')}` : ''}.
                  Vous retrouvez ci-dessous les informations saisies.
                </p>
              </div>
            </div>
          )}

          {/* VAL-002 — Bandeau : correction demandée par le bureau */}
          {isCorrectionRequested && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex items-start gap-3 shadow-sm">
              <AlertTriangle className="h-6 w-6 flex-shrink-0 mt-0.5 text-amber-600" />
              <div>
                <p className="font-bold text-amber-800">⚠ Correction demandée</p>
                <p className="text-sm text-amber-900 mt-0.5">Le Bureau vous demande de modifier ce compte rendu.</p>
                {existingReport.correction_reason && (
                  <div className="mt-2">
                    <p className="text-xs font-semibold text-amber-700">Motif :</p>
                    <p className="text-sm text-amber-900 whitespace-pre-wrap">« {existingReport.correction_reason} »</p>
                  </div>
                )}
                <p className="text-xs text-amber-700 mt-2">Vous pouvez modifier le compte rendu ci-dessous puis le renvoyer.</p>
              </div>
            </div>
          )}

          {/* Informations du chantier (lecture seule) */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-green-900 mb-3 flex items-center">
              <MapPin className="h-4 w-4 mr-2" />
              Informations du chantier
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {mission.worksite_reference && (
                <div>
                  <span className="text-gray-600">Réf. chantier:</span>
                  <p className="font-medium text-gray-900">{mission.worksite_reference}</p>
                </div>
              )}
              {mission.quote_number && (
                <div>
                  <span className="text-gray-600">Réf. devis:</span>
                  <p className="font-medium text-gray-900">{mission.quote_number}</p>
                </div>
              )}
              <div>
                <span className="text-gray-600">Localisation:</span>
                <p className="font-medium text-gray-900">{mission.location}</p>
              </div>
              <div>
                <span className="text-gray-600">Date de début:</span>
                <p className="font-medium text-gray-900">
                  {mission.start_date ? new Date(mission.start_date).toLocaleDateString('fr-FR') : 'Non définie'}
                </p>
              </div>
              <div>
                <span className="text-gray-600">Date de fin:</span>
                <p className="font-medium text-gray-900">
                  {mission.end_date ? new Date(mission.end_date).toLocaleDateString('fr-FR') : 'Non définie'}
                </p>
              </div>
              <div>
                <span className="text-gray-600">Durée initiale:</span>
                <p className="font-medium text-gray-900">
                  {initialDurationDays ? `${initialDurationDays} jour${initialDurationDays > 1 ? 's' : ''}` : 'Non définie'}
                </p>
              </div>
              <div>
                <span className="text-gray-600">Durée réelle:</span>
                <p className="font-medium text-gray-900">
                  {realDurationDays ? `${realDurationDays} jour${realDurationDays > 1 ? 's' : ''}` : 'Chantier non clôturé'}
                </p>
              </div>
              {mission.client_name && (
                <div>
                  <span className="text-gray-600">Client:</span>
                  <p className="font-medium text-gray-900">{mission.client_name}</p>
                </div>
              )}
              {mission.intervention_category && (
                <div>
                  <span className="text-gray-600">Type:</span>
                  <p className="font-medium text-gray-900 capitalize">{mission.intervention_category}</p>
                </div>
              )}
              {worksiteTeam.length > 0 && (
                <div className="col-span-2">
                  <span className="text-gray-600">Équipe:</span>
                  <div className="mt-1 space-y-1">
                    {worksiteTeam.map((m, i) => (
                      <p key={i} className="font-medium text-gray-900">
                        {m.name} <span className="text-xs font-normal text-gray-500">({m.role})</span>
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Travaux effectués */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Travaux effectués <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.works_performed}
              onChange={(e) => handleInputChange('works_performed', e.target.value)}
              readOnly={isLocked}
              placeholder="Décrivez en détail les travaux réalisés..."
              className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none ${isLocked ? 'bg-gray-50 text-gray-700' : ''}`}
              rows={4}
            />
          </div>

          {/* Matériaux utilisés */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Matériaux utilisés
            </label>
            <textarea
              value={formData.materials_used}
              onChange={(e) => handleInputChange('materials_used', e.target.value)}
              readOnly={isLocked}
              placeholder="Liste des matériaux et quantités utilisés..."
              className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none ${isLocked ? 'bg-gray-50 text-gray-700' : ''}`}
              rows={3}
            />
          </div>

          {/* Durée réelle */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Durée réelle (heures)
            </label>
            <Input
              type="number"
              step="0.5"
              value={formData.duration_hours}
              onChange={(e) => handleInputChange('duration_hours', e.target.value)}
              readOnly={isLocked}
              placeholder="Ex: 3.5"
              className={`border-gray-300 focus:ring-green-500 ${isLocked ? 'bg-gray-50 text-gray-700' : ''}`}
            />
          </div>

          {/* Photos avant */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Photos avant travaux
            </label>
            <div className="space-y-3">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handlePhotoUpload(e, 'before')}
                className="hidden"
                id="photos-before"
              />
              {!isLocked && (
                <label
                  htmlFor="photos-before"
                  className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl hover:border-green-500 cursor-pointer transition-colors"
                >
                  <Camera className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-gray-600">Ajouter des photos avant</span>
                </label>
              )}
              {existingPhotosBefore.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-green-700 mb-2">Photos déjà enregistrées</p>
                  <div className="grid grid-cols-3 gap-3">
                    {existingPhotosBefore.map((url, index) => (
                      <a key={`ex-before-${index}`} href={url} target="_blank" rel="noopener noreferrer" className="block">
                        <img src={url} alt={`Avant existante ${index + 1}`} className="w-full h-24 object-cover rounded-lg border border-green-200" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {photosBeforePreview.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {photosBeforePreview.map((preview, index) => (
                    <div key={index} className="relative group">
                      <img src={preview} alt={`Avant ${index + 1}`} className="w-full h-24 object-cover rounded-lg" />
                      <button
                        onClick={() => removePhoto(index, 'before')}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Photos après */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Photos après travaux
            </label>
            <div className="space-y-3">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handlePhotoUpload(e, 'after')}
                className="hidden"
                id="photos-after"
              />
              {!isLocked && (
                <label
                  htmlFor="photos-after"
                  className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl hover:border-green-500 cursor-pointer transition-colors"
                >
                  <Camera className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-gray-600">Ajouter des photos après</span>
                </label>
              )}
              {existingPhotosAfter.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-green-700 mb-2">Photos déjà enregistrées</p>
                  <div className="grid grid-cols-3 gap-3">
                    {existingPhotosAfter.map((url, index) => (
                      <a key={`ex-after-${index}`} href={url} target="_blank" rel="noopener noreferrer" className="block">
                        <img src={url} alt={`Après existante ${index + 1}`} className="w-full h-24 object-cover rounded-lg border border-green-200" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {photosAfterPreview.length > 0 && (
                <div className="grid grid-cols-3 gap-3">
                  {photosAfterPreview.map((preview, index) => (
                    <div key={index} className="relative group">
                      <img src={preview} alt={`Après ${index + 1}`} className="w-full h-24 object-cover rounded-lg" />
                      <button
                        onClick={() => removePhoto(index, 'after')}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Problèmes rencontrés */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Problèmes ou difficultés rencontrés
            </label>
            <textarea
              value={formData.issues_encountered}
              onChange={(e) => handleInputChange('issues_encountered', e.target.value)}
              readOnly={isLocked}
              placeholder="Décrivez les problèmes ou difficultés rencontrés lors de l'intervention..."
              className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none ${isLocked ? 'bg-gray-50 text-gray-700' : ''}`}
              rows={3}
            />
          </div>

          {/* Observations */}
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              Observations générales
            </label>
            <textarea
              value={formData.observations}
              onChange={(e) => handleInputChange('observations', e.target.value)}
              readOnly={isLocked}
              placeholder="Autres observations ou remarques..."
              className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none ${isLocked ? 'bg-gray-50 text-gray-700' : ''}`}
              rows={3}
            />
          </div>

          {/* Boutons d'action */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <Button variant="outline" onClick={onCancel} disabled={loading}>
              {existingReport ? 'Retour' : 'Annuler'}
            </Button>
            {isCorrectionRequested ? (
              <Button
                onClick={handleResubmit}
                disabled={loading || !formData.works_performed}
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Renvoi...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Renvoyer le compte rendu
                  </>
                )}
              </Button>
            ) : existingReport ? (
              <div className="flex items-center gap-2 text-green-700 font-medium">
                <CheckCircle className="h-5 w-5" />
                Compte-rendu déjà transmis au bureau
              </div>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading || !formData.works_performed}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Enregistrer le compte-rendu
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const INITIAL_AUTO_SAVE_STATE = { saving: false, lastSaved: null, error: null };
const PROFILE_PHOTO_REQUIRED_ERROR = "Ajoutez une photo de profil pour activer l'enregistrement du brouillon.";

const createInitialSections = (generateReferenceNumber) => ([
  {
    id: 'general_info',
    type: 'required',
    title: 'Informations Générales',
    icon: User,
    value: '',
    required: true,
    fieldType: 'multiple',
    collapsed: false,
    photos: [],
    fields: [
      { id: 'nom', label: 'Nom', value: '', required: true, type: 'text' },
      { id: 'prenom', label: 'Prénom', value: '', required: true, type: 'text' },
      { id: 'adresse', label: 'Adresse', value: '', required: true, type: 'textarea' },
      { id: 'ville', label: 'Ville', value: '', required: true, type: 'text' },
      { id: 'code_postal', label: 'Code postal', value: '', required: true, type: 'text' },
      { id: 'numero_recherche', label: 'Numéro de recherche', value: generateReferenceNumber(), required: false, type: 'text', readonly: true },
      { id: 'client_type', label: 'Type de client', value: 'occasional', required: false, type: 'radio', options: [{value: 'occasional', label: 'Client occasionnel (pas de suivi)'}, {value: 'recurring', label: 'Client récurrent (avec suivi)'}] },
      { id: 'client_id', label: 'Client', value: '', required: false, type: 'select', dependsOn: 'client_type', dependsOnValue: 'recurring', options: [] }
    ]
  },
  {
    id: 'description',
    type: 'required', 
    title: 'Description de la recherche',
    icon: FileText,
    value: '',
    required: true,
    placeholder: 'Décrivez en détail ce que vous recherchez (réseaux, canalisations, câbles électriques...)',
    fieldType: 'textarea',
    rows: 4,
    collapsed: false,
    photos: []
  },
  {
    id: 'observations',
    type: 'default',
    title: 'Observations et remarques',
    icon: Eye,
    value: '',
    required: false,
    placeholder: 'Ajoutez vos observations sur le terrain, les conditions de travail...',
    fieldType: 'textarea',
    rows: 3,
    collapsed: false,
    photos: []
  }
]);

// Clé localStorage : copie de secours du brouillon en cours (survit à un kill de l'app,
// contrairement au state React qui est perdu). Ne contient que les champs texte (pas les photos).
const DRAFT_BACKUP_KEY = 'skyapp_search_draft_backup';

// Enhanced Search Form Component with Dynamic Sections
const SearchForm = forwardRef(({ onDraftEvent, searchType = 'terrain', initialSearch = null, onSearchLoaded, recentlyFinalizedRef }, ref) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [photoPreview, setPhotoPreview] = useState([]); // Photos en attente d'upload (nouveaux fichiers)
  const [savedPhotos, setSavedPhotos] = useState([]); // Photos déjà sauvegardées sur le serveur
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [showLightbox, setShowLightbox] = useState(false); // État pour la lightbox
  const [lightboxImage, setLightboxImage] = useState(null); // URL de l'image à afficher
  const [annotatingPhoto, setAnnotatingPhoto] = useState(null); // { url, sectionId, photoIndex }
  const [useGeolocation, setUseGeolocation] = useState(false);
  const [manualCoords, setManualCoords] = useState({
    latitude: '',
    longitude: ''
  });
  const [geoStatus, setGeoStatus] = useState('');

  // Generate reference number
  const generateReferenceNumber = useCallback(() => {
    const date = new Date();
    const year = date.getFullYear().toString().slice(-2);
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `REF${year}${month}${day}-${random}`;
  }, []);

  // Dynamic sections system
  const [sections, setSections] = useState(() => createInitialSections(generateReferenceNumber));

  const [availableSectionTypes, setAvailableSectionTypes] = useState([
    { 
      type: 'equipment', 
      title: 'Équipements utilisés', 
      icon: Settings, 
      placeholder: 'Liste des équipements et outils utilisés sur le terrain',
      fieldType: 'textarea',
      rows: 2
    },
    { 
      type: 'safety', 
      title: 'Consignes de sécurité', 
      icon: Shield, 
      placeholder: 'Mesures de sécurité appliquées et recommandations',
      fieldType: 'textarea',
      rows: 2
    },
    { 
      type: 'weather', 
      title: 'Conditions météorologiques', 
      icon: Globe, 
      placeholder: 'Conditions météo lors de l\'intervention',
      fieldType: 'input'
    },
    { 
      type: 'duration', 
      title: 'Durée d\'intervention', 
      icon: Clock, 
      placeholder: 'Temps passé sur le terrain',
      fieldType: 'input'
    },
    { 
      type: 'team', 
      title: 'Équipe présente', 
      icon: Users, 
      placeholder: 'Membres de l\'équipe présents sur le terrain',
      fieldType: 'textarea',
      rows: 2
    },
    { 
      type: 'difficulties', 
      title: 'Difficultés rencontrées', 
      icon: AlertTriangle, 
      placeholder: 'Obstacles, problèmes ou difficultés rencontrées',
      fieldType: 'textarea',
      rows: 3
    },
    { 
      type: 'recommendations', 
      title: 'Recommandations', 
      icon: Lightbulb, 
      placeholder: 'Recommandations pour les prochaines interventions',
      fieldType: 'textarea',
      rows: 3
    },
    { 
      type: 'custom', 
      title: 'Section personnalisée', 
      icon: Plus, 
      placeholder: 'Contenu personnalisé',
      fieldType: 'textarea',
      rows: 2
    }
  ]);

  const handleSectionChange = (sectionId, value) => {
    setHasUserInteraction(true);
    setHasPendingChanges(true);
    setSections(prev => prev.map(section => 
      section.id === sectionId ? { ...section, value } : section
    ));
  };

  const handleFieldChange = (sectionId, fieldId, value) => {
    setHasUserInteraction(true);
    setHasPendingChanges(true);
    setSections(prev => prev.map(section => 
      section.id === sectionId 
        ? {
            ...section,
            fields: section.fields?.map(field =>
              field.id === fieldId ? { ...field, value } : field
            )
          }
        : section
    ));
  };

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('La taille du fichier ne peut pas dépasser 5MB');
        return;
      }
      
      setProfilePhoto(file);
      setHasUserInteraction(true);
      setHasPendingChanges(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeSection = (sectionId) => {
    setHasUserInteraction(true);
    setHasPendingChanges(true);
    setSections(prev => prev.filter(section => section.id !== sectionId));
  };

  const toggleSectionCollapse = (sectionId) => {
    setSections(prev => prev.map(section => 
      section.id === sectionId ? { ...section, collapsed: !section.collapsed } : section
    ));
  };

  const moveSectionUp = (index) => {
    if (index > 0) {
      const newSections = [...sections];
      [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
      setSections(newSections);
      setHasUserInteraction(true);
      setHasPendingChanges(true);
    }
  };

  const moveSectionDown = (index) => {
    if (index < sections.length - 1) {
      const newSections = [...sections];
      [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
      setSections(newSections);
      setHasUserInteraction(true);
      setHasPendingChanges(true);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    if (startIndex === endIndex) return;

    const newPhotos = Array.from(photoPreview);
    const [reorderedPhoto] = newPhotos.splice(startIndex, 1);
    newPhotos.splice(endIndex, 0, reorderedPhoto);

    // Update numbers after reordering
    const updatedPhotos = newPhotos.map((photo, index) => ({
      ...photo,
      number: index + 1
    }));

    setPhotoPreview(updatedPhotos);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Add new files to existing ones
      const newPreviews = files.map((file, index) => ({
        file,
        url: URL.createObjectURL(file),
        number: photoPreview.length + savedPhotos.length + index + 1,
        name: file.name
      }));
      
      setPhotoPreview(prev => [...prev, ...newPreviews]);
      setHasUserInteraction(true);
      setHasPendingChanges(true);
    }
  };

  const removePhoto = (indexToRemove) => {
    const newPreviews = photoPreview.filter((_, index) => index !== indexToRemove)
      .map((preview, index) => ({
        ...preview,
        number: index + 1
      }));
    
    // Revoke URL for removed photo
    if (photoPreview[indexToRemove]) {
      URL.revokeObjectURL(photoPreview[indexToRemove].url);
    }
    
    setPhotoPreview(newPreviews);
  };

  const removeSavedPhoto = async (photoFilename) => {
    const authToken = resolveToken();
    if (!authToken || !draftId) {
      return;
    }

    try {
      await http.delete(`${API}/searches/${draftId}/photos/${photoFilename}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });

      // Retirer de l'état local après suppression réussie
      setSavedPhotos(prev => prev.filter(photo => photo.filename !== photoFilename));
    } catch (error) {
      console.error('Erreur lors de la suppression de la photo:', error);
      // Afficher un message d'erreur à l'utilisateur si nécessaire
    }
  };

  const getCurrentLocation = () => {
    setGeoStatus('Recherche de votre position...');
    
    if (!navigator.geolocation) {
      setGeoStatus('Géolocalisation non supportée par ce navigateur');
      return Promise.reject('Geolocation not supported');
    }

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGeoStatus(`Position trouvée: ${position.coords.latitude.toFixed(4)}, ${position.coords.longitude.toFixed(4)}`);
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          let errorMessage = 'Erreur de géolocalisation';
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Permission de géolocalisation refusée';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Position non disponible';
              break;
            case error.TIMEOUT:
              errorMessage = 'Timeout de géolocalisation';
              break;
          }
          setGeoStatus(errorMessage);
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    });
  };

  // Auto-save brouillon
  const [draftId, setDraftId] = useState(null);
  const [originalSearchStatus, setOriginalSearchStatus] = useState(null); // Conserver le statut original
  const [autoSaveState, setAutoSaveState] = useState(() => ({ ...INITIAL_AUTO_SAVE_STATE }));
  const [hasUserInteraction, setHasUserInteraction] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const autoSaveTimer = useRef(null);
  const draftCreationPromise = useRef(null);
  const initialDraftRequested = useRef(false);
  const isInitialMount = useRef(true); // Pour éviter l'auto-save au premier rendu
  const autoSaveDraftRef = useRef(null); // Ref stable pour le cleanup effect
  const isSavingRef = useRef(false); // Guard contre les auto-saves concurrents
  const lastErrorToastAt = useRef(0); // Throttle du toast d'erreur réseau (évite le spam en offline prolongé)

  const resolveToken = useCallback(() => token || localStorage.getItem('token'), [token]);

  const buildPayloadFromSections = useCallback(() => {
    const generalSection = sections.find((section) => section.id === 'general_info');
    const nomField = generalSection?.fields?.find((field) => field.id === 'nom');
    const prenomField = generalSection?.fields?.find((field) => field.id === 'prenom');
    const addressField = generalSection?.fields?.find((field) => field.id === 'adresse');
    const villeField = generalSection?.fields?.find((field) => field.id === 'ville');
    const codePostalField = generalSection?.fields?.find((field) => field.id === 'code_postal');
    const clientTypeField = generalSection?.fields?.find((field) => field.id === 'client_type');
    const clientIdField = generalSection?.fields?.find((field) => field.id === 'client_id');
    const descriptionSection = sections.find((section) => section.id === 'description');
    
    // Sauvegarder les sections personnalisées comme JSON
    const customSections = sections
      .filter(s => s.type === 'custom')
      .map(s => ({ id: s.id, title: s.title, value: s.value || '' }));
    
    const observationsSection = sections.find(s => s.id === 'observations');
    const observationsText = observationsSection?.value || '';
    
    // Combiner observations texte + sections custom en JSON
    const observationsData = {
      text: observationsText,
      customSections: customSections
    };

    const payload = {
      nom: nomField?.value || '',
      prenom: prenomField?.value || '',
      location: addressField?.value || '',
      ville: villeField?.value || '',
      code_postal: codePostalField?.value || '',
      description: descriptionSection?.value || '',
      observations: JSON.stringify(observationsData)
    };
    
    // Ajouter client_id uniquement si type = recurring et un client est sélectionné
    if (clientTypeField?.value === 'recurring' && clientIdField?.value) {
      payload.client_id = clientIdField.value;
    } else {
      // Explicitement null pour les clients occasionnels
      payload.client_id = null;
    }
    
    return payload;
  }, [sections]);

  const createDraftIfNeeded = useCallback(async ({ forceCreation = false } = {}) => {
    if (draftId) {
      return draftId;
    }

    if (!forceCreation && !hasUserInteraction) {
      return null;
    }

    // Vérifier que la première section "Informations Générales" est complète
  const generalInfoSection = sections.find(s => s.id === 'general_info');
  const nameField = generalInfoSection?.fields?.find(f => f.id === 'nom');
  const firstNameField = generalInfoSection?.fields?.find(f => f.id === 'prenom');
  const addressField = generalInfoSection?.fields?.find(f => f.id === 'adresse');
    
    const isFirstSectionComplete = 
      nameField?.value?.trim() && 
      firstNameField?.value?.trim() && 
      addressField?.value?.trim();
    
    if (!forceCreation && !isFirstSectionComplete) {
      // Ne pas créer le brouillon tant que la première section n'est pas complète
      return null;
    }
    
    if (draftCreationPromise.current) {
      return draftCreationPromise.current;
    }

    const authToken = resolveToken();
    if (!authToken) {
      return null;
    }

    draftCreationPromise.current = (async () => {
      try {
        const response = await http.post(`${API}/searches/draft`, {}, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        const newDraftId = response.data?.search?.id;
        if (newDraftId) {
          setDraftId(newDraftId);
          onDraftEvent?.({ type: 'created', draft: response.data.search });
          setAutoSaveState((prev) => ({ ...prev, error: null }));
        }
        return newDraftId;
      } catch (error) {
        console.warn('Impossible de créer le brouillon:', error);
        return null;
      } finally {
        draftCreationPromise.current = null;
      }
    })();

    return draftCreationPromise.current;
  }, [draftId, hasUserInteraction, onDraftEvent, resolveToken, sections]);

  const autoSaveDraft = useCallback(async ({ forceSave = false } = {}) => {
    // Guard contre les auto-saves concurrents
    if (isSavingRef.current) {
      return false;
    }
    // BLOQUER l'auto-save si une finalisation est en cours
    if (isFinalizingRef.current && !forceSave) {
      if (DEBUG_MODE) console.log('⏸️ Auto-save bloqué : finalisation en cours');
      return false;
    }
    
    const authToken = resolveToken();
    if (!authToken) {
      return false;
    }

    let targetDraftId = draftId;
    if (!targetDraftId) {
      // Créer le brouillon seulement si les champs obligatoires sont remplis
      // Ne pas forcer la création - respecter les vérifications de createDraftIfNeeded
      targetDraftId = await createDraftIfNeeded({ forceCreation: false });
      if (!targetDraftId) {
        // Brouillon pas créé car les conditions ne sont pas remplies
        return false;
      }
    }

    if (!forceSave && !hasPendingChanges && photoPreview.length === 0) {
      return false;
    }

    isSavingRef.current = true;
    setAutoSaveState((prev) => ({ ...prev, saving: true, error: null }));
    try {
      // 1. Sauvegarder les données textuelles
      const payload = buildPayloadFromSections();

      // 🛟 Copie de secours locale AVANT l'appel réseau : si l'app est tuée pendant
      // l'envoi (coupure réseau, fermeture brutale), ces données restent récupérables
      // au prochain lancement, contrairement au state React qui disparaît.
      try {
        localStorage.setItem(DRAFT_BACKUP_KEY, JSON.stringify({
          draftId: targetDraftId,
          payload,
          savedAt: Date.now()
        }));
      } catch (storageErr) {
        // Quota dépassé ou navigation privée : non bloquant
      }

      try {
        // NE PAS envoyer de status lors de l'auto-save pour ne pas écraser un status ACTIVE
        // Le status DRAFT est appliqué uniquement lors de la création initiale du brouillon
        await http.patch(`${API}/searches/${targetDraftId}`, { ...payload, search_type: searchType?.toUpperCase() }, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
      } catch (patchError) {
        // Si le draft n'existe plus (404), c'est une erreur critique
        if (patchError.response?.status === 404) {
          console.error('❌ Draft introuvable en base de données:', targetDraftId);
          throw new Error('Le brouillon a été supprimé. Veuillez recharger la page.');
        } else {
          throw patchError;
        }
      }

      // 2. Uploader les photos en attente (y compris photo de profil)
      
      // A. Uploader la photo de profil si présente
      if (profilePhoto && !savedPhotos.some(p => p.is_profile)) {
        const formData = new FormData();
        formData.append('files', profilePhoto);
        formData.append('is_profile', 'true');
        formData.append('section_id', 'profile'); // Section spéciale pour photo de profil

        const uploadResponse = await http.post(
          `${API}/searches/${targetDraftId}/photos`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (uploadResponse.data.photos) {
          // Éviter les doublons
          setSavedPhotos(prev => {
            const existingFilenames = new Set(prev.map(p => p.filename));
            const newPhotos = uploadResponse.data.photos.filter(p => !existingFilenames.has(p.filename));
            return [...prev, ...newPhotos];
          });
          
          const profilePhotoData = uploadResponse.data.photos.find(p => p.is_profile);
          if (profilePhotoData) {
            const photoUrl = profilePhotoData.url || `${API}/searches/${targetDraftId}/photos/${profilePhotoData.filename}`;
            setProfilePhotoPreview(photoUrl);
            setProfilePhoto(null);
          }
        }
      }

      // B. Uploader les photos de chaque section
      for (const section of sections) {
        if (section.photos && section.photos.length > 0) {
          // Filtrer uniquement les photos non encore sauvegardées (celles qui ont un objet file)
          const unsavedPhotos = section.photos.filter(photo => photo.file);
          
          if (unsavedPhotos.length > 0) {
            const formData = new FormData();
            unsavedPhotos.forEach(photo => {
              formData.append('files', photo.file);
            });
            formData.append('section_id', section.id); // ✅ Envoyer le section_id

            const uploadResponse = await http.post(
              `${API}/searches/${targetDraftId}/photos`,
              formData,
              {
                headers: {
                  Authorization: `Bearer ${authToken}`,
                  'Content-Type': 'multipart/form-data'
                }
              }
            );

            // Mettre à jour les photos de la section avec les URLs Supabase
            if (uploadResponse.data.photos) {
              setSections(prevSections =>
                prevSections.map(s => {
                  if (s.id === section.id) {
                    const savedSectionPhotos = uploadResponse.data.photos.map(p => ({
                      url: p.url,
                      filename: p.filename,
                      name: p.original_name || p.filename
                    }));
                    
                    // Remplacer UNIQUEMENT les previews locales (avec file) par les URLs Supabase
                    // Garder uniquement les photos déjà sur le serveur qui ne sont PAS dans les nouvelles photos
                    const newFilenames = new Set(savedSectionPhotos.map(p => p.filename));
                    const existingPhotos = s.photos.filter(photo => 
                      photo.filename && !newFilenames.has(photo.filename) // Éviter les doublons
                    );
                    
                    return {
                      ...s,
                      photos: [
                        ...existingPhotos, // Photos existantes (pas de doublons)
                        ...savedSectionPhotos // Nouvelles photos sauvegardées
                      ]
                    };
                  }
                  return s;
                })
              );

              // Nettoyer les previews des fichiers uploadés
              unsavedPhotos.forEach(photo => {
                if (photo.url) {
                  URL.revokeObjectURL(photo.url);
                }
              });
            }
          }
        }
      }

      // C. Uploader les photos de l'ancienne section globale (compatibilité)
      if (photoPreview.length > 0) {
        const formData = new FormData();
        photoPreview.forEach(photo => {
          formData.append('files', photo.file);
        });
        // Pas de section_id = photos globales (ancienne méthode)

        const uploadResponse = await http.post(
          `${API}/searches/${targetDraftId}/photos`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        if (uploadResponse.data.photos) {
          // Éviter les doublons en vérifiant les filenames
          setSavedPhotos(prev => {
            const existingFilenames = new Set(prev.map(p => p.filename));
            const newPhotos = uploadResponse.data.photos.filter(p => !existingFilenames.has(p.filename));
            return [...prev, ...newPhotos];
          });
        }
        
        photoPreview.forEach(photo => {
          if (photo.url) {
            URL.revokeObjectURL(photo.url);
          }
        });
        setPhotoPreview([]);
      }

      setAutoSaveState({ saving: false, lastSaved: new Date(), error: null });
      setHasPendingChanges(false);
      onDraftEvent?.({ type: 'saved', draftId: targetDraftId });
      // Sauvegarde serveur réussie : la copie locale de secours n'est plus nécessaire
      try { localStorage.removeItem(DRAFT_BACKUP_KEY); } catch (e) { /* non bloquant */ }
      return true;
    } catch (error) {
      const isNetworkError = error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK' ||
        error.message?.toLowerCase().includes('network') || !error.response;
      setAutoSaveState((prev) => ({
        ...prev,
        saving: false,
        error: isNetworkError ? 'Connexion instable — nouvel essai automatique' : 'Erreur auto-save'
      }));
      console.error('Erreur auto-save:', error);
      // Message explicite, mais limité à 1 toast toutes les 15s pour ne pas spammer en cas de coupure prolongée
      const now = Date.now();
      if (now - lastErrorToastAt.current > 15000) {
        lastErrorToastAt.current = now;
        if (isNetworkError) {
          toast.error('Connexion réseau instable : vos données sont gardées en sécurité (local + prochain essai automatique).');
        } else {
          toast.error('Erreur lors de la sauvegarde du brouillon. Vos données restent conservées localement.');
        }
      }
      return false;
    } finally {
      isSavingRef.current = false;
    }
  }, [buildPayloadFromSections, createDraftIfNeeded, draftId, hasPendingChanges, photoPreview, onDraftEvent, resolveToken]);

  // Garder la ref à jour pour les cleanup effects (évite dépendance dans useEffect)
  useEffect(() => {
    autoSaveDraftRef.current = autoSaveDraft;
  }, [autoSaveDraft]);

  // Marquer que le premier rendu est passé
  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  // DÉSACTIVÉ : Auto-save automatique après chaque modification
  // L'utilisateur doit explicitement sauvegarder via le bouton "Enregistrer"
  // Le brouillon se crée uniquement quand la 1ère section (Nom + Prénom + Adresse) est complète
  // et que l'utilisateur sauvegarde explicitement ou quitte la page
  
  // Auto-save uniquement avant de quitter la page (si 1ère section complète)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Sauvegarder si possible avant de quitter
      if (autoSaveDraftRef.current) {
        autoSaveDraftRef.current({ forceSave: true }).catch(console.error);
        e.preventDefault();
        e.returnValue = 'Des modifications non sauvegardées seront perdues.';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  useEffect(() => {
    if (!token) {
      initialDraftRequested.current = false;
      return;
    }
    if (draftId || initialDraftRequested.current) {
      return;
    }

    // NE PAS créer automatiquement le brouillon au chargement
    // L'utilisateur doit d'abord remplir la section "Informations Générales"
    // Le brouillon sera créé lors du premier auto-save après modification
    
    /* Création automatique désactivée
    let cancelled = false;
    initialDraftRequested.current = true;
    createDraftIfNeeded({ forceCreation: true })
      .then((newId) => {
        if (!cancelled && !newId) {
          initialDraftRequested.current = false;
        }
      })
      .catch((error) => {
        if (!cancelled) {
          initialDraftRequested.current = false;
          console.error('Création initiale du brouillon échouée:', error);
        }
      });

    return () => {
      cancelled = true;
    };
    */
  }, [token, draftId, createDraftIfNeeded]);

  // Charger la liste des clients au montage du composant
  useEffect(() => {
    const loadClients = async () => {
      if (!localStorage.getItem('token')) return;
      try {
        const authToken = resolveToken();
        if (!authToken) return;
        
        const response = await http.get(`${API}/clients`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        
        const clientList = response.data || [];
        setClients(clientList);
        
        // Mettre à jour les options du champ client_id
        setSections(prev => prev.map(section => {
          if (section.id === 'general_info') {
            return {
              ...section,
              fields: section.fields?.map(field => {
                if (field.id === 'client_id') {
                  return {
                    ...field,
                    options: clientList.map(c => ({
                      value: c.id,
                      label: `${c.nom} ${c.prenom || ''}`.trim()
                    }))
                  };
                }
                return field;
              })
            };
          }
          return section;
        }));
      } catch (error) {
        console.error('Erreur chargement clients:', error);
      }
    };
    
    loadClients();
  }, [resolveToken]);

  // Cleanup effect: sauvegarde UNIQUEMENT au démontage du composant (pas à chaque changement)
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = null;
      }
      // Utiliser la ref pour éviter de re-déclencher cet effect à chaque changement de autoSaveDraft
      if (autoSaveDraftRef.current) {
        autoSaveDraftRef.current({ forceSave: true }).catch(() => {});
      }
    };
  }, []); // Volontairement vide: ne doit s'exécuter qu'au démontage

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (autoSaveDraftRef.current) {
        autoSaveDraftRef.current({ forceSave: true }).catch(() => {});
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // BLOQUER l'auto-save pendant toute la durée de la finalisation
    isFinalizingRef.current = true;
    if (DEBUG_MODE) console.log('🔒 Finalisation démarrée - Auto-save désactivé');
    
    try {
      const authToken = resolveToken();
      if (!authToken) {
        throw new Error('Authentification requise');
      }

      // Vérifier que les champs obligatoires sont remplis avant la soumission
  const generalInfoSection = sections.find(s => s.id === 'general_info');
  const nameField = generalInfoSection?.fields?.find(f => f.id === 'nom');
  const firstNameField = generalInfoSection?.fields?.find(f => f.id === 'prenom');
  const addressField = generalInfoSection?.fields?.find(f => f.id === 'adresse');
      
      if (!nameField?.value?.trim() || !firstNameField?.value?.trim() || !addressField?.value?.trim()) {
        throw new Error('Veuillez remplir tous les champs obligatoires : Nom, Prénom et Adresse');
      }

      const payload = buildPayloadFromSections();
      let ensuredDraftId;
      let response;

      // Si un brouillon existe (reprise d'une recherche), le mettre à jour
      if (draftId) {
        console.log('🔄 Finalisation d\'un brouillon existant:', draftId);
        ensuredDraftId = draftId;
        
        // Sauvegarder les photos avant finalisation
        console.log('📸 Sauvegarde des photos...');
        const savedOk = await autoSaveDraft({ forceSave: true });
        if (!savedOk) {
          // Échec (texte et/ou photos) malgré les tentatives automatiques : on arrête ici
          // plutôt que de continuer et d'annoncer un succès trompeur à l'utilisateur.
          throw new Error('NETWORK_SAVE_FAILED');
        }
        console.log('✅ Photos sauvegardées');
        
        // Mettre à jour la recherche : passer en ACTIVE si c'était un brouillon, sinon préserver le statut (SHARED, PROCESSED, etc.)
        const statusToUse = (originalSearchStatus && originalSearchStatus !== 'DRAFT') ? originalSearchStatus : 'ACTIVE';
        console.log('📊 Statut utilisé pour la mise à jour:', statusToUse);
        response = await http.patch(`${API}/searches/${ensuredDraftId}`, { ...payload, status: statusToUse, search_type: searchType?.toUpperCase() }, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
      } else {
        // Pas de brouillon = créer temporairement puis immédiatement mettre en ACTIVE
        console.log('✨ Création directe d\'une recherche ACTIVE (sans brouillon intermédiaire)');
        
        // Étape 1 : Créer un brouillon temporaire
        const draftResponse = await http.post(`${API}/searches/draft`, {}, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        ensuredDraftId = draftResponse.data?.search?.id;
        console.log('📝 Brouillon temporaire créé:', ensuredDraftId);
        
        // Étape 2 : Immédiatement le passer en ACTIVE
        response = await http.patch(`${API}/searches/${ensuredDraftId}`, { ...payload, status: 'ACTIVE', search_type: searchType?.toUpperCase() }, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        console.log('✅ Immédiatement passé en ACTIVE');
        
        // Uploader les photos après création
        if (ensuredDraftId && (photoPreview.length > 0 || profilePhoto)) {
          console.log('📸 Upload des photos...');
          
          // Photo de profil
          if (profilePhoto) {
            const formData = new FormData();
            formData.append('files', profilePhoto);
            formData.append('is_profile', 'true');
            formData.append('section_id', 'profile');
            await http.post(`${API}/searches/${ensuredDraftId}/photos`, formData, {
              headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
            });
          }
          
          // Photos de sections
          for (const section of sections) {
            if (section.photos && section.photos.length > 0) {
              const unsavedPhotos = section.photos.filter(photo => photo.file);
              if (unsavedPhotos.length > 0) {
                const formData = new FormData();
                unsavedPhotos.forEach(photo => formData.append('files', photo.file));
                formData.append('section_id', section.id);
                await http.post(`${API}/searches/${ensuredDraftId}/photos`, formData, {
                  headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
                });
              }
            }
          }
          
          console.log('✅ Photos uploadées');
        }
      }
      
      console.log('✅ Recherche finalisée sur le serveur:', response.data);
      console.log('🔍 Status retourné par le backend:', response.data?.search?.status);
      
      // Ajouter au Set des recherches récemment finalisées (exclusion de la modal brouillons)
      // Vérifier que recentlyFinalizedRef existe (optionnel dans certains contextes comme le menu Bureau)
      if (recentlyFinalizedRef?.current) {
        recentlyFinalizedRef.current.add(ensuredDraftId);
        console.log('🔐 Recherche', ensuredDraftId, 'marquée comme récemment finalisée');
        // Nettoyer après 60 secondes
        setTimeout(() => {
          recentlyFinalizedRef.current.delete(ensuredDraftId);
          console.log('🔓 Recherche', ensuredDraftId, 'retirée du cache de finalisation');
        }, 60000);
      }
      
      // Émettre un événement pour notifier que les recherches doivent être rechargées
      // Petit délai pour laisser le temps au composant SearchHistory de se remonter
      console.log('📡 Émission de l\'événement searchFinalized avec:', { searchId: ensuredDraftId });
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('searchFinalized', { detail: { searchId: ensuredDraftId } }));
        console.log('✨ Événement searchFinalized émis!');
      }, 100);
      
      // Notifier le parent que la recherche n'est plus un brouillon
      if (onDraftEvent) {
        onDraftEvent({ type: 'finalized', draftId: ensuredDraftId });
        console.log('🔄 Parent notifié : brouillon finalisé et retiré de la liste');
      }
      
      setSubmitStatus({ type: 'success', message: 'Recherche finalisée avec succès! Elle apparaît maintenant dans "Mes Recherches".', location: 'Base de données' });
      setTimeout(() => setSubmitStatus(null), 5000);
    
    // Réinitialisation complète UNIQUEMENT si ce n'est PAS une édition
    // En mode édition (initialSearch fourni), on garde le formulaire intact pour que l'utilisateur voie ses modifications
    if (!initialSearch) {
      console.log('🔄 Mode création: réinitialisation du formulaire');
      
      // Nettoyer les URLs blob si présentes
      if (profilePhotoPreview && profilePhotoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(profilePhotoPreview);
      }
      photoPreview.forEach(photo => {
        if (photo.url && photo.url.startsWith('blob:')) {
          URL.revokeObjectURL(photo.url);
        }
      });
      
      // Nettoyer TOUTES les photos des sections (y compris les URLs blob)
      sections.forEach(section => {
        if (section.photos && Array.isArray(section.photos)) {
          section.photos.forEach(photo => {
            if (photo.url && photo.url.startsWith('blob:')) {
              URL.revokeObjectURL(photo.url);
            }
          });
        }
      });
      
      // Créer de nouvelles sections complètement vides (pas de photos, pas de fields avec values)
      const freshSections = createInitialSections(generateReferenceNumber);
      setSections(freshSections);
      setPhotoPreview([]);
      setSavedPhotos([]); // ✅ Vider les photos sauvegardées
      setProfilePhoto(null);
      setProfilePhotoPreview(null);
      setDraftId(null);
      initialDraftRequested.current = false;
      setHasUserInteraction(false);
      setHasPendingChanges(false);
      setAutoSaveState({ ...INITIAL_AUTO_SAVE_STATE });
      
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = null;
      }
    } else {
      console.log('✏️ Mode édition: conservation du formulaire avec les modifications');
      // En mode édition, on garde le formulaire tel quel
      // L'utilisateur verra ses modifications et pourra fermer la modal manuellement
      setHasPendingChanges(false); // Pas de changements en attente après la sauvegarde
    }
    
    // Notifier le parent dans tous les cas
    onDraftEvent?.({ type: 'finalized', draftId: ensuredDraftId });
    } catch (e) {
      const isSaveFailure = e.message === 'NETWORK_SAVE_FAILED';
      const isNetworkError = isSaveFailure || e.code === 'ECONNABORTED' || e.code === 'ERR_NETWORK' ||
        e.message?.toLowerCase().includes('network') || !e.response;
      let msg = 'Erreur lors de la finalisation';
      if (isSaveFailure) {
        msg = 'Le texte et/ou les photos n\'ont pas pu être envoyés après plusieurs tentatives (connexion instable). Rien n\'a été perdu : réessayez "Finaliser" dès que la connexion est meilleure.';
      } else if (isNetworkError) {
        msg = 'Connexion réseau instable : la finalisation a échoué après plusieurs tentatives automatiques. Vos données restent conservées, réessayez dès que la connexion est meilleure.';
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      } else if (e.message) {
        msg = e.message;
      }
      setSubmitStatus({ type: 'error', message: msg });
      setTimeout(() => setSubmitStatus(null), 8000);
    } finally {
      // DÉBLOQUER l'auto-save
      isFinalizingRef.current = false;
      console.log('🔓 Finalisation terminée - Auto-save réactivé');
      setLoading(false);
    }
  };

  // New functions for enhanced form
  const [submitStatus, setSubmitStatus] = useState(null);
  const isFinalizingRef = useRef(false); // Flag pour bloquer l'auto-save pendant finalisation

  const handleSectionTitleChange = (sectionId, newTitle) => {
    setHasUserInteraction(true);
    setHasPendingChanges(true);
    setSections(prev => prev.map(section => 
      section.id === sectionId ? { ...section, title: newTitle } : section
    ));
  };

  const addNewSection = () => {
    const newSection = {
      id: `custom_${Date.now()}`,
      type: 'custom',
      title: `Section ${sections.length + 1}`,
      icon: Plus,
      value: '',
      required: false,
      placeholder: 'Décrivez le contenu de cette section...',
      fieldType: 'textarea',
      rows: 4,
      removable: true,
      collapsed: false,
      photos: []
    };
    setSections(prev => [...prev, newSection]);
    setHasUserInteraction(true);
    setHasPendingChanges(true);
  };

  const handleSectionPhotos = (sectionId, files) => {
    if (files && files.length > 0) {
      const newPhotos = Array.from(files).map((file, index) => ({
        file,
        url: URL.createObjectURL(file),
        name: file.name
      }));
      
      setSections(prev => prev.map(section => 
        section.id === sectionId 
          ? { ...section, photos: [...(section.photos || []), ...newPhotos] }
          : section
      ));
    }
  };

  const removeSectionPhoto = async (sectionId, photoIndex) => {
    const section = sections.find(s => s.id === sectionId);
    const photoToRemove = section?.photos?.[photoIndex];
    
    // Si la photo a un filename (donc elle est sur le serveur) et qu'on a un draftId, la supprimer du serveur
    if (photoToRemove?.filename && draftId) {
      try {
        await http.delete(`${API}/searches/${draftId}/photos/${photoToRemove.filename}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (error) {
        console.error('Erreur lors de la suppression de la photo:', error);
      }
    }
    
    setSections(prev => prev.map(section => {
      if (section.id === sectionId && section.photos) {
        const newPhotos = section.photos.filter((_, index) => index !== photoIndex);
        // Revoke URL for removed photo
        if (section.photos[photoIndex]) {
          URL.revokeObjectURL(section.photos[photoIndex].url);
        }
        return { ...section, photos: newPhotos };
      }
      return section;
    }));
  };

  // New function for photo drag and drop
  const handleSectionPhotoDragEnd = (sectionId, result) => {
    if (!result.destination) {
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return;
    }

    setSections(prev => prev.map(section => {
      if (section.id === sectionId && section.photos) {
        const reorderedPhotos = Array.from(section.photos);
        const [reorderedPhoto] = reorderedPhotos.splice(sourceIndex, 1);
        reorderedPhotos.splice(destinationIndex, 0, reorderedPhoto);
        
        return { ...section, photos: reorderedPhotos };
      }
      return section;
    }));
  };

  // Fonction pour charger un draft (utilisée par useImperativeHandle et useEffect)
  const loadDraft = useCallback((draft) => {
    if (!draft) {
      console.log('❌ [loadDraft] Pas de brouillon fourni');
      return;
    }

    console.log('📥 [loadDraft] Chargement du brouillon:', draft.id, 'photos:', draft.photos?.length || 0);

    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = null;
    }

    const baseSections = createInitialSections(generateReferenceNumber).map((section) => {
      if (section.id === 'general_info') {
        return {
          ...section,
          fields: section.fields?.map((field) => {
            if (field.id === 'nom') {
              return { ...field, value: draft.nom || '' };
            }
            if (field.id === 'prenom') {
              return { ...field, value: draft.prenom || '' };
            }
            if (field.id === 'adresse') {
              return { ...field, value: draft.location || '' };
            }
            if (field.id === 'ville') {
              return { ...field, value: draft.ville || '' };
            }
            if (field.id === 'code_postal') {
              return { ...field, value: draft.code_postal || '' };
            }
            return field;
          })
        };
      }
      if (section.id === 'description') {
        return { ...section, value: draft.description || '' };
      }
      if (section.id === 'observations') {
        // Parser le JSON pour récupérer le texte observations
        try {
          const observationsData = JSON.parse(draft.observations || '{}');
          return { ...section, value: observationsData.text || '' };
        } catch (e) {
          // Si ce n'est pas du JSON, c'est l'ancien format texte simple
          return { ...section, value: draft.observations || '' };
        }
      }
      return section;
    });

    setDraftId(draft.id);
    setPhotoPreview([]);
    setHasUserInteraction(false);
    setHasPendingChanges(false);
    
    // Parser et restaurer les sections personnalisées
    let customSectionsToAdd = [];
    try {
      const observationsData = JSON.parse(draft.observations || '{}');
      if (observationsData.customSections && Array.isArray(observationsData.customSections)) {
        customSectionsToAdd = observationsData.customSections.map(cs => ({
          id: cs.id,
          type: 'custom',
          title: cs.title,
          icon: Plus,
          value: cs.value,
          required: false,
          placeholder: 'Décrivez le contenu de cette section...',
          fieldType: 'textarea',
          rows: 4,
          removable: true,
          collapsed: false,
          photos: []
        }));
        console.log(`✅ [loadDraft] ${customSectionsToAdd.length} sections personnalisées restaurées`);
      }
    } catch (e) {
      console.log('⚠️ [loadDraft] Pas de sections personnalisées ou ancien format');
    }

    // Charger les photos existantes depuis le serveur
    if (draft.photos && Array.isArray(draft.photos) && draft.photos.length > 0) {
      console.log('📸 [loadDraft] Photos trouvées:', draft.photos);
      setSavedPhotos(draft.photos);
      
      // A. Restaurer la photo de profil si elle existe
      const profilePhotoData = draft.photos.find(p => p.is_profile);
      console.log('🖼️ [loadDraft] Photo de profil:', profilePhotoData);
      if (profilePhotoData) {
        const photoUrl = profilePhotoData.url || `${API}/searches/${draft.id}/photos/${profilePhotoData.filename}`;
        console.log('✅ [loadDraft] URL photo de profil:', photoUrl);
        setProfilePhotoPreview(photoUrl);
        setProfilePhoto(null);
      } else {
        console.log('⚠️ [loadDraft] Aucune photo de profil trouvée');
        setProfilePhoto(null);
        setProfilePhotoPreview(null);
      }

      // B. Distribuer les photos dans leurs sections respectives (sections de base + custom)
      const allSections = [...baseSections, ...customSectionsToAdd];
      const sectionsWithPhotos = allSections.map(section => {
        // Filtrer les photos qui appartiennent à cette section
        const sectionPhotos = draft.photos
          .filter(p => p.section_id === section.id && !p.is_profile)
          .map(p => ({
            url: p.url || `${API}/searches/${draft.id}/photos/${p.filename}`,
            filename: p.filename,
            name: p.original_name || p.filename
          }));

        if (sectionPhotos.length > 0) {
          console.log(`📸 [loadDraft] Section ${section.id}: ${sectionPhotos.length} photos chargées`);
          return { ...section, photos: sectionPhotos };
        }
        return section;
      });

      console.log('✅ [loadDraft] Sections avec photos distribuées:', sectionsWithPhotos.filter(s => s.photos?.length > 0).map(s => ({ id: s.id, photosCount: s.photos.length })));
      setSections(sectionsWithPhotos);
    } else {
      setSavedPhotos([]);
      setProfilePhoto(null);
      setProfilePhotoPreview(null);
      setSections([...baseSections, ...customSectionsToAdd]);
    }

    // Définir le draftId pour que la finalisation sache qu'il s'agit d'une édition
    setDraftId(draft.id);
    console.log('✅ [loadDraft] draftId défini:', draft.id);
    
    // Stocker le statut original pour le préserver lors de la finalisation
    setOriginalSearchStatus(draft.status);
    console.log('📊 [loadDraft] Statut original conservé:', draft.status);

    const lastSaved = draft.updated_at || draft.created_at;
    setAutoSaveState({ saving: false, lastSaved: lastSaved ? new Date(lastSaved) : null, error: null });
  }, [generateReferenceNumber]);

  useImperativeHandle(ref, () => ({
    forceAutoSave: async () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = null;
      }
      return autoSaveDraft({ forceSave: true });
    },
    loadDraft: loadDraft,
    getDraftId: () => draftId,
    ensureDraft: (options = {}) => createDraftIfNeeded({ forceCreation: false, ...options })
  }), [autoSaveDraft, createDraftIfNeeded, draftId, generateReferenceNumber]);

  // Charger automatiquement le draft existant ou initialSearch au montage du composant
  useEffect(() => {
    // 🛟 Restaure la dernière copie locale de secours si aucun brouillon serveur n'est trouvé
    // (cas : l'app a été fermée/coupée avant que la toute première sauvegarde réseau aboutisse)
    const restoreFromLocalBackupIfAny = () => {
      try {
        const raw = localStorage.getItem(DRAFT_BACKUP_KEY);
        if (!raw) return;
        const backup = JSON.parse(raw);
        if (!backup?.payload || Date.now() - backup.savedAt > 24 * 60 * 60 * 1000) {
          localStorage.removeItem(DRAFT_BACKUP_KEY);
          return;
        }
        const { payload } = backup;
        let observationsText = '';
        let customSectionsToAdd = [];
        try {
          const obsData = JSON.parse(payload.observations || '{}');
          observationsText = obsData.text || '';
          customSectionsToAdd = Array.isArray(obsData.customSections) ? obsData.customSections.map(cs => ({
            id: cs.id, type: 'custom', title: cs.title, icon: Plus, value: cs.value, required: false,
            placeholder: 'Décrivez le contenu de cette section...', fieldType: 'textarea', rows: 4,
            removable: true, collapsed: false, photos: []
          })) : [];
        } catch (e) {
          observationsText = payload.observations || '';
        }

        const baseSections = createInitialSections(generateReferenceNumber).map((section) => {
          if (section.id === 'general_info') {
            return {
              ...section,
              fields: section.fields?.map((field) => {
                if (field.id === 'nom') return { ...field, value: payload.nom || '' };
                if (field.id === 'prenom') return { ...field, value: payload.prenom || '' };
                if (field.id === 'adresse') return { ...field, value: payload.location || '' };
                if (field.id === 'ville') return { ...field, value: payload.ville || '' };
                if (field.id === 'code_postal') return { ...field, value: payload.code_postal || '' };
                return field;
              })
            };
          }
          if (section.id === 'description') return { ...section, value: payload.description || '' };
          if (section.id === 'observations') return { ...section, value: observationsText };
          return section;
        });

        setSections([...baseSections, ...customSectionsToAdd]);
        setDraftId(backup.draftId || null);
        setHasPendingChanges(true);
        toast.info('Des données non enregistrées ont été retrouvées et restaurées (dernière sauvegarde locale).');
      } catch (e) {
        console.warn('Restauration de la sauvegarde locale impossible:', e);
      }
    };

    const loadExistingDraft = async () => {
      // Priorité 1: Charger initialSearch si fourni
      if (initialSearch) {
        console.log('🔄 Chargement de initialSearch:', initialSearch.id);
        loadDraft(initialSearch);
        if (onSearchLoaded) {
          onSearchLoaded();
        }
        return;
      }
      
      // Priorité 2: Charger le draft existant
      try {
        const response = await http.get(`${API}/searches`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: 'DRAFT', limit: 1 }
        });
        
        if (response.data && response.data.length > 0) {
          const existingDraft = response.data[0];
          console.log('📥 Draft existant chargé:', existingDraft);
          
          // Utiliser directement les setters au lieu de draftActions
          if (existingDraft) {
            const baseSections = createInitialSections(generateReferenceNumber).map((section) => {
              if (section.id === 'general_info') {
                return {
                  ...section,
                  fields: section.fields?.map((field) => {
                    if (field.id === 'nom') {
                      return { ...field, value: existingDraft.nom || '' };
                    }
                    if (field.id === 'prenom') {
                      return { ...field, value: existingDraft.prenom || '' };
                    }
                    if (field.id === 'adresse') {
                      return { ...field, value: existingDraft.location || '' };
                    }
                    return field;
                  })
                };
              }
              if (section.id === 'description') {
                return { ...section, value: existingDraft.description || '' };
              }
              if (section.id === 'observations') {
                return { ...section, value: existingDraft.observations || '' };
              }
              return section;
            });

            setDraftId(existingDraft.id);
            setPhotoPreview([]);
            setHasUserInteraction(false);
            setHasPendingChanges(false);

            // Charger les photos existantes et les distribuer par section_id
            if (existingDraft.photos && Array.isArray(existingDraft.photos) && existingDraft.photos.length > 0) {
              setSavedPhotos(existingDraft.photos);
              
              // A. Restaurer la photo de profil
              const profilePhotoData = existingDraft.photos.find(p => p.is_profile);
              if (profilePhotoData) {
                const photoUrl = profilePhotoData.url || `${API}/searches/${existingDraft.id}/photos/${profilePhotoData.filename}`;
                setProfilePhotoPreview(photoUrl);
                setProfilePhoto(null);
              } else {
                setProfilePhoto(null);
                setProfilePhotoPreview(null);
              }

              // B. Distribuer les photos dans leurs sections respectives
              const sectionsWithPhotos = baseSections.map(section => {
                // Filtrer les photos qui appartiennent à cette section
                const sectionPhotos = existingDraft.photos
                  .filter(p => p.section_id === section.id && !p.is_profile)
                  .map(p => ({
                    url: p.url || `${API}/searches/${existingDraft.id}/photos/${p.filename}`,
                    filename: p.filename,
                    name: p.original_name || p.filename
                  }));

                if (sectionPhotos.length > 0) {
                  console.log(`📸 Section ${section.id}: ${sectionPhotos.length} photos chargées`);
                  return { ...section, photos: sectionPhotos };
                }
                return section;
              });

              console.log('✅ Sections avec photos distribuées:', sectionsWithPhotos.filter(s => s.photos?.length > 0).map(s => ({ id: s.id, photosCount: s.photos.length })));
              setSections(sectionsWithPhotos);
            } else {
              setSavedPhotos([]);
              setProfilePhoto(null);
              setProfilePhotoPreview(null);
              setSections(baseSections);
            }

            const lastSaved = existingDraft.updated_at || existingDraft.created_at;
            setAutoSaveState({ saving: false, lastSaved: lastSaved ? new Date(lastSaved) : null, error: null });
          }
        } else {
          restoreFromLocalBackupIfAny();
        }
      } catch (error) {
        console.error('❌ Erreur chargement draft existant:', error);
        restoreFromLocalBackupIfAny();
      }
    };

    // NOTE: Chargement automatique DÉSACTIVÉ sauf si initialSearch est fourni
    // L'utilisateur doit charger manuellement via "Brouillon en attente" ou "Modifier"
    if (token && !draftId) {
      loadExistingDraft();
    }
  }, [token, draftId, generateReferenceNumber, initialSearch, onSearchLoaded]);

  return (
    <div className="max-w-4xl mx-auto">
      <Card className="bg-white rounded-2xl border-0 shadow-sm">
        <CardHeader className="pb-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-violet-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Search className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-semibold text-gray-900">
              {searchType === 'rapport' ? 'Nouveau Compte-Rendu Chantier' : searchType === 'infiltration' ? "Nouvelle Recherche d'Infiltration" : 'Nouvelle Recherche'}
            </CardTitle>
            <CardDescription className="text-gray-600 mt-2">
              Formulaire personnalisable avec sections modulaires
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="px-8 pb-8">
          {/* Auto-save explanation */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Sauvegarde automatique :</span> Dès que vous complétez les 3 champs obligatoires (Nom, Prénom, Adresse) de la section "Informations Générales", un brouillon est créé. Vos modifications sont ensuite automatiquement enregistrées 2 secondes après chaque changement. Vous pouvez quitter et revenir sans perdre votre travail.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Auto-save indicator */}
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-gray-500">
                {autoSaveState.saving ? (
                  <span className="inline-flex items-center text-amber-600"><Loader2 className="h-4 w-4 mr-1 animate-spin"/>Sauvegarde du brouillon…</span>
                ) : autoSaveState.error ? (
                  <span className="inline-flex items-center text-red-600"><AlertCircle className="h-4 w-4 mr-1"/>Erreur auto‑save</span>
                ) : hasPendingChanges ? (
                  <span className="inline-flex items-center text-amber-600"><Clock className="h-4 w-4 mr-1"/>Modifications en attente…</span>
                ) : autoSaveState.lastSaved ? (
                  <span className="inline-flex items-center text-green-600"><Check className="h-4 w-4 mr-1"/>Sauvegardé {autoSaveState.lastSaved.toLocaleTimeString()}</span>
                ) : draftId ? (
                  <span className="text-gray-400">Brouillon créé</span>
                ) : (
                  <span className="text-gray-400">Complétez Nom, Prénom et Adresse pour créer le brouillon</span>
                )}
              </div>
            </div>
            {/* Dynamic Sections */}
            <div className="space-y-6">
              {sections.map((section, index) => (
                <div key={section.id} className="relative group bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-all duration-200 hover:shadow-sm">
                  {/* Section Header */}
                  <div className="flex items-center justify-between p-6 border-b border-gray-100">
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-violet-600 rounded-lg flex items-center justify-center flex-shrink-0">
                        <section.icon className="h-5 w-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={section.title}
                          onChange={(e) => handleSectionTitleChange(section.id, e.target.value)}
                          className="font-medium text-gray-900 bg-transparent border-none outline-none focus:bg-gray-50 px-2 py-1 rounded text-lg w-full"
                          placeholder="Nom de la section..."
                        />
                      </div>
                    </div>
                    
                    {/* Section Controls */}
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => toggleSectionCollapse(section.id)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        {section.collapsed ? (
                          <ChevronDown className="h-5 w-5" />
                        ) : (
                          <ChevronUp className="h-5 w-5" />
                        )}
                      </button>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center space-x-1">
                        <button
                          type="button"
                          onClick={() => moveSectionUp(index)}
                          disabled={index === 0}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Déplacer vers le haut"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveSectionDown(index)}
                          disabled={index === sections.length - 1}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Déplacer vers le bas"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeSection(section.id)}
                          className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer la section"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Section Content */}
                  {!section.collapsed && (
                    <div className="p-6">
                      {/* Multiple fields for general_info section */}
                      {section.fieldType === 'multiple' && section.fields ? (
                        <div className="space-y-4 mb-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {section.fields.map((field) => {
                              // Gérer les champs conditionnels
                              if (field.dependsOn) {
                                const dependentField = section.fields.find(f => f.id === field.dependsOn);
                                if (dependentField && dependentField.value !== field.dependsOnValue) {
                                  return null; // Ne pas afficher si la condition n'est pas remplie
                                }
                              }
                              
                              return (
                              <div key={field.id} className="space-y-2" style={field.type === 'radio' ? {gridColumn: '1 / -1'} : {}}>
                                <label className="block text-sm font-medium text-gray-700">
                                  {field.label}
                                  {field.required && <span className="text-red-500 ml-1">*</span>}
                                </label>
                                {field.type === 'radio' ? (
                                  <div className="flex gap-4 p-4 bg-gray-50 rounded-xl">
                                    {field.options?.map(option => (
                                      <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                                        <input
                                          type="radio"
                                          name={field.id}
                                          value={option.value}
                                          checked={field.value === option.value}
                                          onChange={(e) => handleFieldChange(section.id, field.id, e.target.value)}
                                          className="w-4 h-4 text-gray-900"
                                        />
                                        <span className="text-sm">{option.label}</span>
                                      </label>
                                    ))}
                                  </div>
                                ) : field.type === 'select' ? (
                                  <select
                                    value={field.value}
                                    onChange={(e) => handleFieldChange(section.id, field.id, e.target.value)}
                                    className="w-full rounded-xl border border-gray-200 focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 transition-colors duration-200 text-base p-4"
                                    required={field.required}
                                  >
                                    <option value="">-- Sélectionner un client --</option>
                                    {field.options?.map(option => (
                                      <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                  </select>
                                ) : field.type === 'textarea' ? (
                                  <textarea
                                    value={field.value}
                                    onChange={(e) => handleFieldChange(section.id, field.id, e.target.value)}
                                    placeholder={field.placeholder || `Saisissez ${field.label.toLowerCase()}...`}
                                    rows={3}
                                    className="w-full rounded-xl border border-gray-200 focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 transition-colors duration-200 text-base p-4 resize-none"
                                    required={field.required}
                                    readOnly={field.readonly}
                                  />
                                ) : (
                                  <input
                                    type={field.type}
                                    value={field.value}
                                    onChange={(e) => handleFieldChange(section.id, field.id, e.target.value)}
                                    placeholder={field.placeholder || `Saisissez ${field.label.toLowerCase()}...`}
                                    className="w-full rounded-xl border border-gray-200 focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 transition-colors duration-200 text-base p-4"
                                    required={field.required}
                                    readOnly={field.readonly}
                                  />
                                )}
                              </div>
                              );
                            })}
                          </div>
                          
                          {/* Profile Photo for general_info section - Design amélioré avec fond */}
                          {section.id === 'general_info' && (
                            <div className="mt-6 relative overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
                              {/* Image de fond avec effet de fondu */}
                              {profilePhotoPreview && (
                                <div 
                                  className="absolute inset-0 bg-cover bg-center opacity-10 blur-sm scale-110"
                                  style={{ backgroundImage: `url(${profilePhotoPreview})` }}
                                />
                              )}
                              
                              {/* Contenu principal */}
                              <div className="relative bg-gradient-to-br from-white/95 to-gray-50/95 backdrop-blur-sm p-6">
                                <h5 className="text-sm font-semibold text-gray-800 mb-4 flex items-center">
                                  <User className="h-4 w-4 mr-2" />
                                  Photo de profil (optionnelle)
                                </h5>
                                
                                <div className="flex items-center gap-6">
                                  {/* Aperçu de la photo */}
                                  <div className="relative group">
                                    {profilePhotoPreview ? (
                                      <>
                                        <div className="relative">
                                          <img
                                            src={profilePhotoPreview}
                                            alt="Profile preview"
                                            className="w-24 h-24 object-cover rounded-2xl border-4 border-white shadow-lg transition-transform duration-300 group-hover:scale-105"
                                          />
                                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl" />
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setProfilePhoto(null);
                                            setProfilePhotoPreview(null);
                                            setHasUserInteraction(true);
                                            setHasPendingChanges(true);
                                          }}
                                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-600 transition-all shadow-lg hover:scale-110"
                                          title="Supprimer la photo"
                                        >
                                          <X className="h-4 w-4" />
                                        </button>
                                      </>
                                    ) : (
                                      <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-2xl flex items-center justify-center bg-white/50 backdrop-blur-sm transition-colors hover:border-gray-400">
                                        <User className="h-10 w-10 text-gray-400" />
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Zone de texte et bouton */}
                                  <div className="flex-1">
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={handleProfilePhotoChange}
                                      className="hidden"
                                      id="profile-photo"
                                    />
                                    <label
                                      htmlFor="profile-photo"
                                      className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white rounded-xl cursor-pointer transition-all font-medium shadow-md hover:shadow-lg hover:scale-105"
                                    >
                                      <Camera className="h-4 w-4 mr-2" />
                                      {profilePhotoPreview ? 'Changer la photo' : 'Choisir une photo'}
                                    </label>
                                    <p className="text-xs text-gray-600 mt-2 flex items-center">
                                      <span className="inline-block w-1 h-1 bg-gray-400 rounded-full mr-2"></span>
                                      Formats acceptés: JPG, PNG, WebP (max 5MB)
                                    </p>
                                    {profilePhotoPreview && (
                                      <p className="text-xs text-green-600 mt-1 flex items-center font-medium">
                                        <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                                        Photo ajoutée avec succès
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Default text content for other sections */
                        <div className="space-y-4">
                          <textarea
                            placeholder="Décrivez le contenu de cette section..."
                            value={section.value || ''}
                            onChange={(e) => handleSectionChange(section.id, e.target.value)}
                            rows={4}
                            className="w-full rounded-xl border border-gray-200 focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 transition-colors duration-200 text-base p-4 resize-none"
                          />
                          
                          {/* Photos pour chaque section */}
                          <div className="p-4 bg-gray-50 rounded-xl space-y-3">
                            <div className="flex items-center justify-between">
                              <label className="text-sm font-medium text-gray-700">
                                Photos de cette section
                              </label>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) => handleSectionPhotos(section.id, e.target.files)}
                                className="hidden"
                                id={`section-photos-${section.id}`}
                              />
                              <label
                                htmlFor={`section-photos-${section.id}`}
                                className="inline-flex items-center px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-700 rounded-lg cursor-pointer transition-colors text-sm font-medium border border-gray-200"
                              >
                                <Camera className="h-4 w-4 mr-1.5" />
                                Ajouter des photos
                              </label>
                            </div>

                            {section.photos && section.photos.length > 0 ? (
                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                {section.photos.map((photo, photoIndex) => (
                                  <div 
                                    key={photoIndex} 
                                    className="relative group cursor-move bg-white rounded-lg border-2 border-gray-200 hover:border-blue-400 transition-all"
                                    draggable
                                    onDragStart={(e) => {
                                      e.dataTransfer.effectAllowed = 'move';
                                      e.dataTransfer.setData('text/plain', JSON.stringify({
                                        sectionId: section.id,
                                        fromIndex: photoIndex
                                      }));
                                    }}
                                    onDragOver={(e) => {
                                      e.preventDefault();
                                      e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
                                    }}
                                    onDragLeave={(e) => {
                                      e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                                    }}
                                    onDrop={(e) => {
                                      e.preventDefault();
                                      e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                                      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                                      if (data.sectionId === section.id && data.fromIndex !== photoIndex) {
                                        // Réorganiser les photos
                                        setSections(prevSections => prevSections.map(s => {
                                          if (s.id === section.id) {
                                            const newPhotos = [...s.photos];
                                            const [movedPhoto] = newPhotos.splice(data.fromIndex, 1);
                                            newPhotos.splice(photoIndex, 0, movedPhoto);
                                            return { ...s, photos: newPhotos };
                                          }
                                          return s;
                                        }));
                                        setHasPendingChanges(true);
                                      }
                                    }}
                                  >
                                    {/* Image avec fond pour voir l'image complète */}
                                    <div 
                                      className="w-full h-32 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer"
                                      onClick={() => {
                                        setLightboxImage(photo.url);
                                        setShowLightbox(true);
                                      }}
                                    >
                                      <img
                                        src={photo.url}
                                        alt={photo.name}
                                        className="max-w-full max-h-full object-contain"
                                      />
                                    </div>
                                    
                                    {/* Boutons d'action */}
                                    <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setAnnotatingPhoto({ url: photo.url, sectionId: section.id, photoIndex });
                                        }}
                                        className="bg-orange-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-orange-600 shadow-lg"
                                        title="Annoter"
                                      >
                                        <Edit2 className="h-3.5 w-3.5" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setLightboxImage(photo.url);
                                          setShowLightbox(true);
                                        }}
                                        className="bg-blue-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-blue-600 shadow-lg"
                                        title="Voir en détail"
                                      >
                                        <Eye className="h-3.5 w-3.5" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => removeSectionPhoto(section.id, photoIndex)}
                                        className="bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-600 shadow-lg"
                                        title="Supprimer"
                                      >
                                        <X className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                    
                                    {/* Barre bas : ◀ numéro ▶ — fonctionne souris ET tactile */}
                                    <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between bg-black/75 text-white px-1 py-1 rounded-b-lg">
                                      <button
                                        type="button"
                                        disabled={photoIndex === 0}
                                        onPointerDown={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          if (photoIndex > 0) {
                                            setSections(prev => prev.map(s => {
                                              if (s.id !== section.id) return s;
                                              const photos = [...s.photos];
                                              [photos[photoIndex - 1], photos[photoIndex]] = [photos[photoIndex], photos[photoIndex - 1]];
                                              return { ...s, photos };
                                            }));
                                            setHasPendingChanges(true);
                                          }
                                        }}
                                        className={`w-7 h-7 rounded flex items-center justify-center text-sm font-bold select-none ${
                                          photoIndex === 0 ? 'opacity-25 cursor-not-allowed' : 'hover:bg-white/20 active:bg-white/40'
                                        }`}
                                      >◀</button>
                                      <span className="text-xs font-medium flex items-center gap-1 select-none">
                                        <GripVertical className="h-3 w-3" />
                                        #{photoIndex + 1}
                                      </span>
                                      <button
                                        type="button"
                                        disabled={photoIndex === section.photos.length - 1}
                                        onPointerDown={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          if (photoIndex < section.photos.length - 1) {
                                            setSections(prev => prev.map(s => {
                                              if (s.id !== section.id) return s;
                                              const photos = [...s.photos];
                                              [photos[photoIndex + 1], photos[photoIndex]] = [photos[photoIndex], photos[photoIndex + 1]];
                                              return { ...s, photos };
                                            }));
                                            setHasPendingChanges(true);
                                          }
                                        }}
                                        className={`w-7 h-7 rounded flex items-center justify-center text-sm font-bold select-none ${
                                          photoIndex === section.photos.length - 1 ? 'opacity-25 cursor-not-allowed' : 'hover:bg-white/20 active:bg-white/40'
                                        }`}
                                      >▶</button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-500 text-center py-3 border border-dashed border-gray-200 rounded-lg">
                                Aucune photo ajoutée pour cette section
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Add New Section Button */}
              <button
                type="button"
                onClick={addNewSection}
                className="w-full flex items-center justify-center space-x-3 py-6 border-2 border-dashed border-gray-300 hover:border-gray-400 rounded-xl transition-colors group"
              >
                <div className="w-10 h-10 bg-gray-100 group-hover:bg-gradient-to-br group-hover:from-blue-600 group-hover:to-violet-600 rounded-lg flex items-center justify-center transition-colors">
                  <Plus className="h-5 w-5 text-gray-600 group-hover:text-white" />
                </div>
                <span className="text-gray-600 group-hover:text-gray-900 font-medium">
                  Ajouter une nouvelle section
                </span>
              </button>
            </div>

            {/* Enhanced Submit Button */}
            <div className="pt-6 border-t border-gray-200 space-y-3">
              {/* Bouton Finaliser la recherche */}
              <Button 
                type="submit" 
                disabled={loading || sections.length === 0} 
                className="w-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white rounded-xl py-4 text-base font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-3">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Finalisation en cours...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-3">
                    <CheckCheck className="h-5 w-5" />
                    <span>Finaliser la recherche</span>
                    <div className="ml-2">
                      <Lightbulb className="h-4 w-4 text-yellow-400 animate-pulse" />
                    </div>
                  </div>
                )}
              </Button>
              
              {/* Success/Error Feedback */}
              {submitStatus && (
                <div className={`mt-4 p-4 rounded-xl flex items-center space-x-3 ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  {submitStatus.type === 'success' ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium">{submitStatus.message}</p>
                    {submitStatus.location && (
                      <div className="flex items-center space-x-2 mt-2 text-sm">
                        <Lightbulb className="h-4 w-4 text-yellow-600" />
                        <span>Recherche enregistrée dans : {submitStatus.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <p className="text-sm text-gray-500 text-center mt-4">
                Personnalisez entièrement votre rapport terrain avec des sections modulaires
              </p>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Modal Annotateur Photo */}
      {annotatingPhoto && (
        <div className="fixed inset-0 z-[9999] bg-black/95">
          <div className="w-full h-full">
            <Annotator
              imageSrc={annotatingPhoto.url}
              onSave={({ dataUrl, annotations: annData }) => {
                // Convertir le dataUrl en File et remplacer la photo dans la section
                fetch(dataUrl)
                  .then(res => res.blob())
                  .then(blob => {
                    const file = new File([blob], `annotated_${Date.now()}.png`, { type: 'image/png' });
                    const newUrl = URL.createObjectURL(file);
                    setSections(prev => prev.map(s => {
                      if (s.id === annotatingPhoto.sectionId) {
                        const newPhotos = [...(s.photos || [])];
                        if (newPhotos[annotatingPhoto.photoIndex]) {
                          // Revoke old preview URL if it was a local blob
                          const oldPhoto = newPhotos[annotatingPhoto.photoIndex];
                          if (oldPhoto.url && oldPhoto.url.startsWith('blob:')) {
                            URL.revokeObjectURL(oldPhoto.url);
                          }
                          newPhotos[annotatingPhoto.photoIndex] = {
                            ...oldPhoto,
                            file,
                            url: newUrl,
                            name: file.name,
                            annotated: true,
                            annotationData: annData,
                          };
                        }
                        return { ...s, photos: newPhotos };
                      }
                      return s;
                    }));
                    setHasPendingChanges(true);
                    setAnnotatingPhoto(null);
                    toast.success('Photo annotée enregistrée');
                  });
              }}
              onClose={() => setAnnotatingPhoto(null)}
            />
          </div>
        </div>
      )}

      {/* Lightbox Modal pour visualiser les photos en grand */}
      {showLightbox && (
        <div 
          className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowLightbox(false);
              setLightboxImage(null);
            }
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowLightbox(false);
              setLightboxImage(null);
            }}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full w-12 h-12 flex items-center justify-center backdrop-blur-sm transition-all z-[10000]"
          >
            <X className="h-6 w-6" />
          </button>
          
          <div 
            className="relative max-w-7xl max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage}
              alt="Aperçu"
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            />
          </div>
          
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm">
            Cliquez n'importe où pour fermer
          </div>
        </div>
      )}
    </div>
  );
});

SearchForm.displayName = 'SearchForm';
export { SearchForm, AuthContext };

// Apple-style Search History Component
const SearchHistory = ({ onEditSearch }) => {
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [hasMore, setHasMore] = useState(false);
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');
  const [editingSearch, setEditingSearch] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [selectedSearchForPDF, setSelectedSearchForPDF] = useState(null);
  const [pdfUrl, setPdfUrl] = useState('');
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadSearches();
  }, [page, sortBy, sortDir]);

  // Écouter l'événement de finalisation de recherche
  useEffect(() => {
    const handleSearchFinalized = (event) => {
      console.log('🔔 [SearchHistory] Événement searchFinalized reçu!', event.detail);
      console.log('🔄 Rechargement des recherches...');
      setLoading(true);
      setPage(1); // Revenir à la première page pour voir la nouvelle recherche
      loadSearches();
    };
    
    console.log('✅ [SearchHistory] Écouteur d\'événement searchFinalized installé');
    window.addEventListener('searchFinalized', handleSearchFinalized);
    return () => {
      console.log('❌ [SearchHistory] Écouteur d\'événement searchFinalized retiré');
      window.removeEventListener('searchFinalized', handleSearchFinalized);
    };
  }, []); // Pas de dépendances - on veut juste un écouteur qui utilise la dernière version de loadSearches

  const loadSearches = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const params = {
        page,
        page_size: pageSize,
        sort_by: sortBy,
        sort_dir: sortDir,
      };
      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      if (filter !== 'all') {
        params.status = filter.toUpperCase();
      }
      const response = await http.get(`${API}/searches`, {
        params,
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      console.log('📥 [SearchHistory] Réponse API reçue:', response.data);
      
      if (Array.isArray(response.data)) {
        // rétro‑compat si backend renvoie liste simple
        console.log('📋 [SearchHistory] Format array simple, recherches:', response.data.length);
        setSearches(response.data);
        setHasMore(false);
      } else if (response.data.data) {
        // Format standardisé : { data: [...], count: X }
        console.log('📋 [SearchHistory] Format standardisé, recherches:', response.data.data.length);
        setSearches(response.data.data || []);
        setHasMore(!!response.data.has_more);
      } else {
        // Format pagination : { items: [...], has_more: true }
        console.log('📋 [SearchHistory] Format pagination, recherches:', (response.data.items || []).length);
        setSearches(response.data.items || []);
        setHasMore(!!response.data.has_more);
      }
    } catch (error) {
      console.error('❌ [SearchHistory] Erreur chargement recherches:', error);
    } finally {
      setLoading(false);
    }
  };

  const onSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const applySearch = () => {
    setLoading(true);
    loadSearches();
  };

  const nextPage = () => { if (hasMore) setPage(p => p + 1); };
  const prevPage = () => { if (page > 1) setPage(p => p - 1); };
  const changeSort = (field) => {
    if (field === sortBy) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  const editSearch = async (searchId, updatedData) => {
    try {
      await http.put(`${API}/searches/${searchId}`, updatedData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Recharger la recherche complète avec toutes les données (y compris photos)
      const refreshedSearch = await http.get(`${API}/searches/${searchId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setSearches(prev => prev.map(search => 
        search.id === searchId ? refreshedSearch.data : search
      ));
      
      setShowEditModal(false);
      setEditingSearch(null);
      
    } catch (error) {
      console.error('Erreur mise à jour recherche:', error);
      alert('Erreur lors de la mise à jour de la recherche');
    }
  };

  const generatePDFPreview = async (searchId) => {
    setGenerating(true);
    try {
      const response = await http({
        method: 'get',
        url: `${API}/searches/${searchId}/pdf`,
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedSearchForPDF(searchId);
      setShowPDFPreview(true);
    } catch (error) {
      console.error('Erreur génération aperçu PDF:', error);
      alert('Erreur lors de la génération de l\'aperçu PDF');
    } finally {
      setGenerating(false);
    }
  };

  const downloadPDF = async (searchId) => {
    setGenerating(true);
    try {
      const response = await http({
        method: 'get',
        url: `${API}/searches/${searchId}/pdf`,
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport-${searchId}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur téléchargement PDF:', error);
      alert('Erreur lors du téléchargement du PDF');
    } finally {
      setGenerating(false);
    }
  };

  const updateSearchStatus = async (searchId, status) => {
    try {
      await http.put(`${API}/searches/${searchId}`, { status }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setSearches(prev => prev.map(search => 
        search.id === searchId ? { ...search, status } : search
      ));
      
      alert(`Statut mis à jour: ${status}`);
    } catch (error) {
      console.error('Erreur mise à jour statut:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const deleteSearch = async (search) => {
    if (!search) return;
    
    // Message différent selon le statut
    let confirmMessage;
    if (search.status === 'DRAFT') {
      confirmMessage = 'Supprimer ce brouillon définitivement ?';
    } else if (search.status === 'ARCHIVED') {
      confirmMessage = 'Supprimer définitivement cette recherche archivée ?\nCette action est irréversible.';
    } else {
      confirmMessage = 'Archiver cette recherche ?\nElle sera marquée comme archivée.';
    }
    
    if (!confirm(confirmMessage)) return;
    
    try {
      const resp = await http.delete(`${API}/searches/${search.id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const body = resp.data || {};
      if (body.search) {
        // Archivé: mettre à jour l'élément
        setSearches(prev => prev.map(s => s.id === search.id ? body.search : s));
      } else {
        // Supprimé définitivement
        setSearches(prev => prev.filter(s => s.id !== search.id));
      }
    } catch (error) {
      if (error?.response?.status === 405) {
        try {
          const resp = await http.post(`${API}/searches/${search.id}/delete`, null, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          const body = resp.data || {};
          if (body.search) {
            setSearches(prev => prev.map(s => s.id === search.id ? body.search : s));
          } else {
            setSearches(prev => prev.filter(s => s.id !== search.id));
          }
          return;
        } catch (fallbackError) {
          console.error('Erreur suppression (fallback):', fallbackError);
        }
      } else {
        console.error('Erreur suppression recherche:', error);
      }
      alert('Impossible de supprimer/archiver la recherche pour le moment.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'ACTIVE': 'bg-blue-100 text-blue-700',
      'SHARED': 'bg-green-100 text-green-700',
      'PROCESSED': 'bg-orange-100 text-orange-600',
      'ARCHIVED': 'bg-red-100 text-red-600',
      'DRAFT': 'bg-violet-100 text-violet-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusBackgroundColor = (status) => {
    // Carte blanche épurée (thème SkyApp) ; la couleur du statut est portée
    // par la barre d'accent et le badge.
    return 'bg-white border-gray-200';
  };

  const getStatusAccent = (status) => {
    const accents = {
      'DRAFT': 'linear-gradient(90deg, #7C3AED 0%, #A855F7 100%)',
      'ACTIVE': 'linear-gradient(90deg, #2563EB 0%, #7C3AED 100%)',
      'SHARED': 'linear-gradient(90deg, #22C55E 0%, #16A34A 100%)',
      'PROCESSED': 'linear-gradient(90deg, #FB923C 0%, #F97316 100%)',
      'ARCHIVED': 'linear-gradient(90deg, #EF4444 0%, #DC2626 100%)'
    };
    return accents[status] || 'linear-gradient(90deg, #2563EB 0%, #7C3AED 50%, #FB923C 100%)';
  };

  const formatFullAddress = (search) => {
    const capitalize = (str) => {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };
    
    const parts = [];
    if (search.location) parts.push(capitalize(search.location));
    if (search.ville) parts.push(capitalize(search.ville));
    if (search.code_postal) parts.push(search.code_postal);
    return parts.join(', ') || 'Adresse non spécifiée';
  };

  const filteredSearches = searches.filter(search => {
    // Exclure les brouillons de "Mes Recherches" sauf si on filtre spécifiquement sur DRAFT
    if (filter === 'all' && search.status === 'DRAFT') {
      return false;
    }
    
    const matchesFilter = filter === 'all' || search.status === filter;
    const matchesSearch = !searchTerm || 
      search.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      search.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Edit Modal Component - Utilise le même formulaire dynamique que SearchForm
  const EditSearchModal = ({ search, onSave, onClose }) => {
    const generateReferenceNumber = useCallback(() => {
      const date = new Date();
      const year = date.getFullYear().toString().slice(-2);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      return `REF${year}${month}${day}-${random}`;
    }, []);

    // Créer TOUTES les sections comme dans SearchForm
    const createAllSections = useCallback(() => [
      {
        id: 'general_info',
        type: 'required',
        title: 'Informations Générales',
        icon: User,
        value: '',
        required: true,
        fieldType: 'multiple',
        collapsed: false,
        photos: [],
        fields: [
          { id: 'nom', label: 'Nom', value: '', required: true, type: 'text' },
          { id: 'prenom', label: 'Prénom', value: '', required: true, type: 'text' },
          { id: 'adresse', label: 'Adresse', value: '', required: true, type: 'textarea' },
          { id: 'numero_recherche', label: 'Numéro de recherche', value: generateReferenceNumber(), required: false, type: 'text', readonly: true },
          { id: 'client_type', label: 'Type de client', value: 'occasional', required: false, type: 'radio', options: [{value: 'occasional', label: 'Client occasionnel (pas de suivi)'}, {value: 'recurring', label: 'Client récurrent (avec suivi)'}] },
          { id: 'client_id', label: 'Client', value: '', required: false, type: 'select', dependsOn: 'client_type', dependsOnValue: 'recurring', options: [] }
        ]
      },
      {
        id: 'description',
        type: 'required', 
        title: 'Description de la recherche',
        icon: FileText,
        value: '',
        required: true,
        placeholder: 'Décrivez en détail ce que vous recherchez (réseaux, canalisations, câbles électriques...)',
        fieldType: 'textarea',
        rows: 4,
        collapsed: false,
        photos: []
      },
      {
        id: 'observations',
        type: 'default',
        title: 'Observations et remarques',
        icon: Eye,
        value: '',
        required: false,
        placeholder: 'Ajoutez vos observations sur le terrain, les conditions de travail...',
        fieldType: 'textarea',
        rows: 3,
        collapsed: false,
        photos: []
      }
    ], [generateReferenceNumber]);

    const [sections, setSections] = useState(() => createAllSections());
    const [profilePhoto, setProfilePhoto] = useState(null);
    const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
    const [showLightbox, setShowLightbox] = useState(false);
    const [lightboxImage, setLightboxImage] = useState(null);
    const [clients, setClients] = useState([]);

    // Charger la liste des clients
    useEffect(() => {
      const loadClients = async () => {
        if (!localStorage.getItem('token')) return;
        try {
          const response = await http.get(`${API}/clients`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          
          const clientList = response.data || [];
          setClients(clientList);
          
          // Mettre à jour les options du champ client_id
          setSections(prev => prev.map(section => {
            if (section.id === 'general_info') {
              return {
                ...section,
                fields: section.fields?.map(field => {
                  if (field.id === 'client_id') {
                    return {
                      ...field,
                      options: clientList.map(c => ({
                        value: c.id,
                        label: `${c.nom} ${c.prenom || ''}`.trim()
                      }))
                    };
                  }
                  return field;
                })
              };
            }
            return section;
          }));
        } catch (error) {
          console.error('Erreur chargement clients:', error);
        }
      };
      
      loadClients();
    }, []);

    useEffect(() => {
      if (search) {
        console.log('🔄 [EditSearchModal] Chargement de la recherche:', search.id, 'avec', search.photos?.length || 0, 'photos');
        
        // Charger les photos d'abord pour préparer la distribution
        const photosBySection = {};
        let profilePhotoUrl = null;
        
        if (search.photos && Array.isArray(search.photos)) {
          console.log('📸 [EditSearchModal] Photos trouvées:', search.photos.length);
          
          // A. Trouver la photo de profil
          const profilePhotoData = search.photos.find(p => p.is_profile);
          if (profilePhotoData) {
            profilePhotoUrl = profilePhotoData.url || `${API}/searches/${search.id}/photos/${profilePhotoData.filename}`;
            console.log('🖼️ [EditSearchModal] Photo de profil:', profilePhotoUrl);
            setProfilePhotoPreview(profilePhotoUrl);
          }
          
          // B. Grouper les photos par section
          const nonProfilePhotos = search.photos.filter(p => !p.is_profile);
          console.log('📷 [EditSearchModal] Photos non-profil:', nonProfilePhotos.map(p => ({ section: p.section_id, filename: p.filename })));
          
          nonProfilePhotos.forEach(p => {
              if (!photosBySection[p.section_id]) {
                photosBySection[p.section_id] = [];
              }
              photosBySection[p.section_id].push({
                url: p.url || `${API}/searches/${search.id}/photos/${p.filename}`,
                filename: p.filename,
                name: p.original_name || p.filename
              });
            });
          
          console.log('📁 [EditSearchModal] Photos par section:', Object.keys(photosBySection).map(k => `${k}: ${photosBySection[k].length}`));
        }
        
        // Charger TOUTES les données en une seule fois (texte + photos)
        setSections(prev => prev.map(section => {
          let updatedSection = { ...section };
          
          // Charger les champs texte
          if (section.id === 'general_info') {
            updatedSection.fields = section.fields?.map(field => {
              if (field.id === 'nom') return { ...field, value: search.nom || '' };
              if (field.id === 'prenom') return { ...field, value: search.prenom || '' };
              if (field.id === 'adresse') return { ...field, value: search.location || '' };
              if (field.id === 'client_type') return { ...field, value: search.client_id ? 'recurring' : 'occasional' };
              if (field.id === 'client_id') return { ...field, value: search.client_id || '' };
              return field;
            });
          }
          if (section.id === 'description') {
            updatedSection.value = search.description || '';
          }
          if (section.id === 'observations') {
            // Parser le JSON pour récupérer le texte observations
            try {
              const observationsData = JSON.parse(search.observations || '{}');
              updatedSection.value = observationsData.text || '';
            } catch (e) {
              // Si ce n'est pas du JSON, c'est l'ancien format texte simple
              updatedSection.value = search.observations || '';
            }
          }
          
          // Ajouter les photos de cette section
          if (photosBySection[section.id]) {
            updatedSection.photos = photosBySection[section.id];
            console.log(`✅ [EditSearchModal] Section ${section.id}: ${photosBySection[section.id].length} photos ajoutées`);
          }
          
          return updatedSection;
        }));
        
        // Recréer les sections personnalisées à partir du JSON
        try {
          const observationsData = JSON.parse(search.observations || '{}');
          if (observationsData.customSections && Array.isArray(observationsData.customSections)) {
            const customSections = observationsData.customSections.map(cs => ({
              id: cs.id,
              type: 'custom',
              title: cs.title,
              icon: Plus,
              value: cs.value,
              required: false,
              placeholder: 'Décrivez le contenu de cette section...',
              fieldType: 'textarea',
              rows: 4,
              removable: true,
              collapsed: false,
              photos: photosBySection[cs.id] || []
            }));
            
            if (customSections.length > 0) {
              console.log(`✅ [EditSearchModal] ${customSections.length} sections personnalisées restaurées`);
              // Filtrer les sections custom déjà présentes pour éviter les doublons
              setSections(prev => {
                const baseAndStandardSections = prev.filter(s => s.type !== 'custom');
                return [...baseAndStandardSections, ...customSections];
              });
            }
          }
        } catch (e) {
          console.log('⚠️ [EditSearchModal] Pas de sections personnalisées ou ancien format');
        }
      }
    }, [search]);

    const handleSectionChange = (sectionId, value) => {
      setSections(prev => prev.map(section => 
        section.id === sectionId ? { ...section, value } : section
      ));
    };

    const handleFieldChange = (sectionId, fieldId, value) => {
      setSections(prev => prev.map(section => 
        section.id === sectionId 
          ? {
              ...section,
              fields: section.fields?.map(field =>
                field.id === fieldId ? { ...field, value } : field
              )
            }
          : section
      ));
    };

    const handleProfilePhotoChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          alert('La taille du fichier ne peut pas dépasser 5MB');
          return;
        }
        setProfilePhoto(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setProfilePhotoPreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    };

    const removeSection = (sectionId) => {
      setSections(prev => prev.filter(section => section.id !== sectionId));
    };

    const toggleSectionCollapse = (sectionId) => {
      setSections(prev => prev.map(section => 
        section.id === sectionId ? { ...section, collapsed: !section.collapsed } : section
      ));
    };

    const addNewSection = () => {
      const newSection = {
        id: `custom_${Date.now()}`,
        type: 'custom',
        title: `Section ${sections.length + 1}`,
        icon: Plus,
        value: '',
        required: false,
        placeholder: 'Décrivez le contenu de cette section...',
        fieldType: 'textarea',
        rows: 4,
        removable: true,
        collapsed: false,
        photos: []
      };
      setSections(prev => [...prev, newSection]);
    };

    const handleSectionTitleChange = (sectionId, newTitle) => {
      setSections(prev => prev.map(section => 
        section.id === sectionId ? { ...section, title: newTitle } : section
      ));
    };

    const handleSectionPhotos = (sectionId, files) => {
      if (files && files.length > 0) {
        const newPhotos = Array.from(files).map((file) => ({
          file,
          url: URL.createObjectURL(file),
          name: file.name
        }));
        
        setSections(prev => prev.map(section => 
          section.id === sectionId 
            ? { ...section, photos: [...(section.photos || []), ...newPhotos] }
            : section
        ));
      }
    };

    const removeSectionPhoto = async (sectionId, photoIndex) => {
      const section = sections.find(s => s.id === sectionId);
      if (!section || !section.photos) return;

      const photoToRemove = section.photos[photoIndex];
      
      // Si la photo a un filename (déjà sauvegardée sur le serveur), la supprimer
      if (photoToRemove.filename && search?.id) {
        try {
          const token = localStorage.getItem('token');
          await http.delete(`${API}/searches/${search.id}/photos/${photoToRemove.filename}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log(`✅ Photo ${photoToRemove.filename} supprimée du serveur`);
        } catch (error) {
          console.error('Erreur suppression photo:', error);
          alert('Erreur lors de la suppression de la photo');
          return;
        }
      }

      // Mettre à jour l'état local
      setSections(prev => prev.map(s => {
        if (s.id === sectionId && s.photos) {
          const newPhotos = s.photos.filter((_, index) => index !== photoIndex);
          // Révoquer l'URL locale si c'est une preview
          if (s.photos[photoIndex]?.url?.startsWith('blob:')) {
            URL.revokeObjectURL(s.photos[photoIndex].url);
          }
          return { ...s, photos: newPhotos };
        }
        return s;
      }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      
      // Construire le payload depuis les sections
      const generalSection = sections.find(s => s.id === 'general_info');
      const addressField = generalSection?.fields?.find(f => f.id === 'adresse');
      const clientTypeField = generalSection?.fields?.find(f => f.id === 'client_type');
      const clientIdField = generalSection?.fields?.find(f => f.id === 'client_id');
      const descriptionSection = sections.find(s => s.id === 'description');
      // Sauvegarder les sections personnalisées comme JSON
      const customSections = sections
        .filter(s => s.type === 'custom')
        .map(s => ({ id: s.id, title: s.title, value: s.value || '' }));
      
      const observationsSection = sections.find(s => s.id === 'observations');
      const observationsText = observationsSection?.value || '';
      
      // Combiner observations texte + sections custom en JSON
      const observationsData = {
        text: observationsText,
        customSections: customSections
      };

      const payload = {
        location: addressField?.value || '',
        description: descriptionSection?.value || '',
        observations: JSON.stringify(observationsData)
      };
      
      // Ajouter client_id uniquement si type = recurring et un client est sélectionné
      if (clientTypeField?.value === 'recurring' && clientIdField?.value) {
        payload.client_id = clientIdField.value;
      } else {
        // Explicitement null pour les clients occasionnels
        payload.client_id = null;
      }

      try {
        // 1. Sauvegarder d'abord les données textuelles
        await onSave(search.id, payload);

        // 2. Uploader les nouvelles photos de chaque section
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Token d\'authentification manquant');
        }

        // A. Uploader la photo de profil si nouvelle
        if (profilePhoto) {
          const formData = new FormData();
          formData.append('files', profilePhoto);
          formData.append('is_profile', 'true');
          formData.append('section_id', 'profile');

          await http.post(
            `${API}/searches/${search.id}/photos`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            }
          );
        }

        // B. Uploader les nouvelles photos de chaque section
        for (const section of sections) {
          if (section.photos && section.photos.length > 0) {
            // Filtrer uniquement les nouvelles photos (celles qui ont un objet file)
            const newPhotos = section.photos.filter(photo => photo.file);
            
            if (newPhotos.length > 0) {
              const formData = new FormData();
              newPhotos.forEach(photo => {
                formData.append('files', photo.file);
              });
              formData.append('section_id', section.id);

              await http.post(
                `${API}/searches/${search.id}/photos`,
                formData,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                  }
                }
              );
            }
          }
        }

        // C. Sauvegarder les données textuelles
        await onSave(search.id, payload);

        alert('Recherche et photos mises à jour avec succès !');
        
        // Fermer le modal - la liste se rafraîchira automatiquement via onSave
        onClose();
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
        alert('Erreur lors de la mise à jour de la recherche');
      }
    };

    if (!search) return null;

    return (
      <>
      <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl shadow-2xl w-[98vw] h-[98vh] overflow-y-auto p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-200">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Modifier la Recherche</h2>
              <p className="text-sm text-gray-600 mt-1">Formulaire personnalisable avec sections modulaires</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6 py-4">
            {/* Rendu des sections dynamiques */}
            {sections.map((section, index) => (
              <div key={section.id} className="bg-gray-50 rounded-xl p-6 relative">
                {/* En-tête de section */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                      <section.icon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1">
                      {section.type === 'custom' ? (
                        <input
                          type="text"
                          value={section.title}
                          onChange={(e) => handleSectionTitleChange(section.id, e.target.value)}
                          className="text-lg font-semibold text-gray-900 bg-transparent border-b border-dashed border-gray-300 focus:border-gray-900 outline-none w-full"
                        />
                      ) : (
                        <h3 className="text-lg font-semibold text-gray-900">{section.title}</h3>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => toggleSectionCollapse(section.id)}
                      className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      {section.collapsed ? <ChevronDown className="h-5 w-5" /> : <ChevronUp className="h-5 w-5" />}
                    </button>
                    {section.removable && (
                      <button
                        type="button"
                        onClick={() => removeSection(section.id)}
                        className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Contenu de section */}
                {!section.collapsed && (
                  <div className="space-y-4">
                    {/* Champs multiples pour general_info */}
                    {section.fieldType === 'multiple' && section.fields ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {section.fields.map((field) => {
                            // Gérer les champs conditionnels
                            if (field.dependsOn) {
                              const dependentField = section.fields.find(f => f.id === field.dependsOn);
                              if (dependentField && dependentField.value !== field.dependsOnValue) {
                                return null; // Ne pas afficher si la condition n'est pas remplie
                              }
                            }
                            
                            return (
                            <div key={field.id} className="space-y-2" style={field.type === 'radio' ? {gridColumn: '1 / -1'} : {}}>
                              <label className="block text-sm font-medium text-gray-700">
                                {field.label}
                                {field.required && <span className="text-red-500 ml-1">*</span>}
                              </label>
                              {field.type === 'radio' ? (
                                <div className="flex gap-4 p-4 bg-gray-50 rounded-xl">
                                  {field.options?.map(option => (
                                    <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="radio"
                                        name={field.id}
                                        value={option.value}
                                        checked={field.value === option.value}
                                        onChange={(e) => handleFieldChange(section.id, field.id, e.target.value)}
                                        className="w-4 h-4 text-gray-900"
                                      />
                                      <span className="text-sm">{option.label}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : field.type === 'select' ? (
                                <select
                                  value={field.value}
                                  onChange={(e) => handleFieldChange(section.id, field.id, e.target.value)}
                                  className="w-full rounded-xl border border-gray-200 focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 transition-colors duration-200 text-base p-4"
                                  required={field.required}
                                >
                                  <option value="">-- Sélectionner un client --</option>
                                  {field.options?.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                </select>
                              ) : field.type === 'textarea' ? (
                                <textarea
                                  value={field.value}
                                  onChange={(e) => handleFieldChange(section.id, field.id, e.target.value)}
                                  placeholder={field.placeholder || `Saisissez ${field.label.toLowerCase()}...`}
                                  rows={3}
                                  className="w-full rounded-xl border border-gray-200 focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 transition-colors duration-200 text-base p-4 resize-none"
                                  required={field.required}
                                  readOnly={field.readonly}
                                />
                              ) : (
                                <input
                                  type={field.type}
                                  value={field.value}
                                  onChange={(e) => handleFieldChange(section.id, field.id, e.target.value)}
                                  placeholder={field.placeholder || `Saisissez ${field.label.toLowerCase()}...`}
                                  className="w-full rounded-xl border border-gray-200 focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 transition-colors duration-200 text-base p-4"
                                  required={field.required}
                                  readOnly={field.readonly}
                                />
                              )}
                            </div>
                            );
                          })}
                        </div>
                        
                        {/* Photo de profil pour general_info */}
                        {section.id === 'general_info' && (
                          <div className="mt-6 p-4 bg-white rounded-xl border border-gray-200">
                            <h5 className="text-sm font-medium text-gray-700 mb-3">Photo de profil (optionnelle)</h5>
                            <div className="flex items-center space-x-4">
                              {profilePhotoPreview ? (
                                <div className="relative">
                                  <img
                                    src={profilePhotoPreview}
                                    alt="Profile preview"
                                    className="w-20 h-20 object-cover rounded-full border-2 border-gray-200"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setProfilePhoto(null);
                                      setProfilePhotoPreview(null);
                                    }}
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              ) : (
                                <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-full flex items-center justify-center">
                                  <User className="h-8 w-8 text-gray-400" />
                                </div>
                              )}
                              <div>
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={handleProfilePhotoChange}
                                  className="hidden"
                                  id="edit-profile-photo"
                                />
                                <label
                                  htmlFor="edit-profile-photo"
                                  className="inline-flex items-center px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-lg cursor-pointer transition-colors text-sm font-medium border border-gray-200"
                                >
                                  <Camera className="h-4 w-4 mr-2" />
                                  Choisir une photo
                                </label>
                                <p className="text-xs text-gray-500 mt-1">Formats acceptés: JPG, PNG (max 5MB)</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Contenu textarea par défaut */
                      <div className="space-y-4">
                        <textarea
                          placeholder={section.placeholder || 'Décrivez le contenu de cette section...'}
                          value={section.value || ''}
                          onChange={(e) => handleSectionChange(section.id, e.target.value)}
                          rows={section.rows || 4}
                          required={section.required}
                          className="w-full rounded-xl border border-gray-200 focus:border-gray-900 focus:ring-2 focus:ring-gray-900/10 transition-all duration-200 text-base p-4 resize-none"
                        />

                        {/* Photos de section */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-700">
                              Photos de cette section
                            </label>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(e) => handleSectionPhotos(section.id, e.target.files)}
                              className="hidden"
                              id={`section-photos-${section.id}`}
                            />
                            <label
                              htmlFor={`section-photos-${section.id}`}
                              className="inline-flex items-center px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-700 rounded-lg cursor-pointer transition-colors text-sm font-medium border border-gray-200"
                            >
                              <Camera className="h-4 w-4 mr-1.5" />
                              Ajouter des photos
                            </label>
                          </div>

                          {section.photos && section.photos.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                              {section.photos.map((photo, photoIndex) => (
                                <div 
                                  key={photoIndex} 
                                  className="relative group cursor-move bg-white rounded-lg border-2 border-gray-200 hover:border-blue-400 transition-all"
                                  draggable
                                  onDragStart={(e) => {
                                    e.dataTransfer.effectAllowed = 'move';
                                    e.dataTransfer.setData('text/plain', JSON.stringify({
                                      sectionId: section.id,
                                      fromIndex: photoIndex
                                    }));
                                  }}
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
                                  }}
                                  onDragLeave={(e) => {
                                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                                  }}
                                  onDrop={(e) => {
                                    e.preventDefault();
                                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                                    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
                                    if (data.sectionId === section.id && data.fromIndex !== photoIndex) {
                                      setSections(prevSections => prevSections.map(s => {
                                        if (s.id === section.id) {
                                          const newPhotos = [...s.photos];
                                          const [movedPhoto] = newPhotos.splice(data.fromIndex, 1);
                                          newPhotos.splice(photoIndex, 0, movedPhoto);
                                          return { ...s, photos: newPhotos };
                                        }
                                        return s;
                                      }));
                                    }
                                  }}
                                >
                                  <div 
                                    className="w-full h-32 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer"
                                    onClick={() => {
                                      setLightboxImage(photo.url);
                                      setShowLightbox(true);
                                    }}
                                  >
                                    <img
                                      src={photo.url}
                                      alt={photo.name}
                                      className="max-w-full max-h-full object-contain"
                                    />
                                  </div>
                                  
                                  <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setLightboxImage(photo.url);
                                        setShowLightbox(true);
                                      }}
                                      className="bg-blue-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-blue-600 shadow-lg"
                                      title="Voir en détail"
                                    >
                                      <Eye className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => removeSectionPhoto(section.id, photoIndex)}
                                      className="bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-red-600 shadow-lg"
                                      title="Supprimer"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                  
                                  {/* Barre bas : ◀ numéro ▶ — fonctionne souris ET tactile */}
                                  <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between bg-black/75 text-white px-1 py-1 rounded-b-lg">
                                    <button
                                      type="button"
                                      disabled={photoIndex === 0}
                                      onPointerDown={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        if (photoIndex > 0) {
                                          setSections(prev => prev.map(s => {
                                            if (s.id !== section.id) return s;
                                            const photos = [...s.photos];
                                            [photos[photoIndex - 1], photos[photoIndex]] = [photos[photoIndex], photos[photoIndex - 1]];
                                            return { ...s, photos };
                                          }));
                                          setHasPendingChanges(true);
                                        }
                                      }}
                                      className={`w-7 h-7 rounded flex items-center justify-center text-sm font-bold select-none ${
                                        photoIndex === 0 ? 'opacity-25 cursor-not-allowed' : 'hover:bg-white/20 active:bg-white/40'
                                      }`}
                                    >◀</button>
                                    <span className="text-xs font-medium flex items-center gap-1 select-none">
                                      <GripVertical className="h-3 w-3" />
                                      #{photoIndex + 1}
                                    </span>
                                    <button
                                      type="button"
                                      disabled={photoIndex === section.photos.length - 1}
                                      onPointerDown={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        if (photoIndex < section.photos.length - 1) {
                                          setSections(prev => prev.map(s => {
                                            if (s.id !== section.id) return s;
                                            const photos = [...s.photos];
                                            [photos[photoIndex + 1], photos[photoIndex]] = [photos[photoIndex], photos[photoIndex + 1]];
                                            return { ...s, photos };
                                          }));
                                          setHasPendingChanges(true);
                                        }
                                      }}
                                      className={`w-7 h-7 rounded flex items-center justify-center text-sm font-bold select-none ${
                                        photoIndex === section.photos.length - 1 ? 'opacity-25 cursor-not-allowed' : 'hover:bg-white/20 active:bg-white/40'
                                      }`}
                                    >▶</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Bouton ajouter section */}
            <button
              type="button"
              onClick={addNewSection}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-gray-900 hover:text-gray-900 transition-all duration-200 flex items-center justify-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Ajouter une section personnalisée</span>
            </button>
            
            {/* Boutons d'action */}
            <div className="flex flex-col space-y-3 pt-4 border-t">
              <Button 
                type="button"
                onClick={async () => {
                  // Sauvegarder comme brouillon (status = DRAFT)
                  const generalSection = sections.find(s => s.id === 'general_info');
                  const addressField = generalSection?.fields?.find(f => f.id === 'adresse');
                  const descriptionSection = sections.find(s => s.id === 'description');
                  const additionalContent = sections
                    .filter(s => s.value && !['general_info', 'description'].includes(s.id))
                    .map(s => `**${s.title}:**\n${s.value}`)
                    .join('\n\n');

                  const payload = {
                    location: addressField?.value || '',
                    description: descriptionSection?.value || '',
                    observations: additionalContent || '',
                    status: 'DRAFT'
                  };

                  try {
                    await onSave(search.id, payload);
                    alert('Brouillon sauvegardé !');
                  } catch (error) {
                    alert('Erreur lors de la sauvegarde du brouillon');
                  }
                }}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl py-3"
              >
                <Save className="h-4 w-4 mr-2" />
                Enregistrer le brouillon
              </Button>
              
              <div className="flex space-x-3">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-xl">
                  Annuler
                </Button>
                <Button type="submit" className="flex-1 bg-gray-900 hover:bg-gray-800 rounded-xl">
                  <CheckCheck className="h-4 w-4 mr-2" />
                  Finaliser la recherche
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Lightbox Modal pour visualiser les photos en grand - Rendu via Portal pour éviter les conflits avec Dialog */}
      {showLightbox && ReactDOM.createPortal(
          <div 
            className="fixed inset-0 bg-black/90 flex items-center justify-center p-4"
            style={{ zIndex: 99999 }}
            onClick={() => {
              setShowLightbox(false);
              setLightboxImage(null);
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowLightbox(false);
                setLightboxImage(null);
              }}
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full w-12 h-12 flex items-center justify-center backdrop-blur-sm transition-all"
            >
              <X className="h-6 w-6" />
            </button>
            
            <div 
              className="relative max-w-7xl max-h-[90vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={lightboxImage}
                alt="Aperçu"
                className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              />
            </div>
            
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm">
              Cliquez n'importe où pour fermer
            </div>
          </div>,
          document.body
      )}
      </>
    );
  };

  const PDFPreviewModal = () => (
    <Dialog open={showPDFPreview} onOpenChange={() => {
      setShowPDFPreview(false);
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
        setPdfUrl('');
      }
    }}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[95vh] p-6">
        <DialogHeader>
          <DialogTitle>Aperçu du Rapport PDF</DialogTitle>
          <DialogDescription>
            Prévisualisez le rapport avant téléchargement
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 h-[calc(95vh-180px)]">
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              className="w-full h-full rounded-lg border"
              title="Aperçu PDF"
            />
          )}
        </div>
        <div className="flex justify-end space-x-3 pt-4">
          <Button 
            variant="outline" 
            onClick={() => {
              setShowPDFPreview(false);
              if (pdfUrl) {
                window.URL.revokeObjectURL(pdfUrl);
                setPdfUrl('');
              }
            }}
          >
            Fermer
          </Button>
          <Button 
            onClick={() => downloadPDF(selectedSearchForPDF)}
            disabled={generating}
            className={generating ? 'bg-green-600 hover:bg-green-600 cursor-wait' : 'bg-gray-900 hover:bg-gray-800'}
          >
            {generating ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                </svg>
                PDF en création…
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Télécharger
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
        <div className="p-12 flex flex-col items-center justify-center gap-4">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-100 border-t-indigo-600"></div>
            <div className="absolute inset-0 rounded-full animate-ping opacity-20 bg-indigo-400" style={{animationDuration:'1.5s'}}></div>
          </div>
          <p className="text-gray-500 font-medium">Chargement en cours…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Search and Filters */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Mes Recherches</h2>
            <p className="text-gray-600 mt-1">Consultez, modifiez et gérez vos recherches terrain</p>
          </div>
          
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 w-full lg:w-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={onSearchTermChange}
                className="pl-10 rounded-xl border-gray-200 focus:border-gray-900 focus:ring-gray-900/10 w-full sm:w-64"
              />
            </div>
            
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="bg-white rounded-xl border border-gray-200 focus:border-gray-900 focus:ring-gray-900/10 transition-all duration-200 px-4 py-2 text-sm"
            >
              <option value="all">Tous les statuts</option>
              <option value="ACTIVE">Actives</option>
              <option value="SHARED">Partagées</option>
              <option value="PROCESSED">Traitées</option>
              <option value="ARCHIVED">Archivées</option>
            </select>

            <div className="flex space-x-2">
              <Button variant="outline" onClick={() => changeSort('updated_at')} className="text-xs">Tri: {sortBy === 'updated_at' ? 'Modifié' : sortBy} {sortDir === 'asc' ? '↑' : '↓'}</Button>
              <Button variant="outline" onClick={applySearch} className="text-xs">Appliquer</Button>
            </div>
          </div>
        </div>

        {/* Pagination controls */}
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-500">Page {page}{hasMore ? '' : ' (fin)'} • {searches.length} éléments chargés</div>
          <div className="flex space-x-2">
            <Button variant="outline" disabled={page === 1} onClick={prevPage} className="text-xs">Précédent</Button>
            <Button variant="outline" disabled={!hasMore} onClick={nextPage} className="text-xs">Suivant</Button>
          </div>
        </div>
        {/* Stats (incluant Brouillons et Archivées) */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
          {[
            { key: 'draft', status: 'DRAFT', label: 'Brouillons', bg: 'bg-violet-50', textColor: 'text-violet-600', labelColor: 'text-violet-700' },
            { key: 'active', status: 'ACTIVE', label: 'Actives', bg: 'bg-blue-50', textColor: 'text-blue-600', labelColor: 'text-blue-700' },
            { key: 'shared', status: 'SHARED', label: 'Partagées', bg: 'bg-green-50', textColor: 'text-green-600', labelColor: 'text-green-700' },
            { key: 'processed', status: 'PROCESSED', label: 'Traitées', bg: 'bg-orange-50', textColor: 'text-orange-500', labelColor: 'text-orange-600' },
            { key: 'total', status: null, label: 'Total (page)', bg: 'bg-black', textColor: 'text-white', labelColor: 'text-gray-200' }
          ].map((stat) => (
            <div key={stat.key} className={`${stat.bg} rounded-xl p-4 text-center`}>
              <p className={`text-2xl font-bold ${stat.textColor}`}>
                {stat.status ? searches.filter(s => s.status === stat.status).length : searches.length}
              </p>
              <p className={`text-sm ${stat.labelColor}`}>{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Search List */}
      {filteredSearches.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
          <div className="p-12 text-center">
            <History className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm || filter !== 'all' ? 'Aucun résultat' : 'Aucune recherche enregistrée'}
            </h3>
            <p className="text-gray-500">
              {searchTerm || filter !== 'all' ? 'Essayez de modifier vos critères de recherche' : 'Créez votre première recherche pour commencer'}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid gap-8">
          {filteredSearches.map((search) => (
            <div key={search.id} className={`rounded-2xl border shadow-md hover:shadow-xl transition-all duration-300 group overflow-hidden transform hover:-translate-y-0.5 ${getStatusBackgroundColor(search.status)}`}>
              {/* Barre d'accent SkyApp selon le statut */}
              <div className="h-1.5 w-full" style={{ background: getStatusAccent(search.status) }} />
              <div className="p-6">
                {/* Header avec location et statut */}
                <div className="flex items-start justify-between mb-5">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2 flex-wrap gap-2">
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        <h3 className="text-xl font-bold text-gray-900 tracking-tight">{formatFullAddress(search)}</h3>
                      </div>
                      <span className={`text-xs px-3 py-1.5 rounded-full font-bold uppercase tracking-wider shadow-sm ${getStatusColor(search.status)}`}>
                        {search.status}
                      </span>
                      {(search.status === 'SHARED' || search.status === 'SHARED_TO_BUREAU') && (
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border-2 border-green-400 shadow-md">
                          <Share2 className="h-3.5 w-3.5 mr-1.5" strokeWidth={2.5} />
                          ✅ Déjà partagé au bureau
                        </span>
                      )}
                    </div>
                    
                    {/* Informations client */}
                    {(search.nom || search.prenom) && (
                      <div className="flex items-center space-x-2 mb-2 bg-gradient-to-r from-blue-50 to-violet-50 rounded-lg px-3 py-2 border border-blue-100">
                        <User className="h-4 w-4 text-blue-600" />
                        <p className="text-sm font-semibold text-gray-800">
                          {search.prenom} {search.nom}
                        </p>
                      </div>
                    )}
                    
                    <p className="text-gray-700 leading-relaxed text-base">{search.description}</p>
                  </div>
                </div>
                
                {/* Photos - Toutes en horizontal */}
                {search.photos && search.photos.length > 0 && (
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-bold text-gray-900 flex items-center">
                        <svg className="w-4 h-4 mr-2 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {search.photos.length} {search.photos.length > 1 ? 'Photos' : 'Photo'}
                      </p>
                    </div>
                    <div className="flex space-x-3 overflow-x-auto pb-2 scrollbar-hide">
                      {search.photos.map((photo, index) => (
                        <div key={photo.filename || `${search.id}-photo-${index}`} className="relative flex-shrink-0 group/photo">
                          <img
                            src={photo.url || `${API}/searches/${search.id}/photos/${photo.filename}`}
                            alt={`Photo ${index + 1}/${search.photos.length}`}
                            className="w-24 h-24 object-cover rounded-xl border-2 border-white shadow-lg hover:scale-105 hover:rotate-1 transition-all duration-200 cursor-pointer ring-1 ring-gray-100"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                          <div className="absolute top-1 right-1 px-1.5 h-5 bg-gradient-to-br from-blue-600 to-violet-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold shadow-md">
                            {index + 1}/{search.photos.length}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Observations */}
                {search.observations && cleanObservations(search.observations) && (
                  <div className="bg-gradient-to-r from-blue-50 via-violet-50 to-violet-50/60 rounded-xl p-4 mb-5 border border-blue-200 shadow-sm">
                    <p className="text-sm text-blue-900 leading-relaxed">
                      <span className="font-bold text-violet-700">💬 Observations:</span> {cleanObservations(search.observations)}
                    </p>
                  </div>
                )}
                    
                    {/* Métadonnées */}
                    <div className="flex items-center space-x-4 text-sm mb-5 pb-4 border-b border-gray-200">
                      <span className="flex items-center font-semibold text-gray-700 bg-gray-50 px-3 py-2 rounded-lg">
                        <Calendar className="h-4 w-4 mr-2 text-violet-600" strokeWidth={2} />
                        {formatDate(search.created_at)}
                      </span>
                      {search.latitude && search.longitude && (
                        <span className="flex items-center font-semibold text-gray-700 bg-gray-50 px-3 py-2 rounded-lg">
                          <MapPin className="h-4 w-4 mr-2 text-red-500" strokeWidth={2} />
                          {search.latitude.toFixed(4)}, {search.longitude.toFixed(4)}
                        </span>
                      )}
                    </div>

                    {/* Action Buttons - Toujours visibles sur mobile */}
                    <div className="flex flex-wrap gap-3 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
                      <button
                        onClick={async () => {
                          // Charger la recherche complète avec les photos et ouvrir en mode édition plein écran
                          try {
                            console.log('🔄 Chargement de la recherche complète avec photos...');
                            const response = await http.get(`${API}/searches/${search.id}`, {
                              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                            });
                            console.log('✅ Recherche chargée avec photos:', response.data);
                            if (onEditSearch) {
                              onEditSearch(response.data);
                            }
                          } catch (error) {
                            console.error('❌ Erreur chargement recherche:', error);
                            // Fallback: utiliser les données déjà disponibles
                            if (onEditSearch) {
                              onEditSearch(search);
                            }
                          }
                        }}
                        className="inline-flex items-center px-4 py-2 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-violet-600 rounded-xl hover:from-blue-700 hover:to-violet-700 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                      >
                        <Edit className="h-4 w-4 mr-2" strokeWidth={2} />
                        Modifier
                      </button>
                      
                      <button
                        onClick={() => generatePDFPreview(search.id)}
                        disabled={generating}
                        className={`inline-flex items-center px-4 py-2 text-sm font-bold rounded-xl shadow-md transition-all duration-200 transform ${
                          generating
                            ? 'text-white bg-gradient-to-r from-green-500 to-emerald-500 cursor-wait scale-95 shadow-green-200'
                            : 'text-gray-700 bg-white border-2 border-gray-300 hover:border-blue-400 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 hover:shadow-lg hover:scale-105'
                        }`}
                      >
                        {generating ? (
                          <>
                            <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                            </svg>
                            PDF en création…
                          </>
                        ) : (
                          <>
                            <Eye className="h-4 w-4 mr-2" strokeWidth={2} />
                            Aperçu PDF
                          </>
                        )}
                      </button>
                      
                      {/* Bouton Partager - masqué si déjà partagé */}
                      {search.status !== 'SHARED' && search.status !== 'SHARED_TO_BUREAU' && (
                        <button
                          onClick={() => updateSearchStatus(search.id, 'SHARED')}
                          className="inline-flex items-center px-4 py-2 text-sm font-bold text-blue-700 bg-gradient-to-r from-blue-100 to-violet-100 border-2 border-blue-300 rounded-xl hover:from-blue-200 hover:to-violet-200 hover:border-blue-400 shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                        >
                          <Share2 className="h-4 w-4 mr-2" strokeWidth={2} />
                          Partager
                        </button>
                      )}

                      <button
                        onClick={() => downloadPDF(search.id)}
                        disabled={generating}
                        className="inline-flex items-center px-4 py-2 text-sm font-bold text-white bg-gradient-to-r from-green-500 to-green-600 rounded-xl hover:from-green-600 hover:to-green-700 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                      >
                        {generating ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <Download className="h-4 w-4 mr-2" strokeWidth={2} />
                        )}
                        Télécharger PDF
                      </button>

                      <button
                        onClick={() => deleteSearch(search)}
                        className="inline-flex items-center px-4 py-2 text-sm font-bold text-red-600 bg-red-50 border-2 border-red-200 rounded-xl hover:bg-red-100 hover:border-red-300 shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                      >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Supprimer
                      </button>
                    </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <EditSearchModal 
        search={editingSearch} 
        onSave={editSearch} 
        onClose={() => {
          setShowEditModal(false);
          setEditingSearch(null);
        }} 
      />
      <PDFPreviewModal />
    </div>
  );
};

// Share Search Component with PDF Generation
// Apple-style Share Search Component
const ShareSearch = () => {
  const [selectedSearches, setSelectedSearches] = useState([]);
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const loadSearches = useCallback(async () => {
    console.log('🔄 Chargement des recherches... (trigger:', refreshTrigger, ')');
    setLoading(true);
    try {
      const response = await http.get(`${API}/searches`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      // Utiliser response.data.data car l'API retourne { data: [...], count: X }
      const searchesData = response.data.data || response.data || [];
      setSearches(searchesData);
      console.log('✅ Recherches chargées:', searchesData.length, 'total dont', searchesData.filter(s => s.status === 'DRAFT').length, 'brouillons,', searchesData.filter(s => s.status === 'ACTIVE').length, 'actives');
    } catch (error) {
      console.error('❌ Erreur chargement recherches:', error);
    } finally {
      setLoading(false);
    }
  }, [refreshTrigger]);

  useEffect(() => {
    loadSearches();
  }, [loadSearches]);

  // Écouter l'événement de finalisation pour recharger automatiquement
  useEffect(() => {
    const handleSearchFinalized = (event) => {
      console.log('🔔 Événement searchFinalized reçu!', event.detail);
      setRefreshTrigger(prev => {
        const newValue = prev + 1;
        console.log('📈 RefreshTrigger incrémenté:', prev, '→', newValue);
        return newValue;
      });
    };
    
    console.log('👂 Écouteur searchFinalized activé');
    window.addEventListener('searchFinalized', handleSearchFinalized);
    return () => {
      console.log('👋 Écouteur searchFinalized désactivé');
      window.removeEventListener('searchFinalized', handleSearchFinalized);
    };
  }, []);

  const handleSearchToggle = (searchId) => {
    setSelectedSearches(prev => 
      prev.includes(searchId)
        ? prev.filter(id => id !== searchId)
        : [...prev, searchId]
    );
  };

  const generateAndShareToBureau = async (searchId = null) => {
    setGenerating(true);
    
    try {
      const searchesToShare = searchId ? [searchId] : selectedSearches;
      
      if (searchesToShare.length === 0) {
        alert('Veuillez sélectionner au moins une recherche');
        return;
      }

      // Envoyer vers les rapports Bureau
      await http.post(`${API}/reports/share-to-bureau`, {
        search_ids: searchesToShare
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      alert(`${searchesToShare.length} rapport(s) envoyé(s) vers le Bureau avec succès !`);
      setSelectedSearches([]);
      
    } catch (error) {
      console.error('Erreur envoi vers Bureau:', error);
      alert('Erreur lors de l\'envoi vers le Bureau');
    } finally {
      setGenerating(false);
    }
  };

  const generatePDF = async (searchId = null) => {
    setGenerating(true);
    
    try {
      if (searchId) {
        // Generate PDF for single search
        const response = await http({
          method: 'get',
          url: `${API}/searches/${searchId}/pdf`,
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        // Create download link
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const contentDisposition = response.headers['content-disposition'];
        const filename = contentDisposition 
          ? contentDisposition.split('filename=')[1].replace(/"/g, '')
          : `rapport_${new Date().toISOString().split('T')[0]}.pdf`;
        
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        alert('Rapport PDF généré avec succès !');
      } else {
        // Generate summary PDF for selected searches  
        const response = await http({
          method: 'post',
          url: `${API}/reports/generate-summary-pdf`,
          data: { search_ids: selectedSearches },
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        // Create download link
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        const contentDisposition = response.headers['content-disposition'];
        const filename = contentDisposition 
          ? contentDisposition.split('filename=')[1].replace(/"/g, '')
          : `synthese_${new Date().toISOString().split('T')[0]}.pdf`;
        
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        alert('Rapport PDF généré avec succès !');
      }
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      alert('Erreur lors de la génération du PDF');
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Mes Recherches</h2>
            <p className="text-gray-600 mt-1">Consultez et partagez vos recherches finalisées</p>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-gray-500">
              {searches.filter(s => s.status !== 'DRAFT').length} recherche(s) finalisée(s)
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <button
            onClick={() => generatePDF()}
            disabled={selectedSearches.length === 0 || generating}
            className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-gray-900 rounded-xl hover:bg-gray-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Génération...
              </>
            ) : (
              <>
                <FileBarChart className="h-4 w-4 mr-2" />
                Générer PDF Synthèse ({selectedSearches.length})
              </>
            )}
          </button>

          <button
            onClick={() => generateAndShareToBureau()}
            disabled={selectedSearches.length === 0 || generating}
            className="inline-flex items-center px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Envoi...
              </>
            ) : (
              <>
                <Share2 className="h-4 w-4 mr-2" />
                Envoyer au Bureau ({selectedSearches.length})
              </>
            )}
          </button>
          
          <button
            onClick={() => {
              setSelectedSearches([]);
            }}
            className="inline-flex items-center px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors duration-200"
          >
            Désélectionner tout
          </button>
        </div>
      </div>

      {/* Searches List */}
      {(() => {
        // Filtrer pour exclure les brouillons (DRAFT) - uniquement les recherches finalisées
        const finalizedSearches = searches.filter(s => s.status !== 'DRAFT');
        
        return finalizedSearches.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="p-12 text-center">
              <Share2 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucune recherche finalisée</h3>
              <p className="text-gray-500">Les recherches finalisées apparaîtront ici. Les brouillons sont disponibles dans "Brouillons en attente".</p>
            </div>
          </div>
        ) : (
          <div className="grid gap-4">
            {finalizedSearches.map((search) => {
              const isShared = search.status === 'SHARED' || search.status === 'SHARED_TO_BUREAU';
              
              return (
            <div key={search.id} className={`rounded-2xl border-2 shadow-sm hover:shadow-md transition-all duration-200 ${
              isShared ? 'bg-green-50 border-green-300' : 'bg-white border-gray-200'
            }`}>
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    {/* Checkbox */}
                    <div className="flex items-center mt-1">
                      <input
                        type="checkbox"
                        checked={selectedSearches.includes(search.id)}
                        onChange={() => handleSearchToggle(search.id)}
                        disabled={isShared}
                        className="w-5 h-5 text-gray-900 bg-white border-2 border-gray-300 rounded focus:ring-gray-900 focus:ring-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>
                    
                    {/* Search Content */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{formatFullAddress(search)}</h3>
                        {isShared && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border-2 border-green-400 shadow-sm">
                            <Share2 className="h-3 w-3 mr-1.5" strokeWidth={2.5} />
                            ✅ Déjà partagé
                          </span>
                        )}
                      </div>
                      <p className="text-gray-600 mb-3 line-clamp-2">{search.description}</p>
                      
                      {search.observations && cleanObservations(search.observations) && (
                        <div className="bg-blue-50 rounded-xl p-3 mb-3">
                          <p className="text-sm text-blue-800">
                            <strong>Observations:</strong> {cleanObservations(search.observations)}
                          </p>
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {formatDate(search.created_at)}
                        </span>
                        {search.latitude && search.longitude && (
                          <span className="flex items-center">
                            <MapPin className="h-4 w-4 mr-1" />
                            {search.latitude.toFixed(4)}, {search.longitude.toFixed(4)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Individual Action Buttons */}
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => generatePDF(search.id)}
                      disabled={generating}
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                    >
                      {generating ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          PDF
                        </>
                      )}
                    </button>
                    
                    <button
                      onClick={() => generateAndShareToBureau(search.id)}
                      disabled={generating || isShared}
                      className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                        isShared
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'text-white bg-blue-600 hover:bg-blue-700'
                      }`}
                      title={isShared ? 'Déjà partagé au bureau' : 'Partager au bureau'}
                    >
                      {generating ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                        <>
                          <Share2 className="h-4 w-4 mr-2" />
                          {isShared ? 'Partagé' : 'Bureau'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        </div>
        );
      })()}
    </div>
  );
};

// Bureau Interface Components

// Reports View Component with Enhanced PDF Generation and Analytics
// Nouveau composant ConnectionView pour remplacer ReportsView
const ConnectionView = () => {
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [supabaseStatus, setSupabaseStatus] = useState('checking');
  const [apiStatus, setApiStatus] = useState('checking');
  const [connectionLogs, setConnectionLogs] = useState([]);

  useEffect(() => {
    checkConnections();
    const interval = setInterval(checkConnections, 30000); // Vérifier toutes les 30 secondes
    return () => clearInterval(interval);
  }, []);

  const checkConnections = async () => {
    const logs = [];
    
    // Vérifier l'API Backend
    try {
      const response = await http.get(`${API}/health`);
      if (response.status === 200) {
        setApiStatus('connected');
        logs.push({
          time: new Date().toLocaleTimeString(),
          type: 'success',
          message: 'API Backend: Connexion réussie'
        });
      }
    } catch (error) {
      setApiStatus('disconnected');
      logs.push({
        time: new Date().toLocaleTimeString(),
        type: 'error',
        message: 'API Backend: Connexion échouée'
      });
    }

    // Vérifier Supabase (simulation)
    try {
      // Simuler une vérification Supabase
      setTimeout(() => {
        setSupabaseStatus('connected');
        logs.push({
          time: new Date().toLocaleTimeString(),
          type: 'success',
          message: 'Supabase: Base de données en ligne'
        });
      }, 500);
    } catch (error) {
      setSupabaseStatus('disconnected');
      logs.push({
        time: new Date().toLocaleTimeString(),
        type: 'error',
        message: 'Supabase: Erreur de connexion'
      });
    }

    setConnectionLogs(prevLogs => [...logs, ...prevLogs].slice(0, 20));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'text-green-500';
      case 'disconnected': return 'text-red-500';
      case 'checking': return 'text-yellow-500';
      default: return 'text-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'disconnected': return <XCircle className="h-5 w-5 text-red-500" />;
      case 'checking': return <Loader2 className="h-5 w-5 text-yellow-500 animate-spin" />;
      default: return <AlertCircle className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">État des Connexions</h2>
        <p className="text-gray-600">Surveillance en temps réel des connexions système</p>
      </div>

      {/* Cartes de statut */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Statut API Backend */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center space-x-2">
              <Wifi className="h-5 w-5" />
              <span>API Backend</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {getStatusIcon(apiStatus)}
                <span className={`font-medium ${getStatusColor(apiStatus)}`}>
                  {apiStatus === 'connected' ? 'Connecté' : 
                   apiStatus === 'disconnected' ? 'Déconnecté' : 'Vérification...'}
                </span>
              </div>
              <Badge variant={apiStatus === 'connected' ? 'default' : 'destructive'}>
                {apiStatus === 'connected' ? 'En ligne' : 'Hors ligne'}
              </Badge>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Serveur: {BACKEND_URL}
            </p>
          </CardContent>
        </Card>

        {/* Statut Supabase */}
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-5 w-5" />
              <span>Base de données Supabase</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {getStatusIcon(supabaseStatus)}
                <span className={`font-medium ${getStatusColor(supabaseStatus)}`}>
                  {supabaseStatus === 'connected' ? 'Connecté' : 
                   supabaseStatus === 'disconnected' ? 'Déconnecté' : 'Vérification...'}
                </span>
              </div>
              <Badge variant={supabaseStatus === 'connected' ? 'default' : 'destructive'}>
                {supabaseStatus === 'connected' ? 'Opérationnel' : 'Indisponible'}
              </Badge>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              URL: wursductnatclwrqvgua.supabase.co
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Logs de connexion */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <History className="h-5 w-5" />
            <span>Journal des connexions</span>
          </CardTitle>
          <CardDescription>
            Historique en temps réel des vérifications de connexion
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {connectionLogs.length > 0 ? connectionLogs.map((log, index) => (
              <div key={index} className="flex items-center justify-between p-2 rounded-lg bg-gray-50">
                <div className="flex items-center space-x-2">
                  {log.type === 'success' ? 
                    <CheckCircle className="h-4 w-4 text-green-500" /> : 
                    <XCircle className="h-4 w-4 text-red-500" />
                  }
                  <span className="text-sm font-medium">{log.message}</span>
                </div>
                <span className="text-xs text-gray-500">{log.time}</span>
              </div>
            )) : (
              <div className="text-center py-4 text-gray-500">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                <p>Vérification des connexions...</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Actions de test */}
      <Card>
        <CardHeader>
          <CardTitle>Actions de test</CardTitle>
          <CardDescription>
            Tester manuellement les connexions système
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-4">
            <Button onClick={checkConnections} variant="outline">
              <Wifi className="h-4 w-4 mr-2" />
              Tester connexions
            </Button>
            <Button onClick={() => window.open(`${API}/health`, '_blank')} variant="outline">
              <Globe className="h-4 w-4 mr-2" />
              Ouvrir API
            </Button>
            <Button onClick={() => window.open('https://wursductnatclwrqvgua.supabase.co', '_blank')} variant="outline">
              <Shield className="h-4 w-4 mr-2" />
              Dashboard Supabase
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ReportsView = () => {
  const [reports, setReports] = useState([]);
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date_desc');
  const [searchTerm, setSearchTerm] = useState('');
  
  // NEW: Enhanced filtering and analysis states
  const [dateRange, setDateRange] = useState('all');
  const [technicienFilter, setTechnicienFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [analysisView, setAnalysisView] = useState('list'); // 'list', 'analytics', 'map'
  const [selectedReports, setSelectedReports] = useState([]);
  const [exportFormat, setExportFormat] = useState('pdf');
  
  const [stats, setStats] = useState({
    total: 0,
    thisWeek: 0,
    thisMonth: 0,
    pending: 0,
    // NEW: Enhanced statistics
    completed: 0,
    averagePerDay: 0,
    topTechnician: '',
    topLocation: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const [reportsRes, searchesRes] = await Promise.allSettled([
        http.get(`${API}/reports`),
        http.get(`${API}/searches`)
      ]);
      
      if (reportsRes.status === 'fulfilled') setReports(reportsRes.value.data);
      else console.error('Erreur chargement rapports:', reportsRes.reason);
      
      const searchData = searchesRes.status === 'fulfilled' ? searchesRes.value.data : [];
      if (searchesRes.status === 'fulfilled') setSearches(searchData);
      else { setSearches([]); console.error('Erreur chargement recherches:', searchesRes.reason); }
      
      // Calculate stats
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      setStats({
        total: searchData.length,
        thisWeek: searchData.filter(s => new Date(s.created_at) >= weekAgo).length,
        thisMonth: searchData.filter(s => new Date(s.created_at) >= monthAgo).length,
        pending: searchData.filter(s => s.status === 'ACTIVE').length,
        completed: searchData.filter(s => s.status === 'COMPLETED').length,
        averagePerDay: (searchData.length / 30).toFixed(1),
        topTechnician: 'Jean Dupont',
        topLocation: 'Paris 11ème'
      });
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async (searchId) => {
    setGenerating(true);
    
    try {
      const response = await http({
        method: 'get',
        url: `${API}/searches/${searchId}/pdf`,
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `rapport_${new Date().toISOString().split('T')[0]}.pdf`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('Rapport PDF généré avec succès !');
      
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      alert('Erreur lors de la génération du PDF');
    } finally {
      setGenerating(false);
    }
  };

  const generateSummaryPDF = async () => {
    const filteredIds = filteredSearches.map(s => s.id);
    setGenerating(true);
    
    try {
      const response = await http({
        method: 'post',
        url: `${API}/reports/generate-summary-pdf`,
        data: { search_ids: filteredIds },
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `synthese_rapports_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('Rapport de synthèse généré avec succès !');
    } catch (error) {
      console.error('Erreur génération PDF synthèse:', error);
      alert('Erreur lors de la génération du PDF de synthèse');
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'SHARED': return 'bg-green-100 text-green-700 border-green-200';
      case 'PROCESSED': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'ARCHIVED': return 'bg-black text-white border-black';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // NEW: Enhanced export functions
  const exportToExcel = async () => {
    try {
      const selectedData = selectedReports.length > 0 
        ? searches.filter(s => selectedReports.includes(s.id))
        : filteredSearches;
      
      // Create CSV data
      const csvData = selectedData.map(search => ({
        'Date': formatDate(search.created_at),
        'Localisation': search.location,
        'Description': search.description,
        'Statut': search.status,
        'Technicien': search.technician_name || 'N/A',
        'Coordonnées': `${search.latitude}, ${search.longitude}`,
        'ID': search.id
      }));
      
      // Convert to CSV
      const headers = Object.keys(csvData[0]).join(',');
      const csvContent = csvData.map(row => Object.values(row).join(',')).join('\n');
      const csv = headers + '\n' + csvContent;
      
      // Download
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapports_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('Export Excel réalisé avec succès !');
    } catch (error) {
      console.error('Erreur export Excel:', error);
      alert('Erreur lors de l\'export Excel');
    }
  };

  const exportToJSON = async () => {
    try {
      const selectedData = selectedReports.length > 0 
        ? searches.filter(s => selectedReports.includes(s.id))
        : filteredSearches;
      
      const jsonData = JSON.stringify(selectedData, null, 2);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapports_data_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      alert('Export JSON réalisé avec succès !');
    } catch (error) {
      console.error('Erreur export JSON:', error);
      alert('Erreur lors de l\'export JSON');
    }
  };

  const generateAnalyticsReport = async () => {
    try {
      setGenerating(true);
      
      // Create analytics data
      const analyticsData = {
        period: `${new Date().toLocaleDateString('fr-FR')}`,
        totalSearches: stats.total,
        completionRate: (stats.completed / stats.total * 100).toFixed(1) + '%',
        averagePerDay: stats.averagePerDay,
        statusBreakdown: {
          active: searches.filter(s => s.status === 'ACTIVE').length,
          completed: stats.completed,
          pending: stats.pending
        },
        topPerformers: {
          technician: stats.topTechnician,
          location: stats.topLocation
        },
        trends: {
          thisWeek: stats.thisWeek,
          thisMonth: stats.thisMonth,
          growth: ((stats.thisWeek / 7) * 30 - stats.thisMonth).toFixed(1)
        }
      };
      
      alert(`Rapport d'analyse généré !\n\nRésumé:\n- Total: ${analyticsData.totalSearches} recherches\n- Taux de complétion: ${analyticsData.completionRate}\n- Moyenne/jour: ${analyticsData.averagePerDay}\n- Meilleur technicien: ${analyticsData.topPerformers.technician}`);
    } catch (error) {
      console.error('Erreur génération analytics:', error);
      alert('Erreur lors de la génération du rapport d\'analyse');
    } finally {
      setGenerating(false);
    }
  };

  const toggleReportSelection = (searchId) => {
    setSelectedReports(prev => 
      prev.includes(searchId) 
        ? prev.filter(id => id !== searchId)
        : [...prev, searchId]
    );
  };

  const selectAllReports = () => {
    setSelectedReports(filteredSearches.map(s => s.id));
  };

  const clearSelection = () => {
    setSelectedReports([]);
  };

  // Filter and sort searches
  let filteredSearches = searches.filter(search => {
    const matchesStatus = filterStatus === 'all' || search.status === filterStatus;
    const matchesSearch = searchTerm === '' || 
      search.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      search.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Sort searches
  filteredSearches.sort((a, b) => {
    switch (sortBy) {
      case 'date_desc': return new Date(b.created_at) - new Date(a.created_at);
      case 'date_asc': return new Date(a.created_at) - new Date(b.created_at);
      case 'location': return a.location.localeCompare(b.location);
      case 'status': return a.status.localeCompare(b.status);
      default: return 0;
    }
  });

  if (loading) {
    return (
      <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
        <CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des rapports...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Header with Statistics */}
      <div className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl p-8">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0 mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Centre de Rapports</h2>
            <p className="text-gray-600 mt-1">Gérez, analysez et générez les rapports PDF des recherches terrain</p>
          </div>
          <div className="flex space-x-2">
            <Button
              onClick={generateSummaryPDF}
              disabled={generating || filteredSearches.length === 0}
              className="bg-gradient-to-r from-blue-600 to-black hover:from-blue-700 hover:to-black text-white rounded-2xl px-6 py-3 shadow-lg transform transition-all duration-200 hover:scale-105"
            >
              {generating ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <FileBarChart className="h-4 w-4 mr-2" />
              )}
              Rapport Synthèse ({filteredSearches.length})
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
            <p className="text-sm text-blue-700">Total Recherches</p>
          </div>
          <div className="bg-blue-50 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.thisWeek}</p>
            <p className="text-sm text-blue-700">Cette Semaine</p>
          </div>
          <div className="bg-blue-50 rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.thisMonth}</p>
            <p className="text-sm text-blue-700">Ce Mois</p>
          </div>
          <div className="bg-black rounded-2xl p-4 text-center">
            <p className="text-2xl font-bold text-white">{stats.pending}</p>
            <p className="text-sm text-gray-200">En Attente</p>
          </div>
        </div>

        {/* Enhanced Filters and Search */}
        <div className="flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Rechercher par localisation ou description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
            />
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-white rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all duration-200 px-4 py-2"
          >
            <option value="all">Tous les statuts</option>
            <option value="ACTIVE">Actives</option>
            <option value="SHARED">Partagées</option>
            <option value="PROCESSED">Traitées</option>
            <option value="ARCHIVED">Archivées</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-white rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all duration-200 px-4 py-2"
          >
            <option value="date_desc">Plus récent</option>
            <option value="date_asc">Plus ancien</option>
            <option value="location">Par localisation</option>
            <option value="status">Par statut</option>
          </select>
        </div>
      </div>

      {/* Enhanced Searches List */}
      {filteredSearches.length === 0 ? (
        <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
          <CardContent className="p-12 text-center">
            <FileBarChart className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm || filterStatus !== 'all' ? 'Aucun résultat trouvé' : 'Aucune recherche disponible'}
            </h3>
            <p className="text-gray-500">
              {searchTerm || filterStatus !== 'all' 
                ? 'Essayez de modifier vos critères de recherche'
                : 'Les recherches créées par les techniciens apparaîtront ici.'
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredSearches.map((search) => (
            <Card key={search.id} className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  {/* NEW: Selection checkbox */}
                  <div className="flex-shrink-0 pt-1">
                    <input
                      type="checkbox"
                      checked={selectedReports.includes(search.id)}
                      onChange={() => toggleReportSelection(search.id)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{formatFullAddress(search)}</h3>
                      <Badge className={`text-xs px-2 py-1 rounded-full ${getStatusColor(search.status)}`}>
                        {search.status}
                      </Badge>
                    </div>
                    <p className="text-gray-600 mb-3 line-clamp-2">{search.description}</p>
                    
                    {search.observations && cleanObservations(search.observations) && (
                      <div className="bg-blue-50 rounded-xl p-3 mb-3">
                        <p className="text-sm text-blue-800">
                          <strong>Observations:</strong> {cleanObservations(search.observations)}
                        </p>
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(search.created_at)}
                      </span>
                      {search.latitude && search.longitude && (
                        <span className="flex items-center">
                          <MapPin className="h-4 w-4 mr-1" />
                          {search.latitude.toFixed(4)}, {search.longitude.toFixed(4)}
                        </span>
                      )}
                      <span className="flex items-center">
                        <FileText className="h-4 w-4 mr-1" />
                        ID: {search.id.substring(0, 8)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0">
                    <div className="flex flex-col space-y-2">
                      <Button
                        onClick={() => generatePDF(search.id)}
                        disabled={generating}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-4 py-2 shadow-md transform transition-all duration-200 hover:scale-105"
                      >
                        {generating ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            PDF
                          </>
                        )}
                      </Button>
                      
                      <Button
                        variant="outline"
                        className="rounded-xl px-4 py-2 border-gray-200 hover:bg-gray-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        Détails
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination placeholder */}
      {filteredSearches.length > 10 && (
        <div className="flex justify-center">
          <Button variant="outline" className="rounded-2xl px-6 py-2">
            Voir plus de recherches ({filteredSearches.length - 10} restantes)
          </Button>
        </div>
      )}
        
        {/* NEW: Advanced Analytics and Export Section */}
        <div className="mt-6 space-y-4">
          {/* Advanced Controls */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
            <div className="flex space-x-2">
              <Button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                variant="outline"
                className="rounded-xl border-gray-200 hover:bg-gray-50"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filtres Avancés
              </Button>
              
              <Button
                onClick={() => setAnalysisView(analysisView === 'list' ? 'analytics' : 'list')}
                variant="outline"
                className="rounded-xl border-gray-200 hover:bg-gray-50"
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                {analysisView === 'list' ? 'Vue Analytics' : 'Vue Liste'}
              </Button>
            </div>

            {/* Selection and Export Controls */}
            <div className="flex items-center space-x-3">
              {selectedReports.length > 0 && (
                <div className="flex items-center space-x-2 text-sm text-gray-600 bg-blue-50 rounded-lg px-3 py-1">
                  <CheckCircle className="h-4 w-4 text-blue-500" />
                  <span>{selectedReports.length} sélectionné(s)</span>
                  <Button
                    onClick={clearSelection}
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 text-blue-500 hover:text-blue-700"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}

              <div className="flex space-x-1">
                <Button
                  onClick={selectAllReports}
                  disabled={filteredSearches.length === 0}
                  variant="outline"
                  size="sm"
                  className="rounded-lg text-xs"
                >
                  Tout sélectionner
                </Button>
                
                <Button
                  onClick={exportToExcel}
                  disabled={filteredSearches.length === 0}
                  variant="outline"
                  size="sm"
                  className="rounded-lg text-xs"
                >
                  <Download className="h-3 w-3 mr-1" />
                  Excel
                </Button>
                
                <Button
                  onClick={exportToJSON}
                  disabled={filteredSearches.length === 0}
                  variant="outline"
                  size="sm"
                  className="rounded-lg text-xs"
                >
                  <Download className="h-3 w-3 mr-1" />
                  JSON
                </Button>
                
                <Button
                  onClick={generateAnalyticsReport}
                  disabled={generating || filteredSearches.length === 0}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg text-xs"
                  size="sm"
                >
                  {generating ? (
                    <div className="animate-spin rounded-full h-3 w-3 border-b border-white mr-1"></div>
                  ) : (
                    <BarChart3 className="h-3 w-3 mr-1" />
                  )}
                  Analytics
                </Button>
              </div>
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <h4 className="font-medium text-gray-900 mb-3">Filtres Avancés</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Période</label>
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="w-full bg-white rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  >
                    <option value="all">Toutes les dates</option>
                    <option value="today">Aujourd'hui</option>
                    <option value="week">Cette semaine</option>
                    <option value="month">Ce mois</option>
                    <option value="quarter">Ce trimestre</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Technicien</label>
                  <select
                    value={technicienFilter}
                    onChange={(e) => setTechnicienFilter(e.target.value)}
                    className="w-full bg-white rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  >
                    <option value="all">Tous les techniciens</option>
                    <option value="jean_dupont">Jean Dupont</option>
                    <option value="marie_martin">Marie Martin</option>
                    <option value="pierre_durand">Pierre Durand</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zone géographique</label>
                  <Input
                    placeholder="Ex: Paris, Lyon..."
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="rounded-lg border-gray-200 text-sm"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Statistics in Analytics View */}
          {analysisView === 'analytics' && (
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Analyse Avancée</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                  <p className="text-lg font-bold text-purple-600">{stats.completed}</p>
                  <p className="text-xs text-gray-600">Complétées</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                  <p className="text-lg font-bold text-green-600">{stats.averagePerDay}</p>
                  <p className="text-xs text-gray-600">Moyenne/Jour</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                  <p className="text-lg font-bold text-blue-600">{stats.topTechnician}</p>
                  <p className="text-xs text-gray-600">Top Technicien</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center shadow-sm">
                  <p className="text-lg font-bold text-orange-600">{stats.topLocation}</p>
                  <p className="text-xs text-gray-600">Zone Principale</p>
                </div>
              </div>
            </div>
          )}
        </div>
    </div>
  );
};

// Worksites Management Component - Standalone
const WorksitesManagement = () => {
  const confirm = useConfirm();
  const _cachedClients = getCached(`${API}/clients`);
  const _cachedWorksites = getCached(`${API}/worksites`);
  const [clients, setClients] = useState(_cachedClients || []);
  const [worksites, setWorksites] = useState(_cachedWorksites || []);
  const [loading, setLoading] = useState(!_cachedWorksites);
  const [showWorksiteForm, setShowWorksiteForm] = useState(false);
  const [editingWorksite, setEditingWorksite] = useState(null);
  const [worksiteFilterTab, setWorksiteFilterTab] = useState('ALL');
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [worksiteFormData, setWorksiteFormData] = useState({
    title: '',
    description: '',
    address: '',
    client_id: '',
    status: 'PLANNED',
    progress: 0,
    start_date: '',
    end_date: '',
    budget: '',
    team_size: 1,
    notes: ''
  });

  // --- Planification intégrée ---
  const [autoPlanning, setAutoPlanning] = useState(true);
  const [selectedTechnicians, setSelectedTechnicians] = useState([]);
  const [planningTime, setPlanningTime] = useState('08:00');
  const [planningEndTime, setPlanningEndTime] = useState('17:00');
  const [allCollaborators, setAllCollaborators] = useState([]);
  const [allTeamLeaders, setAllTeamLeaders] = useState([]);
  const [selectedTeamLeader, setSelectedTeamLeader] = useState('');
  const [savingWorksite, setSavingWorksite] = useState(false);
  // --- Équipe assignée (mode édition) ---
  const [editTechnicians, setEditTechnicians] = useState([]);
  const [initialEditTechnicians, setInitialEditTechnicians] = useState([]);
  const [initialEditLeader, setInitialEditLeader] = useState('');
  const [teamsWithMembers, setTeamsWithMembers] = useState([]);
  // --- Vue détail chantier (lecture seule) ---
  const [viewingWorksite, setViewingWorksite] = useState(null);
  const [viewingWorksiteTeam, setViewingWorksiteTeam] = useState([]);
  const [viewingWorksiteRealDates, setViewingWorksiteRealDates] = useState({ start: null, end: null });
  const currentWorksiteViewerId = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').id; } catch { return undefined; } })();
  // --- Chantiers déjà planifiés (au moins un schedule) ---
  const [plannedWorksiteIds, setPlannedWorksiteIds] = useState(() => new Set());

  const loadPlannedWorksites = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const res = await fetchPlanningSchedules(http, `${API}/schedules`);
      const ids = new Set((res.data || []).filter(s => s.worksite_id && !['cancelled', 'partially_completed'].includes(s.status)).map(s => s.worksite_id));
      setPlannedWorksiteIds(ids);
    } catch (e) {
      console.error('Erreur chargement plannings:', e);
    }
  };

  const isWorksitePlanned = (worksiteId) => plannedWorksiteIds.has(worksiteId);

  const openWorksiteEditor = async (worksite) => {
    setEditingWorksite(worksite);
    setWorksiteFormData({
      title: worksite.title || '',
      description: worksite.description || '',
      address: worksite.address || '',
      client_id: worksite.client_id || '',
      status: worksite.status || 'PLANNED',
      progress: worksite.progress || 0,
      start_date: worksite.start_date || '',
      end_date: worksite.end_date || '',
      budget: worksite.budget || '',
      team_size: worksite.team_size || 1,
      notes: worksite.notes || ''
    });
    // Charger les techniciens déjà assignés via les schedules
    try {
      const res = await fetchPlanningSchedules(http, `${API}/schedules`, { params: { worksite_id: worksite.id } });
      const today = new Date().toLocaleDateString('en-CA');
      const wsSchedules = (res.data || []).filter(schedule => ['scheduled', 'unassigned'].includes(schedule.status) && schedule.start_date > today);
      const techIds = [...new Set(wsSchedules.filter(s => s.collaborator_id).map(s => s.collaborator_id))];
      setEditTechnicians(techIds);
      setInitialEditTechnicians(techIds);
      const leaders = [...new Set(wsSchedules.map(schedule => schedule.team_leader_id).filter(Boolean))];
      const leader = leaders.length === 1 ? leaders[0] : '';
      setSelectedTeamLeader(leader);
      setInitialEditLeader(leader);
    } catch {
      toast.error('Impossible de charger les affectations du chantier');
      return;
    }
    setShowWorksiteForm(true);
  };

  useEffect(() => {
    if (!viewingWorksite) { setViewingWorksiteTeam([]); setViewingWorksiteRealDates({ start: null, end: null }); return; }
    (async () => {
      try {
        const res = await http.get(`${API}/schedules?worksite_id=${viewingWorksite.id}`);
        // Filtre défensif: le backend filtre déjà par worksite_id mais on sécurise côté client
        const scheds = (res.data || []).filter(s => s.worksite_id === viewingWorksite.id);
        // Dates réelles : début = 1re intervention réelle ; fin = date de clôture par le bureau (completed_at)
        const activeScheds = scheds.filter(s => s.status !== 'cancelled');
        const starts = activeScheds.map(s => s.start_date).filter(Boolean);
        const completions = activeScheds.map(s => (s.completed_at || '').slice(0, 10)).filter(Boolean);
        setViewingWorksiteRealDates({
          start: starts.length ? starts.reduce((a, b) => (a < b ? a : b)) : null,
          end: completions.length ? completions.reduce((a, b) => (a > b ? a : b)) : null,
        });
        const techIds = [...new Set(scheds.filter(s => s.collaborator_id).map(s => s.collaborator_id))];
        const leaderIds = [...new Set(scheds.filter(s => s.team_leader_id).map(s => s.team_leader_id))];
        const team = [];
        // Map collaborator names from allCollaborators
        techIds.forEach(id => {
          const c = allCollaborators.find(x => x.id === id);
          if (c) team.push({ id, name: `${c.first_name || ''} ${c.last_name || ''}`.trim(), role: 'Collaborateur' });
          else {
            const s = scheds.find(x => x.collaborator_id === id);
            if (s) team.push({ id, name: `${s.collaborator_first_name || ''} ${s.collaborator_last_name || ''}`.trim() || id, role: 'Collaborateur' });
          }
        });
        // Map team leaders
        leaderIds.forEach(id => {
          if (techIds.includes(id)) return;
          const l = allTeamLeaders.find(x => x.id === id);
          if (l) team.push({ id, name: `${l.first_name || l.name || ''} ${l.last_name || ''}`.trim(), role: 'Chef d\'équipe' });
        });
        // Chefs d'équipe en premier
        team.sort((a, b) => (a.role === 'Chef d\'équipe' ? 0 : 1) - (b.role === 'Chef d\'équipe' ? 0 : 1));
        setViewingWorksiteTeam(team);
      } catch { setViewingWorksiteTeam([]); setViewingWorksiteRealDates({ start: null, end: null }); }
    })();
  }, [viewingWorksite]);

  useEffect(() => {
    perf.start('Worksites.mount');
    loadClients();
    loadWorksites();
    loadCollaboratorsForPlanning();
    loadPlannedWorksites();
  }, []);

  const loadCollaboratorsForPlanning = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const [collabRes, leadersRes, teamsRes] = await Promise.allSettled([
        http.get(`${API}/collaborators`),
        http.get(`${API}/team-leaders`),
        http.get(`${API}/team-leaders-stats`)
      ]);
      if (collabRes.status === 'fulfilled') setAllCollaborators(collabRes.value.data || []);
      if (leadersRes.status === 'fulfilled') setAllTeamLeaders(leadersRes.value.data || []);
      if (teamsRes.status === 'fulfilled') setTeamsWithMembers(teamsRes.value.data || []);
    } catch (e) {
      console.error('Erreur chargement collaborateurs:', e);
    }
  };

  // ⚡ Realtime — rafraîchir automatiquement quand worksites ou clients changent
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.table === 'worksites') loadWorksites();
      if (e.detail?.table === 'clients') loadClients();
      if (e.detail?.table === 'schedules') loadPlannedWorksites();
    };
    window.addEventListener('realtime-update', handler);
    return () => window.removeEventListener('realtime-update', handler);
  }, []);

  const loadClients = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await http.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Erreur chargement clients:', error);
    }
  };

  const loadWorksites = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await http.get(`${API}/worksites`);
      setWorksites(response.data);
    } catch (error) {
      console.error('Erreur chargement chantiers:', error);
      setWorksites([]);
    } finally {
      perf.end('Worksites.mount');
      setLoading(false);
    }
  };

  const saveWorksite = async (e) => {
    e.preventDefault();
    setSavingWorksite(true);
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      if (editingWorksite) {
        const payload = { ...worksiteFormData };
        if (JSON.stringify([...editTechnicians].sort()) !== JSON.stringify([...initialEditTechnicians].sort())) payload.technicians = editTechnicians;
        if (selectedTeamLeader !== initialEditLeader) payload.team_leader_id = selectedTeamLeader || null;
        if (payload.status === 'COMPLETED') payload.progress = 100;
        await http.put(`${API}/worksites/${editingWorksite.id}`, payload, { headers });
        toast.success('Chantier modifié avec succès');
      } else {
        // Création avec planification intégrée
        const payload = { ...worksiteFormData };
        if (autoPlanning && worksiteFormData.start_date && worksiteFormData.end_date) {
          payload.planning = {
            technician_ids: selectedTechnicians,
            team_leader_id: selectedTeamLeader || null,
            time: planningTime,
            end_time: planningEndTime,
          };
        }
        const res = await http.post(`${API}/worksites`, payload, { headers });
        const nbSchedules = res.data?.schedules_created || 0;
        if (nbSchedules > 0) {
          toast.success(`Chantier créé avec ${nbSchedules} affectations`);
        } else {
          toast.success('Chantier créé avec succès');
        }
      }

      setWorksiteFormData({
        title: '', description: '', address: '', client_id: '',
        status: 'PLANNED', progress: 0, start_date: '', end_date: '',
        budget: '', team_size: 1, notes: ''
      });
      setAutoPlanning(true);
      setSelectedTechnicians([]);
      setEditTechnicians([]);
      setSelectedTeamLeader('');
      setPlanningTime('08:00');
      setPlanningEndTime('17:00');
      setShowWorksiteForm(false);
      setEditingWorksite(null);
      loadWorksites();
      loadPlannedWorksites();
    } catch (error) {
      console.error('Erreur sauvegarde chantier:', error);
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Erreur lors de la sauvegarde du chantier');
    } finally {
      setSavingWorksite(false);
    }
  };

  const deleteWorksite = async (worksiteId) => {
    if (!(await confirm('Êtes-vous sûr de vouloir supprimer ce chantier ? Cette action est irréversible.'))) {
      return;
    }

    try {
      await http.delete(`${API}/worksites/${worksiteId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      alert('Chantier supprimé avec succès');
      loadWorksites();
    } catch (error) {
      // 404 = le chantier n'existe déjà plus (déjà supprimé, ou liste obsolète
      // suite à une déconnexion Realtime). On considère l'opération réussie et
      // on rafraîchit la liste pour retirer l'entrée fantôme de l'écran.
      if (error?.response?.status === 404) {
        console.warn('Chantier déjà supprimé (404) — rafraîchissement de la liste');
        loadWorksites();
        return;
      }
      console.error('Erreur suppression chantier:', error);
      alert('Erreur lors de la suppression du chantier');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-3xl p-8 text-white shadow-2xl" style={{background:'linear-gradient(135deg,#2563EB 0%,#7C3AED 55%,#FB923C 100%)'}}>
          <h2 className="text-3xl font-bold mb-2">Gestion des Chantiers</h2>
          <p className="text-blue-100">Chargement en cours...</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-white rounded-2xl p-6 shadow-md animate-pulse">
              <div className="h-3 w-20 bg-gray-200 rounded mb-3"></div>
              <div className="h-8 w-16 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 animate-pulse">
              <div className="h-4 w-1/3 bg-gray-200 rounded mb-3"></div>
              <div className="h-5 w-2/3 bg-gray-200 rounded mb-4"></div>
              <div className="h-3 w-1/2 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 w-1/4 bg-gray-100 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec statistiques */}
      <div className="rounded-3xl p-8 text-white shadow-2xl" style={{background:'linear-gradient(135deg,#2563EB 0%,#7C3AED 55%,#FB923C 100%)'}}>
        <h2 className="text-3xl font-bold mb-2">Gestion des Chantiers</h2>
        <p className="text-blue-100">Pilotez tous vos chantiers depuis une interface unique</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-6 shadow-md border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase mb-1">Total Chantiers</p>
              <p className="text-3xl font-bold text-gray-900">{worksites.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Building className="h-7 w-7 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-md border-l-4 border-violet-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase mb-1">En Cours</p>
              <p className="text-3xl font-bold text-gray-900">{worksites.filter(w => w.status === 'IN_PROGRESS').length}</p>
            </div>
            <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
              <Clock className="h-7 w-7 text-violet-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-md border-l-4 border-orange-400">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase mb-1">Terminés</p>
              <p className="text-3xl font-bold text-gray-900">{worksites.filter(w => w.status === 'COMPLETED').length}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <CheckCircle className="h-7 w-7 text-orange-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Onglets de filtrage par statut */}
      <div className="flex flex-wrap gap-2 p-1.5 bg-gray-100 rounded-2xl">
        {[
          { key: 'ALL', label: 'Tous', icon: '📋', count: worksites.length },
          { key: 'PLANNED', label: 'Planifiés', icon: '📅', count: worksites.filter(w => w.status === 'PLANNED').length },
          { key: 'IN_PROGRESS', label: 'En cours', icon: '🔧', count: worksites.filter(w => w.status === 'IN_PROGRESS').length },
          { key: 'COMPLETED', label: 'Terminés', icon: '✅', count: worksites.filter(w => w.status === 'COMPLETED').length },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setWorksiteFilterTab(tab.key)}
            className={`flex-1 min-w-[100px] px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
              worksiteFilterTab === tab.key
                ? 'bg-white text-blue-600 shadow-lg'
                : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
            }`}
          >
            <span className="mr-1">{tab.icon}</span> {tab.label}
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
              worksiteFilterTab === tab.key
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-500'
            }`}>{tab.count}</span>
          </button>
        ))}
      </div>

      {/* Bouton Nouveau Chantier */}
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-gray-900">Liste des Chantiers</h3>
        <Button
          onClick={() => {
            setShowWorksiteForm(true);
            setEditingWorksite(null);
            setWorksiteFormData({
              title: '',
              description: '',
              address: '',
              client_id: '',
              status: 'PLANNED',
              progress: 0,
              start_date: '',
              end_date: '',
              budget: '',
              team_size: 1,
              notes: ''
            });
          }}
          className="bg-gradient-to-r from-[#2563EB] to-[#7C3AED] hover:from-[#1d4ed8] hover:to-[#6d28d9] text-white rounded-xl px-6 py-3 shadow-lg transition-all"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nouveau Chantier
        </Button>
      </div>

      {/* Formulaire de création/modification */}
      {showWorksiteForm && (
        <Card className="bg-white rounded-3xl border-2 border-gray-200 shadow-2xl">
          <CardHeader className="border-b border-gray-100">
            <div className="flex items-center justify-between">
              <CardTitle className="text-2xl font-bold text-gray-900">
                {editingWorksite ? 'Modifier le chantier' : 'Créer un nouveau chantier'}
              </CardTitle>
              <Button
                onClick={() => {
                  setShowWorksiteForm(false);
                  setEditingWorksite(null);
                }}
                variant="ghost"
                size="sm"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={saveWorksite} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Titre du chantier *</label>
                  <input
                    type="text"
                    required
                    value={worksiteFormData.title}
                    onChange={(e) => setWorksiteFormData({...worksiteFormData, title: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none"
                    placeholder="Ex: Rénovation immeuble..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Client</label>
                  <div className="relative">
                    {/* Champ d'affichage / recherche */}
                    <div
                      className={`w-full px-4 py-3 border-2 rounded-xl cursor-pointer flex items-center justify-between transition-colors ${clientDropdownOpen ? 'border-violet-500 ring-2 ring-violet-200' : 'border-gray-200 hover:border-gray-300'}`}
                      onClick={() => { setClientDropdownOpen(!clientDropdownOpen); setClientSearchTerm(''); }}
                    >
                      {worksiteFormData.client_id ? (
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {(clients.find(c => c.id === worksiteFormData.client_id)?.nom || '?')[0]?.toUpperCase()}
                          </div>
                          <span className="font-medium text-gray-900">{clients.find(c => c.id === worksiteFormData.client_id)?.nom}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">Sélectionner un client</span>
                      )}
                      <svg className={`w-4 h-4 text-gray-400 transition-transform ${clientDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </div>

                    {/* Dropdown panel */}
                    {clientDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border-2 border-violet-200 rounded-xl shadow-2xl overflow-hidden">
                        {/* Barre de recherche */}
                        <div className="p-2 border-b border-gray-100">
                          <input
                            autoFocus
                            type="text"
                            placeholder="Rechercher un client..."
                            value={clientSearchTerm}
                            onChange={e => setClientSearchTerm(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-violet-400 focus:outline-none"
                          />
                        </div>
                        {/* Option vide */}
                        <div
                          className="px-4 py-2.5 text-sm text-gray-400 hover:bg-gray-50 cursor-pointer"
                          onClick={() => { setWorksiteFormData({...worksiteFormData, client_id: ''}); setClientDropdownOpen(false); }}
                        >
                          — Aucun client
                        </div>
                        {/* Liste filtrée */}
                        <div className="max-h-52 overflow-y-auto">
                          {clients.filter(c => (c.nom || '').toLowerCase().includes(clientSearchTerm.toLowerCase())).map(client => (
                            <div
                              key={client.id}
                              className={`px-4 py-2.5 flex items-center gap-3 cursor-pointer hover:bg-violet-50 transition-colors ${worksiteFormData.client_id === client.id ? 'bg-violet-50' : ''}`}
                              onClick={() => { setWorksiteFormData({...worksiteFormData, client_id: client.id}); setClientDropdownOpen(false); setClientSearchTerm(''); }}
                            >
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                {(client.nom || '?')[0]?.toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-gray-900 truncate">{client.nom}</div>
                                {client.email && <div className="text-xs text-gray-400 truncate">{client.email}</div>}
                              </div>
                              {worksiteFormData.client_id === client.id && (
                                <svg className="w-4 h-4 text-violet-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                              )}
                            </div>
                          ))}
                          {clients.filter(c => (c.nom || '').toLowerCase().includes(clientSearchTerm.toLowerCase())).length === 0 && (
                            <div className="px-4 py-6 text-center text-sm text-gray-400">Aucun client trouvé</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                <textarea
                  value={worksiteFormData.description}
                  onChange={(e) => setWorksiteFormData({...worksiteFormData, description: e.target.value})}
                  rows="4"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none"
                  placeholder="Description détaillée du chantier..."
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse</label>
                <input
                  type="text"
                  value={worksiteFormData.address}
                  onChange={(e) => setWorksiteFormData({...worksiteFormData, address: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none"
                  placeholder="Adresse complète du chantier"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Statut</label>
                  <select
                    value={worksiteFormData.status}
                    onChange={(e) => setWorksiteFormData({...worksiteFormData, status: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none"
                  >
                    <option value="PLANNED">📋 Planifié</option>
                    <option value="IN_PROGRESS">🔧 En cours</option>
                    <option value="ON_HOLD">⏸️ En pause</option>
                    <option value="COMPLETED">✅ Terminé</option>
                    <option value="CANCELLED">❌ Annulé</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Avancement (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={worksiteFormData.progress}
                    readOnly
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-100 cursor-not-allowed"
                    title="L'avancement est calculé automatiquement en fonction des jours de planning"
                  />
                  <p className="text-xs text-gray-500 mt-1">📊 Calculé automatiquement à partir des jours de planning</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date de début</label>
                  <input
                    type="date"
                    value={worksiteFormData.start_date}
                    onChange={(e) => setWorksiteFormData({...worksiteFormData, start_date: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Date de fin prévue</label>
                  <input
                    type="date"
                    value={worksiteFormData.end_date}
                    onChange={(e) => setWorksiteFormData({...worksiteFormData, end_date: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Budget (€)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={worksiteFormData.budget}
                    onChange={(e) => setWorksiteFormData({...worksiteFormData, budget: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Taille de l'équipe</label>
                  <input
                    type="number"
                    min="1"
                    value={worksiteFormData.team_size}
                    onChange={(e) => setWorksiteFormData({...worksiteFormData, team_size: parseInt(e.target.value) || 1})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Notes internes</label>
                <textarea
                  value={worksiteFormData.notes}
                  onChange={(e) => setWorksiteFormData({...worksiteFormData, notes: e.target.value})}
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-black focus:outline-none"
                  placeholder="Notes pour l'équipe..."
                />
              </div>

              {/* ═══════════════════════════════════════════════════════════
                  SECTION ÉQUIPE ASSIGNÉE (uniquement en édition)
                  ═══════════════════════════════════════════════════════════ */}
              {editingWorksite && (
                <div className="border-2 border-green-200 rounded-2xl p-6 bg-green-50/50">
                  <div className="flex items-center gap-3 mb-4">
                    <Users className="h-6 w-6 text-green-600" />
                    <h3 className="text-lg font-bold text-gray-900">Équipe assignée</h3>
                    <span className="text-sm text-gray-500">({editTechnicians.length} sélectionné{editTechnicians.length > 1 ? 's' : ''})</span>
                  </div>

                  {/* Sélection par équipe */}
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Chef des affectations futures
                    <select value={selectedTeamLeader} onChange={event => setSelectedTeamLeader(event.target.value)} className="block w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 bg-white">
                      <option value="">Sans chef unique</option>
                      {allTeamLeaders.map(leader => <option key={leader.id} value={leader.id}>{leader.first_name || leader.name} {leader.last_name || ''}</option>)}
                    </select>
                  </label>
                  {teamsWithMembers.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Sélectionner une équipe</p>
                      <div className="space-y-2">
                        {teamsWithMembers.map(team => {
                          const memberIds = (team.collaborators || []).map(c => c.id);
                          const allSelected = memberIds.length > 0 && memberIds.every(id => editTechnicians.includes(id));
                          const someSelected = memberIds.some(id => editTechnicians.includes(id));
                          return (
                            <div key={team.id} className={`border-2 rounded-xl overflow-hidden transition-colors ${allSelected ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white'}`}>
                              <button
                                type="button"
                                onClick={() => {
                                  if (allSelected) {
                                    setEditTechnicians(prev => prev.filter(id => !memberIds.includes(id)));
                                  } else {
                                    setEditTechnicians(prev => [...new Set([...prev, ...memberIds])]);
                                  }
                                }}
                                className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left"
                              >
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${allSelected ? 'bg-green-600 border-green-600' : someSelected ? 'bg-green-200 border-green-400' : 'border-gray-300'}`}>
                                  {allSelected && <Check className="h-3 w-3 text-white" />}
                                  {someSelected && !allSelected && <div className="w-2 h-2 bg-green-600 rounded-sm" />}
                                </div>
                                <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                                  {`${team.first_name || ''} ${team.last_name || ''}`.trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-bold text-gray-900 truncate">
                                    {`${team.first_name || ''} ${team.last_name || ''}`.trim() || team.email || 'Chef d\'équipe'}
                                  </p>
                                  <p className="text-xs text-gray-400">{memberIds.length} membre{memberIds.length > 1 ? 's' : ''}</p>
                                </div>
                              </button>
                              {/* Membres de l'équipe */}
                              {(team.collaborators || []).length > 0 && (
                                <div className="border-t border-gray-100 px-3 py-2 bg-gray-50/50 space-y-1">
                                  {team.collaborators.map(member => (
                                    <label key={member.id} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${editTechnicians.includes(member.id) ? 'bg-green-100' : 'hover:bg-white'}`}>
                                      <input
                                        type="checkbox"
                                        checked={editTechnicians.includes(member.id)}
                                        onChange={(e) => {
                                          if (e.target.checked) setEditTechnicians(prev => [...prev, member.id]);
                                          else setEditTechnicians(prev => prev.filter(id => id !== member.id));
                                        }}
                                        className="w-4 h-4 text-green-600 rounded"
                                      />
                                      <span className="text-sm text-gray-700">
                                        {member.first_name || member.last_name
                                          ? `${member.first_name || ''} ${member.last_name || ''}`.trim()
                                          : member.email}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Sélection individuelle */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Tous les collaborateurs</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border-2 border-gray-200 rounded-xl p-3 bg-white">
                      {allCollaborators.length === 0 ? (
                        <p className="text-sm text-gray-400 col-span-2">Aucun collaborateur trouvé</p>
                      ) : (
                        allCollaborators.filter(c => c.role !== 'FONDATEUR').map(collab => (
                          <label key={collab.id} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${editTechnicians.includes(collab.id) ? 'bg-green-100 border border-green-300' : 'hover:bg-gray-50'}`}>
                            <input
                              type="checkbox"
                              checked={editTechnicians.includes(collab.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setEditTechnicians(prev => [...prev, collab.id]);
                                } else {
                                  setEditTechnicians(prev => prev.filter(id => id !== collab.id));
                                }
                              }}
                              className="w-4 h-4 text-green-600 rounded"
                            />
                            <span className="text-sm font-medium text-gray-800">
                              {collab.first_name || collab.last_name
                                ? `${collab.first_name || ''} ${collab.last_name || ''}`.trim()
                                : collab.email}
                            </span>
                            <span className="text-xs text-gray-400 ml-auto">{collab.role}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>

                  {editTechnicians.length === 0 && (
                    <p className="mt-3 text-sm text-orange-600">⚠️ Aucun technicien sélectionné — les plannings resteront non assignés.</p>
                  )}
                </div>
              )}

              {/* ═══════════════════════════════════════════════════════════
                  SECTION PLANIFICATION INTÉGRÉE (uniquement en création)
                  ═══════════════════════════════════════════════════════════ */}
              {!editingWorksite && (
                <div className="border-2 border-blue-200 rounded-2xl p-6 bg-blue-50/50">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Calendar className="h-6 w-6 text-blue-600" />
                      <h3 className="text-lg font-bold text-gray-900">Planification automatique</h3>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoPlanning}
                        onChange={(e) => setAutoPlanning(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                    </label>
                  </div>

                  {autoPlanning && (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-500 mb-3">
                        {worksiteFormData.start_date && worksiteFormData.end_date
                          ? `Les plannings seront générés du ${new Date(worksiteFormData.start_date).toLocaleDateString('fr-FR')} au ${new Date(worksiteFormData.end_date).toLocaleDateString('fr-FR')} (jours ouvrés uniquement).`
                          : 'Renseignez les dates de début et fin ci-dessus pour activer la planification.'}
                      </p>

                      {/* Horaires */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">
                            <Clock className="h-4 w-4 inline mr-1" />Heure de début
                          </label>
                          <input
                            type="time"
                            value={planningTime}
                            onChange={(e) => setPlanningTime(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">
                            <Clock className="h-4 w-4 inline mr-1" />Heure de fin
                          </label>
                          <input
                            type="time"
                            value={planningEndTime}
                            onChange={(e) => setPlanningEndTime(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                          />
                        </div>
                      </div>

                      {/* Chef d'équipe */}
                      {allTeamLeaders.length > 0 && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Chef d'équipe</label>
                          <select
                            value={selectedTeamLeader}
                            onChange={(e) => setSelectedTeamLeader(e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                          >
                            <option value="">Aucun (optionnel)</option>
                            {allTeamLeaders.map(tl => (
                              <option key={tl.id} value={tl.id}>
                                {tl.first_name || tl.last_name ? `${tl.first_name || ''} ${tl.last_name || ''}`.trim() : tl.email}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Sélection techniciens */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <Users className="h-4 w-4 inline mr-1" />
                          Techniciens à assigner ({selectedTechnicians.length} sélectionné{selectedTechnicians.length > 1 ? 's' : ''})
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border-2 border-gray-200 rounded-xl p-3 bg-white">
                          {allCollaborators.length === 0 ? (
                            <p className="text-sm text-gray-400 col-span-2">Aucun collaborateur trouvé</p>
                          ) : (
                            allCollaborators.filter(c => c.role !== 'FONDATEUR').map(collab => (
                              <label key={collab.id} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${selectedTechnicians.includes(collab.id) ? 'bg-blue-100 border border-blue-300' : 'hover:bg-gray-50'}`}>
                                <input
                                  type="checkbox"
                                  checked={selectedTechnicians.includes(collab.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedTechnicians(prev => [...prev, collab.id]);
                                    } else {
                                      setSelectedTechnicians(prev => prev.filter(id => id !== collab.id));
                                    }
                                  }}
                                  className="w-4 h-4 text-blue-600 rounded"
                                />
                                <span className="text-sm font-medium text-gray-800">
                                  {collab.first_name || collab.last_name
                                    ? `${collab.first_name || ''} ${collab.last_name || ''}`.trim()
                                    : collab.email}
                                </span>
                                <span className="text-xs text-gray-400 ml-auto">{collab.role}</span>
                              </label>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Résumé */}
                      {worksiteFormData.start_date && worksiteFormData.end_date && (() => {
                        const start = new Date(worksiteFormData.start_date);
                        const end = new Date(worksiteFormData.end_date);
                        let workDays = 0;
                        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                          if (d.getDay() !== 0 && d.getDay() !== 6) workDays++;
                        }
                        if (selectedTechnicians.length > 0) {
                          const totalSchedules = workDays * selectedTechnicians.length;
                          return (
                            <div className="bg-blue-100 rounded-xl p-3 text-sm text-blue-800">
                              <strong>{totalSchedules} plannings</strong> seront créés ({workDays} jours ouvrés × {selectedTechnicians.length} technicien{selectedTechnicians.length > 1 ? 's' : ''})
                            </div>
                          );
                        } else {
                          return (
                            <div className="bg-orange-100 rounded-xl p-3 text-sm text-orange-800">
                              <strong>⚠️ {workDays} plannings NON ASSIGNÉS</strong> seront créés ({workDays} jours ouvrés, aucun technicien sélectionné). Vous pourrez les assigner plus tard depuis le planning.
                            </div>
                          );
                        }
                      })()}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={savingWorksite}
                  className="flex-1 bg-black hover:bg-gray-800 text-white rounded-xl py-4 text-lg font-semibold shadow-lg disabled:opacity-50"
                >
                  {savingWorksite ? (
                    <><Loader2 className="h-5 w-5 mr-2 animate-spin" />Création en cours...</>
                  ) : (
                    <><Check className="h-5 w-5 mr-2" />{editingWorksite ? 'Enregistrer les modifications' : (autoPlanning && worksiteFormData.start_date && worksiteFormData.end_date ? (selectedTechnicians.length > 0 ? 'Créer le chantier + Planning' : 'Créer le chantier + Planning (non assigné)') : 'Créer le chantier')}</>
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setShowWorksiteForm(false);
                    setEditingWorksite(null);
                  }}
                  variant="outline"
                  className="rounded-xl px-8 border-2 border-gray-300"
                >
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Liste des chantiers */}
      {worksites.length === 0 ? (
        <Card className="bg-white rounded-3xl shadow-xl">
          <CardContent className="p-16 text-center">
            <Building className="h-20 w-20 mx-auto mb-4 text-gray-300" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Aucun chantier</h3>
            <p className="text-gray-500 mb-6">Créez votre premier chantier pour commencer</p>
            <Button
              onClick={() => setShowWorksiteForm(true)}
              className="bg-black hover:bg-gray-800 text-white rounded-xl px-6 py-3"
            >
              <Plus className="h-5 w-5 mr-2" />
              Créer un chantier
            </Button>
          </CardContent>
        </Card>
      ) : worksites.filter(w => worksiteFilterTab === 'ALL' || w.status === worksiteFilterTab).length === 0 ? (
        <Card className="bg-white rounded-3xl shadow-xl">
          <CardContent className="p-16 text-center">
            <Building className="h-20 w-20 mx-auto mb-4 text-gray-300" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Aucun chantier dans cette catégorie</h3>
            <p className="text-gray-500 mb-6">Changez de filtre pour voir d'autres chantiers</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {worksites.filter(w => worksiteFilterTab === 'ALL' || w.status === worksiteFilterTab).map((worksite) => (
            <Card key={worksite.id} className="bg-white rounded-2xl border-2 border-gray-100 hover:border-gray-300 hover:shadow-2xl transition-all duration-300 cursor-pointer" onClick={() => setViewingWorksite(worksite)}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{worksite.title}</h3>
                    <span className={`inline-block px-3 py-1 rounded-lg text-xs font-bold ${
                      worksite.status === 'PLANNED' ? 'bg-gray-100 text-gray-700' :
                      worksite.status === 'IN_PROGRESS' ? 'bg-gray-200 text-black' :
                      worksite.status === 'ON_HOLD' ? 'bg-gray-100 text-gray-600' :
                      worksite.status === 'COMPLETED' ? 'bg-black text-white' : 'bg-gray-50 text-gray-500'
                    }`}>
                      {worksite.status === 'PLANNED' ? '📋 Planifié' :
                       worksite.status === 'IN_PROGRESS' ? '🔧 En cours' :
                       worksite.status === 'ON_HOLD' ? '⏸️ En pause' :
                       worksite.status === 'COMPLETED' ? '✅ Terminé' : worksite.status}
                    </span>
                  </div>
                </div>

                {worksite.clients?.name && (
                  <p className="text-sm text-gray-600 flex items-center mb-2">
                    <Users className="h-4 w-4 mr-2" />
                    {worksite.clients.name}
                  </p>
                )}
                
                {worksite.address && (
                  <p className="text-sm text-gray-600 flex items-center mb-4">
                    <MapPin className="h-4 w-4 mr-2" />
                    {worksite.address}
                  </p>
                )}

                {worksite.progress !== null && worksite.progress !== undefined && (
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-600 mb-2">
                      <span className="font-semibold">Avancement</span>
                      <span className="font-bold">{worksite.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div
                        className="bg-black rounded-full h-3 transition-all duration-500"
                        style={{ width: `${worksite.progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 mb-4">
                  {!!worksite.budget && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Budget</p>
                      <p className="text-lg font-bold text-black">{parseFloat(worksite.budget).toLocaleString()}€</p>
                    </div>
                  )}
                  {!!worksite.team_size && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Équipe</p>
                      <p className="text-lg font-bold text-black">{worksite.team_size} pers.</p>
                    </div>
                  )}
                </div>

                <div className="flex space-x-2" onClick={e => e.stopPropagation()}>
                  {!isWorksitePlanned(worksite.id) && (
                    <Button
                      size="sm"
                      onClick={() => openWorksiteEditor(worksite)}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                    >
                      <Calendar className="h-4 w-4 mr-1" />
                      Planifier
                    </Button>
                  )}
                  <Button
                    size="sm"
                    onClick={() => openWorksiteEditor(worksite)}
                    className="flex-1 bg-black hover:bg-gray-800 text-white rounded-lg"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Modifier
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => deleteWorksite(worksite.id)}
                    variant="outline"
                    className="rounded-lg border-2 border-gray-300 text-gray-600 hover:bg-gray-100"
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal détail chantier (lecture seule) */}
      {viewingWorksite && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewingWorksite(null)}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">{viewingWorksite.title}</h2>
                {viewingWorksite.reference && (
                  <p className="text-xs font-semibold text-gray-500 mt-0.5">Réf. {viewingWorksite.reference}</p>
                )}
                <span className={`inline-block mt-2 px-3 py-1 rounded-lg text-xs font-bold ${
                  viewingWorksite.status === 'PLANNED' ? 'bg-gray-100 text-gray-700' :
                  viewingWorksite.status === 'IN_PROGRESS' ? 'bg-gray-200 text-black' :
                  viewingWorksite.status === 'ON_HOLD' ? 'bg-gray-100 text-gray-600' :
                  viewingWorksite.status === 'COMPLETED' ? 'bg-black text-white' : 'bg-gray-50 text-gray-500'
                }`}>
                  {viewingWorksite.status === 'PLANNED' ? '📋 Planifié' :
                   viewingWorksite.status === 'IN_PROGRESS' ? '🔧 En cours' :
                   viewingWorksite.status === 'ON_HOLD' ? '⏸️ En pause' :
                   viewingWorksite.status === 'COMPLETED' ? '✅ Terminé' : viewingWorksite.status}
                </span>
              </div>
              <button onClick={() => setViewingWorksite(null)} className="p-2 hover:bg-gray-100 rounded-full transition">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Client */}
              {(viewingWorksite.clients?.name || viewingWorksite.client_name) && (
                <div className="flex items-center text-gray-700">
                  <Users className="h-5 w-5 mr-3 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400 font-medium">Client</p>
                    <p className="font-semibold">{viewingWorksite.clients?.name || viewingWorksite.client_name}</p>
                  </div>
                </div>
              )}

              {/* Adresse */}
              {viewingWorksite.address && (
                <div className="flex items-center text-gray-700">
                  <MapPin className="h-5 w-5 mr-3 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400 font-medium">Adresse</p>
                    <p className="font-semibold">{viewingWorksite.address}</p>
                  </div>
                </div>
              )}

              {/* Description */}
              {viewingWorksite.description && (
                <div className="flex items-start text-gray-700">
                  <FileText className="h-5 w-5 mr-3 mt-0.5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400 font-medium">Description</p>
                    <p className="text-sm leading-relaxed">{viewingWorksite.description}</p>
                  </div>
                </div>
              )}

              {/* Avancement */}
              {viewingWorksite.progress !== null && viewingWorksite.progress !== undefined && (
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span className="font-semibold">Avancement</span>
                    <span className="font-bold">{viewingWorksite.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-black rounded-full h-3 transition-all duration-500" style={{ width: `${viewingWorksite.progress}%` }}></div>
                  </div>
                </div>
              )}

              {/* Infos clés */}
              <div className="grid grid-cols-2 gap-3">
                {!!viewingWorksite.budget && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">Budget</p>
                    <p className="text-lg font-bold text-black">{parseFloat(viewingWorksite.budget).toLocaleString()}€</p>
                  </div>
                )}
                {!!viewingWorksite.team_size && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">Équipe</p>
                    <p className="text-lg font-bold text-black">{viewingWorksite.team_size} personne{viewingWorksite.team_size > 1 ? 's' : ''}</p>
                  </div>
                )}
                {viewingWorksite.start_date && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">Début (prévu)</p>
                    <p className="text-sm font-bold text-gray-900">{new Date(viewingWorksite.start_date).toLocaleDateString('fr-FR')}</p>
                  </div>
                )}
                {viewingWorksite.end_date && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">Fin (prévue)</p>
                    <p className="text-sm font-bold text-gray-900">{new Date(viewingWorksite.end_date).toLocaleDateString('fr-FR')}</p>
                  </div>
                )}
                {viewingWorksiteRealDates.start && (
                  <div className="bg-blue-50 rounded-xl p-4">
                    <p className="text-xs text-blue-500 mb-1">Début (réel)</p>
                    <p className="text-sm font-bold text-blue-900">{new Date(viewingWorksiteRealDates.start).toLocaleDateString('fr-FR')}</p>
                  </div>
                )}
                {viewingWorksiteRealDates.end && (
                  <div className="bg-blue-50 rounded-xl p-4">
                    <p className="text-xs text-blue-500 mb-1">Fin (réelle)</p>
                    <p className="text-sm font-bold text-blue-900">{new Date(viewingWorksiteRealDates.end).toLocaleDateString('fr-FR')}</p>
                  </div>
                )}
              </div>

              {/* Équipe assignée */}
              {viewingWorksiteTeam.length > 0 && (
                <div>
                  <div className="flex items-center mb-3">
                    <Users className="h-5 w-5 mr-3 text-gray-400" />
                    <p className="text-xs text-gray-400 font-medium">Équipe assignée</p>
                  </div>
                  <div className="space-y-2">
                    {viewingWorksiteTeam.map(m => (
                      <div key={m.id} className="flex items-center bg-gray-50 rounded-xl px-4 py-3">
                        <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold mr-3">
                          {m.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">{m.name}</p>
                          {m.role && <p className="text-xs text-gray-400">{m.role}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {viewingWorksiteTeam.length === 0 && (
                <div className="flex items-center text-gray-400">
                  <Users className="h-5 w-5 mr-3" />
                  <p className="text-sm italic">Aucun collaborateur assigné</p>
                </div>
              )}

              {/* Notes */}
              {viewingWorksite.notes && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <p className="text-xs text-yellow-600 font-medium mb-1">Notes internes</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{viewingWorksite.notes}</p>
                </div>
              )}

              <WorksitePhotos worksiteId={viewingWorksite.id} />
              <MissionComments worksiteId={viewingWorksite.id} currentUserId={currentWorksiteViewerId} />
            </div>

            {/* Footer actions */}
            <div className="flex space-x-3 p-6 border-t border-gray-100">
              <Button
                onClick={async () => {
                  const ws = viewingWorksite;
                  setViewingWorksite(null);
                  await openWorksiteEditor(ws);
                }}
                className="flex-1 bg-black hover:bg-gray-800 text-white rounded-xl"
              >
                <Edit className="h-4 w-4 mr-2" />
                Modifier
              </Button>
              <Button
                onClick={() => setViewingWorksite(null)}
                variant="outline"
                className="flex-1 rounded-xl border-2 border-gray-200"
              >
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Quote Create Component with Full Workflow - Complete Implementation
const QuoteCreate = ({ defaultTab = 'quotes' }) => {
  const confirm = useConfirm();
  const location = useLocation();
  const navigate = useNavigate();
  const _cachedClients = getCached(`${API}/clients`);
  const _cachedQuotes = getCached(`${API}/quotes`);
  const _cachedWorksites = getCached(`${API}/worksites`);
  const _cachedSchedules = getCached(`${API}/schedules`);
  const _cachedCatalog = getCached(`${API}/catalog/products`);
  const _hasCache = !!(_cachedClients && _cachedQuotes);
  const [clients, setClients] = useState(_cachedClients || []);
  const [quotes, setQuotes] = useState(() => _cachedQuotes?.data || _cachedQuotes || []);
  const [worksites, setWorksites] = useState(_cachedWorksites || []);
  const [schedules, setSchedules] = useState(_cachedSchedules || []);
  const [loading, setLoading] = useState(false);
  const [dataReady, setDataReady] = useState(_hasCache);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [searchTerm, setSearchTerm] = useState('');
  const [quoteStatusFilter, setQuoteStatusFilter] = useState('');
  const [quoteAmountFilter, setQuoteAmountFilter] = useState('');
  const [quotePeriodFilter, setQuotePeriodFilter] = useState('');
  const [editingQuote, setEditingQuote] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false); // Mode édition pour le formulaire principal
  const [downloadingPdfId, setDownloadingPdfId] = useState(null);
  const [quoteLoadingIds, setQuoteLoadingIds] = useState({});
  const [expandedColumn, setExpandedColumn] = useState(null); // Pour le menu déroulant des colonnes
  const [selectedWorksiteQuote, setSelectedWorksiteQuote] = useState(null); // Pour afficher le devis d'un chantier
  const [worksiteStatusFilter, setWorksiteStatusFilter] = useState('ALL'); // Filtre statut chantiers: ALL, PLANNED, IN_PROGRESS, COMPLETED
  const [invoicedFilter, setInvoicedFilter] = useState('ALL'); // Filtre vue Facturés: ALL, WITHOUT, WITH
  // Historique des modifications d'un devis (DEV-010)
  const [openQuoteHistoryId, setOpenQuoteHistoryId] = useState(null);
  const [quoteHistoryLogs, setQuoteHistoryLogs] = useState({}); // { [quoteId]: [...] }
  const [loadingQuoteHistoryId, setLoadingQuoteHistoryId] = useState(null);
  
  // NEW: Templates management states
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    description: '',
    category: 'standard',
    items: [{ name: '', description: '', unit_price: 0, default_quantity: 1 }]
  });
  // States pour l'autocomplétion du catalogue dans les templates
  const [templateItemSuggestions, setTemplateItemSuggestions] = useState({});
  const [templateDescSuggestions, setTemplateDescSuggestions] = useState({});
  
  // Helper pour générer un ID unique par ligne de devis (clé React stable)
  const genItemId = () => `item_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '', // Nom du client (manuel ou sélectionné)
    client_first_name: '', // Prénom du nouveau client
    client_last_name: '', // Nom du nouveau client
    client_phone: '', // Téléphone du nouveau client
    client_email: '', // Email du nouveau client
    title: '',
    description: '',
    worksite_address: '',
    amount: '',
    status: 'DRAFT', // Statut par défaut
    tva_rate: 20, // Taux de TVA par défaut (20%)
    tva_autoliquidation: false,
    deposit_percent: '',
    planned_works_text: '',
    items: [{ _id: `item_init_${Date.now()}`, name: '', description: '', quantity: 1, price: 0, total: 0, tva_rate: 20, unit: '' }]
  });

  // Patch automatique : si un item n'a pas de _id, le corriger dans le state
  // (sécurité en cas de chemin de code oublié — évite key={index} → insertBefore crash)
  useEffect(() => {
    const hasMissing = formData.items?.some(item => !item._id);
    if (hasMissing) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.map(item => item._id ? item : { ...item, _id: genItemId() })
      }));
    }
  }, [formData.items]);

  // 📜 CGV : déterminer l'audience du client sélectionné puis charger la version active
  useEffect(() => {
    const selectedClient = clients.find(c => c.id === formData.client_id);
    const aud = selectedClient?.customer_type === 'consumer' ? 'consumer' : 'professional';
    setClientAudience(aud);
    let cancelled = false;
    http.get(`${API}/company-terms-active`, { params: { audience: aud } })
      .then(r => { if (!cancelled) setTermsInfo(r.data); })
      .catch(() => { if (!cancelled) setTermsInfo(null); });
    return () => { cancelled = true; };
  }, [formData.client_id, clients]);
  
  // États pour l'autocomplétion des clients
  const [clientSearch, setClientSearch] = useState('');
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [filteredClients, setFilteredClients] = useState([]);
  
  // États pour l'autocomplétion des articles du catalogue
  const [itemSearchStates, setItemSearchStates] = useState({});
  const [showItemSuggestions, setShowItemSuggestions] = useState({});
  const [catalogProducts, setCatalogProducts] = useState(() => _cachedCatalog?.data || _cachedCatalog || []);
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [catalogSearchTerm, setCatalogSearchTerm] = useState('');
  
  // Taux de TVA disponibles (conformité France)
  const tvaRates = [
    { value: 20, label: '20% - Taux normal', description: 'Matériel, prestations générales' },
    { value: 10, label: '10% - Taux intermédiaire', description: 'Travaux d\'amélioration, transformation, entretien' },
    { value: 5.5, label: '5,5% - Taux réduit', description: 'Rénovation énergétique, isolation' },
    { value: 0, label: '0% - Non applicable', description: 'Auto-liquidation, intracommunautaire' },
    { value: -1, label: 'Exonéré de TVA', description: 'Art. 293B du CGI - TVA non applicable' }
  ];
  
  // État pour les photos du devis
  const [quotePhotos, setQuotePhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // 📜 CGV — conditions générales de vente
  const [termsInfo, setTermsInfo] = useState(null); // { version, attach_terms_to_quotes, require_terms_acceptance }
  const [attachTermsOverride, setAttachTermsOverride] = useState(null); // null = suivre le réglage entreprise
  const [clientAudience, setClientAudience] = useState('professional');
  // Modal d'acceptation des CGV (avant conversion devis → chantier)
  const [termsAcceptModal, setTermsAcceptModal] = useState({ open: false, quoteId: null, snapshot: null });
  const [termsAcceptChecked, setTermsAcceptChecked] = useState(false);
  const [termsSignatoryName, setTermsSignatoryName] = useState('');
  const [termsAccepting, setTermsAccepting] = useState(false);
  
  const [clientData, setClientData] = useState({
    nom: '',
    email: '',
    telephone: '',
    adresse: '',
    domaine: 'BTP'
  });

  // États pour la gestion des chantiers
  const [showWorksiteForm, setShowWorksiteForm] = useState(false);
  const [editingWorksite, setEditingWorksite] = useState(null);
  const [viewingWorksite, setViewingWorksite] = useState(null);
  const [viewingWorksiteTeam, setViewingWorksiteTeam] = useState([]);
  const [worksiteFormData, setWorksiteFormData] = useState({
    title: '',
    description: '',
    address: '',
    client_id: '',
    status: 'PLANNED',
    progress: 0,
    start_date: '',
    end_date: '',
    budget: '',
    team_size: 1,
    notes: ''
  });
  const [savingWorksite, setSavingWorksite] = useState(false); // Garde-fou anti double-clic (EDGE-005)
  const [showPlanningModal, setShowPlanningModal] = useState(false);
  const [selectedWorksiteForPlanning, setSelectedWorksiteForPlanning] = useState(null);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [planningData, setPlanningData] = useState({
    team_leader_id: '',
    collaborator_ids: [],
    start_date: '',
    end_date: '',
    notes: ''
  });

  useEffect(() => {
    perf.start('QuoteCreate.mount');
    perf.start('QuoteCreate.structure');
    loadData(); // charge clients + quotes + worksites + schedules en une seule vague
    loadCatalogProducts();
    loadTemplates(); // Charger les templates de devis
    // Structure visible immédiatement (skeleton)
    requestAnimationFrame(() => perf.end('QuoteCreate.structure', 100));
  }, []);

  // Charger l'équipe assignée quand on ouvre le détail d'un chantier
  useEffect(() => {
    if (!viewingWorksite) { setViewingWorksiteTeam([]); return; }
    (async () => {
      try {
        const res = await http.get(`${API}/schedules?worksite_id=${viewingWorksite.id}`);
        // Filtre défensif: le backend filtre déjà par worksite_id mais on sécurise côté client
        const scheds = (res.data || []).filter(s => s.worksite_id === viewingWorksite.id);
        const techIds = [...new Set(scheds.filter(s => s.collaborator_id).map(s => s.collaborator_id))];
        const leaderIds = [...new Set(scheds.filter(s => s.team_leader_id).map(s => s.team_leader_id))];
        const team = [];
        techIds.forEach(id => {
          const c = collaborators.find(x => x.id === id);
          if (c) team.push({ id, name: `${c.first_name || ''} ${c.last_name || ''}`.trim(), role: 'Collaborateur' });
          else {
            const s = scheds.find(x => x.collaborator_id === id);
            if (s) team.push({ id, name: `${s.collaborator_first_name || ''} ${s.collaborator_last_name || ''}`.trim() || id, role: 'Collaborateur' });
          }
        });
        leaderIds.forEach(id => {
          if (techIds.includes(id)) return;
          const l = teamLeaders.find(x => x.id === id);
          if (l) team.push({ id, name: `${l.first_name || l.name || ''} ${l.last_name || ''}`.trim(), role: 'Chef d\'équipe' });
        });
        setViewingWorksiteTeam(team);
      } catch { setViewingWorksiteTeam([]); }
    })();
  }, [viewingWorksite]);

  // ⚡ Realtime — rafraîchir uniquement les données concernées (debounced)
  const realtimeTimerRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      const t = e.detail?.table;
      // Debounce: éviter les rafales de reload (300ms)
      clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = setTimeout(() => {
        if (t === 'clients') {
          http.get(`${API}/clients`).then(r => setClients(r.data)).catch(() => {});
        }
        if (t === 'worksites') loadWorksites();
        if (t === 'schedules') loadSchedules();
        if (t === 'catalog_products') loadCatalogProducts();
      }, 300);
    };
    window.addEventListener('realtime-update', handler);
    return () => {
      window.removeEventListener('realtime-update', handler);
      clearTimeout(realtimeTimerRef.current);
    };
  }, []);

  // ⏱ Ref pour le debounce du realtime
  const realtimeDebounceRef = useRef(null);

  // ⚡ Supabase Realtime — synchronisation instantanée des devis entre tous les utilisateurs
  useEffect(() => {
    let channel = null;
    let cancelled = false;

    const setupRealtime = async () => {
      // Attendre que la session Supabase soit prête (le setSession IIFE peut être en cours)
      let retries = 0;
      while (retries < 10 && !cancelled) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) break;
        retries++;
        await new Promise(r => setTimeout(r, 500));
      }

      if (cancelled) return;

      const storedUser = localStorage.getItem('user');
      const companyId = storedUser ? JSON.parse(storedUser)?.company_id : null;
      if (!companyId || !localStorage.getItem('token')) {
        console.warn('[Realtime] Pas de company_id ou token, abandon');
        return;
      }

      const handleRealtime = async (payload) => {
        console.log('[Realtime] quotes change:', payload.eventType, payload.new?.id || payload.old?.id);
        if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
        realtimeDebounceRef.current = setTimeout(async () => {
          if (payload.eventType === 'INSERT' && payload.new) {
            // Fetch complet via API pour avoir client_name et données jointes
            try {
              const resp = await http.get(`${API}/quotes/${payload.new.id}`);
              if (resp.data) {
                setQuotes(prev => {
                  if (prev.some(q => q.id === resp.data.id)) return prev;
                  return [resp.data, ...prev];
                });
              }
            } catch (err) {
              // Fallback : utiliser le payload brut
              setQuotes(prev => {
                if (prev.some(q => q.id === payload.new.id)) return prev;
                return [payload.new, ...prev];
              });
            }
          } else if (payload.eventType === 'UPDATE' && payload.new) {
            setQuotes(prev => prev.map(q =>
              q.id === payload.new.id ? { ...q, ...payload.new } : q
            ));
          } else if (payload.eventType === 'DELETE' && payload.old) {
            setQuotes(prev => prev.filter(q => q.id !== payload.old.id));
          }
        }, 300);
      };

      channel = supabase
        .channel(`quotes-realtime-${companyId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'quotes',
            filter: `company_id=eq.${companyId}`,
          },
          handleRealtime
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            console.log('[Realtime] ✅ Abonné aux changements de devis');
          } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
            console.warn('[Realtime] ⚠️ Connexion perdue:', status);
          }
        });
    };

    setupRealtime();

    return () => {
      cancelled = true;
      if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
      if (channel) supabase.removeChannel(channel);
      console.log('[Realtime] Désabonné des changements de devis');
    };
  }, []);

  // 🔵 Ref pour éviter que le pré-remplissage projet ne se re-trigger
  const projectPrefilled = useRef(false);

  // 🔵 Gérer les données de projet venant de Mon Entreprise
  useEffect(() => {
    if (projectPrefilled.current) return; // Déjà traité, ne pas re-trigger
    if (location.state?.fromProject && location.state?.project && clients.length > 0) {
      projectPrefilled.current = true; // Marquer comme traité
      const { project, client_id } = location.state;
      const client = clients.find(c => c.id === (client_id || project.client_id));
      
      console.log('✅ Pré-remplissage du devis depuis projet:', project);
      console.log('✅ Client trouvé:', client);
      
      if (client) {
        // Mettre à jour le champ de recherche client pour l'affichage
        setClientSearch(`${client.nom} ${client.prenom || ''}`.trim());
      }
      
      setFormData({
        client_id: client_id || project.client_id,
        client_name: client ? `${client.nom} ${client.prenom || ''}`.trim() : '',
        title: `Devis - ${project.name}`,
        description: project.notes || '',
        amount: project.estimated_value || '',
        status: 'DRAFT',
        tva_rate: 20,
        items: [{ _id: genItemId(), name: '', quantity: 1, price: 0, total: 0, tva_rate: 20, unit: '' }]
      });
      
      setShowForm(true);
      setActiveTab('quotes');
      
      // Nettoyer le state pour éviter re-trigger
      window.history.replaceState({}, document.title);
    }
  }, [location.state, clients]);

  const loadSchedules = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await http.get(`${API}/schedules`);
      setSchedules(response.data || []);
    } catch (error) {
      console.error('Erreur chargement schedules:', error);
      setSchedules([]);
    }
  };

  // Fonction helper pour vérifier si un chantier a des schedules
  const hasSchedule = (worksiteId) => {
    return schedules.some(schedule => schedule.worksite_id === worksiteId);
  };

  const loadData = async () => {
    if (!localStorage.getItem('token')) return;
    perf.start('QuoteCreate.data');
    try {
      const [clientsRes, quotesRes, worksitesRes, schedulesRes] = await Promise.allSettled([
        http.get(`${API}/clients`),
        http.get(`${API}/quotes`),
        http.get(`${API}/worksites`),
        http.get(`${API}/schedules`),
      ]);
      
      if (clientsRes.status === 'fulfilled') setClients(clientsRes.value.data);
      else console.error('Erreur chargement clients:', clientsRes.reason);
      
      if (quotesRes.status === 'fulfilled') setQuotes(quotesRes.value.data.data || []);
      else console.error('Erreur chargement devis:', quotesRes.reason);
      
      if (worksitesRes.status === 'fulfilled') setWorksites(worksitesRes.value.data || []);
      else console.error('Erreur chargement chantiers:', worksitesRes.reason);
      
      if (schedulesRes.status === 'fulfilled') setSchedules(schedulesRes.value.data || []);
      else console.error('Erreur chargement plannings:', schedulesRes.reason);
      
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      perf.end('QuoteCreate.data');
      perf.end('QuoteCreate.mount');
      setDataReady(true);
    }
  };

  const loadWorksites = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await http.get(`${API}/worksites`);
      setWorksites(response.data);
    } catch (error) {
      console.error('Erreur chargement chantiers:', error);
      setWorksites([]);
    }
  };

  const loadTeamData = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const [leadersRes, collabRes] = await Promise.allSettled([
        http.get(`${API}/team-leaders`),
        http.get(`${API}/collaborators`)
      ]);
      if (leadersRes.status === 'fulfilled') setTeamLeaders(leadersRes.value.data || []);
      if (collabRes.status === 'fulfilled') setCollaborators(collabRes.value.data || []);
    } catch (error) {
      console.error('Erreur chargement équipes:', error);
    }
  };

  const openPlanningModal = (worksite) => {
    // Rediriger vers le planning avec les données du chantier
    window.location.href = `/bureau/planning?worksite=${worksite.id}&action=plan`;
  };

  const loadCatalogProducts = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await http.get(`${API}/catalog/products`);
      setCatalogProducts(response.data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des produits du catalogue:', error);
      setCatalogProducts([]);
    }
  };

  // ── Historique des modifications d'un devis (DEV-010) ──
  const QUOTE_HISTORY_ACTION_LABELS = {
    QUOTE_CREATE: '🆕 Création',
    QUOTE_UPDATE: '✏️ Modification',
    QUOTE_DELETE: '🗑️ Envoi à la corbeille',
    QUOTE_PERMANENT_DELETE: '❌ Suppression définitive',
    QUOTE_ARCHIVE: '📦 Archivage',
    QUOTE_RESTORE: '♻️ Restauration',
  };
  const QUOTE_HISTORY_FIELD_LABELS = {
    title: 'Titre',
    description: 'Description',
    amount: 'Montant',
    status: 'Statut',
    client_id: 'Client',
  };
  const QUOTE_HISTORY_STATUS_LABELS = {
    DRAFT: 'Brouillon',
    SENT: 'Envoyé',
    REJECTED: 'Refusé',
    ACCEPTED: 'Accepté',
    EXPIRED: 'Expiré',
    CONVERTED_TO_WORKSITE: 'Converti en chantier',
    INVOICED: 'Facturé',
    ARCHIVED: 'Archivé',
    TRASHED: 'Corbeille',
  };
  const QUOTE_HISTORY_IGNORED_FIELDS = new Set(['id', 'company_id', 'created_at', 'updated_at', 'quote_number', 'items']);

  const formatQuoteHistoryValue = (field, value) => {
    if (value === undefined || value === null || value === '') return '—';
    if (field === 'amount') return `${parseFloat(value).toFixed(2)}€`;
    if (field === 'status') return QUOTE_HISTORY_STATUS_LABELS[value] || value;
    return String(value);
  };

  const getQuoteHistoryChanges = (log) => {
    const before = log.before || {};
    const after = log.after || {};
    const changes = [];
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    keys.forEach((k) => {
      if (QUOTE_HISTORY_IGNORED_FIELDS.has(k)) return;
      const bv = before[k];
      const av = after[k];
      if (JSON.stringify(bv) !== JSON.stringify(av)) {
        changes.push({
          type: 'field',
          field: QUOTE_HISTORY_FIELD_LABELS[k] || k,
          before: formatQuoteHistoryValue(k, bv),
          after: formatQuoteHistoryValue(k, av),
        });
      }
    });
    // Diff ligne par ligne des articles (qté/prix) — ligne par position
    const beforeItems = before.items || [];
    const afterItems = after.items || [];
    const maxLen = Math.max(beforeItems.length, afterItems.length);
    for (let i = 0; i < maxLen; i++) {
      const bi = beforeItems[i] || null;
      const ai = afterItems[i] || null;
      const bq = bi?.quantity, bp = bi?.price, aq = ai?.quantity, ap = ai?.price;
      if ((bq ?? '') === (aq ?? '') && (bp ?? '') === (ap ?? '')) continue;
      const label = `Article ${i + 1} — ${ai?.name || bi?.name || 'sans nom'}`;
      if (!bi && ai) {
        changes.push({ type: 'added', field: label, after: `Qté ${aq} × ${(ap ?? 0).toFixed ? ap.toFixed(2) : ap}€` });
      } else if (bi && !ai) {
        changes.push({ type: 'removed', field: label, before: `Qté ${bq} × ${(bp ?? 0).toFixed ? bp.toFixed(2) : bp}€` });
      } else {
        changes.push({
          type: 'modified',
          field: label,
          before: `Qté ${bq ?? '-'} × ${bp ?? '-'}€`,
          after: `Qté ${aq ?? '-'} × ${ap ?? '-'}€`,
        });
      }
    }
    return changes;
  };

  const toggleQuoteHistory = async (quoteId) => {
    if (openQuoteHistoryId === quoteId) {
      setOpenQuoteHistoryId(null);
      return;
    }
    setOpenQuoteHistoryId(quoteId);
    setLoadingQuoteHistoryId(quoteId);
    try {
      const response = await http.get(`${API}/audit-logs`, {
        params: { resource_type: 'quote', resource_id: quoteId, limit: 100 },
      });
      setQuoteHistoryLogs(prev => ({ ...prev, [quoteId]: response.data || [] }));
    } catch (error) {
      console.error('Erreur chargement historique devis:', error);
      toast.error("Erreur lors du chargement de l'historique");
      setQuoteHistoryLogs(prev => ({ ...prev, [quoteId]: [] }));
    } finally {
      setLoadingQuoteHistoryId(null);
    }
  };

  const addQuickClient = async () => {
    try {
      const response = await http.post(`${API}/clients`, clientData);
      
      setClients(prev => [...prev, response.data]);
      setFormData(prev => ({...prev, client_id: response.data.id}));
      setClientData({ nom: '', email: '', telephone: '', adresse: '', domaine: 'BTP' });
      setShowClientForm(false);
      alert('Client ajouté avec succès !');
    } catch (error) {
      console.error('Erreur ajout client:', error);
      alert('Erreur lors de l\'ajout du client');
    }
  };

  // Gestion de l'autocomplétion des clients
  const handleClientSearchChange = (e) => {
    const value = e.target.value;
    setClientSearch(value);
    setFormData(prev => ({...prev, client_name: value, client_id: ''}));
    
    if (value.trim().length > 0) {
      const filtered = clients.filter(client => 
        client.nom.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredClients(filtered);
      setShowClientSuggestions(true);
    } else {
      setFilteredClients([]);
      setShowClientSuggestions(false);
    }
  };

  const selectClient = (client) => {
    setClientSearch(client.nom);
    setFormData(prev => ({...prev, client_id: client.id, client_name: client.nom}));
    setShowClientSuggestions(false);
  };

  // Gestion autocomplétion articles
  const handleItemNameChange = (index, value) => {
    // Mettre à jour le nom de l'article
    updateItem(index, 'name', value);
    
    // Mettre à jour l'état de recherche pour cet index
    setItemSearchStates(prev => ({...prev, [index]: value}));
    
    // Filtrer les produits du catalogue
    if (value.trim().length > 0) {
      setShowItemSuggestions(prev => ({...prev, [index]: true}));
    } else {
      setShowItemSuggestions(prev => ({...prev, [index]: false}));
    }
  };

  const selectCatalogProduct = (index, product) => {
    // Remplir l'article avec les données du produit (functional update pour éviter stale closure)
    setFormData(prev => {
      const newItems = prev.items.map((item, i) => {
        if (i === index) {
          const qty = item.quantity || 1;
          return {
            ...item,
            name: product.name,
            description: product.description || '',
            price: product.price,
            quantity: qty,
            total: product.price * qty,
            tva_rate: product.tva_rate !== undefined ? product.tva_rate : 20,
            unit: product.unit || ''
          };
        }
        return item;
      });
      return { ...prev, items: newItems };
    });
    setItemSearchStates(prev => ({...prev, [index]: product.name}));
    setShowItemSuggestions(prev => ({...prev, [index]: false}));
  };

  const getFilteredProducts = (index) => {
    const searchTerm = itemSearchStates[index] || '';
    if (!searchTerm.trim()) return [];
    
    return catalogProducts.filter(product =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingPhoto(true);
    try {
      const newPhotos = [];
      for (const file of files) {
        // Créer une preview en base64
        const reader = new FileReader();
        const base64Promise = new Promise((resolve) => {
          reader.onloadend = () => {
            resolve(reader.result);
          };
          reader.readAsDataURL(file);
        });
        
        const base64 = await base64Promise;
        newPhotos.push({
          id: Date.now() + Math.random(),
          file,
          preview: base64,
          name: file.name
        });
      }
      
      setQuotePhotos(prev => [...prev, ...newPhotos]);
      alert(`${files.length} photo(s) ajoutée(s) au devis`);
    } catch (error) {
      console.error('Erreur upload photo:', error);
      alert('Erreur lors de l\'ajout des photos');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = (photoId) => {
    setQuotePhotos(prev => prev.filter(p => p.id !== photoId));
  };

  const handleItemPhotoUpload = async (itemIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('La photo ne doit pas dépasser 5 MB');
      return;
    }

    // Compresser côté client (max 1200px, JPEG 80%) — les photos partent en base64
    // dans quotes.items : sans compression elles saturent la RAM du backend
    const compressImage = (f) => new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(f);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const MAX = 1200;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          const ratio = Math.min(MAX / width, MAX / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image invalide')); };
      img.src = url;
    });

    let dataUrl;
    try {
      dataUrl = await compressImage(file);
    } catch {
      // Fallback : lecture brute si la compression échoue
      dataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
    }

    const photoData = {
      name: file.name,
      type: 'image/jpeg',
      data: dataUrl,
      preview: dataUrl
    };

    // Mettre à jour l'item avec la photo
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[itemIndex] = {
        ...newItems[itemIndex],
        photo: photoData
      };
      return { ...prev, items: newItems };
    });
  };

  const removeItemPhoto = (itemIndex) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[itemIndex] = {
        ...newItems[itemIndex],
        photo: null
      };
      return { ...prev, items: newItems };
    });
  };

  // Vérifie qu'un devis contient au moins une ligne exploitable (nom renseigné)
  const hasValidQuoteItems = (quote) => {
    return (quote.items || []).some(item => item.name && item.name.trim() !== '');
  };

  const sendQuote = async (quoteId) => {
    if (quoteLoadingIds[quoteId]) return;
    // Garde-fou : refuser l'envoi si aucune ligne valide
    const quote = quotes.find(q => q.id === quoteId);
    if (!quote || !hasValidQuoteItems(quote)) {
      toast.error('Ajoutez au moins une ligne avant d\'envoyer le devis.');
      return;
    }
    setQuoteLoadingIds(prev => ({ ...prev, [quoteId]: 'sending' }));
    // Optimistic UI
    const prevQuotes = quotes;
    setQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, status: 'SENT' } : q));
    try {
      console.time(`sendQuote-${quoteId}`);
      await http.put(`${API}/quotes/${quoteId}`, { status: 'SENT' });
      console.timeEnd(`sendQuote-${quoteId}`);
      toast.success('✅ Devis envoyé au client !', { id: `send-${quoteId}` });
    } catch (error) {
      setQuotes(prevQuotes); // Rollback
      console.error('Erreur envoi devis:', error);
      if (error.response?.status === 401) {
        toast.error('⚠️ Session expirée.');
      } else {
        toast.error('Erreur lors de l\'envoi du devis');
      }
    } finally {
      setQuoteLoadingIds(prev => ({ ...prev, [quoteId]: false }));
    }
  };

  const downloadQuotePDF = async (quoteId) => {
    try {
      setDownloadingPdfId(quoteId);
      const quote = quotes.find(q => q.id === quoteId);
      if (!quote) {
        alert('❌ Devis non trouvé');
        return;
      }

      if (!localStorage.getItem('token')) {
        toast.error('⚠️ Session expirée. Veuillez vous reconnecter.');
        return;
      }

      // L'intercepteur injecte le token ET gère le refresh 401 automatiquement
      const response = await http.get(`${API}/quotes/${quoteId}/pdf`, {
        responseType: 'blob'
      });

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Devis_${quote.quote_number || quoteId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF téléchargé avec succès !');
    } catch (error) {
      console.error('Erreur téléchargement PDF:', error);
      if (error.response?.status === 401) {
        toast.error('⚠️ Session expirée. Reconnexion nécessaire.');
      } else {
        alert('❌ Erreur lors du téléchargement du PDF');
      }
    } finally {
      setDownloadingPdfId(null);
    }
  };

  const acceptQuote = async (quoteId) => {
    if (quoteLoadingIds[quoteId]) return;

    // 📜 CGV : si l'acceptation est obligatoire et qu'un snapshot non accepté
    // est attaché au devis, exiger l'acceptation explicite avant conversion
    try {
      const [snapRes, activeRes] = await Promise.all([
        http.get(`${API}/quotes/${quoteId}/terms-snapshot`),
        http.get(`${API}/company-terms-active`)
      ]);
      const snapshot = snapRes.data?.snapshot;
      const requireAcceptance = activeRes.data?.require_terms_acceptance === true;
      if (snapshot && !snapshot.accepted_at && requireAcceptance) {
        setTermsAcceptChecked(false);
        setTermsSignatoryName('');
        setTermsAcceptModal({ open: true, quoteId, snapshot });
        return; // la conversion reprendra après acceptation via la modal
      }
    } catch (e) {
      // Endpoint indisponible (migration non appliquée ?) — ne pas bloquer le flux existant
      console.warn('[CGV] Vérification acceptation ignorée:', e?.response?.status);
    }

    await proceedAcceptQuote(quoteId);
  };

  const proceedAcceptQuote = async (quoteId) => {
    setQuoteLoadingIds(prev => ({ ...prev, [quoteId]: 'accepting' }));
    // Toast de chargement (mis à jour ensuite en succès/erreur via le même id)
    toast.loading('🏗️ Création d\'un chantier...', { id: `accept-${quoteId}` });
    // Optimistic UI
    const prevQuotes = quotes;
    setQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, status: 'CONVERTED_TO_WORKSITE' } : q));
    try {
      if (!localStorage.getItem('token')) {
        toast.error('⚠️ Session expirée. Veuillez vous reconnecter.', { id: `accept-${quoteId}` });
        setQuotes(prevQuotes);
        return;
      }
      
      const quote = prevQuotes.find(q => q.id === quoteId);
      if (!quote) {
        toast.error('Devis non trouvé', { id: `accept-${quoteId}` });
        setQuotes(prevQuotes);
        return;
      }

      let projectId = null;
      try {
        const projectsRes = await http.get(`${API}/projects`);
        const projectsList = Array.isArray(projectsRes.data)
          ? projectsRes.data
          : Array.isArray(projectsRes.data?.data)
            ? projectsRes.data.data
            : [];
        
        const matchingProject = projectsList.find(p => 
          p.client_id === quote.client_id || 
          (p.name && quote.title && p.name.includes(quote.title.substring(0, 20)))
        );
        
        if (matchingProject) {
          projectId = matchingProject.id;
        }
      } catch (err) {
        console.error('Erreur recherche projet:', err);
      }

      const worksiteTitle = quote.title && quote.title.trim() !== '' 
        ? quote.title 
        : `Chantier ${quote.client_name || 'Client'} - Devis #${quote.quote_number || ''}`;
      
      const worksiteData = {
        title: worksiteTitle,
        description: quote.description || '',
        address: quote.worksite_address || '',
        client_id: quote.client_id,
        client_name: quote.client_name || '',
        status: 'PLANNED',
        quote_id: quoteId,
        amount: quote.amount || 0,
        source: 'QUOTE'
      };
      
      if (projectId) {
        worksiteData.project_id = projectId;
      }
      
      console.time(`acceptQuote-${quoteId}`);
      await Promise.all([
        http.post(`${API}/worksites`, worksiteData),
        http.put(`${API}/quotes/${quoteId}`, { status: 'CONVERTED_TO_WORKSITE' })
      ]);
      console.timeEnd(`acceptQuote-${quoteId}`);

      setExpandedColumn(null);
      toast.success('✅ Chantier créé avec succès !', { id: `accept-${quoteId}` });
      // Rafraîchir worksites en arrière-plan (sans bloquer l'UI)
      loadWorksites?.().catch(() => {});
    } catch (error) {
      setQuotes(prevQuotes); // Rollback
      console.error('Erreur création chantier:', error);
      if (error.response?.status === 401) {
        toast.error('⚠️ Session expirée.', { id: `accept-${quoteId}` });
      } else {
        toast.error(`Erreur lors de la création du chantier: ${error.response?.data?.detail || error.message}`, { id: `accept-${quoteId}` });
      }
    } finally {
      setQuoteLoadingIds(prev => ({ ...prev, [quoteId]: false }));
    }
  };

  const rejectQuote = async (quoteId) => {
    if (quoteLoadingIds[quoteId]) return;
    setQuoteLoadingIds(prev => ({ ...prev, [quoteId]: 'rejecting' }));
    // Optimistic UI
    const prevQuotes = quotes;
    setQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, status: 'REJECTED' } : q));
    try {
      console.time(`rejectQuote-${quoteId}`);
      await http.put(`${API}/quotes/${quoteId}`, { status: 'REJECTED' });
      console.timeEnd(`rejectQuote-${quoteId}`);
      toast.success('Devis refusé', { id: `reject-${quoteId}` });
    } catch (error) {
      setQuotes(prevQuotes); // Rollback
      console.error('Erreur refus devis:', error);
      toast.error('Erreur lors du refus du devis');
    } finally {
      setQuoteLoadingIds(prev => ({ ...prev, [quoteId]: false }));
    }
  };

  const deleteQuote = async (quoteId) => {
    if (quoteLoadingIds[quoteId]) return;
    const quote = quotes.find(q => q.id === quoteId);
    const isActive = !['ARCHIVED', 'REJECTED', 'TRASHED'].includes(quote?.status);
    const confirmMsg = isActive ? 'Archiver ce devis ?' : 'Déplacer ce devis dans la corbeille ?';
    if (!(await confirm(confirmMsg))) {
      return;
    }
    setQuoteLoadingIds(prev => ({ ...prev, [quoteId]: 'deleting' }));
    // Optimistic UI : actif→ARCHIVED, archivé/refusé→TRASHED
    const prevQuotes = quotes;
    const optimisticStatus = isActive ? 'ARCHIVED' : 'TRASHED';
    setQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, status: optimisticStatus } : q));
    try {
      console.time(`deleteQuote-${quoteId}`);
      const response = await http.delete(`${API}/quotes/${quoteId}`);
      console.timeEnd(`deleteQuote-${quoteId}`);
      // Sync avec le vrai statut retourné par le backend
      if (response.data?.status) {
        setQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, status: response.data.status } : q));
      }
      toast.success(isActive ? 'Devis archivé' : 'Devis déplacé dans la corbeille', { id: `delete-${quoteId}` });
    } catch (error) {
      setQuotes(prevQuotes); // Rollback
      console.error('Erreur suppression devis:', error);
      toast.error('Erreur lors de la suppression du devis');
    } finally {
      setQuoteLoadingIds(prev => ({ ...prev, [quoteId]: false }));
    }
  };

  const permanentDeleteQuote = async (quoteId) => {
    if (quoteLoadingIds[quoteId]) return;
    if (!(await confirm('Supprimer définitivement ce devis ? Cette action est irréversible.'))) {
      return;
    }
    setQuoteLoadingIds(prev => ({ ...prev, [quoteId]: 'deleting' }));
    // Optimistic UI
    const prevQuotes = quotes;
    setQuotes(prev => prev.filter(q => q.id !== quoteId));
    try {
      console.time(`permanentDelete-${quoteId}`);
      await http.delete(`${API}/quotes/${quoteId}/permanent`);
      console.timeEnd(`permanentDelete-${quoteId}`);
      toast.success('Devis supprimé définitivement', { id: `perm-delete-${quoteId}` });
    } catch (error) {
      setQuotes(prevQuotes); // Rollback
      console.error('Erreur suppression définitive:', error);
      toast.error('Erreur lors de la suppression définitive');
    } finally {
      setQuoteLoadingIds(prev => ({ ...prev, [quoteId]: false }));
    }
  };

  const editQuote = (quote) => {
    // 🔒 Bloquer l'édition des devis archivés
    if (quote.status === 'ARCHIVED') {
      toast.error('🔒 Ce devis est archivé et ne peut pas être modifié. Restaurez-le d\'abord en brouillon.', { id: `archived-${quote.id}` });
      return;
    }
    console.log('📝 Édition du devis:', quote);
    console.log('📊 Items avec photos:', quote.items?.filter(i => i.photo).length || 0);
    
    setEditingQuote(quote);
    setIsEditMode(true);
    
    // Copier les items en préservant les photos + ajouter _id si absent
    const itemsWithPhotos = (quote.items || [{ name: '', description: '', quantity: 1, price: 0, total: 0, tva_rate: 20, unit: '' }]).map(item => ({
      ...item,
      _id: item._id || genItemId(),
      tva_rate: item.tva_rate !== undefined ? item.tva_rate : 20,
      unit: item.unit || '',
      total: (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0),
      // S'assurer que la photo est préservée
      photo: item.photo || null
    }));
    
    setFormData({
      client_id: quote.client_id,
      title: quote.title,
      description: quote.description,
      worksite_address: quote.worksite_address || '',
      amount: quote.amount,
      status: quote.status || 'DRAFT',
      tva_rate: 20,
      tva_autoliquidation: quote.tva_autoliquidation || false,
      deposit_percent: quote.deposit_percent ?? '',
      planned_works_text: quote.planned_works_text || '',
      items: itemsWithPhotos
    });

    // Pré-remplir la recherche client
    if (quote.client_id) {
      const client = clients.find(c => c.id === quote.client_id);
      if (client) { setClientSearch(`${client.nom} ${client.prenom || ''}`.trim()); }
      else if (quote.client_name) { setClientSearch(quote.client_name); }
    } else if (quote.client_name) {
      setClientSearch(quote.client_name);
    }
    
    console.log('✅ FormData chargé avec', itemsWithPhotos.length, 'items');
    
    setShowForm(true); // Ouvre le formulaire principal au lieu du modal
    // Scroll vers le formulaire
    setTimeout(() => {
      const el = document.getElementById('quote-edit-form');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const restoreFromTrash = async (quoteId) => {
    if (quoteLoadingIds[quoteId]) return;
    // Protection : bloquer si le devis n'est pas dans un statut restaurable
    const quote = quotes.find(q => q.id === quoteId);
    if (quote && !['TRASHED', 'ARCHIVED', 'REJECTED'].includes(quote.status)) {
      toast.error('Impossible de restaurer ce devis (statut actuel non restaurable)');
      return;
    }
    setQuoteLoadingIds(prev => ({ ...prev, [quoteId]: 'restoring' }));
    // Optimistic UI: TRASHED → DRAFT (statut actif)
    const prevQuotes = quotes;
    setQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, status: 'DRAFT' } : q));
    try {
      console.time(`restoreFromTrash-${quoteId}`);
      const response = await http.post(`${API}/quotes/${quoteId}/restore`);
      console.timeEnd(`restoreFromTrash-${quoteId}`);
      // Synchroniser avec le statut réel renvoyé par le backend
      if (response.data?.status) {
        setQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, status: response.data.status } : q));
      }
      toast.success('Devis restauré avec succès', { id: `restore-${quoteId}` });
    } catch (error) {
      setQuotes(prevQuotes); // Rollback
      console.error('Erreur restauration devis:', error);
      toast.error('Erreur lors de la restauration du devis');
    } finally {
      setQuoteLoadingIds(prev => ({ ...prev, [quoteId]: false }));
    }
  };

  // Charger aussi les chantiers depuis le backend
  // Créer ou modifier un chantier
  const saveWorksite = async (e) => {
    e.preventDefault();
    if (savingWorksite) return; // Anti double-clic (EDGE-005)
    setSavingWorksite(true);
    try {
      if (editingWorksite) {
        // Modification
        const payload = { ...worksiteFormData };
        if (payload.status === 'COMPLETED') payload.progress = 100;
        await http.put(`${API}/worksites/${editingWorksite.id}`, payload);
        alert('Chantier modifié avec succès !');
      } else {
        // Création
        await http.post(`${API}/worksites`, worksiteFormData);
        alert('Chantier créé avec succès !');
      }

      // Réinitialiser le formulaire
      setWorksiteFormData({
        title: '',
        description: '',
        address: '',
        client_id: '',
        status: 'PLANNED',
        progress: 0,
        start_date: '',
        end_date: '',
        budget: '',
        team_size: 1,
        notes: ''
      });
      setShowWorksiteForm(false);
      setEditingWorksite(null);
      loadWorksites();
    } catch (error) {
      console.error('Erreur sauvegarde chantier:', error);
      alert('Erreur lors de la sauvegarde du chantier');
    } finally {
      setSavingWorksite(false);
    }
  };

  // Supprimer un chantier
  const deleteWorksite = async (worksiteId) => {
    if (!(await confirm('Êtes-vous sûr de vouloir supprimer ce chantier ? Cette action est irréversible.'))) {
      return;
    }

    try {
      await http.delete(`${API}/worksites/${worksiteId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      alert('Chantier supprimé avec succès');
      loadWorksites();
    } catch (error) {
      // 404 = le chantier n'existe déjà plus (déjà supprimé, ou liste obsolète
      // suite à une déconnexion Realtime). On considère l'opération réussie et
      // on rafraîchit la liste pour retirer l'entrée fantôme de l'écran.
      if (error?.response?.status === 404) {
        console.warn('Chantier déjà supprimé (404) — rafraîchissement de la liste');
        loadWorksites();
        return;
      }
      console.error('Erreur suppression chantier:', error);
      alert('Erreur lors de la suppression du chantier');
    }
  };

  const getQuoteStatusColor = (status) => {
    switch (status) {
      case 'DRAFT': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'SENT': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'REJECTED': return 'bg-red-100 text-red-700 border-red-200';
      case 'TRASHED': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 0;
      const price = parseFloat(item.price) || 0;
      return sum + (qty * price);
    }, 0);
  };

  // Calculer le TTC d'un devis à partir de ses items
  const calculateQuoteTTC = (quote) => {
    if (!quote.items || quote.items.length === 0) {
      // Si pas d'items, on suppose que amount est HT avec TVA 20%
      return (quote.amount * 1.20).toFixed(2);
    }
    
    let totalHT = 0;
    let totalTVA = 0;
    
    quote.items.forEach(item => {
      const itemTotal = (item.quantity || 0) * (item.price || 0);
      totalHT += itemTotal;
      
      const itemTvaRate = item.tva_rate !== undefined ? item.tva_rate : 20;
      if (itemTvaRate >= 0) {
        totalTVA += itemTotal * (itemTvaRate / 100);
      }
    });
    
    return (totalHT + totalTVA).toFixed(2);
  };

  // Filtre centralisé pour la barre de recherche + filtres statut/montant/période
  const matchesQuoteFilters = (quote) => {
    // Filtre texte (recherche)
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      const matchText = (quote.title || '').toLowerCase().includes(s) ||
        (quote.description || '').toLowerCase().includes(s) ||
        (quote.client_name || '').toLowerCase().includes(s) ||
        (quote.quote_number || '').toLowerCase().includes(s) ||
        (quote.amount || '').toString().includes(s);
      if (!matchText) return false;
    }
    // Filtre statut
    if (quoteStatusFilter) {
      if (quote.status !== quoteStatusFilter) return false;
    }
    // Filtre montant (basé sur TTC)
    if (quoteAmountFilter) {
      const ttc = parseFloat(calculateQuoteTTC(quote)) || 0;
      switch (quoteAmountFilter) {
        case '0-1000': if (ttc > 1000) return false; break;
        case '1000-5000': if (ttc < 1000 || ttc > 5000) return false; break;
        case '5000-10000': if (ttc < 5000 || ttc > 10000) return false; break;
        case '10000+': if (ttc < 10000) return false; break;
        default: break;
      }
    }
    // Filtre période
    if (quotePeriodFilter) {
      const created = new Date(quote.created_at);
      const now = new Date();
      switch (quotePeriodFilter) {
        case 'today': {
          const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          if (created < startOfDay) return false;
          break;
        }
        case 'week': {
          const dayOfWeek = now.getDay() || 7; // Lundi = 1
          const startOfWeek = new Date(now);
          startOfWeek.setDate(now.getDate() - dayOfWeek + 1);
          startOfWeek.setHours(0, 0, 0, 0);
          if (created < startOfWeek) return false;
          break;
        }
        case 'month': {
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          if (created < startOfMonth) return false;
          break;
        }
        case 'quarter': {
          const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
          const startOfQuarter = new Date(now.getFullYear(), quarterMonth, 1);
          if (created < startOfQuarter) return false;
          break;
        }
        default: break;
      }
    }
    return true;
  };

  // ── Listes de devis pré-calculées (évite ~40 .filter() inline par render) ──
  const draftQuotes = useMemo(() =>
    quotes.filter(q => q.status === 'DRAFT' && matchesQuoteFilters(q)),
    [quotes, searchTerm, quoteStatusFilter, quoteAmountFilter, quotePeriodFilter]
  );
  const sentQuotes = useMemo(() =>
    quotes.filter(q => (q.status === 'SENT' || q.status === 'PENDING') && matchesQuoteFilters(q)),
    [quotes, searchTerm, quoteStatusFilter, quoteAmountFilter, quotePeriodFilter]
  );
  const trashedQuotes = useMemo(() =>
    quotes.filter(q => q.status === 'TRASHED'),
    [quotes]
  );
  const rejectedQuotes = useMemo(() =>
    quotes.filter(q => q.status === 'REJECTED' && matchesQuoteFilters(q)),
    [quotes, searchTerm, quoteStatusFilter, quoteAmountFilter, quotePeriodFilter]
  );
  const archivedQuotes = useMemo(() =>
    quotes.filter(q => q.status === 'ARCHIVED'),
    [quotes]
  );
  const draftCount = useMemo(() => quotes.filter(q => q.status === 'DRAFT').length, [quotes]);
  const sentCount = useMemo(() => quotes.filter(q => q.status === 'SENT').length, [quotes]);
  const trashedCount = useMemo(() => trashedQuotes.length, [trashedQuotes]);
  const rejectedCount = useMemo(() => quotes.filter(q => q.status === 'REJECTED').length, [quotes]);
  const archivedCount = useMemo(() => archivedQuotes.length, [archivedQuotes]);
  const convertedCount = useMemo(() => quotes.filter(q => q.status === 'CONVERTED_TO_WORKSITE' || q.status === 'INVOICED').length, [quotes]);
  const activeQuoteCount = useMemo(() => quotes.length - trashedCount - rejectedCount - archivedCount, [quotes.length, trashedCount, rejectedCount, archivedCount]);

  const addItem = () => {
    const newIndex = formData.items.length;
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { _id: genItemId(), name: '', description: '', quantity: 1, price: 0, total: 0, tva_rate: prev.tva_rate, unit: '' }]
    }));
    // Initialiser les états d'autocomplétion pour le nouvel item
    setItemSearchStates(prev => ({...prev, [newIndex]: ''}));
    setShowItemSuggestions(prev => ({...prev, [newIndex]: false}));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
    // Nettoyer les états d'autocomplétion
    setItemSearchStates(prev => {
      const newStates = {...prev};
      delete newStates[index];
      return newStates;
    });
    setShowItemSuggestions(prev => {
      const newStates = {...prev};
      delete newStates[index];
      return newStates;
    });
  };

  // Calcul des totaux avec TVA (par taux)
  const calculateTotals = () => {
    let totalHT = 0;
    const tvaByRate = {}; // { '20': 50.00, '10': 25.00, etc. }
    
    formData.items.forEach(item => {
      const itemTotal = (item.quantity || 0) * (item.price || 0);
      totalHT += itemTotal;
      
      // Calculer la TVA pour chaque ligne
      const itemTvaRate = item.tva_rate !== undefined ? item.tva_rate : 20;
      
      if (itemTvaRate >= 0) {
        const tvaAmount = itemTotal * (itemTvaRate / 100);
        if (tvaByRate[itemTvaRate] === undefined) {
          tvaByRate[itemTvaRate] = 0;
        }
        tvaByRate[itemTvaRate] += tvaAmount;
      }
    });
    
    const totalTVA = Object.values(tvaByRate).reduce((sum, tva) => sum + tva, 0);
    const totalTTC = totalHT + totalTVA;
    
    return {
      totalHT: totalHT.toFixed(2),
      totalTVA: totalTVA.toFixed(2),
      totalTTC: totalTTC.toFixed(2),
      tvaByRate: Object.entries(tvaByRate).map(([rate, amount]) => ({
        rate: parseFloat(rate),
        amount: amount.toFixed(2)
      })).sort((a, b) => b.rate - a.rate) // Trier par taux décroissant
    };
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = prev.items.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };
          // Toujours recalculer le total HT de la ligne
          const qty = parseFloat(updatedItem.quantity) || 0;
          const price = parseFloat(updatedItem.price) || 0;
          updatedItem.total = qty * price;
          return updatedItem;
        }
        return item;
      });
      
      return { ...prev, items: newItems };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // 🔒 Bloquer la modification si le devis est archivé
    if (isEditMode && editingQuote?.status === 'ARCHIVED') {
      toast.error('🔒 Ce devis est archivé et ne peut pas être modifié.', { id: 'archived-save' });
      return;
    }
    if (creating) return; // Anti double-clic (EDGE-005)
    setCreating(true);

    try {
      // Si un nom de client est saisi mais pas d'ID, créer le client d'abord
      let clientId = formData.client_id;
      
      if (clientSearch && !formData.client_id) {
        // Créer un nouveau client avec les informations saisies
        const newClientData = {
          nom: formData.client_last_name || clientSearch, // Nom de famille
          prenom: formData.client_first_name || '', // Prénom
          email: formData.client_email || '',
          telephone: formData.client_phone || '',
          adresse: '',
          domaine: 'BTP'
        };
        
        try {
          const clientResponse = await http.post(`${API}/clients`, newClientData);
          
          clientId = clientResponse.data.id;
          
          // Mise à jour ciblée de la liste des clients (sans reload complet)
          setClients(prev => [...prev, clientResponse.data]);
        } catch (clientError) {
          console.error('Erreur création client:', clientError);
          if (clientError.response?.status === 401) {
            toast.error('⚠️ Session expirée. Veuillez vous reconnecter.');
            return;
          }
          throw clientError;
        }
      }
      
      // Préparer les données — totaux recalculés depuis les lignes
      const currentTotals = calculateTotals();
      const quoteData = {
        client_id: clientId || null,
        title: formData.title,
        description: formData.description,
        worksite_address: (formData.worksite_address || '').trim(),
        amount: parseFloat(currentTotals.totalHT),
        total_ht: parseFloat(currentTotals.totalHT),
        total_tva: parseFloat(currentTotals.totalTVA),
        total_ttc: parseFloat(currentTotals.totalTTC),
        status: formData.status,
        attach_terms: attachTermsOverride, // null = suivre le réglage entreprise
        tva_autoliquidation: formData.tva_autoliquidation || false,
        deposit_percent: formData.deposit_percent !== '' && formData.deposit_percent !== null && formData.deposit_percent !== undefined ? parseFloat(formData.deposit_percent) : null,
        planned_works_text: (formData.planned_works_text || '').trim(),
        items: formData.items.map(({ _id, ...rest }) => rest), // Retirer _id interne avant envoi
        photos: quotePhotos.map(p => ({
          name: p.name,
          data: p.data,
          type: p.type
        })),
        // 🔒 Verrouillage optimiste (EDGE-003) : version chargée, pour détecter un conflit
        // si quelqu'un d'autre a modifié ce devis entre-temps dans un autre onglet.
        ...(isEditMode && editingQuote ? { expected_updated_at: editingQuote.updated_at } : {})
      };

      if (isEditMode && editingQuote) {
        // Mode modification : PUT request (l'intercepteur injecte le token)
        await http.put(`${API}/quotes/${editingQuote.id}`, quoteData);
        toast.success('Devis modifié avec succès !');
      } else {
        // Mode création : POST request (l'intercepteur injecte le token)
        await http.post(`${API}/quotes`, quoteData);
        toast.success(clientSearch && !formData.client_id ? 
          'Devis créé avec succès ! Le client a été ajouté automatiquement.' : 
          'Devis créé avec succès !');
      }

      // Réinitialisation
      setFormData({
        client_id: '',
        client_name: '',
        title: '',
        description: '',
        worksite_address: '',
        amount: '',
        status: 'DRAFT',
        tva_rate: 20,
        tva_autoliquidation: false,
        deposit_percent: '',
        planned_works_text: '',
        items: [{ _id: genItemId(), name: '', description: '', quantity: 1, price: 0, total: 0, tva_rate: 20, unit: '' }]
      });
      setClientSearch('');
      setShowClientSuggestions(false);
      setItemSearchStates({});
      setShowItemSuggestions({});
      setQuotePhotos([]);
      setUploadingPhoto(false);
      setShowForm(false);
      setIsEditMode(false);
      setEditingQuote(null);
      // Rafraîchir uniquement les devis en arrière-plan (non-bloquant)
      http.get(`${API}/quotes`).then(r => setQuotes(r.data.data || [])).catch(() => {});
    } catch (error) {
      console.error('Erreur:', error);
      // Gestion spécifique du 401 (session expirée → l'intercepteur gère le refresh/logout)
      if (error.response?.status === 401) {
        toast.error('⚠️ Session expirée. Reconnexion en cours…');
        // L'intercepteur a déjà tenté le refresh. Si on arrive ici, le refresh a échoué
        // et forceLogout() a été appelé. Pas besoin de dupliquer la logique.
      } else if (error.response?.status === 409) {
        // 🔒 Conflit d'édition concurrente (EDGE-003) : quelqu'un d'autre a modifié ce devis
        // entre-temps. On NE sauvegarde PAS par-dessus — on prévient clairement l'utilisateur.
        toast.error('⚠️ Ce devis a été modifié par quelqu\'un d\'autre entre-temps. Rechargez la page pour voir les dernières modifications avant de réessayer.', { duration: 8000 });
      } else {
        toast.error(isEditMode ? 'Erreur lors de la modification du devis' : 'Erreur lors de la création du devis');
      }
    } finally {
      setCreating(false);
    }
  };

  // NEW: Templates management functions
  const loadTemplates = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await http.get(`${API}/quote-templates`);
      // Le backend retourne {data: [...], count: ...}
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Erreur chargement templates:', error);
      // Set sample templates if API fails
      setTemplates([
        {
          id: '1',
          name: 'Devis Standard BTP',
          description: 'Template de base pour travaux BTP',
          category: 'standard',
          items: [
            { name: 'Main d\'œuvre', description: 'Coût horaire technicien', unit_price: 45, default_quantity: 8 },
            { name: 'Matériaux', description: 'Matériaux de base', unit_price: 200, default_quantity: 1 }
          ]
        },
        {
          id: '2',
          name: 'Devis Plomberie',
          description: 'Template spécialisé plomberie',
          category: 'plomberie',
          items: [
            { name: 'Installation tuyauterie', description: 'Pose et raccordement', unit_price: 85, default_quantity: 1 },
            { name: 'Équipements', description: 'Robinetterie et accessoires', unit_price: 150, default_quantity: 1 }
          ]
        }
      ]);
    }
  };

  const createTemplate = async () => {
    try {
      // Filtrer uniquement les champs valides pour éviter erreur 500
      const templatePayload = {
        name: templateFormData.name,
        description: templateFormData.description,
        items: templateFormData.items,
        tags: [templateFormData.category] // Convertir category en tags
      };

      if (isEditingTemplate && editingTemplate) {
        // Mode modification : PUT request
        const response = await http.put(`${API}/quote-templates/${editingTemplate.id}`, templatePayload);
        setTemplates(prev => prev.map(t => t.id === editingTemplate.id ? response.data : t));
        alert('Template modifié avec succès !');
      } else {
        // Mode création : POST request
        const response = await http.post(`${API}/quote-templates`, templatePayload);
        setTemplates(prev => [...prev, response.data]);
        alert('Template créé avec succès !');
      }

      // Réinitialisation
      setTemplateFormData({
        name: '',
        description: '',
        category: 'standard',
        items: [{ name: '', description: '', unit_price: 0, default_quantity: 1 }]
      });
      setShowTemplateForm(false);
      setIsEditingTemplate(false);
      setEditingTemplate(null);
    } catch (error) {
      console.error('Erreur template:', error);
      alert(isEditingTemplate ? 'Erreur lors de la modification du template' : 'Erreur lors de la création du template');
    }
  };

  const editTemplate = (template) => {
    setEditingTemplate(template);
    setIsEditingTemplate(true);
    setTemplateFormData({
      name: template.name,
      description: template.description || '',
      category: (template.tags && template.tags[0]) || 'standard',
      items: template.items || [{ name: '', description: '', unit_price: 0, default_quantity: 1 }]
    });
    setShowTemplateForm(true);
  };

  const deleteTemplate = async (templateId) => {
    if (!(await confirm('Êtes-vous sûr de vouloir supprimer ce template ?'))) return;
    
    try {
      await http.delete(`${API}/quote-templates/${templateId}`);
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      alert('Template supprimé avec succès !');
    } catch (error) {
      console.error('Erreur suppression template:', error);
      alert('Erreur lors de la suppression du template');
    }
  };

  const useTemplate = (template) => {
    const templateItems = template.items.map(item => ({
      _id: genItemId(),
      name: item.name,
      quantity: item.default_quantity,
      price: item.unit_price,
      total: item.default_quantity * item.unit_price,
      tva_rate: 20
    }));
    
    setFormData({
      client_id: '',
      title: template.name,
      description: template.description,
      amount: templateItems.reduce((sum, item) => sum + item.total, 0).toString(),
      status: 'DRAFT',
      tva_rate: 20,
      items: templateItems
    });
    
    setShowTemplates(false);
    setShowForm(true);
    toast.success('Template appliqué ! Vous pouvez maintenant personnaliser le devis.');
  };

  const addTemplateItem = () => {
    setTemplateFormData(prev => ({
      ...prev,
      items: [...prev.items, { name: '', description: '', unit_price: 0, default_quantity: 1 }]
    }));
  };

  const updateTemplateItem = (index, field, value) => {
    setTemplateFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
    
    // Si c'est le champ name, gérer l'autocomplétion
    if (field === 'name') {
      if (value.trim()) {
        setTemplateItemSuggestions(prev => ({...prev, [index]: true}));
      } else {
        setTemplateItemSuggestions(prev => ({...prev, [index]: false}));
      }
    }
    
    // Si c'est le champ description, gérer l'autocomplétion
    if (field === 'description') {
      if (value.trim()) {
        setTemplateDescSuggestions(prev => ({...prev, [index]: true}));
      } else {
        setTemplateDescSuggestions(prev => ({...prev, [index]: false}));
      }
    }
  };
  
  const getFilteredTemplateProducts = (index) => {
    const searchTerm = templateFormData.items[index]?.name || '';
    if (!searchTerm.trim()) return [];
    
    return catalogProducts.filter(product =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };
  
  const selectTemplateProduct = (index, product) => {
    setTemplateFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? {
          ...item,
          name: product.name,
          description: product.description || product.unit || '',
          unit_price: product.price || 0
        } : item
      )
    }));
    setTemplateItemSuggestions(prev => ({...prev, [index]: false}));
  };
  
  const getFilteredTemplateProductsByDesc = (index) => {
    const searchTerm = templateFormData.items[index]?.description || '';
    if (!searchTerm.trim()) return [];
    
    return catalogProducts.filter(product =>
      (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.unit && product.unit.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (product.name && product.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };
  
  const selectTemplateProductByDesc = (index, product) => {
    setTemplateFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? {
          ...item,
          name: product.name,
          description: product.description || product.unit || '',
          unit_price: product.price || 0
        } : item
      )
    }));
    setTemplateDescSuggestions(prev => ({...prev, [index]: false}));
  };

  const removeTemplateItem = (index) => {
    setTemplateFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // ✅ Calcul unique des totaux par rendu (évite 6 appels redondants + insertBefore)
  const totals = useMemo(() => calculateTotals(), [formData.items]);

  // ⚡ Skeleton KPI pendant le chargement initial
  const SkeletonKPI = () => (
    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 text-center animate-pulse">
      <div className="h-8 w-12 bg-white/20 rounded mx-auto mb-2"></div>
      <div className="h-3 w-16 bg-white/10 rounded mx-auto"></div>
    </div>
  );

  if (activeTab === 'worksites') {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => setActiveTab('quotes')}>Retour aux devis</Button>
        <WorksitesManagement />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl p-8">
        {/* Header professionnel avec KPI Dashboard */}
        <div className="rounded-3xl p-8 text-white mb-8" style={{ background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 55%, #FB923C 100%)' }}>
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-6 lg:space-y-0">
            <div>
              <h2 className="text-3xl font-bold mb-2">Centre de Gestion Commerciale</h2>
              <p className="text-gray-300">Pilotez votre activité devis et transformez vos prospects en chantiers</p>
            </div>
            
            {/* KPI Dashboard */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 w-full lg:w-auto">
              {!dataReady ? <><SkeletonKPI /><SkeletonKPI /><SkeletonKPI /></> : <>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 text-center">
                <div className="text-2xl font-bold text-white">
                  {activeQuoteCount}
                </div>
                <div className="text-xs text-gray-300 uppercase tracking-wide">Devis Actifs</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 text-center">
                <div className="text-2xl font-bold text-orange-400">
                  {draftCount}
                </div>
                <div className="text-xs text-gray-300 uppercase tracking-wide">En Attente</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 text-center">
                <div className="text-2xl font-bold text-blue-400">
                  {sentCount}
                </div>
                <div className="text-xs text-gray-300 uppercase tracking-wide">Envoyés</div>
              </div>
              </>}
            </div>
          </div>
          
          {/* Actions rapides */}
          <div className="flex flex-wrap gap-3 mt-6 items-center">
            <Button
              onClick={() => setShowForm(true)}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border-0 rounded-2xl px-6 py-3 font-medium transition-all duration-200"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouveau Devis
            </Button>
            <Button
              onClick={() => setShowClientForm(true)}
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border-0 rounded-2xl px-6 py-3 font-medium transition-all duration-200"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Nouveau Client
            </Button>
            <Button
              onClick={() => {
                setShowTemplates(true);
                loadTemplates();
              }}
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border-0 rounded-2xl px-6 py-3 font-medium transition-all duration-200"
            >
              <FileBarChart className="h-4 w-4 mr-2" />
              Templates
            </Button>
            <Button
              onClick={() => {
                alert('📊 Analytics Devis\n\n' +
                  '• Taux de conversion: 73%\n' +
                  '• Montant moyen devis: 2,450€\n' +
                  '• Délai moyen acceptation: 4 jours\n' +
                  '• Performance mensuelle: +15%\n' +
                  '• Meilleur secteur: Plomberie\n\n' +
                  'Dashboard complet en développement...'
                );
              }}
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border-0 rounded-2xl px-6 py-3 font-medium transition-all duration-200"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </Button>
          </div>
        </div>
        
        {/* Barre de recherche et filtres intelligents */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            {/* Recherche intelligente */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Rechercher un devis, client, montant..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all duration-200"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            
            {/* Filtres intelligents */}
            <div className="flex flex-wrap gap-2">
              <select
                value={quoteStatusFilter}
                onChange={(e) => setQuoteStatusFilter(e.target.value)}
                className="px-4 py-2 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-sm"
              >
                <option value="">Tous statuts</option>
                <option value="DRAFT">Brouillon</option>
                <option value="SENT">Envoyé</option>
                <option value="REJECTED">Refusé</option>
                <option value="CONVERTED_TO_WORKSITE">Converti</option>
              </select>
              
              <select
                value={quoteAmountFilter}
                onChange={(e) => setQuoteAmountFilter(e.target.value)}
                className="px-4 py-2 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-sm"
              >
                <option value="">Montant</option>
                <option value="0-1000">0€ - 1 000€</option>
                <option value="1000-5000">1 000€ - 5 000€</option>
                <option value="5000-10000">5 000€ - 10 000€</option>
                <option value="10000+">10 000€+</option>
              </select>
              
              <select
                value={quotePeriodFilter}
                onChange={(e) => setQuotePeriodFilter(e.target.value)}
                className="px-4 py-2 rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-sm"
              >
                <option value="">Période</option>
                <option value="today">Aujourd'hui</option>
                <option value="week">Cette semaine</option>
                <option value="month">Ce mois</option>
                <option value="quarter">Ce trimestre</option>
              </select>
              
              {(searchTerm || quoteStatusFilter || quoteAmountFilter || quotePeriodFilter) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setQuoteStatusFilter('');
                    setQuoteAmountFilter('');
                    setQuotePeriodFilter('');
                  }}
                  className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                >
                  <X className="h-4 w-4 mr-1" />
                  Réinitialiser
                </Button>
              )}
              
              <Button
                variant="outline"
                size="sm"
                className="rounded-xl border-gray-200 hover:bg-gray-50"
              >
                <Filter className="h-4 w-4 mr-2" />
                Plus de filtres
              </Button>
              
              <Button
                size="sm"
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl"
              >
                <Download className="h-4 w-4 mr-2" />
                Exporter
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs améliorés */}
        <div className="mt-6">
          <div className="flex space-x-1 bg-gray-100 rounded-2xl p-1">
            <button
              onClick={() => setActiveTab('quotes')}
              className={`px-6 py-2 rounded-xl font-medium transition-colors ${
                activeTab === 'quotes' 
                  ? 'bg-white text-purple-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Devis ({draftCount + sentCount + convertedCount})
            </button>
            <button
              onClick={() => setActiveTab('worksites')}
              className={`px-6 py-2 rounded-xl font-medium transition-colors ${
                activeTab === 'worksites' 
                  ? 'bg-white text-purple-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Chantiers ({worksites.length})
            </button>
            <button
              onClick={() => setActiveTab('trash')}
              className={`px-6 py-2 rounded-xl font-medium transition-colors ${
                activeTab === 'trash' 
                  ? 'bg-white text-purple-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Corbeille ({trashedCount})
            </button>
            <button
              onClick={() => setActiveTab('archive')}
              className={`px-6 py-2 rounded-xl font-medium transition-colors ${
                activeTab === 'archive' 
                  ? 'bg-white text-purple-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Archive ({rejectedCount + archivedCount})
            </button>
          </div>
        </div>
      </div>

      {/* Quick Client Form */}
      {showClientForm && (
        <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
          <CardHeader className="pb-6">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold text-gray-900">Ajouter un client rapidement</CardTitle>
              <Button
                onClick={() => setShowClientForm(false)}
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                placeholder="Nom du client"
                value={clientData.nom}
                onChange={(e) => setClientData(prev => ({...prev, nom: e.target.value}))}
                className="rounded-xl border-gray-200"
              />
              <Input
                type="email"
                placeholder="Email"
                value={clientData.email}
                onChange={(e) => setClientData(prev => ({...prev, email: e.target.value}))}
                className="rounded-xl border-gray-200"
              />
              <Input
                placeholder="Téléphone"
                value={clientData.telephone}
                onChange={(e) => setClientData(prev => ({...prev, telephone: e.target.value}))}
                className="rounded-xl border-gray-200"
              />
              <select
                value={clientData.domaine}
                onChange={(e) => setClientData(prev => ({...prev, domaine: e.target.value}))}
                className="rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all duration-200 p-3"
              >
                <option value="BTP">BTP - Bâtiment Travaux Publics</option>
                <option value="ELEC">Électricité</option>
                <option value="PLOMB">Plomberie</option>
                <option value="TELECOM">Télécommunications</option>
                <option value="AUTRE">Autre</option>
              </select>
            </div>
            <div className="mt-4">
              <Input
                placeholder="Adresse complète"
                value={clientData.adresse}
                onChange={(e) => setClientData(prev => ({...prev, adresse: e.target.value}))}
                className="rounded-xl border-gray-200"
              />
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <Button variant="outline" onClick={() => setShowClientForm(false)}>
                Annuler
              </Button>
              <Button onClick={addQuickClient} className="bg-purple-600 hover:bg-purple-700 text-white">
                Ajouter Client
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Quote Form */}
      {showForm && (
        <Card id="quote-edit-form" className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
          <CardHeader className="pb-6">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl font-semibold text-gray-900">
                  {isEditMode ? 'Modifier le devis' : 'Créer un nouveau devis'}
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  {isEditMode ? 'Modifiez les informations du devis ci-dessous' : 'Tous les champs sont optionnels - remplissez seulement ce dont vous avez besoin'}
                </p>
              </div>
              <Button
                onClick={() => {
                  setShowForm(false);
                  setIsEditMode(false);
                  setEditingQuote(null);
                  setFormData({
                    client_id: '',
                    client_name: '',
                    client_first_name: '',
                    client_last_name: '',
                    client_phone: '',
                    client_email: '',
                    title: '',
                    description: '',
                    amount: '',
                    status: 'DRAFT',
                    tva_rate: 20,
                    items: [{ _id: genItemId(), name: '', description: '', quantity: 1, price: 0, total: 0, tva_rate: 20, unit: '' }]
                  });
                }}
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            <form onSubmit={handleSubmit} className="space-y-6" translate="no">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Client
                    <span className="text-xs text-gray-500 ml-2">(Saisissez un nom ou sélectionnez)</span>
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      value={clientSearch}
                      onChange={handleClientSearchChange}
                      onFocus={() => clientSearch && setShowClientSuggestions(true)}
                      placeholder="Ex: Jean Dupont, ABC Entreprise..."
                      className="rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20 pr-10"
                    />
                    {clientSearch && (
                      <button
                        type="button"
                        onClick={() => {
                          setClientSearch('');
                          setFormData(prev => ({...prev, client_id: '', client_name: ''}));
                          setShowClientSuggestions(false);
                        }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  
                  {/* Suggestions d'autocomplétion */}
                  {showClientSuggestions && filteredClients.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {filteredClients.map(client => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => selectClient(client)}
                          className="w-full text-left px-4 py-3 hover:bg-purple-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{client.nom}</div>
                          {client.email && (
                            <div className="text-xs text-gray-500 mt-1">{client.email}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Message si client sélectionné depuis la base */}
                  {formData.client_id && (
                    <p className="text-xs text-green-600 mt-1 flex items-center">
                      <Check className="h-3 w-3 mr-1" />
                      Client trouvé dans votre base de données
                    </p>
                  )}
                  
                  {/* Message si client manuel */}
                  {clientSearch && !formData.client_id && (
                    <p className="text-xs text-blue-600 mt-1 flex items-center">
                      <User className="h-3 w-3 mr-1" />
                      Nouveau client - sera enregistré avec le devis
                    </p>
                  )}
                </div>
                
                {/* Champs supplémentaires pour nouveau client */}
                {clientSearch && !formData.client_id && (
                  <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-xl p-6 space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <User className="h-4 w-4 mr-2 text-blue-600" />
                      Informations du nouveau client
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Prénom
                        </label>
                        <Input
                          type="text"
                          value={formData.client_first_name || ''}
                          onChange={(e) => setFormData(prev => ({...prev, client_first_name: e.target.value}))}
                          placeholder="Ex: Jean"
                          className="rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nom
                        </label>
                        <Input
                          type="text"
                          value={formData.client_last_name || ''}
                          onChange={(e) => setFormData(prev => ({...prev, client_last_name: e.target.value}))}
                          placeholder="Ex: Dupont"
                          className="rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Numéro de téléphone
                        </label>
                        <Input
                          type="tel"
                          value={formData.client_phone || ''}
                          onChange={(e) => setFormData(prev => ({...prev, client_phone: e.target.value}))}
                          placeholder="Ex: 06 12 34 56 78"
                          className="rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email
                        </label>
                        <Input
                          type="email"
                          value={formData.client_email || ''}
                          onChange={(e) => setFormData(prev => ({...prev, client_email: e.target.value}))}
                          placeholder="Ex: jean.dupont@email.com"
                          className="rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500/20"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Statut du devis</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({...prev, status: e.target.value}))}
                    className="w-full rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all duration-200 p-3 font-medium cursor-pointer bg-white hover:border-purple-300"
                    style={{
                      backgroundColor: 
                        formData.status === 'DRAFT' ? '#FEF3C7' :
                        formData.status === 'SENT' ? '#DBEAFE' :
                        formData.status === 'REJECTED' ? '#FEE2E2' : '#FFFFFF',
                      color: 
                        formData.status === 'DRAFT' ? '#92400E' :
                        formData.status === 'SENT' ? '#1E40AF' :
                        formData.status === 'REJECTED' ? '#991B1B' : '#374151'
                    }}
                  >
                    <option value="DRAFT" style={{backgroundColor: '#FEF3C7', color: '#92400E'}}>📋 Brouillon</option>
                    <option value="SENT" style={{backgroundColor: '#DBEAFE', color: '#1E40AF'}}>✉️ Envoyé</option>
                    <option value="REJECTED" style={{backgroundColor: '#FEE2E2', color: '#991B1B'}}>❌ Refusé</option>
                  </select>
                </div>
              </div>

              {/* Numéro de référence (en mode édition uniquement) */}
              {isEditMode && editingQuote?.quote_number != null && editingQuote.quote_number !== '' && (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Référence du devis</label>
                      <div className="text-2xl font-bold text-purple-900 font-mono">
                        #{editingQuote.quote_number}
                      </div>
                    </div>
                    <div className="bg-purple-100 rounded-full p-3">
                      <FileText className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    Ce numéro est généré automatiquement et ne peut pas être modifié
                  </p>
                </div>
              )}

              {/* Nouveau devis : Info sur génération auto */}
              {!isEditMode && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <div className="bg-blue-100 rounded-full p-2 mt-0.5">
                      <FileText className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">Numéro de référence automatique</h4>
                      <p className="text-xs text-gray-600">
                        Un numéro unique sera généré automatiquement lors de la création du devis (format: YYYYMM-XXX)
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Titre du devis</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                    placeholder="Ex: Recherche réseaux Boulevard Saint-Michel (optionnel)"
                    className="rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Adresse du chantier</label>
                <Input
                  value={formData.worksite_address}
                  onChange={(e) => setFormData(prev => ({...prev, worksite_address: e.target.value}))}
                  placeholder="Ex: 12 rue de la Paix, 75001 Paris"
                  className="rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20"
                />
                <p className="text-xs text-gray-400 mt-1">Transmise automatiquement au chantier lors de l'acceptation du devis — visible par les techniciens sur leur planning.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                  rows={3}
                  placeholder="Description détaillée des travaux..."
                  className="w-full rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all duration-200 p-3 resize-none"
                />
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Articles / Prestations</label>
                    <p className="text-xs text-gray-500 mt-1">Ajoutez les détails de vos prestations ou importez depuis le catalogue</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => setShowCatalogModal(true)}
                      size="sm"
                      variant="outline"
                      className="rounded-xl border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                    >
                      <FileBarChart className="h-4 w-4 mr-1" />
                      Catalogue
                    </Button>
                    <Button
                      type="button"
                      onClick={addItem}
                      size="sm"
                      className="bg-purple-100 text-purple-700 hover:bg-purple-200 rounded-xl"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Ajouter
                    </Button>
                  </div>
                </div>
                
                {/* En-tête des colonnes */}
                <div className="grid grid-cols-12 gap-3 items-start mb-2 px-2 text-xs font-semibold text-gray-500 uppercase">
                  <div className="col-span-4">Article / Prestation</div>
                  <div className="col-span-1 text-center">Unité</div>
                  <div className="col-span-1 text-center">Qté</div>
                  <div className="col-span-2 text-center">Prix HT</div>
                  <div className="col-span-1 text-center">TVA</div>
                  <div className="col-span-2 text-center">Total HT / TTC</div>
                  <div className="col-span-1"></div>
                </div>

                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={item._id || `item-fallback-${index}`} className="grid grid-cols-12 gap-3 items-start relative bg-gray-50 p-3 rounded-xl">
                      <div className="col-span-4 relative space-y-2">
                        <Input
                          value={item.name}
                          onChange={(e) => handleItemNameChange(index, e.target.value)}
                          onFocus={() => item.name && setShowItemSuggestions(prev => ({...prev, [index]: true}))}
                          placeholder="Ex: Détection réseaux, Inspection..."
                          className="rounded-xl border-gray-200 focus:border-purple-500 focus:ring-purple-500/20"
                        />
                        
                        {/* Suggestions du catalogue — ternaire pour éviter insertBefore */}
                        {showItemSuggestions[index] && getFilteredProducts(index).length > 0 ? (
                          <div className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                            {getFilteredProducts(index).map(product => (
                              <button
                                key={product.id}
                                type="button"
                                onClick={() => selectCatalogProduct(index, product)}
                                className="w-full text-left px-4 py-3 hover:bg-purple-50 transition-colors border-b last:border-0"
                              >
                                <div className="flex justify-between items-start">
                                  <div className="flex-1 mr-3">
                                    <div className="font-medium text-gray-900">{product.name}</div>
                                    {product.description ? (
                                      <div className="text-xs text-gray-600 mt-1">{product.description}</div>
                                    ) : null}
                                    <div className="text-xs text-gray-500 mt-0.5">{product.unit}</div>
                                  </div>
                                  <div className="text-sm font-semibold text-purple-600 whitespace-nowrap">{product.price}€</div>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : null}
                        
                        {item.name && !getFilteredProducts(index).length && showItemSuggestions[index] ? (
                          <p className="text-xs text-blue-600 mt-1 flex items-center">
                            <Plus className="h-3 w-3 mr-1 inline" />
                            Nouvel article personnalisé
                          </p>
                        ) : null}
                        
                        {/* Champ Description */}
                        <textarea
                          value={item.description || ''}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          placeholder="Description de l'article (optionnel)"
                          rows={2}
                          className="w-full rounded-xl border border-gray-200 focus:border-purple-500 focus:ring-purple-500/20 transition-all duration-200 p-2 resize-none text-sm"
                        />
                        
                        {/* Bouton Photo pour l'article */}
                        <div className="flex items-center gap-2 mt-2">
                          <label className="flex-1 cursor-pointer">
                            <div className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-all">
                              <Camera className="h-3.5 w-3.5 text-gray-500" />
                              <span className="text-xs text-gray-600">Photo</span>
                            </div>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleItemPhotoUpload(index, e)}
                              className="hidden"
                            />
                          </label>
                          {item.photo ? (
                            <div className="relative group">
                              <img
                                src={item.photo.preview}
                                alt="Photo article"
                                className="h-10 w-10 object-cover rounded-lg border border-gray-200 cursor-pointer"
                                onClick={() => {
                                  // Agrandir l'image au clic
                                  window.open(item.photo.preview, '_blank');
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => removeItemPhoto(index)}
                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                      
                      <div className="col-span-1">
                        <Input
                          value={item.unit || ''}
                          onChange={(e) => updateItem(index, 'unit', e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                          placeholder="Ens"
                          className="rounded-xl border-gray-200 text-center text-sm"
                        />
                      </div>

                      <div className="col-span-1">
                        <Input
                          type="number"
                          value={item.quantity ?? ''}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value === '' ? '' : parseFloat(e.target.value))}
                          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                          placeholder="1"
                          className="rounded-xl border-gray-200 text-center"
                          min="0"
                          step="0.01"
                        />
                      </div>
                      
                      <div className="col-span-2">
                        <Input
                          type="number"
                          value={item.price ?? ''}
                          onChange={(e) => updateItem(index, 'price', e.target.value === '' ? '' : parseFloat(e.target.value))}
                          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                          placeholder="0.00"
                          className="rounded-xl border-gray-200"
                          step="0.01"
                        />
                      </div>
                      
                      <div className="col-span-1">
                        <select
                          value={item.tva_rate !== undefined ? item.tva_rate : 20}
                          onChange={(e) => updateItem(index, 'tva_rate', parseFloat(e.target.value))}
                          className="w-full px-2 py-2 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none text-sm bg-white"
                        >
                          <option value={20}>20%</option>
                          <option value={10}>10%</option>
                          <option value={5.5}>5,5%</option>
                          <option value={0}>0%</option>
                          <option value={-1}>Exo</option>
                        </select>
                      </div>
                      
                      <div className="col-span-2 text-center">
                        <span className="block text-sm font-semibold text-gray-900">{((parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0)).toFixed(2)}€</span>
                        {(() => {
                          const ht = (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0);
                          const rate = item.tva_rate !== undefined ? parseFloat(item.tva_rate) : 20;
                          const ttc = rate < 0 ? ht : ht * (1 + rate / 100);
                          return <span className="block text-xs font-medium text-purple-600 mt-0.5">{ttc.toFixed(2)}€ TTC</span>;
                        })()}
                      </div>
                      
                      <div className="col-span-1 text-center">
                        {formData.items.length > 1 ? (
                          <Button
                            type="button"
                            onClick={() => removeItem(index)}
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Récapitulatif avec TVA détaillée par taux — totals calculé UNE SEULE FOIS via useMemo */}
                <div className="mt-6 space-y-3">
                  <div className="p-5 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl border-2 border-gray-200">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-medium text-gray-600">Total HT</span>
                      <span className="text-lg font-semibold text-gray-900">{totals.totalHT}€</span>
                    </div>
                    
                    {/* Section TVA — structure DOM FIXE pour éviter insertBefore lors du recalcul */}
                    <div className="mb-3 pb-3 border-b border-gray-300 space-y-2">
                      {totals.tvaByRate.length > 0 ? (
                        totals.tvaByRate.map((tva) => (
                          <div key={`tva-${tva.rate}`} className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-600">
                              {tva.rate === -1 ? '🚫 TVA Exonérée' : `TVA ${tva.rate}%`}
                            </span>
                            <span className="text-sm font-semibold text-gray-900">{tva.amount}€</span>
                          </div>
                        ))
                      ) : (
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-amber-600">🚫 TVA non applicable</span>
                          <span className="text-lg font-semibold text-amber-600">0.00€</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                        <span className="text-sm font-semibold text-gray-700">Total TVA</span>
                        <span className="text-lg font-bold text-gray-900">{totals.totalTVA}€</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-base font-bold text-gray-900">Total TTC</span>
                      <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                        {totals.totalTTC}€
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 📜 Bloc CGV — Conditions générales de vente */}
              <div className="p-4 border-2 border-gray-100 rounded-2xl space-y-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    <FileText className="h-4 w-4 text-indigo-600" />
                    Conditions générales de vente
                    <span className="text-xs font-normal text-gray-400">
                      · Client {clientAudience === 'consumer' ? 'particulier' : 'professionnel'}
                    </span>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={attachTermsOverride !== null ? attachTermsOverride : (termsInfo?.attach_terms_to_quotes !== false)}
                      onChange={e => setAttachTermsOverride(e.target.checked)}
                      disabled={!termsInfo?.version}
                      className="w-4 h-4 accent-indigo-600"
                    />
                    Joindre les CGV au devis
                  </label>
                </div>
                {termsInfo?.version ? (
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-xs text-gray-500">
                      Version <span className="font-semibold text-gray-700">v{termsInfo.version.version_number}</span>
                      {termsInfo.version.effective_at ? ` du ${new Date(termsInfo.version.effective_at).toLocaleDateString('fr-FR')}` : ''}
                      {' '}sera annexée au PDF du devis.
                    </p>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          if (termsInfo.version.source_type === 'pdf_upload') {
                            const r = await http.get(`${API}/company-terms/${termsInfo.version.id}/file-url`);
                            if (r.data?.url) window.open(r.data.url, '_blank');
                          } else {
                            const r = await http.get(`${API}/company-terms/${termsInfo.version.id}/pdf`, { responseType: 'blob' });
                            const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                            window.open(url, '_blank');
                            setTimeout(() => URL.revokeObjectURL(url), 60000);
                          }
                        } catch { toast.error('Impossible d\'ouvrir les CGV'); }
                      }}
                      className="text-xs text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-1"
                    >
                      <Eye className="h-3.5 w-3.5" /> Consulter
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-amber-600 flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                    Aucune CGV active pour les clients {clientAudience === 'consumer' ? 'particuliers' : 'professionnels'}. Le devis sera envoyé sans CGV.
                    Configurez-les dans Paramètres → Conditions générales de vente.
                  </p>
                )}
              </div>

              <div className="p-4 border-2 border-gray-100 rounded-2xl space-y-3">
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.tva_autoliquidation || false}
                    onChange={e => setFormData(prev => ({ ...prev, tva_autoliquidation: e.target.checked }))}
                    className="w-4 h-4 accent-indigo-600"
                  />
                  <span>TVA autoliquidation <span className="text-gray-400 font-normal">(sous-traitance BTP — art. 283-2 CGI)</span></span>
                </label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Acompte à la signature (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="1"
                      value={formData.deposit_percent}
                      onChange={e => setFormData(prev => ({ ...prev, deposit_percent: e.target.value }))}
                      placeholder="Réglage entreprise par défaut"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Travaux prévus (facultatif)
                    </label>
                    <input
                      type="text"
                      value={formData.planned_works_text}
                      onChange={e => setFormData(prev => ({ ...prev, planned_works_text: e.target.value }))}
                      placeholder="Ex: Début prévu le 15/09, durée 3 jours"
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-4 flex space-x-4">
                <Button
                  type="submit"
                  disabled={creating}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-2xl py-3 shadow-lg transform transition-all duration-200 hover:scale-105"
                >
                  <span className="inline-flex items-center">
                    <span className="inline-flex items-center justify-center h-4 w-4 mr-2">
                      {creating ? (
                        <span className="animate-spin inline-block rounded-full h-4 w-4 border-b-2 border-white" />
                      ) : (
                        isEditMode ? <Check className="h-4 w-4" /> : <FileText className="h-4 w-4" />
                      )}
                    </span>
                    <span>
                      {creating
                        ? (isEditMode ? 'Modification...' : 'Création...')
                        : (isEditMode ? 'Enregistrer les modifications' : 'Créer le devis')
                      }
                    </span>
                  </span>
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setIsEditMode(false);
                    setEditingQuote(null);
                    setQuotePhotos([]);
                    setUploadingPhoto(false);
                    setFormData({
                      client_id: '',
                      title: '',
                      description: '',
                      amount: '',
                      status: 'DRAFT',
                      tva_rate: 20,
                      items: [{ _id: genItemId(), name: '', description: '', quantity: 1, price: 0, total: 0, tva_rate: 20, unit: '' }]
                    });
                  }}
                  variant="outline"
                  className="rounded-2xl px-6"
                >
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* 📜 Modal d'acceptation des CGV (obligatoire avant conversion) */}
      {termsAcceptModal.open && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden">
            <CardHeader className="pb-4 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-indigo-600" />
                  Acceptation des CGV
                </CardTitle>
                <button
                  onClick={() => setTermsAcceptModal({ open: false, quoteId: null, snapshot: null })}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="pt-5 space-y-4">
              <p className="text-sm text-gray-600">
                L'acceptation des conditions générales de vente est obligatoire avant de valider ce devis.
              </p>
              <div className="p-3 bg-gray-50 rounded-xl text-sm text-gray-700 flex items-center justify-between gap-2">
                <span>
                  CGV <span className="font-semibold">version {termsAcceptModal.snapshot?.version_number}</span>
                  {termsAcceptModal.snapshot?.effective_at ? ` du ${new Date(termsAcceptModal.snapshot.effective_at).toLocaleDateString('fr-FR')}` : ''}
                </span>
                <button
                  type="button"
                  className="text-xs text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-1 flex-shrink-0"
                  onClick={async () => {
                    try {
                      const r = await http.get(`${API}/quotes/${termsAcceptModal.quoteId}/pdf`, { responseType: 'blob' });
                      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                      window.open(url, '_blank');
                      setTimeout(() => URL.revokeObjectURL(url), 60000);
                    } catch { toast.error('Impossible d\'ouvrir le devis'); }
                  }}
                >
                  <Eye className="h-3.5 w-3.5" /> Consulter le devis complet
                </button>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Nom du signataire</label>
                <input
                  type="text"
                  value={termsSignatoryName}
                  onChange={e => setTermsSignatoryName(e.target.value)}
                  placeholder="Ex: Jean Dupont"
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:border-indigo-400 focus:outline-none"
                />
              </div>
              <label className="flex items-start gap-3 cursor-pointer p-3 border-2 border-gray-100 rounded-xl hover:border-indigo-200 transition-colors">
                <input
                  type="checkbox"
                  checked={termsAcceptChecked}
                  onChange={e => setTermsAcceptChecked(e.target.checked)}
                  className="w-4 h-4 accent-indigo-600 mt-0.5 flex-shrink-0"
                />
                <span className="text-xs text-gray-700 leading-relaxed">
                  Je reconnais avoir pris connaissance du devis et des conditions générales de vente
                  version {termsAcceptModal.snapshot?.version_number}
                  {termsAcceptModal.snapshot?.effective_at ? ` du ${new Date(termsAcceptModal.snapshot.effective_at).toLocaleDateString('fr-FR')}` : ''},
                  et je les accepte sans réserve.
                </span>
              </label>
              <div className="flex gap-3 pt-1">
                <Button
                  disabled={!termsAcceptChecked || !termsSignatoryName.trim() || termsAccepting}
                  onClick={async () => {
                    setTermsAccepting(true);
                    try {
                      await http.post(`${API}/quotes/${termsAcceptModal.quoteId}/accept-terms`, {
                        signatory_name: termsSignatoryName.trim(),
                        accepted: true
                      });
                      const qid = termsAcceptModal.quoteId;
                      setTermsAcceptModal({ open: false, quoteId: null, snapshot: null });
                      toast.success('CGV acceptées — validation du devis…');
                      await proceedAcceptQuote(qid);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Erreur lors de l\'acceptation');
                    } finally {
                      setTermsAccepting(false);
                    }
                  }}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl disabled:opacity-50"
                >
                  {termsAccepting ? 'Enregistrement…' : 'Accepter et valider le devis'}
                </Button>
                <Button
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => setTermsAcceptModal({ open: false, quoteId: null, snapshot: null })}
                >
                  Annuler
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal Catalogue */}
      {showCatalogModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
            <CardHeader className="pb-4 border-b">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-semibold text-gray-900">Catalogue Produits</CardTitle>
                  <p className="text-sm text-gray-500 mt-1">Sélectionnez des articles à ajouter au devis</p>
                </div>
                <Button
                  onClick={() => {
                    setShowCatalogModal(false);
                    setCatalogSearchTerm('');
                  }}
                  variant="ghost"
                  size="sm"
                  className="rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
              
              {/* Barre de recherche */}
              <div className="mt-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    autoFocus
                    placeholder="Rechercher un article..."
                    value={catalogSearchTerm}
                    onChange={(e) => setCatalogSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none transition-colors"
                  />
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-3">
                {catalogProducts
                  .filter(product => {
                    if (!catalogSearchTerm.trim()) return true;
                    const q = catalogSearchTerm.toLowerCase();
                    return (
                      product.name.toLowerCase().includes(q) ||
                      (product.description || '').toLowerCase().includes(q) ||
                      (product.reference || '').toLowerCase().includes(q) ||
                      (product.category || '').toLowerCase().includes(q)
                    );
                  })
                  .map((product) => {
                    const tvaLabel = product.tva_rate !== undefined ? (product.tva_rate < 0 ? 'Exo' : `${product.tva_rate}%`) : '20%';
                    return (
                    <div
                      key={product.id}
                      className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl hover:shadow-md transition-all cursor-pointer border-2 border-transparent hover:border-indigo-200"
                      onClick={() => {
                        // Ajouter l'article au devis
                        const newItem = {
                          _id: genItemId(),
                          name: product.name,
                          description: product.description || '',
                          quantity: 1,
                          price: product.price,
                          total: product.price,
                          tva_rate: product.tva_rate !== undefined ? product.tva_rate : 20
                        };
                        setFormData(prev => ({
                          ...prev,
                          items: [...prev.items, newItem]
                        }));
                        setShowCatalogModal(false);
                        setCatalogSearchTerm('');
                      }}
                    >
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{product.name}</h4>
                        {product.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{product.description}</p>}
                        <p className="text-sm text-gray-500 mt-1">Unité: {product.unit} · TVA: {tvaLabel}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-lg font-bold text-indigo-600">{product.price}€</span>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            const newItem = {
                              _id: genItemId(),
                              name: product.name,
                              description: product.description || '',
                              quantity: 1,
                              price: product.price,
                              total: product.price,
                              tva_rate: product.tva_rate !== undefined ? product.tva_rate : 20
                            };
                            setFormData(prev => ({
                              ...prev,
                              items: [...prev.items, newItem]
                            }));
                            setShowCatalogModal(false);
                            setCatalogSearchTerm('');
                          }}
                          size="sm"
                          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Ajouter
                        </Button>
                      </div>
                    </div>
                  );
                  })}
                
                {catalogSearchTerm.trim() && catalogProducts.filter(product => {
                    const q = catalogSearchTerm.toLowerCase();
                    return (
                      product.name.toLowerCase().includes(q) ||
                      (product.description || '').toLowerCase().includes(q) ||
                      (product.reference || '').toLowerCase().includes(q) ||
                      (product.category || '').toLowerCase().includes(q)
                    );
                  }).length === 0 && (
                  <div className="text-center py-12">
                    <FileBarChart className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Aucun article trouvé</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create/Edit Worksite Form */}
      {showWorksiteForm && (
        <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
          <CardHeader className="pb-6">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl font-semibold text-gray-900">
                  {editingWorksite ? 'Modifier le chantier' : 'Créer un nouveau chantier'}
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Renseignez les informations du chantier
                </p>
              </div>
              <Button
                onClick={() => {
                  setShowWorksiteForm(false);
                  setEditingWorksite(null);
                  setWorksiteFormData({
                    title: '',
                    description: '',
                    address: '',
                    client_id: '',
                    status: 'PLANNED',
                    progress: 0,
                    start_date: '',
                    end_date: '',
                    budget: '',
                    team_size: 1,
                    notes: ''
                  });
                }}
                variant="outline"
                className="rounded-xl"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={saveWorksite} className="space-y-6">
              {/* Informations principales */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Titre du chantier *</label>
                  <input
                    type="text"
                    required
                    value={worksiteFormData.title}
                    onChange={(e) => setWorksiteFormData({...worksiteFormData, title: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-gray-400 focus:outline-none transition-colors"
                    placeholder="Ex: Rénovation immeuble..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
                  <select
                    value={worksiteFormData.client_id}
                    onChange={(e) => setWorksiteFormData({...worksiteFormData, client_id: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-gray-400 focus:outline-none transition-colors"
                  >
                    <option value="">Sélectionner un client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.nom}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={worksiteFormData.description}
                  onChange={(e) => setWorksiteFormData({...worksiteFormData, description: e.target.value})}
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-gray-400 focus:outline-none transition-colors"
                  placeholder="Description détaillée du chantier..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
                <input
                  type="text"
                  value={worksiteFormData.address}
                  onChange={(e) => setWorksiteFormData({...worksiteFormData, address: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-gray-400 focus:outline-none transition-colors"
                  placeholder="Adresse complète du chantier"
                />
              </div>

              {/* Statut et avancement */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                  <select
                    value={worksiteFormData.status}
                    onChange={(e) => setWorksiteFormData({...worksiteFormData, status: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-gray-400 focus:outline-none transition-colors"
                  >
                    <option value="PLANNED">📋 Planifié</option>
                    <option value="IN_PROGRESS">🔧 En cours</option>
                    <option value="ON_HOLD">⏸️ En pause</option>
                    <option value="COMPLETED">✅ Terminé</option>
                    <option value="CANCELLED">❌ Annulé</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Avancement (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={worksiteFormData.progress}
                    readOnly
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-100 cursor-not-allowed"
                    title="L'avancement est calculé automatiquement en fonction des jours de planning"
                  />
                  <p className="text-xs text-gray-500 mt-1">📊 Calculé automatiquement à partir des jours de planning</p>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date de début</label>
                  <input
                    type="date"
                    value={worksiteFormData.start_date}
                    onChange={(e) => setWorksiteFormData({...worksiteFormData, start_date: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-gray-400 focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date de fin prévue</label>
                  <input
                    type="date"
                    value={worksiteFormData.end_date}
                    onChange={(e) => setWorksiteFormData({...worksiteFormData, end_date: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-gray-400 focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Budget et équipe */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Budget (€)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={worksiteFormData.budget}
                    onChange={(e) => setWorksiteFormData({...worksiteFormData, budget: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-gray-400 focus:outline-none transition-colors"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Taille de l'équipe</label>
                  <input
                    type="number"
                    min="1"
                    value={worksiteFormData.team_size}
                    onChange={(e) => setWorksiteFormData({...worksiteFormData, team_size: parseInt(e.target.value) || 1})}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-gray-400 focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes internes</label>
                <textarea
                  value={worksiteFormData.notes}
                  onChange={(e) => setWorksiteFormData({...worksiteFormData, notes: e.target.value})}
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-gray-400 focus:outline-none transition-colors"
                  placeholder="Notes pour l'équipe..."
                />
              </div>

              {/* Boutons d'action */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  disabled={savingWorksite}
                  className="flex-1 bg-black hover:bg-gray-800 text-white rounded-2xl py-3 shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {savingWorksite ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{editingWorksite ? 'Enregistrement...' : 'Création...'}</>
                  ) : (
                    <><Check className="h-4 w-4 mr-2" />{editingWorksite ? 'Enregistrer les modifications' : 'Créer le chantier'}</>
                  )}
                </Button>
                <Button
                  type="button"
                  disabled={savingWorksite}
                  onClick={() => {
                    setShowWorksiteForm(false);
                    setEditingWorksite(null);
                    setWorksiteFormData({
                      title: '',
                      description: '',
                      address: '',
                      client_id: '',
                      status: 'PLANNED',
                      progress: 0,
                      start_date: '',
                      end_date: '',
                      budget: '',
                      team_size: 1,
                      notes: ''
                    });
                  }}
                  variant="outline"
                  className="rounded-2xl px-6"
                >
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Quotes List */}
      {activeTab === 'quotes' && (
        <div className="space-y-6">
          {/* Vue Kanban Pipeline Commercial */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            
            {/* Colonne 1: Brouillons */}
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl border-0 shadow-lg overflow-hidden">
              <div className="h-1" style={{background:'linear-gradient(90deg,#FB923C,#F97316)'}} />
              <div 
                className="p-4 border-b border-orange-100 cursor-pointer hover:bg-orange-50/40 transition-colors"
                onClick={() => setExpandedColumn(expandedColumn === 'DRAFT' ? null : 'DRAFT')}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <div className="w-3 h-3 bg-orange-400 rounded-full mr-2"></div>
                    Brouillons
                    {expandedColumn === 'DRAFT' ? (
                      <ChevronUp className="h-4 w-4 ml-2 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 ml-2 text-gray-400" />
                    )}
                  </h3>
                  <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {draftCount}
                  </span>
                </div>
              </div>
              <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                {draftQuotes.map((quote) => (
                  <div key={quote.id} className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-orange-100 hover:border-orange-300 transition-all duration-300 ${quoteLoadingIds[quote.id] ? 'opacity-60 pointer-events-none' : 'cursor-pointer'}`}>
                    <div className="h-1" style={{background:'linear-gradient(90deg,#FB923C,#F97316)'}} />
                    <div className="p-5">
                    {/* Header avec numéro */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        {quote.quote_number != null && quote.quote_number !== '' && (
                          <span className="text-xs font-mono text-gray-400 block mb-2">
                            {quote.quote_number}
                          </span>
                        )}
                        <h4 className="font-semibold text-gray-900 text-base leading-tight">{quote.title || 'Devis sans titre'}</h4>
                      </div>
                    </div>
                    
                    {/* Client et description */}
                    <div className="space-y-2 mb-4">
                      {quote.client_name && (
                        <div className="flex items-center text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-2"></div>
                          <span>{quote.client_name}</span>
                        </div>
                      )}
                      {quote.description && (
                        <p className="text-xs text-gray-500 line-clamp-2 pl-3.5">{quote.description}</p>
                      )}
                    </div>
                    
                    {/* Montant */}
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-xl p-4 mb-4">
                      <div className="flex items-end justify-between">
                        <div>
                          <span className="text-xs text-gray-500 uppercase tracking-wider font-medium block mb-1">Montant</span>
                          <span className="text-2xl font-bold text-gray-900">{calculateQuoteTTC(quote)}€</span>
                          <span className="text-xs text-gray-500 ml-1.5">TTC</span>
                        </div>
                        <span className="text-xs text-gray-400 font-medium">
                          {new Date(quote.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </span>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex flex-col gap-1.5">
                      <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => sendQuote(quote.id)}
                        disabled={!!quoteLoadingIds[quote.id] || !hasValidQuoteItems(quote)}
                        title={!hasValidQuoteItems(quote) ? 'Ajoutez au moins une ligne pour pouvoir envoyer le devis.' : undefined}
                        className="flex-1 text-white rounded-xl h-9 font-medium shadow-sm hover:shadow-md transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{background:'linear-gradient(90deg,#2563EB,#7C3AED)'}}
                      >
                        {quoteLoadingIds[quote.id] === 'sending' ? (
                          <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Envoi...</>
                        ) : (
                          <><Check className="h-4 w-4 mr-1.5" />Envoyer</>
                        )}
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => editQuote(quote)}
                        disabled={!!quoteLoadingIds[quote.id]}
                        variant="outline"
                        className="flex-1 rounded-xl border-gray-200 hover:border-gray-300 hover:bg-gray-50 h-9 font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <Edit className="h-4 w-4 mr-1.5" />
                        Éditer
                      </Button>
                      </div>
                      <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => downloadQuotePDF(quote.id)}
                        variant="outline"
                        disabled={downloadingPdfId === quote.id}
                        className="flex-1 rounded-xl border-gray-200 hover:border-green-300 hover:bg-green-50 h-9 flex items-center justify-center gap-1.5 group"
                        title="Télécharger le PDF"
                      >
                        {downloadingPdfId === quote.id ? (
                          <Loader2 className="h-4 w-4 text-green-500 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4 text-gray-400 group-hover:text-green-500 transition-colors" />
                        )}
                        <span className="text-xs font-medium text-gray-500 group-hover:text-green-600">PDF</span>
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => deleteQuote(quote.id)}
                        disabled={!!quoteLoadingIds[quote.id]}
                        variant="outline"
                        className="flex-1 rounded-xl border-gray-200 hover:border-red-300 hover:bg-red-50 h-9 flex items-center justify-center gap-1.5 group disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {quoteLoadingIds[quote.id] === 'deleting' ? (
                          <Loader2 className="h-4 w-4 text-red-500 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4 text-gray-400 group-hover:text-red-500 transition-colors" />
                        )}
                        <span className="text-xs font-medium text-gray-500 group-hover:text-red-600">Supprimer</span>
                      </Button>
                      </div>
                      {!hasValidQuoteItems(quote) && (
                        <p className="text-xs text-amber-600 mt-0.5">Ajoutez au moins une ligne pour pouvoir envoyer le devis.</p>
                      )}
                    </div>
                    </div>
                  </div>
                ))}
                {draftQuotes.length === 0 && dataReady && (
                  <div className="text-center py-8">
                    <Calculator className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-500">Aucun brouillon</p>
                  </div>
                )}
                {!dataReady && [1,2,3].map(i => (
                  <div key={`sk-draft-${i}`} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 animate-pulse">
                    <div className="h-3 w-20 bg-gray-200 rounded mb-3"></div>
                    <div className="h-5 w-3/4 bg-gray-200 rounded mb-4"></div>
                    <div className="h-3 w-1/2 bg-gray-100 rounded mb-2"></div>
                    <div className="h-3 w-1/3 bg-gray-100 rounded"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Colonne 2: Envoyés */}
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl border-0 shadow-lg overflow-hidden">
              <div className="h-1" style={{background:'linear-gradient(90deg,#2563EB,#3B82F6)'}} />
              <div 
                className="p-4 border-b border-blue-100 cursor-pointer hover:bg-blue-50/40 transition-colors"
                onClick={() => setExpandedColumn(expandedColumn === 'SENT' ? null : 'SENT')}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                    Envoyés
                    {expandedColumn === 'SENT' ? (
                      <ChevronUp className="h-4 w-4 ml-2 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 ml-2 text-gray-400" />
                    )}
                  </h3>
                  <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {sentQuotes.length}
                  </span>
                </div>
              </div>
              <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                {sentQuotes.map((quote) => (
                  <div key={quote.id} className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-blue-100 hover:border-blue-300 transition-all duration-300 ${quoteLoadingIds[quote.id] ? 'opacity-60 pointer-events-none' : 'cursor-pointer'}`}>
                    <div className="h-1" style={{background:'linear-gradient(90deg,#2563EB,#3B82F6)'}} />
                    <div className="p-5">
                    {/* Header avec numéro */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        {quote.quote_number != null && quote.quote_number !== '' && (
                          <span className="text-xs font-mono text-gray-400 block mb-2">
                            {quote.quote_number}
                          </span>
                        )}
                        <h4 className="font-semibold text-gray-900 text-base leading-tight">{quote.title || 'Devis sans titre'}</h4>
                      </div>
                    </div>
                    
                    {/* Client et description */}
                    <div className="space-y-2 mb-4">
                      {quote.client_name && (
                        <div className="flex items-center text-sm text-gray-600">
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-2"></div>
                          <span>{quote.client_name}</span>
                        </div>
                      )}
                      {quote.description && (
                        <p className="text-xs text-gray-500 line-clamp-2 pl-3.5">{quote.description}</p>
                      )}
                    </div>
                    
                    {/* Montant */}
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-xl p-4 mb-4">
                      <div className="flex items-end justify-between">
                        <div>
                          <span className="text-xs text-gray-500 uppercase tracking-wider font-medium block mb-1">Montant</span>
                          <span className="text-2xl font-bold text-gray-900">{calculateQuoteTTC(quote)}€</span>
                          <span className="text-xs text-gray-500 ml-1.5">TTC</span>
                        </div>
                        <span className="text-xs text-gray-400 font-medium">
                          {new Date(quote.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </span>
                      </div>
                    </div>
                    
                    {/* Actions principales */}
                    <div className="flex gap-2 mb-2">
                      <Button 
                        size="sm" 
                        onClick={() => downloadQuotePDF(quote.id)}
                        variant="outline"
                        disabled={downloadingPdfId === quote.id}
                        className="rounded-xl border-gray-200 hover:border-green-300 hover:bg-green-50 h-9 w-9 flex items-center justify-center p-0 group"
                        title="Télécharger le PDF"
                      >
                        {downloadingPdfId === quote.id ? (
                          <Loader2 className="h-5 w-5 text-green-500 animate-spin" />
                        ) : (
                          <Download className="h-5 w-5 text-gray-400 group-hover:text-green-500 transition-colors" />
                        )}
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => acceptQuote(quote.id)}
                        disabled={!!quoteLoadingIds[quote.id]}
                        className="flex-1 text-white rounded-xl h-9 font-medium shadow-sm hover:shadow-md transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                        style={{background:'linear-gradient(90deg,#16A34A,#22C55E)'}}
                      >
                        {quoteLoadingIds[quote.id] === 'accepting' ? (
                          <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Traitement...</>
                        ) : (
                          <><Check className="h-4 w-4 mr-1.5" />Accepter</>
                        )}
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => rejectQuote(quote.id)}
                        disabled={!!quoteLoadingIds[quote.id]}
                        variant="outline"
                        className="flex-1 rounded-xl border-gray-200 hover:border-gray-300 hover:bg-gray-50 h-9 font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {quoteLoadingIds[quote.id] === 'rejecting' ? (
                          <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Traitement...</>
                        ) : (
                          <><X className="h-4 w-4 mr-1.5" />Refuser</>
                        )}
                      </Button>
                    </div>
                    
                    {/* Actions secondaires */}
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => deleteQuote(quote.id)}
                        disabled={!!quoteLoadingIds[quote.id]}
                        variant="outline"
                        className="flex-1 rounded-xl border-gray-200 hover:border-red-300 hover:bg-red-50 h-9 font-medium text-sm group disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {quoteLoadingIds[quote.id] === 'deleting' ? (
                          <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Suppression...</>
                        ) : (
                          <><Trash2 className="h-4 w-4 mr-1.5 group-hover:text-red-500 transition-colors" />Supprimer</>
                        )}
                      </Button>
                    </div>
                  </div>
                  </div>
                ))}
                {sentQuotes.length === 0 && dataReady && (
                  <div className="text-center py-8">
                    <Mail className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-500">Aucun devis envoyé</p>
                  </div>
                )}
                {!dataReady && [1,2].map(i => (
                  <div key={`sk-sent-${i}`} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 animate-pulse">
                    <div className="h-3 w-20 bg-gray-200 rounded mb-3"></div>
                    <div className="h-5 w-3/4 bg-gray-200 rounded mb-4"></div>
                    <div className="h-3 w-1/2 bg-gray-100 rounded mb-2"></div>
                    <div className="h-3 w-1/3 bg-gray-100 rounded"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Colonne 3: Convertis en Chantiers */}
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl border-0 shadow-lg overflow-hidden">
              <div className="h-1" style={{background:'linear-gradient(90deg,#7C3AED,#A855F7)'}} />
              <div 
                className="p-4 border-b border-violet-100 cursor-pointer hover:bg-violet-50/40 transition-colors"
                onClick={() => setExpandedColumn(expandedColumn === 'WORKSITE' ? null : 'WORKSITE')}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <div className="w-3 h-3 bg-violet-500 rounded-full mr-2"></div>
                    Chantiers
                    {expandedColumn === 'WORKSITE' ? (
                      <ChevronUp className="h-4 w-4 ml-2 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 ml-2 text-gray-400" />
                    )}
                  </h3>
                  <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {worksites.length}
                  </span>
                </div>
              </div>
              <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                {worksites.map((worksite) => (
                  <div key={worksite.id} className="bg-white rounded-xl overflow-hidden hover:shadow-lg transition-all relative border border-violet-100 hover:border-violet-300">
                    <div className="h-1" style={{background:'linear-gradient(90deg,#7C3AED,#A855F7)'}} />
                    <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium bg-violet-100 text-violet-700 px-2 py-1 rounded-full">
                          🏗️ Chantier
                        </span>
                        {hasSchedule(worksite.id) && (
                          <span className="text-xs font-medium bg-green-100 text-green-700 px-2 py-1 rounded-full">
                            ✓ Planifié
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {worksite.client_name && (
                      <div className="mb-3 p-2 bg-violet-50 rounded-lg">
                        <p className="text-sm font-semibold text-violet-900">
                          👤 {worksite.client_name}
                        </p>
                      </div>
                    )}
                    
                    <h4 className="font-bold text-gray-900 mb-2">{worksite.title}</h4>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{worksite.description}</p>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-gray-900">{worksite.amount || 0}€</span>
                        <Button 
                          size="sm"
                          onClick={() => openPlanningModal(worksite)}
                          className={`${hasSchedule(worksite.id) ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white rounded-lg text-xs px-3 py-1.5 h-8 flex-1 font-medium`}
                        >
                          📅 {hasSchedule(worksite.id) ? 'Planifié' : 'Planifier'}
                        </Button>
                        <Button 
                          size="sm"
                          onClick={() => deleteWorksite(worksite.id)}
                          className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-xs px-3 py-1.5 h-8"
                          title="Supprimer le chantier"
                        >
                          🗑️
                        </Button>
                      </div>
                      {worksite.quote_id && (
                        <Button 
                          size="sm"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const quoteRes = await http.get(`${API}/quotes/${worksite.quote_id}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              setSelectedWorksiteQuote({ ...worksite, quote: quoteRes.data });
                            } catch (error) {
                              console.error('Erreur chargement devis:', error);
                              alert('Erreur lors du chargement du devis');
                            }
                          }}
                          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300 rounded-lg text-xs px-3 py-1.5 h-8 font-medium"
                        >
                          📄 Voir le Devis
                        </Button>
                      )}
                    </div>
                    </div>
                  </div>
                ))}
                {worksites.length === 0 && dataReady && (
                  <div className="text-center py-8">
                    <Building className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-500">Aucun chantier</p>
                  </div>
                )}
                {!dataReady && [1,2].map(i => (
                  <div key={`sk-ws-${i}`} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 animate-pulse">
                    <div className="h-3 w-20 bg-gray-200 rounded mb-3"></div>
                    <div className="h-5 w-3/4 bg-gray-200 rounded mb-4"></div>
                    <div className="h-3 w-1/2 bg-gray-100 rounded"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Colonne 4: Facturation */}
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl border-0 shadow-lg overflow-hidden">
              <div className="h-1" style={{background:'linear-gradient(90deg,#16A34A,#22C55E)'}} />
              <div 
                className="p-4 border-b border-emerald-100 cursor-pointer hover:bg-emerald-50/40 transition-colors"
                onClick={() => setExpandedColumn(expandedColumn === 'INVOICED' ? null : 'INVOICED')}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 flex items-center">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full mr-2"></div>
                    Facturation
                    {expandedColumn === 'INVOICED' ? (
                      <ChevronUp className="h-4 w-4 ml-2 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 ml-2 text-gray-400" />
                    )}
                  </h3>
                  <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {worksites.filter(w => hasSchedule(w.id)).length}
                  </span>
                </div>
              </div>
              <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                {worksites.filter(w => hasSchedule(w.id)).map((worksite) => (
                  <div key={worksite.id} className="bg-white rounded-xl overflow-hidden hover:shadow-lg transition-all relative border border-emerald-100 hover:border-emerald-300">
                    <div className="h-1" style={{background:'linear-gradient(90deg,#16A34A,#22C55E)'}} />
                    <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">
                          💰 Facturé
                        </span>
                      </div>
                    </div>
                    
                    {worksite.client_name && (
                      <div className="mb-3 p-2 bg-emerald-50 rounded-lg">
                        <p className="text-sm font-semibold text-emerald-900">
                          👤 {worksite.client_name}
                        </p>
                      </div>
                    )}
                    
                    <h4 className="font-bold text-gray-900 mb-2">{worksite.title}</h4>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{worksite.description}</p>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-gray-900">{worksite.amount || 0}€</span>
                      </div>
                      {worksite.quote_id && (
                        <Button 
                          size="sm"
                          onClick={async () => {
                            try {
                              const token = localStorage.getItem('token');
                              const quoteRes = await http.get(`${API}/quotes/${worksite.quote_id}`, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              setSelectedWorksiteQuote({ ...worksite, quote: quoteRes.data });
                            } catch (error) {
                              console.error('Erreur chargement devis:', error);
                              alert('Erreur lors du chargement du devis');
                            }
                          }}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs px-3 py-1 h-7"
                        >
                          📄 Voir le Devis
                        </Button>
                      )}
                      <Button
                          size="sm"
                          onClick={() => navigate('/bureau/facturation')}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs px-3 py-1 h-7"
                        >
                          💰 Facturation
                        </Button>
                    </div>
                    </div>
                  </div>
                ))}
                {worksites.filter(w => hasSchedule(w.id)).length === 0 && (
                  <div className="text-center py-8">
                    <DollarSign className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm text-gray-500">Aucun chantier facturé</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section déroulante détaillée selon la colonne sélectionnée */}
          {expandedColumn && (
            <div className="bg-gradient-to-br from-white via-purple-50/30 to-blue-50/30 backdrop-blur-xl rounded-3xl border-2 border-purple-100 shadow-2xl p-8 animate-in slide-in-from-top duration-300">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-3xl font-bold text-gray-900">
                    {expandedColumn === 'DRAFT' && '📝 Détails Brouillons'}
                    {expandedColumn === 'SENT' && '📧 Détails Envoyés'}
                    {expandedColumn === 'WORKSITE' && '🏗️ Détails Chantiers'}
                    {expandedColumn === 'INVOICED' && '💰 Facturation'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {expandedColumn === 'INVOICED' ? 
                      `${worksites.filter(w => hasSchedule(w.id)).length} chantiers facturés` :
                      expandedColumn === 'WORKSITE' ?
                      `${worksites.length} chantiers` :
                      `${quotes.filter(q => {
                        if (expandedColumn === 'DRAFT') return q.status === 'DRAFT';
                        if (expandedColumn === 'SENT') return q.status === 'SENT' || q.status === 'PENDING';
                        return false;
                      }).length} devis • Cliquez sur un statut pour le modifier directement`
                    }
                  </p>
                </div>
                <Button
                  onClick={() => setExpandedColumn(null)}
                  variant="ghost"
                  size="sm"
                  className="rounded-xl hover:bg-red-50 hover:text-red-600"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>

              {/* Liste détaillée des devis ou chantiers de la colonne sélectionnée */}
              <div className="space-y-6">
                {expandedColumn === 'INVOICED' ? (
                  // Affichage des chantiers facturés
                  (() => {
                    const all = worksites.filter(w => hasSchedule(w.id));
                    const filtered = all;

                    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : null;

                    const renderCard = (worksite) => {
                      const invoiced = !!worksite.invoice_id;
                      const borderColor = invoiced ? 'border-emerald-200 hover:border-emerald-400' : 'border-red-200 hover:border-red-400';
                      const amountBg   = invoiced ? 'bg-gradient-to-br from-emerald-100 to-emerald-200 text-emerald-900' : 'bg-gradient-to-br from-red-100 to-red-200 text-red-900';
                      const amountLabel = invoiced ? 'text-emerald-700' : 'text-red-700';
                      const badge      = invoiced
                        ? { label: '💰 FACTURÉ', bg: 'bg-emerald-500' }
                        : { label: '⚠️ À FACTURER', bg: 'bg-red-500' };
                      const btnClass   = invoiced
                        ? 'bg-emerald-600 hover:bg-emerald-700'
                        : 'bg-red-600 hover:bg-red-700';
                      const btnLabel   = invoiced ? '💰 Facturation' : '📄 Créer la facture';
                      const goToFacturation = () => {
                        localStorage.setItem('facturation_search', worksite.title || '');
                        navigate('/bureau/facturation');
                      };
                      const startDate = fmtDate(worksite.start_date || worksite.scheduled_date || worksite.created_at);
                      const endDate   = fmtDate(worksite.end_date);

                      return (
                        <div key={worksite.id} className={`bg-white rounded-2xl p-6 border-2 ${borderColor} hover:shadow-xl transition-all duration-200`}>
                          <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 flex-wrap mb-2">
                                <h4 className="text-xl font-bold text-gray-900">{worksite.title || 'Chantier sans titre'}</h4>
                                {startDate && (
                                  <span className="flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">
                                    <Calendar className="h-3.5 w-3.5" />
                                    {startDate}{endDate ? ` → ${endDate}` : ''}
                                  </span>
                                )}
                                <span className={`ml-auto px-3 py-2 ${badge.bg} text-white text-sm font-bold rounded-xl shadow-lg`}>
                                  {badge.label}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="flex items-center text-sm font-medium text-gray-700 bg-gray-100 px-3 py-1 rounded-lg">
                                  <Users className="h-4 w-4 mr-1.5 text-gray-500" />
                                  {worksite.client_name || <span className="text-gray-400 italic">Client non renseigné</span>}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="space-y-4">
                              <div className={`${amountBg} rounded-xl p-4`}>
                                <div className={`text-xs font-semibold ${amountLabel} mb-1`}>MONTANT</div>
                                <div className="text-3xl font-bold">{worksite.amount?.toFixed(2) || '0.00'}€</div>
                              </div>
                            </div>
                            <div className="space-y-4">
                              {worksite.description && (
                                <div>
                                  <div className="text-xs font-semibold text-gray-500 mb-2">DESCRIPTION</div>
                                  <p className="text-sm text-gray-700 leading-relaxed">{worksite.description}</p>
                                </div>
                              )}
                              <div className="mt-2">
                                <Button size="sm" onClick={goToFacturation}
                                    className={`w-full text-white rounded-xl ${btnClass}`}>
                                    {btnLabel}
                                  </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    };

                    return (
                      <>
                        {/* Compteur simple — filtres e-facture supprimés */}
                        <div className="flex items-center gap-2 mb-6 px-4 py-3 bg-emerald-50 rounded-2xl border border-emerald-100">
                          <span className="text-sm font-semibold text-emerald-700">📋 {all.length} chantier{all.length > 1 ? 's' : ''} facturé{all.length > 1 ? 's' : ''}</span>
                        </div>

                        {/* Cartes filtrées */}
                        {filtered.length === 0 ? (
                          <div className="text-center py-12">
                            <DollarSign className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                            <p className="text-gray-500">Aucun chantier dans cette catégorie</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {filtered.map(w => renderCard(w))}
                          </div>
                        )}
                      </>
                    );
                  })()
                ) : expandedColumn === 'WORKSITE' ? (
                  // Affichage des chantiers avec filtrage par statut
                  <>
                    {/* Onglets de filtrage par statut */}
                    <div className="flex flex-wrap gap-2 mb-6 p-1 bg-gray-100 rounded-2xl">
                      {[
                        { key: 'ALL', label: 'Tous', icon: '📋', count: worksites.length },
                        { key: 'PLANNED', label: 'Planifiés', icon: '📅', count: worksites.filter(w => w.status === 'PLANNED').length },
                        { key: 'IN_PROGRESS', label: 'En cours', icon: '🔧', count: worksites.filter(w => w.status === 'IN_PROGRESS').length },
                        { key: 'COMPLETED', label: 'Terminés', icon: '✅', count: worksites.filter(w => w.status === 'COMPLETED').length },
                      ].map(tab => (
                        <button
                          key={tab.key}
                          onClick={() => setWorksiteStatusFilter(tab.key)}
                          className={`flex-1 min-w-[120px] px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
                            worksiteStatusFilter === tab.key
                              ? 'bg-white text-gray-900 shadow-lg'
                              : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                          }`}
                        >
                          <span className="mr-1">{tab.icon}</span> {tab.label}
                          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                            worksiteStatusFilter === tab.key
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-gray-200 text-gray-500'
                          }`}>{tab.count}</span>
                        </button>
                      ))}
                    </div>

                    {/* Liste filtrée des chantiers */}
                    <div className="space-y-6">
                    {worksites.filter(w => worksiteStatusFilter === 'ALL' || w.status === worksiteStatusFilter).length === 0 ? (
                      <div className="text-center py-12">
                        <Building className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">Aucun chantier dans cette catégorie</h4>
                        <p className="text-gray-500 text-sm">Changez de filtre pour voir d'autres chantiers</p>
                      </div>
                    ) : (
                    worksites.filter(w => worksiteStatusFilter === 'ALL' || w.status === worksiteStatusFilter).map((worksite) => (
                    <div key={worksite.id} className="bg-white rounded-2xl p-6 border-2 border-purple-100 hover:border-purple-300 hover:shadow-xl transition-all duration-200">
                      <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xl font-bold text-gray-900">{worksite.title || 'Chantier sans titre'}</h4>
                            <span className={`px-3 py-2 text-white text-sm font-bold rounded-xl shadow-lg ${
                              worksite.status === 'COMPLETED' ? 'bg-green-500' :
                              worksite.status === 'IN_PROGRESS' ? 'bg-blue-500' :
                              worksite.status === 'PLANNED' ? 'bg-purple-500' :
                              worksite.status === 'PENDING' ? 'bg-orange-500' : 'bg-gray-500'
                            }`}>
                              🏗️ {worksite.status === 'COMPLETED' ? 'TERMINÉ' : 
                                   worksite.status === 'IN_PROGRESS' ? 'EN COURS' :
                                   worksite.status === 'PLANNED' ? 'PLANIFIÉ' :
                                   worksite.status === 'PENDING' ? 'EN ATTENTE' : worksite.status}
                            </span>
                          </div>
                          <div className="flex items-center flex-wrap gap-2">
                            {worksite.client_name && (
                              <div className="flex items-center text-sm font-medium text-gray-700 bg-gray-100 px-3 py-1 rounded-lg">
                                <Users className="h-4 w-4 mr-1.5" />
                                {worksite.client_name}
                              </div>
                            )}
                            {worksite.clients?.telephone && (
                              <a href={`tel:${worksite.clients.telephone}`} className="flex items-center text-sm font-medium text-gray-700 bg-gray-100 px-3 py-1 rounded-lg hover:bg-gray-200 transition-colors">
                                <Phone className="h-4 w-4 mr-1.5" />
                                {worksite.clients.telephone}
                              </a>
                            )}
                            {worksite.clients?.email && (
                              <a href={`mailto:${worksite.clients.email}`} className="flex items-center text-sm font-medium text-gray-700 bg-gray-100 px-3 py-1 rounded-lg hover:bg-gray-200 transition-colors">
                                <Mail className="h-4 w-4 mr-1.5" />
                                {worksite.clients.email}
                              </a>
                            )}
                            {worksite.quotes?.quote_number && (
                              <div className="flex items-center text-sm font-medium text-purple-700 bg-purple-50 px-3 py-1 rounded-lg">
                                <FileText className="h-4 w-4 mr-1.5" />
                                Devis {worksite.quotes.quote_number}
                              </div>
                            )}
                            {worksite.address && (
                              <div className="flex items-center text-sm text-gray-600">
                                <MapPin className="h-4 w-4 mr-1" />
                                {worksite.address}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          {worksite.start_date && (
                            <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl p-4">
                              <div className="text-xs font-semibold text-purple-700 mb-1">DATES</div>
                              <div className="text-sm font-bold text-purple-900">
                                Début: {new Date(worksite.start_date).toLocaleDateString('fr-FR')}
                                {worksite.end_date && (
                                  <div className="mt-1">Fin: {new Date(worksite.end_date).toLocaleDateString('fr-FR')}</div>
                                )}
                              </div>
                            </div>
                          )}
                          {(worksite.amount != null && Number(worksite.amount) !== 0) && (
                            <div className="bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl p-4">
                              <div className="text-xs font-semibold text-emerald-700 mb-1">MONTANT</div>
                              <div className="text-2xl font-bold text-emerald-900">{Number(worksite.amount).toFixed(2)}€</div>
                            </div>
                          )}
                          {(worksite.budget != null && Number(worksite.budget) !== 0) && (
                            <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl p-4">
                              <div className="text-xs font-semibold text-blue-700 mb-1">BUDGET</div>
                              <div className="text-xl font-bold text-blue-900">{Number(worksite.budget).toFixed(2)}€</div>
                            </div>
                          )}
                          {(worksite.team_size != null && Number(worksite.team_size) > 0) && (
                            <div className="flex items-center text-sm font-medium text-gray-700 bg-gray-100 px-3 py-2 rounded-lg">
                              <Users className="h-4 w-4 mr-2" />
                              Équipe : {worksite.team_size} personne{Number(worksite.team_size) > 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                        <div className="space-y-4">
                          {worksite.description && (
                            <div>
                              <div className="text-xs font-semibold text-gray-500 mb-2">DESCRIPTION</div>
                              <p className="text-sm text-gray-700 leading-relaxed">{worksite.description}</p>
                            </div>
                          )}
                          {(worksite.progress != null && Number(worksite.progress) > 0) && (
                            <div>
                              <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-1">
                                <span>PROGRESSION</span>
                                <span className="text-gray-700">{worksite.progress}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2.5">
                                <div className="bg-gradient-to-r from-purple-500 to-blue-500 h-2.5 rounded-full transition-all" style={{ width: `${Math.min(100, Number(worksite.progress))}%` }}></div>
                              </div>
                            </div>
                          )}
                          {worksite.notes && (
                            <div>
                              <div className="text-xs font-semibold text-gray-500 mb-2">NOTES</div>
                              <p className="text-sm text-gray-700 leading-relaxed bg-amber-50 rounded-lg p-3 whitespace-pre-wrap">{worksite.notes}</p>
                            </div>
                          )}
                          {hasSchedule(worksite.id) && (
                            <div className="flex items-center text-sm font-medium text-emerald-700 bg-emerald-50 px-3 py-2 rounded-lg">
                              <Calendar className="h-4 w-4 mr-2" />
                              Planning créé
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2 text-xs text-gray-500 pt-3 border-t border-gray-100">
                            {worksite.created_at && (
                              <span className="bg-gray-100 px-2 py-1 rounded-md">Créé le {new Date(worksite.created_at).toLocaleDateString('fr-FR')}</span>
                            )}
                            {worksite.source && (
                              <span className="bg-gray-100 px-2 py-1 rounded-md">Source : {worksite.source === 'quote' ? 'Devis' : worksite.source}</span>
                            )}
                            {worksite.id && (
                              <span className="bg-gray-100 px-2 py-1 rounded-md font-mono">#{String(worksite.id).slice(0, 8)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Bouton voir le devis */}
                      {worksite.quote_id && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <Button
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const quoteRes = await http.get(`${API}/quotes/${worksite.quote_id}`, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                setSelectedWorksiteQuote({ ...worksite, quote: quoteRes.data });
                              } catch (error) {
                                console.error('Erreur chargement devis:', error);
                                alert('Erreur lors du chargement du devis');
                              }
                            }}
                            className="w-full bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white rounded-xl"
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            Voir le devis détaillé
                          </Button>
                        </div>
                      )}
                    </div>
                  )))}
                    </div>
                  </>
                ) : (
                  // Affichage des devis normaux
                  quotes.filter(q => {
                    if (expandedColumn === 'DRAFT') return q.status === 'DRAFT';
                    if (expandedColumn === 'SENT') return q.status === 'SENT' || q.status === 'PENDING';
                    if (expandedColumn === 'WORKSITE') return q.status === 'CONVERTED_TO_WORKSITE';
                    return false;
                  }).map((quote) => (
                  <div key={quote.id} className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-gray-300 hover:shadow-xl transition-all duration-200">
                    {/* Header avec titre et statut */}
                    <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100">
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xl font-bold text-gray-900">{quote.title || 'Devis sans titre'}</h4>
                          {quote.quote_number != null && quote.quote_number !== '' && (
                            <span className="px-3 py-2 bg-gradient-to-r from-blue-600 to-violet-600 text-white text-sm font-mono font-bold rounded-xl shadow-lg">
                              #{quote.quote_number}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-3">
                          {quote.client_name && (
                            <div className="flex items-center text-sm font-medium text-gray-700 bg-gray-100 px-3 py-1 rounded-lg">
                              <Users className="h-4 w-4 mr-1.5" />
                              {quote.client_name}
                            </div>
                          )}
                          <div className="flex items-center text-xs text-gray-500">
                            <Clock className="h-3.5 w-3.5 mr-1" />
                            {new Date(quote.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </div>
                          {quote.created_by_name && (
                            <div className="flex items-center text-xs font-medium text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-lg">
                              👤 {quote.created_by_name}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Statut modifiable directement */}
                      <div className="ml-4">
                        <label className="block text-xs font-medium text-gray-500 mb-1">Statut</label>
                        <select
                          value={quote.status}
                          onChange={async (e) => {
                            const newStatus = e.target.value;
                            const prevQuotes = quotes;
                            setQuotes(prev => prev.map(q => q.id === quote.id ? { ...q, status: newStatus } : q));
                            try {
                              await http.put(`${API}/quotes/${quote.id}`, {
                                client_id: quote.client_id,
                                title: quote.title,
                                description: quote.description,
                                amount: quote.amount,
                                status: newStatus,
                                items: quote.items || []
                              });
                              toast.success('Statut mis à jour', { id: `status-${quote.id}` });
                            } catch (error) {
                              setQuotes(prevQuotes);
                              console.error('Erreur changement statut:', error);
                              toast.error('Erreur lors du changement de statut');
                            }
                          }}
                          className="text-sm font-medium px-4 py-2 rounded-xl border-2 focus:border-gray-400 focus:ring-2 focus:ring-gray-400/20 transition-all cursor-pointer"
                          style={{
                            backgroundColor: quote.status === 'DRAFT' ? '#fef3c7' : 
                                           quote.status === 'SENT' ? '#dbeafe' : '#fce7f3',
                            color: quote.status === 'DRAFT' ? '#92400e' : 
                                  quote.status === 'SENT' ? '#1e40af' : '#9f1239'
                          }}
                        >
                          <option value="DRAFT">📋 Brouillon</option>
                          <option value="SENT">✉️ Envoyé</option>
                          <option value="REJECTED">❌ Refusé</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Colonne 1: Informations client et montant */}
                      <div className="space-y-4">
                        <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl p-4">
                          <div className="text-xs font-semibold text-gray-600 mb-1">MONTANT TOTAL TTC</div>
                          <div className="text-3xl font-bold text-black">{calculateQuoteTTC(quote)}€</div>
                        </div>
                        
                        {quote.client_email && (
                          <div className="flex items-center text-sm text-gray-700 bg-gray-50 rounded-lg p-3">
                            <Mail className="h-4 w-4 mr-2 text-gray-500" />
                            <span className="truncate">{quote.client_email}</span>
                          </div>
                        )}
                        {quote.client_phone && (
                          <div className="flex items-center text-sm text-gray-700 bg-gray-50 rounded-lg p-3">
                            <Phone className="h-4 w-4 mr-2 text-gray-500" />
                            {quote.client_phone}
                          </div>
                        )}
                      </div>

                      {/* Colonne 2: Description et Articles/Prestations */}
                      <div className="space-y-4">
                        {quote.description && (
                          <div>
                            <div className="text-xs font-semibold text-gray-500 mb-2">DESCRIPTION</div>
                            <p className="text-sm text-gray-700 leading-relaxed">{quote.description}</p>
                          </div>
                        )}
                        
                        {quote.items && quote.items.length > 0 && (
                          <div>
                            <div className="text-xs font-semibold text-gray-500 mb-2">ARTICLES / PRESTATIONS</div>
                            <div className="space-y-2">
                              {quote.items.map((item, idx) => (
                                <div key={idx} className="bg-gray-50 rounded-lg p-3 text-sm">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="font-medium text-gray-900">{item.name || 'Article sans nom'}</div>
                                      <div className="text-xs text-gray-600 mt-1">
                                        Qté: {item.quantity} × {item.price?.toFixed(2)}€
                                      </div>
                                    </div>
                                    <div className="font-bold text-black ml-2">
                                      {((item.quantity || 0) * (item.price || 0)).toFixed(2)}€
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Colonne 3: Actions */}
                      <div className="flex flex-col space-y-1.5">
                        {quote.status === 'DRAFT' && (
                          <>
                            <Button
                              onClick={() => editQuote(quote)}
                              className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white rounded-lg shadow-md px-3 py-1.5 text-sm"
                              size="sm"
                            >
                              <Edit className="h-3.5 w-3.5 mr-1.5" />
                              Modifier
                            </Button>
                            <Button
                              onClick={() => sendQuote(quote)}
                              className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white rounded-lg shadow-md px-3 py-1.5 text-sm"
                              size="sm"
                            >
                              <Mail className="h-3.5 w-3.5 mr-1.5" />
                              Envoyer
                            </Button>
                          </>
                        )}
                        <Button
                          onClick={() => alert(`Générer PDF pour ${quote.title}`)}
                          variant="outline"
                          className="rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-100 px-3 py-1.5 text-sm"
                          size="sm"
                        >
                          <FileText className="h-3.5 w-3.5 mr-1.5" />
                          PDF
                        </Button>
                        <Button
                          onClick={() => deleteQuote(quote.id)}
                          variant="outline"
                          className="rounded-lg border-2 border-gray-300 text-gray-600 hover:bg-gray-100 px-3 py-1.5 text-sm"
                          size="sm"
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1.5 text-red-500" />
                          Supprimer
                        </Button>
                        <Button
                          onClick={() => toggleQuoteHistory(quote.id)}
                          variant="outline"
                          className="rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-100 px-3 py-1.5 text-sm"
                          size="sm"
                        >
                          <History className="h-3.5 w-3.5 mr-1.5" />
                          {openQuoteHistoryId === quote.id ? 'Masquer historique' : 'Historique'}
                        </Button>
                      </div>
                    </div>

                    {/* Historique des modifications (DEV-010) */}
                    {openQuoteHistoryId === quote.id && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="text-xs font-semibold text-gray-500 mb-3">HISTORIQUE DES MODIFICATIONS</div>
                        {loadingQuoteHistoryId === quote.id ? (
                          <p className="text-sm text-gray-400 italic">Chargement...</p>
                        ) : (quoteHistoryLogs[quote.id] || []).length === 0 ? (
                          <p className="text-sm text-gray-400 italic">Aucune modification enregistrée pour ce devis.</p>
                        ) : (
                          <div className="space-y-3">
                            {(quoteHistoryLogs[quote.id] || []).map((log) => {
                              const changes = getQuoteHistoryChanges(log);
                              return (
                                <div key={log.id} className="bg-gray-50 rounded-lg p-3 text-sm">
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span className="font-medium text-gray-900">
                                      {QUOTE_HISTORY_ACTION_LABELS[log.action] || log.action}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {new Date(log.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  </div>
                                  <div className="text-xs text-gray-500 mb-2">
                                    Par <span className="font-medium text-gray-700">{log.actor_email || 'inconnu'}</span> ({log.actor_role || 'rôle inconnu'})
                                  </div>
                                  {changes.length > 0 && (
                                    <ul className="space-y-1.5">
                                      {changes.map((c, i) => {
                                        const style = {
                                          added: { border: 'border-green-200', bg: 'bg-green-50', badge: 'bg-green-100 text-green-700', label: 'Ajouté' },
                                          removed: { border: 'border-red-200', bg: 'bg-red-50', badge: 'bg-red-100 text-red-700', label: 'Supprimé' },
                                          modified: { border: 'border-amber-200', bg: 'bg-amber-50', badge: 'bg-amber-100 text-amber-700', label: 'Modifié' },
                                          field: { border: 'border-blue-200', bg: 'bg-blue-50', badge: 'bg-blue-100 text-blue-700', label: 'Modifié' },
                                        }[c.type] || { border: 'border-gray-200', bg: 'bg-gray-50', badge: 'bg-gray-100 text-gray-700', label: 'Modifié' };
                                        return (
                                          <li key={i} className={`text-xs text-gray-700 border-l-2 ${style.border} ${style.bg} rounded-r-lg px-2.5 py-1.5`}>
                                            <div className="flex items-center gap-2 mb-0.5">
                                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${style.badge}`}>{style.label}</span>
                                              <span className="font-medium text-gray-900">{c.field}</span>
                                            </div>
                                            {c.type === 'added' ? (
                                              <span>{c.after}</span>
                                            ) : c.type === 'removed' ? (
                                              <span className="line-through text-gray-500">{c.before}</span>
                                            ) : (
                                              <span>{c.before} <span className="text-gray-400">→</span> <span className="font-semibold text-gray-900">{c.after}</span></span>
                                            )}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  ))
                )}

                {expandedColumn !== 'INVOICED' && expandedColumn !== 'WORKSITE' && (
                  quotes.filter(q => {
                    if (expandedColumn === 'DRAFT') return q.status === 'DRAFT';
                    if (expandedColumn === 'SENT') return q.status === 'SENT' || q.status === 'PENDING';
                    return false;
                  }).length === 0 && (
                    <div className="text-center py-12">
                      <div className="text-gray-400 mb-4">
                        <FileText className="h-16 w-16 mx-auto" />
                      </div>
                      <p className="text-gray-500">Aucun devis dans cette catégorie</p>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* Actions rapides en bas */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-3xl p-6">
            <div className="flex flex-col lg:flex-row items-center justify-between space-y-4 lg:space-y-0">
              <div className="text-center lg:text-left">
                <h3 className="font-semibold text-gray-900 mb-1">Accélérez votre processus commercial</h3>
                <p className="text-sm text-gray-600">Templates, relances automatiques, et outils d'analyse pour optimiser vos ventes</p>
              </div>
              <div className="flex space-x-3">
                <Button 
                  onClick={() => {
                    setShowTemplates(true);
                    loadTemplates();
                  }}
                  className="bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-2xl"
                >
                  <FileBarChart className="h-4 w-4 mr-2" />
                  Templates Pro
                </Button>
                <Button 
                  onClick={() => {
                    alert('🚀 Analyse Performance Avancée\n\n' +
                      '📈 INDICATEURS CLÉS:\n' +
                      '• CA généré: 45,200€\n' +
                      '• Nombre devis envoyés: 23\n' +
                      '• Taux acceptation: 73.9%\n' +
                      '• Délai moyen réponse: 3.2 jours\n\n' +
                      '🎯 RECOMMANDATIONS:\n' +
                      '• Optimiser les devis plomberie (+23%)\n' +
                      '• Relancer les devis en attente\n' +
                      '• Templates pour électricité\n\n' +
                      'Rapport détaillé disponible...'
                    );
                  }}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-2xl"
                >
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Analyse Performance
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modale pour afficher le devis d'un chantier */}
      {selectedWorksiteQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Devis du Chantier</h2>
                <p className="text-sm text-gray-600 mt-1">{selectedWorksiteQuote.title}</p>
              </div>
              <button
                onClick={() => setSelectedWorksiteQuote(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Informations client */}
              {selectedWorksiteQuote.client_name && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Client</h3>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Users className="w-4 h-4" />
                    <span>{selectedWorksiteQuote.client_name}</span>
                  </div>
                </div>
              )}

              {/* Informations créateur du devis */}
              {selectedWorksiteQuote.quote?.created_by && (
                <div className="bg-blue-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Créé par</h3>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Users className="w-4 h-4" />
                    <span>
                      {selectedWorksiteQuote.quote.created_by.first_name} {selectedWorksiteQuote.quote.created_by.last_name}
                      {selectedWorksiteQuote.quote.created_by.email && (
                        <span className="text-sm text-gray-500 ml-2">({selectedWorksiteQuote.quote.created_by.email})</span>
                      )}
                    </span>
                  </div>
                </div>
              )}

              {/* Articles du devis */}
              {selectedWorksiteQuote.quote && selectedWorksiteQuote.quote.items && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Articles / Prestations</h3>
                  <div className="space-y-3">
                    {selectedWorksiteQuote.quote.items.map((item, index) => (
                      <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{item.name}</h4>
                            {item.description && (
                              <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                            )}
                            <div className="mt-2 grid grid-cols-3 gap-4 text-sm text-gray-600">
                              <div>
                                <span className="font-medium">Quantité:</span> {item.quantity}
                              </div>
                              <div>
                                <span className="font-medium">Prix unitaire:</span> {item.price?.toFixed(2)}€
                              </div>
                              <div>
                                <span className="font-medium">Total:</span> {((item.quantity || 0) * (item.price || 0)).toFixed(2)}€
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Montant total */}
              {selectedWorksiteQuote.quote && (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Montant Total</span>
                    <span className="text-3xl font-bold text-gray-900">
                      {selectedWorksiteQuote.quote.amount?.toFixed(2)}€
                    </span>
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedWorksiteQuote.quote?.description && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                    Description / Notes
                  </h4>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap">{selectedWorksiteQuote.quote.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Worksites List */}
      {activeTab === 'worksites' && (
        <div className="space-y-6">
          {/* Header avec statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-2xl p-4 shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Total Chantiers</p>
                  <p className="text-2xl font-bold text-black">{worksites.length}</p>
                </div>
                <Building className="h-8 w-8 text-gray-400" />
              </div>
            </div>
            <div className="bg-white rounded-2xl p-4 shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">En Cours</p>
                  <p className="text-2xl font-bold text-black">{worksites.filter(w => w.status === 'IN_PROGRESS').length}</p>
                </div>
                <Clock className="h-8 w-8 text-gray-400" />
              </div>
            </div>
            <div className="bg-white rounded-2xl p-4 shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Terminés</p>
                  <p className="text-2xl font-bold text-black">{worksites.filter(w => w.status === 'COMPLETED').length}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-gray-400" />
              </div>
            </div>
            <div className="bg-white rounded-2xl p-4 shadow-md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Budget Total</p>
                  <p className="text-2xl font-bold text-black">
                    {worksites.reduce((sum, w) => sum + (w.budget || 0), 0).toLocaleString()}€
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-gray-400" />
              </div>
            </div>
          </div>

          {/* Bouton Nouveau Chantier */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Mes Chantiers</h2>
            <Button
              onClick={() => {
                setShowWorksiteForm(true);
                setEditingWorksite(null);
                setWorksiteFormData({
                  title: '',
                  description: '',
                  address: '',
                  client_id: '',
                  status: 'PLANNED',
                  progress: 0,
                  start_date: '',
                  end_date: '',
                  budget: '',
                  team_size: 1,
                  notes: ''
                });
              }}
              className="bg-black hover:bg-gray-800 text-white rounded-xl px-6 py-3"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouveau Chantier
            </Button>
          </div>

          {/* Liste des chantiers */}
          {worksites.length === 0 ? (
            <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
              <CardContent className="p-12 text-center">
                <Building className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun chantier</h3>
                <p className="text-gray-500">Créez votre premier chantier ou convertissez un devis accepté.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {worksites.map((worksite) => (
                <Card key={worksite.id} className="bg-white rounded-2xl border-2 border-gray-100 hover:border-gray-300 hover:shadow-xl transition-all duration-200 cursor-pointer" onClick={() => setViewingWorksite(worksite)}>
                  <CardContent className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">{worksite.title}</h3>
                          <span className={`px-2 py-1 rounded-lg text-xs font-semibold ${
                            worksite.status === 'PLANNED' ? 'bg-gray-100 text-gray-700' :
                            worksite.status === 'IN_PROGRESS' ? 'bg-gray-200 text-black' :
                            worksite.status === 'ON_HOLD' ? 'bg-gray-100 text-gray-600' :
                            worksite.status === 'COMPLETED' ? 'bg-black text-white' : 'bg-gray-50 text-gray-500'
                          }`}>
                            {worksite.status === 'PLANNED' ? '📋 Planifié' :
                             worksite.status === 'IN_PROGRESS' ? '🔧 En cours' :
                             worksite.status === 'ON_HOLD' ? '⏸️ En pause' :
                             worksite.status === 'COMPLETED' ? '✅ Terminé' : worksite.status}
                          </span>
                        </div>
                        {worksite.clients?.name && (
                          <p className="text-sm text-gray-600 flex items-center mb-2">
                            <Users className="h-4 w-4 mr-1" />
                            {worksite.clients.name}
                          </p>
                        )}
                        {worksite.address && (
                          <p className="text-sm text-gray-600 flex items-center mb-3">
                            <MapPin className="h-4 w-4 mr-1" />
                            {worksite.address}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Progression */}
                    {worksite.progress !== null && worksite.progress !== undefined && (
                      <div className="mb-4">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>Avancement</span>
                          <span className="font-semibold">{worksite.progress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-black rounded-full h-2 transition-all duration-300"
                            style={{ width: `${worksite.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {/* Infos clés */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {!!worksite.budget && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Budget</p>
                          <p className="text-sm font-bold text-black">{worksite.budget.toLocaleString()}€</p>
                        </div>
                      )}
                      {!!worksite.team_size && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Équipe</p>
                          <p className="text-sm font-bold text-black">{worksite.team_size} personne{worksite.team_size > 1 ? 's' : ''}</p>
                        </div>
                      )}
                      {worksite.start_date && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Début</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {new Date(worksite.start_date).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      )}
                      {worksite.end_date && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">Fin prévue</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {new Date(worksite.end_date).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex space-x-2" onClick={e => e.stopPropagation()}>
                      <Button
                        size="sm"
                        onClick={async () => {
                          setEditingWorksite(worksite);
                          setWorksiteFormData({
                            title: worksite.title || '',
                            description: worksite.description || '',
                            address: worksite.address || '',
                            client_id: worksite.client_id || '',
                            status: worksite.status || 'PLANNED',
                            progress: worksite.progress || 0,
                            start_date: worksite.start_date || '',
                            end_date: worksite.end_date || '',
                            budget: worksite.budget || '',
                            team_size: worksite.team_size || 1,
                            notes: worksite.notes || ''
                          });
                          // Charger les techniciens déjà assignés via les schedules
                          try {
                            const res = await http.get(`${API}/schedules?worksite_id=${worksite.id}`);
                            const wsSchedules = res.data || [];
                            const techIds = [...new Set(wsSchedules.filter(s => s.collaborator_id).map(s => s.collaborator_id))];
                            setEditTechnicians(techIds);
                          } catch { setEditTechnicians([]); }
                          setShowWorksiteForm(true);
                        }}
                        className="flex-1 bg-black hover:bg-gray-800 text-white rounded-lg"
                      >
                        <Edit className="h-3.5 w-3.5 mr-1" />
                        Modifier
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => deleteWorksite(worksite.id)}
                        variant="outline"
                        className="rounded-lg border-2 border-gray-300 text-gray-600 hover:bg-gray-100"
                      >
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal détail chantier (lecture seule) — 2e composant */}
      {viewingWorksite && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setViewingWorksite(null)}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">{viewingWorksite.title}</h2>
                <span className={`inline-block mt-2 px-3 py-1 rounded-lg text-xs font-bold ${
                  viewingWorksite.status === 'PLANNED' ? 'bg-gray-100 text-gray-700' :
                  viewingWorksite.status === 'IN_PROGRESS' ? 'bg-gray-200 text-black' :
                  viewingWorksite.status === 'ON_HOLD' ? 'bg-gray-100 text-gray-600' :
                  viewingWorksite.status === 'COMPLETED' ? 'bg-black text-white' : 'bg-gray-50 text-gray-500'
                }`}>
                  {viewingWorksite.status === 'PLANNED' ? '📋 Planifié' :
                   viewingWorksite.status === 'IN_PROGRESS' ? '🔧 En cours' :
                   viewingWorksite.status === 'ON_HOLD' ? '⏸️ En pause' :
                   viewingWorksite.status === 'COMPLETED' ? '✅ Terminé' : viewingWorksite.status}
                </span>
              </div>
              <button onClick={() => setViewingWorksite(null)} className="p-2 hover:bg-gray-100 rounded-full transition">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              {viewingWorksite.clients?.name && (
                <div className="flex items-center text-gray-700">
                  <Users className="h-5 w-5 mr-3 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400 font-medium">Client</p>
                    <p className="font-semibold">{viewingWorksite.clients.name}</p>
                  </div>
                </div>
              )}
              {viewingWorksite.address && (
                <div className="flex items-center text-gray-700">
                  <MapPin className="h-5 w-5 mr-3 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400 font-medium">Adresse</p>
                    <p className="font-semibold">{viewingWorksite.address}</p>
                  </div>
                </div>
              )}
              {viewingWorksite.description && (
                <div className="flex items-start text-gray-700">
                  <FileText className="h-5 w-5 mr-3 mt-0.5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-400 font-medium">Description</p>
                    <p className="text-sm leading-relaxed">{viewingWorksite.description}</p>
                  </div>
                </div>
              )}
              {viewingWorksite.progress !== null && viewingWorksite.progress !== undefined && (
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span className="font-semibold">Avancement</span>
                    <span className="font-bold">{viewingWorksite.progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div className="bg-black rounded-full h-3 transition-all duration-500" style={{ width: `${viewingWorksite.progress}%` }}></div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                {!!viewingWorksite.budget && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">Budget</p>
                    <p className="text-lg font-bold text-black">{parseFloat(viewingWorksite.budget).toLocaleString()}€</p>
                  </div>
                )}
                {!!viewingWorksite.team_size && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">Équipe</p>
                    <p className="text-lg font-bold text-black">{viewingWorksite.team_size} personne{viewingWorksite.team_size > 1 ? 's' : ''}</p>
                  </div>
                )}
                {viewingWorksite.start_date && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">Date de début</p>
                    <p className="text-sm font-bold text-gray-900">{new Date(viewingWorksite.start_date).toLocaleDateString('fr-FR')}</p>
                  </div>
                )}
                {viewingWorksite.end_date && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-400 mb-1">Date de fin</p>
                    <p className="text-sm font-bold text-gray-900">{new Date(viewingWorksite.end_date).toLocaleDateString('fr-FR')}</p>
                  </div>
                )}
              </div>
              {/* Équipe assignée */}
              {viewingWorksiteTeam.length > 0 && (
                <div>
                  <div className="flex items-center mb-3">
                    <Users className="h-5 w-5 mr-3 text-gray-400" />
                    <p className="text-xs text-gray-400 font-medium">Équipe assignée</p>
                  </div>
                  <div className="space-y-2">
                    {viewingWorksiteTeam.map(m => (
                      <div key={m.id} className="flex items-center bg-gray-50 rounded-xl px-4 py-3">
                        <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-xs font-bold mr-3">
                          {m.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900">{m.name}</p>
                          {m.role && <p className="text-xs text-gray-400">{m.role}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {viewingWorksiteTeam.length === 0 && (
                <div className="flex items-center text-gray-400">
                  <Users className="h-5 w-5 mr-3" />
                  <p className="text-sm italic">Aucun collaborateur assigné</p>
                </div>
              )}
              {viewingWorksite.notes && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <p className="text-xs text-yellow-600 font-medium mb-1">Notes internes</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{viewingWorksite.notes}</p>
                </div>
              )}
            </div>
            <div className="flex space-x-3 p-6 border-t border-gray-100">
              <Button
                onClick={() => {
                  const ws = viewingWorksite;
                  setViewingWorksite(null);
                  setEditingWorksite(ws);
                  setWorksiteFormData({
                    title: ws.title || '',
                    description: ws.description || '',
                    address: ws.address || '',
                    client_id: ws.client_id || '',
                    status: ws.status || 'PLANNED',
                    progress: ws.progress || 0,
                    start_date: ws.start_date || '',
                    end_date: ws.end_date || '',
                    budget: ws.budget || '',
                    team_size: ws.team_size || 1,
                    notes: ws.notes || ''
                  });
                  setShowWorksiteForm(true);
                }}
                className="flex-1 bg-black hover:bg-gray-800 text-white rounded-xl"
              >
                <Edit className="h-4 w-4 mr-2" />
                Modifier
              </Button>
              <Button
                onClick={() => setViewingWorksite(null)}
                variant="outline"
                className="flex-1 rounded-xl border-2 border-gray-200"
              >
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Trash List - Corbeille avec devis TRASHED */}
      {activeTab === 'trash' && (
        <div className="space-y-4">
          {/* Bouton vider la corbeille */}
          {trashedQuotes.length > 0 && (
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={async () => {
                  if (!(await confirm(`Supprimer définitivement les ${trashedQuotes.length} devis de la corbeille ?`))) return;
                  const prevQuotes = quotes;
                  const trashedIds = trashedQuotes.map(q => q.id);
                  setQuotes(prev => prev.filter(q => q.status !== 'TRASHED'));
                  toast.success('Corbeille vidée', { id: 'empty-trash' });
                  const deletePromises = trashedIds.map(id =>
                    http.delete(`${API}/quotes/${id}/permanent`).catch(err => console.error(err))
                  );
                  const results = await Promise.allSettled(deletePromises);
                  const failures = results.filter(r => r.status === 'rejected').length;
                  if (failures > 0) {
                    toast.error(`${failures} devis n'ont pas pu être supprimés`);
                    http.get(`${API}/quotes`).then(r => setQuotes(r.data.data || [])).catch(() => {});
                  }
                }}
                variant="outline"
                className="rounded-xl border-red-300 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Vider la corbeille
              </Button>
            </div>
          )}

          <div className="grid gap-4">
            {trashedQuotes.length === 0 ? (
              <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
                <CardContent className="p-12 text-center">
                  <Trash2 className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Corbeille vide</h3>
                  <p className="text-gray-500">Les devis supprimés apparaîtront ici et pourront être restaurés.</p>
                </CardContent>
              </Card>
            ) : (
              trashedQuotes.map((quote) => (
                <Card key={quote.id} className="bg-red-50/80 backdrop-blur-xl rounded-3xl border border-red-200 shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          {quote.quote_number != null && quote.quote_number !== '' && (
                            <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-1 rounded-lg">
                              {quote.quote_number}
                            </span>
                          )}
                          <span className="px-2 py-1 rounded-lg text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                            🗑️ Corbeille
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">{quote.title || 'Devis sans titre'}</h3>
                        {quote.client_name && (
                          <div className="flex items-center text-sm text-gray-600 mb-2">
                            <User className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                            {quote.client_name}
                          </div>
                        )}
                        {quote.description && (
                          <p className="text-sm text-gray-500 mb-3">{quote.description}</p>
                        )}
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="text-gray-500">
                            Créé le: {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                          </span>
                          {quote.updated_at && (
                            <span className="text-red-500">
                              Supprimé le: {new Date(quote.updated_at).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                          <span className="font-semibold text-gray-900">
                            {calculateQuoteTTC(quote)}€ TTC
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <Button 
                          size="sm"
                          onClick={() => restoreFromTrash(quote.id)}
                          disabled={!!quoteLoadingIds[quote.id]}
                          className="bg-green-600 hover:bg-green-700 text-white rounded-xl disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {quoteLoadingIds[quote.id] === 'restoring' ? (
                            <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Restauration...</>
                          ) : (
                            <><ArrowLeft className="h-4 w-4 mr-1" />Restaurer</>
                          )}
                        </Button>
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => permanentDeleteQuote(quote.id)}
                          disabled={!!quoteLoadingIds[quote.id]}
                          className="rounded-xl border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {quoteLoadingIds[quote.id] === 'deleting' ? (
                            <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Suppression...</>
                          ) : (
                            <><Trash2 className="h-4 w-4 mr-1" />Supprimer</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      )}

      {/* Archive - Devis Refusés + Archivés */}
      {activeTab === 'archive' && (
        <div className="grid gap-4">
          {rejectedQuotes.length === 0 && archivedQuotes.length === 0 ? (
            <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
              <CardContent className="p-12 text-center">
                <Archive className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun devis archivé</h3>
                <p className="text-gray-500">Les devis refusés et archivés apparaîtront ici.</p>
              </CardContent>
            </Card>
          ) : (
            <>
            {/* Section ARCHIVED (verrouillés) */}
            {archivedQuotes.map((quote) => (
              <Card key={quote.id} className="bg-gray-50/80 backdrop-blur-xl rounded-3xl border border-gray-300 shadow-lg hover:shadow-xl transition-all duration-300 opacity-80">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        {quote.quote_number != null && quote.quote_number !== '' && (
                          <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-1 rounded-lg">
                            {quote.quote_number}
                          </span>
                        )}
                        <span className="px-2 py-1 rounded-lg text-xs font-semibold bg-gray-800 text-white border border-gray-700">
                          🔒 Archivé
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{quote.title || 'Devis sans titre'}</h3>
                      {quote.client_name && (
                        <div className="flex items-center text-sm text-gray-600 mb-2">
                          <User className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                          {quote.client_name}
                        </div>
                      )}
                      {quote.description && (
                        <p className="text-sm text-gray-500 mb-3">{quote.description}</p>
                      )}
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="text-gray-500">
                          Créé le: {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                        </span>
                        {quote.updated_at && (
                          <span className="text-gray-500">
                            Archivé le: {new Date(quote.updated_at).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                        <span className="font-semibold text-gray-900">
                          {calculateQuoteTTC(quote)}€ TTC
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-2 italic">🔒 Ce devis est verrouillé. Restaurez-le pour le modifier.</p>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Button 
                        size="sm"
                        disabled={!!quoteLoadingIds[quote.id]}
                        onClick={async () => {
                          if (quoteLoadingIds[quote.id]) return;
                          setQuoteLoadingIds(prev => ({ ...prev, [quote.id]: 'restoring' }));
                          const prevQuotes = quotes;
                          setQuotes(prev => prev.map(q => q.id === quote.id ? { ...q, status: 'DRAFT' } : q));
                          try {
                            await http.post(`${API}/quotes/${quote.id}/restore`);
                            toast.success('Devis restauré en brouillon', { id: `restore-${quote.id}` });
                          } catch (error) {
                            setQuotes(prevQuotes);
                            toast.error(error.response?.data?.detail || 'Erreur lors de la restauration');
                          } finally {
                            setQuoteLoadingIds(prev => ({ ...prev, [quote.id]: false }));
                          }
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white rounded-xl disabled:opacity-60"
                      >
                        {quoteLoadingIds[quote.id] === 'restoring' ? (
                          <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Restauration...</>
                        ) : (
                          <><ArrowLeft className="h-4 w-4 mr-1" />Restaurer</>
                        )}
                      </Button>
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => downloadQuotePDF(quote.id)}
                        className="rounded-xl border-gray-300 hover:bg-gray-100"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        PDF
                      </Button>
                      <Button 
                        size="sm"
                        variant="outline"
                        disabled={!!quoteLoadingIds[quote.id]}
                        onClick={async () => {
                          if (quoteLoadingIds[quote.id]) return;
                          setQuoteLoadingIds(prev => ({ ...prev, [quote.id]: 'deleting' }));
                          const prevQuotes = quotes;
                          setQuotes(prev => prev.map(q => q.id === quote.id ? { ...q, status: 'TRASHED' } : q));
                          try {
                            await http.delete(`${API}/quotes/${quote.id}`);
                            toast.success('Devis envoyé dans la corbeille', { id: `trash-${quote.id}` });
                          } catch (error) {
                            setQuotes(prevQuotes);
                            toast.error(error.response?.data?.detail || 'Erreur lors de la mise en corbeille');
                          } finally {
                            setQuoteLoadingIds(prev => ({ ...prev, [quote.id]: false }));
                          }
                        }}
                        className="rounded-xl border-red-300 hover:bg-red-50 text-red-600 hover:text-red-700 disabled:opacity-60"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {/* Section REJECTED (non verrouillés, peuvent être archivés ou restaurés) */}
            {rejectedQuotes.map((quote) => (
              <Card key={quote.id} className="bg-red-50/80 backdrop-blur-xl rounded-3xl border border-red-200 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        {quote.quote_number != null && quote.quote_number !== '' && (
                          <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2 py-1 rounded-lg">
                            {quote.quote_number}
                          </span>
                        )}
                        <span className="px-2 py-1 rounded-lg text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                          ❌ Refusé
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{quote.title || 'Devis sans titre'}</h3>
                      {quote.client_name && (
                        <div className="flex items-center text-sm text-gray-600 mb-2">
                          <User className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                          {quote.client_name}
                        </div>
                      )}
                      {quote.description && (
                        <p className="text-sm text-gray-500 mb-3">{quote.description}</p>
                      )}
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="text-gray-500">
                          Créé le: {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                        </span>
                        {quote.updated_at && (
                          <span className="text-red-500">
                            Refusé le: {new Date(quote.updated_at).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                        <span className="font-semibold text-gray-900">
                          {calculateQuoteTTC(quote)}€ TTC
                        </span>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Button 
                        size="sm"
                        disabled={!!quoteLoadingIds[quote.id]}
                        onClick={async () => {
                          if (quoteLoadingIds[quote.id]) return;
                          setQuoteLoadingIds(prev => ({ ...prev, [quote.id]: 'restoring' }));
                          const prevQuotes = quotes;
                          setQuotes(prev => prev.map(q => q.id === quote.id ? { ...q, status: 'DRAFT' } : q));
                          try {
                            await http.post(`${API}/quotes/${quote.id}/restore`);
                            toast.success('Devis restauré en brouillon', { id: `restore-${quote.id}` });
                          } catch (error) {
                            setQuotes(prevQuotes);
                            toast.error(error.response?.data?.detail || 'Erreur lors de la restauration');
                          } finally {
                            setQuoteLoadingIds(prev => ({ ...prev, [quote.id]: false }));
                          }
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white rounded-xl disabled:opacity-60"
                      >
                        {quoteLoadingIds[quote.id] === 'restoring' ? (
                          <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Restauration...</>
                        ) : (
                          <><ArrowLeft className="h-4 w-4 mr-1" />Restaurer</>
                        )}
                      </Button>
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => downloadQuotePDF(quote.id)}
                        className="rounded-xl border-gray-300 hover:bg-gray-100"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Voir
                      </Button>
                      <Button 
                        size="sm"
                        variant="outline"
                        disabled={!!quoteLoadingIds[quote.id]}
                        onClick={async () => {
                          if (quoteLoadingIds[quote.id]) return;
                          setQuoteLoadingIds(prev => ({ ...prev, [quote.id]: 'deleting' }));
                          const prevQuotes = quotes;
                          setQuotes(prev => prev.map(q => q.id === quote.id ? { ...q, status: 'TRASHED' } : q));
                          try {
                            await http.delete(`${API}/quotes/${quote.id}`);
                            toast.success('Devis envoyé dans la corbeille', { id: `trash-${quote.id}` });
                          } catch (error) {
                            setQuotes(prevQuotes);
                            toast.error(error.response?.data?.detail || 'Erreur lors de la mise en corbeille');
                          } finally {
                            setQuoteLoadingIds(prev => ({ ...prev, [quote.id]: false }));
                          }
                        }}
                        className="rounded-xl border-red-300 hover:bg-red-50 text-red-600 hover:text-red-700 disabled:opacity-60"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            </>
          )}
        </div>
      )}

      {/* NEW: Templates Management Page */}
      {showTemplates && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Gestion des Templates</h2>
                  <p className="text-gray-600">Créez et gérez vos modèles de devis personnalisés</p>
                </div>
                <div className="flex space-x-3">
                  <Button
                    onClick={() => setShowTemplateForm(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-4 py-2"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nouveau Template
                  </Button>
                  <Button
                    onClick={() => setShowTemplates(false)}
                    variant="outline"
                    className="rounded-xl px-4 py-2"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Fermer
                  </Button>
                </div>
              </div>

              {/* Templates Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {templates.map((template) => (
                  <Card key={template.id} className="bg-white border border-gray-200 rounded-2xl hover:shadow-lg transition-all duration-200">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">{template.name}</h3>
                          <p className="text-sm text-gray-600 mb-2">{template.description}</p>
                          <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                            {template.category}
                          </span>
                        </div>
                      </div>
                      
                      {/* Template Items Preview */}
                      <div className="bg-gray-50 rounded-lg p-3 mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Articles ({template.items?.length || 0})</h4>
                        {template.items?.slice(0, 2).map((item, idx) => (
                          <div key={idx} className="text-xs text-gray-600 flex justify-between">
                            <span>{item.name}</span>
                            <span>{item.unit_price}€</span>
                          </div>
                        ))}
                        {template.items?.length > 2 && (
                          <div className="text-xs text-gray-500 mt-1">+{template.items.length - 2} autres...</div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex space-x-2">
                        <Button
                          onClick={() => useTemplate(template)}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm py-2"
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Utiliser
                        </Button>
                        <Button
                          onClick={() => editTemplate(template)}
                          variant="outline"
                          className="px-3 py-2 border-blue-200 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          onClick={() => deleteTemplate(template.id)}
                          variant="outline"
                          className="px-3 py-2 border-red-200 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Empty State */}
              {templates.length === 0 && (
                <div className="text-center py-12">
                  <FileBarChart className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Aucun template créé</h3>
                  <p className="text-gray-600 mb-4">Créez votre premier template pour accélérer vos devis</p>
                  <Button
                    onClick={() => setShowTemplateForm(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-6 py-3"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Créer un Template
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* NEW: Template Creation Form */}
      {showTemplateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {isEditingTemplate ? 'Modifier le Template' : 'Créer un Template'}
                  </h2>
                  <p className="text-gray-600">
                    {isEditingTemplate ? 'Modifiez votre modèle de devis' : 'Définissez votre modèle de devis personnalisé'}
                  </p>
                </div>
                <Button
                  onClick={() => setShowTemplateForm(false)}
                  variant="outline"
                  className="rounded-xl px-4 py-2"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom du Template</label>
                    <Input
                      placeholder="Ex: Devis Plomberie Standard"
                      value={templateFormData.name}
                      onChange={(e) => setTemplateFormData(prev => ({...prev, name: e.target.value}))}
                      className="rounded-xl border-gray-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
                    <select
                      value={templateFormData.category}
                      onChange={(e) => setTemplateFormData(prev => ({...prev, category: e.target.value}))}
                      className="w-full rounded-xl border border-gray-200 px-3 py-2"
                    >
                      <option value="standard">Standard</option>
                      <option value="plomberie">Plomberie</option>
                      <option value="electricite">Électricité</option>
                      <option value="menuiserie">Menuiserie</option>
                      <option value="renovation">Rénovation</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    placeholder="Décrivez ce template et son utilisation..."
                    value={templateFormData.description}
                    onChange={(e) => setTemplateFormData(prev => ({...prev, description: e.target.value}))}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 h-20 resize-none"
                  />
                </div>

                {/* Items */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <label className="block text-sm font-medium text-gray-700">Articles du Template</label>
                    <Button
                      onClick={addTemplateItem}
                      variant="outline"
                      className="rounded-lg text-sm px-3 py-1"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Ajouter Article
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {templateFormData.items.map((item, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <div className="grid md:grid-cols-4 gap-3 items-end">
                          <div className="relative">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Nom</label>
                            <Input
                              placeholder="Ex: Main d'œuvre"
                              value={item.name}
                              onChange={(e) => updateTemplateItem(index, 'name', e.target.value)}
                              onFocus={() => item.name && setTemplateItemSuggestions(prev => ({...prev, [index]: true}))}
                              className="rounded-lg text-sm"
                            />
                            
                            {/* Suggestions du catalogue */}
                            {templateItemSuggestions[index] && getFilteredTemplateProducts(index).length > 0 && (
                              <div className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                {getFilteredTemplateProducts(index).map(product => (
                                  <button
                                    key={product.id}
                                    type="button"
                                    onClick={() => selectTemplateProduct(index, product)}
                                    className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-b last:border-0"
                                  >
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <div className="font-medium text-gray-900 text-sm">{product.name}</div>
                                        {product.description && (
                                          <div className="text-xs text-gray-600 mt-0.5">{product.description}</div>
                                        )}
                                        <div className="text-xs text-gray-500">{product.unit}</div>
                                      </div>
                                      <div className="text-sm font-semibold text-blue-600">{product.price}€</div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                            
                            {item.name && !getFilteredTemplateProducts(index).length && templateItemSuggestions[index] && (
                              <p className="text-xs text-blue-600 mt-1 flex items-center">
                                <Plus className="h-3 w-3 mr-1 inline" />
                                Nouvel article personnalisé
                              </p>
                            )}
                          </div>
                          <div className="relative">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                            <Input
                              placeholder="Description détaillée"
                              value={item.description}
                              onChange={(e) => updateTemplateItem(index, 'description', e.target.value)}
                              onFocus={() => item.description && setTemplateDescSuggestions(prev => ({...prev, [index]: true}))}
                              className="rounded-lg text-sm"
                            />
                            
                            {/* Suggestions du catalogue par description */}
                            {templateDescSuggestions[index] && getFilteredTemplateProductsByDesc(index).length > 0 && (
                              <div className="absolute z-50 w-full mt-1 bg-white border rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                {getFilteredTemplateProductsByDesc(index).map(product => (
                                  <button
                                    key={product.id}
                                    type="button"
                                    onClick={() => selectTemplateProductByDesc(index, product)}
                                    className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-b last:border-0"
                                  >
                                    <div className="flex justify-between items-start">
                                      <div>
                                        <div className="font-medium text-gray-900 text-sm">{product.name}</div>
                                        {product.description && (
                                          <div className="text-xs text-gray-600 mt-0.5">{product.description}</div>
                                        )}
                                        <div className="text-xs text-gray-500">{product.unit}</div>
                                      </div>
                                      <div className="text-sm font-semibold text-blue-600">{product.price}€</div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Prix Unitaire (€)</label>
                            <Input
                              type="number"
                              placeholder="0"
                              value={item.unit_price}
                              onChange={(e) => updateTemplateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                              className="rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Qté par défaut</label>
                            <div className="flex space-x-1">
                              <Input
                                type="number"
                                min="1"
                                value={item.default_quantity}
                                onChange={(e) => updateTemplateItem(index, 'default_quantity', parseInt(e.target.value) || 1)}
                                className="rounded-lg text-sm"
                              />
                              <Button
                                onClick={() => removeTemplateItem(index)}
                                variant="outline"
                                className="px-2 py-1 border-red-200 text-red-600 hover:bg-red-50 rounded-lg"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total du template */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 font-medium">Total estimé du template :</span>
                    <span className="text-2xl font-bold text-purple-600">
                      {templateFormData.items.reduce((sum, item) => 
                        sum + ((item.unit_price || 0) * (item.default_quantity || 1)), 0
                      ).toFixed(2)}€
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Basé sur {templateFormData.items.length} article{templateFormData.items.length > 1 ? 's' : ''} 
                    {templateFormData.items.length > 0 && ` avec les quantités par défaut`}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button
                    onClick={() => {
                      setShowTemplateForm(false);
                      setIsEditingTemplate(false);
                      setEditingTemplate(null);
                      setTemplateFormData({
                        name: '',
                        description: '',
                        category: 'standard',
                        items: [{ name: '', description: '', unit_price: 0, default_quantity: 1 }]
                      });
                    }}
                    variant="outline"
                    className="rounded-xl px-6 py-2"
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={createTemplate}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-6 py-2"
                  >
                    {isEditingTemplate ? 'Enregistrer Modifications' : 'Créer Template'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// Worksite Statistics Component
const WorksiteStatistics = () => {
  const [stats, setStats] = useState({
    totalWorksites: 0,
    activeWorksites: 0,
    completedWorksites: 0,
    totalSearches: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatistics();
  }, []);

  const loadStatistics = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      // Simulate loading statistics
      setTimeout(() => {
        setStats({
          totalWorksites: 25,
          activeWorksites: 8,
          completedWorksites: 17,
          totalSearches: 142
        });
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Statistiques des Chantiers</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Briefcase className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Chantiers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalWorksites}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">En Cours</p>
                <p className="text-2xl font-bold text-gray-900">{stats.activeWorksites}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Terminés</p>
                <p className="text-2xl font-bold text-gray-900">{stats.completedWorksites}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Search className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Recherches</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalSearches}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Worksite Map Component - Interactive Map with Team Leaders
const WorksiteMap = () => {
  const [searches, setSearches] = useState([]);
  const [worksites, setWorksites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTeamLeader, setFilterTeamLeader] = useState('all');

  useEffect(() => {
    loadSearches();
  }, []);

  const loadSearches = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await http.get(`${API}/searches`);
      const searchesWithCoords = response.data.filter(search => search.latitude && search.longitude);
      setSearches(searchesWithCoords);

      // Données simulées de chantiers avec coordonnées et chefs d'équipe
      const simulatedWorksites = [
        {
          id: '1',
          title: 'Chantier Boulevard Saint-Michel',
          latitude: 48.8566,
          longitude: 2.3522,
          status: 'IN_PROGRESS',
          team_leader: 'jean',
          client: 'Entreprise Martin',
          type: 'current'
        }
      ];
      
      setWorksites(simulatedWorksites);
    } catch (error) {
      console.error('Erreur chargement recherches:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTeamLeaderColor = (teamLeaderId) => {
    const leader = teamLeaders.find(tl => tl.id === teamLeaderId);
    return leader ? leader.color : 'bg-gray-500';
  };

  const getMarkerAnimation = (worksite) => {
    if (worksite.status === 'IN_PROGRESS') {
      return 'animate-pulse';
    }
    return '';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const [selectedMarker, setSelectedMarker] = useState(null);

  const teamLeaders = [
    { id: 'jean', name: 'Jean Dupont', color: 'bg-blue-500' },
    { id: 'marie', name: 'Marie Martin', color: 'bg-green-500' },
    { id: 'pierre', name: 'Pierre Durand', color: 'bg-purple-500' }
  ];

  const filteredWorksites = useMemo(() => [...worksites, ...searches.map(search => ({
    id: search.id,
    title: search.location,
    latitude: search.latitude,
    longitude: search.longitude,
    client: 'Client GPS',
    status: 'COMPLETED',
    team_leader: 'jean',
    type: 'past'
  }))], [worksites, searches]);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Carte des Sites</h2>
        <div className="flex items-center space-x-4">
          <select
            value={filterTeamLeader}
            onChange={(e) => setFilterTeamLeader(e.target.value)}
            className="bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="all">Tous les chefs d'équipe</option>
            {teamLeaders.map(leader => (
              <option key={leader.id} value={leader.id}>{leader.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map Placeholder */}
        <div className="lg:col-span-2">
          <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
            <CardContent className="p-0">
              <div className="relative h-96 bg-gradient-to-br from-blue-50 to-green-50 rounded-3xl flex items-center justify-center">
                <div className="text-center">
                  <Map className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">Carte Interactive</h3>
                  <p className="text-gray-500 mb-4">Intégration Google Maps en cours</p>
                  <Button className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-2xl px-6 py-3">
                    Voir en plein écran
                  </Button>
                </div>
                
                {/* Simulated Map Points */}
                <div className="absolute inset-0 pointer-events-none">
                  {searches.slice(0, 5).map((search, index) => (
                    <div
                      key={search.id}
                      className="absolute w-3 h-3 bg-red-500 rounded-full shadow-lg animate-pulse"
                      style={{
                        left: `${20 + index * 15}%`,
                        top: `${30 + index * 10}%`
                      }}
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sites List */}
        <div className="space-y-4">
          <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-gray-900">Sites Référencés</CardTitle>
            </CardHeader>
            <CardContent className="px-6 pb-6 max-h-80 overflow-y-auto space-y-3">
              {searches.length === 0 ? (
                <div className="text-center py-8">
                  <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-500 text-sm">Aucun site géolocalisé</p>
                </div>
              ) : (
                searches.map((search) => (
                  <div
                    key={search.id}
                    className={`p-3 rounded-xl cursor-pointer transition-all duration-200 bg-gray-50 hover:bg-gray-100`}
                    onClick={() => setSelectedMarker({
                      id: search.id,
                      title: search.location,
                      latitude: search.latitude,
                      longitude: search.longitude,
                      client: 'Client GPS',
                      status: 'COMPLETED',
                      team_leader: 'jean',
                      type: 'past'
                    })}
                  >
                    <h4 className="font-medium text-gray-900 text-sm mb-1">{search.location}</h4>
                    <p className="text-xs text-gray-600 mb-2 line-clamp-2">{search.description}</p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{formatDate(search.created_at)}</span>
                      <span className="bg-red-100 text-red-700 px-2 py-1 rounded-lg">
                        {search.latitude.toFixed(3)}, {search.longitude.toFixed(3)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Selected Site Details */}
          {selectedMarker && selectedMarker.type === 'past' && (
            <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-gray-900">Détails du Site</CardTitle>
              </CardHeader>
              <CardContent className="px-6 pb-6">
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-900">{selectedMarker.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">Site géolocalisé par GPS</p>
                  </div>
                  
                  <div className="pt-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Coordonnées:</span>
                      <span className="font-mono text-gray-700">
                        {selectedMarker.latitude?.toFixed(6)}, {selectedMarker.longitude?.toFixed(6)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Type:</span>
                      <span className="text-gray-700">Recherche GPS</span>
                    </div>
                  </div>
                  
                  <div className="pt-3 flex space-x-2">
                    <Button size="sm" variant="outline" className="flex-1 rounded-xl">
                      <MapPin className="h-3 w-3 mr-1" />
                      Naviguer
                    </Button>
                    <Button size="sm" className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl">
                      <FileText className="h-3 w-3 mr-1" />
                      Connexion
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

// Upcoming Sites Component - Complete Implementation
const UpcomingSites = () => {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [teamLeaders, setTeamLeaders] = useState([]);
  
  // NEW: Enhanced states for all button actions
  const [showFollowModal, setShowFollowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    description: '',
    scheduled_date: '',
    scheduled_time: '08:00',
    priority: 'MEDIUM',
    assigned_to: '',
    status: 'PLANNED'
  });

  useEffect(() => {
    loadSites();
  }, []);

  const loadSites = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      // Charger les chefs d'équipe depuis le backend
      const token = localStorage.getItem('token');
      const leadersRes = await http.get(`${API}/team-leaders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTeamLeaders(leadersRes.data || []);
    } catch (error) {
      console.warn('Chefs d\'équipe non disponibles, utilisation de données simulées');
      // Données simulées des chefs d'équipe
      setTeamLeaders([
        { id: '1', nom: 'Dupont', prenom: 'Jean', specialite: 'Détection de réseaux' },
        { id: '2', nom: 'Martin', prenom: 'Marie', specialite: 'Géolocalisation' },
        { id: '3', nom: 'Durand', prenom: 'Pierre', specialite: 'Électricité' },
        { id: '4', nom: 'Leroy', prenom: 'Sophie', specialite: 'Plomberie' }
      ]);
    }

    // Simulation de données de planning  
    setTimeout(() => {
      setSites([
        {
          id: '1',
          title: 'Recherche réseaux Avenue des Champs',
          location: 'Paris 8ème',
          description: 'Localisation réseaux électriques et télécoms',
          scheduled_date: '2024-01-20',
          scheduled_time: '09:00',
          priority: 'HIGH',
          status: 'PLANNED',
          assigned_to: 'Jean Dupont'
        },
        {
          id: '2',
          title: 'Inspection canalisations Boulevard Saint-Michel',
          location: 'Paris 5ème',
          description: 'Vérification état canalisations eau',
          scheduled_date: '2024-01-22',
          scheduled_time: '14:30',
          priority: 'MEDIUM',
          status: 'IN_PROGRESS',
          assigned_to: 'Marie Martin'
        },
        {
          id: '3',
          title: 'Détection réseaux gaz Rue de Rivoli',
          location: 'Paris 1er',
          description: 'Localisation précise conduites gaz',
          scheduled_date: '2024-01-25',
          scheduled_time: '08:00',
          priority: 'HIGH',
          status: 'PLANNED',
          assigned_to: 'Pierre Durand'
        }
      ]);
      setLoading(false);
    }, 1000);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'HIGH': return 'bg-red-100 text-red-700 border-red-200';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'LOW': return 'bg-green-100 text-green-700 border-green-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PLANNED': return 'bg-blue-100 text-blue-700';
      case 'IN_PROGRESS': return 'bg-orange-100 text-orange-700';
      case 'COMPLETED': return 'bg-green-100 text-green-700';
      case 'CANCELLED': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // NEW: Enhanced button action functions
  const handleFollowSite = (site) => {
    setSelectedSite(site);
    setShowFollowModal(true);
  };

  const handleEditSite = (site) => {
    setSelectedSite(site);
    setFormData({
      title: site.title,
      location: site.location,
      description: site.description,
      scheduled_date: site.scheduled_date,
      scheduled_time: site.scheduled_time || '08:00',
      priority: site.priority,
      assigned_to: site.assigned_to,
      status: site.status
    });
    setShowEditModal(true);
  };

  const handleStartSite = (site) => {
    setSelectedSite(site);
    setShowStartModal(true);
  };

  const handleViewDetails = (site) => {
    setSelectedSite(site);
    setShowDetailsModal(true);
  };

  const updateSiteStatus = (siteId, newStatus) => {
    setSites(prev => prev.map(site => 
      site.id === siteId ? { ...site, status: newStatus } : site
    ));
  };

  const confirmStartSite = () => {
    if (selectedSite) {
      updateSiteStatus(selectedSite.id, 'IN_PROGRESS');
      setShowStartModal(false);
      setSelectedSite(null);
      alert(`✅ Chantier "${selectedSite.title}" démarré avec succès !`);
    }
  };

  const updateSite = () => {
    if (selectedSite) {
      setSites(prev => prev.map(site => 
        site.id === selectedSite.id ? { ...site, ...formData } : site
      ));
      setShowEditModal(false);
      setSelectedSite(null);
      alert(`✅ Chantier "${formData.title}" modifié avec succès !`);
    }
  };

  const completeSite = (siteId) => {
    if (confirm('Marquer ce chantier comme terminé ?')) {
      updateSiteStatus(siteId, 'COMPLETED');
      alert('✅ Chantier marqué comme terminé !');
    }
  };

  const cancelSite = (siteId) => {
    if (confirm('Annuler ce chantier ? Cette action ne peut pas être annulée.')) {
      updateSiteStatus(siteId, 'CANCELLED');
      alert('❌ Chantier annulé.');
    }
  };

  if (loading) {
    return (
      <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
        <CardContent className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du planning...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl p-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Gestion des Chantiers</h2>
            <p className="text-gray-600 mt-1">Organisez et suivez vos chantiers actifs et à planifier</p>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-2xl px-6 py-3 shadow-lg transform transition-all duration-200 hover:scale-105"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouveau Chantier
          </Button>
        </div>
      </div>

      {/* Planning Form */}
      {showForm && (
        <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
          <CardHeader className="pb-6">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold text-gray-900">Créer un nouveau chantier</CardTitle>
              <Button
                onClick={() => setShowForm(false)}
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            <form className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom du chantier *</label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                    placeholder="Ex: Rénovation Bureau Avenue des Champs"
                    className="rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500/20"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Localisation *</label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData(prev => ({...prev, location: e.target.value}))}
                    placeholder="Adresse ou zone du chantier"
                    className="rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500/20"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description du chantier</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                  rows={3}
                  placeholder="Description détaillée des travaux à effectuer..."
                  className="w-full rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all duration-200 p-3 resize-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date prévue *</label>
                  <Input
                    type="date"
                    value={formData.scheduled_date}
                    onChange={(e) => setFormData(prev => ({...prev, scheduled_date: e.target.value}))}
                    className="rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Heure prévue *</label>
                  <Input
                    type="time"
                    value={formData.scheduled_time}
                    onChange={(e) => setFormData(prev => ({...prev, scheduled_time: e.target.value}))}
                    className="rounded-xl border-gray-200 focus:border-orange-500 focus:ring-orange-500/20"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priorité</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({...prev, priority: e.target.value}))}
                    className="w-full rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all duration-200 p-3"
                  >
                    <option value="LOW">Faible</option>
                    <option value="MEDIUM">Moyenne</option>
                    <option value="HIGH">Élevée</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assigné à</label>
                  <select
                    value={formData.assigned_to}
                    onChange={(e) => setFormData(prev => ({...prev, assigned_to: e.target.value}))}
                    className="w-full rounded-xl border border-gray-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 transition-all duration-200 p-3"
                  >
                    <option value="">Sélectionner un chef d'équipe</option>
                    {teamLeaders.map(leader => (
                      <option key={leader.id} value={`${leader.prenom} ${leader.nom}`}>
                        {leader.prenom} {leader.nom} - {leader.specialite}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="pt-4 flex space-x-4">
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white rounded-2xl py-3 shadow-lg transform transition-all duration-200 hover:scale-105"
                >
                  <Building className="h-4 w-4 mr-2" />
                  Créer le chantier
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowForm(false)}
                  variant="outline"
                  className="rounded-2xl px-6"
                >
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* NEW: Three-Section Layout for Chantiers */}
      <div className="grid lg:grid-cols-3 gap-6">
        
        {/* Section 1: Chantiers Actifs */}
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                Chantiers Actifs
              </h3>
              <p className="text-sm text-gray-600 mt-1">Chantiers en cours d'exécution</p>
            </div>
            <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
              {sites.filter(s => s.status === 'IN_PROGRESS').length} en cours
            </div>
          </div>
          
          <div className="space-y-4">
            {sites.filter(s => s.status === 'IN_PROGRESS').length === 0 ? (
              <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Building className="h-6 w-6 text-green-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Aucun chantier actif</h4>
                  <p className="text-gray-500 text-sm">Les chantiers en cours apparaîtront ici</p>
                </CardContent>
              </Card>
            ) : (
              sites.filter(s => s.status === 'IN_PROGRESS').map((site) => (
                <Card key={site.id} className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          <h4 className="text-lg font-semibold text-gray-900">{site.title}</h4>
                          <Badge className="bg-green-100 text-green-700">En cours</Badge>
                          <Badge className={getPriorityColor(site.priority)}>
                            {site.priority === 'HIGH' ? 'Élevée' : site.priority === 'MEDIUM' ? 'Moyenne' : 'Faible'}
                          </Badge>
                        </div>
                        
                        <p className="text-gray-600 mb-3 text-sm">{site.description}</p>
                        
                        <div className="space-y-2 text-xs text-gray-500">
                          <div className="flex items-center">
                            <MapPin className="h-3 w-3 mr-2" />
                            {site.location}
                          </div>
                          <div className="flex items-center space-x-4">
                            <span className="flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              {formatDate(site.scheduled_date)}
                            </span>
                            {site.scheduled_time && (
                              <span className="flex items-center">
                                <Clock className="h-3 w-3 mr-1" />
                                {site.scheduled_time}
                              </span>
                            )}
                            <span className="flex items-center">
                              <Users className="h-3 w-3 mr-1" />
                              {site.assigned_to}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col space-y-2">
                        <Button 
                          size="sm" 
                          onClick={() => handleFollowSite(site)}
                          className="bg-green-600 hover:bg-green-700 text-white rounded-xl text-xs px-3 py-1"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Suivre
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={() => handleEditSite(site)}
                          variant="outline" 
                          className="rounded-xl text-xs px-3 py-1 border-gray-200"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Modifier
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>

        {/* Section 2: Chantiers à Planifier */}
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                Chantiers à Planifier
              </h3>
              <p className="text-sm text-gray-600 mt-1">Chantiers en attente de démarrage</p>
            </div>
            <div className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm font-medium">
              {sites.filter(s => s.status === 'PLANNED').length} à planifier
            </div>
          </div>
          
          <div className="space-y-4">
            {sites.filter(s => s.status === 'PLANNED').length === 0 ? (
              <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Calendar className="h-6 w-6 text-orange-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Aucun chantier à planifier</h4>
                  <p className="text-gray-500 text-sm">Planifiez votre premier chantier pour commencer</p>
                  <Button
                    onClick={() => setShowForm(true)}
                    className="mt-4 bg-orange-600 hover:bg-orange-700 text-white rounded-xl px-4 py-2 text-sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Nouveau Chantier
                  </Button>
                </CardContent>
              </Card>
            ) : (
              sites.filter(s => s.status === 'PLANNED').map((site) => (
                <Card key={site.id} className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                          <h4 className="text-lg font-semibold text-gray-900">{site.title}</h4>
                          <Badge className="bg-orange-100 text-orange-700">À planifier</Badge>
                          <Badge className={getPriorityColor(site.priority)}>
                            {site.priority === 'HIGH' ? 'Élevée' : site.priority === 'MEDIUM' ? 'Moyenne' : 'Faible'}
                          </Badge>
                        </div>
                        
                        <p className="text-gray-600 mb-3 text-sm">{site.description}</p>
                        
                        <div className="space-y-2 text-xs text-gray-500">
                          <div className="flex items-center">
                            <MapPin className="h-3 w-3 mr-2" />
                            {site.location}
                          </div>
                          <div className="flex items-center space-x-4">
                            <span className="flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              {formatDate(site.scheduled_date)}
                            </span>
                            {site.scheduled_time && (
                              <span className="flex items-center">
                                <Clock className="h-3 w-3 mr-1" />
                                {site.scheduled_time}
                              </span>
                            )}
                            <span className="flex items-center">
                              <Users className="h-3 w-3 mr-1" />
                              {site.assigned_to}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col space-y-2">
                        <Button 
                          size="sm" 
                          onClick={() => handleStartSite(site)}
                          className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl text-xs px-3 py-1"
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Démarrer
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={() => handleEditSite(site)}
                          variant="outline" 
                          className="rounded-xl text-xs px-3 py-1 border-gray-200"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Modifier
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>

        {/* Section 3: Chantiers Terminés */}
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900 flex items-center">
                <div className="w-3 h-3 bg-gray-500 rounded-full mr-3"></div>
                Chantiers Terminés
              </h3>
              <p className="text-sm text-gray-600 mt-1">Chantiers finalisés</p>
            </div>
            <div className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
              {sites.filter(s => s.status === 'COMPLETED' || s.status === 'CANCELLED').length} terminés
            </div>
          </div>
          
          <div className="space-y-4">
            {sites.filter(s => s.status === 'COMPLETED' || s.status === 'CANCELLED').length === 0 ? (
              <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg">
                <CardContent className="p-8 text-center">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-6 w-6 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Aucun chantier terminé</h4>
                  <p className="text-gray-500 text-sm">Les chantiers terminés apparaîtront ici</p>
                </CardContent>
              </Card>
            ) : (
              sites.filter(s => s.status === 'COMPLETED' || s.status === 'CANCELLED').map((site) => (
                <Card key={site.id} className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className={`w-3 h-3 rounded-full ${site.status === 'COMPLETED' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                          <h4 className="text-lg font-semibold text-gray-900">{site.title}</h4>
                          <Badge className={site.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {site.status === 'COMPLETED' ? '✅ Terminé' : '❌ Annulé'}
                          </Badge>
                        </div>
                        
                        {site.description && (
                          <p className="text-gray-600 mb-3 text-sm">{site.description}</p>
                        )}
                        
                        <div className="space-y-2 text-xs text-gray-500">
                          {site.location && (
                            <div className="flex items-center">
                              <MapPin className="h-3 w-3 mr-2" />
                              {site.location}
                            </div>
                          )}
                          <div className="flex items-center space-x-4">
                            {site.scheduled_date && (
                              <span className="flex items-center">
                                <Calendar className="h-3 w-3 mr-1" />
                                {formatDate(site.scheduled_date)}
                              </span>
                            )}
                            {site.assigned_to && (
                              <span className="flex items-center">
                                <Users className="h-3 w-3 mr-1" />
                                {site.assigned_to}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col space-y-2">
                        <Button 
                          size="sm" 
                          onClick={() => handleFollowSite(site)}
                          className="bg-gray-600 hover:bg-gray-700 text-white rounded-xl text-xs px-3 py-1"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          Détails
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>

      {/* NEW: Follow Site Modal */}
      {showFollowModal && selectedSite && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Suivi du Chantier</h2>
                  <p className="text-gray-600">{selectedSite.title}</p>
                </div>
                <Button
                  onClick={() => {
                    setShowFollowModal(false);
                    setSelectedSite(null);
                  }}
                  variant="outline"
                  className="rounded-xl"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-6">
                {/* Chantier Info */}
                <div className="bg-green-50 rounded-2xl p-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="font-semibold text-green-900 mb-3">Informations Générales</h3>
                      <div className="space-y-2 text-sm">
                        <p><strong>Localisation:</strong> {selectedSite.location}</p>
                        <p><strong>Priorité:</strong> {selectedSite.priority === 'HIGH' ? 'Élevée' : selectedSite.priority === 'MEDIUM' ? 'Moyenne' : 'Faible'}</p>
                        <p><strong>Assigné à:</strong> {selectedSite.assigned_to}</p>
                        <p><strong>Date programmée:</strong> {formatDate(selectedSite.scheduled_date)}</p>
                        {selectedSite.scheduled_time && <p><strong>Heure:</strong> {selectedSite.scheduled_time}</p>}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-semibold text-green-900 mb-3">Progression</h3>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Avancement</span>
                            <span>78%</span>
                          </div>
                          <div className="w-full bg-green-200 rounded-full h-2">
                            <div className="bg-green-600 h-2 rounded-full w-3/4"></div>
                          </div>
                        </div>
                        <div className="text-sm text-green-700">
                          <p>✅ Préparation terminée</p>
                          <p>✅ Équipements déployés</p>
                          <p>🔄 Intervention en cours</p>
                          <p>⏳ Finalisation en attente</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3">
                  <Button
                    onClick={() => completeSite(selectedSite.id)}
                    className="bg-green-600 hover:bg-green-700 text-white rounded-xl"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Marquer Terminé
                  </Button>
                  <Button
                    onClick={() => handleViewDetails(selectedSite)}
                    variant="outline"
                    className="rounded-xl"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Connexion
                  </Button>
                  <Button
                    onClick={() => cancelSite(selectedSite.id)}
                    variant="outline"
                    className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Annuler Chantier
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Start Site Modal */}
      {showStartModal && selectedSite && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Démarrer le Chantier</h2>
                  <p className="text-gray-600">{selectedSite.title}</p>
                </div>
                <Button
                  onClick={() => {
                    setShowStartModal(false);
                    setSelectedSite(null);
                  }}
                  variant="outline"
                  className="rounded-xl"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-6">
                <div className="bg-orange-50 rounded-2xl p-6">
                  <h3 className="font-semibold text-orange-900 mb-4">Vérifications avant démarrage</h3>
                  <div className="space-y-3 text-sm">
                    <label className="flex items-center space-x-3">
                      <input type="checkbox" className="rounded border-gray-300" defaultChecked />
                      <span>✅ Équipe assignée disponible ({selectedSite.assigned_to})</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input type="checkbox" className="rounded border-gray-300" defaultChecked />
                      <span>✅ Matériel et équipements prêts</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input type="checkbox" className="rounded border-gray-300" defaultChecked />
                      <span>✅ Autorisations et permis obtenus</span>
                    </label>
                    <label className="flex items-center space-x-3">
                      <input type="checkbox" className="rounded border-gray-300" defaultChecked />
                      <span>✅ Site accessible et sécurisé</span>
                    </label>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Le démarrage de ce chantier changera son statut de "À planifier" vers "En cours" et notifiera l'équipe assignée.
                  </p>
                </div>

                <div className="flex space-x-3">
                  <Button
                    onClick={confirmStartSite}
                    className="flex-1 bg-orange-600 hover:bg-orange-700 text-white rounded-xl"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Démarrer Maintenant
                  </Button>
                  <Button
                    onClick={() => {
                      setShowStartModal(false);
                      setSelectedSite(null);
                    }}
                    variant="outline"
                    className="rounded-xl px-6"
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Edit Site Modal */}
      {showEditModal && selectedSite && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Modifier le Chantier</h2>
                  <p className="text-gray-600">Mettre à jour les informations</p>
                </div>
                <Button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedSite(null);
                  }}
                  variant="outline"
                  className="rounded-xl"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nom du chantier</label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                      className="rounded-xl border-gray-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Localisation</label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData(prev => ({...prev, location: e.target.value}))}
                      className="rounded-xl border-gray-200"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                    rows={3}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 resize-none"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date programmée</label>
                    <Input
                      type="date"
                      value={formData.scheduled_date}
                      onChange={(e) => setFormData(prev => ({...prev, scheduled_date: e.target.value}))}
                      className="rounded-xl border-gray-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Heure</label>
                    <Input
                      type="time"
                      value={formData.scheduled_time}
                      onChange={(e) => setFormData(prev => ({...prev, scheduled_time: e.target.value}))}
                      className="rounded-xl border-gray-200"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Priorité</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData(prev => ({...prev, priority: e.target.value}))}
                      className="w-full rounded-xl border border-gray-200 px-3 py-2"
                    >
                      <option value="LOW">Faible</option>
                      <option value="MEDIUM">Moyenne</option>
                      <option value="HIGH">Élevée</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assigné à</label>
                  <select
                    value={formData.assigned_to}
                    onChange={(e) => setFormData(prev => ({...prev, assigned_to: e.target.value}))}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2"
                  >
                    <option value="">Sélectionner un chef d'équipe</option>
                    {teamLeaders.map((leader) => (
                      <option key={leader.id} value={`${leader.prenom} ${leader.nom}`}>
                        {leader.prenom} {leader.nom} - {leader.specialite}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex space-x-3 pt-4 border-t">
                  <Button
                    onClick={updateSite}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Sauvegarder Modifications
                  </Button>
                  <Button
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedSite(null);
                    }}
                    variant="outline"
                    className="rounded-xl px-6"
                  >
                    Annuler
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Details Modal */}
      {showDetailsModal && selectedSite && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Détails du Chantier</h2>
                  <p className="text-gray-600">{selectedSite.title}</p>
                </div>
                <Button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedSite(null);
                  }}
                  variant="outline"
                  className="rounded-xl"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-6">
                {/* Informations Détaillées */}
                <div className="bg-blue-50 rounded-2xl p-6">
                  <h3 className="font-semibold text-blue-900 mb-4">Rapport Complet</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Informations Générales</h4>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p><strong>Localisation:</strong> {selectedSite.location}</p>
                          <p><strong>Description:</strong> {selectedSite.description}</p>
                          <p><strong>Priorité:</strong> {selectedSite.priority === 'HIGH' ? 'Élevée' : selectedSite.priority === 'MEDIUM' ? 'Moyenne' : 'Faible'}</p>
                          <p><strong>Statut:</strong> {selectedSite.status === 'IN_PROGRESS' ? 'En cours' : 'À planifier'}</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Planning</h4>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p><strong>Date programmée:</strong> {formatDate(selectedSite.scheduled_date)}</p>
                          <p><strong>Heure de début:</strong> {selectedSite.scheduled_time}</p>
                          <p><strong>Durée estimée:</strong> 4-6 heures</p>
                          <p><strong>Équipe assignée:</strong> {selectedSite.assigned_to}</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Ressources</h4>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p>• Détecteur de réseaux portable</p>
                          <p>• Géoradar haute précision</p>
                          <p>• Équipement de marquage</p>
                          <p>• Matériel de sécurité</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Contacts</h4>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p><strong>Chef d'équipe:</strong> {selectedSite.assigned_to}</p>
                          <p><strong>Téléphone:</strong> 06.12.34.56.78</p>
                          <p><strong>Email:</strong> {selectedSite.assigned_to.toLowerCase().replace(' ', '.')}@skyapp.fr</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex space-x-3">
                  <Button
                    onClick={() => alert('📄 Rapport PDF généré !\n\nLe rapport détaillé a été téléchargé avec succès.')}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Télécharger PDF
                  </Button>
                  <Button
                    onClick={() => alert('📧 Rapport envoyé !\n\nLe rapport a été envoyé par email à l\'équipe concernée.')}
                    variant="outline"
                    className="rounded-xl"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Envoyer par Email
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Client Details View Component
const ClientDetailsView = ({ clientId, onBack }) => {
  const [client, setClient] = useState(null);
  const [searches, setSearches] = useState([]);
  const [projects, setProjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClientData();
  }, [clientId]);

  const loadClientData = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      setLoading(true);
      
      // Cas spécial : recherches sans client
      if (clientId === 'NO_CLIENT') {
        setClient({ id: 'NO_CLIENT', nom: 'Clients occasionnels', email: '' });
        
        const searchesRes = await http.get(`${API}/searches?without_client=true`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).catch(() => ({ data: [] }));
        
        setSearches(searchesRes.data || []);
        setProjects([]);
        setLoading(false);
        return;
      }
      
      const [clientRes, searchesRes, projectsRes, quotesRes, worksitesRes] = await Promise.allSettled([
        http.get(`${API}/clients/${clientId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        http.get(`${API}/searches?client_id=${clientId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        http.get(`${API}/projects?client_id=${clientId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        http.get(`${API}/quotes?client_id=${clientId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        http.get(`${API}/worksites?client_id=${clientId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (clientRes.status === 'fulfilled') setClient(clientRes.value.data);
      else console.error('Erreur chargement client:', clientRes.reason);
      const searchData = searchesRes.status === 'fulfilled' ? (searchesRes.value.data.data || searchesRes.value.data || []) : [];
      setSearches(searchData);
      const projectData = projectsRes.status === 'fulfilled' ? (projectsRes.value.data.data || projectsRes.value.data || []) : [];
      setProjects(projectData);
      const quoteData = quotesRes.status === 'fulfilled' ? (quotesRes.value.data.data || quotesRes.value.data || []) : [];
      setQuotes(quoteData);
      const worksiteData = worksitesRes.status === 'fulfilled' ? (worksitesRes.value.data || []) : [];
      setInvoices(worksiteData);
      
    } catch (error) {
      console.error('Erreur chargement client:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
        <CardContent className="p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des détails...</p>
        </CardContent>
      </Card>
    );
  }

  if (!client) {
    return (
      <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
        <CardContent className="p-12 text-center">
          <p className="text-gray-500 mb-4">Client introuvable</p>
          <Button onClick={onBack} variant="outline" className="rounded-xl">
            ← Retour à la liste
          </Button>
        </CardContent>
      </Card>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', count: null },
    { id: 'searches', label: 'Recherches', count: searches.length },
    { id: 'projects', label: 'Projets', count: projects.length },
    { id: 'quotes', label: 'Devis', count: quotes.length },
    { id: 'invoices', label: 'Factures', count: invoices.length },
  ];

  const recentActivity = [
    ...searches.map(s => ({ type: 'search', date: s.created_at, title: s.title || 'Recherche sans titre', data: s })),
    ...projects.map(p => ({ type: 'project', date: p.created_at, title: p.name, data: p })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 10);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
        <CardContent className="p-8">
          <Button 
            onClick={onBack}
            variant="ghost"
            className="mb-6 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour à la liste
          </Button>

          <div className="flex items-start justify-between">
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-lg">
                {client.nom?.charAt(0).toUpperCase()}
              </div>

              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-3">
                  {clientId === 'NO_CLIENT' ? '📋 ' : ''}{client.nom}
                </h1>
                {clientId === 'NO_CLIENT' ? (
                  <p className="text-gray-600 mt-2">
                    Recherches de clients occasionnels (sans suivi client)
                  </p>
                ) : (
                  <>
                    <div className="space-y-2 text-gray-600">
                      {client.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          <a href={`mailto:${client.email}`} className="hover:text-teal-600">
                            {client.email}
                          </a>
                        </div>
                      )}
                      {client.telephone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          <a href={`tel:${client.telephone}`} className="hover:text-teal-600">
                            {client.telephone}
                          </a>
                        </div>
                      )}
                      {client.adresse && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          <span>{client.adresse}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 mt-3">
                      Client depuis le {new Date(client.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{searches.length}</div>
                <div className="text-sm text-gray-600">Recherches</div>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{projects.length}</div>
                <div className="text-sm text-gray-600">Projets</div>
              </div>
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">{quotes.length}</div>
                <div className="text-sm text-gray-600">Devis</div>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-orange-600">{invoices.length}</div>
                <div className="text-sm text-gray-600">Factures</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
        <div className="flex border-b overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-8 py-4 font-medium whitespace-nowrap transition ${
                activeTab === tab.id
                  ? 'border-b-2 border-teal-600 text-teal-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {tab.label}
              {tab.count !== null && tab.count !== undefined && (
                <span className="ml-2 px-2 py-1 bg-gray-100 rounded-full text-xs font-semibold">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <CardContent className="p-8">
          {/* Vue d'ensemble */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Activité récente
                </h3>
                
                {recentActivity.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-2xl">
                    <p className="text-gray-500">Aucune activité pour ce client</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentActivity.map((activity, index) => (
                      <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition">
                        {activity.type === 'search' ? (
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Search className="h-5 w-5 text-blue-600" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FolderOpen className="h-5 w-5 text-green-600" />
                          </div>
                        )}
                        
                        <div className="flex-1">
                          <div className="font-medium text-gray-800">{activity.title}</div>
                          <div className="text-sm text-gray-500">
                            {activity.type === 'search' ? '🔍 Recherche' : '📊 Projet'} • 
                            {' '}{new Date(activity.date).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Statistiques */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6">
                  <h4 className="font-semibold mb-4 flex items-center gap-2">
                    <Search className="h-5 w-5" />
                    Recherches par type
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">🏞️ Terrain</span>
                      <span className="font-bold text-blue-600">
                        {searches.filter(s => s.search_type === 'TERRAIN').length}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">💧 Infiltration</span>
                      <span className="font-bold text-orange-600">
                        {searches.filter(s => s.search_type === 'INFILTRATION').length}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6">
                  <h4 className="font-semibold mb-4 flex items-center gap-2">
                    <FolderOpen className="h-5 w-5" />
                    Projets
                  </h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">📊 Total</span>
                      <span className="font-bold text-green-600">
                        {projects.length}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recherches */}
          {activeTab === 'searches' && (
            <div>
              {searches.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-2xl">
                  <Search className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Aucune recherche pour ce client</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {searches.map(search => (
                    <div key={search.id} className="border-2 border-gray-100 rounded-2xl p-5 hover:shadow-lg transition">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-3">
                          {search.search_type === 'TERRAIN' ? (
                            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Landmark className="h-6 w-6 text-blue-600" />
                            </div>
                          ) : (
                            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Droplets className="h-6 w-6 text-orange-600" />
                            </div>
                          )}
                          
                          <div>
                            <h4 className="font-semibold text-gray-800">{search.title || 'Sans titre'}</h4>
                            <p className="text-sm text-gray-500">
                              {new Date(search.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        </div>

                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          search.status === 'SHARED' 
                            ? 'bg-green-100 text-green-700' 
                            : search.status === 'ACTIVE'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {search.status === 'SHARED' ? '📤 Partagée' : 
                           search.status === 'ACTIVE' ? '✅ Active' : '📝 Brouillon'}
                        </span>
                      </div>

                      {search.address && (
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                          <MapPin className="h-4 w-4" />
                          {search.address}
                        </div>
                      )}

                      {search.description && (
                        <p className="text-sm text-gray-600 line-clamp-2">{search.description}</p>
                      )}

                      {search.project_id && (
                        <div className="mt-3 flex items-center gap-2 text-sm text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          Converti en projet
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Projets */}
          {activeTab === 'projects' && (
            <div>
              {projects.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-2xl">
                  <FolderOpen className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Aucun projet pour ce client</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {projects.map(project => (
                    <div key={project.id} className="border-2 border-gray-100 rounded-2xl p-5 hover:shadow-lg transition">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-semibold text-gray-800">{project.name}</h4>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          project.status === 'COMPLETED' 
                            ? 'bg-green-100 text-green-700' 
                            : project.status === 'ACTIVE'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {project.status}
                        </span>
                      </div>

                      {project.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{project.description}</p>
                      )}

                      <div className="text-xs text-gray-400">
                        Créé le {new Date(project.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Devis */}
          {activeTab === 'quotes' && (
            <div>
              {quotes.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-2xl">
                  <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Aucun devis pour ce client</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {quotes.map(quote => (
                    <div key={quote.id} className="border-2 border-gray-100 rounded-2xl p-5 hover:shadow-lg transition">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-1">{quote.title || 'Devis sans titre'}</h4>
                          {quote.quote_number != null && quote.quote_number !== '' && (
                            <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                              #{quote.quote_number}
                            </span>
                          )}
                        </div>
                        
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          quote.status === 'CONVERTED_TO_WORKSITE' 
                            ? 'bg-green-100 text-green-700' 
                            : quote.status === 'SENT' || quote.status === 'PENDING'
                            ? 'bg-blue-100 text-blue-700'
                            : quote.status === 'DRAFT'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {quote.status === 'CONVERTED_TO_WORKSITE' ? '🏗️ Chantier' :
                           quote.status === 'SENT' ? '📧 Envoyé' :
                           quote.status === 'PENDING' ? '⏳ En attente' :
                           quote.status === 'DRAFT' ? '📝 Brouillon' : quote.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-xs text-gray-500">Montant</p>
                          <p className="text-lg font-bold text-gray-900">{quote.amount?.toFixed(2) || '0.00'}€</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Date</p>
                          <p className="text-sm text-gray-700">
                            {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>

                      {quote.description && (
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{quote.description}</p>
                      )}

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => window.open(`/devis/${quote.id}`, '_blank')}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-900 rounded-lg text-xs"
                        >
                          <FileText className="h-3 w-3 mr-1" />
                          Voir le devis
                        </Button>
                        {quote.status === 'CONVERTED_TO_WORKSITE' && (
                          <span className="inline-flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-lg">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Converti en chantier
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Factures / Chantiers */}
          {activeTab === 'invoices' && (
            <div>
              {invoices.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-2xl">
                  <Receipt className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Aucun chantier pour ce client</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {invoices.map(worksite => (
                    <div key={worksite.id} className="border-2 border-purple-100 rounded-2xl p-5 hover:shadow-lg transition">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-800 mb-1">{worksite.title || 'Chantier sans titre'}</h4>
                          {worksite.address && (
                            <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                              <MapPin className="h-3 w-3" />
                              {worksite.address}
                            </div>
                          )}
                        </div>
                        
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          worksite.status === 'COMPLETED' 
                            ? 'bg-green-100 text-green-700' 
                            : worksite.status === 'IN_PROGRESS'
                            ? 'bg-blue-100 text-blue-700'
                            : worksite.status === 'PENDING'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {worksite.status === 'COMPLETED' ? '✓ Terminé' :
                           worksite.status === 'IN_PROGRESS' ? '⚙️ En cours' :
                           worksite.status === 'PENDING' ? '⏳ En attente' : worksite.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-xs text-gray-500">Montant</p>
                          <p className="text-lg font-bold text-gray-900">{worksite.amount?.toFixed(2) || '0.00'}€</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Date de création</p>
                          <p className="text-sm text-gray-700">
                            {new Date(worksite.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>

                      {worksite.description && (
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{worksite.description}</p>
                      )}

                      <div className="flex gap-2 flex-wrap">
                        {worksite.quote_id && (
                          <Button
                            size="sm"
                            onClick={() => window.open(`/devis/${worksite.quote_id}`, '_blank')}
                            className="bg-blue-100 hover:bg-blue-200 text-blue-900 rounded-lg text-xs"
                          >
                            <FileText className="h-3 w-3 mr-1" />
                            Voir le devis
                          </Button>
                        )}
                        {worksite.status === 'COMPLETED' && (
                          <span className="inline-flex items-center text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Facturé
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Clients View Component - Complete Implementation
const ClientsView = () => {
  const _cachedClients = getCached(`${API}/clients`);
  const [clients, setClients] = useState(_cachedClients || []);
  const [loading, setLoading] = useState(!_cachedClients);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDetails, setShowClientDetails] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, clientId: null, clientName: '', chantiersEnCours: [], devisEnAttente: [], loadingInfo: false });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [savingClient, setSavingClient] = useState(false); // Garde-fou anti double-clic (EDGE-005)

  // ⚡ Rendu progressif : n'affiche que les 50 premiers clients, bouton "Voir plus"
  const filteredClients = useMemo(() => {
    if (!searchTerm) return clients;
    const search = searchTerm.toLowerCase();
    return clients.filter(client =>
      client.nom?.toLowerCase().includes(search) ||
      client.email?.toLowerCase().includes(search) ||
      client.telephone?.toLowerCase().includes(search)
    );
  }, [clients, searchTerm]);
  const { visibleItems: visibleClients, hasMore: hasMoreClients, showMore: showMoreClients, totalCount: totalClientsCount } = useProgressiveList(filteredClients, 50, 50);

  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    adresse: '',
    contact: '',
    ville: '',
    code_postal: '',
    pays: 'France',
    siret: '',
    siren: '',
    customer_type: 'professional'
  });

  useEffect(() => {
    perf.start('Clients.mount');
    loadClients();
  }, []);

  // ⚡ Realtime — rafraîchir automatiquement quand les clients changent
  useEffect(() => {
    const handler = (e) => { if (e.detail?.table === 'clients') loadClients(); };
    window.addEventListener('realtime-update', handler);
    return () => window.removeEventListener('realtime-update', handler);
  }, []);

  const loadClients = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await http.get(`${API}/clients`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setClients(response.data);
    } catch (error) {
      console.error('Erreur chargement clients:', error);
    } finally {
      perf.end('Clients.mount');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredFields = [
      { key: 'nom', label: 'Nom / Raison sociale' },
      { key: 'contact', label: 'Contact' },
      { key: 'telephone', label: 'Téléphone' },
      { key: 'ville', label: 'Ville' },
      { key: 'code_postal', label: 'Code postal' },
    ];
    for (const field of requiredFields) {
      if (!formData[field.key] || !formData[field.key].trim()) {
        toast.error(`Veuillez renseigner le champ « ${field.label} »`);
        return;
      }
    }
    if (formData.siret && formData.siret.trim() && !/^\d{14}$/.test(formData.siret.trim())) {
      toast.error('Le SIRET doit contenir exactement 14 chiffres');
      return;
    }
    if (savingClient) return; // Anti double-clic (EDGE-005)
    setSavingClient(true);
    try {
      if (editingClient) {
        // Update existing client
        await http.put(`${API}/clients/${editingClient.id}`, formData, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      } else {
        await http.post(`${API}/clients`, formData, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      }

      toast.success(editingClient ? 'Client modifié avec succès !' : 'Client créé avec succès !');
      resetForm();
      loadClients();
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingClient(false);
    }
  };

  const resetForm = () => {
    setFormData({ nom: '', email: '', telephone: '', adresse: '', contact: '', ville: '', code_postal: '', pays: 'France', siret: '', siren: '', customer_type: 'professional' });
    setShowForm(false);
    setEditingClient(null);
  };

  const editClient = (client) => {
    setFormData({
      nom: client.nom,
      email: client.email,
      telephone: client.telephone || '',
      adresse: client.adresse || '',
      contact: client.contact || '',
      ville: client.ville || '',
      code_postal: client.code_postal || '',
      pays: client.pays || 'France',
      siret: client.siret || '',
      siren: client.siren || (client.siret ? client.siret.replace(/\s/g, '').slice(0, 9) : ''),
      customer_type: client.customer_type || 'professional'
    });
    setEditingClient(client);
    setShowForm(true);
  };

  const deleteClient = async (clientId, clientName) => {
    setConfirmDelete({ open: true, clientId, clientName, chantiersEnCours: [], devisEnAttente: [], loadingInfo: true });
    try {
      const token = localStorage.getItem('token');
      const [worksitesRes, quotesRes] = await Promise.allSettled([
        http.get(`${API}/worksites?client_id=${clientId}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        http.get(`${API}/quotes?client_id=${clientId}`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      const worksites = worksitesRes.status === 'fulfilled' ? (worksitesRes.value.data || []) : [];
      const quotes = quotesRes.status === 'fulfilled' ? (quotesRes.value.data?.data || quotesRes.value.data || []) : [];
      const chantiersEnCours = worksites.filter(w => w.status === 'IN_PROGRESS' || w.status === 'PLANNED');
      const devisEnAttente = quotes.filter(q => q.status === 'SENT' || q.status === 'DRAFT');
      setConfirmDelete(prev => ({ ...prev, chantiersEnCours, devisEnAttente, loadingInfo: false }));
    } catch {
      setConfirmDelete(prev => ({ ...prev, loadingInfo: false }));
    }
  };

  const confirmDeleteClient = async () => {
    const { clientId } = confirmDelete;
    setDeleteLoading(true);
    try {
      setClients(prev => prev.filter(c => c.id !== clientId));
      const response = await http.delete(`${API}/clients/${clientId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const msg = response.data?.message || 'Client supprimé avec succès';
      setConfirmDelete({ open: false, clientId: null, clientName: '', chantiersEnCours: [], devisEnAttente: [], loadingInfo: false });
      toast.info(`✅ ${msg}`);
    } catch (error) {
      console.error('Erreur suppression:', error);
      const detail = error.response?.data?.detail || 'Erreur lors de la suppression du client';
      setConfirmDelete({ open: false, clientId: null, clientName: '', chantiersEnCours: [], devisEnAttente: [], loadingInfo: false });
      toast.error(`❌ ${detail}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleClientClick = (client) => {
    setSelectedClient(client);
    setShowClientDetails(true);
  };

  if (showClientDetails && selectedClient) {
    return (
      <ClientDetailsView 
        clientId={selectedClient.id}
        onBack={() => {
          setShowClientDetails(false);
          setSelectedClient(null);
          loadClients();
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-8 text-white shadow-2xl animate-pulse">
          <div className="h-8 w-48 bg-white/20 rounded mb-3"></div>
          <div className="h-4 w-64 bg-white/10 rounded"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1,2,3].map(i => (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 animate-pulse">
              <div className="h-5 w-2/3 bg-gray-200 rounded mb-3"></div>
              <div className="h-3 w-1/2 bg-gray-100 rounded mb-2"></div>
              <div className="h-3 w-1/3 bg-gray-100 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Gestion Clients</h2>
            <p className="text-gray-600 mt-1">Base de données clients et historique des interventions</p>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={() => {
                setSelectedClient({ id: 'NO_CLIENT', nom: 'Clients occasionnels' });
                setShowClientDetails(true);
              }}
              variant="outline"
              className="rounded-2xl px-6 py-3 border-2 border-gray-300 hover:border-gray-400"
            >
              <FileText className="h-4 w-4 mr-2" />
              Recherches sans client
            </Button>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white rounded-2xl px-6 py-3 shadow-lg transform transition-all duration-200 hover:scale-105"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouveau Client
            </Button>
          </div>
        </div>

        {/* Barre de recherche */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Rechercher un client (nom, email, téléphone)..."
            className="pl-12 pr-4 py-3 rounded-2xl border-gray-200 focus:border-teal-500 focus:ring-teal-500/20 w-full"
            value={searchTerm || ''}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Client Form */}
      {showForm && (
        <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
          <CardHeader className="pb-6">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold text-gray-900">
                {editingClient ? 'Modifier le client' : 'Nouveau client'}
              </CardTitle>
              <Button
                onClick={resetForm}
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Type de clientèle (CGV) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type de client</label>
                <div className="flex gap-2">
                  {[
                    { key: 'professional', label: '🏢 Professionnel' },
                    { key: 'consumer', label: '👤 Particulier' },
                  ].map(t => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, customer_type: t.key }))}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                        formData.customer_type === t.key
                          ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-1.5">Détermine les CGV jointes aux devis de ce client (professionnelles ou particuliers).</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom/Raison sociale *</label>
                  <Input
                    value={formData.nom}
                    onChange={(e) => setFormData(prev => ({...prev, nom: e.target.value}))}
                    required
                    placeholder="Nom du client ou de l'entreprise"
                    className="rounded-xl border-gray-200 focus:border-teal-500 focus:ring-teal-500/20"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                    required
                    placeholder="contact@client.fr"
                    className="rounded-xl border-gray-200 focus:border-teal-500 focus:ring-teal-500/20"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Contact <span className="text-red-500">*</span></label>
                  <Input
                    value={formData.contact}
                    onChange={(e) => setFormData(prev => ({...prev, contact: e.target.value}))}
                    placeholder="Nom du contact principal"
                    className="rounded-xl border-gray-200 focus:border-teal-500 focus:ring-teal-500/20"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone <span className="text-red-500">*</span></label>
                  <Input
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData(prev => ({...prev, telephone: e.target.value}))}
                    placeholder="01 23 45 67 89"
                    className="rounded-xl border-gray-200 focus:border-teal-500 focus:ring-teal-500/20"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
                  <Input
                    value={formData.adresse}
                    onChange={(e) => setFormData(prev => ({...prev, adresse: e.target.value}))}
                    placeholder="Numéro et rue"
                    className="rounded-xl border-gray-200 focus:border-teal-500 focus:ring-teal-500/20"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ville <span className="text-red-500">*</span></label>
                  <Input
                    value={formData.ville}
                    onChange={(e) => setFormData(prev => ({...prev, ville: e.target.value}))}
                    placeholder="Paris, Lyon, Marseille..."
                    className="rounded-xl border-gray-200 focus:border-teal-500 focus:ring-teal-500/20"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Code postal <span className="text-red-500">*</span></label>
                  <Input
                    value={formData.code_postal}
                    onChange={(e) => setFormData(prev => ({...prev, code_postal: e.target.value}))}
                    placeholder="75001"
                    maxLength={10}
                    className="rounded-xl border-gray-200 focus:border-teal-500 focus:ring-teal-500/20"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pays</label>
                  <Input
                    value={formData.pays}
                    onChange={(e) => setFormData(prev => ({...prev, pays: e.target.value}))}
                    placeholder="France"
                    className="rounded-xl border-gray-200 focus:border-teal-500 focus:ring-teal-500/20"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    SIREN <span className="text-xs text-gray-400">(9 chiffres — obligatoire pour facturation électronique)</span>
                  </label>
                  <Input
                    value={formData.siren}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 9);
                      setFormData(prev => ({ ...prev, siren: val }));
                    }}
                    placeholder="123456789"
                    maxLength={9}
                    className="rounded-xl border-gray-200 focus:border-teal-500 focus:ring-teal-500/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">SIRET <span className="text-xs text-gray-400">(14 chiffres)</span></label>
                  <Input
                    value={formData.siret}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 14);
                      const siren = val.slice(0, 9);
                      setFormData(prev => ({ ...prev, siret: val, siren: prev.siren || siren }));
                    }}
                    placeholder="12345678900012"
                    maxLength={14}
                    className="rounded-xl border-gray-200 focus:border-teal-500 focus:ring-teal-500/20"
                  />
                  {formData.siret && formData.siret.length !== 14 && (
                    <p className="text-xs text-red-500 mt-1">
                      Il manque {14 - formData.siret.length} chiffre{14 - formData.siret.length > 1 ? 's' : ''} (14 chiffres requis)
                    </p>
                  )}
                </div>
              </div>

              <div className="pt-4 flex space-x-4">
                <Button
                  type="submit"
                  disabled={savingClient}
                  className="flex-1 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white rounded-2xl py-3 shadow-lg transform transition-all duration-200 hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {savingClient ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{editingClient ? 'Modification...' : 'Création...'}</>
                  ) : (
                    <><Users className="h-4 w-4 mr-2" />{editingClient ? 'Modifier le client' : 'Créer le client'}</>
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={resetForm}
                  disabled={savingClient}
                  variant="outline"
                  className="rounded-2xl px-6"
                >
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Clients List */}
      <div className="grid gap-4">
        {clients.length === 0 ? (
          <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
            <CardContent className="p-12 text-center">
              <Users className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun client enregistré</h3>
              <p className="text-gray-500">Ajoutez votre premier client pour commencer.</p>
            </CardContent>
          </Card>
        ) : (
          <>
          {visibleClients
            .map((client) => (
            <Card key={client.id} className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer" onClick={() => handleClientClick(client)}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center">
                        <span className="text-white font-semibold text-lg">
                          {client.nom.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{client.nom}</h3>
                        <p className="text-sm text-gray-500">Client depuis {new Date(client.created_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Mail className="h-4 w-4" />
                        <span>{client.email}</span>
                      </div>
                      {client.telephone && (
                        <div className="flex items-center space-x-2 text-sm text-gray-600">
                          <Phone className="h-4 w-4" />
                          <span>{client.telephone}</span>
                        </div>
                      )}
                      {client.adresse && (
                        <div className="flex items-center space-x-2 text-sm text-gray-600 md:col-span-2">
                          <MapPin className="h-4 w-4" />
                          <span>{client.adresse}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    <div className="flex space-x-2">
                      <Button
                        onClick={(e) => { e.stopPropagation(); editClient(client); }}
                        size="sm"
                        variant="outline"
                        className="rounded-xl flex-1"
                      >
                        Modifier
                      </Button>
                      <Button
                        onClick={(e) => { e.stopPropagation(); deleteClient(client.id, client.nom); }}
                        size="sm"
                        variant="destructive"
                        className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <Button
                      onClick={(e) => e.stopPropagation()}
                      size="sm"
                      variant="outline"
                      className="rounded-xl border-2 border-teal-500 text-teal-600 hover:bg-teal-50 w-full"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Créer un Devis
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {hasMoreClients && (
            <button
              onClick={showMoreClients}
              className="w-full py-3 text-sm font-medium text-teal-600 hover:text-teal-800 bg-white/60 rounded-2xl border border-teal-200 hover:bg-teal-50 transition-colors"
            >
              Voir plus ({totalClientsCount - visibleClients.length} restants)
            </button>
          )}
          </>
        )}
      </div>

      {/* Modale de confirmation de suppression */}
      <Dialog open={confirmDelete.open} onOpenChange={(open) => { if (!open) setConfirmDelete({ open: false, clientId: null, clientName: '', chantiersEnCours: [], devisEnAttente: [], loadingInfo: false }); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-red-600">
              <AlertTriangle className="h-6 w-6" />
              <span>Supprimer le client</span>
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-800 font-medium mb-3">
              Êtes-vous sûr de vouloir supprimer le client <span className="font-bold">"{confirmDelete.clientName}"</span> ?
            </p>

            {confirmDelete.loadingInfo && (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500"></div>
                <span className="ml-3 text-sm text-gray-500">Vérification des données associées...</span>
              </div>
            )}

            {!confirmDelete.loadingInfo && confirmDelete.chantiersEnCours.length > 0 && (
              <div className="bg-orange-50 border border-orange-300 rounded-lg p-3 mb-3">
                <p className="text-sm font-semibold text-orange-700 mb-1">⚠️ {confirmDelete.chantiersEnCours.length} chantier(s) en cours / planifié(s)</p>
                <ul className="text-sm text-orange-600 space-y-0.5">
                  {confirmDelete.chantiersEnCours.map(c => (
                    <li key={c.id} className="flex items-center space-x-1"><span>•</span><span>{c.title}</span></li>
                  ))}
                </ul>
              </div>
            )}

            {!confirmDelete.loadingInfo && confirmDelete.devisEnAttente.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 mb-3">
                <p className="text-sm font-semibold text-yellow-700 mb-1">📄 {confirmDelete.devisEnAttente.length} devis en attente / brouillon</p>
                <ul className="text-sm text-yellow-600 space-y-0.5">
                  {confirmDelete.devisEnAttente.map(d => (
                    <li key={d.id} className="flex items-center space-x-1"><span>•</span><span>{d.title || d.id}</span></li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
              <p className="text-sm font-semibold text-red-700 mb-2">Cette action supprimera également :</p>
              <ul className="text-sm text-red-600 space-y-1">
                <li className="flex items-center space-x-2">
                  <span>•</span><span>Tous les chantiers associés à ce client</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span>•</span><span>Toutes les missions/plannings liés à ces chantiers</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span>•</span><span>Tous les devis associés à ce client</span>
                </li>
              </ul>
            </div>
            <p className="text-sm text-gray-500 italic">⚠️ Cette action est irréversible.</p>
          </div>
          <div className="flex justify-end space-x-3 pt-2">
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => setConfirmDelete({ open: false, clientId: null, clientName: '', chantiersEnCours: [], devisEnAttente: [], loadingInfo: false })}
              disabled={deleteLoading}
            >
              Annuler
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
              onClick={confirmDeleteClient}
              disabled={deleteLoading || confirmDelete.loadingInfo}
            >
              {deleteLoading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Suppression...</>
              ) : (
                <><Trash2 className="h-4 w-4 mr-2" /> Confirmer la suppression</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Catalog Management Component - Complete Implementation
const CatalogManagement = () => {
  const confirm = useConfirm();
  const _cachedProducts = getCached(`${API}/catalog/products`);
  const [products, setProducts] = useState(() => _cachedProducts?.data || _cachedProducts || []);
  const [categories, setCategories] = useState([
    'Détection', 'Localisation', 'Inspection', 'Réparation',
    'Diagnostic', 'Maintenance', 'Installation', 'Mise en service',
    'Intervention urgente', 'Étude & Conseil',
    'Fourniture de matériel', "Main d'\u0153uvre", 'Déplacement', 'Autre',
  ]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productPhoto, setProductPhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // ⚡ Rendu progressif : n'affiche que les 50 premiers produits
  const { visibleItems: visibleProducts, hasMore: hasMoreProducts, showMore: showMoreProducts, totalCount: totalProductsCount } = useProgressiveList(products, 50, 50);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'Détection',
    price: '',
    unit: 'unité',
    reference: '',
    tva_rate: 20
  });

  useEffect(() => {
    perf.start('Catalog.mount');
    loadProducts();
  }, []);

  // ⚡ Realtime — rafraîchir automatiquement quand le catalogue change
  useEffect(() => {
    const handler = (e) => { if (e.detail?.table === 'catalog_products') loadProducts(); };
    window.addEventListener('realtime-update', handler);
    return () => window.removeEventListener('realtime-update', handler);
  }, []);

  const loadProducts = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      if (!products.length) setLoading(true);
      const response = await http.get(`${API}/catalog/products`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setProducts(response.data.data || []);
    } catch (error) {
      console.error('Erreur chargement produits:', error);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      perf.end('Catalog.mount');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return; // Anti double-clic (EDGE-005)
    
    try {
      setLoading(true);
      
      const productData = {
        ...formData,
        photo: productPhoto ? {
          name: productPhoto.name,
          type: productPhoto.type,
          data: productPhoto.data
        } : null
      };
      
      if (editingProduct) {
        await http.put(`${API}/catalog/products/${editingProduct.id}`, productData, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Produit modifié avec succès !');
      } else {
        await http.post(`${API}/catalog/products`, productData, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Produit ajouté avec succès !');
      }
      
      await loadProducts();
      resetForm();
    } catch (error) {
      console.error('Erreur sauvegarde produit:', error);
      toast.error('Erreur lors de la sauvegarde du produit');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'Détection',
      price: '',
      unit: 'unité',
      reference: '',
      tva_rate: 20
    });
    setProductPhoto(null);
    setUploadingPhoto(false);
    setShowForm(false);
    setEditingProduct(null);
  };

  const editProduct = (product) => {
    setFormData({
      name: product.name,
      description: product.description,
      category: product.category,
      price: product.price.toString(),
      unit: product.unit,
      reference: product.reference,
      tva_rate: product.tva_rate !== undefined ? product.tva_rate : 20
    });
    setEditingProduct(product);
    setShowForm(true);
  };

  const handleProductPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert('La photo ne doit pas dépasser 5 Mo');
      return;
    }

    try {
      setUploadingPhoto(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProductPhoto({
          name: file.name,
          type: file.type,
          data: reader.result,
          preview: reader.result
        });
        setUploadingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Erreur upload photo:', error);
      alert('Erreur lors du téléchargement de la photo');
      setUploadingPhoto(false);
    }
  };

  const removeProductPhoto = () => {
    setProductPhoto(null);
  };

  const deleteProduct = async (productId) => {
    if (!(await confirm('Êtes-vous sûr de vouloir supprimer ce produit ?'))) {
      return;
    }
    
    try {
      await http.delete(`${API}/catalog/products/${productId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setProducts(prev => prev.filter(p => p.id !== productId));
      alert('Produit supprimé avec succès !');
    } catch (error) {
      console.error('Erreur suppression produit:', error);
      alert('Erreur lors de la suppression du produit');
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Détection': return 'bg-blue-100 text-blue-700';
      case 'Localisation': return 'bg-green-100 text-green-700';
      case 'Inspection': return 'bg-purple-100 text-purple-700';
      case 'Réparation': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl p-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Catalogue Produits & Services</h2>
            <p className="text-gray-600 mt-1">Gérez votre catalogue de prestations et tarifs</p>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-2xl px-6 py-3 shadow-lg transform transition-all duration-200 hover:scale-105"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouveau Produit
          </Button>
        </div>
      </div>

      {/* Product Form */}
      {showForm && (
        <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
          <CardHeader className="pb-6">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold text-gray-900">
                {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
              </CardTitle>
              <Button
                onClick={resetForm}
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom du produit/service *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
                    required
                    placeholder="Ex: Détection réseaux électriques"
                    className="rounded-xl border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Référence</label>
                  <Input
                    value={formData.reference}
                    onChange={(e) => setFormData(prev => ({...prev, reference: e.target.value}))}
                    placeholder="Ex: DET-ELEC-001"
                    className="rounded-xl border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                  rows={3}
                  placeholder="Description détaillée du produit ou service..."
                  className="w-full rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all duration-200 p-3 resize-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({...prev, category: e.target.value}))}
                    className="w-full rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all duration-200 p-3"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prix unitaire (€) HT</label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData(prev => ({...prev, price: e.target.value}))}
                    placeholder="0.00"
                    step="0.01"
                    className="rounded-xl border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">TVA</label>
                  <select
                    value={formData.tva_rate}
                    onChange={(e) => setFormData(prev => ({...prev, tva_rate: parseFloat(e.target.value)}))}
                    className="w-full rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all duration-200 p-3"
                  >
                    <option value={20}>20%</option>
                    <option value={10}>10%</option>
                    <option value={5.5}>5,5%</option>
                    <option value={0}>0%</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Unité</label>
                  <select
                    value={formData.unit}
                    onChange={(e) => setFormData(prev => ({...prev, unit: e.target.value}))}
                    className="w-full rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all duration-200 p-3"
                  >
                    <option value="unité">unité</option>
                    <option value="mètre linéaire">mètre linéaire</option>
                    <option value="mètre carré">mètre carré</option>
                    <option value="heure">heure</option>
                    <option value="forfait">forfait</option>
                  </select>
                </div>
              </div>

              {/* Section Photo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Photo du produit (optionnel)</label>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <label className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl hover:border-indigo-400 hover:bg-indigo-50 transition-all">
                        <Camera className="h-5 w-5 text-gray-500" />
                        <span className="text-sm text-gray-600">
                          {uploadingPhoto ? 'Téléchargement...' : 'Ajouter une photo'}
                        </span>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleProductPhotoUpload}
                        className="hidden"
                        disabled={uploadingPhoto}
                      />
                    </label>
                  </div>

                  {productPhoto && (
                    <div className="relative inline-block">
                      <img
                        src={productPhoto.preview}
                        alt={productPhoto.name}
                        className="w-48 h-32 object-cover rounded-lg border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={removeProductPhoto}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <div className="mt-1 text-xs text-gray-600 truncate max-w-[12rem]">
                        {productPhoto.name}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="pt-4 flex space-x-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-2xl py-3 shadow-lg transform transition-all duration-200 hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{editingProduct ? 'Modification...' : 'Ajout...'}</>
                  ) : (
                    <><ClipboardList className="h-4 w-4 mr-2" />{editingProduct ? 'Modifier le produit' : 'Ajouter au catalogue'}</>
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={resetForm}
                  disabled={loading}
                  variant="outline"
                  className="rounded-2xl px-6"
                >
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Products List */}
      <div className="grid gap-4">
        {loading ? (
          <>
            {[1,2,3,4].map(i => (
              <Card key={i} className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg">
                <CardContent className="p-6 animate-pulse">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="h-5 w-1/3 bg-gray-200 rounded mb-3"></div>
                      <div className="h-3 w-2/3 bg-gray-100 rounded mb-2"></div>
                      <div className="h-3 w-1/4 bg-gray-100 rounded"></div>
                    </div>
                    <div className="h-8 w-20 bg-gray-200 rounded-xl"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </>
        ) : products.length === 0 ? (
          <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
            <CardContent className="p-12 text-center">
              <ClipboardList className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Catalogue vide</h3>
              <p className="text-gray-500">Ajoutez vos premiers produits et services.</p>
            </CardContent>
          </Card>
        ) : (
          <>
          {visibleProducts.map((product) => (
            <Card key={product.id} className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{product.name}</h3>
                      <Badge className={getCategoryColor(product.category)}>
                        {product.category}
                      </Badge>
                      {product.reference && (
                        <Badge variant="outline" className="text-xs">
                          {product.reference}
                        </Badge>
                      )}
                    </div>
                    
                    <p className="text-gray-600 mb-3">{product.description}</p>
                    
                    <div className="flex items-center space-x-4 text-sm">
                      <span className="text-lg font-bold text-indigo-600">
                        {product.price.toFixed(2)}€
                      </span>
                      <span className="text-gray-500">
                        par {product.unit}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => editProduct(product)}
                      size="sm"
                      variant="outline"
                      className="rounded-xl"
                    >
                      Modifier
                    </Button>
                    <Button
                      onClick={() => deleteProduct(product.id)}
                      size="sm"
                      variant="outline"
                      className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Supprimer
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {hasMoreProducts && (
            <button
              onClick={showMoreProducts}
              className="w-full py-3 text-sm font-medium text-indigo-600 hover:text-indigo-800 bg-white/60 rounded-2xl border border-indigo-200 hover:bg-indigo-50 transition-colors"
            >
              Voir plus ({totalProductsCount - visibleProducts.length} restants)
            </button>
          )}
          </>
        )}
      </div>
    </div>
  );
};

// Invite Management Component - Complete Implementation
const InviteManagementView = () => {
  const confirm = useConfirm();
  const [invites, setInvites] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [deletingMemberId, setDeletingMemberId] = useState(null);
  const [togglingMemberId, setTogglingMemberId] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    role: 'TECHNICIEN'
  });

  // Detecter si l'utilisateur courant est ADMIN ou fondateur
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.role === 'ADMIN' || currentUser.is_fondateur === true ||
    (currentUser.email || '').toLowerCase() === FOUNDER_EMAIL;

  useEffect(() => {
    loadInvites();
    if (isAdmin) loadMembers();
  }, []);

  // ⚡ Realtime — rafraîchir automatiquement quand les invitations changent
  useEffect(() => {
    const handler = (e) => { if (e.detail?.table === 'invitations') { loadInvites(); if (isAdmin) loadMembers(); } };
    window.addEventListener('realtime-update', handler);
    return () => window.removeEventListener('realtime-update', handler);
  }, []);

  const loadInvites = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      setLoading(true);
      const response = await http.get(`${API}/invitations/sent`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setInvites(response.data);
    } catch (err) {
      console.error('Erreur chargement invitations:', err);
      setError(err?.response?.data?.detail || "Erreur lors du chargement des invitations");
    } finally {
      setLoading(false);
    }
  };

  const loadMembers = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await http.get(`${API}/company/members`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setMembers(response.data || []);
    } catch (err) {
      console.error('Erreur chargement membres:', err);
    }
  };

  const deleteMember = async (memberId, memberEmail) => {
    if (!(await confirm(`Supprimer le compte de ${memberEmail} ?\n\nCette action est irr\u00e9versible. L'utilisateur perdra l'acc\u00e8s \u00e0 l'application.`))) {
      return;
    }
    try {
      setDeletingMemberId(memberId);
      await http.delete(`${API}/users/${memberId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      await loadMembers();
      await loadInvites();
      alert(`Le compte de ${memberEmail} a \u00e9t\u00e9 supprim\u00e9 avec succ\u00e8s.`);
    } catch (err) {
      alert(err?.response?.data?.detail || "Erreur lors de la suppression du membre");
    } finally {
      setDeletingMemberId(null);
    }
  };

  const toggleMemberActive = async (memberId, memberEmail, currentlyActive) => {
    const action = currentlyActive ? 'd\u00e9sactiver' : 'r\u00e9activer';
    if (!(await confirm(`Voulez-vous ${action} le compte de ${memberEmail} ?${currentlyActive ? "\n\nL'utilisateur ne pourra plus se connecter et toute session en cours sera coup\u00e9e." : ''}`))) {
      return;
    }
    try {
      setTogglingMemberId(memberId);
      await http.patch(`${API}/users/${memberId}/status`, { actif: !currentlyActive }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      await loadMembers();
      toast.success(currentlyActive ? `Compte de ${memberEmail} d\u00e9sactiv\u00e9` : `Compte de ${memberEmail} r\u00e9activ\u00e9`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la mise \u00e0 jour du statut");
    } finally {
      setTogglingMemberId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = {
        email: formData.email.toLowerCase(),
        role: formData.role
      };
      const response = await http.post(`${API}/invitations/send`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Recharger la liste complète depuis le serveur
      await loadInvites();
      resetForm();
      toast.success('Invitation envoyée avec succès !');
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi de l'invitation");
      setError(err?.response?.data?.detail || "Erreur lors de l'envoi de l'invitation");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      role: 'TECHNICIEN'
    });
    setShowForm(false);
  };

  const resendInvite = async (inviteId) => {
    // Pour renvoyer: annuler l'ancienne et créer une nouvelle avec le même email
    // On capture email/role avant toute opération async (le Realtime peut vider le state entre-temps)
    const invite = invites.find(inv => inv.id === inviteId);
    if (!invite) return;
    const { email, role } = invite;

    try {
      // Annuler l'ancienne — ignorer 404 (Realtime l'a peut-être déjà supprimée du state)
      try {
        await http.delete(`${API}/invitations/${inviteId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      } catch (deleteErr) {
        if (deleteErr?.response?.status !== 404) throw deleteErr;
      }
      
      // Envoyer une nouvelle
      await http.post(`${API}/invitations/send`, {
        email,
        role
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      await loadInvites();
      toast.success('Invitation renvoyée avec succès !');
    } catch (err) {
      console.error('Erreur renvoi invitation:', err);
      toast.error(err?.response?.data?.detail || "Erreur lors du renvoi de l'invitation");
    }
  };

  const cancelInvite = async (inviteId) => {
    if (!(await confirm('Êtes-vous sûr de vouloir annuler cette invitation ?'))) {
      return;
    }
    
    try {
      await http.delete(`${API}/invitations/${inviteId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      await loadInvites();
      toast.success('Invitation annulée.');
    } catch (err) {
      console.error('Erreur annulation invitation:', err);
      toast.error(err?.response?.data?.detail || "Erreur lors de l'annulation de l'invitation");
    }
  };

  const getStatusColor = (status) => {
    const normalizedStatus = (status || '').toLowerCase();
    switch (normalizedStatus) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'accepted': return 'bg-green-100 text-green-700';
      case 'expired': return 'bg-red-100 text-red-700';
      case 'cancelled': return 'bg-gray-100 text-gray-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'ADMIN': return 'bg-purple-100 text-purple-700';
      case 'BUREAU': return 'bg-blue-100 text-blue-700';
      case 'TECHNICIEN': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl border-0 shadow-2xl p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h2 className="text-lg sm:text-2xl font-semibold text-gray-900">Inviter des Techniciens</h2>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Seuls les techniciens (User) peuvent être invités par un administrateur</p>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 text-white rounded-xl sm:rounded-2xl px-4 sm:px-6 py-2.5 sm:py-3 shadow-lg transform transition-all duration-200 hover:scale-105 text-sm sm:text-base flex-shrink-0"
          >
            <Plus className="h-4 w-4 mr-1.5 sm:mr-2" />
            Inviter un utilisateur
          </Button>
        </div>
      </div>

      {/* Invite Form */}
      {showForm && (
        <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
          <CardHeader className="pb-6">
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl font-semibold text-gray-900">Inviter un technicien</CardTitle>
              <Button
                onClick={resetForm}
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Adresse email *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                    required
                    placeholder="utilisateur@email.fr"
                    className="rounded-xl border-gray-200 focus:border-pink-500 focus:ring-pink-500/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rôle *</label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(value) => setFormData(prev => ({...prev, role: value}))}
                  >
                    <SelectTrigger className="rounded-xl border-gray-200 focus:border-pink-500 focus:ring-pink-500/20">
                      <SelectValue placeholder="Sélectionner un rôle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TECHNICIEN">Technicien / User</SelectItem>
                      <SelectItem value="BUREAU">Bureau</SelectItem>
                      <SelectItem value="ADMIN">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="bg-pink-50 rounded-xl p-4">
                <h4 className="font-medium text-pink-800 mb-2">Permissions du rôle sélectionné :</h4>
                {formData.role === 'TECHNICIEN' ? (
                  <ul className="text-sm text-pink-700 space-y-1">
                    <li>• Créer et consulter ses propres recherches</li>
                    <li>• Générer des rapports PDF</li>
                    <li>• Partager avec le bureau</li>
                  </ul>
                ) : formData.role === 'BUREAU' ? (
                  <ul className="text-sm text-pink-700 space-y-1">
                    <li>• Toutes les permissions du Technicien</li>
                    <li>• Voir toutes les recherches de la société</li>
                    <li>• Gérer les clients, devis, chantiers</li>
                    <li>• Gérer le planning et les équipes</li>
                    <li>• Gérer le matériel et les équipements</li>
                    <li>❌ Ne peut pas inviter de membres</li>
                    <li>❌ Pas d'accès aux statistiques</li>
                  </ul>
                ) : (
                  <ul className="text-sm text-pink-700 space-y-1">
                    <li>• Toutes les permissions du Bureau</li>
                    <li>• Inviter de nouveaux utilisateurs</li>
                    <li>• Accès aux statistiques de l'entreprise</li>
                    <li>• Supprimer des plannings</li>
                    <li>• Gérer tous les aspects de l'entreprise</li>
                  </ul>
                )}
              </div>

              {error && (
                <div className="text-red-600 text-sm">{error}</div>
              )}

              <div className="pt-4 flex space-x-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 text-white rounded-2xl py-3 shadow-lg transform transition-all duration-200 hover:scale-105"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  {loading ? 'Envoi en cours...' : "Envoyer l'invitation"}
                </Button>
                <Button
                  type="button"
                  onClick={resetForm}
                  variant="outline"
                  className="rounded-2xl px-6"
                >
                  Annuler
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Error Display */}
      {error && !showForm && (
        <Card className="bg-red-50 border-red-200 rounded-3xl border shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3 text-red-800">
              <AlertCircle className="h-5 w-5" />
              <p className="font-medium">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Members Section - Admin Only */}
      {isAdmin && (() => {
        // Exclure les techniciens créés sans invitation email (adresse générée @temp-skyapp.local)
        const visibleMembers = members.filter((m) => !m.email?.includes('@temp-skyapp.local'));
        return (
        <Card className="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl border-0 shadow-2xl">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-purple-600" />
                <CardTitle className="text-lg font-semibold text-gray-900">Membres de l'entreprise</CardTitle>
                <span className="text-xs text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">{visibleMembers.length}</span>
              </div>
              <Button onClick={loadMembers} variant="ghost" size="sm" className="text-gray-500 hover:text-gray-700 text-xs">
                Rafraîchir
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-4 sm:px-6 pb-6">
            {visibleMembers.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">Aucun membre trouvé</p>
            ) : (
              <div className="space-y-3">
                {visibleMembers.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-9 w-9 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                        {(member.email || '?')[0].toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-900 truncate">{member.email}</span>
                          <Badge className={`text-xs ${getRoleColor(member.role)}`}>{member.role}</Badge>
                          {member.is_fondateur && (
                            <Badge className="text-xs bg-amber-100 text-amber-700">Fondateur</Badge>
                          )}
                          {member.actif === false && (
                            <Badge className="text-xs bg-red-100 text-red-700">Désactivé</Badge>
                          )}
                          {!member.can_delete && !member.is_fondateur && member.role !== 'ADMIN' && (
                            <Badge className="text-xs bg-gray-100 text-gray-500">Vous</Badge>
                          )}
                        </div>
                        {member.full_name && (
                          <p className="text-xs text-gray-500 mt-0.5">{member.full_name}</p>
                        )}
                      </div>
                    </div>
                    {member.can_delete && (
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                        <Button
                          onClick={() => toggleMemberActive(member.id, member.email, member.actif !== false)}
                          disabled={togglingMemberId === member.id}
                          variant="ghost"
                          size="sm"
                          className={member.actif === false ? "text-green-600 hover:text-green-700 hover:bg-green-50 rounded-xl" : "text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-xl"}
                        >
                          {togglingMemberId === member.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : member.actif === false ? (
                            'Réactiver'
                          ) : (
                            <Lock className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          onClick={() => deleteMember(member.id, member.email)}
                          disabled={deletingMemberId === member.id}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl"
                        >
                          {deletingMemberId === member.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        );
      })()}

      {/* Invites List */}
      <div className="grid gap-4">
        {loading && !showForm ? (
          <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
            <CardContent className="p-12 text-center">
              <Loader2 className="h-16 w-16 mx-auto mb-4 text-pink-600 animate-spin" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Chargement...</h3>
              <p className="text-gray-500">Récupération des invitations en cours</p>
            </CardContent>
          </Card>
        ) : invites.length === 0 ? (
          <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
            <CardContent className="p-12 text-center">
              <UserPlus className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucune invitation envoyée</h3>
              <p className="text-gray-500">Invitez vos premiers collaborateurs pour commencer.</p>
            </CardContent>
          </Card>
        ) : (
          invites.map((invite) => {
            const isPending = (invite.status || '').toUpperCase() === 'PENDING';
            const isAccepted = (invite.status || '').toUpperCase() === 'ACCEPTED';
            const expired = isPending && isExpired(invite.expires_at);
            
            return (
              <Card key={invite.id} className="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl flex-shrink-0 flex items-center justify-center">
                          <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm sm:text-lg font-semibold text-gray-900 truncate">{invite.email}</h3>
                          <div className="flex flex-wrap items-center gap-1.5 sm:space-x-2 mt-1">
                            <Badge className={`text-xs ${getRoleColor(invite.role)}`}>
                              {invite.role === 'TECHNICIEN' ? 'Technicien / User' : 
                               invite.role === 'BUREAU' ? 'Bureau' : 
                               invite.role === 'ADMIN' ? 'Admin' : invite.role}
                            </Badge>
                            <Badge className={`text-xs ${getStatusColor(invite.status)}`}>
                              {isPending ? (expired ? 'Expirée' : 'En attente') : 
                               isAccepted ? 'Acceptée' : 
                               invite.status === 'CANCELLED' ? 'Annulée' : invite.status}
                            </Badge>
                            {expired && (
                              <Badge className="bg-red-100 text-red-700 text-xs">
                                <Clock className="h-3 w-3 mr-1" />
                                Expirée
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="ml-12 sm:ml-13 space-y-1 sm:grid sm:grid-cols-2 sm:gap-4 sm:space-y-0 mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600">
                        <div>
                          <span className="font-medium">Envoyée le:</span> {formatDate(invite.created_at)}
                        </div>
                        {isPending && (
                          <div className={expired ? 'text-red-600' : ''}>
                            <span className="font-medium">Expire le:</span> {formatDate(invite.expires_at)}
                          </div>
                        )}
                        {isAccepted && invite.accepted_at && (
                          <div className="text-green-600">
                            <span className="font-medium">Acceptée le:</span> {formatDate(invite.accepted_at)}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex space-x-2 ml-12 sm:ml-0 sm:flex-shrink-0">
                      {isPending && !expired && (
                        <>
                          <Button
                            onClick={() => resendInvite(invite.id)}
                            size="sm"
                            variant="outline"
                            className="rounded-xl text-xs sm:text-sm"
                          >
                            <Mail className="h-3 w-3 mr-1" />
                            Renvoyer
                          </Button>
                          <Button
                            onClick={() => cancelInvite(invite.id)}
                            size="sm"
                            variant="outline"
                            className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50 text-xs sm:text-sm"
                          >
                            <X className="h-3 w-3 mr-1" />
                            Annuler
                          </Button>
                        </>
                      )}
                      {isPending && expired && (
                        <Button
                          onClick={() => resendInvite(invite.id)}
                          size="sm"
                          className="bg-pink-600 hover:bg-pink-700 text-white rounded-xl text-xs sm:text-sm"
                        >
                          <Mail className="h-3 w-3 mr-1" />
                          Renvoyer
                        </Button>
                      )}
                      {isAccepted && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="rounded-xl text-green-600 text-xs sm:text-sm"
                          disabled
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Acceptée
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};
// Gestion du Matériel (liste, ajout, édition, suppression, QR codes, maintenance)
import { api } from './lib/supabase'
const MaterialManagement = () => {
  // ═══════════════════════════════════════════════════
  //  ÉTATS PRINCIPAUX
  // ═══════════════════════════════════════════════════
  const _cachedMaterials = getCached(`${API}/materials`);
  const [items, setItems] = useState(Array.isArray(_cachedMaterials) ? _cachedMaterials : (_cachedMaterials?.data || []))
  const [loading, setLoading] = useState(!_cachedMaterials)
  const [error, setError] = useState('')
  const [me, setMe] = useState(null)
  const [activeTab, setActiveTab] = useState('inventory')
  // Détail d'un matériel sélectionné
  const [selectedItem, setSelectedItem] = useState(null)
  const [maintenanceLogs, setMaintenanceLogs] = useState([])
  const [logsLoading, setLogsLoading] = useState(false)
  // Formulaire d'ajout/édition
  const [form, setForm] = useState({
    id: null,
    name: '',
    description: '',
    category: 'TOOL',
    location: '',
    serial_number: '',
    brand: '',
    model: '',
    purchase_date: '',
    warranty_end: '',
    condition: 'BON',
    maintenance_interval_days: '',
    notes: '',
    qr_code: ''
  })
  const [saving, setSaving] = useState(false)
  // Formulaire maintenance
  const [maintenanceForm, setMaintenanceForm] = useState({
    maintenance_type: 'ENTRETIEN',
    description: '',
    cost: '',
    performed_at: new Date().toISOString().split('T')[0],
    next_due_date: ''
  })
  const [savingMaintenance, setSavingMaintenance] = useState(false)
  // Scanner QR
  const [scanning, setScanning] = useState(false)
  const videoRef = useRef(null)
  const [scanResult, setScanResult] = useState('')
  // Filtres
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState('ALL')
  const [filterCondition, setFilterCondition] = useState('ALL')
  // Confirmation suppression
  const [deleteItem, setDeleteItem] = useState(null)
  // Aperçu QR
  const [qrPreviewUrl, setQrPreviewUrl] = useState('')

  // ═══════════════════════════════════════════════════
  //  CONSTANTES
  // ═══════════════════════════════════════════════════
  const CATEGORIES = [
    { value: 'TOOL', label: 'Outil', icon: '🔧' },
    { value: 'SAFETY', label: 'Sécurité', icon: '🦺' },
    { value: 'MEASUREMENT', label: 'Mesure', icon: '📏' },
    { value: 'VEHICLE', label: 'Véhicule', icon: '🚛' },
    { value: 'EQUIPMENT', label: 'Équipement lourd', icon: '⚙️' },
    { value: 'CONSUMABLE', label: 'Consommable', icon: '📦' }
  ]

  const CONDITIONS = [
    { value: 'NEUF', label: 'Neuf', color: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
    { value: 'BON', label: 'Bon état', color: 'bg-blue-100 text-blue-800', dot: 'bg-blue-500' },
    { value: 'USAGE', label: 'Usé', color: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
    { value: 'A_REVISER', label: 'À réviser', color: 'bg-orange-100 text-orange-800', dot: 'bg-orange-500' },
    { value: 'FIN_DE_VIE', label: 'Fin de vie', color: 'bg-red-100 text-red-800', dot: 'bg-red-500' }
  ]

  const MAINTENANCE_TYPES = [
    { value: 'ENTRETIEN', label: 'Entretien courant', icon: '🔧' },
    { value: 'REPARATION', label: 'Réparation', icon: '🛠️' },
    { value: 'INSPECTION', label: 'Inspection', icon: '🔍' },
    { value: 'CONTROLE', label: 'Contrôle réglementaire', icon: '📋' },
    { value: 'REMPLACEMENT_PIECE', label: 'Remplacement pièce', icon: '🔩' }
  ]

  // ═══════════════════════════════════════════════════
  //  UTILITAIRES
  // ═══════════════════════════════════════════════════
  const getConditionInfo = (cond) => CONDITIONS.find(c => c.value === cond) || CONDITIONS[1]
  const getCategoryInfo = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[0]
  const getMaintenanceTypeInfo = (type) => MAINTENANCE_TYPES.find(t => t.value === type) || MAINTENANCE_TYPES[0]

  const isMaintenanceDue = (item) => {
    if (!item.next_maintenance_date) return false
    return new Date(item.next_maintenance_date) <= new Date()
  }

  const isMaintenanceSoon = (item) => {
    if (!item.next_maintenance_date) return false
    const diff = new Date(item.next_maintenance_date) - new Date()
    return diff > 0 && diff < 7 * 24 * 60 * 60 * 1000
  }

  const daysUntilMaintenance = (item) => {
    if (!item.next_maintenance_date) return null
    const diff = new Date(item.next_maintenance_date) - new Date()
    return Math.ceil(diff / (24 * 60 * 60 * 1000))
  }

  const cryptoRandom = () => {
    try { return 'SKY_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8).toUpperCase() }
    catch { return 'SKY_' + Date.now() }
  }

  // ═══════════════════════════════════════════════════
  //  FILTRAGE MÉMORISÉ
  // ═══════════════════════════════════════════════════
  const filteredItems = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    return items.filter(m => {
      const okSearch = !term || [m.name, m.description, m.location, m.serial_number, m.brand, m.model, m.qr_code].some(x => (x || '').toLowerCase().includes(term))
      const okCat = filterCategory === 'ALL' || (m.category || '') === filterCategory
      const okCond = filterCondition === 'ALL' || (m.condition || 'BON') === filterCondition
      return okSearch && okCat && okCond
    })
  }, [items, searchTerm, filterCategory, filterCondition])

  // ⚡ Rendu progressif : n'affiche que les 50 premiers matériels
  const { visibleItems: visibleMaterials, hasMore: hasMoreMaterials, showMore: showMoreMaterials, totalCount: totalMaterialsCount } = useProgressiveList(filteredItems, 50, 50);

  // Stats
  const stats = React.useMemo(() => ({
    total: items.length,
    neuf: items.filter(m => m.condition === 'NEUF').length,
    bon: items.filter(m => m.condition === 'BON' || !m.condition).length,
    usage: items.filter(m => m.condition === 'USAGE').length,
    a_reviser: items.filter(m => m.condition === 'A_REVISER').length,
    fin_de_vie: items.filter(m => m.condition === 'FIN_DE_VIE' || m.end_of_life).length,
    maintenance_due: items.filter(m => isMaintenanceDue(m)).length
  }), [items])

  // ═══════════════════════════════════════════════════
  //  CHARGEMENT DES DONNÉES
  // ═══════════════════════════════════════════════════
  const load = async () => {
    perf.start('Materials.mount');
    if (!items.length) setLoading(true)
    const { data, error } = await api.materials.getAll()
    if (error) setError(String(error.message || error))
    setItems(data || [])
    perf.end('Materials.mount');
    setLoading(false)
  }

  const loadMaintenanceLogs = async (materialId) => {
    setLogsLoading(true)
    const { data, error } = await api.materials.getMaintenanceLogs(materialId)
    if (error) { setError(String(error.message || error)); setMaintenanceLogs([]) }
    else setMaintenanceLogs(data || [])
    setLogsLoading(false)
  }

  useEffect(() => { 
    load(); 
    (async () => { 
      const { data } = await api.users.getCurrent(); 
      if (data) {
        setMe(data)
      } else {
        // Fallback: utiliser le user stocké en localStorage
        const stored = localStorage.getItem('user')
        if (stored) setMe(JSON.parse(stored))
      }
    })() 
  }, [])
  useEffect(() => { return () => { try { stopScan() } catch {} } }, [])

  // ⚡ Realtime — rafraîchir automatiquement quand le matériel change (debounced)
  const materialRealtimeRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.table === 'materials') {
        clearTimeout(materialRealtimeRef.current);
        materialRealtimeRef.current = setTimeout(() => load(), 300);
      }
    };
    window.addEventListener('realtime-update', handler);
    return () => { window.removeEventListener('realtime-update', handler); clearTimeout(materialRealtimeRef.current); };
  }, []);

  // ═══════════════════════════════════════════════════
  //  CRUD MATÉRIEL
  // ═══════════════════════════════════════════════════
  const resetForm = () => setForm({
    id: null, name: '', description: '', category: 'TOOL', location: '',
    serial_number: '', brand: '', model: '', purchase_date: '', warranty_end: '',
    condition: 'BON', maintenance_interval_days: '', notes: '', qr_code: ''
  })

  const onSubmit = async (e) => {
    e.preventDefault()
    if (saving) return // Anti double-clic (EDGE-005)
    setSaving(true)
    setError('')
    try {
      const newQr = form.id ? form.qr_code : cryptoRandom()
      const userStr = localStorage.getItem('user')
      const currentUser = userStr ? JSON.parse(userStr) : null
      const payload = {
        name: form.name,
        description: form.description || null,
        category: form.category,
        location: form.location || null,
        serial_number: form.serial_number || null,
        brand: form.brand || null,
        model: form.model || null,
        purchase_date: form.purchase_date || null,
        warranty_end: form.warranty_end || null,
        condition: form.condition || 'BON',
        maintenance_interval_days: form.maintenance_interval_days ? parseInt(form.maintenance_interval_days) : null,
        notes: form.notes || null,
        qr_code: newQr,
        end_of_life: form.condition === 'FIN_DE_VIE',
        company_id: currentUser?.company_id
      }
      if (form.id) {
        const { error } = await api.materials.update(form.id, payload)
        if (error) throw error
      } else {
        const { data, error } = await api.materials.create(payload)
        if (error) throw error
      }
      const url = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(newQr)}`
      setQrPreviewUrl(url)
      await load()
      resetForm()
      setActiveTab('inventory')
    } catch (err) {
      setError(String(err.message || err))
    } finally { setSaving(false) }
  }

  const onEdit = (m) => {
    setForm({
      id: m.id,
      name: m.name || '',
      description: m.description || '',
      category: m.category || 'TOOL',
      location: m.location || '',
      serial_number: m.serial_number || '',
      brand: m.brand || '',
      model: m.model || '',
      purchase_date: m.purchase_date || '',
      warranty_end: m.warranty_end || '',
      condition: m.condition || 'BON',
      maintenance_interval_days: m.maintenance_interval_days || '',
      notes: m.notes || '',
      qr_code: m.qr_code || ''
    })
    setActiveTab('form')
  }

  const onDeleteConfirmed = async () => {
    if (!deleteItem) return
    const { error } = await api.materials.remove(deleteItem.id)
    if (error) { setError(String(error.message || error)) }
    else { await load(); if (selectedItem?.id === deleteItem.id) { setSelectedItem(null) } }
    setDeleteItem(null)
  }

  // ═══════════════════════════════════════════════════
  //  MAINTENANCE
  // ═══════════════════════════════════════════════════
  const resetMaintenanceForm = () => setMaintenanceForm({
    maintenance_type: 'ENTRETIEN', description: '', cost: '',
    performed_at: new Date().toISOString().split('T')[0], next_due_date: ''
  })

  const onSubmitMaintenance = async (e) => {
    e.preventDefault()
    if (!selectedItem || !me) return
    setSavingMaintenance(true)
    setError('')
    try {
      const log = {
        material_id: selectedItem.id,
        company_id: me.company_id,
        performed_by: me.id,
        maintenance_type: maintenanceForm.maintenance_type,
        description: maintenanceForm.description || null,
        cost: maintenanceForm.cost ? parseFloat(maintenanceForm.cost) : null,
        performed_at: maintenanceForm.performed_at || new Date().toISOString(),
        next_due_date: maintenanceForm.next_due_date || null
      }
      const { error } = await api.materials.addMaintenanceLog(log)
      if (error) throw error
      // Mettre à jour la date de dernière et prochaine maintenance sur le matériel
      const updates = {
        last_maintenance_date: maintenanceForm.performed_at || new Date().toISOString().split('T')[0]
      }
      if (maintenanceForm.next_due_date) {
        updates.next_maintenance_date = maintenanceForm.next_due_date
      } else if (selectedItem.maintenance_interval_days) {
        const next = new Date(maintenanceForm.performed_at || new Date())
        next.setDate(next.getDate() + parseInt(selectedItem.maintenance_interval_days))
        updates.next_maintenance_date = next.toISOString().split('T')[0]
      }
      await api.materials.update(selectedItem.id, updates)
      await loadMaintenanceLogs(selectedItem.id)
      await load()
      // Rafraîchir l'item sélectionné
      const refreshed = (await api.materials.scanQR(selectedItem.qr_code))?.data
      if (refreshed) setSelectedItem(refreshed)
      resetMaintenanceForm()
    } catch (err) {
      setError(String(err.message || err))
    } finally { setSavingMaintenance(false) }
  }

  // ═══════════════════════════════════════════════════
  //  QR SCANNER
  // ═══════════════════════════════════════════════════
  const startScan = async () => {
    setScanResult('')
    setScanning(true)
    try {
      if (!navigator.mediaDevices?.getUserMedia) { setError('Caméra non disponible'); setScanning(false); return }
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play() }
      if ('BarcodeDetector' in window) {
        const detector = new window.BarcodeDetector({ formats: ['qr_code'] })
        const tick = async () => {
          if (!videoRef.current?.srcObject) return
          try {
            const detections = await detector.detect(videoRef.current)
            if (detections?.[0]?.rawValue) {
              const code = detections[0].rawValue
              setScanResult(code)
              await onScanFound(code)
              stopScan()
              return
            }
          } catch {}
          requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
      }
    } catch (e) { setError(String(e.message || e)); setScanning(false) }
  }

  const stopScan = () => {
    setScanning(false)
    const v = videoRef.current
    if (v?.srcObject) { v.srcObject.getTracks().forEach(t => t.stop()); v.srcObject = null }
  }

  const onScanFound = async (qr) => {
    try {
      const { data, error } = await api.materials.scanQR(qr)
      if (error) throw error
      if (data) {
        setSelectedItem(data)
        await loadMaintenanceLogs(data.id)
        setActiveTab('inventory')
      } else { setError('QR non reconnu dans votre inventaire') }
    } catch (e) { setError(String(e.message || e)) }
  }

  const openDetail = async (item) => {
    setSelectedItem(item)
    await loadMaintenanceLogs(item.id)
  }

  const closeDetail = () => {
    setSelectedItem(null)
    setMaintenanceLogs([])
    resetMaintenanceForm()
  }

  // ═══════════════════════════════════════════════════
  //  RENDU
  // ═══════════════════════════════════════════════════
  return (
    <div className="space-y-6">
      {/* En-tête avec statistiques */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: 'Total',        value: stats.total,           icon: Package,       grad: 'from-blue-600 to-violet-600',   bg: 'bg-white',          num: 'text-gray-800' },
          { label: 'Neuf',         value: stats.neuf,            icon: Star,          grad: 'from-emerald-400 to-teal-500',  bg: 'bg-emerald-50',     num: 'text-emerald-700' },
          { label: 'Bon état',     value: stats.bon,             icon: Check,         grad: 'from-blue-400 to-blue-600',     bg: 'bg-blue-50',        num: 'text-blue-700' },
          { label: 'Usé',          value: stats.usage,           icon: Clock,         grad: 'from-amber-400 to-orange-400',  bg: 'bg-amber-50',       num: 'text-amber-700' },
          { label: 'À réviser',    value: stats.a_reviser,       icon: Wrench,        grad: 'from-orange-500 to-red-400',    bg: 'bg-orange-50',      num: 'text-orange-700' },
          { label: 'Fin de vie',   value: stats.fin_de_vie,      icon: AlertTriangle, grad: 'from-red-500 to-rose-600',      bg: 'bg-red-50',         num: 'text-red-700' },
          { label: 'Entretien dû', value: stats.maintenance_due, icon: Calendar,      grad: 'from-violet-500 to-purple-600', bg: 'bg-purple-50',      num: 'text-purple-700' },
        ].map((s, i) => (
          <div key={i} className={`${s.bg} rounded-2xl p-3 flex flex-col items-center justify-center border border-white/60 shadow-sm hover:shadow-md transition-shadow`}>
            <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${s.grad} flex items-center justify-center mb-2 shadow-sm`}>
              <s.icon className="h-4 w-4 text-white" />
            </div>
            <div className={`text-2xl font-bold ${s.num} leading-none`}>{s.value}</div>
            <div className="text-[11px] text-gray-500 mt-1 text-center leading-tight">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Onglets principaux */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-gray-200 rounded-2xl p-1 shadow-sm">
          <TabsTrigger value="inventory" className="rounded-xl data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all">
            <Package className="h-4 w-4 mr-2" />Inventaire
          </TabsTrigger>
          <TabsTrigger value="form" className="rounded-xl data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all">
            <Plus className="h-4 w-4 mr-2" />{form.id ? 'Modifier' : 'Ajouter'}
          </TabsTrigger>
          <TabsTrigger value="scan" className="rounded-xl data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all">
            <Camera className="h-4 w-4 mr-2" />Scanner QR
          </TabsTrigger>
        </TabsList>

        {/* ONGLET INVENTAIRE */}
        <TabsContent value="inventory" className="mt-4 space-y-4">
          {/* Filtres */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 flex flex-wrap gap-3 items-center">
            <Input placeholder="🔍 Rechercher (nom, série, marque, QR...)" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="rounded-xl flex-1 min-w-[200px] border-gray-200 focus:ring-violet-400" />
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="rounded-xl w-44 border-gray-200"><SelectValue placeholder="Catégorie" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Toutes catégories</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.icon} {c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterCondition} onValueChange={setFilterCondition}>
              <SelectTrigger className="rounded-xl w-36 border-gray-200"><SelectValue placeholder="État" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Tous états</SelectItem>
                {CONDITIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-sm text-gray-400 ml-auto whitespace-nowrap">{filteredItems.length} résultat{filteredItems.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Grille de cartes */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3,4,5,6].map(i => (
                <div key={`sk-mat-${i}`} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 animate-pulse">
                  <div className="h-4 w-1/3 bg-gray-200 rounded mb-3"></div>
                  <div className="h-5 w-2/3 bg-gray-200 rounded mb-4"></div>
                  <div className="h-3 w-1/2 bg-gray-100 rounded mb-2"></div>
                  <div className="h-3 w-1/4 bg-gray-100 rounded"></div>
                </div>
              ))}
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Package className="h-12 w-12 mx-auto mb-3 opacity-40" />
              <p className="text-lg font-medium">Aucun matériel</p>
              <p className="text-sm">Ajoutez votre premier équipement via l'onglet "Ajouter"</p>
            </div>
          ) : (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {visibleMaterials.map((m) => {
                const condInfo = getConditionInfo(m.condition || 'BON')
                const catInfo = getCategoryInfo(m.category)
                const maintenanceDue = isMaintenanceDue(m)
                const maintenanceSoon = isMaintenanceSoon(m)
                const daysLeft = daysUntilMaintenance(m)

                return (
                  <div
                    key={m.id}
                    onClick={() => openDetail(m)}
                    className={`bg-white rounded-2xl border ${
                      m.end_of_life || m.condition === 'FIN_DE_VIE' ? 'border-red-200' :
                      maintenanceDue ? 'border-purple-200' :
                      maintenanceSoon ? 'border-amber-200' :
                      'border-gray-100 hover:border-violet-200'
                    } shadow-sm hover:shadow-lg transition-all cursor-pointer overflow-hidden group`}
                  >
                    {/* Bande colorée en haut */}
                    <div className={`h-1 w-full ${
                      m.end_of_life || m.condition === 'FIN_DE_VIE' ? 'bg-gradient-to-r from-red-500 to-rose-500' :
                      maintenanceDue ? 'bg-gradient-to-r from-violet-500 to-purple-600' :
                      maintenanceSoon ? 'bg-gradient-to-r from-amber-400 to-orange-400' :
                      m.condition === 'NEUF' ? 'bg-gradient-to-r from-emerald-400 to-teal-500' :
                      'bg-gradient-to-r from-blue-500 to-violet-500'
                    }`} />

                    <div className="p-4">
                      {/* Header: icône + nom + badge */}
                      <div className="flex items-start justify-between mb-3 gap-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-xl flex-shrink-0 group-hover:bg-gray-200 transition-colors">
                            {catInfo.icon}
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-gray-900 leading-tight truncate">{m.name}</h3>
                            {(m.brand || m.model) && (
                              <p className="text-xs text-gray-400 truncate">{[m.brand, m.model].filter(Boolean).join(' · ')}</p>
                            )}
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${condInfo.color}`}>
                          {condInfo.label}
                        </span>
                      </div>

                      {/* Détails */}
                      <div className="space-y-1.5 text-sm text-gray-600 mb-3">
                        {m.serial_number && (
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-gray-400 w-14 flex-shrink-0">N° série</span>
                            <span className="font-mono text-xs bg-gray-50 px-1.5 py-0.5 rounded text-gray-700">{m.serial_number}</span>
                          </div>
                        )}
                        {m.location && (
                          <div className="flex items-center gap-1.5">
                            <MapPin className="h-3 w-3 text-gray-400 flex-shrink-0" />
                            <span className="text-xs text-gray-600 truncate">{m.location}</span>
                          </div>
                        )}
                      </div>

                      {/* Alertes + QR */}
                      <div className="flex items-end justify-between gap-2">
                        <div className="flex flex-wrap gap-1">
                          {(m.end_of_life || m.condition === 'FIN_DE_VIE') && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-700">
                              <AlertTriangle className="h-2.5 w-2.5" /> Fin de vie
                            </span>
                          )}
                          {maintenanceDue && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 text-purple-700">
                              <Wrench className="h-2.5 w-2.5" /> Entretien en retard
                            </span>
                          )}
                          {maintenanceSoon && !maintenanceDue && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-700">
                              <Clock className="h-2.5 w-2.5" /> {daysLeft}j
                            </span>
                          )}
                          {m.assigned_to && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-700">
                              <User className="h-2.5 w-2.5" /> Assigné
                            </span>
                          )}
                        </div>
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=50x50&data=${encodeURIComponent(m.qr_code)}`}
                          alt="QR"
                          className="w-9 h-9 rounded opacity-40 group-hover:opacity-80 transition-opacity flex-shrink-0"
                        />
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            {hasMoreMaterials && (
              <button
                onClick={showMoreMaterials}
                className="w-full mt-4 py-3 text-sm font-medium text-blue-600 hover:text-blue-800 bg-white/60 rounded-2xl border border-blue-200 hover:bg-blue-50 transition-colors"
              >
                Voir plus ({totalMaterialsCount - visibleMaterials.length} restants)
              </button>
            )}
            </>
          )}
        </TabsContent>

        {/* ONGLET FORMULAIRE */}
        <TabsContent value="form" className="mt-4">
          <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
            <CardHeader>
              <CardTitle>{form.id ? '✏️ Modifier l\'équipement' : '➕ Nouvel équipement'}</CardTitle>
              <CardDescription>Renseignez les informations de l'équipement. Un QR code unique sera généré automatiquement.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={onSubmit} className="space-y-4">
                {/* Ligne 1: Nom + Marque + Modèle */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Nom de l'équipement *</label>
                    <Input placeholder="ex: Treuil hydraulique" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-xl" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Marque</label>
                    <Input placeholder="ex: Bosch" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="rounded-xl" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Modèle</label>
                    <Input placeholder="ex: GBH 2-26" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="rounded-xl" />
                  </div>
                </div>

                {/* Ligne 2: N° série + Catégorie + État */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">N° de série</label>
                    <Input placeholder="ex: SN-2024-0042" value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="rounded-xl font-mono" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Catégorie</label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.icon} {c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">État</label>
                    <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CONDITIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Ligne 3: Localisation + Date achat + Fin garantie */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Localisation / Dépôt</label>
                    <Input placeholder="ex: Entrepôt Nord" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-xl" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Date d'achat</label>
                    <Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="rounded-xl" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Fin de garantie</label>
                    <Input type="date" value={form.warranty_end} onChange={(e) => setForm({ ...form, warranty_end: e.target.value })} className="rounded-xl" />
                  </div>
                </div>

                {/* Ligne 4: Intervalle maintenance + Description */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Intervalle entretien (jours)</label>
                    <Input type="number" placeholder="ex: 90" value={form.maintenance_interval_days} onChange={(e) => setForm({ ...form, maintenance_interval_days: e.target.value })} className="rounded-xl" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Description</label>
                    <Input placeholder="Description de l'équipement..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl" />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Notes internes</label>
                  <textarea
                    placeholder="Notes supplémentaires..."
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="w-full rounded-xl border border-gray-200 p-3 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Boutons */}
                <div className="flex gap-2 pt-2">
                  <Button type="submit" disabled={saving} className="rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white border-0">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    {form.id ? 'Mettre à jour' : 'Enregistrer l\'équipement'}
                  </Button>
                  {form.id && (
                    <Button type="button" variant="outline" onClick={() => { resetForm(); setActiveTab('inventory') }} className="rounded-xl">Annuler</Button>
                  )}
                </div>
              </form>

              {/* Aperçu QR après enregistrement */}
              {qrPreviewUrl && (
                <div className="mt-6 p-4 bg-gray-50 rounded-2xl flex items-center gap-4">
                  <img src={qrPreviewUrl} alt="QR Code" className="w-32 h-32 rounded-xl border" />
                  <div>
                    <p className="font-medium text-gray-800">QR Code généré ✅</p>
                    <p className="text-sm text-gray-500 mb-2">Imprimez et collez ce QR sur l'équipement.</p>
                    <a href={qrPreviewUrl} download={`qr_${Date.now()}.png`} className="text-blue-600 underline text-sm">📥 Télécharger l'image QR</a>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ONGLET SCANNER */}
        <TabsContent value="scan" className="mt-4">
          <Card className="bg-white/80 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
            <CardHeader>
              <CardTitle>📸 Scanner un QR Code</CardTitle>
              <CardDescription>Scannez le QR code collé sur un équipement pour accéder à sa fiche complète.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-3">
                <Button onClick={scanning ? stopScan : startScan} className="rounded-xl" variant={scanning ? 'destructive' : 'default'}>
                  <Camera className="h-4 w-4 mr-2" />{scanning ? 'Arrêter le scan' : 'Ouvrir la caméra'}
                </Button>
              </div>
              {scanning && (
                <div className="space-y-2">
                  <video ref={videoRef} className="w-full max-w-md rounded-2xl border-2 border-blue-200" muted playsInline />
                  <p className="text-sm text-gray-500 animate-pulse">🔍 Recherche d'un QR code...</p>
                  {!('BarcodeDetector' in window) && (
                    <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded-xl">
                      ⚠️ Votre navigateur ne supporte pas la détection automatique. Utilisez Chrome ou Edge.
                    </div>
                  )}
                </div>
              )}
              {scanResult && (
                <div className="p-3 bg-green-50 rounded-xl border border-green-200 text-sm">
                  ✅ Code détecté: <span className="font-mono font-medium">{scanResult}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* PANNEAU DÉTAIL D'UN ÉQUIPEMENT */}
      {selectedItem && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-10 pb-10">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl relative">
            {/* Bouton fermer */}
            <button onClick={closeDetail} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 z-10">
              <X className="h-5 w-5 text-gray-400" />
            </button>

            {/* En-tête fiche */}
            <div className={`p-6 rounded-t-3xl ${
              selectedItem.condition === 'FIN_DE_VIE' ? 'bg-gradient-to-br from-red-500 to-rose-600' :
              selectedItem.condition === 'A_REVISER' ? 'bg-gradient-to-br from-orange-400 to-red-500' :
              selectedItem.condition === 'NEUF' ? 'bg-gradient-to-br from-emerald-500 to-teal-600' :
              'bg-gradient-to-br from-blue-600 to-violet-600'
            }`}>
              <div className="flex items-start justify-between pr-8">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center text-3xl">
                    {getCategoryInfo(selectedItem.category).icon}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">{selectedItem.name}</h2>
                    <p className="text-sm text-white/70">
                      {[selectedItem.brand, selectedItem.model].filter(Boolean).join(' · ') || selectedItem.category}
                    </p>
                  </div>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/20 text-white backdrop-blur-sm">
                  {getConditionInfo(selectedItem.condition || 'BON').label}
                </span>
              </div>

              {/* Alertes */}
              {(selectedItem.end_of_life || selectedItem.condition === 'FIN_DE_VIE') && (
                <div className="mt-3 p-3 bg-white/20 backdrop-blur-sm rounded-xl flex items-center gap-2 text-white text-sm font-medium">
                  <AlertTriangle className="h-5 w-5" />
                  Cet équipement est en fin de vie et doit être remplacé.
                </div>
              )}
              {isMaintenanceDue(selectedItem) && (
                <div className="mt-3 p-3 bg-white/20 backdrop-blur-sm rounded-xl flex items-center gap-2 text-white text-sm font-medium">
                  <Wrench className="h-5 w-5" />
                  L'entretien de cet équipement est en retard ! Prévu le {new Date(selectedItem.next_maintenance_date).toLocaleDateString('fr-FR')}
                </div>
              )}
            </div>

            {/* Corps */}
            <div className="p-6 space-y-6">
              {/* Infos détaillées + QR Code */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2"><Info className="h-4 w-4" /> Informations</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {selectedItem.serial_number && (
                      <div><span className="text-gray-400">N° série</span><div className="font-mono font-medium">{selectedItem.serial_number}</div></div>
                    )}
                    {selectedItem.location && (
                      <div><span className="text-gray-400">Localisation</span><div>{selectedItem.location}</div></div>
                    )}
                    {selectedItem.purchase_date && (
                      <div><span className="text-gray-400">Date d'achat</span><div>{new Date(selectedItem.purchase_date).toLocaleDateString('fr-FR')}</div></div>
                    )}
                    {selectedItem.warranty_end && (
                      <div>
                        <span className="text-gray-400">Fin de garantie</span>
                        <div className={new Date(selectedItem.warranty_end) < new Date() ? 'text-red-600' : 'text-green-600'}>
                          {new Date(selectedItem.warranty_end).toLocaleDateString('fr-FR')}
                          {new Date(selectedItem.warranty_end) < new Date() ? ' (expirée)' : ' ✓'}
                        </div>
                      </div>
                    )}
                    {selectedItem.maintenance_interval_days && (
                      <div><span className="text-gray-400">Intervalle entretien</span><div>Tous les {selectedItem.maintenance_interval_days} jours</div></div>
                    )}
                    {selectedItem.last_maintenance_date && (
                      <div><span className="text-gray-400">Dernier entretien</span><div>{new Date(selectedItem.last_maintenance_date).toLocaleDateString('fr-FR')}</div></div>
                    )}
                    {selectedItem.next_maintenance_date && (
                      <div>
                        <span className="text-gray-400">Prochain entretien</span>
                        <div className={isMaintenanceDue(selectedItem) ? 'text-red-600 font-medium' : isMaintenanceSoon(selectedItem) ? 'text-amber-600' : ''}>
                          {new Date(selectedItem.next_maintenance_date).toLocaleDateString('fr-FR')}
                          {isMaintenanceDue(selectedItem) && ' ⚠️ en retard'}
                        </div>
                      </div>
                    )}
                  </div>
                  {selectedItem.description && (
                    <div className="mt-3 text-sm text-gray-600">{selectedItem.description}</div>
                  )}
                  {selectedItem.notes && (
                    <div className="mt-2 p-2 bg-yellow-50 rounded-xl text-sm text-yellow-800">📝 {selectedItem.notes}</div>
                  )}
                </div>

                {/* QR Code grand format */}
                <div className="flex flex-col items-center justify-start">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(selectedItem.qr_code)}`}
                    alt="QR Code"
                    className="w-40 h-40 rounded-xl border"
                  />
                  <p className="text-xs font-mono text-gray-400 mt-2 text-center break-all">{selectedItem.qr_code}</p>
                  <a
                    href={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(selectedItem.qr_code)}`}
                    download={`qr_${selectedItem.name}.png`}
                    className="text-blue-600 underline text-xs mt-1"
                  >📥 Télécharger</a>
                </div>
              </div>

              {/* Historique de maintenance */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <History className="h-4 w-4" /> Historique de maintenance
                  <span className="text-xs font-normal text-gray-400">({maintenanceLogs.length} entrée{maintenanceLogs.length !== 1 ? 's' : ''})</span>
                </h3>

                {/* Formulaire ajout maintenance */}
                <form onSubmit={onSubmitMaintenance} className="p-4 bg-gray-50 rounded-2xl mb-4 space-y-3">
                  <p className="text-sm font-medium text-gray-700">➕ Enregistrer un entretien</p>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <Select value={maintenanceForm.maintenance_type} onValueChange={(v) => setMaintenanceForm({ ...maintenanceForm, maintenance_type: v })}>
                      <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MAINTENANCE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.icon} {t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input type="date" value={maintenanceForm.performed_at} onChange={(e) => setMaintenanceForm({ ...maintenanceForm, performed_at: e.target.value })} className="rounded-xl" />
                    <Input type="number" step="0.01" placeholder="Coût (€)" value={maintenanceForm.cost} onChange={(e) => setMaintenanceForm({ ...maintenanceForm, cost: e.target.value })} className="rounded-xl" />
                    <Input type="date" placeholder="Prochain entretien" value={maintenanceForm.next_due_date} onChange={(e) => setMaintenanceForm({ ...maintenanceForm, next_due_date: e.target.value })} className="rounded-xl" title="Date du prochain entretien" />
                  </div>
                  <Input placeholder="Description de l'intervention..." value={maintenanceForm.description} onChange={(e) => setMaintenanceForm({ ...maintenanceForm, description: e.target.value })} className="rounded-xl" />
                  <Button type="submit" disabled={savingMaintenance} size="sm" className="rounded-xl">
                    {savingMaintenance ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wrench className="h-4 w-4 mr-1" />}
                    Enregistrer l'entretien
                  </Button>
                </form>

                {/* Timeline de maintenance */}
                {logsLoading ? (
                  <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div>
                ) : maintenanceLogs.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">Aucun entretien enregistré pour cet équipement.</p>
                ) : (
                  <div className="space-y-3">
                    {maintenanceLogs.map((log, i) => {
                      const typeInfo = getMaintenanceTypeInfo(log.maintenance_type)
                      return (
                        <div key={log.id || i} className="flex gap-3 items-start group">
                          <div className="flex flex-col items-center">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-sm">{typeInfo.icon}</div>
                            {i < maintenanceLogs.length - 1 && <div className="w-0.5 h-full bg-gray-200 mt-1" />}
                          </div>
                          <div className="flex-1 pb-4">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-sm text-gray-800">{typeInfo.label}</span>
                              <span className="text-xs text-gray-400">
                                {log.performed_at ? new Date(log.performed_at).toLocaleDateString('fr-FR') : ''}
                              </span>
                            </div>
                            {log.description && <p className="text-sm text-gray-600 mt-0.5">{log.description}</p>}
                            <div className="flex gap-3 mt-1 text-xs text-gray-400">
                              {log.cost && <span>💰 {parseFloat(log.cost).toFixed(2)} €</span>}
                              {log.users && <span>👤 {log.users.prenom} {log.users.nom}</span>}
                              {log.next_due_date && <span>📅 Prochain: {new Date(log.next_due_date).toLocaleDateString('fr-FR')}</span>}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Boutons d'action */}
              <div className="flex gap-2 pt-2 border-t border-gray-100">
                <Button onClick={() => onEdit(selectedItem)} className="rounded-xl" variant="outline">
                  <Edit className="h-4 w-4 mr-2" /> Modifier
                </Button>
                {me && !selectedItem.assigned_to && (
                  <Button onClick={async () => {
                    const updates = { assigned_to: me.id, assigned_date: new Date().toISOString(), status: 'IN_USE' }
                    await api.materials.update(selectedItem.id, updates)
                    const refreshed = { ...selectedItem, ...updates }
                    setSelectedItem(refreshed)
                    load()
                  }} className="rounded-xl" variant="outline">
                    <User className="h-4 w-4 mr-2" /> Prendre
                  </Button>
                )}
                {me && selectedItem.assigned_to === me?.id && (
                  <Button onClick={async () => {
                    const updates = { assigned_to: null, assigned_date: null, status: 'DISPONIBLE' }
                    await api.materials.update(selectedItem.id, updates)
                    const refreshed = { ...selectedItem, ...updates }
                    setSelectedItem(refreshed)
                    load()
                  }} className="rounded-xl" variant="outline">
                    <Check className="h-4 w-4 mr-2" /> Rendre
                  </Button>
                )}
                <Button onClick={() => { setDeleteItem(selectedItem); closeDetail() }} className="rounded-xl text-red-600" variant="outline">
                  <Trash2 className="h-4 w-4 mr-2" /> Supprimer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div className="fixed bottom-4 right-4 z-50 bg-red-100 text-red-700 px-4 py-2 rounded-xl shadow-lg text-sm flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> {error}
          <button onClick={() => setError('')} className="ml-2 hover:text-red-900"><X className="h-3 w-3" /></button>
        </div>
      )}

      {/* Confirmation de suppression */}
      {deleteItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-[90%] max-w-sm">
            <div className="text-lg font-semibold mb-2">🗑️ Supprimer ce matériel ?</div>
            <div className="text-sm text-gray-600 mb-4">
              Cette action est irréversible.<br />
              Matériel: <strong>{deleteItem.name}</strong>
              {deleteItem.serial_number && <span className="text-gray-400"> (SN: {deleteItem.serial_number})</span>}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteItem(null)} className="rounded-xl">Annuler</Button>
              <Button variant="destructive" onClick={onDeleteConfirmed} className="rounded-xl">Supprimer définitivement</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
// Apple-style Bureau Layout Component with Back Button
const BureauLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [pendingEInvoices, setPendingEInvoices] = useState(0);

  // Compter les chantiers facturés sans e-facture (badge alerte)
  const loadPendingInvoices = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const [wsRes, schRes] = await Promise.all([
        http.get(`${API}/worksites`, { headers: { Authorization: `Bearer ${token}` } }),
        http.get(`${API}/schedules`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const worksites = wsRes.data || [];
      const schedules = schRes.data || [];
      const scheduledIds = new Set(schedules.map(s => s.worksite_id));
      const count = worksites.filter(w => scheduledIds.has(w.id) && !w.invoice_id).length;
      setPendingEInvoices(count);
    } catch (_) {}
  };

  useEffect(() => { loadPendingInvoices(); }, []);

  // Rafraîchir le badge quand les worksites changent
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.table === 'worksites' || e.detail?.table === 'invoices_electronic') {
        loadPendingInvoices();
      }
    };
    window.addEventListener('realtime-update', handler);
    return () => window.removeEventListener('realtime-update', handler);
  }, []);

  // Garde auth : si pas de token, tenter refresh ou rediriger
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        // Tenter un refresh silencieux
        http.post(`${API}/auth/refresh`, { refresh_token: refreshToken })
          .then(res => {
            localStorage.setItem('token', res.data.access_token);
            if (res.data.refresh_token) localStorage.setItem('refresh_token', res.data.refresh_token);
            window.location.reload();
          })
          .catch(() => {
            broadcastLogout('no_token');
            forceLogout('no_token');
            window.location.href = '/?session=expired';
          });
      } else {
        broadcastLogout('no_token');
        forceLogout('no_token');
        window.location.href = '/?session=expired';
      }
    }
  }, []);

  // Charger les données utilisateur depuis localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // ⚡ Supabase Realtime — synchronisation globale Bureau (toutes les tables)
  useEffect(() => {
    let channel = null;
    let cancelled = false;
    const debounceTimers = {};

    const setupBureauRealtime = async () => {
      // Attendre la session Supabase
      let retries = 0;
      while (retries < 10 && !cancelled) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) break;
        retries++;
        await new Promise(r => setTimeout(r, 500));
      }
      if (cancelled) return;

      const storedUser = localStorage.getItem('user');
      const companyId = storedUser ? JSON.parse(storedUser)?.company_id : null;
      if (!companyId || !localStorage.getItem('token')) {
        console.warn('[Realtime Bureau] Pas de company_id ou token, abandon');
        return;
      }

      // Tables à surveiller (hors quotes, déjà géré dans QuoteCreate)
      const tables = ['clients', 'worksites', 'schedules', 'catalog_products', 'materials', 'invitations', 'searches', 'projects'];

      const makeHandler = (tableName) => (payload) => {
        console.log(`[Realtime Bureau] ${tableName} change:`, payload.eventType, payload.new?.id || payload.old?.id);
        // Debounce par table (300ms) pour éviter les rafales
        if (debounceTimers[tableName]) clearTimeout(debounceTimers[tableName]);
        debounceTimers[tableName] = setTimeout(() => {
          window.dispatchEvent(new CustomEvent('realtime-update', { detail: { table: tableName, event: payload.eventType, record: payload.new || payload.old } }));
        }, 300);
      };

      channel = supabase.channel(`bureau-realtime-${companyId}`);
      tables.forEach(table => {
        channel = channel.on(
          'postgres_changes',
          { event: '*', schema: 'public', table, filter: `company_id=eq.${companyId}` },
          makeHandler(table)
        );
      });

      channel.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('[Realtime Bureau] ✅ Abonné à', tables.join(', '));
        } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
          console.warn('[Realtime Bureau] ⚠️ Connexion perdue:', status);
        }
      });
    };

    setupBureauRealtime();

    return () => {
      cancelled = true;
      Object.values(debounceTimers).forEach(t => clearTimeout(t));
      if (channel) supabase.removeChannel(channel);
      console.log('[Realtime Bureau] Désabonné');
    };
  }, []);
  
  // Déterminer l'onglet actif depuis l'URL
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/projets') || path.includes('/entreprise')) return 'projects';
    if (path.includes('/devis')) return 'quotes';
    if (path.includes('/planning')) return 'planning';
    if (path.includes('/chantiers')) return 'sites';
    if (path.includes('/facturation')) return 'invoices';
    if (path.includes('/clients')) return 'clients';
    if (path.includes('/catalogue')) return 'catalog';
    if (path.includes('/invitations')) return 'invitations';
    if (path.includes('/materiel')) return 'material';
    if (path.includes('/inventaire')) return 'inventory';
    return 'projects'; // Par défaut: Mon Entreprise
  };
  
  const activeTab = getActiveTab();
  
  // Naviguer vers une route spécifique
  const handleTabChange = (newTab) => {
    const routes = {
      'projects': '/bureau/projets',
      'quotes': '/bureau/devis',
      'planning': '/bureau/planning',
      'sites': '/bureau/chantiers',
      'invoices': '/bureau/facturation',
      'clients': '/bureau/clients',
      'catalog': '/bureau/catalogue',
      'invitations': '/bureau/invitations',
      'material': '/bureau/materiel',
      'inventory': '/bureau/inventaire'
    };
    navigate(routes[newTab] || '/bureau/projets');
  };

  const handleLogout = () => {
    broadcastLogout('user_action');
    forceLogout('user_action');
  };

  const goBack = () => {
    navigate('/role-selection');
  };

  // ─── Prefetch sur hover des onglets pour navigation instantanée ──
  const PREFETCH_MAP = {
    quotes:  [`${API}/clients`, `${API}/quotes`, `${API}/worksites`, `${API}/schedules`, `${API}/catalog/products`],
    sites:   [`${API}/worksites`, `${API}/clients`],
    clients: [`${API}/clients`],
    catalog: [`${API}/catalog/products`],
    material:[`${API}/materials`],
    planning:[`${API}/schedules`, `${API}/worksites`],
  };
  const handleTabHover = (tab) => {
    const urls = PREFETCH_MAP[tab];
    if (urls) prefetch(...urls);
  };

  return (
    <div className="min-h-screen skyapp-page-bg">
      {/* Apple-style Navigation with Back Button */}
      <nav className="bg-white/80 backdrop-blur border-b border-white/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center h-16 sm:h-20 py-2 sm:py-3">
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Bouton retour */}
              <button
                onClick={goBack}
                className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
              
              {/* Mobile menu burger */}
              <Button
                onClick={() => setMobileNavOpen(!mobileNavOpen)}
                variant="ghost"
                className="md:hidden text-gray-500 hover:text-gray-700 p-2"
              >
                {mobileNavOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
              
              <img 
                src="/Logo skyapp v.2.png" 
                alt="SkyApp Logo" 
                className="w-28 h-28 sm:w-28 sm:h-28 md:w-24 md:h-24 lg:w-28 lg:h-28 rounded-lg object-cover logo-neon-stable"
              />
              <div className="h-6 sm:h-8 w-px bg-gray-300 hidden sm:block"></div>
              <div className="hidden sm:block">
                <p className="text-sm font-semibold text-gray-900">{user?.name || user?.email}</p>
                <p className="text-xs text-gray-500">{user?.role === 'ADMIN' ? 'Administrateur' : user?.role === 'BUREAU' ? 'Bureau' : 'Technicien'}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-1 sm:space-x-2">
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="text-gray-500 hover:text-red-600 hover:bg-red-50 transition-all duration-200 flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm px-2 sm:px-3"
              >
                <LogOut className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="font-medium hidden sm:inline">Déconnexion</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-4 sm:py-6 md:py-8 px-4 sm:px-6">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          {/* Apple-style Tab Navigation - Desktop only */}
          <div className="hidden md:flex justify-center mb-6 sm:mb-8 overflow-x-auto overflow-y-visible py-2">
            <TabsList className="bg-gradient-to-r from-gray-50 to-gray-100 border-2 border-gray-200 p-1 sm:p-1.5 rounded-2xl shadow-lg inline-flex min-w-max">
              <TabsTrigger 
                value="projects" 
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 md:px-5 py-2 sm:py-3 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-600 transition-all duration-200 whitespace-nowrap hover:bg-white/50"
              >
                <Briefcase className="h-4 w-4" />
                <span className="font-semibold text-xs sm:text-sm">Entreprise</span>
              </TabsTrigger>
              <TabsTrigger 
                value="quotes" 
                onMouseEnter={() => handleTabHover('quotes')}
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 md:px-5 py-2 sm:py-3 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-600 transition-all duration-200 whitespace-nowrap hover:bg-white/50"
              >
                <Calculator className="h-4 w-4" />
                <span className="font-semibold text-xs sm:text-sm">Devis</span>
              </TabsTrigger>
              <TabsTrigger 
                value="planning" 
                onMouseEnter={() => handleTabHover('planning')}
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 md:px-5 py-2 sm:py-3 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-600 transition-all duration-200 whitespace-nowrap hover:bg-white/50"
              >
                <Calendar className="h-4 w-4" />
                <span className="font-semibold text-xs sm:text-sm">Planning</span>
              </TabsTrigger>
              <TabsTrigger 
                value="sites" 
                onMouseEnter={() => handleTabHover('sites')}
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 md:px-5 py-2 sm:py-3 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-600 transition-all duration-200 whitespace-nowrap hover:bg-white/50"
              >
                <Building className="h-4 w-4" />
                <span className="font-semibold text-xs sm:text-sm">Chantiers</span>
              </TabsTrigger>
              <TabsTrigger 
                value="invoices" 
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 md:px-5 py-2 sm:py-3 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-600 transition-all duration-200 whitespace-nowrap hover:bg-white/50"
              >
                <FileText className="h-4 w-4" />
                <span className="font-semibold text-xs sm:text-sm">Facturation</span>
                {pendingEInvoices > 0 && (
                  <span className="ml-1 min-w-[18px] h-[18px] px-1 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                    {pendingEInvoices}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger 
                value="clients" 
                onMouseEnter={() => handleTabHover('clients')}
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 md:px-5 py-2 sm:py-3 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-600 transition-all duration-200 whitespace-nowrap hover:bg-white/50"
              >
                <Users className="h-4 w-4" />
                <span className="font-semibold text-xs sm:text-sm">Clients</span>
              </TabsTrigger>
              <TabsTrigger 
                value="catalog" 
                onMouseEnter={() => handleTabHover('catalog')}
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 md:px-5 py-2 sm:py-3 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-600 transition-all duration-200 whitespace-nowrap hover:bg-white/50"
              >
                <ClipboardList className="h-4 w-4" />
                <span className="font-semibold text-xs sm:text-sm">Catalogue</span>
              </TabsTrigger>
              <TabsTrigger 
                value="invitations" 
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 md:px-5 py-2 sm:py-3 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-600 transition-all duration-200 whitespace-nowrap hover:bg-white/50"
              >
                <UserPlus className="h-4 w-4" />
                <span className="font-semibold text-xs sm:text-sm">Invitations</span>
              </TabsTrigger>
              <TabsTrigger 
                value="material" 
                onMouseEnter={() => handleTabHover('material')}
                className="flex items-center space-x-1 sm:space-x-2 px-3 sm:px-4 md:px-5 py-2 sm:py-3 rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-blue-600 transition-all duration-200 whitespace-nowrap hover:bg-white/50"
              >
                <Settings className="h-4 w-4" />
                <span className="font-semibold text-xs sm:text-sm">Matériel</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Mobile Navigation Menu */}
          <div className={`md:hidden transition-all duration-300 ease-in-out overflow-hidden ${
            mobileNavOpen ? 'max-h-[600px] opacity-100 mb-6' : 'max-h-0 opacity-0'
          }`}>
            <div className="bg-white border-2 border-black rounded-xl shadow-lg p-2 space-y-2">
              <button
                onClick={() => {
                  handleTabChange('projects');
                  setMobileNavOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === 'projects'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Briefcase className="h-5 w-5" />
                <span className="font-medium">Entreprise</span>
              </button>
              <button
                onClick={() => {
                  handleTabChange('quotes');
                  setMobileNavOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === 'quotes'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Calculator className="h-5 w-5" />
                <span className="font-medium">Devis</span>
              </button>
              <button
                onClick={() => {
                  handleTabChange('planning');
                  setMobileNavOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === 'planning'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Calendar className="h-5 w-5" />
                <span className="font-medium">Planning</span>
              </button>
              <button
                onClick={() => {
                  handleTabChange('sites');
                  setMobileNavOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === 'sites'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Building className="h-5 w-5" />
                <span className="font-medium">Chantiers</span>
              </button>
              <button
                onClick={() => {
                  handleTabChange('invoices');
                  setMobileNavOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === 'invoices'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <FileText className="h-5 w-5" />
                <span className="font-medium">Facturation</span>
                {pendingEInvoices > 0 && (
                  <span className="ml-auto min-w-[20px] h-[20px] px-1.5 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {pendingEInvoices}
                  </span>
                )}
              </button>
              <button
                onClick={() => {
                  handleTabChange('clients');
                  setMobileNavOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === 'clients'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Users className="h-5 w-5" />
                <span className="font-medium">Clients</span>
              </button>
              <button
                onClick={() => {
                  handleTabChange('catalog');
                  setMobileNavOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === 'catalog'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <ClipboardList className="h-5 w-5" />
                <span className="font-medium">Catalogue</span>
              </button>
              <button
                onClick={() => {
                  handleTabChange('invitations');
                  setMobileNavOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === 'invitations'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <UserPlus className="h-5 w-5" />
                <span className="font-medium">Invitations</span>
              </button>
              <button
                onClick={() => {
                  handleTabChange('material');
                  setMobileNavOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  activeTab === 'material'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                <Settings className="h-5 w-5" />
                <span className="font-medium">Matériel</span>
              </button>
              <button
                onClick={() => {
                  handleLogout();
                  setMobileNavOpen(false);
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg bg-white text-red-600 hover:bg-red-50 transition-all"
              >
                <LogOut className="h-5 w-5" />
                <span className="font-medium">Déconnexion</span>
              </button>
            </div>
          </div>

          {/* Onglet connexion supprimé pour les rôles non fondateur */}

          <TabsContent value="projects" className="mt-4 sm:mt-6 md:mt-8">
            <ProjectsHub />
          </TabsContent>

          <TabsContent value="quotes" className="mt-4 sm:mt-6 md:mt-8">
            <QuoteCreate />
          </TabsContent>

          <TabsContent value="planning" className="mt-4 sm:mt-6 md:mt-8">
            <PlanningManagement />
          </TabsContent>

          <TabsContent value="sites" className="mt-4 sm:mt-6 md:mt-8">
            <WorksitesManagement />
          </TabsContent>

          <TabsContent value="invoices" className="mt-4 sm:mt-6 md:mt-8">
            <FacturationHub />
          </TabsContent>

          <TabsContent value="clients" className="mt-4 sm:mt-6 md:mt-8">
            <ClientsView />
          </TabsContent>

          <TabsContent value="catalog" className="mt-4 sm:mt-6 md:mt-8">
            <CatalogManagement />
          </TabsContent>

          <TabsContent value="invitations" className="mt-4 sm:mt-6 md:mt-8">
            <InviteManagementView />
          </TabsContent>

          <TabsContent value="material" className="mt-4 sm:mt-6 md:mt-8">
            <MaterialManagement />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// Dashboard Statistiques Ultra-Complet avec données isolées par société
const StatisticsLayout = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  // Garde auth : si pas de token, tenter refresh ou rediriger
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        http.post(`${API}/auth/refresh`, { refresh_token: refreshToken })
          .then(res => {
            localStorage.setItem('token', res.data.access_token);
            if (res.data.refresh_token) localStorage.setItem('refresh_token', res.data.refresh_token);
            window.location.reload();
          })
          .catch(() => {
            broadcastLogout('no_token');
            forceLogout('no_token');
            window.location.href = '/?session=expired';
          });
      } else {
        broadcastLogout('no_token');
        forceLogout('no_token');
        window.location.href = '/?session=expired';
      }
    }
  }, []);

  useEffect(() => {
    perf.start('Dashboard.mount');
    loadStatistics();
    // Simuler utilisateurs actifs via ref pour éviter re-renders
    activeUsersRef.current = Math.floor(Math.random() * 11) + 25;
    const interval = setInterval(() => {
      activeUsersRef.current = Math.floor(Math.random() * 11) + 25;
      // Mettre à jour le DOM directement pour éviter un re-render complet
      const el = document.getElementById('active-users-count');
      if (el) el.textContent = `${activeUsersRef.current} utilisateurs actifs`;
    }, 5000);
    return () => clearInterval(interval);
  }, [timeRange]);

  const activeUsersRef = useRef(Math.floor(Math.random() * 11) + 25);

  const loadStatistics = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const [statsRes, activityRes, regionsRes] = await Promise.all([
        http.get(`${API}/stats/dashboard`),
        http.get(`${API}/stats/recent-activity`).catch(() => ({ data: [] })),
        http.get(`${API}/stats/regions`).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setRecentActivity(activityRes.data || []);
      setRegions(regionsRes.data || []);
    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
    } finally {
      perf.end('Dashboard.mount');
      setLoading(false);
    }
  };

  const handleLogout = () => {
    broadcastLogout('user_action');
    forceLogout('user_action');
  };

  const goBack = () => {
    navigate('/role-selection');
  };

  // Calculs avancés des KPI — mémorisés pour éviter recalcul à chaque render
  const kpi = React.useMemo(() => {
    const totalRevenue = (stats.total_quotes_amount || 0);
    const previousRevenue = (stats.previous_quotes_amount || totalRevenue * 0.85);
    const monthlyGrowth = previousRevenue > 0 ? Math.round(((totalRevenue - previousRevenue) / previousRevenue) * 100) : 17;
    return {
      totalRevenue,
      monthlyGrowth,
      customerSatisfaction: stats.average_client_rating || 4.7,
      avgProjectDuration: stats.average_project_duration || 12.5,
      productivityIndex: stats.productivity_index || 98,
      totalProjects: stats.total_projects || stats.total_searches || 0,
      totalClients: stats.total_clients || 0,
      conversionRate: stats.quote_conversion_rate || 73,
      avgResponseTime: stats.average_response_time || '2.4h',
      retentionRate: stats.client_retention_rate || 87,
      avgMargin: stats.average_margin || 32,
      dailyInterventions: stats.daily_interventions || Math.round((stats.total_projects || stats.total_searches || 0) / 30) || 3.2,
      activeTeams: stats.active_teams || 12,
      coveredZones: stats.covered_zones || '8 régions',
      generatedReports: stats.generated_reports || (stats.total_projects || stats.total_searches || 0) * 2,
      timeSaved: stats.time_saved || '42h/semaine',
    };
  }, [stats]);

  const StatCard = ({ title, value, change, icon: Icon, color, suffix = '', size = 'normal' }) => (
    <div className={`bg-white rounded-3xl border-0 shadow-lg p-6 hover:shadow-xl transition-all duration-300 ${size === 'large' ? 'md:col-span-2' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-2">{title}</p>
          <p className={`${size === 'large' ? 'text-4xl' : 'text-3xl'} font-bold text-gray-900 mb-1`}>{value}{suffix}</p>
          {change !== undefined && (
            <div className="flex items-center space-x-2">
              <span className={`text-sm px-2 py-1 rounded-full ${change >= 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {change >= 0 ? '+' : ''}{change}%
              </span>
              <span className="text-xs text-gray-500">vs période précédente</span>
            </div>
          )}
        </div>
        <div className={`w-16 h-16 ${color} rounded-2xl flex items-center justify-center shadow-lg`}>
          <Icon className="h-8 w-8 text-white" />
        </div>
      </div>
    </div>
  );

  const MetricCard = ({ title, metrics, icon: Icon, color }) => (
    <div className="bg-white rounded-3xl border-0 shadow-lg p-6 hover:shadow-xl transition-all duration-300">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
      <div className="space-y-3">
        {metrics.map((metric, index) => (
          <div key={index} className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{metric.label}</span>
            <span className="text-sm font-semibold text-gray-900">{metric.value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen skyapp-page-bg">
        <header className="text-white shadow-2xl" style={{background:'linear-gradient(135deg,#2563EB 0%,#7C3AED 55%,#FB923C 100%)'}}>
          <div className="max-w-7xl mx-auto px-6 py-8">
            <h1 className="text-3xl font-bold">Dashboard Exécutif</h1>
            <p className="text-blue-100 mt-1">Chargement en cours...</p>
          </div>
        </header>
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1,2,3,4,5,6,7,8].map(i => (
            <div key={i} className="bg-white rounded-3xl p-6 shadow-lg animate-pulse">
              <div className="h-3 w-20 bg-gray-200 rounded mb-4"></div>
              <div className="h-8 w-24 bg-gray-200 rounded mb-2"></div>
              <div className="h-3 w-16 bg-gray-100 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen skyapp-page-bg">
      {/* Header Premium */}
      <header className="text-white shadow-2xl" style={{background:'linear-gradient(135deg,#2563EB 0%,#7C3AED 55%,#FB923C 100%)'}}>  
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={goBack}
                className="p-3 bg-white/20 hover:bg-white/30 rounded-2xl transition-colors backdrop-blur-sm"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold">Dashboard Exécutif</h1>
                <p className="text-blue-100 mt-1">Analytics & Intelligence d'Entreprise</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-2xl backdrop-blur-sm">
                <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                <span id="active-users-count" className="text-sm font-medium">{activeUsersRef.current} utilisateurs actifs</span>
              </div>
              <div className="flex space-x-2">
                {['7d', '30d', '90d'].map((range) => (
                  <button
                    key={range}
                    onClick={() => setTimeRange(range)}
                    className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                      timeRange === range 
                        ? 'bg-white text-gray-900 shadow-lg' 
                        : 'bg-white/20 hover:bg-white/30 text-white backdrop-blur-sm'
                    }`}
                  >
                    {range === '7d' ? '7 jours' : range === '30d' ? '30 jours' : '90 jours'}
                  </button>
                ))}
              </div>
              <button onClick={handleLogout} className="p-3 bg-red-600 hover:bg-red-700 rounded-2xl transition-colors">
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* KPI Principaux */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Chiffre d'Affaires"
            value={kpi.totalRevenue.toLocaleString('fr-FR')}
            suffix="€"
            change={kpi.monthlyGrowth}
            icon={BarChart3}
            color="bg-gradient-to-br from-blue-500 to-blue-700"
            size="large"
          />
          <StatCard
            title="Projets Actifs"
            value={kpi.totalProjects}
            change={stats.projects_growth || 12}
            icon={Briefcase}
            color="bg-gradient-to-br from-violet-500 to-violet-700"
          />
          <StatCard
            title="Clients Satisfaits"
            value={kpi.customerSatisfaction}
            suffix="/5"
            change={stats.satisfaction_growth || 2}
            icon={Star}
            color="bg-gradient-to-br from-orange-400 to-orange-500"
          />
        </div>

        {/* Métriques Avancées */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <MetricCard
            title="Performance Commerciale"
            icon={Target}
            color="bg-gradient-to-br from-violet-500 to-violet-700"
            metrics={[
              { label: 'Devis Convertis', value: `${kpi.conversionRate}%` },
              { label: 'Temps Moyen Réponse', value: kpi.avgResponseTime },
              { label: 'Taux Fidélisation', value: `${kpi.retentionRate}%` },
              { label: 'Marge Moyenne', value: `${kpi.avgMargin}%` }
            ]}
          />
          <MetricCard
            title="Opérations Terrain"
            icon={MapPin}
            color="bg-gradient-to-br from-blue-500 to-blue-700"
            metrics={[
              { label: 'Interventions/Jour', value: typeof kpi.dailyInterventions === 'number' ? kpi.dailyInterventions.toFixed(1) : kpi.dailyInterventions },
              { label: 'Durée Moyenne Projet', value: `${kpi.avgProjectDuration}j` },
              { label: 'Équipes Déployées', value: kpi.activeTeams.toString() },
              { label: 'Zones Couvertes', value: kpi.coveredZones }
            ]}
          />
          <MetricCard
            title="Productivité Équipe"
            icon={Users}
            color="bg-gradient-to-br from-orange-400 to-orange-500"
            metrics={[
              { label: 'Index Productivité', value: `${kpi.productivityIndex}%` },
              { label: 'Utilisateurs Actifs', value: (stats.active_users || activeUsersRef.current).toString() },
              { label: 'Rapports Générés', value: kpi.generatedReports.toString() },
              { label: 'Temps Économisé', value: kpi.timeSaved }
            ]}
          />
        </div>

        {/* Analytics Avancées */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Performance par Région / Zone */}
          <div className="bg-white rounded-3xl border-0 shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <Globe className="h-6 w-6 mr-2 text-violet-600" />
              Zones d'intervention
            </h3>
            <div className="space-y-4">
              {regions.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">Aucune donnée de localisation disponible</p>
              ) : regions.map((region, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-2xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">{region.name}</span>
                    <span className="text-sm font-semibold text-blue-600">{region.count} mission{region.count > 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 bg-gray-200 rounded-full h-3">
                      <div className={`${region.color} h-3 rounded-full transition-all duration-1000`} style={{ width: `${region.percent}%` }}></div>
                    </div>
                    <span className="text-sm text-gray-500">{region.percent}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Activité Temps Réel */}
          <div className="bg-white rounded-3xl border-0 shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <Clock className="h-6 w-6 mr-2 text-orange-500" />
              Activité Récente
            </h3>
            <div className="space-y-3">
              {recentActivity.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">Aucune activité récente</p>
              ) : recentActivity.map((activity, index) => {
                const iconMap = { quote: Calculator, client: UserPlus, search: MapPin, worksite: Building, report: FileText };
                const colorMap = { quote: 'bg-blue-600', client: 'bg-orange-400', search: 'bg-violet-600', worksite: 'bg-sky-500', report: 'bg-emerald-500' };
                const Icon = iconMap[activity.icon_key] || FileText;
                const color = colorMap[activity.icon_key] || 'bg-gray-400';
                const timeAgo = (() => {
                  if (!activity.time) return '';
                  const diff = Math.floor((Date.now() - new Date(activity.time + 'Z').getTime()) / 60000);
                  if (diff < 1) return 'à l\'instant';
                  if (diff < 60) return `il y a ${diff} min`;
                  if (diff < 1440) return `il y a ${Math.floor(diff/60)}h`;
                  return `il y a ${Math.floor(diff/1440)}j`;
                })();
                return (
                  <div key={index} className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-xl transition-colors">
                    <div className={`w-10 h-10 ${color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{activity.label}</p>
                      <p className="text-xs text-gray-500">par {activity.user} • {timeAgo}</p>
                    </div>
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse flex-shrink-0"></div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Objectifs et Prévisions */}
        <div className="bg-white rounded-3xl border-0 shadow-lg p-8">
          <h3 className="text-2xl font-semibold text-gray-900 mb-8 flex items-center">
            <Target className="h-7 w-7 mr-3 text-violet-600" />
            Objectifs Mensuels & Prévisions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { label: 'Projets Réalisés', current: stats.total_searches || 0, target: 100, unit: '' },
              { label: 'Chiffre d\'Affaires', current: Math.floor(kpi.totalRevenue/1000), target: 150, unit: 'K€' },
              { label: 'Satisfaction Client', current: 4.7, target: 5.0, unit: '/5' }
            ].map((objective, index) => {
              const progress = (objective.current / objective.target) * 100;
              return (
                <div key={index} className="text-center">
                  <div className="mb-4">
                    <div className="text-3xl font-bold text-gray-900 mb-1">
                      {objective.current}{objective.unit}
                    </div>
                    <div className="text-sm text-gray-600">
                      Objectif: {objective.target}{objective.unit}
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                    <div 
                      className="h-4 rounded-full transition-all duration-1000"
                      style={{background:'linear-gradient(90deg,#2563EB 0%,#7C3AED 55%,#FB923C 100%)', width:`${Math.min(progress, 100)}%`}}
                    ></div>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>{objective.label}</span>
                    <span>{Math.round(progress)}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

// ============================================================
// FORGOT PASSWORD MODAL
// ============================================================
const ForgotPasswordModal = ({ open, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const redirectTo = `${window.location.origin}/reset-password`;
      const { error: sbError } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      if (sbError) throw sbError;
      setSent(true);
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'envoi. Vérifiez l\'adresse email.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => { setSent(false); setEmail(''); setError(''); onClose(); };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Mot de passe oublié</DialogTitle>
          <DialogDescription>
            {sent ? 'Email envoyé !' : 'Entrez votre adresse email pour recevoir un lien de réinitialisation.'}
          </DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="space-y-4 text-center py-2">
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto" style={{ background: 'linear-gradient(135deg, #2563EB, #7C3AED)' }}>
              <Mail className="h-7 w-7 text-white" />
            </div>
            <p className="text-sm text-gray-600">Un email a été envoyé à <span className="font-semibold text-gray-900">{email}</span>.<br />Cliquez sur le lien pour créer un nouveau mot de passe.</p>
            <p className="text-xs text-gray-400">Vérifiez aussi vos spams si vous ne voyez pas l'email.</p>
            <Button onClick={handleClose} className="w-full text-white" style={{ background: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 100%)', border: 'none' }}>
              Fermer
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="email"
              placeholder="Adresse email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full text-white font-semibold" style={{ background: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 100%)', border: 'none' }}>
              {loading ? 'Envoi...' : 'Envoyer le lien'}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// RESET PASSWORD PAGE (après clic sur le lien email)
// ============================================================
const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Supabase injecte automatiquement la session via le lien magique
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        // Session prête, on peut afficher le formulaire (déjà affiché)
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Les mots de passe ne correspondent pas.'); return; }
    const passwordError = validatePasswordStrength(password);
    if (passwordError) { setError(passwordError); return; }
    setLoading(true); setError('');
    try {
      // Récupère le token de récupération Supabase (injecté dans l'URL par le
      // lien email) et le transmet à notre backend, qui valide la politique
      // de mot de passe côté serveur avant d'appliquer le changement.
      const { data: { session } } = await supabase.auth.getSession();
      const recoveryToken = session?.access_token;
      if (!recoveryToken) {
        throw new Error('Lien de réinitialisation invalide ou expiré. Redemandez un nouveau lien.');
      }
      await http.post(`${API}/auth/reset-password`, { password }, {
        headers: { Authorization: `Bearer ${recoveryToken}` }
      });
      setDone(true);
      setTimeout(() => navigate('/'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Erreur lors de la réinitialisation.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 40%, #FFF0E6 100%)' }}>
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md border border-gray-100">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg" style={{ background: 'linear-gradient(135deg, #2563EB, #7C3AED)' }}>
            <Lock className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Nouveau mot de passe</h1>
          <p className="text-gray-500 text-sm mt-1">Choisissez un mot de passe sécurisé</p>
        </div>
        {done ? (
          <div className="text-center space-y-3">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <p className="font-semibold text-gray-900">Mot de passe mis à jour !</p>
            <p className="text-sm text-gray-500">Redirection vers la page d'accueil...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
              <Input type="password" placeholder="Au moins 8 caractères" value={password} onChange={e => setPassword(e.target.value)} required minLength={PASSWORD_MIN_LENGTH} />
              <PasswordStrengthMeter password={password} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirmer le mot de passe</label>
              <Input type="password" placeholder="Répétez le mot de passe" value={confirm} onChange={e => setConfirm(e.target.value)} required />
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full text-white font-semibold py-3" style={{ background: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 100%)', border: 'none' }}>
              {loading ? 'Mise à jour...' : 'Mettre à jour le mot de passe'}
            </Button>
            <button type="button" onClick={() => navigate('/')} className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors">
              Retour à l'accueil
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// ============================================================
// VERIFY EMAIL PAGE — écran "Vérifiez votre e-mail" après inscription
// ============================================================
const maskEmail = (email) => {
  const [local, domain] = (email || '').split('@');
  if (!local || !domain) return email || '';
  const visible = local.slice(0, Math.min(2, local.length));
  return `${visible}${'*'.repeat(Math.max(local.length - visible.length, 3))}@${domain}`;
};

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(() => sessionStorage.getItem('pendingVerificationEmail') || '');
  const [emailInput, setEmailInput] = useState('');
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (location.state?.justRegistered) {
      toast.success('Votre compte a bien été créé. Un e-mail de confirmation vient de vous être envoyé.');
    }
  }, [location.state]);

  // Décompte du cooldown (persisté pour survivre à un rechargement de page)
  useEffect(() => {
    const until = parseInt(sessionStorage.getItem('resendVerificationCooldownUntil') || '0', 10);
    const remaining = Math.max(0, Math.ceil((until - Date.now()) / 1000));
    if (remaining > 0) setCooldown(remaining);
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown(c => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const startCooldown = (seconds) => {
    sessionStorage.setItem('resendVerificationCooldownUntil', String(Date.now() + seconds * 1000));
    setCooldown(seconds);
  };

  const handleResend = async () => {
    const targetEmail = email || emailInput.trim();
    if (!targetEmail) { toast.error('Veuillez saisir votre adresse email'); return; }
    if (sending || cooldown > 0) return;
    setSending(true);
    try {
      const resp = await http.post(`${API}/auth/resend-verification`, { email: targetEmail });
      toast.success(resp.data?.message || 'Si une inscription correspondant à cette adresse existe, un nouvel e-mail vient d\'être envoyé.');
      startCooldown(60);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const code = detail?.code;
      if (code === 'over_email_send_rate_limit' || err.response?.status === 429) {
        startCooldown(detail?.retry_after || 60);
        toast.error('Veuillez patienter avant de redemander un e-mail.');
      } else if (code === 'email_address_invalid') {
        toast.error('Adresse email invalide.');
      } else {
        // Réponse générique même en cas d'erreur inattendue (anti-énumération côté backend)
        toast.success('Si une inscription correspondant à cette adresse existe, un nouvel e-mail vient d\'être envoyé.');
        startCooldown(60);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 40%, #FFF0E6 100%)' }}>
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md border border-gray-100">
        <div className="text-center mb-6">
          <img src="/Logo skyapp v.2.png" alt="SkyApp" className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4 shadow-lg" />
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg" style={{ background: 'linear-gradient(135deg, #2563EB, #7C3AED)' }}>
            <Mail className="h-7 w-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Vérifiez votre adresse e-mail</h1>
          {email ? (
            <p className="text-gray-500 text-sm mt-2">Un e-mail a été envoyé à <span className="font-semibold text-gray-700">{maskEmail(email)}</span></p>
          ) : (
            <p className="text-gray-500 text-sm mt-2">Saisissez votre adresse pour recevoir un nouveau lien</p>
          )}
        </div>

        <p className="text-sm text-gray-600 text-center leading-relaxed mb-4">
          Nous avons envoyé un lien de confirmation à votre adresse e-mail. Cliquez sur ce lien pour activer votre compte SkyApp.
        </p>

        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 text-center mb-5">
          Pensez à vérifier vos courriers indésirables (spams) si vous ne trouvez pas l'e-mail.
        </div>

        {!email && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Adresse e-mail</label>
            <Input type="email" placeholder="vous@exemple.fr" value={emailInput} onChange={e => setEmailInput(e.target.value)} />
          </div>
        )}

        <Button
          onClick={handleResend}
          disabled={sending || cooldown > 0}
          className="w-full text-white font-semibold py-3 disabled:opacity-60"
          style={{ background: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 100%)', border: 'none' }}
        >
          {sending ? 'Envoi en cours…' : cooldown > 0 ? `Renvoyer l'e-mail (${cooldown}s)` : 'Renvoyer l\'e-mail'}
        </Button>

        <div className="flex items-center justify-between mt-5 text-sm">
          <button
            type="button"
            onClick={() => { sessionStorage.removeItem('pendingVerificationEmail'); navigate('/?openRegister=true'); }}
            className="text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
          >
            Modifier mon adresse e-mail
          </button>
          <button
            type="button"
            onClick={() => navigate('/?openLogin=true')}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// EMAIL CONFIRMED PAGE — écran de résultat après clic sur le lien
// ============================================================
const EmailConfirmedPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const status = params.get('status');
  const reason = params.get('reason');

  useEffect(() => {
    sessionStorage.removeItem('pendingVerificationEmail');
  }, []);

  const isSuccess = status === 'success';
  const errorMessages = {
    invalid_or_expired: 'Ce lien de confirmation est invalide, a expiré, ou a déjà été utilisé.',
    server_error: 'Une erreur est survenue lors de la confirmation de votre e-mail.',
  };
  const errorMessage = errorMessages[reason] || 'Ce lien de confirmation n\'est plus valable.';

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 40%, #FFF0E6 100%)' }}>
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-md border border-gray-100 text-center">
        <img src="/Logo skyapp v.2.png" alt="SkyApp" className="w-16 h-16 rounded-2xl object-cover mx-auto mb-4 shadow-lg" />
        {isSuccess ? (
          <>
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">E-mail confirmé !</h1>
            <p className="text-gray-500 text-sm mb-6">Votre adresse e-mail est confirmée. Bienvenue sur SkyApp !</p>
            <Button
              onClick={() => navigate('/?openLogin=true')}
              className="w-full text-white font-semibold py-3"
              style={{ background: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 100%)', border: 'none' }}
            >
              Se connecter
            </Button>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Lien invalide</h1>
            <p className="text-gray-500 text-sm mb-6">{errorMessage}</p>
            <div className="space-y-2">
              <Button
                onClick={() => navigate('/auth/verifier-email')}
                className="w-full text-white font-semibold py-3"
                style={{ background: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 100%)', border: 'none' }}
              >
                Demander un nouvel e-mail
              </Button>
              <button type="button" onClick={() => navigate('/?openLogin=true')} className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors py-2">
                Revenir à la connexion
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Login Modal Component with Apple Design
const LoginModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailNotConfirmed, setEmailNotConfirmed] = useState(false);
  const [accountDeactivated, setAccountDeactivated] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  // ── État MFA ──────────────────────────────────────────────────
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState(null);
  // Données de login en attente de validation MFA
  const [pendingLogin, setPendingLogin] = useState(null);

  // Charger l'email sauvegardé au montage du composant
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  // Finaliser le login après MFA ou directement si pas de MFA
  const completeLogin = ({ finalToken, refresh_token, user }) => {
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }
    recordUserActivity(user?.id);
    onClose();
    window.location.href = '/role-selection';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setEmailNotConfirmed(false);
    setAccountDeactivated(false);

    try {
      const response = await http.post(`${API}/auth/login`, { email, password });
      const { token, access_token, refresh_token, user } = response.data;
      
      // Stocker le token (privilégier access_token si disponible)
      const finalToken = access_token || token;
      localStorage.setItem('token', finalToken);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Stocker le refresh_token pour rafraîchir la session plus tard
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }

      // ✅ Réinitialiser les flags d'auth du module http (anti-boucle stale)
      resetHttpAuth();

      // Injecter la session Supabase Auth dans le client JS
      // pour que les appels directs (materials, maintenance_logs) fonctionnent
      if (access_token && refresh_token) {
        await supabase.auth.setSession({ access_token, refresh_token });
      }

      // ── Vérifier si l'utilisateur a MFA activé ───────────────
      const { data: mfaData } = await supabase.auth.mfa.listFactors();
      const verifiedFactor = mfaData?.totp?.find(f => f.status === 'verified');

      if (verifiedFactor) {
        // MFA requis → stocker les données en attente et afficher le modal
        setPendingLogin({ finalToken, refresh_token, user });
        setMfaFactorId(verifiedFactor.id);
        setMfaRequired(true);
        setLoading(false);
        return; // Ne pas finaliser le login avant vérification MFA
      }

      // Pas de MFA → login direct
      completeLogin({ finalToken, refresh_token, user });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const code = typeof detail === 'object' ? detail?.code : null;
      if (err.response?.status === 403 && code === 'email_not_confirmed') {
        setEmailNotConfirmed(true);
        setError('');
      } else if (err.response?.status === 403 && code === 'account_deactivated') {
        setAccountDeactivated(true);
        setError('');
      } else {
        setEmailNotConfirmed(false);
        setError((typeof detail === 'string' ? detail : detail?.message) || 'Erreur de connexion');
      }
    } finally {
      setLoading(false);
    }
  };

  // Appelé par MFAVerifyModal quand le code est correct
  const handleMFASuccess = () => {
    setMfaRequired(false);
    if (pendingLogin) {
      completeLogin(pendingLogin);
    }
  };

  // Annulation MFA → logout propre (ne pas laisser une session aal1 ouverte)
  const handleMFACancel = () => {
    setMfaRequired(false);
    setPendingLogin(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    supabase.auth.signOut();
  };

  const initSampleData = async () => {
    try {
      await http.post(`${API}/init-sample-data`);
      alert('Données d\'exemple créées !\nTechnicien: tech@search-app.fr / tech123\nBureau: bureau@search-app.fr / bureau123');
    } catch (err) {
      console.error('Erreur création données exemple:', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Connexion</DialogTitle>
          <DialogDescription>
            Connectez-vous à votre compte SkyApp
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              type="email"
              placeholder="Adresse email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <Input
              type="password"
              placeholder="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button type="button" onClick={() => { onClose(); setShowForgot(true); }}
              className="mt-1.5 text-xs text-indigo-600 hover:text-indigo-800 font-medium float-right transition-colors">
              Mot de passe oublié ?
            </button>
          </div>
          <div className="clear-both" />
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="rememberMe"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="rememberMe" className="text-sm text-gray-700 cursor-pointer">
              Se souvenir de moi
            </label>
          </div>
          {error && (
            <div className="text-red-500 text-sm">{error}</div>
          )}
          {emailNotConfirmed && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800 space-y-2">
              <p>Votre adresse e-mail n'a pas encore été confirmée. Vérifiez votre boîte de réception ou demandez un nouvel e-mail.</p>
              <button
                type="button"
                onClick={() => {
                  sessionStorage.setItem('pendingVerificationEmail', email);
                  onClose();
                  navigate('/auth/verifier-email');
                }}
                className="text-indigo-600 hover:text-indigo-800 font-semibold underline"
              >
                Renvoyer l'e-mail de confirmation
              </button>
            </div>
          )}
          {accountDeactivated && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800 flex items-start gap-2">
              <Lock className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>Votre compte a été désactivé par un administrateur. Contactez votre entreprise pour plus d'informations.</p>
            </div>
          )}
          <Button type="submit" disabled={loading} className="w-full text-white font-semibold py-2.5" style={{ background: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 100%)', border: 'none' }}>
            {loading ? 'Connexion...' : 'Se connecter'}
          </Button>
        </form>
      </DialogContent>

      {/* Modal MFA — affiché par-dessus le modal de login si MFA requis */}
      <MFAVerifyModal
        open={mfaRequired}
        factorId={mfaFactorId}
        onSuccess={handleMFASuccess}
        onCancel={handleMFACancel}
      />
      <ForgotPasswordModal open={showForgot} onClose={() => setShowForgot(false)} />
    </Dialog>
  );
};

// Administration Fondateur (anciennement panel suprême) - réservé à skyapp@gmail.com
const SupremeAdminLayout = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [systemStats, setSystemStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [companyFilter, setCompanyFilter] = useState('ALL');
  const [companySearch, setCompanySearch] = useState('');

  // Garde auth : si pas de token, tenter refresh ou rediriger
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        http.post(`${API}/auth/refresh`, { refresh_token: refreshToken })
          .then(res => {
            localStorage.setItem('token', res.data.access_token);
            if (res.data.refresh_token) localStorage.setItem('refresh_token', res.data.refresh_token);
            window.location.reload();
          })
          .catch(() => {
            broadcastLogout('no_token');
            forceLogout('no_token');
            window.location.href = '/?session=expired';
          });
      } else {
        broadcastLogout('no_token');
        forceLogout('no_token');
        window.location.href = '/?session=expired';
      }
    }
  }, []);

  useEffect(() => {
    // Vérifier que l'utilisateur est bien le fondateur
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      if (!user.is_fondateur) {
        navigate('/role-selection');
        return;
      }
    } else {
      navigate('/');
      return;
    }
    loadSystemData();
  }, [navigate]);

  const loadSystemData = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const token = localStorage.getItem('token');
      // 1) Utilisateurs réels via endpoint fondateur
      const usersRes = await http.get(`${API}/founder/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = (usersRes.data?.users || []).map(u => ({
        id: u.id,
        email: u.email,
        nom: u.email?.split('@')[0] || '',
        role: u.role || 'TECHNICIEN',
        company: u.company_id || null,
        status: u.actif === false ? 'SUSPENDED' : 'ACTIVE',
        created_at: u.created_at
      }));
      setUsers(list);
      if ((usersRes.data?.count || 0) === 0 && usersRes.data?.debug_reason) {
        setLoadError(usersRes.data.debug_reason);
      } else {
        setLoadError(null);
      }

      // 2) Aperçu système (utilise l'overview pour quelques compteurs)
      const overviewRes = await http.get(`${API}/founder/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ov = overviewRes.data || {};
      setSystemStats({
        total_users: ov?.totals?.users || list.length,
        total_companies: ov?.totals?.companies || 0,
        total_searches: ov?.totals?.searches || 0,
        total_reports: ov?.totals?.reports || ov?.totals?.searches || 0,
        active_sessions: 0,
        server_uptime: '99.9%'
      });

      // 3) Entreprises basique (optionnel: à brancher si endpoint dédié existe)
      try {
        const companiesRes = await http.get(`${API}/companies`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCompanies((companiesRes.data || []).map(c => ({
          id: c.id,
          nom: c.name,
          users_count: list.filter(u => u.company === c.id).length,
          active_searches: 0,
          total_reports: 0,
          subscription: c.subscription || 'N/A',
          created_at: c.created_at
        })));
      } catch {
        setCompanies([]);
      }
    } catch (error) {
      console.error('Erreur chargement données système:', error);
      setLoadError(error?.response?.data?.detail || error?.message || 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  };

  const companyMap = React.useMemo(() => {
    const map = {};
    companies.forEach(c => {
      map[c.id] = c.nom || c.id;
    });
    return map;
  }, [companies]);

  const filteredUsers = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return users.filter(user => {
      if (roleFilter !== 'ALL' && (user.role || '').toUpperCase() !== roleFilter) {
        return false;
      }
      if (companyFilter === 'NONE') {
        if (user.company) return false;
      } else if (companyFilter !== 'ALL' && (user.company || '') !== companyFilter) {
        return false;
      }
      if (!term) return true;
      const companyName = companyMap[user.company] || '';
      return [user.email, user.nom, companyName].some(field => (field || '').toLowerCase().includes(term));
    });
  }, [users, roleFilter, companyFilter, searchTerm, companyMap]);

  const filteredCompanies = React.useMemo(() => {
    const term = companySearch.trim().toLowerCase();
    return companies.filter(company => {
      if (!term) return true;
      return (company.nom || company.id || '').toLowerCase().includes(term);
    });
  }, [companies, companySearch]);

  const handleLogout = () => {
    broadcastLogout('user_action');
    forceLogout('user_action');
  };

  const goBack = () => {
    navigate('/role-selection');
  };

  const toggleUserStatus = (userId) => {
    setUsers(prev => prev.map(user => 
      user.id === userId 
        ? { ...user, status: user.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE' }
        : user
    ));
    alert('Statut utilisateur modifié');
  };

  const deleteCompany = (companyId) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cette entreprise ? Cette action est irréversible.')) {
      setCompanies(prev => prev.filter(company => company.id !== companyId));
      alert('Entreprise supprimée');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du panel administrateur...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-red-900">
  {/* Navigation Administration Fondateur */}
  <nav className="bg-gradient-to-r from-black via-zinc-800 to-red-800 border-b border-red-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={goBack}
                className="mr-4 p-2 text-red-200 hover:text-white hover:bg-red-700/50 rounded-lg transition-colors duration-200"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-3">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <div>
                <span className="text-xl font-semibold text-white">SkyApp Fondateur</span>
                <span className="text-sm text-red-200 ml-2">Administration Globale</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 text-white">
              <span className="text-sm bg-red-900/40 px-3 py-1 rounded-full border border-red-700">Fondateur</span>
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="text-red-200 hover:text-white hover:bg-red-700/50 p-2"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
  <main className="max-w-7xl mx-auto py-8 px-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Administration Fondateur</h1>
          <p className="text-lg text-white">Contrôle global et supervision de l'écosystème SkyApp</p>
        </div>

  {/* System Stats Overview */}
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Utilisateurs</p>
                <p className="text-2xl font-bold text-gray-900">{systemStats.total_users}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Entreprises</p>
                <p className="text-2xl font-bold text-gray-900">{systemStats.total_companies}</p>
              </div>
              <Building className="h-8 w-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Recherches</p>
                <p className="text-2xl font-bold text-gray-900">{systemStats.total_searches}</p>
              </div>
              <Search className="h-8 w-8 text-red-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Rapports</p>
                <p className="text-2xl font-bold text-gray-900">{systemStats.total_reports}</p>
              </div>
              <FileText className="h-8 w-8 text-orange-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Sessions</p>
                <p className="text-2xl font-bold text-gray-900">{systemStats.active_sessions}</p>
              </div>
              <Target className="h-8 w-8 text-red-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Uptime</p>
                <p className="text-2xl font-bold text-green-600">{systemStats.server_uptime}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </div>
        </div>

        {loadError && (
          <div className="mb-6 p-4 rounded-xl border border-amber-300 bg-amber-50 text-amber-800 text-sm">
            {String(loadError)}
          </div>
        )}

        {/* Tabs */}
  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex space-x-1 p-1">
              <button
                onClick={() => setActiveTab('users')}
                className={`px-6 py-3 rounded-xl font-medium transition-colors ${
                  activeTab === 'users' 
                    ? 'bg-red-50 text-red-700 border border-red-200' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Utilisateurs ({users.length})
              </button>
              <button
                onClick={() => setActiveTab('companies')}
                className={`px-6 py-3 rounded-xl font-medium transition-colors ${
                  activeTab === 'companies' 
                    ? 'bg-red-50 text-red-700 border border-red-200' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Entreprises ({companies.length})
              </button>
              <button
                onClick={() => setActiveTab('system')}
                className={`px-6 py-3 rounded-xl font-medium transition-colors ${
                  activeTab === 'system' 
                    ? 'bg-red-50 text-red-700 border border-red-200' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                Système
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Gestion des Utilisateurs</h3>
                <div className="flex flex-col md:flex-row md:items-center md:space-x-3 space-y-3 md:space-y-0 mb-4">
                  <Input
                    placeholder="Rechercher (email, nom, entreprise)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="md:w-1/3"
                  />
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="ALL">Tous les rôles</option>
                    <option value="ADMIN">Admin</option>
                    <option value="BUREAU">Bureau</option>
                    <option value="TECHNICIEN">Technicien</option>
                  </select>
                  <select
                    value={companyFilter}
                    onChange={(e) => setCompanyFilter(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="ALL">Toutes les entreprises</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.nom || c.id}</option>
                    ))}
                    <option value="NONE">Sans entreprise</option>
                  </select>
                </div>
                {filteredUsers.length === 0 ? (
                  <div className="p-6 text-center text-sm text-gray-500 bg-gray-50 rounded-xl">
                    Aucun utilisateur ne correspond aux filtres sélectionnés.
                  </div>
                ) : (
                  filteredUsers.map(user => (
                    <div key={user.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center space-x-4">
                        <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                          <Users className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{user.nom}</h4>
                          <p className="text-sm text-gray-600">{user.email}</p>
                          <div className="flex items-center space-x-2 mt-1">
                            <Badge className={`text-xs ${user.role === 'ADMIN' ? 'bg-red-100 text-red-700' : user.role === 'BUREAU' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                              {user.role}
                            </Badge>
                            <Badge className={`text-xs ${user.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {user.status}
                            </Badge>
                            <Badge className="text-xs bg-gray-100 text-gray-700">
                              {companyMap[user.company] || 'Sans entreprise'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          onClick={() => toggleUserStatus(user.id)}
                          className={user.status === 'ACTIVE' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}
                        >
                          {user.status === 'ACTIVE' ? 'Suspendre' : 'Activer'}
                        </Button>
                        <Button size="sm" variant="outline">
                          Détails
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Companies Tab */}
            {activeTab === 'companies' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Gestion des Entreprises</h3>
                <Input
                  placeholder="Filtrer par nom"
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  className="md:w-1/3"
                />
                <div className="grid gap-4">
                  {filteredCompanies.length === 0 ? (
                    <div className="p-6 text-center text-sm text-gray-500 bg-gray-50 rounded-xl">
                      Aucune entreprise ne correspond aux filtres sélectionnés.
                    </div>
                  ) : (
                    filteredCompanies.map(company => (
                      <div key={company.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                            <Building className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{company.nom}</h4>
                            <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                              <span>{company.users_count} utilisateurs</span>
                              <span>{company.active_searches} recherches</span>
                              <span>{company.total_reports} rapports</span>
                            </div>
                            <Badge className="mt-1 bg-red-100 text-red-700 text-xs">
                              {company.subscription}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex space-x-2">
                          <Button size="sm" variant="outline" onClick={() => navigate(`/statistiques?company=${company.id}`)}>
                            Statistiques
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => deleteCompany(company.id)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* System Tab */}
            {activeTab === 'system' && (
              <div className="space-y-6">
                {/* Panneau Connexions - réservé au fondateur (affiché ici car l'accès à ce layout est déjà restreint) */}
                <ConnectionView />

                {/* Outils système additionnels */}
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900">Administration Système</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Actions Système</h4>
                      <Button className="w-full bg-black hover:bg-red-800 text-white">
                        <Download className="h-4 w-4 mr-2" />
                        Exporter toutes les données
                      </Button>
                      <Button className="w-full bg-zinc-800 hover:bg-zinc-900 text-white">
                        <Settings className="h-4 w-4 mr-2" />
                        Maintenance système
                      </Button>
                      <Button className="w-full bg-red-600 hover:bg-red-700 text-white">
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        Redémarrer serveur
                      </Button>
                    </div>
                    
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Logs Système</h4>
                      <div className="bg-black text-green-400 p-4 rounded-xl font-mono text-sm max-h-64 overflow-y-auto">
                        <div>[2024-01-20 10:30:25] INFO: Système démarré</div>
                        <div>[2024-01-20 10:31:10] INFO: Base de données connectée</div>
                        <div>[2024-01-20 10:32:00] INFO: {systemStats.total_users} utilisateurs actifs</div>
                        <div>[2024-01-20 10:32:30] INFO: Sauvegarde automatique effectuée</div>
                        <div>[2024-01-20 10:33:15] INFO: Uptime: {systemStats.server_uptime}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

// ============================================================================
// INVOICE CREATE FORM - Formulaire création facture électronique
// ============================================================================

const InvoiceCreateForm = ({ onBack, onCreated, initialData }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    siren_client: '',
    address_billing: '',
    address_delivery: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    payment_terms: '30 jours',
    payment_method: 'virement',
    notes: '',
    lines: [
      { line_number: 1, designation: '', quantity: 1, unit_price_ht: 0, tva_rate: 20 }
    ]
  });

  // Charger les clients
  useEffect(() => {
    loadClients();
    // Auto-remplir échéance (+30 jours)
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 30);
    setFormData(prev => ({ ...prev, due_date: dueDate.toISOString().split('T')[0] }));
  }, []);

  // Appliquer le préremplissage depuis un chantier + charger les lignes du devis si dispo
  useEffect(() => {
    if (!initialData) return;
    const apply = async () => {
      const token = localStorage.getItem('token');
      let lines = [{ line_number: 1, designation: initialData.notes || '', quantity: 1, unit_price_ht: initialData.amount ? parseFloat((initialData.amount / 1.2).toFixed(2)) : 0, tva_rate: 20 }];

      // Charger les lignes du devis source si disponible
      if (initialData.quote_id && token) {
        try {
          const res = await http.get(`${API}/quotes/${initialData.quote_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const quote = res.data;
          // Préremplir aussi le client depuis le devis
          const clientMatch = clients.find(c => (c.nom || c.name) === (quote.client_name || initialData.customer_name));
          if (clientMatch) {
            setFormData(prev => ({
              ...prev,
              customer_id: clientMatch.id,
              customer_name: clientMatch.name,
              siren_client: clientMatch.siren || '',
              address_billing: `${clientMatch.address || ''}, ${clientMatch.postal_code || ''} ${clientMatch.city || ''}`.trim(),
            }));
          }
          // Lignes depuis le devis (services)
          if (quote.services && quote.services.length > 0) {
            lines = quote.services.map((s, i) => ({
              line_number: i + 1,
              designation: s.description || s.name || '',
              quantity: parseFloat(s.quantity) || 1,
              unit_price_ht: parseFloat(s.unit_price || s.price || 0),
              tva_rate: 20,
            }));
          }
        } catch (_) {}
      }

      setFormData(prev => ({
        ...prev,
        customer_name: initialData.customer_name || prev.customer_name,
        notes: initialData.notes || '',
        lines,
      }));
    };
    apply();
  }, [initialData, clients]);

  const loadClients = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await http.get(`${API}/clients`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setClients(response.data || []);
    } catch (error) {
      console.error('Erreur chargement clients:', error);
    }
  };

  // Sélection client
  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      const name = client.nom || client.name || '';
      const siren = (client.siret || client.siren || '').slice(0, 9);
      const address = client.adresse || client.address || '';
      const postal = client.code_postal || client.postal_code || '';
      const city = client.ville || client.city || '';
      setFormData(prev => ({
        ...prev,
        customer_id: clientId,
        customer_name: name,
        siren_client: siren,
        address_billing: [address, postal, city].filter(Boolean).join(', '),
        address_delivery: ''
      }));
    }
  };

  // Ajouter ligne
  const addLine = () => {
    setFormData(prev => ({
      ...prev,
      lines: [...prev.lines, {
        line_number: prev.lines.length + 1,
        designation: '',
        quantity: 1,
        unit_price_ht: 0,
        tva_rate: 20
      }]
    }));
  };

  // Supprimer ligne
  const removeLine = (index) => {
    if (formData.lines.length === 1) return;
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== index).map((line, i) => ({
        ...line,
        line_number: i + 1
      }))
    }));
  };

  // Modifier ligne
  const updateLine = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.map((line, i) => 
        i === index ? { ...line, [field]: value } : line
      )
    }));
  };

  // Calculer totaux
  const calculateTotals = () => {
    let totalHT = 0;
    const tvaByRate = {};

    formData.lines.forEach(line => {
      const lineTotal = (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price_ht) || 0);
      totalHT += lineTotal;

      const tvaAmount = lineTotal * (parseFloat(line.tva_rate) / 100);
      if (!tvaByRate[line.tva_rate]) {
        tvaByRate[line.tva_rate] = 0;
      }
      tvaByRate[line.tva_rate] += tvaAmount;
    });

    const totalTVA = Object.values(tvaByRate).reduce((sum, tva) => sum + tva, 0);
    const totalTTC = totalHT + totalTVA;

    return {
      totalHT: totalHT.toFixed(2),
      totalTVA: totalTVA.toFixed(2),
      totalTTC: totalTTC.toFixed(2),
      tvaByRate: Object.entries(tvaByRate).map(([rate, amount]) => ({
        rate: parseFloat(rate),
        amount: amount.toFixed(2)
      })).sort((a, b) => b.rate - a.rate)
    };
  };

  // Soumettre facture
  const handleSubmit = async () => {
    // Validations
    if (!formData.customer_id) {
      alert('❌ Veuillez sélectionner un client');
      return;
    }
    if (!formData.siren_client || formData.siren_client.length !== 9) {
      alert('❌ SIREN client obligatoire (9 chiffres)');
      return;
    }
    if (formData.lines.some(l => !l.designation || l.unit_price_ht <= 0)) {
      alert('❌ Toutes les lignes doivent avoir une désignation et un prix');
      return;
    }

    try {
      setLoading(true);
      
      const totals = calculateTotals();
      const invoiceData = {
        ...formData,
        total_ht: parseFloat(totals.totalHT),
        total_tva: parseFloat(totals.totalTVA),
        total_ttc: parseFloat(totals.totalTTC),
        format: 'pdf', // Commencer par PDF simple
        lines: formData.lines.map((line, i) => ({
          ...line,
          line_number: i + 1,
          quantity: parseFloat(line.quantity),
          unit_price_ht: parseFloat(line.unit_price_ht),
          tva_rate: parseFloat(line.tva_rate),
          tva_amount: (parseFloat(line.quantity) * parseFloat(line.unit_price_ht) * parseFloat(line.tva_rate) / 100).toFixed(2),
          total_ht: (parseFloat(line.quantity) * parseFloat(line.unit_price_ht)).toFixed(2),
          total_ttc: ((parseFloat(line.quantity) * parseFloat(line.unit_price_ht)) * (1 + parseFloat(line.tva_rate) / 100)).toFixed(2)
        }))
      };

      await http.post(`${API}/invoices/electronic`, invoiceData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      alert('✅ Facture créée avec succès !');
      onCreated();
      onBack();
    } catch (error) {
      console.error('Erreur création facture:', error);
      alert('❌ Erreur lors de la création de la facture');
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="h-6 w-6 text-gray-700" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Nouvelle facture électronique</h2>
          <p className="text-gray-600 mt-1">Conforme Factur-X • SIREN obligatoire</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Informations client */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-indigo-600" />
              Informations client
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Sélection client */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Client *</label>
              <select
                value={formData.customer_id}
                onChange={(e) => handleClientChange(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
              >
                <option value="">-- Sélectionner un client --</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.nom || client.name} {(client.siret || client.siren) ? `(SIREN: ${(client.siret || client.siren).slice(0,9)})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* SIREN obligatoire */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                SIREN client * <span className="text-red-500">(9 chiffres obligatoires)</span>
              </label>
              <input
                type="text"
                maxLength="9"
                value={formData.siren_client}
                onChange={(e) => setFormData({ ...formData, siren_client: e.target.value.replace(/\D/g, '') })}
                placeholder="123456789"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
              />
            </div>

            {/* Adresse facturation */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse de facturation *</label>
              <textarea
                value={formData.address_billing}
                onChange={(e) => setFormData({ ...formData, address_billing: e.target.value })}
                rows="2"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
              />
            </div>

            {/* Adresse livraison (optionnelle) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse de livraison (si différente)</label>
              <textarea
                value={formData.address_delivery}
                onChange={(e) => setFormData({ ...formData, address_delivery: e.target.value })}
                rows="2"
                placeholder="Laisser vide si identique à l'adresse de facturation"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
              />
            </div>
          </CardContent>
        </Card>

        {/* Dates et paiement */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-indigo-600" />
              Dates et conditions de paiement
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Date facture *</label>
              <input
                type="date"
                value={formData.invoice_date}
                onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Date échéance *</label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Conditions paiement</label>
              <select
                value={formData.payment_terms}
                onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
              >
                <option value="Comptant">Comptant</option>
                <option value="15 jours">15 jours</option>
                <option value="30 jours">30 jours</option>
                <option value="45 jours">45 jours</option>
                <option value="60 jours">60 jours</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Lignes de facturation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <List className="h-5 w-5 text-indigo-600" />
                Lignes de facturation
              </div>
              <Button onClick={addLine} variant="outline" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                Ajouter ligne
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* En-têtes colonnes */}
            <div className="grid grid-cols-12 gap-3 mb-3 text-sm font-semibold text-gray-700 px-2">
              <div className="col-span-4">Désignation</div>
              <div className="col-span-2">Quantité</div>
              <div className="col-span-2">Prix HT</div>
              <div className="col-span-2">TVA</div>
              <div className="col-span-2 text-right">Total HT</div>
            </div>

            {/* Lignes */}
            <div className="space-y-3">
              {formData.lines.map((line, index) => {
                const lineTotal = (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_price_ht) || 0);
                return (
                  <div key={index} className="grid grid-cols-12 gap-3 items-center bg-gray-50 p-3 rounded-xl">
                    <div className="col-span-4">
                      <input
                        type="text"
                        value={line.designation}
                        onChange={(e) => updateLine(index, 'designation', e.target.value)}
                        placeholder="Description de la ligne"
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        step="0.01"
                        value={line.quantity}
                        onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                      />
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        step="0.01"
                        value={line.unit_price_ht}
                        onChange={(e) => updateLine(index, 'unit_price_ht', e.target.value)}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                      />
                    </div>
                    <div className="col-span-2">
                      <select
                        value={line.tva_rate}
                        onChange={(e) => updateLine(index, 'tva_rate', parseFloat(e.target.value))}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                      >
                        <option value={20}>20%</option>
                        <option value={10}>10%</option>
                        <option value={5.5}>5,5%</option>
                        <option value={0}>0%</option>
                      </select>
                    </div>
                    <div className="col-span-1 text-right font-semibold text-gray-900">
                      {lineTotal.toFixed(2)}€
                    </div>
                    <div className="col-span-1 text-center">
                      {formData.lines.length > 1 && (
                        <button
                          onClick={() => removeLine(index)}
                          className="p-2 hover:bg-red-100 rounded-lg transition-colors text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Totaux */}
            <div className="mt-6 pt-6 border-t-2 border-gray-200">
              <div className="max-w-md ml-auto space-y-3">
                <div className="flex justify-between items-center text-lg">
                  <span className="font-semibold text-gray-700">Total HT</span>
                  <span className="font-bold text-gray-900">{totals.totalHT}€</span>
                </div>
                
                {/* Détail TVA par taux */}
                {totals.tvaByRate.map((tva) => (
                  <div key={tva.rate} className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">TVA {tva.rate}%</span>
                    <span className="font-semibold text-gray-900">{tva.amount}€</span>
                  </div>
                ))}
                
                <div className="flex justify-between items-center text-lg pt-3 border-t border-gray-200">
                  <span className="font-semibold text-gray-700">Total TVA</span>
                  <span className="font-bold text-gray-900">{totals.totalTVA}€</span>
                </div>
                
                <div className="flex justify-between items-center text-2xl pt-3 border-t-2 border-gray-300">
                  <span className="font-bold text-gray-900">Total TTC</span>
                  <span className="font-bold text-indigo-600">{totals.totalTTC}€</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-600" />
              Notes et mentions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
              placeholder="Notes complémentaires, conditions particulières..."
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
            />
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-4 justify-end">
          <Button onClick={onBack} variant="outline" disabled={loading}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Création...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Créer la facture
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MODULES COMPLÉMENTAIRES - Réception, E-Reporting, Archivage
// ============================================================================

// Module 1: Réception de factures
const ReceivedInvoicesModule = () => {
  const [receivedInvoices, setReceivedInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadData, setUploadData] = useState({
    file: null,
    supplier_name: '',
    supplier_siren: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    total_ht: '',
    total_tva: '',
    total_ttc: '',
    format_type: 'pdf-simple',
    notes: ''
  });

  useEffect(() => {
    if (!showUploadForm) {
      loadReceivedInvoices();
    }
  }, [showUploadForm]);

  const loadReceivedInvoices = async () => {
    if (!localStorage.getItem('token')) return;
    setLoading(true);
    try {
      const response = await http.get(`${API}/invoices/received`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setReceivedInvoices(response.data || []);
    } catch (error) {
      console.error('Erreur chargement factures reçues:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadData({ ...uploadData, file });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      alert('Veuillez sélectionner un fichier');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadData.file);
      Object.keys(uploadData).forEach(key => {
        if (key !== 'file' && uploadData[key]) {
          formData.append(key, uploadData[key]);
        }
      });

      await http.post(`${API}/invoices/received`, formData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      alert('✅ Facture importée !');
      setShowUploadForm(false);
      setUploadData({
        file: null,
        supplier_name: '',
        supplier_siren: '',
        invoice_number: '',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: '',
        total_ht: '',
        total_tva: '',
        total_ttc: '',
        format_type: 'pdf-simple',
        notes: ''
      });
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('❌ Erreur upload');
    } finally {
      setLoading(false);
    }
  };

  const updateInvoiceStatus = async (invoiceId, newStatus) => {
    try {
      await http.patch(
        `${API}/invoices/received/${invoiceId}/status`,
        { status: newStatus },
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      loadReceivedInvoices();
    } catch (error) {
      console.error('Erreur mise à jour statut:', error);
    }
  };

  if (showUploadForm) {
    return (
      <div>
        <button onClick={() => setShowUploadForm(false)} className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-5 w-5" />Retour
        </button>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">📤 Importer une facture reçue</h2>
        <form onSubmit={handleUpload} className="space-y-6">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <input type="file" accept=".pdf,.xml" onChange={handleFileChange} className="w-full" required />
              <input type="text" placeholder="Nom fournisseur *" value={uploadData.supplier_name} onChange={(e) => setUploadData({ ...uploadData, supplier_name: e.target.value })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl" required />
              <input type="text" placeholder="SIREN (9 chiffres)" maxLength="9" value={uploadData.supplier_siren} onChange={(e) => setUploadData({ ...uploadData, supplier_siren: e.target.value.replace(/\D/g, '') })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl" />
              <input type="text" placeholder="N° Facture *" value={uploadData.invoice_number} onChange={(e) => setUploadData({ ...uploadData, invoice_number: e.target.value })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl" required />
              <div className="grid grid-cols-3 gap-4">
                <input type="number" step="0.01" placeholder="Total HT *" value={uploadData.total_ht} onChange={(e) => setUploadData({ ...uploadData, total_ht: e.target.value })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl" required />
                <input type="number" step="0.01" placeholder="Total TVA *" value={uploadData.total_tva} onChange={(e) => setUploadData({ ...uploadData, total_tva: e.target.value })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl" required />
                <input type="number" step="0.01" placeholder="Total TTC *" value={uploadData.total_ttc} onChange={(e) => setUploadData({ ...uploadData, total_ttc: e.target.value })} className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl" required />
              </div>
            </CardContent>
          </Card>
          <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : '✅ Importer'}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">📥 Factures reçues</h2>
          <p className="text-gray-600">Import et gestion des factures fournisseurs</p>
        </div>
        <Button onClick={() => setShowUploadForm(true)} className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          <Upload className="h-4 w-4 mr-2" />Importer
        </Button>
      </div>
      {loading ? <div className="text-center py-12"><Loader2 className="h-12 w-12 animate-spin mx-auto text-indigo-600" /></div> : 
      receivedInvoices.length === 0 ? <div className="text-center py-12 bg-gray-50 rounded-2xl"><Download className="h-16 w-16 mx-auto text-gray-300 mb-4" /><p className="text-gray-500">Aucune facture</p></div> :
      <div className="space-y-3">{receivedInvoices.map((inv) => (
        <div key={inv.id} className="p-4 border-2 rounded-xl">
          <div className="flex justify-between">
            <div><div className="font-bold">{inv.invoice_number}</div><div className="text-sm text-gray-500">{inv.supplier_name}</div></div>
            <div><div className="font-bold">{inv.total_ttc}€</div>
            <select value={inv.status} onChange={(e) => updateInvoiceStatus(inv.id, e.target.value)} className="mt-2 px-3 py-1 rounded-lg text-sm">
              <option value="received">📥 Reçue</option>
              <option value="processing">⏳ Traitement</option>
              <option value="validated">✅ Validée</option>
              <option value="rejected">❌ Rejetée</option>
              <option value="paid">💳 Payée</option>
            </select></div>
          </div>
        </div>
      ))}</div>}
    </div>
  );
};

// Module 2: E-Reporting
const EReportingModule = () => {
  const [declarations, setDeclarations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    declaration_type: 'b2c',
    period_start: new Date().toISOString().split('T')[0],
    period_end: new Date().toISOString().split('T')[0],
    total_ht: '',
    total_tva: '',
    total_ttc: '',
    operations_count: '',
    notes: ''
  });

  useEffect(() => {
    if (!showCreateForm) {
      loadDeclarations();
    }
  }, [showCreateForm]);

  const loadDeclarations = async () => {
    if (!localStorage.getItem('token')) return;
    setLoading(true);
    try {
      const response = await http.get(`${API}/e-reporting`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setDeclarations(response.data || []);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await http.post(`${API}/e-reporting`, formData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      alert('✅ Déclaration créée !');
      setShowCreateForm(false);
    } catch (error) {
      alert('❌ Erreur');
    } finally {
      setLoading(false);
    }
  };

  const transmitDeclaration = async (id) => {
    if (!confirm('Transmettre au PDP ?')) return;
    try {
      await http.patch(`${API}/e-reporting/${id}/transmit`, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      alert('✅ Transmise !');
      loadDeclarations();
    } catch (error) {
      alert('❌ Erreur');
    }
  };

  if (showCreateForm) {
    return (
      <div>
        <button onClick={() => setShowCreateForm(false)} className="mb-6 flex items-center gap-2 text-gray-600"><ArrowLeft className="h-5 w-5" />Retour</button>
        <h2 className="text-2xl font-bold mb-6">📊 Nouvelle déclaration</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardContent className="space-y-4 pt-6">
              <select value={formData.declaration_type} onChange={(e) => setFormData({...formData, declaration_type: e.target.value})} className="w-full px-4 py-3 border-2 rounded-xl" required>
                <option value="b2c">B2C (Particuliers)</option>
                <option value="export">Export hors UE</option>
                <option value="intra-ue">Intra-UE</option>
              </select>
              <div className="grid grid-cols-3 gap-4">
                <input type="number" step="0.01" placeholder="Total HT *" value={formData.total_ht} onChange={(e) => setFormData({...formData, total_ht: e.target.value})} className="px-4 py-3 border-2 rounded-xl" required />
                <input type="number" step="0.01" placeholder="Total TVA *" value={formData.total_tva} onChange={(e) => setFormData({...formData, total_tva: e.target.value})} className="px-4 py-3 border-2 rounded-xl" required />
                <input type="number" step="0.01" placeholder="Total TTC *" value={formData.total_ttc} onChange={(e) => setFormData({...formData, total_ttc: e.target.value})} className="px-4 py-3 border-2 rounded-xl" required />
              </div>
              <input type="number" placeholder="Nb opérations *" value={formData.operations_count} onChange={(e) => setFormData({...formData, operations_count: e.target.value})} className="w-full px-4 py-3 border-2 rounded-xl" required />
            </CardContent>
          </Card>
          <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : '✅ Créer'}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between mb-6">
        <div><h2 className="text-2xl font-bold">📊 E-Reporting</h2><p className="text-gray-600">B2C, Export, Intra-UE</p></div>
        <Button onClick={() => setShowCreateForm(true)} className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white"><Plus className="h-4 w-4 mr-2" />Nouvelle</Button>
      </div>
      {loading ? <div className="text-center py-12"><Loader2 className="h-12 w-12 animate-spin mx-auto" /></div> : 
      declarations.length === 0 ? <div className="text-center py-12 bg-gray-50 rounded-2xl"><BarChart3 className="h-16 w-16 mx-auto text-gray-300 mb-4" /><p className="text-gray-500">Aucune déclaration</p></div> :
      <div className="space-y-3">{declarations.map((d) => (
        <div key={d.id} className="p-4 border-2 rounded-xl">
          <div className="flex justify-between">
            <div><div className="font-bold">{d.declaration_type === 'b2c' ? '🛒 B2C' : d.declaration_type === 'export' ? '🌍 Export' : '🇪🇺 Intra-UE'}</div><div className="text-sm">{d.operations_count} ops</div></div>
            <div><div className="font-bold">{d.total_ttc}€</div>
            {d.status === 'draft' ? <Button onClick={() => transmitDeclaration(d.id)} size="sm" className="mt-2">📤 Transmettre</Button> : 
            <Badge className="mt-2">{d.status === 'transmitted' ? '📤 Transmise' : d.status === 'accepted' ? '✅ Acceptée' : '❌ Rejetée'}</Badge>}</div>
          </div>
        </div>
      ))}</div>}
    </div>
  );
};

// Module 3: Archivage
const ArchivesModule = () => {
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    loadArchives();
  }, [filterType]);

  const loadArchives = async () => {
    if (!localStorage.getItem('token')) return;
    setLoading(true);
    try {
      const params = filterType !== 'all' ? { document_type: filterType } : {};
      const response = await http.get(`${API}/archives`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        params
      });
      setArchives(response.data || []);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const verifyIntegrity = async (id) => {
    try {
      const response = await http.post(`${API}/archives/${id}/verify`, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      alert(response.data.integrity_status === 'valid' ? '✅ Archive valide' : '❌ Archive corrompue');
      loadArchives();
    } catch (error) {
      alert('❌ Erreur');
    }
  };

  return (
    <div>
      <div className="flex justify-between mb-6">
        <div><h2 className="text-2xl font-bold">🗄️ Archivage légal</h2><p className="text-gray-600">Conservation 10 ans</p></div>
        <div className="flex gap-2">
          <Button onClick={() => setFilterType('all')} variant={filterType === 'all' ? 'default' : 'outline'} size="sm">Tous</Button>
          <Button onClick={() => setFilterType('invoice-emitted')} variant={filterType === 'invoice-emitted' ? 'default' : 'outline'} size="sm">Émises</Button>
          <Button onClick={() => setFilterType('invoice-received')} variant={filterType === 'invoice-received' ? 'default' : 'outline'} size="sm">Reçues</Button>
        </div>
      </div>
      {loading ? <div className="text-center py-12"><Loader2 className="h-12 w-12 animate-spin mx-auto" /></div> : 
      archives.length === 0 ? <div className="text-center py-12 bg-gray-50 rounded-2xl"><Shield className="h-16 w-16 mx-auto text-gray-300 mb-4" /><p className="text-gray-500">Aucune archive</p></div> :
      <div className="space-y-3">{archives.map((a) => (
        <div key={a.id} className="p-4 border-2 rounded-xl">
          <div className="flex justify-between">
            <div><div className="font-bold">{a.document_number}</div><div className="text-sm text-gray-500">{a.party_name}</div></div>
            <div><div className="font-bold">{a.total_ttc}€</div>
            <Badge className={`mt-2 ${a.integrity_status === 'valid' ? 'bg-green-100 text-green-700' : a.integrity_status === 'corrupted' ? 'bg-red-100 text-red-700' : 'bg-gray-100'}`}>
              {a.integrity_status === 'valid' ? '✅ Valide' : a.integrity_status === 'corrupted' ? '❌ Corrompu' : '⏳ Non vérifié'}
            </Badge>
            <Button onClick={() => verifyIntegrity(a.id)} size="sm" variant="outline" className="mt-2 w-full">🔍 Vérifier</Button></div>
          </div>
        </div>
      ))}</div>}
    </div>
  );
};

// ============================================================================
// FACTURATION CLASSIQUE — Dashboard financier premium
// ============================================================================

const ClassicInvoicingModule = ({ onBack }) => {
  const [quotes, setQuotes] = useState([]);
  const [worksites, setWorksites] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companySettings, setCompanySettings] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [generatedPdfUrls, setGeneratedPdfUrls] = useState({});
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterDate, setFilterDate] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('invoices');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // list | kanban

  useEffect(() => { loadData(); }, []);

  // Lire un filtre de recherche pré-rempli depuis la navigation (ex: clic depuis le Kanban)
  useEffect(() => {
    const q = localStorage.getItem('facturation_search');
    if (q) {
      setSearchQuery(q);
      localStorage.removeItem('facturation_search');
    }
  }, []);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    try {
      const [qRes, wsRes, schRes] = await Promise.all([
        http.get(`${API}/quotes`, { headers: { Authorization: `Bearer ${token}` } }),
        http.get(`${API}/worksites`, { headers: { Authorization: `Bearer ${token}` } }),
        http.get(`${API}/schedules`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setQuotes(qRes.data?.data || qRes.data || []);
      setWorksites(wsRes.data || []);
      setSchedules(schRes.data || []);
      http.get(`${API}/company-settings`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setCompanySettings(r.data))
        .catch(() => {});
    } catch (e) {
      console.error('Erreur chargement factures classiques:', e);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async (quote) => {
    setDownloadingId(quote.id);
    try {
      const response = await http.get(`${API}/quotes/${quote.id}/pdf?mode=facture`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Facture_${quote.quote_number || quote.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé !');
    } catch (e) {
      toast.error('Erreur téléchargement PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  const generateInvoice = async (quote) => {
    setGeneratingId(quote.id);
    try {
      const response = await http.get(`${API}/quotes/${quote.id}/pdf?mode=facture`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setGeneratedPdfUrls(prev => ({ ...prev, [quote.id]: { url, filename: `Facture_${quote.quote_number || quote.id}.pdf` } }));
      toast.success('Facture générée !');
    } catch (e) {
      toast.error('Erreur génération facture');
    } finally {
      setGeneratingId(null);
    }
  };

  const downloadGeneratedInvoice = (quote) => {
    const entry = generatedPdfUrls[quote.id];
    if (!entry) return;
    const a = document.createElement('a');
    a.href = entry.url;
    a.download = entry.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  // Marquer un devis comme facturé (émission de la facture, indépendant du téléchargement PDF)
  const markAsInvoiced = async (quote) => {
    try {
      await http.put(`${API}/quotes/${quote.id}`, { status: 'INVOICED' });
      setQuotes(prev => prev.map(q => q.id === quote.id ? { ...q, status: 'INVOICED' } : q));
      toast.success('Devis marqué comme facturé');
    } catch (e) {
      toast.error("Erreur lors du marquage en facturé");
    }
  };

  // Marquer une facture comme payée (encaissement effectif)
  const markAsPaid = async (quote) => {
    try {
      const paidAt = new Date().toISOString();
      await http.put(`${API}/quotes/${quote.id}`, { status: 'INVOICED', paid_at: paidAt });
      setQuotes(prev => prev.map(q => q.id === quote.id ? { ...q, paid_at: paidAt } : q));
      toast.success('Facture marquée comme payée');
    } catch (e) {
      toast.error('Erreur lors du marquage en payée');
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtAmt  = (a) => (parseFloat(a) || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

  const getLinkedWorksite = (quoteId) => worksites.find(w => w.quote_id === quoteId);

  // Échéance : due_date (facture) ou valid_until (devis) si renseignés, sinon
  // estimation à partir des réglages entreprise (délai paiement / durée validité devis)
  const getEcheance = (q) => {
    let raw = q.due_date || q.valid_until;
    let estimated = false;
    if (!raw) {
      if (!q.created_at) return null;
      const days = q.status === 'INVOICED'
        ? companySettings?.payment_delay_days
        : (companySettings?.quote_validity_days ?? 30);
      if (!days && days !== 0) return null;
      const d = new Date(q.created_at);
      d.setDate(d.getDate() + parseInt(days, 10));
      raw = d.toISOString();
      estimated = true;
    }
    return { date: new Date(raw), estimated };
  };
  const echeanceLabel = (q) => {
    const info = getEcheance(q);
    if (!info) return null;
    const { date: d, estimated } = info;
    const today = new Date(); today.setHours(0,0,0,0);
    const diff = Math.round((d - today) / 86400000);
    const suffix = estimated ? ' (est.)' : '';
    if (diff < 0) return { text: `Retard +${Math.abs(diff)}j${suffix}`, cls: 'text-red-600 font-bold' };
    if (diff === 0) return { text: `Aujourd'hui${suffix}`, cls: 'text-orange-500 font-bold' };
    if (diff <= 7) return { text: `J-${diff}${suffix}`, cls: 'text-amber-500 font-semibold' };
    return { text: `J-${diff}${suffix}`, cls: 'text-gray-400' };
  };

  const getStatusLabel = (q) => {
    if (q.status === 'INVOICED') {
      if (q.paid_at) return { label: 'Payée', cls: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' };
      return { label: 'Facturé', cls: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500' };
    }
    if (q.status === 'SENT' || q.status === 'PENDING') return { label: 'Envoyé', cls: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' };
    if (q.status === 'DRAFT') return { label: 'Brouillon', cls: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400' };
    if (q.status === 'CONVERTED_TO_WORKSITE') {
      const ws = getLinkedWorksite(q.id);
      if (!ws) return { label: 'Chantier', cls: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' };
      if (ws.status === 'COMPLETED') return { label: 'Clôturé', cls: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' };
      if (ws.status === 'IN_PROGRESS') return { label: 'En cours', cls: 'bg-blue-100 text-blue-700', dot: 'bg-blue-400' };
      return { label: 'Planifié', cls: 'bg-purple-100 text-purple-700', dot: 'bg-purple-400' };
    }
    return { label: q.status, cls: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400' };
  };

  // Groupes
  const scheduledIds = new Set(schedules.map(s => s.worksite_id));
  const invoicedQuotes = quotes.filter(q => q.status === 'INVOICED');
  const paidQuotes           = invoicedQuotes.filter(q => q.paid_at);
  const unpaidInvoicedQuotes = invoicedQuotes.filter(q => !q.paid_at);
  const sentQuotes     = quotes.filter(q => q.status === 'SENT' || q.status === 'PENDING');
  const draftQuotes    = quotes.filter(q => q.status === 'DRAFT');
  const worksiteQ      = quotes.filter(q => q.status === 'CONVERTED_TO_WORKSITE');
  const closedWsQuotes = worksiteQ.filter(q => { const ws = getLinkedWorksite(q.id); return ws && ws.status === 'COMPLETED'; });
  const activeWsQuotes = worksiteQ.filter(q => { const ws = getLinkedWorksite(q.id); return !ws || ws.status !== 'COMPLETED'; });
  const facturableWS   = worksites.filter(w => scheduledIds.has(w.id));

  // KPIs financiers
  const totalCA       = paidQuotes.reduce((s, q) => s + (parseFloat(q.amount) || 0), 0);
  const totalAttente  = unpaidInvoicedQuotes.reduce((s, q) => s + (parseFloat(q.amount) || 0), 0);
  const totalClotured = closedWsQuotes.reduce((s, q) => s + (parseFloat(q.amount) || 0), 0);
  const now = new Date();
  const thisMonth = quotes.filter(q => {
    if (!q.created_at) return false;
    const d = new Date(q.created_at);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const caThisMonth = thisMonth.reduce((s, q) => s + (parseFloat(q.amount) || 0), 0);
  const prevMonth = quotes.filter(q => {
    if (!q.created_at) return false;
    const d = new Date(q.created_at);
    const pm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return d.getMonth() === pm.getMonth() && d.getFullYear() === pm.getFullYear();
  });
  const caPrevMonth = prevMonth.reduce((s, q) => s + (parseFloat(q.amount) || 0), 0);
  const evolPct = caPrevMonth > 0 ? Math.round(((caThisMonth - caPrevMonth) / caPrevMonth) * 100) : null;
  const retardQuotes = unpaidInvoicedQuotes.filter(q => { const info = getEcheance(q); return info && info.date < new Date(); });
  const totalRetard  = retardQuotes.reduce((s, q) => s + (parseFloat(q.amount) || 0), 0);

  // Filtrage date
  const filterByDate = (list) => {
    if (filterDate === 'ALL') return list;
    return list.filter(q => {
      if (!q.created_at) return false;
      const d = new Date(q.created_at);
      const today = new Date(); today.setHours(0,0,0,0);
      if (filterDate === 'TODAY') return d >= today;
      if (filterDate === 'WEEK') { const w = new Date(today); w.setDate(w.getDate() - 7); return d >= w; }
      if (filterDate === 'MONTH') { const m = new Date(today.getFullYear(), today.getMonth(), 1); return d >= m; }
      if (filterDate === 'YEAR') { const y = new Date(today.getFullYear(), 0, 1); return d >= y; }
      return true;
    });
  };

  const allForFilter = [...invoicedQuotes, ...sentQuotes, ...closedWsQuotes, ...activeWsQuotes, ...draftQuotes];

  const baseFiltered = (() => {
    switch (filterStatus) {
      case 'INVOICED':  return invoicedQuotes;
      case 'SENT':      return sentQuotes;
      case 'CLOTURE':   return closedWsQuotes;
      case 'ACTIVE':    return activeWsQuotes;
      case 'DRAFT':     return draftQuotes;
      case 'RETARD':    return retardQuotes;
      default:          return allForFilter;
    }
  })();

  const searchFiltered = baseFiltered.filter(q => {
    if (!searchQuery.trim()) return true;
    const s = searchQuery.toLowerCase();
    return (
      (q.quote_number || '').toLowerCase().includes(s) ||
      (q.client_name  || '').toLowerCase().includes(s) ||
      (q.title        || '').toLowerCase().includes(s) ||
      (q.description  || '').toLowerCase().includes(s) ||
      String(q.amount || '').includes(s)
    );
  });

  const filteredQuotes = filterByDate(searchFiltered);

  // Sky IA — recommandations automatiques
  const skyIaRecs = [];
  if (retardQuotes.length > 0) skyIaRecs.push({ icon: '⚠️', text: `${retardQuotes.length} facture${retardQuotes.length > 1 ? 's' : ''} en retard représentant ${fmtAmt(totalRetard)}`, color: 'text-red-600' });
  if (evolPct !== null && evolPct > 0) skyIaRecs.push({ icon: '📈', text: `Votre CA augmente de ${evolPct}% par rapport au mois dernier`, color: 'text-emerald-600' });
  if (evolPct !== null && evolPct < 0) skyIaRecs.push({ icon: '📉', text: `Votre CA baisse de ${Math.abs(evolPct)}% par rapport au mois dernier`, color: 'text-amber-600' });
  if (closedWsQuotes.length > 0) skyIaRecs.push({ icon: '💡', text: `${closedWsQuotes.length} chantier${closedWsQuotes.length > 1 ? 's' : ''} clôturé${closedWsQuotes.length > 1 ? 's' : ''} prêt${closedWsQuotes.length > 1 ? 's' : ''} à facturer (${fmtAmt(totalClotured)})`, color: 'text-indigo-600' });
  if (totalAttente > 0) skyIaRecs.push({ icon: '⏳', text: `${fmtAmt(totalAttente)} en attente de règlement (${unpaidInvoicedQuotes.length} facture${unpaidInvoicedQuotes.length > 1 ? 's' : ''})`, color: 'text-blue-600' });

  // Stats top clients
  const clientStats = Object.entries(
    allForFilter.reduce((acc, q) => {
      const k = q.client_name || 'Inconnu';
      acc[k] = (acc[k] || 0) + (parseFloat(q.amount) || 0);
      return acc;
    }, {})
  ).sort((a,b) => b[1] - a[1]).slice(0, 5);

  // Stats CA mensuel (6 derniers mois)
  const monthlyCA = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    const ca = allForFilter.filter(q => {
      if (!q.created_at) return false;
      const qd = new Date(q.created_at);
      return qd.getMonth() === d.getMonth() && qd.getFullYear() === d.getFullYear();
    }).reduce((s, q) => s + (parseFloat(q.amount) || 0), 0);
    return { label: d.toLocaleDateString('fr-FR', { month: 'short' }), ca };
  });
  const maxCA = Math.max(...monthlyCA.map(m => m.ca), 1);

  // Kanban columns
  const kanbanCols = [
    { key: 'DRAFT',    label: 'Brouillons', color: 'border-gray-300 bg-gray-50',    items: draftQuotes },
    { key: 'SENT',     label: 'Envoyées',   color: 'border-blue-300 bg-blue-50',    items: sentQuotes },
    { key: 'CLOTURE',  label: 'Clôturées',  color: 'border-orange-300 bg-orange-50',items: closedWsQuotes },
    { key: 'INVOICED', label: 'Facturées',  color: 'border-emerald-300 bg-emerald-50', items: invoicedQuotes },
  ];

  if (selectedQuote) {
    const st = getStatusLabel(selectedQuote);
    const ech = echeanceLabel(selectedQuote);
    const ws = getLinkedWorksite(selectedQuote.id);
    const amt = parseFloat(selectedQuote.amount) || 0;
    const tva = amt - amt / 1.2;
    const ht  = amt / 1.2;
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <button onClick={() => setSelectedQuote(null)} className="flex items-center gap-2 text-gray-500 hover:text-gray-800 mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Retour
        </button>
        <div className="grid grid-cols-3 gap-6">
          {/* Colonne principale */}
          <div className="col-span-2 space-y-4">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-xs text-gray-400 font-semibold uppercase mb-1">Facture #{selectedQuote.quote_number || '—'}</p>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedQuote.title || '—'}</h2>
                  {selectedQuote.description && <p className="text-gray-500 mt-1 text-sm">{selectedQuote.description}</p>}
                </div>
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold ${st.cls}`}>
                  <span className={`w-2 h-2 rounded-full ${st.dot}`} />
                  {st.label}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                <div><p className="text-xs text-gray-400 mb-1">Date création</p><p className="font-medium text-gray-800">{fmtDate(selectedQuote.created_at)}</p></div>
                <div><p className="text-xs text-gray-400 mb-1">Échéance estimée</p><p className={`font-medium ${ech?.cls || 'text-gray-800'}`}>{ech ? ech.text : '—'}</p></div>
                {ws && <div><p className="text-xs text-gray-400 mb-1">Chantier lié</p><p className="font-medium text-gray-800">{ws.name || ws.id}</p></div>}
              </div>
            </div>
            {/* Client */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-3">Client</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-gray-400">Nom</p><p className="font-semibold">{selectedQuote.client_name || '—'}</p></div>
                <div><p className="text-xs text-gray-400">Email</p><p className="text-indigo-600">{selectedQuote.client_email || '—'}</p></div>
              </div>
            </div>
            {/* Timeline */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4">Timeline</h3>
              <div className="space-y-3">
                {[
                  { label: 'Devis créé', date: selectedQuote.created_at, done: true },
                  { label: 'Chantier créé', date: ws?.created_at, done: !!ws },
                  { label: 'Chantier terminé', date: null, done: ws?.status === 'COMPLETED' },
                  { label: 'Facture générée', date: selectedQuote.status === 'INVOICED' ? selectedQuote.updated_at : null, done: selectedQuote.status === 'INVOICED' },
                  { label: 'Envoyée', date: null, done: selectedQuote.status === 'SENT' || selectedQuote.status === 'INVOICED' },
                  { label: 'Payée', date: selectedQuote.paid_at, done: !!selectedQuote.paid_at },
                ].map((ev, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${ev.done ? 'bg-indigo-600' : 'bg-gray-200'}`}>
                      {ev.done ? <CheckCircle className="h-4 w-4 text-white" /> : <div className="w-2 h-2 rounded-full bg-gray-400" />}
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${ev.done ? 'text-gray-900' : 'text-gray-400'}`}>{ev.label}</p>
                      {ev.date && <p className="text-xs text-gray-400">{fmtDate(ev.date)}</p>}
                    </div>
                    {i < 5 && <div className="absolute left-3.5 mt-7 h-3 w-0.5 bg-gray-200" />}
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Colonne droite */}
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4">Résumé financier</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Sous-total HT</span><span className="font-medium">{fmtAmt(ht)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">TVA (20%)</span><span className="font-medium">{fmtAmt(tva)}</span></div>
                <div className="flex justify-between border-t border-gray-100 pt-2 mt-2"><span className="font-bold">Total TTC</span><span className="font-bold text-indigo-600 text-lg">{fmtAmt(amt)}</span></div>
                <div className="flex justify-between text-gray-400"><span>Déjà payé</span><span>{selectedQuote.paid_at ? fmtAmt(amt) : '0,00 €'}</span></div>
                <div className="flex justify-between text-red-600 font-semibold border-t border-gray-100 pt-2"><span>Reste à payer</span><span>{selectedQuote.paid_at ? fmtAmt(0) : fmtAmt(amt)}</span></div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-2">
              {generatedPdfUrls[selectedQuote.id] ? (
                <button onClick={() => downloadGeneratedInvoice(selectedQuote)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-semibold transition-colors">
                  <Download className="h-4 w-4" /> Télécharger la facture
                </button>
              ) : (
                <button onClick={() => generateInvoice(selectedQuote)} disabled={generatingId === selectedQuote.id}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50">
                  {generatingId === selectedQuote.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                  Générer la facture
                </button>
              )}
              {selectedQuote.status !== 'INVOICED' && (
                <button onClick={() => { markAsInvoiced(selectedQuote); setSelectedQuote(prev => ({ ...prev, status: 'INVOICED' })); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-indigo-200 hover:bg-indigo-50 text-indigo-700 rounded-xl text-sm font-semibold transition-colors">
                  <FileText className="h-4 w-4" /> Marquer comme facturée
                </button>
              )}
              {selectedQuote.status === 'INVOICED' && !selectedQuote.paid_at && (
                <button onClick={() => { markAsPaid(selectedQuote); setSelectedQuote(prev => ({ ...prev, paid_at: new Date().toISOString() })); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-semibold transition-colors">
                  <CheckCircle className="h-4 w-4" /> Marquer payée
                </button>
              )}
              <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl text-sm font-semibold transition-colors">
                <Mail className="h-4 w-4" /> Envoyer par email
              </button>
              <div className="pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-400 text-center mb-2">Paiement en ligne</p>
                <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-400 rounded-xl text-sm font-semibold cursor-not-allowed">
                  <CreditCard className="h-4 w-4" /> Payer via Stripe
                  <span className="text-xs bg-gray-200 px-1.5 py-0.5 rounded-full ml-1">Bientôt</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-white rounded-xl transition-colors shadow-sm border border-gray-200">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Calculator className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Facturation Classique</h1>
              <p className="text-xs text-gray-400">Tableau de bord financier</p>
            </div>
          </div>
        </div>
        <button onClick={loadData} className="p-2 hover:bg-white rounded-xl border border-gray-200 shadow-sm transition-colors" title="Actualiser">
          <RefreshCw className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-white rounded-2xl animate-pulse border border-gray-100" />
          ))}
        </div>
      ) : (
        <>
          {/* Sky IA */}
          {skyIaRecs.length > 0 && (
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-white text-xs font-bold">IA</span>
                </div>
                <p className="text-sm font-bold text-indigo-700">Sky IA — Recommandations</p>
              </div>
              <div className="space-y-1">
                {skyIaRecs.map((r, i) => (
                  <p key={i} className={`text-sm ${r.color}`}>{r.icon} {r.text}</p>
                ))}
              </div>
            </div>
          )}

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {/* CA encaissé */}
            <div className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm">
              <p className="text-xs font-semibold text-gray-400 uppercase mb-2">CA Encaissé</p>
              <p className="text-2xl font-bold text-emerald-600">{fmtAmt(totalCA)}</p>
              {evolPct !== null && (
                <p className={`text-xs mt-1 font-semibold ${evolPct >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                  {evolPct >= 0 ? '▲' : '▼'} {Math.abs(evolPct)}% vs mois préc.
                </p>
              )}
              <div className="flex gap-0.5 mt-2 h-6 items-end">
                {monthlyCA.map((m, i) => (
                  <div key={i} className="flex-1 bg-emerald-100 rounded-sm" style={{ height: `${Math.round((m.ca / maxCA) * 100)}%`, minHeight: 2 }} />
                ))}
              </div>
            </div>
            {/* À encaisser */}
            <div className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm">
              <p className="text-xs font-semibold text-gray-400 uppercase mb-2">À Encaisser</p>
              <p className="text-2xl font-bold text-blue-600">{fmtAmt(totalAttente)}</p>
              <p className="text-xs text-gray-400 mt-1">{unpaidInvoicedQuotes.length} facture{unpaidInvoicedQuotes.length > 1 ? 's' : ''}</p>
              <div className="mt-2 h-1.5 bg-blue-100 rounded-full"><div className="h-full bg-blue-500 rounded-full" style={{ width: totalCA + totalAttente > 0 ? `${(totalAttente / (totalCA + totalAttente)) * 100}%` : '0%' }} /></div>
            </div>
            {/* En retard */}
            <div className={`rounded-2xl p-4 border shadow-sm ${retardQuotes.length > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
              <p className={`text-xs font-semibold uppercase mb-2 ${retardQuotes.length > 0 ? 'text-red-400' : 'text-gray-400'}`}>En Retard</p>
              <p className={`text-2xl font-bold ${retardQuotes.length > 0 ? 'text-red-600' : 'text-gray-400'}`}>{retardQuotes.length}</p>
              <p className={`text-xs mt-1 ${retardQuotes.length > 0 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>{fmtAmt(totalRetard)}</p>
            </div>
            {/* Prévision */}
            <div className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm">
              <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Mois en cours</p>
              <p className="text-2xl font-bold text-indigo-600">{fmtAmt(caThisMonth)}</p>
              <p className="text-xs text-gray-400 mt-1">{thisMonth.length} dossier{thisMonth.length > 1 ? 's' : ''}</p>
              <div className="mt-2 h-1.5 bg-indigo-100 rounded-full"><div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.min((caThisMonth / Math.max(caPrevMonth, 1)) * 100, 100)}%` }} /></div>
            </div>
          </div>

          {/* Onglets */}
          <div className="flex gap-1 bg-white p-1 rounded-2xl border border-gray-200 shadow-sm mb-5 w-fit">
            {[
              { key: 'invoices', label: '📄 Factures' },
              { key: 'stats',    label: '📊 Statistiques' },
              { key: 'kanban',   label: '🗂 Kanban' },
            ].map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                  activeTab === t.key ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-100'
                }`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* ===== ONGLET FACTURES ===== */}
          {activeTab === 'invoices' && (
            <>
              {/* Barre recherche + filtres */}
              <div className="flex flex-wrap gap-3 mb-4">
                <div className="relative flex-1 min-w-48">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Rechercher client, n°, désignation, montant..."
                    className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 shadow-sm"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">✕</button>
                  )}
                </div>
                <select value={filterDate} onChange={e => setFilterDate(e.target.value)}
                  className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 focus:outline-none shadow-sm">
                  <option value="ALL">Toutes périodes</option>
                  <option value="TODAY">Aujourd'hui</option>
                  <option value="WEEK">Cette semaine</option>
                  <option value="MONTH">Ce mois</option>
                  <option value="YEAR">Cette année</option>
                </select>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {[
                  { key: 'ALL',      label: 'Tous',       count: allForFilter.length },
                  { key: 'INVOICED', label: 'Facturés',   count: invoicedQuotes.length },
                  { key: 'CLOTURE',  label: 'Clôturés',   count: closedWsQuotes.length },
                  { key: 'SENT',     label: 'Envoyés',    count: sentQuotes.length },
                  { key: 'ACTIVE',   label: 'En cours',   count: activeWsQuotes.length },
                  { key: 'RETARD',   label: 'En retard',  count: retardQuotes.length },
                  { key: 'DRAFT',    label: 'Brouillons', count: draftQuotes.length },
                ].map(f => (
                  <button key={f.key} onClick={() => setFilterStatus(f.key)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                      filterStatus === f.key
                        ? f.key === 'RETARD' ? 'bg-red-600 text-white shadow-md' : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md'
                        : f.key === 'RETARD' && retardQuotes.length > 0 ? 'bg-red-50 text-red-600 border border-red-200' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
                    }`}>
                    {f.label} <span className="ml-1 opacity-70">{f.count}</span>
                  </button>
                ))}
              </div>

              {/* Tableau */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                {filteredQuotes.length === 0 ? (
                  <div className="text-center py-14">
                    <FileText className="h-12 w-12 mx-auto text-gray-200 mb-3" />
                    <p className="text-gray-400 font-medium">Aucun résultat</p>
                    {searchQuery && <p className="text-xs text-gray-400 mt-1">Modifiez votre recherche</p>}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                          <th className="text-left px-4 py-3">N°</th>
                          <th className="text-left px-4 py-3">Client</th>
                          <th className="text-left px-4 py-3">Désignation</th>
                          <th className="text-left px-4 py-3">Date</th>
                          <th className="text-left px-4 py-3">Échéance</th>
                          <th className="text-left px-4 py-3">Statut</th>
                          <th className="text-right px-4 py-3">Montant TTC</th>
                          <th className="text-right px-4 py-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {filteredQuotes.map(q => {
                          const st  = getStatusLabel(q);
                          const ech = echeanceLabel(q);
                          return (
                            <tr key={q.id} onClick={() => setSelectedQuote(q)}
                              className="hover:bg-indigo-50/40 cursor-pointer transition-colors group">
                              <td className="px-4 py-3 font-mono text-gray-500 text-xs">#{q.quote_number || '—'}</td>
                              <td className="px-4 py-3">
                                <p className="font-semibold text-gray-900 truncate max-w-36">{q.client_name || '—'}</p>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-gray-700 truncate max-w-44">{q.title || '—'}</p>
                                {q.description && <p className="text-xs text-gray-400 truncate max-w-44">{q.description}</p>}
                              </td>
                              <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{fmtDate(q.created_at)}</td>
                              <td className="px-4 py-3 text-xs whitespace-nowrap">
                                {ech ? <span className={ech.cls}>{ech.text}</span> : <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${st.cls}`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{st.label}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-gray-900 whitespace-nowrap">{fmtAmt(q.amount)}</td>
                              <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                                <div className="flex justify-end items-center gap-1">
                                  <button onClick={() => downloadPDF(q)} disabled={downloadingId === q.id}
                                    className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Télécharger PDF">
                                    {downloadingId === q.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                                  </button>
                                  <div className="relative">
                                    <button onClick={() => setOpenMenuId(openMenuId === q.id ? null : q.id)}
                                      className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                                      <MoreHorizontal className="h-3.5 w-3.5" />
                                    </button>
                                    {openMenuId === q.id && (
                                      <div className="absolute right-0 top-7 z-30 w-44 bg-white border border-gray-200 rounded-xl shadow-xl py-1">
                                        <button onClick={() => { setSelectedQuote(q); setOpenMenuId(null); }}
                                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                          <Eye className="h-3.5 w-3.5" /> Voir le détail
                                        </button>
                                        <button onClick={() => { downloadPDF(q); setOpenMenuId(null); }}
                                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                          <Download className="h-3.5 w-3.5" /> Télécharger PDF
                                        </button>
                                        <button className="w-full text-left px-3 py-2 text-sm text-gray-400 hover:bg-gray-50 flex items-center gap-2 cursor-not-allowed">
                                          <Mail className="h-3.5 w-3.5" /> Envoyer
                                        </button>
                                        {q.status !== 'INVOICED' && (
                                          <button onClick={() => { markAsInvoiced(q); setOpenMenuId(null); }}
                                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                            <FileText className="h-3.5 w-3.5" /> Marquer comme facturée
                                          </button>
                                        )}
                                        {q.status === 'INVOICED' && !q.paid_at && (
                                          <button onClick={() => { markAsPaid(q); setOpenMenuId(null); }}
                                            className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                            <CheckCircle className="h-3.5 w-3.5" /> Marquer payée
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ===== ONGLET STATISTIQUES ===== */}
          {activeTab === 'stats' && (
            <div className="space-y-5">
              {/* CA mensuel */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                <h3 className="font-bold text-gray-900 mb-4">CA sur 6 mois</h3>
                <div className="flex items-end gap-2 h-32">
                  {monthlyCA.map((m, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <p className="text-xs font-semibold text-indigo-600">{m.ca > 0 ? `${Math.round(m.ca / 1000)}k` : ''}</p>
                      <div className="w-full bg-indigo-100 rounded-t-lg transition-all" style={{ height: `${Math.round((m.ca / maxCA) * 96) + 4}px` }}>
                        <div className="w-full h-full bg-gradient-to-t from-indigo-600 to-indigo-400 rounded-t-lg" />
                      </div>
                      <p className="text-xs text-gray-400 capitalize">{m.label}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {/* Top clients */}
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                  <h3 className="font-bold text-gray-900 mb-3">Top clients</h3>
                  <div className="space-y-2">
                    {clientStats.length === 0 && <p className="text-gray-400 text-sm">Aucune donnée</p>}
                    {clientStats.map(([name, ca], i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 text-xs font-bold flex items-center justify-center">{i + 1}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                          <div className="h-1 bg-indigo-100 rounded-full mt-1">
                            <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${(ca / (clientStats[0]?.[1] || 1)) * 100}%` }} />
                          </div>
                        </div>
                        <p className="text-sm font-bold text-gray-700 whitespace-nowrap">{fmtAmt(ca)}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Récap */}
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
                  <h3 className="font-bold text-gray-900 mb-3">Récapitulatif</h3>
                  <div className="space-y-2 text-sm">
                    {[
                      { label: 'Total dossiers',     value: allForFilter.length },
                      { label: 'Montant moyen',       value: allForFilter.length ? fmtAmt(allForFilter.reduce((s,q) => s + (parseFloat(q.amount)||0), 0) / allForFilter.length) : '—' },
                      { label: 'CA facturé',          value: fmtAmt(totalCA) },
                      { label: 'À encaisser',         value: fmtAmt(totalAttente) },
                      { label: 'Chantiers clôturés',  value: closedWsQuotes.length },
                      { label: 'TVA collectée (est.)',value: fmtAmt(totalCA - totalCA / 1.2) },
                    ].map((row, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="text-gray-500">{row.label}</span>
                        <span className="font-semibold text-gray-800">{row.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===== ONGLET KANBAN ===== */}
          {activeTab === 'kanban' && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {kanbanCols.map(col => (
                <div key={col.key} className={`rounded-2xl border-2 ${col.color} p-3`}>
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-bold text-gray-700 text-sm">{col.label}</p>
                    <span className="bg-white/60 text-gray-600 text-xs font-bold px-2 py-0.5 rounded-full">{col.items.length}</span>
                  </div>
                  <div className="space-y-2">
                    {col.items.length === 0 && <p className="text-gray-400 text-xs text-center py-4">Vide</p>}
                    {col.items.map(q => (
                      <div key={q.id} onClick={() => setSelectedQuote(q)}
                        className="bg-white rounded-xl p-3 shadow-sm border border-white/80 cursor-pointer hover:shadow-md transition-shadow">
                        <p className="font-mono text-xs text-gray-400 mb-1">#{q.quote_number || '—'}</p>
                        <p className="font-semibold text-gray-900 text-sm truncate">{q.client_name || '—'}</p>
                        <p className="text-xs text-gray-500 truncate mt-0.5">{q.title || '—'}</p>
                        <p className="text-sm font-bold text-indigo-600 mt-2">{fmtAmt(q.amount)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============================================================================
// FACTURATION HUB — Choix entre Classique et Électronique
// ============================================================================

const FacturationHub = () => {
  const [mode, setMode] = useState(null); // null | 'classic' | 'electronic'

  if (mode === 'classic') {
    return <ClassicInvoicingModule onBack={() => setMode(null)} />;
  }

  if (mode === 'electronic') {
    return <InvoicingModule onBack={() => setMode(null)} />;
  }

  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center px-4 py-12">
      {/* Titre */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg mb-4">
          <FileText className="h-8 w-8 text-white" />
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-3">Facturation</h1>
        <p className="text-gray-500 text-lg max-w-md">Choisissez votre mode de facturation</p>
      </div>

      {/* Deux cartes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-3xl">

        {/* Facturation Classique */}
        <button
          onClick={() => setMode('classic')}
          className="group relative bg-white border-2 border-gray-200 hover:border-indigo-400 rounded-3xl p-8 text-left shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
        >
          <div className="flex items-center justify-between mb-5">
            <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <Calculator className="h-7 w-7 text-white" />
            </div>
            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">✅ Disponible</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Facturation Classique</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-5">
            Créez et gérez vos devis, convertissez-les en factures PDF, suivez les paiements et l'historique client.
          </p>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-center gap-2"><span className="text-blue-500">•</span> Devis & factures PDF</li>
            <li className="flex items-center gap-2"><span className="text-blue-500">•</span> Suivi des paiements</li>
            <li className="flex items-center gap-2"><span className="text-blue-500">•</span> Historique complet</li>
          </ul>
          <div className="mt-6 flex items-center gap-2 text-indigo-600 font-semibold text-sm group-hover:gap-3 transition-all">
            Accéder <span>→</span>
          </div>
        </button>

        {/* Facturation Électronique — Coming Soon */}
        <button
          onClick={() => setMode('electronic')}
          className="group relative bg-white border-2 border-gray-200 hover:border-purple-400 rounded-3xl p-8 text-left shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
        >
          {/* Badge Coming Soon */}
          <div className="absolute -top-3 -right-3 z-10">
            <span className="px-3 py-1.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-bold rounded-full shadow-lg">
              Bientôt disponible
            </span>
          </div>

          <div className="flex items-center justify-between mb-5">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <FileText className="h-7 w-7 text-white" />
            </div>
            <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-bold rounded-full">⚡ Réforme 2026</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Facturation Électronique</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-5">
            Conformité DGFiP 2026-2027. Émission Factur-X, transmission via PDP IOPOLE, e-reporting B2C et archivage légal.
          </p>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-center gap-2"><span className="text-purple-500">•</span> Format Factur-X (PDF+XML)</li>
            <li className="flex items-center gap-2"><span className="text-purple-500">•</span> PDP IOPOLE certifié</li>
            <li className="flex items-center gap-2"><span className="text-purple-500">•</span> E-Reporting DGFiP</li>
          </ul>
          <div className="mt-6 flex items-center gap-2 text-purple-600 font-semibold text-sm group-hover:gap-3 transition-all">
            Aperçu <span>→</span>
          </div>
        </button>

      </div>

      {/* Note légale */}
      <div className="mt-10 text-center text-sm text-gray-400 max-w-lg">
        <span className="font-medium text-gray-500">📅 Obligations légales :</span> Réception obligatoire dès le <strong>1er sept. 2026</strong> · Émission : <strong>1er sept. 2026</strong> (ETI) / <strong>1er sept. 2027</strong> (PME/TPE)
      </div>
    </div>
  );
};

// ============================================================================
// INVOICING MODULE - Facturation Électronique (Réforme 2026-2027)
// ============================================================================

const InvoicingModule = ({ onBack } = {}) => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [activeSubTab, setActiveSubTab] = useState('all'); // all, emit, receive, reporting, archive
  const [invoices, setInvoices] = useState([]);
  const [worksites, setWorksites] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [prefillData, setPrefillData] = useState(null);

  // Détecter un préremplissage depuis la page Devis/Chantiers
  useEffect(() => {
    const raw = localStorage.getItem('invoice_prefill');
    if (raw) {
      try {
        const data = JSON.parse(raw);
        setPrefillData(data);
        setActiveSubTab('emit');
        setShowCreateForm(true);
      } catch (_) {}
      localStorage.removeItem('invoice_prefill');
    }
  }, []);

  // Charger les factures
  useEffect(() => {
    if (activeSubTab === 'emit' || activeSubTab === 'all') {
      loadInvoices();
    }
    if (activeSubTab === 'all') {
      loadWorksitesData();
    }
  }, [activeSubTab]);

  const loadWorksitesData = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const [wsRes, schRes] = await Promise.all([
        http.get(`${API}/worksites`, { headers: { Authorization: `Bearer ${token}` } }),
        http.get(`${API}/schedules`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setWorksites(wsRes.data || []);
      setSchedules(schRes.data || []);
    } catch (_) {}
  };

  // ⚡ Realtime — rafraîchir automatiquement quand les factures/devis changent
  useEffect(() => {
    const handler = (e) => { if (e.detail?.table === 'quotes' || e.detail?.table === 'worksites') loadInvoices(); };
    window.addEventListener('realtime-update', handler);
    return () => window.removeEventListener('realtime-update', handler);
  }, []);

  const loadInvoices = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      setLoading(true);
      const response = await http.get(`${API}/invoices/electronic`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      setInvoices(response.data || []);
    } catch (error) {
      console.error('Erreur chargement factures:', error);
    } finally {
      setLoading(false);
    }
  };

  const transmitInvoice = async (invoiceId, invoiceNumber) => {
    if (!(await confirm(`Transmettre la facture ${invoiceNumber} au PDP IOPOLE ?`))) return;
    try {
      const res = await http.patch(`${API}/invoices/electronic/${invoiceId}/transmit`, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      alert(res.data.simulation
        ? `✅ Facture transmise (simulation sandbox)\nRéférence : ${res.data.pdp_reference}`
        : `✅ Facture transmise !\nRéférence PDP : ${res.data.pdp_reference}`
      );
      loadInvoices();
    } catch (error) {
      alert('❌ Erreur lors de la transmission : ' + (error.response?.data?.detail || error.message));
    }
  };

  const downloadInvoiceXml = async (invoiceId, invoiceNumber) => {
    try {
      const response = await http.get(`${API}/invoices/electronic/${invoiceId}/xml`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `facture_${invoiceNumber}_facturx.xml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('❌ Erreur génération XML : ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6 relative">

      {/* Bouton retour — reste toujours cliquable */}
      {onBack && (
        <div className="absolute top-6 left-6 z-20">
          <button onClick={onBack} className="p-2 bg-white hover:bg-gray-50 rounded-xl transition-colors shadow-sm border border-gray-200">
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      )}

      {/* Badge "Aperçu" */}
      <div className="absolute top-6 right-6 z-20">
        <span className="flex items-center gap-2 px-4 py-2 bg-amber-100 border border-amber-300 text-amber-700 rounded-full text-sm font-semibold shadow-sm">
          <Clock className="h-4 w-4" />
          Aperçu — Disponible bientôt
        </span>
      </div>

      {/* Overlay bloquant toutes les interactions */}
      <div className="absolute inset-0 z-10 rounded-none cursor-not-allowed" style={{ background: 'rgba(248,250,252,0.45)' }} />

      {/* Contenu grisé + non interactif */}
      <div className="pointer-events-none select-none" style={{ filter: 'grayscale(35%) opacity(0.72)' }}>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-3">
          <div className="w-10 h-10" /> {/* placeholder bouton retour (déjà au-dessus) */}
          <FileText className="h-10 w-10 text-indigo-600" />
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Facturation Électronique</h1>
            <p className="text-gray-600 mt-1">Conforme réforme DGFiP 2026-2027 • Format Factur-X</p>
          </div>
        </div>
        
        {/* Bannière conformité */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-4 rounded-2xl shadow-lg">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-6 w-6" />
            <div>
              <p className="font-semibold">✅ Module conforme loi française</p>
              <p className="text-sm text-white/90">Réception obligatoire : 1er sept. 2026 • Émission : 1er sept. 2026 (ETI) / 2027 (PME/TPE)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sous-onglets */}
      <div className="mb-6">
        <div className="flex gap-2 bg-white p-2 rounded-2xl shadow-md border border-gray-200 overflow-x-auto">
          <button
            onClick={() => setActiveSubTab('all')}
            className={`px-6 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
              activeSubTab === 'all'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            📋 Toutes les factures
          </button>
          <button
            onClick={() => setActiveSubTab('emit')}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              activeSubTab === 'emit'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            📤 Émettre
          </button>
          <button
            onClick={() => setActiveSubTab('receive')}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              activeSubTab === 'receive'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            📥 Recevoir
          </button>
          <button
            onClick={() => setActiveSubTab('reporting')}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              activeSubTab === 'reporting'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            📊 E-Reporting
          </button>
          <button
            onClick={() => setActiveSubTab('archive')}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              activeSubTab === 'archive'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            🗄️ Archivage
          </button>
        </div>
      </div>

      {/* Contenu selon sous-onglet */}
      <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-100">
      {/* ─── Onglet Toutes les factures ─── */}
        {activeSubTab === 'all' && (
          (() => {
            const scheduledIds = new Set(schedules.map(s => s.worksite_id));
            const pending = worksites.filter(w => scheduledIds.has(w.id) && !w.invoice_id);
            const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
            const totalPending = pending.reduce((s, w) => s + (parseFloat(w.amount) || 0), 0);
            const totalDone = invoices.reduce((s, inv) => s + (parseFloat(inv.total_ttc) || 0), 0);

            return (
              <div>
                {/* KPIs */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                  <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-5 border border-emerald-200">
                    <p className="text-xs font-semibold text-emerald-600 mb-1">CHANTIERS FACTURÉS</p>
                    <p className="text-3xl font-bold text-emerald-700">{pending.length}</p>
                    <p className="text-sm text-emerald-600 mt-1">{totalPending.toFixed(2)} € total</p>
                  </div>
                  <div className="col-span-2 bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-5 border border-gray-200 flex items-center gap-4">
                    <span className="text-2xl">🔒</span>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Facturation électronique</p>
                      <p className="text-xs text-gray-500">Fonctionnalité en cours d'activation — disponible prochainement</p>
                    </div>
                  </div>
                </div>

                {/* Chantiers facturés */}
                {pending.length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
                      <h3 className="text-lg font-bold text-emerald-700">Chantiers facturés ({pending.length})</h3>
                    </div>
                    <div className="space-y-3">
                      {pending.map(w => (
                        <div key={w.id} className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4 hover:shadow-md transition-all">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-gray-900 truncate">{w.title || 'Chantier sans titre'}</span>
                              {fmtDate(w.start_date || w.created_at) && (
                                <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-lg border border-gray-200">
                                  {fmtDate(w.start_date || w.created_at)}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-0.5">{w.client_name || <span className="italic text-gray-400">Client non renseigné</span>}</p>
                          </div>
                          <div className="flex items-center gap-4 ml-4 shrink-0">
                            <span className="text-lg font-bold text-emerald-700">{(parseFloat(w.amount) || 0).toFixed(2)} €</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* E-factures émises */}
                {invoices.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
                      <h3 className="text-lg font-bold text-emerald-700">E-factures émises ({invoices.length})</h3>
                    </div>
                    <div className="space-y-3">
                      {invoices.map(inv => (
                        <div key={inv.id} className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4 hover:shadow-md transition-all">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-gray-900 font-mono text-sm">{inv.invoice_number}</span>
                              <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                                inv.status_pdp === 'transmitted' ? 'bg-emerald-100 text-emerald-700' :
                                inv.status_pdp === 'draft' ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-700'
                              }`}>{inv.status_pdp === 'transmitted' ? '✅ Transmise' : inv.status_pdp === 'draft' ? '📝 Brouillon' : inv.status_pdp}</span>
                              <span className="text-xs text-gray-500">{fmtDate(inv.invoice_date)}</span>
                            </div>
                            <p className="text-sm text-gray-600 mt-0.5">{inv.customer_name}</p>
                          </div>
                          <div className="flex items-center gap-4 ml-4 shrink-0">
                            <span className="text-lg font-bold text-emerald-700">{(parseFloat(inv.total_ttc) || 0).toFixed(2)} €</span>
                            <div className="flex gap-2">
                              <button onClick={() => downloadInvoiceXml(inv.id, inv.invoice_number)}
                                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold rounded-lg transition-colors">
                                📥 XML
                              </button>
                              {inv.status_pdp === 'draft' && (
                                <button onClick={() => transmitInvoice(inv.id, inv.invoice_number)}
                                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors">
                                  📤 Transmettre
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {pending.length === 0 && invoices.length === 0 && (
                  <div className="text-center py-16 bg-gray-50 rounded-2xl">
                    <FileText className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 font-medium">Aucune facture pour le moment</p>
                    <p className="text-sm text-gray-400 mt-1">Les chantiers facturés apparaîtront ici</p>
                  </div>
                )}
              </div>
            );
          })()
        )}

        {activeSubTab === 'emit' && !showCreateForm && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">📤 Factures émises</h2>
                <p className="text-gray-600 mt-1">Gérez vos factures électroniques conformes Factur-X</p>
              </div>
              <Button
                onClick={() => setShowCreateForm(true)}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle Facture
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="h-12 w-12 animate-spin mx-auto text-indigo-600" />
                <p className="text-gray-500 mt-4">Chargement des factures...</p>
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-2xl">
                <FileText className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 font-medium">Aucune facture émise</p>
                <p className="text-sm text-gray-400 mt-1">Cliquez sur "Nouvelle Facture" pour commencer</p>
              </div>
            ) : (
              <div className="space-y-3">
                {invoices.map((invoice) => (
                  <div key={invoice.id} className="p-4 border-2 border-gray-200 rounded-xl hover:border-indigo-300 transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-indigo-100 rounded-lg">
                          <FileText className="h-6 w-6 text-indigo-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{invoice.invoice_number}</div>
                          <div className="text-sm text-gray-500">{invoice.customer_name}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="font-bold text-gray-900">{invoice.total_ttc}€</div>
                          <div className="text-xs text-gray-500">{new Date(invoice.invoice_date).toLocaleDateString('fr-FR')}</div>
                        </div>
                        <Badge className={`
                          ${invoice.status_pdp === 'accepted' ? 'bg-green-100 text-green-700' : ''}
                          ${invoice.status_pdp === 'rejected' ? 'bg-red-100 text-red-700' : ''}
                          ${invoice.status_pdp === 'draft' ? 'bg-gray-100 text-gray-700' : ''}
                          ${invoice.status_pdp === 'transmitted' ? 'bg-blue-100 text-blue-700' : ''}
                        `}>
                          {invoice.status_pdp === 'accepted' && '✅ Acceptée'}
                          {invoice.status_pdp === 'rejected' && '❌ Rejetée'}
                          {invoice.status_pdp === 'draft' && '📝 Brouillon'}
                          {invoice.status_pdp === 'transmitted' && '📤 Envoyée'}
                        </Badge>
                        <button
                          onClick={() => downloadInvoiceXml(invoice.id, invoice.invoice_number)}
                          className="px-3 py-1.5 text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                          title="Télécharger XML Factur-X"
                        >
                          📥 XML
                        </button>
                        {invoice.status_pdp === 'draft' && (
                          <button
                            onClick={() => transmitInvoice(invoice.id, invoice.invoice_number)}
                            className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            title="Transmettre au PDP IOPOLE"
                          >
                            📤 Transmettre
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeSubTab === 'emit' && showCreateForm && (
          <InvoiceCreateForm onBack={() => { setShowCreateForm(false); setPrefillData(null); }} onCreated={loadInvoices} initialData={prefillData} />
        )}

        {activeSubTab === 'receive' && (
          <ReceivedInvoicesModule />
        )}

        {activeSubTab === 'reporting' && (
          <EReportingModule />
        )}

        {activeSubTab === 'archive' && (
          <ArchivesModule />
        )}
      </div>

      {/* Informations complémentaires */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <h3 className="font-bold text-gray-900">Formats supportés</h3>
          </div>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Factur-X (PDF + XML)</li>
            <li>• UBL XML (standard UE)</li>
            <li>• CII (Cross Industry Invoice)</li>
          </ul>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <Globe className="h-8 w-8 text-blue-500" />
            <h3 className="font-bold text-gray-900">PDP Compatible</h3>
          </div>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• <span className="font-semibold text-blue-600">IOPOLE</span></li>
            <li>• Yooz</li>
            <li>• Jefacture</li>
            <li>• Pennylane</li>
            <li>• Sellsy / MyUnisoft</li>
          </ul>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <Calendar className="h-8 w-8 text-orange-500" />
            <h3 className="font-bold text-gray-900">Deadlines légales</h3>
          </div>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• 📥 Réception : 01/09/2026</li>
            <li>• 📤 ETI : 01/09/2026</li>
            <li>• 📤 PME/TPE : 01/09/2027</li>
          </ul>
        </div>
      </div>

      </div> {/* fin pointer-events-none */}
    </div>
  );
};

// ============================================================================
// COMPANY SETTINGS - Paramètres Entreprise
// ============================================================================

// ============================================================================
// CGV - GESTION DES CONDITIONS GÉNÉRALES DE VENTE
// ============================================================================

const TERMS_DEFAULT_TEMPLATE = `
<h2>1. Objet et champ d'application</h2>
<p>Les présentes conditions générales de vente s'appliquent à toutes les prestations conclues entre {{company_name}} et ses clients.</p>
<h2>2. Acceptation du devis et formation du contrat</h2>
<p>Le contrat est formé à la signature du devis par le client, valant acceptation sans réserve des présentes conditions.</p>
<h2>3. Prix</h2>
<p>Les prix sont exprimés en euros, hors taxes. La TVA applicable est celle en vigueur au jour de la facturation.</p>
<h2>4. Acompte et modalités de paiement</h2>
<p>Un acompte pourra être demandé à la commande. Le solde est payable selon les modalités indiquées sur le devis.</p>
<h2>5. Retards de paiement</h2>
<p>Tout retard de paiement entraîne l'application de pénalités de retard ainsi qu'une indemnité forfaitaire pour frais de recouvrement de 40 €.</p>
<h2>6. Délais d'exécution</h2>
<p>Les délais d'exécution sont donnés à titre indicatif et ne constituent pas un engagement ferme sauf mention contraire sur le devis.</p>
<h2>7. Modification des prestations</h2>
<p>Toute modification demandée par le client fera l'objet d'un avenant ou d'un nouveau devis.</p>
<h2>8. Travaux ou prestations supplémentaires</h2>
<p>Les travaux non prévus au devis initial feront l'objet d'une facturation complémentaire après accord du client.</p>
<h2>9. Annulation</h2>
<p>En cas d'annulation par le client après acceptation du devis, les sommes déjà versées resteront acquises à l'entreprise.</p>
<h2>10. Réception des travaux et réserves</h2>
<p>La réception des travaux donne lieu à un procès-verbal signé par les deux parties, mentionnant les éventuelles réserves.</p>
<h2>11. Responsabilité et assurances</h2>
<p>{{company_name}} est titulaire des assurances professionnelles requises pour l'exercice de son activité.</p>
<h2>12. Garanties</h2>
<p>Les prestations bénéficient des garanties légales en vigueur.</p>
<h2>13. Force majeure</h2>
<p>La responsabilité de l'entreprise ne pourra être engagée en cas d'inexécution due à un cas de force majeure.</p>
<h2>14. Réclamations</h2>
<p>Toute réclamation doit être adressée par écrit à {{company_email}}.</p>
<h2>15. Médiation de la consommation</h2>
<p>Conformément aux articles L.612-1 et suivants du Code de la consommation, le client particulier peut recourir gratuitement au médiateur suivant : {{mediator_name}}, {{mediator_address}} — {{mediator_website}}.</p>
<h2>16. Droit de rétractation</h2>
<p>Lorsqu'il est applicable (contrat conclu hors établissement), le client particulier dispose d'un délai de rétractation de 14 jours.</p>
<h2>17. Droit applicable et règlement des litiges</h2>
<p>Les présentes conditions sont soumises au droit français. À défaut d'accord amiable, tout litige relèvera des tribunaux compétents.</p>
`.trim();

const TERMS_VARIABLES = [
  { key: 'company_name', label: 'Nom entreprise' },
  { key: 'legal_form', label: 'Forme juridique' },
  { key: 'company_address', label: 'Adresse' },
  { key: 'company_postal_code', label: 'Code postal' },
  { key: 'company_city', label: 'Ville' },
  { key: 'company_email', label: 'Email' },
  { key: 'company_phone', label: 'Téléphone' },
  { key: 'siren', label: 'SIREN' },
  { key: 'siret', label: 'SIRET' },
  { key: 'rcs_rm', label: 'RCS/RM' },
  { key: 'vat_number', label: 'N° TVA' },
  { key: 'mediator_name', label: 'Médiateur (nom)' },
  { key: 'mediator_address', label: 'Médiateur (adresse)' },
  { key: 'mediator_website', label: 'Médiateur (site)' },
  { key: 'mediator_email', label: 'Médiateur (e-mail)' },
];

// Variante B2B : clauses médiation consommation + rétractation retirées (réservées aux particuliers)
const TERMS_DEFAULT_TEMPLATE_B2B = TERMS_DEFAULT_TEMPLATE
  .replace(/<h2>15\. Médiation[\s\S]*?<h2>17\. /, '<h2>15. ');

const TermsStatusBadge = ({ status }) => {
  const map = {
    none: { label: 'Aucune CGV', cls: 'bg-gray-100 text-gray-600' },
    draft: { label: 'Brouillon', cls: 'bg-amber-100 text-amber-700' },
    active: { label: 'Actives', cls: 'bg-green-100 text-green-700' },
    archived: { label: 'Archivées', cls: 'bg-slate-100 text-slate-500' },
  };
  const cfg = map[status] || map.none;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

const TermsManagement = () => {
  const confirm = useConfirm();
  const storedUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const isAdmin = storedUser?.role === 'ADMIN' || storedUser?.is_fondateur === true;

  const [audience, setAudience] = useState('professional');
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [editingVersion, setEditingVersion] = useState(null); // brouillon en cours d'édition
  const [editorMode, setEditorMode] = useState('editor'); // editor | pdf_upload
  const [draftTitle, setDraftTitle] = useState('Conditions générales de vente');
  const [draftEffectiveAt, setDraftEffectiveAt] = useState('');
  // ID de la version en cours de traitement (activer/archiver/supprimer/dupliquer) — désactive
  // les boutons concernés pendant l'appel réseau pour éviter les doubles clics et donner un
  // retour visuel immédiat (évite l'impression qu'un clic "ne fait rien" ou reste affiché).
  const [actionLoading, setActionLoading] = useState(null);
  const editorRef = useRef(null);
  const editorHtmlRef = useRef('');

  // Paramètres d'utilisation (stockés dans company_settings)
  const [termsSettings, setTermsSettings] = useState(null); // null = pas encore chargé
  const [companySettingsFull, setCompanySettingsFull] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [termsRes, settingsRes] = await Promise.allSettled([
        http.get(`${API}/company-terms`, authHeaders),
        http.get(`${API}/company-settings`, authHeaders),
      ]);
      if (termsRes.status === 'fulfilled') setVersions(termsRes.value.data?.data || []);
      if (settingsRes.status === 'fulfilled' && settingsRes.value.data) {
        const s = settingsRes.value.data;
        setCompanySettingsFull(s);
        setTermsSettings({
          attach_terms_to_quotes: s.attach_terms_to_quotes !== false,
          require_terms_acceptance: s.require_terms_acceptance === true,
          invoice_terms_mode: s.invoice_terms_mode || 'reference',
        });
      } else {
        setTermsSettings({ attach_terms_to_quotes: true, require_terms_acceptance: false, invoice_terms_mode: 'reference' });
      }
    } catch (e) {
      console.error('Erreur chargement CGV:', e);
      toast.error('Erreur lors du chargement des CGV');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const audienceVersions = versions.filter(v => v.audience === audience);
  const activeVersion = audienceVersions.find(v => v.status === 'active');
  const draftVersion = audienceVersions.find(v => v.status === 'draft');
  const globalStatus = activeVersion ? 'active' : (draftVersion ? 'draft' : (audienceVersions.length > 0 ? 'archived' : 'none'));

  const fmtDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return String(d).slice(0, 10); }
  };

  // ─── Actions ───
  const createDraft = async (copyFromId = null) => {
    if (actionLoading) return; // anti double-clic
    setActionLoading(copyFromId || 'new');
    try {
      const resp = await http.post(`${API}/company-terms`, {
        audience,
        source_type: 'editor',
        title: 'Conditions générales de vente',
        copy_from_version_id: copyFromId,
        content_html: copyFromId ? null : (audience === 'professional' ? TERMS_DEFAULT_TEMPLATE_B2B : TERMS_DEFAULT_TEMPLATE),
      }, authHeaders);
      toast.success(copyFromId ? 'Nouvelle version créée à partir de la précédente' : 'Brouillon de CGV créé');
      // Ajout optimiste immédiat — pas besoin d'attendre un reload complet pour le voir apparaître
      setVersions(prev => [...prev, resp.data]);
      openEditor(resp.data);
      loadAll(); // resynchronisation en arrière-plan (silencieuse, ne bloque pas l'UI)
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur lors de la création';
      toast.error(msg);
    } finally {
      setActionLoading(null);
    }
  };

  const openEditor = async (version) => {
    try {
      // Recharger le contenu complet
      const resp = await http.get(`${API}/company-terms/${version.id}`, authHeaders);
      const full = resp.data;
      setEditingVersion(full);
      setEditorMode(full.source_type === 'pdf_upload' ? 'pdf_upload' : 'editor');
      setDraftTitle(full.title || 'Conditions générales de vente');
      setDraftEffectiveAt(full.effective_at || '');
      editorHtmlRef.current = full.content_html || '';
      // Injecter dans le contentEditable après render
      setTimeout(() => {
        if (editorRef.current) editorRef.current.innerHTML = editorHtmlRef.current || '<p></p>';
      }, 50);
    } catch (e) {
      toast.error('Impossible de charger cette version');
    }
  };

  const saveDraft = async () => {
    if (!editingVersion) return;
    if (saving) return; // Anti double-clic (EDGE-005)
    setSaving(true);
    try {
      const html = editorRef.current ? editorRef.current.innerHTML : editorHtmlRef.current;
      await http.put(`${API}/company-terms/${editingVersion.id}`, {
        title: draftTitle,
        content_html: editorMode === 'editor' ? html : undefined,
        effective_at: draftEffectiveAt || null,
        source_type: editorMode,
      }, authHeaders);
      toast.success('Brouillon enregistré');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const activateVersion = async (versionId) => {
    if (actionLoading) return;
    if (!(await confirm('Activer cette version ? L\'ancienne version active sera automatiquement archivée. Les prochains devis utiliseront cette version.'))) return;
    setActionLoading(versionId);
    try {
      // Sauvegarder le brouillon avant activation si on est en train de l'éditer
      if (editingVersion?.id === versionId && editorMode === 'editor') {
        const html = editorRef.current ? editorRef.current.innerHTML : editorHtmlRef.current;
        await http.put(`${API}/company-terms/${versionId}`, { content_html: html, title: draftTitle, effective_at: draftEffectiveAt || null }, authHeaders);
      }
      await http.post(`${API}/company-terms/${versionId}/activate`, {}, authHeaders);
      toast.success('✅ CGV activées ! Elles seront jointes aux prochains devis.');
      setEditingVersion(null);
      // Màj optimiste immédiate: la version activée passe active, l'ancienne active (autre id) passe archivée
      setVersions(prev => prev.map(v => {
        if (v.id === versionId) return { ...v, status: 'active', title: draftTitle || v.title };
        if (v.audience === audience && v.status === 'active') return { ...v, status: 'archived' };
        return v;
      }));
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'activation');
    } finally {
      setActionLoading(null);
    }
  };

  const archiveVersion = async (versionId) => {
    if (actionLoading) return;
    if (!(await confirm('Archiver cette version ? Plus aucune CGV ne sera jointe aux nouveaux devis tant qu\'une autre version n\'est pas activée.'))) return;
    setActionLoading(versionId);
    try {
      await http.post(`${API}/company-terms/${versionId}/archive`, {}, authHeaders);
      toast.success('Version archivée');
      // Màj optimiste immédiate — sans attendre le reload
      setVersions(prev => prev.map(v => v.id === versionId ? { ...v, status: 'archived' } : v));
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'archivage');
    } finally {
      setActionLoading(null);
    }
  };

  const deleteDraft = async (versionId) => {
    if (actionLoading) return;
    if (!(await confirm('Supprimer définitivement ce brouillon ?'))) return;
    setActionLoading(versionId);
    try {
      await http.delete(`${API}/company-terms/${versionId}`, authHeaders);
      toast.success('Brouillon supprimé');
      if (editingVersion?.id === versionId) setEditingVersion(null);
      // Suppression optimiste immédiate de l'état local — ne dépend pas du succès du reload
      // qui suit (évite qu'un brouillon supprimé reste affiché si le GET de resynchronisation
      // échoue ou est retardé, ex: coupure réseau ponctuelle).
      setVersions(prev => prev.filter(v => v.id !== versionId));
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setActionLoading(null);
    }
  };

  const uploadPdf = async (file) => {
    if (!editingVersion) return;
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Fichier trop volumineux (max 10 Mo)'); return; }
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Seuls les fichiers PDF sont acceptés'); return; }
    if (editingVersion.storage_path && !(await confirm('Remplacer le fichier PDF actuel de ce brouillon ?'))) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await http.post(`${API}/company-terms/${editingVersion.id}/upload-pdf`, fd, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('PDF importé avec succès');
      const resp = await http.get(`${API}/company-terms/${editingVersion.id}`, authHeaders);
      setEditingVersion(resp.data);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setUploading(false);
    }
  };

  const openPdfPreview = async (version) => {
    try {
      if (version.source_type === 'pdf_upload') {
        const resp = await http.get(`${API}/company-terms/${version.id}/file-url`, authHeaders);
        if (resp.data?.url) window.open(resp.data.url, '_blank');
      } else {
        const resp = await http.get(`${API}/company-terms/${version.id}/pdf`, {
          ...authHeaders, responseType: 'blob'
        });
        const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      }
    } catch (e) {
      toast.error('Impossible de générer l\'aperçu PDF');
    }
  };

  const openHtmlPreview = async (version) => {
    try {
      const resp = await http.get(`${API}/company-terms/${version.id}/preview`, authHeaders);
      setPreviewData(resp.data);
      setShowPreview(true);
    } catch (e) {
      toast.error('Impossible de charger l\'aperçu');
    }
  };

  const saveTermsSettings = async (patch) => {
    if (!companySettingsFull?.company_name) {
      toast.warning('Renseignez d\'abord les informations générales de l\'entreprise');
      return;
    }
    const next = { ...termsSettings, ...patch };
    setTermsSettings(next);
    setSavingSettings(true);
    try {
      await http.post(`${API}/company-settings`, {
        ...companySettingsFull,
        ...next,
      }, authHeaders);
      toast.success('Paramètres CGV enregistrés');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement des paramètres');
      setTermsSettings(termsSettings); // rollback
    } finally {
      setSavingSettings(false);
    }
  };

  // ─── Toolbar éditeur ───
  const exec = (cmd, val = null) => {
    if (editorRef.current) editorRef.current.focus();
    document.execCommand(cmd, false, val);
  };
  const insertVariable = (key) => {
    if (editorRef.current) editorRef.current.focus();
    document.execCommand('insertText', false, `{{${key}}}`);
  };
  const insertLink = () => {
    const url = window.prompt('URL du lien (https://...)');
    if (url && url.startsWith('http')) exec('createLink', url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <Card className="bg-white/90 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
        <CardHeader className="border-b border-gray-100 pb-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <CardTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="h-6 w-6 text-indigo-600" />
                Conditions générales de vente
                <TermsStatusBadge status={globalStatus} />
              </CardTitle>
              <p className="text-sm text-gray-500 mt-2">
                Créez et gérez les conditions générales jointes à vos devis et documents commerciaux.
              </p>
              {activeVersion && (
                <p className="text-xs text-gray-500 mt-1">
                  Version active : <span className="font-semibold text-gray-700">{activeVersion.title || 'Conditions générales de vente'} (v{activeVersion.version_number})</span> — en vigueur depuis le {fmtDate(activeVersion.effective_at || activeVersion.activated_at)}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {isAdmin && !draftVersion && !activeVersion && (
                <Button onClick={() => createDraft()} disabled={actionLoading === 'new'} className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl">
                  <Plus className="h-4 w-4 mr-1" /> {actionLoading === 'new' ? 'Création…' : 'Créer des CGV'}
                </Button>
              )}
              {activeVersion && (
                <Button variant="outline" className="rounded-xl" onClick={() => openPdfPreview(activeVersion)}>
                  <Eye className="h-4 w-4 mr-1" /> Aperçu PDF
                </Button>
              )}
              <Button variant="outline" className="rounded-xl" onClick={() => setShowHistory(h => !h)}>
                <History className="h-4 w-4 mr-1" /> Historique
              </Button>
            </div>
          </div>

          {/* Sous-onglets audience */}
          <div className="flex gap-2 mt-5 overflow-x-auto">
            {[
              { key: 'professional', label: 'Clients professionnels' },
              { key: 'consumer', label: 'Clients particuliers' },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => { setAudience(t.key); setEditingVersion(null); }}
                className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                  audience === t.key
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2">
            Certaines dispositions diffèrent selon le type de client (médiation, droit de rétractation…). Gérez un jeu de CGV distinct pour chaque catégorie.
          </p>
        </CardHeader>

        <CardContent className="pt-6 space-y-6">
          {/* Encadré juridique */}
          <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
            <p className="text-sm text-blue-800 flex items-start gap-2">
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
              Les CGV doivent être personnalisées et vérifiées par l'entreprise. SkyApp fournit un outil de rédaction et de gestion, mais ne remplace pas un conseil juridique.
            </p>
          </div>

          {/* Alerte médiateur incomplet — B2C uniquement */}
          {audience === 'consumer' && companySettingsFull && (
            !(companySettingsFull.mediator_name || '').trim() ||
            !(companySettingsFull.mediator_address || '').trim() ||
            !(companySettingsFull.mediator_website || '').trim()
          ) && (
            <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl text-sm text-red-800">
              <p className="flex items-start gap-2 font-semibold">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                Informations du médiateur de la consommation incomplètes
              </p>
              <p className="mt-1 ml-6">
                Les CGV destinées aux particuliers doivent mentionner le médiateur (nom, adresse, site internet).
                Champs manquants :{' '}
                {[
                  !(companySettingsFull.mediator_name || '').trim() && 'nom',
                  !(companySettingsFull.mediator_address || '').trim() && 'adresse',
                  !(companySettingsFull.mediator_website || '').trim() && 'site internet',
                ].filter(Boolean).join(', ')}.
                {' '}Renseignez-les dans <button onClick={() => { try { const url = new URL(window.location.href); url.searchParams.delete('tab'); window.history.replaceState({}, '', url.toString()); } catch {} window.location.reload(); }} className="underline font-semibold">Informations générales</button> avant d'activer ces CGV,
                sinon la clause médiation apparaîtra incomplète sur les documents.
              </p>
            </div>
          )}

          {/* Alerte pas de version active */}
          {!activeVersion && !draftVersion && (
            <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-xl text-sm text-amber-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              Aucune CGV n'est configurée pour les {audience === 'professional' ? 'clients professionnels' : 'clients particuliers'}. Les devis seront envoyés sans CGV.
            </div>
          )}

          {/* Brouillon existant non ouvert */}
          {draftVersion && !editingVersion && (
            <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-xl flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-amber-800">
                <span className="font-semibold">{draftVersion.title || 'Conditions générales de vente'} — Brouillon v{draftVersion.version_number}</span> — dernière modification le {fmtDate(draftVersion.updated_at)}
              </div>
              {isAdmin && (
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-lg bg-amber-600 hover:bg-amber-700 text-white" onClick={() => openEditor(draftVersion)} disabled={actionLoading === draftVersion.id}>
                    <Edit className="h-4 w-4 mr-1" /> Reprendre l'édition
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-lg text-red-600 border-red-200 hover:bg-red-50" title="Supprimer ce brouillon" onClick={() => deleteDraft(draftVersion.id)} disabled={actionLoading === draftVersion.id}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* ÉDITEUR DE BROUILLON */}
          {editingVersion && isAdmin && (
            <div className="border-2 border-indigo-100 rounded-2xl p-4 md:p-5 space-y-4 bg-white">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="font-semibold text-gray-900">
                  Brouillon v{editingVersion.version_number} — {audience === 'professional' ? 'Professionnels' : 'Particuliers'}
                </h3>
                <button onClick={() => setEditingVersion(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Mode de création */}
              <div className="flex gap-2">
                <button
                  onClick={() => setEditorMode('editor')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${editorMode === 'editor' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  <Edit2 className="h-3.5 w-3.5 inline mr-1" /> Éditeur de texte
                </button>
                <button
                  onClick={() => setEditorMode('pdf_upload')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium ${editorMode === 'pdf_upload' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  <Upload className="h-3.5 w-3.5 inline mr-1" /> Importer un PDF
                </button>
              </div>

              {/* Titre + date d'entrée en vigueur */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Titre du document</label>
                  <input
                    type="text"
                    value={draftTitle}
                    onChange={e => setDraftTitle(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-indigo-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Date d'entrée en vigueur</label>
                  <input
                    type="date"
                    value={draftEffectiveAt}
                    onChange={e => setDraftEffectiveAt(e.target.value)}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-indigo-400 focus:outline-none"
                  />
                </div>
              </div>

              {editorMode === 'editor' ? (
                <>
                  {/* Toolbar */}
                  <div className="flex flex-wrap gap-1 p-2 bg-gray-50 rounded-xl border border-gray-200 sticky top-0 z-10">
                    {[
                      { label: 'B', title: 'Gras', cmd: () => exec('bold'), cls: 'font-bold' },
                      { label: 'I', title: 'Italique', cmd: () => exec('italic'), cls: 'italic' },
                      { label: 'U', title: 'Souligné', cmd: () => exec('underline'), cls: 'underline' },
                      { label: 'H2', title: 'Titre', cmd: () => exec('formatBlock', 'h2'), cls: 'font-bold text-xs' },
                      { label: 'H3', title: 'Sous-titre', cmd: () => exec('formatBlock', 'h3'), cls: 'font-bold text-xs' },
                      { label: '¶', title: 'Paragraphe', cmd: () => exec('formatBlock', 'p'), cls: '' },
                      { label: '• Liste', title: 'Liste à puces', cmd: () => exec('insertUnorderedList'), cls: 'text-xs' },
                      { label: '1. Liste', title: 'Liste numérotée', cmd: () => exec('insertOrderedList'), cls: 'text-xs' },
                      { label: '🔗', title: 'Lien', cmd: insertLink, cls: '' },
                      { label: '―', title: 'Séparateur', cmd: () => exec('insertHorizontalRule'), cls: '' },
                      { label: '↶', title: 'Annuler', cmd: () => exec('undo'), cls: '' },
                      { label: '↷', title: 'Rétablir', cmd: () => exec('redo'), cls: '' },
                    ].map((b, i) => (
                      <button
                        key={i}
                        type="button"
                        title={b.title}
                        onMouseDown={e => e.preventDefault()}
                        onClick={b.cmd}
                        className={`px-2.5 py-1.5 rounded-lg hover:bg-white hover:shadow-sm text-gray-700 text-sm transition-all ${b.cls}`}
                      >
                        {b.label}
                      </button>
                    ))}
                  </div>

                  {/* Variables */}
                  <div className="flex flex-wrap gap-1.5">
                    <span className="text-xs text-gray-500 self-center mr-1">Variables :</span>
                    {TERMS_VARIABLES.map(v => (
                      <button
                        key={v.key}
                        type="button"
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => insertVariable(v.key)}
                        className="px-2 py-1 rounded-md bg-indigo-50 text-indigo-600 text-xs font-mono hover:bg-indigo-100 transition-colors"
                        title={`Insérer {{${v.key}}}`}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>

                  {/* Zone d'édition */}
                  <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={() => { editorHtmlRef.current = editorRef.current?.innerHTML || ''; }}
                    className="terms-editor min-h-[400px] max-h-[600px] overflow-y-auto p-5 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none text-sm leading-relaxed bg-white"
                    style={{ wordBreak: 'break-word' }}
                  />
                  <style>{`
                    .terms-editor h2 { font-size: 1.05rem; font-weight: 700; margin: 0.9em 0 0.35em; color: #111827; }
                    .terms-editor h3 { font-size: 0.95rem; font-weight: 600; margin: 0.7em 0 0.3em; color: #1f2937; }
                    .terms-editor p { margin: 0.35em 0; color: #374151; }
                    .terms-editor ul, .terms-editor ol { padding-left: 1.4em; margin: 0.4em 0; }
                    .terms-editor ul { list-style: disc; } .terms-editor ol { list-style: decimal; }
                    .terms-editor a { color: #4f46e5; text-decoration: underline; }
                    .terms-editor hr { border: none; border-top: 1px solid #e5e7eb; margin: 1em 0; }
                  `}</style>
                </>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center space-y-3">
                  {editingVersion.storage_path ? (
                    <div className="space-y-2">
                      <FileText className="h-10 w-10 text-indigo-500 mx-auto" />
                      <p className="text-sm font-medium text-gray-700">{editingVersion.file_name}</p>
                      <p className="text-xs text-gray-500">{editingVersion.file_size ? `${(editingVersion.file_size / 1024 / 1024).toFixed(2)} Mo` : ''}</p>
                      <div className="flex justify-center gap-2">
                        <Button size="sm" variant="outline" className="rounded-lg" onClick={() => openPdfPreview(editingVersion)}>
                          <Eye className="h-4 w-4 mr-1" /> Consulter
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 text-gray-300 mx-auto" />
                      <p className="text-sm text-gray-500">Importez vos CGV au format PDF (max 10 Mo)</p>
                    </>
                  )}
                  <label className="cursor-pointer inline-block">
                    <input type="file" accept="application/pdf" className="hidden"
                      onChange={e => { uploadPdf(e.target.files?.[0]); e.target.value = ''; }} />
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white ${uploading ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'} transition-colors`}>
                      {uploading ? 'Import en cours…' : (editingVersion.storage_path ? 'Remplacer le fichier' : 'Choisir un fichier PDF')}
                    </span>
                  </label>
                </div>
              )}

              {/* Actions brouillon */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                <Button onClick={saveDraft} disabled={saving || !!actionLoading} className="rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white">
                  {saving ? 'Enregistrement…' : (<><Save className="h-4 w-4 mr-1" /> Enregistrer le brouillon</>)}
                </Button>
                <Button variant="outline" className="rounded-xl" onClick={() => openHtmlPreview(editingVersion)} disabled={editorMode === 'pdf_upload'}>
                  <Eye className="h-4 w-4 mr-1" /> Aperçu
                </Button>
                <Button
                  onClick={() => activateVersion(editingVersion.id)}
                  disabled={saving || actionLoading === editingVersion.id}
                  className="rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                >
                  <CheckCircle className="h-4 w-4 mr-1" /> {actionLoading === editingVersion.id ? 'Activation…' : 'Activer cette version'}
                </Button>
                <Button
                  variant="outline"
                  className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => deleteDraft(editingVersion.id)}
                  disabled={saving || actionLoading === editingVersion.id}
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Supprimer
                </Button>
              </div>
            </div>
          )}

          {/* VERSION ACTIVE (résumé) */}
          {activeVersion && !editingVersion && (
            <div className="p-4 bg-green-50 border-2 border-green-200 rounded-xl flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-green-800">
                <span className="font-semibold">{activeVersion.title || 'Conditions générales de vente'}</span>
                {' '}<span className="text-green-700">(v{activeVersion.version_number} active)</span>
                {' '}— {activeVersion.source_type === 'pdf_upload' ? `PDF importé (${activeVersion.file_name || 'fichier'})` : 'rédigée dans l\'éditeur'}
                {' '}— en vigueur depuis le {fmtDate(activeVersion.effective_at || activeVersion.activated_at)}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="rounded-lg" onClick={() => openPdfPreview(activeVersion)}>
                  <Eye className="h-4 w-4 mr-1" /> Consulter
                </Button>
                {isAdmin && !draftVersion && (
                  <Button size="sm" variant="outline" className="rounded-lg text-indigo-600 border-indigo-200 hover:bg-indigo-50" onClick={() => createDraft(activeVersion.id)} disabled={actionLoading === activeVersion.id} title="Crée une nouvelle version modifiable à partir de celle-ci">
                    <Edit className="h-4 w-4 mr-1" /> {actionLoading === activeVersion.id ? 'Création…' : 'Modifier'}
                  </Button>
                )}
                {isAdmin && (
                  <Button size="sm" variant="outline" className="rounded-lg text-red-600 border-red-200 hover:bg-red-50" onClick={() => archiveVersion(activeVersion.id)} disabled={actionLoading === activeVersion.id}>
                    <Archive className="h-4 w-4 mr-1" /> {actionLoading === activeVersion.id ? 'Archivage…' : 'Archiver'}
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* HISTORIQUE */}
          {showHistory && (
            <div className="border-2 border-gray-100 rounded-2xl overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 font-semibold text-sm text-gray-700">Historique des versions — {audience === 'professional' ? 'Professionnels' : 'Particuliers'}</div>
              {audienceVersions.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-400">Aucune version pour cette audience</div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {audienceVersions.map(v => (
                    <div key={v.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-gray-900">{v.title || 'Conditions générales de vente'} (v{v.version_number})</span>
                        <TermsStatusBadge status={v.status} />
                        <span className="text-gray-500 text-xs">
                          Créée le {fmtDate(v.created_at)}
                          {v.activated_at ? ` · activée le ${fmtDate(v.activated_at)}` : ''}
                          {` · modifiée le ${fmtDate(v.updated_at)}`}
                        </span>
                      </div>
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" className="rounded-lg h-8 px-2" onClick={() => openPdfPreview(v)} title="Consulter">
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        {isAdmin && v.status === 'draft' && (
                          <>
                            <Button size="sm" variant="outline" className="rounded-lg h-8 px-2 text-amber-600 border-amber-200 hover:bg-amber-50" title="Modifier ce brouillon"
                              onClick={() => openEditor(v)} disabled={actionLoading === v.id}>
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="outline" className="rounded-lg h-8 px-2 text-red-600 border-red-200 hover:bg-red-50" title="Supprimer ce brouillon"
                              onClick={() => deleteDraft(v.id)} disabled={actionLoading === v.id}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </>
                        )}
                        {isAdmin && v.status === 'active' && !draftVersion && (
                          <Button size="sm" variant="outline" className="rounded-lg h-8 px-2 text-indigo-600 border-indigo-200 hover:bg-indigo-50" title="Crée une nouvelle version modifiable à partir de celle-ci"
                            onClick={() => createDraft(v.id)} disabled={actionLoading === v.id}>
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {isAdmin && v.status === 'archived' && !draftVersion && (
                          <Button size="sm" variant="outline" className="rounded-lg h-8 px-2" title="Dupliquer en brouillon"
                            onClick={async () => { if (await confirm('Dupliquer cette version archivée dans un nouveau brouillon ?')) createDraft(v.id); }} disabled={actionLoading === v.id}>
                            <Plus className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* PARAMÈTRES D'UTILISATION */}
          {termsSettings && (
            <div className="border-2 border-gray-100 rounded-2xl p-4 md:p-5 space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Settings className="h-4 w-4 text-indigo-600" /> Paramètres d'utilisation
              </h3>

              <label className={`flex items-center justify-between gap-4 ${!isAdmin ? 'opacity-60 pointer-events-none' : 'cursor-pointer'}`}>
                <div>
                  <p className="text-sm font-medium text-gray-800">Joindre automatiquement les CGV aux devis</p>
                  <p className="text-xs text-gray-500">La version active correspondant au type du client sera annexée au PDF du devis.</p>
                </div>
                <input
                  type="checkbox"
                  checked={termsSettings.attach_terms_to_quotes}
                  disabled={savingSettings}
                  onChange={e => saveTermsSettings({ attach_terms_to_quotes: e.target.checked })}
                  className="w-5 h-5 accent-indigo-600 flex-shrink-0"
                />
              </label>

              <label className={`flex items-center justify-between gap-4 ${!isAdmin ? 'opacity-60 pointer-events-none' : 'cursor-pointer'}`}>
                <div>
                  <p className="text-sm font-medium text-gray-800">Rendre l'acceptation des CGV obligatoire avant la signature</p>
                  <p className="text-xs text-gray-500">Le devis ne pourra pas être marqué accepté sans acceptation explicite des CGV.</p>
                </div>
                <input
                  type="checkbox"
                  checked={termsSettings.require_terms_acceptance}
                  disabled={savingSettings}
                  onChange={e => saveTermsSettings({ require_terms_acceptance: e.target.checked })}
                  className="w-5 h-5 accent-indigo-600 flex-shrink-0"
                />
              </label>

              <div className={!isAdmin ? 'opacity-60 pointer-events-none' : ''}>
                <p className="text-sm font-medium text-gray-800 mb-1.5">Factures</p>
                <select
                  value={termsSettings.invoice_terms_mode}
                  disabled={savingSettings}
                  onChange={e => saveTermsSettings({ invoice_terms_mode: e.target.value })}
                  className="w-full md:w-auto px-3 py-2 border-2 border-gray-200 rounded-lg text-sm bg-white focus:border-indigo-400 focus:outline-none"
                >
                  <option value="none">Ne rien afficher</option>
                  <option value="reference">Afficher uniquement une référence aux CGV (recommandé)</option>
                  <option value="full">Joindre les CGV complètes à la facture</option>
                </select>
                <p className="text-xs text-gray-400 mt-1.5">
                  La référence prend la forme : « Document soumis aux CGV version X du JJ/MM/AAAA, communiquées avec le devis associé. »
                  La présentation des CGV sur la facture ne remplace jamais leur communication avec le devis.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* MODAL APERÇU HTML */}
      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">{previewData.title}</h3>
                <p className="text-xs text-gray-500">Version {previewData.version_number} — variables résolues</p>
              </div>
              <button onClick={() => setShowPreview(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            {previewData.missing_variables?.length > 0 && (
              <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-100 text-xs text-amber-800 flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
                Variables non renseignées (vides dans le document) : {previewData.missing_variables.map(v => `{{${v}}}`).join(', ')} — complétez les informations de l'entreprise.
              </div>
            )}
            <div className="p-6 overflow-y-auto terms-editor text-sm" dangerouslySetInnerHTML={{ __html: previewData.content_html }} />
          </div>
        </div>
      )}
    </div>
  );
};

const CompanySettings = ({ onBack }) => {
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Onglet actif — persisté dans l'URL (?tab=cgv) pour partage/rechargement
  const [activeSettingsTab, setActiveSettingsTab] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const t = params.get('tab');
      return (t === 'cgv' || t === 'pdf') ? t : 'general';
    } catch { return 'general'; }
  });
  const switchSettingsTab = (tab) => {
    setActiveSettingsTab(tab);
    try {
      const url = new URL(window.location.href);
      if (tab === 'cgv' || tab === 'pdf') url.searchParams.set('tab', tab);
      else url.searchParams.delete('tab');
      window.history.replaceState({}, '', url.toString());
    } catch { /* noop */ }
  };
  const [companyInfo, setCompanyInfo] = useState({
    company_name: '',
    legal_form: '',
    address: '',
    postal_code: '',
    city: '',
    siret: '',
    siren: '',
    rcs_rm: '',
    phone: '',
    email: '',
    logo_url: '',
    payment_delay_days: '',
    payment_conditions: '',
    deposit_percent: '',
    quote_validity_days: '',
    mediator_name: '',
    mediator_address: '',
    mediator_website: '',
    mediator_email: '',
    night_surcharge_enabled: false,
    night_surcharge_type: 'percent',
    night_surcharge_value: '25',
    night_surcharge_label: 'Majoration nuit',
    standard_day_hours: '7'
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  // ── Thème PDF (état local séparé, sauvegarde dédiée) ──
  const DEFAULT_PDF_THEME = {
    primary_color: '#075A9B',
    secondary_color: '#003D73',
    border_color: '#003D73',
    text_color: '#111827',
    pdf_watermark_enabled: false,
    pdf_watermark_url: '',
    pdf_watermark_opacity: 0.06,
    pdf_footer_left_icon_url: '',
    pdf_footer_right_icon_url: '',
    pdf_footer_text: '',
    pdf_show_company_info: true,
    pdf_header_banner_url: '',
    pdf_header_style: 'solid',
    pdf_logo_size: 'medium',
    pdf_logo_position: 'right',
  };
  const [pdfTheme, setPdfTheme] = useState(DEFAULT_PDF_THEME);
  const [pdfAssetFiles, setPdfAssetFiles] = useState({ footer_left: null, footer_right: null, watermark: null, header_banner: null });
  const [savingPdfTheme, setSavingPdfTheme] = useState(false);
  const [realPreviewUrl, setRealPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Thèmes prédéfinis (1 clic)
  const PDF_PRESETS = [
    { name: 'SkyApp Bleu', primary_color: '#075A9B', secondary_color: '#003D73', border_color: '#003D73', text_color: '#111827', pdf_header_style: 'solid' },
    { name: 'Ardoise', primary_color: '#334155', secondary_color: '#0F172A', border_color: '#334155', text_color: '#0F172A', pdf_header_style: 'underline' },
    { name: 'Vert BTP', primary_color: '#15803D', secondary_color: '#166534', border_color: '#166534', text_color: '#111827', pdf_header_style: 'solid' },
    { name: 'Indigo', primary_color: '#6366F1', secondary_color: '#4338CA', border_color: '#4338CA', text_color: '#111827', pdf_header_style: 'gradient' },
    { name: 'Orange chantier', primary_color: '#EA580C', secondary_color: '#9A3412', border_color: '#9A3412', text_color: '#111827', pdf_header_style: 'rounded' },
  ];
  const legalForms = [
    'Auto-entrepreneur / Micro-entreprise',
    'SASU',
    'SARL',
    'SAS',
    'EURL',
    'SNC',
    'Artisan',
    'Association',
    'Autre'
  ];

  useEffect(() => {
    loadCompanyInfo();
  }, []);

  const loadCompanyInfo = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await http.get(`${API}/company-settings`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data) {
        // S'assurer que tous les champs ont des valeurs (pas undefined)
        const data = {
          company_name: response.data.company_name || '',
          legal_form: response.data.legal_form || '',
          address: response.data.address || '',
          postal_code: response.data.postal_code || '',
          city: response.data.city || '',
          siret: response.data.siret || '',
          siren: response.data.siren || '',
          rcs_rm: response.data.rcs_rm || '',
          phone: response.data.phone || '',
          email: response.data.email || '',
          logo_url: response.data.logo_url || '',
          payment_delay_days: response.data.payment_delay_days ?? '',
          payment_conditions: response.data.payment_conditions || '',
          deposit_percent: response.data.deposit_percent ?? '',
          quote_validity_days: response.data.quote_validity_days ?? '',
          mediator_name: response.data.mediator_name || '',
          mediator_address: response.data.mediator_address || '',
          mediator_website: response.data.mediator_website || '',
          mediator_email: response.data.mediator_email || '',
          night_surcharge_enabled: response.data.night_surcharge_enabled ?? false,
          night_surcharge_type: response.data.night_surcharge_type || 'percent',
          night_surcharge_value: response.data.night_surcharge_value ?? 25,
          night_surcharge_label: response.data.night_surcharge_label || 'Majoration nuit',
          standard_day_hours: response.data.standard_day_hours ?? 7
        };
        setCompanyInfo(data);
        
        if (response.data.logo_url) {
          // Si l'URL du logo est relative, ajouter l'URL du backend
          const logoUrl = response.data.logo_url.startsWith('http') 
            ? response.data.logo_url 
            : `${API.replace('/api', '')}${response.data.logo_url}`;
          setLogoPreview(logoUrl);
        }

        // Charger le thème PDF (fusion avec les défauts pour les champs absents)
        const d = response.data;
        setPdfTheme({
          primary_color: d.primary_color || DEFAULT_PDF_THEME.primary_color,
          secondary_color: d.secondary_color || DEFAULT_PDF_THEME.secondary_color,
          border_color: d.border_color || DEFAULT_PDF_THEME.border_color,
          text_color: d.text_color || DEFAULT_PDF_THEME.text_color,
          pdf_watermark_enabled: d.pdf_watermark_enabled ?? DEFAULT_PDF_THEME.pdf_watermark_enabled,
          pdf_watermark_url: d.pdf_watermark_url || '',
          pdf_watermark_opacity: d.pdf_watermark_opacity ?? DEFAULT_PDF_THEME.pdf_watermark_opacity,
          pdf_footer_left_icon_url: d.pdf_footer_left_icon_url || '',
          pdf_footer_right_icon_url: d.pdf_footer_right_icon_url || '',
          pdf_footer_text: d.pdf_footer_text || '',
          pdf_show_company_info: d.pdf_show_company_info ?? DEFAULT_PDF_THEME.pdf_show_company_info,
          pdf_header_banner_url: d.pdf_header_banner_url || '',
          pdf_header_style: d.pdf_header_style || DEFAULT_PDF_THEME.pdf_header_style,
          pdf_logo_size: d.pdf_logo_size || DEFAULT_PDF_THEME.pdf_logo_size,
          pdf_logo_position: d.pdf_logo_position || DEFAULT_PDF_THEME.pdf_logo_position,
        });
      }
    } catch (error) {
      console.log('Aucune info entreprise enregistrée');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    // Validation SIRET : exactement 14 chiffres
    const siretRaw = String(companyInfo.siret || '').trim();
    if (!/^\d{14}$/.test(siretRaw)) {
      toast.error('Le SIRET doit contenir exactement 14 chiffres.');
      return;
    }
    // Validation durée standard d'une journée
    const dayHoursRaw = String(companyInfo.standard_day_hours ?? '').trim();
    const parsedDayHours = parseFloat(dayHoursRaw);
    if (dayHoursRaw === '' || isNaN(parsedDayHours) || parsedDayHours <= 0 || parsedDayHours > 24) {
      toast.error('La durée standard d\'une journée doit être un nombre entre 0 et 24.');
      return;
    }
    // Validation majoration nuit (si activée)
    const nightSurchargeValueRaw = String(companyInfo.night_surcharge_value ?? '').trim();
    let nightSurchargeValue = 0;
    if (companyInfo.night_surcharge_enabled) {
      const parsedNight = parseFloat(nightSurchargeValueRaw);
      if (nightSurchargeValueRaw === '' || isNaN(parsedNight) || parsedNight < 0) {
        toast.error('La valeur de la majoration nuit doit être un nombre positif.');
        return;
      }
      nightSurchargeValue = parsedNight;
    } else {
      nightSurchargeValue = parseFloat(nightSurchargeValueRaw) || 0;
    }
    // Validation délai de paiement : entier positif ou nul
    const delayRaw = String(companyInfo.payment_delay_days ?? '').trim();
    let delayValue = null;
    if (delayRaw !== '') {
      const parsed = parseInt(delayRaw, 10);
      if (isNaN(parsed) || parsed < 0 || String(parsed) !== delayRaw) {
        toast.error('Le délai de paiement doit être un nombre entier positif ou nul (ex: 0, 30, 45).');
        return;
      }
      delayValue = parsed;
    }
    // Validation email médiateur (si renseigné)
    const medEmail = (companyInfo.mediator_email || '').trim();
    if (medEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(medEmail)) {
      toast.error("L'adresse e-mail du médiateur n'est pas valide.");
      return;
    }
    // Validation acompte par défaut : nombre entre 0 et 100 ou vide
    const depositRaw = String(companyInfo.deposit_percent ?? '').trim();
    let depositValue = null;
    if (depositRaw !== '') {
      const parsedDeposit = parseFloat(depositRaw);
      if (isNaN(parsedDeposit) || parsedDeposit < 0 || parsedDeposit > 100) {
        toast.error('L\'acompte par défaut doit être un nombre entre 0 et 100.');
        return;
      }
      depositValue = parsedDeposit;
    }
    // Validation durée de validité du devis : entier positif
    const validityRaw = String(companyInfo.quote_validity_days ?? '').trim();
    let validityValue = null;
    if (validityRaw !== '') {
      const parsedValidity = parseInt(validityRaw, 10);
      if (isNaN(parsedValidity) || parsedValidity <= 0 || String(parsedValidity) !== validityRaw) {
        toast.error('La durée de validité du devis doit être un nombre entier positif (ex: 30).');
        return;
      }
      validityValue = parsedValidity;
    }
    if (saving) return; // Anti double-clic (EDGE-005)
    setSaving(true);
    try {
      // Si un logo est uploadé, l'envoyer d'abord
      let logoUrl = companyInfo.logo_url;
      if (logoFile) {
        const formData = new FormData();
        formData.append('logo', logoFile);
        
        const uploadResponse = await http.post(`${API}/company-settings/logo`, formData, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        logoUrl = uploadResponse.data.logo_url;
      }

      // Enregistrer les informations
      // Champs texte : '' permet d'effacer (le backend ignore null)
      await http.post(`${API}/company-settings`, {
        ...companyInfo,
        payment_delay_days: delayValue,
        payment_conditions: (companyInfo.payment_conditions || '').trim(),
        deposit_percent: depositValue,
        quote_validity_days: validityValue,
        mediator_name: (companyInfo.mediator_name || '').trim(),
        mediator_address: (companyInfo.mediator_address || '').trim(),
        mediator_website: (companyInfo.mediator_website || '').trim(),
        mediator_email: medEmail,
        logo_url: logoUrl,
        night_surcharge_enabled: !!companyInfo.night_surcharge_enabled,
        night_surcharge_type: companyInfo.night_surcharge_type === 'fixed' ? 'fixed' : 'percent',
        night_surcharge_value: nightSurchargeValue,
        night_surcharge_label: (companyInfo.night_surcharge_label || 'Majoration nuit').trim(),
        standard_day_hours: parsedDayHours
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success('Informations enregistrées avec succès !');
      
      // Réinitialiser le fichier logo et recharger
      setLogoFile(null);
      await loadCompanyInfo();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      if (error.response?.status === 403) {
        toast.error('🔒 Seul l\'administrateur peut modifier les paramètres de l\'entreprise.');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } finally {
      setSaving(false);
    }
  };

  const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

  const handleSavePdfTheme = async () => {
    // Validation des couleurs
    for (const key of ['primary_color', 'secondary_color', 'border_color', 'text_color']) {
      if (!HEX_RE.test(String(pdfTheme[key] || ''))) {
        alert(`❌ Couleur invalide (${key}). Format attendu : #RRGGBB.`);
        return;
      }
    }
    setSavingPdfTheme(true);
    try {
      // Uploader les assets modifiés (réutilise le bucket company-logos côté backend)
      const uploaded = {};
      for (const kind of ['footer_left', 'footer_right', 'watermark', 'header_banner']) {
        const file = pdfAssetFiles[kind];
        if (!file) continue;
        const fd = new FormData();
        fd.append('kind', kind);
        fd.append('file', file);
        const res = await http.post(`${API}/company-settings/pdf-asset`, fd, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        uploaded[kind] = res.data.url;
      }
      const payload = {
        ...pdfTheme,
        pdf_footer_left_icon_url: uploaded.footer_left ?? pdfTheme.pdf_footer_left_icon_url,
        pdf_footer_right_icon_url: uploaded.footer_right ?? pdfTheme.pdf_footer_right_icon_url,
        pdf_watermark_url: uploaded.watermark ?? pdfTheme.pdf_watermark_url,
        pdf_header_banner_url: uploaded.header_banner ?? pdfTheme.pdf_header_banner_url,
      };
      // Le POST /company-settings exige les champs identité obligatoires : on les renvoie tels quels
      await http.post(`${API}/company-settings`, {
        ...companyInfo,
        ...payload,
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      alert('✅ Thème des documents enregistré !');
      setPdfAssetFiles({ footer_left: null, footer_right: null, watermark: null });
      await loadCompanyInfo();
    } catch (error) {
      console.error('Erreur sauvegarde thème PDF:', error);
      if (error.response?.status === 403) {
        alert('🔒 Seul l\'administrateur peut modifier le thème des documents.');
      } else {
        alert('❌ Erreur lors de l\'enregistrement du thème.');
      }
    } finally {
      setSavingPdfTheme(false);
    }
  };

  const handleResetPdfTheme = async () => {
    if (!(await confirm('Restaurer le thème SkyApp par défaut ? Les couleurs, le filigrane et les icônes de pied de page seront réinitialisés (les informations légales de l\'entreprise ne sont pas affectées).'))) {
      return;
    }
    setPdfTheme({ ...DEFAULT_PDF_THEME });
    setPdfAssetFiles({ footer_left: null, footer_right: null, watermark: null, header_banner: null });
  };

  const handlePdfAssetChange = (kind, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('❌ Fichier trop volumineux (max 2 Mo).');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      setPdfAssetFiles(prev => ({ ...prev, [kind]: file }));
      // Aperçu immédiat via data URL
      const urlKey = kind === 'watermark' ? 'pdf_watermark_url'
        : kind === 'footer_left' ? 'pdf_footer_left_icon_url'
        : kind === 'header_banner' ? 'pdf_header_banner_url'
        : 'pdf_footer_right_icon_url';
      setPdfTheme(prev => ({ ...prev, [urlKey]: dataUrl }));
    };
    reader.readAsDataURL(file);
  };

  const handleGenerateRealPreview = async () => {
    setPreviewLoading(true);
    try {
      // Les images non encore sauvegardées (data URL) ne sont pas résolvables côté serveur : on les retire
      const overrides = {};
      Object.entries(pdfTheme).forEach(([k, v]) => {
        if (typeof v === 'string' && v.startsWith('data:')) return;
        overrides[k] = v;
      });
      const res = await http.post(`${API}/company-settings/pdf-preview`, overrides, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setRealPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (e) {
      alert("Impossible de générer l'aperçu PDF réel.");
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 p-6">
      {/* Header */}
      <div className="mb-8 flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 hover:bg-white/50 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-6 w-6 text-gray-700" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Settings className="h-8 w-8 text-indigo-600" />
            Paramètres Entreprise
          </h1>
          <p className="text-gray-600 mt-1">Informations légales pour vos documents</p>
        </div>
      </div>

      {/* Navigation par onglets */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {[
            { key: 'general', label: 'Informations générales', icon: Building },
            { key: 'pdf', label: 'Documents PDF', icon: FileText },
            { key: 'cgv', label: 'Conditions générales de vente', icon: FileText },
          ].map(t => {
            const TabIcon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => switchSettingsTab(t.key)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                  activeSettingsTab === t.key
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                    : 'bg-white/80 text-gray-600 hover:bg-white shadow-sm'
                }`}
              >
                <TabIcon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Onglet CGV */}
      {activeSettingsTab === 'cgv' && (
        <div className="max-w-4xl mx-auto">
          <TermsManagement />
        </div>
      )}

      {/* Onglet Documents PDF (personnalisation du thème graphique) */}
      {activeSettingsTab === 'pdf' && (
        <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Colonne réglages */}
          <Card className="bg-white/90 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
            <CardHeader className="border-b border-gray-100 pb-6">
              <CardTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="h-6 w-6 text-indigo-600" />
                Personnalisation des documents
              </CardTitle>
              <p className="text-sm text-gray-500 mt-2">
                Couleurs, filigrane et pied de page appliqués à vos PDF (rapports, devis, comptes rendus…)
              </p>
            </CardHeader>

            <CardContent className="pt-6 space-y-6">
              {/* Thèmes prédéfinis */}
              <div className="space-y-2">
                <h3 className="text-sm font-bold text-gray-800">✨ Thèmes prédéfinis</h3>
                <div className="flex flex-wrap gap-2">
                  {PDF_PRESETS.map((preset) => (
                    <button
                      key={preset.name}
                      type="button"
                      onClick={() => setPdfTheme(prev => ({ ...prev, ...preset }))}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-full border-2 border-gray-200 hover:border-indigo-400 transition text-sm"
                      title={`Appliquer le thème ${preset.name}`}
                    >
                      <span className="flex -space-x-1">
                        <span className="h-4 w-4 rounded-full border border-white" style={{ background: preset.primary_color }} />
                        <span className="h-4 w-4 rounded-full border border-white" style={{ background: preset.secondary_color }} />
                      </span>
                      {preset.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Couleurs */}
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-gray-800">🎨 Couleurs</h3>
                {[
                  { key: 'primary_color', label: 'Couleur principale' },
                  { key: 'secondary_color', label: 'Couleur secondaire' },
                  { key: 'border_color', label: 'Couleur des bordures' },
                  { key: 'text_color', label: 'Couleur du texte' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-3">
                    <label className="w-40 text-sm font-medium text-gray-700">{label}</label>
                    <input
                      type="color"
                      value={/^#[0-9a-fA-F]{6}$/.test(pdfTheme[key]) ? pdfTheme[key] : '#000000'}
                      onChange={(e) => setPdfTheme(prev => ({ ...prev, [key]: e.target.value }))}
                      className="h-10 w-14 rounded border border-gray-200 cursor-pointer bg-white"
                      aria-label={label}
                    />
                    <input
                      type="text"
                      value={pdfTheme[key]}
                      onChange={(e) => setPdfTheme(prev => ({ ...prev, [key]: e.target.value }))}
                      className="w-32 px-3 py-2 border-2 border-gray-200 rounded-lg font-mono text-sm focus:border-indigo-400 focus:outline-none uppercase"
                      placeholder="#075A9B"
                      maxLength={7}
                    />
                  </div>
                ))}
              </div>

              {/* Bandeau d'en-tête */}
              <div className="p-4 bg-gray-50 rounded-xl space-y-4">
                <h3 className="text-sm font-bold text-gray-800">🎯 Bandeau d'en-tête</h3>
                <div className="flex items-center gap-3">
                  {pdfTheme.pdf_header_banner_url ? (
                    <img src={pdfTheme.pdf_header_banner_url} alt="Bandeau" className="h-12 w-32 object-contain border rounded bg-white" />
                  ) : (
                    <div className="h-12 w-32 border-2 border-dashed border-gray-300 rounded flex items-center justify-center bg-white">
                      <ImageIcon className="h-6 w-6 text-gray-300" />
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <label className="cursor-pointer text-sm text-indigo-600 hover:underline">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePdfAssetChange('header_banner', e)} />
                      {pdfTheme.pdf_header_banner_url ? 'Modifier le bandeau' : 'Ajouter mon bandeau'}
                    </label>
                    {pdfTheme.pdf_header_banner_url && (
                      <button
                        type="button"
                        onClick={() => { setPdfTheme(prev => ({ ...prev, pdf_header_banner_url: '' })); setPdfAssetFiles(prev => ({ ...prev, header_banner: null })); }}
                        className="text-sm text-red-500 hover:underline text-left"
                      >
                        Supprimer
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Image pleine largeur (PNG transparent conseillé, max 2 Mo). Si renseignée, elle remplace le bandeau coloré.
                </p>
                {!pdfTheme.pdf_header_banner_url && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Forme du bandeau (sans image)</label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { key: 'solid', label: 'Uni' },
                          { key: 'gradient', label: 'Dégradé' },
                          { key: 'rounded', label: 'Coins arrondis' },
                          { key: 'underline', label: 'Souligné' },
                        ].map(({ key, label }) => {
                          const active = pdfTheme.pdf_header_style === key;
                          const bg = key === 'gradient'
                            ? `linear-gradient(90deg, ${pdfTheme.primary_color}, ${pdfTheme.secondary_color})`
                            : key === 'underline' ? '#ffffff' : pdfTheme.primary_color;
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => setPdfTheme(prev => ({ ...prev, pdf_header_style: key }))}
                              className={`rounded-lg border-2 p-2 text-left transition ${active ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-gray-200'}`}
                            >
                              <div
                                className="h-6 w-full mb-1"
                                style={{
                                  background: bg,
                                  borderRadius: key === 'rounded' ? 8 : 2,
                                  borderBottom: key === 'underline' ? `3px solid ${pdfTheme.secondary_color}` : 'none',
                                }}
                              />
                              <span className="text-xs text-gray-700">{label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Taille du logo</label>
                        <select
                          value={pdfTheme.pdf_logo_size}
                          onChange={(e) => setPdfTheme(prev => ({ ...prev, pdf_logo_size: e.target.value }))}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm bg-white focus:border-indigo-400 focus:outline-none"
                        >
                          <option value="small">Petit</option>
                          <option value="medium">Moyen</option>
                          <option value="large">Grand</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Emplacement du logo</label>
                        <select
                          value={pdfTheme.pdf_logo_position}
                          onChange={(e) => setPdfTheme(prev => ({ ...prev, pdf_logo_position: e.target.value }))}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm bg-white focus:border-indigo-400 focus:outline-none"
                        >
                          <option value="left">Gauche</option>
                          <option value="center">Centre</option>
                          <option value="right">Droite</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Filigrane */}
              <div className="p-4 bg-gray-50 rounded-xl space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold text-gray-800">💧 Filigrane</h3>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!pdfTheme.pdf_watermark_enabled}
                      onChange={(e) => setPdfTheme(prev => ({ ...prev, pdf_watermark_enabled: e.target.checked }))}
                      className="h-4 w-4"
                    />
                    <span className="text-sm text-gray-700">Activer</span>
                  </label>
                </div>
                {pdfTheme.pdf_watermark_enabled && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Opacité : {Math.round((pdfTheme.pdf_watermark_opacity || 0) * 100)}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="0.2"
                        step="0.01"
                        value={pdfTheme.pdf_watermark_opacity}
                        onChange={(e) => setPdfTheme(prev => ({ ...prev, pdf_watermark_opacity: parseFloat(e.target.value) }))}
                        className="w-full"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      {pdfTheme.pdf_watermark_url ? (
                        <img src={pdfTheme.pdf_watermark_url} alt="Filigrane" className="w-16 h-16 object-contain border rounded bg-white" />
                      ) : (
                        <span className="text-xs text-gray-500">Par défaut : logo de l'entreprise</span>
                      )}
                      <label className="cursor-pointer text-sm text-indigo-600 hover:underline">
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePdfAssetChange('watermark', e)} />
                        Choisir une image
                      </label>
                      {pdfTheme.pdf_watermark_url && (
                        <button
                          type="button"
                          onClick={() => { setPdfTheme(prev => ({ ...prev, pdf_watermark_url: '' })); setPdfAssetFiles(prev => ({ ...prev, watermark: null })); }}
                          className="text-sm text-red-500 hover:underline"
                        >
                          Supprimer
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Pied de page */}
              <div className="p-4 bg-gray-50 rounded-xl space-y-4">
                <h3 className="text-sm font-bold text-gray-800">📄 Pied de page</h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { kind: 'footer_left', urlKey: 'pdf_footer_left_icon_url', label: 'Icône gauche' },
                    { kind: 'footer_right', urlKey: 'pdf_footer_right_icon_url', label: 'Icône droite' },
                  ].map(({ kind, urlKey, label }) => (
                    <div key={kind} className="space-y-2">
                      <label className="block text-xs font-medium text-gray-600">{label}</label>
                      {pdfTheme[urlKey] ? (
                        <img src={pdfTheme[urlKey]} alt={label} className="w-16 h-16 object-contain border rounded bg-white" />
                      ) : (
                        <div className="w-16 h-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center bg-white">
                          <ImageIcon className="h-6 w-6 text-gray-300" />
                        </div>
                      )}
                      <div className="flex flex-col gap-1">
                        <label className="cursor-pointer text-sm text-indigo-600 hover:underline">
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePdfAssetChange(kind, e)} />
                          {pdfTheme[urlKey] ? 'Modifier' : 'Ajouter'}
                        </label>
                        {pdfTheme[urlKey] && (
                          <button
                            type="button"
                            onClick={() => { setPdfTheme(prev => ({ ...prev, [urlKey]: '' })); setPdfAssetFiles(prev => ({ ...prev, [kind]: null })); }}
                            className="text-sm text-red-500 hover:underline text-left"
                          >
                            Supprimer
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!pdfTheme.pdf_show_company_info}
                    onChange={(e) => setPdfTheme(prev => ({ ...prev, pdf_show_company_info: e.target.checked }))}
                    className="h-4 w-4"
                  />
                  <span className="text-sm text-gray-700">Afficher les coordonnées de l'entreprise au centre</span>
                </label>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Texte personnalisé du pied de page</label>
                  <input
                    type="text"
                    value={pdfTheme.pdf_footer_text}
                    onChange={(e) => setPdfTheme(prev => ({ ...prev, pdf_footer_text: e.target.value }))}
                    className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-indigo-400 focus:outline-none"
                    placeholder="Ex: Une solution CORACORP — www.skyapp.fr"
                  />
                </div>
              </div>

              {/* Boutons */}
              <div className="flex flex-wrap gap-3 pt-2">
                <Button
                  onClick={handleSavePdfTheme}
                  disabled={savingPdfTheme}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl py-3 font-semibold disabled:opacity-50"
                >
                  {savingPdfTheme ? 'Enregistrement…' : (<><Save className="h-5 w-5 mr-2" />Enregistrer le thème</>)}
                </Button>
                <Button onClick={handleResetPdfTheme} variant="outline" className="rounded-xl">
                  Restaurer le thème par défaut
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Colonne aperçu A4 */}
          <div className="lg:sticky lg:top-6 self-start">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">
                {realPreviewUrl ? 'Aperçu PDF réel' : 'Aperçu indicatif'}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleGenerateRealPreview}
                  disabled={previewLoading}
                  className="text-xs px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {previewLoading ? 'Génération…' : 'Aperçu PDF réel'}
                </button>
                {realPreviewUrl && (
                  <button
                    type="button"
                    onClick={() => { URL.revokeObjectURL(realPreviewUrl); setRealPreviewUrl(null); }}
                    className="text-xs px-2 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                  >
                    Vue schématique
                  </button>
                )}
              </div>
            </div>
            {realPreviewUrl ? (
              <iframe
                src={realPreviewUrl}
                title="Aperçu PDF réel"
                className="w-full bg-white shadow-2xl rounded-sm border"
                style={{ width: '100%', maxWidth: 360, aspectRatio: '210 / 297' }}
              />
            ) : (
            <div
              className="relative mx-auto bg-white shadow-2xl rounded-sm overflow-hidden border"
              style={{ width: '100%', maxWidth: 360, aspectRatio: '210 / 297', color: pdfTheme.text_color }}
            >
              {/* Bandeau */}
              {pdfTheme.pdf_header_banner_url ? (
                <img src={pdfTheme.pdf_header_banner_url} alt="bandeau" className="w-full h-12 object-cover" />
              ) : (
                <div
                  className="flex items-center gap-2 px-3 py-2"
                  style={{
                    justifyContent: pdfTheme.pdf_logo_position === 'center' ? 'center'
                      : pdfTheme.pdf_logo_position === 'right' ? 'flex-end' : 'flex-start',
                    background: pdfTheme.pdf_header_style === 'gradient'
                      ? `linear-gradient(90deg, ${pdfTheme.primary_color}, ${pdfTheme.secondary_color})`
                      : pdfTheme.pdf_header_style === 'underline' ? '#ffffff' : pdfTheme.primary_color,
                    borderTopLeftRadius: pdfTheme.pdf_header_style === 'rounded' ? 10 : 0,
                    borderTopRightRadius: pdfTheme.pdf_header_style === 'rounded' ? 10 : 0,
                    borderBottom: pdfTheme.pdf_header_style === 'underline' ? `3px solid ${pdfTheme.secondary_color}` : 'none',
                  }}
                >
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="logo"
                      className="object-contain"
                      style={{ height: pdfTheme.pdf_logo_size === 'small' ? 16 : pdfTheme.pdf_logo_size === 'large' ? 34 : 24 }}
                    />
                  ) : (
                    <span
                      className="text-xs font-bold"
                      style={{ color: pdfTheme.pdf_header_style === 'underline' ? pdfTheme.text_color : '#ffffff' }}
                    >
                      {companyInfo.company_name || 'SkyApp'}
                    </span>
                  )}
                </div>
              )}
              {/* Filigrane */}
              {pdfTheme.pdf_watermark_enabled && (pdfTheme.pdf_watermark_url || logoPreview) && (
                <img
                  src={pdfTheme.pdf_watermark_url || logoPreview}
                  alt="filigrane"
                  className="absolute inset-0 m-auto max-w-[70%] max-h-[50%] object-contain pointer-events-none"
                  style={{ opacity: pdfTheme.pdf_watermark_opacity }}
                />
              )}
              {/* Titre + cadres */}
              <div className="p-3 space-y-2">
                <div className="text-center text-[10px] font-bold py-1 border-2 rounded" style={{ borderColor: pdfTheme.border_color }}>
                  RAPPORT D'INTERVENTION
                </div>
                <div className="border-2 rounded p-2 text-[7px] leading-tight" style={{ borderColor: pdfTheme.border_color }}>
                  <div className="font-bold mb-1" style={{ color: pdfTheme.secondary_color }}>DONNEUR D'ORDRE</div>
                  Nom : …<br />Adresse : …
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[0, 1].map(i => (
                    <div key={i} className="border-2 rounded p-2 text-[7px] h-10" style={{ borderColor: pdfTheme.border_color }} />
                  ))}
                </div>
              </div>
              {/* Footer */}
              <div className="absolute bottom-0 left-0 right-0 border-t px-2 py-1 flex items-center justify-between text-[6px]" style={{ borderColor: pdfTheme.border_color }}>
                <div className="w-6">
                  {pdfTheme.pdf_footer_left_icon_url && <img src={pdfTheme.pdf_footer_left_icon_url} alt="" className="h-5 object-contain" />}
                </div>
                <div className="text-center flex-1">
                  {pdfTheme.pdf_show_company_info && (companyInfo.company_name || 'Entreprise')}
                  {pdfTheme.pdf_footer_text && <div>{pdfTheme.pdf_footer_text}</div>}
                </div>
                <div className="w-6 text-right">
                  {pdfTheme.pdf_footer_right_icon_url && <img src={pdfTheme.pdf_footer_right_icon_url} alt="" className="h-5 object-contain inline-block" />}
                </div>
              </div>
            </div>
            )}
            <p className="text-xs text-gray-500 mt-2 text-center">
              {realPreviewUrl
                ? 'PDF généré par le serveur avec le pipeline réel (fidèle au téléchargement).'
                : "Vue schématique. Cliquez sur « Aperçu PDF réel » pour le rendu exact généré par le serveur."}
            </p>
          </div>
        </div>
      )}

      {/* Onglet Informations générales (formulaire existant) */}
      {activeSettingsTab === 'general' && (
      <div className="max-w-4xl mx-auto">
        <Card className="bg-white/90 backdrop-blur-xl rounded-3xl border-0 shadow-2xl">
          <CardHeader className="border-b border-gray-100 pb-6">
            <CardTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Building className="h-6 w-6 text-indigo-600" />
              Identité de l'entreprise
            </CardTitle>
            <p className="text-sm text-gray-500 mt-2">
              Ces informations apparaîtront sur vos devis et factures
            </p>
          </CardHeader>
          
          <CardContent className="pt-6 space-y-6">
            {/* Logo */}
            <div className="flex items-start gap-6 p-5 bg-gray-50 rounded-xl">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Logo de l'entreprise
                </label>
                <div className="flex items-center gap-4">
                  {logoPreview ? (
                    <div className="relative">
                      <img
                        src={logoPreview}
                        alt="Logo"
                        className="w-32 h-32 object-contain border-2 border-gray-200 rounded-lg bg-white"
                      />
                      <button
                        onClick={() => {
                          setLogoFile(null);
                          setLogoPreview(null);
                          setCompanyInfo(prev => ({ ...prev, logo_url: '' }));
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-white">
                      <ImageIcon className="h-12 w-12 text-gray-300" />
                    </div>
                  )}
                  
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                    <div className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2">
                      <Upload className="h-4 w-4" />
                      Choisir un logo
                    </div>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">Format: PNG, JPG (max 2 MB)</p>
              </div>
            </div>

            {/* Nom et forme juridique */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nom de l'entreprise <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={companyInfo.company_name}
                  onChange={(e) => setCompanyInfo(prev => ({ ...prev, company_name: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                  placeholder="Ex: SkyApp Solutions"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Forme juridique <span className="text-red-500">*</span>
                </label>
                <select
                  value={companyInfo.legal_form}
                  onChange={(e) => setCompanyInfo(prev => ({ ...prev, legal_form: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none bg-white"
                >
                  <option value="">Sélectionner...</option>
                  {legalForms.map((form) => (
                    <option key={form} value={form}>{form}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Téléphone et Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Numéro de téléphone
                </label>
                <input
                  type="tel"
                  value={companyInfo.phone}
                  onChange={(e) => setCompanyInfo(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                  placeholder="Ex: 01 23 45 67 89"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Adresse email
                </label>
                <input
                  type="email"
                  value={companyInfo.email}
                  onChange={(e) => setCompanyInfo(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                  placeholder="Ex: contact@entreprise.fr"
                />
              </div>
            </div>

            {/* Adresse */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Adresse complète <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={companyInfo.address}
                onChange={(e) => setCompanyInfo(prev => ({ ...prev, address: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                placeholder="Ex: 123 Rue de la République"
              />
            </div>

            {/* Code postal et ville */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Code postal <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={companyInfo.postal_code}
                  onChange={(e) => setCompanyInfo(prev => ({ ...prev, postal_code: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                  placeholder="Ex: 75001"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Ville <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={companyInfo.city}
                  onChange={(e) => setCompanyInfo(prev => ({ ...prev, city: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                  placeholder="Ex: Paris"
                />
              </div>
            </div>

            {/* SIRET et SIREN */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  SIRET <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={companyInfo.siret}
                  onChange={(e) => setCompanyInfo(prev => ({ ...prev, siret: e.target.value.replace(/\D/g, '').slice(0, 14) }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                  placeholder="Ex: 123 456 789 00012"
                  maxLength={14}
                />
                {companyInfo.siret && companyInfo.siret.length !== 14 ? (
                  <p className="text-xs text-red-500 mt-1">
                    Il manque {14 - companyInfo.siret.length} chiffre{14 - companyInfo.siret.length > 1 ? 's' : ''} (14 chiffres requis)
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">14 chiffres</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  SIREN <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={companyInfo.siren}
                  onChange={(e) => setCompanyInfo(prev => ({ ...prev, siren: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                  placeholder="Ex: 123 456 789"
                  maxLength={9}
                />
                <p className="text-xs text-gray-500 mt-1">9 chiffres</p>
              </div>
            </div>

            {/* RCS/RM */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                RCS ou RM (si artisan)
              </label>
              <input
                type="text"
                value={companyInfo.rcs_rm}
                onChange={(e) => setCompanyInfo(prev => ({ ...prev, rcs_rm: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                placeholder="Ex: RCS Paris 123 456 789 ou RM 12345"
              />
              <p className="text-xs text-gray-500 mt-1">
                Registre du Commerce et des Sociétés ou Répertoire des Métiers
              </p>
            </div>

            {/* ==== CONDITIONS DU DEVIS ==== */}
            <div className="p-5 bg-purple-50 rounded-xl space-y-4">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                📄 Conditions du devis
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Durée de validité du devis (jours)
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={companyInfo.quote_validity_days}
                    onChange={(e) => setCompanyInfo(prev => ({ ...prev, quote_validity_days: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                    placeholder="Ex: 30"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Affiché sur le devis : « Devis valable N jours »
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Acompte par défaut (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    value={companyInfo.deposit_percent}
                    onChange={(e) => setCompanyInfo(prev => ({ ...prev, deposit_percent: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                    placeholder="Ex: 30"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Modifiable individuellement sur chaque devis. Laisser vide pour ne pas afficher d'acompte.
                  </p>
                </div>
              </div>
            </div>

            {/* ==== CONDITIONS DE RÈGLEMENT ==== */}
            <div className="p-5 bg-blue-50 rounded-xl space-y-4">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                💰 Conditions de règlement (factures)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Délai de paiement (jours)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={companyInfo.payment_delay_days}
                    onChange={(e) => setCompanyInfo(prev => ({ ...prev, payment_delay_days: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                    placeholder="Ex: 30"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Utilisé pour calculer la date d'échéance sur les factures (0 = paiement comptant)
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Conditions de règlement
                  </label>
                  <input
                    type="text"
                    value={companyInfo.payment_conditions}
                    onChange={(e) => setCompanyInfo(prev => ({ ...prev, payment_conditions: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                    placeholder="Ex: 30 jours net fin de mois"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Texte libre affiché dans les modalités de règlement
                  </p>
                </div>
              </div>
            </div>

            {/* ==== DURÉE DU TRAVAIL (PLANNING) ==== */}
            <div className="p-5 bg-teal-50 rounded-xl space-y-4">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                🕒 Durée du travail (planning)
              </h3>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Durée standard d'une journée (heures)
                </label>
                <input
                  type="number"
                  min="0"
                  max="24"
                  step="0.5"
                  value={companyInfo.standard_day_hours}
                  onChange={(e) => setCompanyInfo(prev => ({ ...prev, standard_day_hours: e.target.value }))}
                  className="w-full md:w-48 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                  placeholder="Ex: 7"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Utilisée pour les créneaux "Journée complète" et "Nuit" du planning (Matinée/Après-midi = moitié).
                  Légal France : 7h (35h/semaine). Beaucoup d'entreprises BTP utilisent 8h (39h/40h).
                </p>
              </div>
            </div>

            {/* ==== MAJORATION NUIT (PLANNING) ==== */}
            <div className="p-5 bg-slate-50 rounded-xl space-y-4">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                🌙 Majoration nuit (planning)
              </h3>
              <p className="text-xs text-gray-600">
                Chaque entreprise applique sa propre règle. Quand activée, elle est appliquée et affichée
                automatiquement sur toute mission planifiée en créneau "Nuit".
              </p>
              <label className="flex items-center gap-2 cursor-pointer w-fit">
                <input
                  type="checkbox"
                  checked={!!companyInfo.night_surcharge_enabled}
                  onChange={(e) => setCompanyInfo(prev => ({ ...prev, night_surcharge_enabled: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm font-semibold text-gray-700">Activer la majoration nuit</span>
              </label>
              {companyInfo.night_surcharge_enabled && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
                    <select
                      value={companyInfo.night_surcharge_type}
                      onChange={(e) => setCompanyInfo(prev => ({ ...prev, night_surcharge_type: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                    >
                      <option value="percent">Pourcentage (%)</option>
                      <option value="fixed">Montant fixe (€)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Valeur {companyInfo.night_surcharge_type === 'fixed' ? '(€)' : '(%)'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={companyInfo.night_surcharge_value}
                      onChange={(e) => setCompanyInfo(prev => ({ ...prev, night_surcharge_value: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                      placeholder="Ex: 25"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Libellé affiché</label>
                    <input
                      type="text"
                      value={companyInfo.night_surcharge_label}
                      onChange={(e) => setCompanyInfo(prev => ({ ...prev, night_surcharge_label: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                      placeholder="Ex: Majoration nuit"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ==== MÉDIATEUR DE LA CONSOMMATION (B2C) ==== */}
            <div className="p-5 bg-amber-50 rounded-xl space-y-4">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                ⚖️ Médiateur de la consommation
              </h3>
              <p className="text-xs text-gray-600">
                Obligatoire pour les CGV destinées aux <b>particuliers</b> (articles L.612-1 et suivants du Code de la consommation).
                Ces informations remplissent les variables des CGV B2C.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom du médiateur
                  </label>
                  <input
                    type="text"
                    value={companyInfo.mediator_name}
                    onChange={(e) => setCompanyInfo(prev => ({ ...prev, mediator_name: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                    placeholder="Ex: CM2C - Centre de médiation"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Adresse du médiateur
                  </label>
                  <input
                    type="text"
                    value={companyInfo.mediator_address}
                    onChange={(e) => setCompanyInfo(prev => ({ ...prev, mediator_address: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                    placeholder="Ex: 14 rue Saint-Jean, 75017 Paris"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Site internet du médiateur
                  </label>
                  <input
                    type="url"
                    value={companyInfo.mediator_website}
                    onChange={(e) => setCompanyInfo(prev => ({ ...prev, mediator_website: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                    placeholder="Ex: https://www.cm2c.net"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    E-mail du médiateur <span className="text-gray-400 font-normal">(optionnel)</span>
                  </label>
                  <input
                    type="email"
                    value={companyInfo.mediator_email}
                    onChange={(e) => setCompanyInfo(prev => ({ ...prev, mediator_email: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-indigo-400 focus:outline-none"
                    placeholder="Ex: contact@cm2c.net"
                  />
                </div>
              </div>
            </div>

            {/* Boutons */}
            <div className="flex gap-4 pt-4">
              <Button
                onClick={handleSave}
                disabled={saving || !companyInfo.company_name || !companyInfo.legal_form}
                className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-xl py-3 text-lg font-semibold disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
              
              <Button
                onClick={onBack}
                variant="outline"
                className="px-8 rounded-xl"
              >
                Retour
              </Button>
            </div>

            {/* Note admin */}
            <div className="mt-6 p-4 bg-amber-50 border-2 border-amber-200 rounded-xl">
              <p className="text-sm text-amber-800 flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span className="font-semibold">Note :</span> Ces paramètres ne peuvent être modifiés que par les administrateurs.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      )}
    </div>
  );
};

// ============================================================================
// SEARCH DETAILS MODAL - Affichage détaillé d'une recherche
// ============================================================================

const SearchDetailsModal = ({ search, onClose }) => {
  const [photos, setPhotos] = useState([]);
  const [photosBySection, setPhotosBySection] = useState({});
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [currentLightboxPhotos, setCurrentLightboxPhotos] = useState([]);

  useEffect(() => {
    loadPhotos();
  }, [search.id]);

  const loadPhotos = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await http.get(`${API}/searches/${search.id}/photos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const allPhotos = response.data || [];
      setPhotos(allPhotos);
      
      // Grouper les photos par section
      const grouped = {};
      allPhotos.forEach(photo => {
        if (!photo.is_profile && photo.section_id) {
          if (!grouped[photo.section_id]) {
            grouped[photo.section_id] = [];
          }
          grouped[photo.section_id].push(photo);
        }
      });
      setPhotosBySection(grouped);
      console.log('📸 Photos groupées par section:', grouped);
    } catch (error) {
      console.error('Erreur chargement photos:', error);
    }
  };

  const openLightbox = (index, photosArray) => {
    setCurrentPhotoIndex(index);
    setCurrentLightboxPhotos(photosArray);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % currentLightboxPhotos.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + currentLightboxPhotos.length) % currentLightboxPhotos.length);
  };

  // Parser les sections personnalisées depuis observations
  let parsedSections = [];
  console.log('🔍 [SearchDetailsModal] search.observations:', search.observations);
  if (search.observations) {
    try {
      const observationsData = typeof search.observations === 'string' 
        ? JSON.parse(search.observations) 
        : search.observations;
      
      console.log('📦 [SearchDetailsModal] observationsData:', observationsData);
      
      if (observationsData.customSections && Array.isArray(observationsData.customSections)) {
        parsedSections = observationsData.customSections;
        console.log('✅ [SearchDetailsModal] Sections personnalisées trouvées:', parsedSections.length, parsedSections);
      } else {
        console.log('⚠️ [SearchDetailsModal] Pas de customSections dans observationsData');
      }
    } catch (e) {
      console.error('❌ [SearchDetailsModal] Erreur parsing observations:', e);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] overflow-hidden flex flex-col">
        {/* Header avec thème SkyApp */}
        <div className="sticky top-0 bg-gradient-to-r from-gray-900 to-black text-white px-6 py-4 flex items-center justify-between z-10 shadow-lg border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white bg-opacity-10 rounded-xl flex items-center justify-center border border-white border-opacity-20">
              <Search size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold">Détails de la recherche</h2>
              <p className="text-gray-400 text-xs mt-0.5">
                📅 {new Date(search.shared_at || search.created_at).toLocaleString('fr-FR')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-10 rounded-lg p-2 transition-colors"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenu avec sections organisées comme le formulaire */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Badges de statut */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-medium px-3 py-1.5 rounded-lg ${
              search.search_type === 'TERRAIN' 
                ? 'bg-blue-100 text-blue-800' 
                : 'bg-orange-100 text-orange-800'
            }`}>
              {search.search_type === 'TERRAIN' ? '📍 Terrain' : '💧 Infiltration'}
            </span>
            <span className={`text-xs font-medium px-3 py-1.5 rounded-lg ${
              search.status === 'SHARED' ? 'bg-green-100 text-green-800' :
              search.status === 'ACTIVE' ? 'bg-blue-100 text-blue-800' :
              search.status === 'DRAFT' ? 'bg-purple-100 text-purple-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {search.status === 'SHARED' ? '✉️ Partagée' :
               search.status === 'ACTIVE' ? '🔵 Active' :
               search.status === 'DRAFT' ? '📝 Brouillon' :
               search.status}
            </span>
            {search.project_id && (
              <span className="text-xs font-medium px-3 py-1.5 rounded-lg bg-teal-100 text-teal-800">
                🎯 Convertie en projet
              </span>
            )}
          </div>

          {/* Section Informations Générales */}
          <div className="bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-all duration-200 hover:shadow-sm">
            <div className="flex items-center gap-3 p-4 border-b border-gray-100">
              <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                <User className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">Informations Générales</h3>
            </div>
            
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(search.nom || search.prenom) && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase mb-1.5 block">Client</label>
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <p className="text-sm font-medium text-gray-900">
                        👤 {search.prenom} {search.nom}
                      </p>
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase mb-1.5 block flex items-center gap-1">
                    <MapPin size={12} /> Localisation
                  </label>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <p className="text-sm font-medium text-gray-900">{search.location || 'Non spécifiée'}</p>
                    {(search.latitude && search.longitude) && (
                      <p className="text-xs text-gray-500 mt-1">
                        📍 {search.latitude.toFixed(6)}, {search.longitude.toFixed(6)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section Description */}
          {search.description && cleanObservations(search.description) && (
            <div className="bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-all duration-200 hover:shadow-sm">
              <div className="flex items-center gap-3 p-4 border-b border-gray-100">
                <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">Description de la recherche</h3>
              </div>
              
              <div className="p-4">
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{cleanObservations(search.description)}</p>
                
                {/* Photos de la section Description */}
                {photosBySection['description'] && photosBySection['description'].length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs font-medium text-gray-500 uppercase mb-2 flex items-center gap-1">
                      <Camera size={12} /> Photos ({photosBySection['description'].length})
                    </p>
                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                      {photosBySection['description'].map((photo, index) => (
                        <div
                          key={photo.filename || index}
                          className="group relative rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 border border-gray-200 hover:border-gray-900"
                          onClick={() => openLightbox(index, photosBySection['description'])}
                          style={{ aspectRatio: '1/1' }}
                        >
                          <img
                            src={photo.url || `${SUPABASE_URL}/storage/v1/object/public/${photo.storage_path}`}
                            alt={`Photo ${index + 1}`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                          <div className="absolute bottom-1 left-1">
                            <span className="text-white text-xs font-medium bg-black/60 px-1.5 py-0.5 rounded">#{index + 1}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section Observations */}
          {search.observations && cleanObservations(search.observations) && (
            <div className="bg-white border border-gray-200 rounded-xl hover:border-gray-300 transition-all duration-200 hover:shadow-sm">
              <div className="flex items-center gap-3 p-4 border-b border-gray-100">
                <div className="w-10 h-10 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Eye className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-medium text-gray-900">Observations et remarques</h3>
              </div>
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{cleanObservations(search.observations)}</p>
              
              {/* Photos de la section Observations */}
              {photosBySection['observations'] && photosBySection['observations'].length > 0 && (
                <div className="mt-4 pt-4 border-t border-amber-200">
                  <p className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                    <Camera size={14} /> Photos ({photosBySection['observations'].length})
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {photosBySection['observations'].map((photo, index) => (
                      <div
                        key={photo.filename || index}
                        className="group relative rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 border-2 border-amber-200 hover:border-amber-400"
                        onClick={() => openLightbox(index, photosBySection['observations'])}
                        style={{ aspectRatio: '1/1' }}
                      >
                        <img
                          src={photo.url || `${SUPABASE_URL}/storage/v1/object/public/${photo.storage_path}`}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
                        <div className="absolute bottom-2 left-2">
                          <span className="text-white text-xs font-bold bg-black bg-opacity-50 px-2 py-1 rounded-lg">#{index + 1}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sections personnalisées avec style moderne */}
          {parsedSections.length > 0 && parsedSections.map((section, idx) => (
            <div key={idx} className="bg-white rounded-xl shadow-md p-6 border-2 border-purple-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
                  <Plus className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">{section.title}</h3>
              </div>
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{cleanObservations(section.value)}</p>
              
              {/* Photos de cette section personnalisée */}
              {photosBySection[section.id] && photosBySection[section.id].length > 0 && (
                <div className="mt-4 pt-4 border-t border-purple-200">
                  <p className="text-sm font-semibold text-gray-600 mb-3 flex items-center gap-2">
                    <Camera size={14} /> Photos ({photosBySection[section.id].length})
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {photosBySection[section.id].map((photo, photoIndex) => (
                      <div
                        key={photo.filename || photoIndex}
                        className="group relative rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-300 border-2 border-purple-200 hover:border-purple-400"
                        onClick={() => openLightbox(photoIndex, photosBySection[section.id])}
                        style={{ aspectRatio: '1/1' }}
                      >
                        <img
                          src={photo.url || `${SUPABASE_URL}/storage/v1/object/public/${photo.storage_path}`}
                          alt={`Photo ${photoIndex + 1}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60" />
                        <div className="absolute bottom-2 left-2">
                          <span className="text-white text-xs font-bold bg-black bg-opacity-50 px-2 py-1 rounded-lg">#{photoIndex + 1}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Section Envoyé par avec style moderne */}
          <div className="bg-white rounded-xl shadow-md p-6 border-2 border-blue-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <User className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Envoyé par</h3>
            </div>
            {search.users ? (
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  👷 {search.users.first_name} {search.users.last_name}
                </p>
                {search.users.email && !search.users.email.includes('@temp-skyapp.local') && (
                  <p className="text-sm text-gray-600 mt-2 flex items-center gap-2">
                    📧 {search.users.email}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-500 italic bg-gray-50 rounded-lg p-4">Technicien non identifié</p>
            )}
          </div>
        </div>

        {/* Footer avec bouton moderne */}
        <div className="sticky bottom-0 bg-white p-6 border-t-2 border-gray-200 shadow-lg">
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-gray-700 to-gray-900 text-white px-6 py-4 rounded-xl hover:from-gray-800 hover:to-black transition-all duration-300 font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
          >
            Fermer
          </button>
        </div>
      </div>

      {/* Lightbox pour les photos */}
      {lightboxOpen && photos.length > 0 && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeLightbox();
            }
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
            className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <X size={32} />
          </button>
          
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevPhoto();
            }}
            className="absolute left-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-3 transition-colors"
          >
            <ChevronLeft size={40} />
          </button>

          <div className="max-w-5xl max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={currentLightboxPhotos[currentPhotoIndex]?.url || `${SUPABASE_URL}/storage/v1/object/public/${currentLightboxPhotos[currentPhotoIndex]?.storage_path}`}
              alt={`Photo ${currentPhotoIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <p className="text-white mt-4 text-lg">
              {currentPhotoIndex + 1} / {currentLightboxPhotos.length}
            </p>
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              nextPhoto();
            }}
            className="absolute right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-3 transition-colors"
          >
            <ChevronRight size={40} />
          </button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// EDIT SEARCH MODAL - Modal d'édition complet avec photos
// ============================================================================

const EditSearchModal = ({ search, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    location: search.location || '',
    description: search.description || '',
    observations: search.observations || '',
    client_id: search.client_id || null,
    nom: search.nom || '',
    prenom: search.prenom || '',
    search_type: search.search_type || 'TERRAIN'
  });
  
  // Sections avec leurs photos (style formulaire)
  const [sections, setSections] = useState([
    { 
      id: 'general_info', 
      title: 'Informations générales',
      icon: User,
      collapsed: false,
      hasPhotos: false,
      fields: [
        { id: 'nom', label: 'Nom', type: 'text', value: search.nom || '' },
        { id: 'prenom', label: 'Prénom', type: 'text', value: search.prenom || '' },
        { id: 'client_id', label: 'Client récurrent', type: 'select', value: search.client_id || '' },
        { id: 'adresse', label: 'Adresse', type: 'text', value: search.location || '', required: true }
      ]
    },
    { 
      id: 'description', 
      title: 'Description de la recherche',
      icon: FileText,
      collapsed: false,
      hasPhotos: true,
      photos: [],
      newPhotos: [],
      fields: [
        { id: 'description', label: 'Description', type: 'textarea', value: search.description || '', rows: 4 }
      ]
    },
    { 
      id: 'observations', 
      title: 'Observations et remarques',
      icon: Eye,
      collapsed: false,
      hasPhotos: true,
      photos: [],
      newPhotos: [],
      fields: [
        { id: 'observations', label: 'Observations', type: 'textarea', value: (() => {
          try {
            const data = JSON.parse(search.observations || '{}');
            return data.text || '';
          } catch (e) {
            return search.observations || '';
          }
        })(), rows: 3 }
      ]
    }
  ]);
  
  const [clients, setClients] = useState([]);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [photosToDelete, setPhotosToDelete] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [currentSection, setCurrentSection] = useState(null);

  useEffect(() => {
    loadClients();
    loadPhotos();
  }, []);

  const loadClients = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await http.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setClients(response.data || []);
    } catch (error) {
      console.error('Erreur chargement clients:', error);
    }
  };

  const loadPhotos = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      console.log('🔍 Chargement photos pour recherche:', search.id);
      const response = await http.get(`${API}/searches/${search.id}/photos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      console.log('✅ Photos API response:', response.data);
      
      const allPhotos = response.data || [];
      
      // Photo de profil
      const profilePic = allPhotos.find(photo => photo.is_profile);
      if (profilePic) {
        setProfilePhotoPreview(profilePic.url || `${SUPABASE_URL}/storage/v1/object/public/${profilePic.storage_path}`);
      }
      
      // Organiser les photos par section
      setSections(prevSections => prevSections.map(section => {
        if (!section.hasPhotos) return section;
        
        const sectionPhotos = allPhotos.filter(photo => 
          !photo.is_profile && photo.section_id === section.id
        );
        
        return { ...section, photos: sectionPhotos, newPhotos: [] };
      }));
    } catch (error) {
      console.error('❌ Erreur chargement photos:', error);
    }
  };

  const handleFieldChange = (sectionId, fieldId, value) => {
    setSections(sections.map(s => {
      if (s.id === sectionId) {
        return {
          ...s,
          fields: s.fields.map(f => f.id === fieldId ? { ...f, value } : f)
        };
      }
      return s;
    }));
  };

  const toggleSection = (sectionId) => {
    setSections(sections.map(s => 
      s.id === sectionId ? { ...s, collapsed: !s.collapsed } : s
    ));
  };

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePhoto(file);
      setProfilePhotoPreview(URL.createObjectURL(file));
    }
  };

  const handlePhotoAdd = (sectionId, e) => {
    const files = Array.from(e.target.files);
    
    const photoUrls = files.map(file => ({
      file,
      url: URL.createObjectURL(file),
      isNew: true,
      section_id: sectionId
    }));
    
    setSections(sections.map(s => 
      s.id === sectionId 
        ? { ...s, newPhotos: [...(s.newPhotos || []), ...photoUrls] }
        : s
    ));
  };

  const handlePhotoDelete = (sectionId, photo, isNew) => {
    if (isNew) {
      // Supprimer une nouvelle photo pas encore uploadée
      URL.revokeObjectURL(photo.url);
      setSections(sections.map(s => 
        s.id === sectionId 
          ? { ...s, newPhotos: s.newPhotos.filter(p => p.url !== photo.url) }
          : s
      ));
    } else {
      // Marquer une photo existante pour suppression
      setPhotosToDelete([...photosToDelete, photo.filename || photo.storage_path]);
      setSections(sections.map(s => 
        s.id === sectionId 
          ? { ...s, photos: s.photos.filter(p => p !== photo) }
          : s
      ));
    }
  };

  const openLightbox = (sectionId, index) => {
    setCurrentSection(sectionId);
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setCurrentSection(null);
  };

  const nextPhoto = () => {
    const section = sections.find(s => s.id === currentSection);
    if (!section) return;
    const totalPhotos = section.photos.length + section.newPhotos.length;
    setCurrentPhotoIndex((prev) => (prev + 1) % totalPhotos);
  };

  const prevPhoto = () => {
    const section = sections.find(s => s.id === currentSection);
    if (!section) return;
    const totalPhotos = section.photos.length + section.newPhotos.length;
    setCurrentPhotoIndex((prev) => (prev - 1 + totalPhotos) % totalPhotos);
  };

  const getCurrentPhoto = () => {
    const section = sections.find(s => s.id === currentSection);
    if (!section) return null;
    const allPhotos = [...section.photos, ...section.newPhotos];
    return allPhotos[currentPhotoIndex];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Construire les données depuis les sections
      const generalSection = sections.find(s => s.id === 'general_info');
      const descSection = sections.find(s => s.id === 'description');
      const obsSection = sections.find(s => s.id === 'observations');
      
      const updatedData = {
        nom: generalSection?.fields.find(f => f.id === 'nom')?.value || '',
        prenom: generalSection?.fields.find(f => f.id === 'prenom')?.value || '',
        client_id: generalSection?.fields.find(f => f.id === 'client_id')?.value || null,
        location: generalSection?.fields.find(f => f.id === 'adresse')?.value || '',
        description: descSection?.fields.find(f => f.id === 'description')?.value || '',
        observations: obsSection?.fields.find(f => f.id === 'observations')?.value || '',
        search_type: formData.search_type
      };

      // 1. Supprimer les photos marquées
      for (const photoPath of photosToDelete) {
        try {
          await http.delete(`${API}/searches/${search.id}/photos/${photoPath}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
        } catch (err) {
          console.error('Erreur suppression photo:', err);
        }
      }

      // 2. Upload photo de profil si nouvelle
      if (profilePhoto) {
        const formDataProfile = new FormData();
        formDataProfile.append('files', profilePhoto);
        formDataProfile.append('is_profile', 'true');
        formDataProfile.append('section_id', 'profile');

        await http.post(`${API}/searches/${search.id}/photos`, formDataProfile, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      // 3. Uploader les nouvelles photos par section
      for (const section of sections) {
        if (section.hasPhotos && section.newPhotos && section.newPhotos.length > 0) {
          const formDataPhotos = new FormData();
          section.newPhotos.forEach(photo => {
            formDataPhotos.append('files', photo.file);
          });
          formDataPhotos.append('section_id', section.id);

          await http.post(`${API}/searches/${search.id}/photos`, formDataPhotos, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data'
            }
          });
        }
      }

      // 4. Mettre à jour les données textuelles
      await onSave(search.id, updatedData);
      
      alert('✅ Recherche modifiée avec succès !');
      onClose();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      alert('❌ Erreur lors de la sauvegarde: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-xl flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Edit2 size={28} />
              Modifier la recherche
            </h2>
            <p className="text-indigo-100 text-sm mt-1">Correction par le bureau</p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Photo de profil */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <User size={18} />
              Photo de profil (optionnelle)
            </h3>
            
            {profilePhotoPreview ? (
              <div className="relative w-24 h-24 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 group">
                <img src={profilePhotoPreview} alt="Profil" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => { setProfilePhoto(null); setProfilePhotoPreview(null); }}
                  className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                >
                  <X size={24} className="text-white" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                  <User size={32} className="text-gray-400" />
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePhotoChange}
                    id="profile-photo-edit"
                    className="hidden"
                  />
                  <label
                    htmlFor="profile-photo-edit"
                    className="inline-flex items-center gap-2 bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-900 cursor-pointer transition-colors text-sm"
                  >
                    <Camera size={16} />
                    Choisir une photo
                  </label>
                  <p className="text-xs text-gray-500 mt-1">Formats acceptés: JPG, PNG, WebP (max 5MB)</p>
                </div>
              </div>
            )}
          </div>

          {/* Sections dynamiques collapsibles */}
          {sections.map((section) => {
            const SectionIcon = section.icon;
            const sectionPhotos = section.photos || [];
            const sectionNewPhotos = section.newPhotos || [];
            const totalPhotos = sectionPhotos.length + sectionNewPhotos.length;
            
            return (
              <div key={section.id} className="bg-white border-2 border-gray-200 rounded-xl shadow-sm overflow-hidden">
                {/* Section Header Collapsible */}
                <button
                  type="button"
                  onClick={() => toggleSection(section.id)}
                  className="w-full px-5 py-4 flex items-center justify-between bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-150 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-gray-800 p-2 rounded-lg">
                      <SectionIcon size={20} className="text-white" />
                    </div>
                    <span className="font-semibold text-gray-900">{section.title}</span>
                    {section.hasPhotos && totalPhotos > 0 && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                        {totalPhotos} photo{totalPhotos > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  {section.collapsed ? <ChevronDown size={20} className="text-gray-600" /> : <ChevronUp size={20} className="text-gray-600" />}
                </button>

                {/* Section Content */}
                {!section.collapsed && (
                  <div className="p-5 space-y-4 bg-white">
                    {/* Fields */}
                    {section.fields && section.fields.map(field => (
                      <div key={field.id}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {field.label} {field.required && <span className="text-red-500">*</span>}
                        </label>
                        {field.type === 'select' ? (
                          <select
                            value={field.value}
                            onChange={(e) => handleFieldChange(section.id, field.id, e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            required={field.required}
                          >
                            <option value="">Client occasionnel (pas de suivi)</option>
                            {clients.map(client => (
                              <option key={client.id} value={client.id}>
                                {client.first_name} {client.last_name}
                              </option>
                            ))}
                          </select>
                        ) : field.type === 'textarea' ? (
                          <textarea
                            value={field.value}
                            onChange={(e) => handleFieldChange(section.id, field.id, e.target.value)}
                            rows={field.rows || 3}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                            required={field.required}
                            placeholder={field.placeholder || `Décrivez le contenu de cette section...`}
                          />
                        ) : (
                          <input
                            type={field.type}
                            value={field.value}
                            onChange={(e) => handleFieldChange(section.id, field.id, e.target.value)}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            required={field.required}
                            placeholder={field.placeholder || `Saisissez ${field.label.toLowerCase()}...`}
                          />
                        )}
                      </div>
                    ))}

                    {/* Photos de cette section */}
                    {section.hasPhotos && (
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-sm font-medium text-gray-700">Photos de cette section</label>
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={(e) => handlePhotoAdd(section.id, e)}
                            id={`photos-${section.id}`}
                            className="hidden"
                          />
                          <label
                            htmlFor={`photos-${section.id}`}
                            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 cursor-pointer transition-colors text-sm"
                          >
                            <Camera size={16} />
                            Ajouter des photos
                          </label>
                        </div>

                        {totalPhotos > 0 ? (
                          <div className="grid grid-cols-3 gap-3">
                            {sectionPhotos.map((photo, idx) => (
                              <div key={`existing-${idx}`} className="relative aspect-square rounded-lg overflow-hidden group border border-gray-200">
                                <img
                                  src={photo.url || `${SUPABASE_URL}/storage/v1/object/public/${photo.storage_path}`}
                                  alt={`Photo ${idx + 1}`}
                                  className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => openLightbox(section.id, idx)}
                                />
                                <button
                                  type="button"
                                  onClick={() => handlePhotoDelete(section.id, photo, false)}
                                  className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ))}
                            {sectionNewPhotos.map((photo, idx) => (
                              <div key={`new-${idx}`} className="relative aspect-square rounded-lg overflow-hidden group border-2 border-green-500">
                                <img
                                  src={photo.url}
                                  alt={`Nouvelle ${idx + 1}`}
                                  className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => openLightbox(section.id, sectionPhotos.length + idx)}
                                />
                                <button
                                  type="button"
                                  onClick={() => handlePhotoDelete(section.id, photo, true)}
                                  className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                >
                                  <X size={14} />
                                </button>
                                <div className="absolute bottom-2 left-2 bg-green-600 text-white text-xs px-2 py-1 rounded-md font-medium shadow">
                                  Nouveau
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                            <Camera size={32} className="mx-auto text-gray-400 mb-2" />
                            <p className="text-sm text-gray-500">Aucune photo ajoutée pour cette section</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Info */}
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
            <p className="text-sm text-amber-800 flex items-center gap-2">
              <AlertCircle size={16} />
              <span>Les modifications seront visibles immédiatement pour le technicien.</span>
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save size={20} />
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Lightbox */}
      {lightboxOpen && currentSection && (() => {
        const section = sections.find(s => s.id === currentSection);
        const allPhotos = section ? [...section.photos, ...section.newPhotos] : [];
        const currentPhoto = allPhotos[currentPhotoIndex];
        
        return allPhotos.length > 0 && currentPhoto ? (
          <div 
            className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-[60]"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                closeLightbox();
              }
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                closeLightbox();
              }}
              className="absolute top-4 right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
            >
              <X size={32} />
            </button>
            
            {allPhotos.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevPhoto();
                  }}
                  className="absolute left-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-3 transition-colors"
                >
                  <ChevronLeft size={40} />
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextPhoto();
                  }}
                  className="absolute right-4 text-white hover:bg-white hover:bg-opacity-20 rounded-full p-3 transition-colors"
                >
                  <ChevronRight size={40} />
                </button>
              </>
            )}

            <div className="max-w-5xl max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
              <img
                src={
                  currentPhoto.file
                    ? currentPhoto.url
                    : (currentPhoto.url || `${SUPABASE_URL}/storage/v1/object/public/${currentPhoto.storage_path}`)
                }
                alt={`${section.title} ${currentPhotoIndex + 1}`}
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
              />
              <p className="text-white mt-4 text-lg">
                {section.title} - Photo {currentPhotoIndex + 1} / {allPhotos.length}
              </p>
            </div>
          </div>
        ) : null;
      })()}
    </div>
  );
};

// ============================================================================
// BUREAU SEARCHES VIEW - Recherches partagées par les techniciens
// ============================================================================

// VAL-002 — Métadonnées d'affichage du statut d'un compte-rendu
const REPORT_STATUS_META = {
  submitted: { label: 'À contrôler', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  correction_requested: { label: 'Correction demandée', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  resubmitted: { label: 'Corrigé — à contrôler', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  validated: { label: 'Validé', cls: 'bg-green-50 text-green-700 border-green-200' },
  draft: { label: 'Brouillon', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
};
const getReportStatusMeta = (status) => REPORT_STATUS_META[status] || REPORT_STATUS_META.submitted;

const BureauSearchesView = () => {
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('ALL'); // 'ALL', 'TERRAIN', 'INFILTRATION'
  const [statusFilter, setStatusFilter] = useState('ALL'); // 'ALL', 'SHARED', 'ACTIVE', 'DRAFT', 'CONVERTED'
  const [convertedSearches, setConvertedSearches] = useState(new Set()); // Track converted searches
  const [selectedSearch, setSelectedSearch] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    loadSearches();
  }, [statusFilter]);

  const loadSearches = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      let url = `${API}/searches`;
      
      // Charger selon le filtre de statut
      if (statusFilter === 'SHARED') {
        url = `${API}/reports/bureau-searches`;
      } else if (statusFilter === 'ACTIVE') {
        url = `${API}/searches?status=ACTIVE`;
      } else if (statusFilter === 'DRAFT') {
        url = `${API}/searches?status=DRAFT`;
      } else if (statusFilter === 'CONVERTED') {
        // Récupérer les recherches qui ont été converties en projet
        url = `${API}/searches?converted=true`;
      } else {
        // ALL : charger toutes les recherches
        url = `${API}/searches`;
      }
      
      const response = await http.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      const searchList = statusFilter === 'SHARED' ? response.data.searches : response.data;
      setSearches(searchList || []);
    } catch (error) {
      console.error('Erreur chargement recherches bureau:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (search) => {
    setSelectedSearch(search);
    setIsDetailsModalOpen(true);
  };

  const handleEdit = (search) => {
    setSelectedSearch(search);
    setIsEditModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedSearch(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedSearch(null);
    loadSearches(); // Recharger les recherches après édition
  };

  const handleSaveEdit = async (searchId, payload) => {
    try {
      await http.put(`${API}/searches/${searchId}`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (error) {
      console.error('Erreur sauvegarde recherche:', error);
      throw error;
    }
  };

  // Déterminer le type de recherche depuis la base de données
  const getSearchType = (search) => {
    // Utiliser directement le champ search_type de la DB
    if (search.search_type) return search.search_type.toUpperCase();
    
    // Fallback : Heuristique basée sur les données (pour anciennes recherches)
    const desc = (search.description || '').toLowerCase();
    if (desc.includes('infiltration') || desc.includes('perméabilité') || desc.includes('test')) {
      return 'INFILTRATION';
    }
    return 'TERRAIN';
  };

  const filteredSearches = searches.filter(search => {
    if (filterType === 'ALL') return true;
    return getSearchType(search) === filterType;
  });

  const terrainCount = searches.filter(s => getSearchType(s) === 'TERRAIN').length;
  const infiltrationCount = searches.filter(s => getSearchType(s) === 'INFILTRATION').length;
  
  // Compter par statut
  const sharedCount = searches.filter(s => s.status === 'SHARED').length;
  const activeCount = searches.filter(s => s.status === 'ACTIVE').length;
  const draftCount = searches.filter(s => s.status === 'DRAFT').length;
  const convertedCount = searches.filter(s => s.project_id).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">🔍 Gestion des Recherches</h1>
        <p className="text-gray-600">Toutes les recherches : brouillons, actives, partagées et converties</p>
      </div>

      {/* Filtres de Statut */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Filtrer par statut</h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setStatusFilter('ALL')}
            className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
              statusFilter === 'ALL'
                ? 'bg-gray-900 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📋 Toutes ({searches.length})
          </button>
          <button
            onClick={() => setStatusFilter('SHARED')}
            className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
              statusFilter === 'SHARED'
                ? 'bg-green-600 text-white shadow-md'
                : 'bg-green-50 text-green-700 hover:bg-green-100'
            }`}
          >
            ✉️ Partagées ({sharedCount})
          </button>
          <button
            onClick={() => setStatusFilter('ACTIVE')}
            className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
              statusFilter === 'ACTIVE'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
            }`}
          >
            🔵 Actives ({activeCount})
          </button>
          <button
            onClick={() => setStatusFilter('DRAFT')}
            className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
              statusFilter === 'DRAFT'
                ? 'bg-purple-600 text-white shadow-md'
                : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
            }`}
          >
            📝 Brouillons ({draftCount})
          </button>
          <button
            onClick={() => setStatusFilter('CONVERTED')}
            className={`px-4 py-2 rounded-lg font-medium transition-all text-sm ${
              statusFilter === 'CONVERTED'
                ? 'bg-teal-600 text-white shadow-md'
                : 'bg-teal-50 text-teal-700 hover:bg-teal-100'
            }`}
          >
            🎯 Converties en projet ({convertedCount})
          </button>
        </div>
      </div>

      {/* Filtres de Type */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Filtrer par type</h3>
        <div className="flex gap-3">
          <button
            onClick={() => setFilterType('ALL')}
            className={`px-6 py-3 rounded-lg font-medium transition-all ${
              filterType === 'ALL'
                ? 'bg-gray-900 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            📋 Tous types ({searches.length})
          </button>
          <button
            onClick={() => setFilterType('TERRAIN')}
            className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
              filterType === 'TERRAIN'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
            }`}
          >
            <MapPin size={18} />
            Terrain ({terrainCount})
          </button>
          <button
            onClick={() => setFilterType('INFILTRATION')}
            className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
              filterType === 'INFILTRATION'
                ? 'bg-orange-600 text-white shadow-md'
                : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
            }`}
          >
            <Layers size={18} />
            Infiltration ({infiltrationCount})
          </button>
        </div>
      </div>

      {/* Grid en 2 colonnes : Terrain à gauche, Infiltration à droite */}
      <div className="grid grid-cols-2 gap-6">
        {/* Colonne Terrain */}
        <div>
          <h2 className="text-xl font-bold text-blue-900 mb-4 flex items-center gap-2">
            <MapPin className="text-blue-600" size={24} />
            Recherches Terrain ({terrainCount})
          </h2>
          <div className="space-y-4">
            {searches.filter(s => getSearchType(s) === 'TERRAIN').length === 0 ? (
              <div className="bg-blue-50 border-2 border-dashed border-blue-200 rounded-xl p-8 text-center">
                <MapPin className="h-12 w-12 mx-auto mb-3 text-blue-300" />
                <p className="text-blue-600 font-medium">Aucune recherche terrain</p>
              </div>
            ) : (
              searches.filter(s => getSearchType(s) === 'TERRAIN').map(search => (
                <div key={search.id} className="bg-white border-2 border-blue-200 rounded-xl p-5 hover:shadow-lg transition-all relative">
                  {/* Badge converti en projet */}
                  {search.project_id && (
                    <div className="absolute -top-2 -right-2 z-10">
                      <span className="bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
                        🎯 Projet créé
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <MapPin className="text-blue-600" size={20} />
                      <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        Terrain
                      </span>
                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 flex items-center gap-1">
                        <User size={12} />
                        {search.users ? `${search.users.first_name} ${search.users.last_name}` : 'Technicien'}
                      </span>
                      {search.status && (
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          search.status === 'SHARED' ? 'bg-green-100 text-green-700' :
                          search.status === 'ACTIVE' ? 'bg-blue-100 text-blue-700' :
                          search.status === 'DRAFT' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {search.status === 'SHARED' ? '✉️ Partagée' :
                           search.status === 'ACTIVE' ? '🔵 Active' :
                           search.status === 'DRAFT' ? '📝 Brouillon' :
                           search.status}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(search.shared_at || search.created_at).toLocaleString('fr-FR', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  
                  {/* Client info */}
                  {(search.nom || search.prenom) && (
                    <div className="mb-3 p-2 bg-blue-50 rounded-lg">
                      <p className="text-sm font-semibold text-blue-900">
                        👤 {search.prenom} {search.nom}
                      </p>
                    </div>
                  )}
                  
                  {/* Location */}
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                    <MapPin size={16} className="text-blue-600 mt-1 flex-shrink-0" />
                    <span>{search.location || 'Sans adresse'}</span>
                  </h3>
                  
                  {/* Description */}
                  {search.description && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-500 mb-1">Description :</p>
                      <p className="text-sm text-gray-700 line-clamp-3">{search.description}</p>
                    </div>
                  )}
                  
                  {/* Observations */}
                  {search.observations && cleanObservations(search.observations) && (
                    <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs font-semibold text-amber-700 mb-1">📝 Observations :</p>
                      <p className="text-sm text-gray-700 line-clamp-2">{cleanObservations(search.observations)}</p>
                    </div>
                  )}
                  
                  {/* Boutons d'action */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewDetails(search)}
                      className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      <Eye size={16} />
                      Détails
                    </button>
                    <button
                      onClick={() => !search.project_id && handleEdit(search)}
                      disabled={!!search.project_id}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                        search.project_id
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                          : 'bg-gray-700 text-white hover:bg-gray-800'
                      }`}
                      title={search.project_id ? 'Projet déjà créé - modification désactivée' : 'Modifier la recherche'}
                    >
                      <Edit2 size={16} />
                      Modifier
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const response = await fetch(`${API}/searches/${search.id}/pdf`, {
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                          });
                          if (!response.ok) throw new Error('Erreur génération PDF');
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `recherche_${search.location || search.id}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } catch (error) {
                          alert('❌ Erreur lors de la génération du PDF');
                          console.error(error);
                        }
                      }}
                      className="flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      title="Télécharger en PDF"
                    >
                      <Download size={16} />
                      PDF
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Colonne Infiltration */}
        <div>
          <h2 className="text-xl font-bold text-orange-900 mb-4 flex items-center gap-2">
            <Layers className="text-orange-600" size={24} />
            Recherches Infiltration ({infiltrationCount})
          </h2>
          <div className="space-y-4">
            {searches.filter(s => getSearchType(s) === 'INFILTRATION').length === 0 ? (
              <div className="bg-orange-50 border-2 border-dashed border-orange-200 rounded-xl p-8 text-center">
                <Layers className="h-12 w-12 mx-auto mb-3 text-orange-300" />
                <p className="text-orange-600 font-medium">Aucune recherche d'infiltration</p>
              </div>
            ) : (
              searches.filter(s => getSearchType(s) === 'INFILTRATION').map(search => (
                <div key={search.id} className="bg-white border-2 border-orange-200 rounded-xl p-5 hover:shadow-lg transition-all relative">
                  {/* Badge converti en projet */}
                  {search.project_id && (
                    <div className="absolute -top-2 -right-2 z-10">
                      <span className="bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1">
                        🎯 Projet créé
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Layers className="text-orange-600" size={20} />
                      <span className="text-xs font-medium bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                        Infiltration
                      </span>
                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 flex items-center gap-1">
                        <User size={12} />
                        {search.users ? `${search.users.first_name} ${search.users.last_name}` : 'Technicien'}
                      </span>
                      {search.status && (
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          search.status === 'SHARED' ? 'bg-green-100 text-green-700' :
                          search.status === 'ACTIVE' ? 'bg-blue-100 text-blue-700' :
                          search.status === 'DRAFT' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {search.status === 'SHARED' ? '✉️ Partagée' :
                           search.status === 'ACTIVE' ? '🔵 Active' :
                           search.status === 'DRAFT' ? '📝 Brouillon' :
                           search.status}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      {new Date(search.shared_at || search.created_at).toLocaleString('fr-FR', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  
                  {/* Client info */}
                  {(search.nom || search.prenom) && (
                    <div className="mb-3 p-2 bg-orange-50 rounded-lg">
                      <p className="text-sm font-semibold text-orange-900">
                        👤 {search.prenom} {search.nom}
                      </p>
                    </div>
                  )}
                  
                  {/* Location */}
                  <h3 className="font-bold text-gray-900 mb-2 flex items-start gap-2">
                    <MapPin size={16} className="text-orange-600 mt-1 flex-shrink-0" />
                    <span>{search.location || 'Sans adresse'}</span>
                  </h3>
                  
                  {/* Description */}
                  {search.description && (
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-500 mb-1">Description :</p>
                      <p className="text-sm text-gray-700 line-clamp-3">{search.description}</p>
                    </div>
                  )}
                  
                  {/* Observations */}
                  {search.observations && cleanObservations(search.observations) && (
                    <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs font-semibold text-amber-700 mb-1">📝 Observations :</p>
                      <p className="text-sm text-gray-700 line-clamp-2">{cleanObservations(search.observations)}</p>
                    </div>
                  )}
                  
                  {/* Boutons d'action */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewDetails(search)}
                      className="flex-1 flex items-center justify-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
                    >
                      <Eye size={16} />
                      Détails
                    </button>
                    <button
                      onClick={() => !search.project_id && handleEdit(search)}
                      disabled={!!search.project_id}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
                        search.project_id
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                          : 'bg-gray-700 text-white hover:bg-gray-800'
                      }`}
                      title={search.project_id ? 'Projet déjà créé - modification désactivée' : 'Modifier la recherche'}
                    >
                      <Edit2 size={16} />
                      Modifier
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          const response = await fetch(`${API}/searches/${search.id}/pdf`, {
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                          });
                          if (!response.ok) throw new Error('Erreur génération PDF');
                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `recherche_${search.location || search.id}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        } catch (error) {
                          alert('❌ Erreur lors de la génération du PDF');
                          console.error(error);
                        }
                      }}
                      className="flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                      title="Télécharger en PDF"
                    >
                      <Download size={16} />
                      PDF
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Message si aucune recherche */}
      {searches.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Search className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucune recherche partagée</h3>
          <p className="text-gray-500">
            Les recherches envoyées par les techniciens apparaîtront ici
          </p>
        </div>
      )}
      
      {/* Modal Détails */}
      {isDetailsModalOpen && selectedSearch && (
        <SearchDetailsModal 
          search={selectedSearch} 
          onClose={handleCloseDetailsModal}
        />
      )}
      
      {/* Modal Édition */}
      {isEditModalOpen && selectedSearch && (
        <EditSearchModal 
          search={selectedSearch} 
          onSave={handleSaveEdit}
          onClose={handleCloseEditModal}
        />
      )}
    </div>
  );
};

// ============================================================================
// PROJECTS HUB - Mon Entreprise
// ============================================================================

const ProjectsHub = () => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [searches, setSearches] = useState([]);
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [downloadingReportPdf, setDownloadingReportPdf] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [editReportForm, setEditReportForm] = useState({});
  const [savingReport, setSavingReport] = useState(false);
  // VAL-002 — Demande de correction (Bureau/Admin)
  const [correctionTarget, setCorrectionTarget] = useState(null);
  const [correctionReason, setCorrectionReason] = useState('');
  const [sendingCorrection, setSendingCorrection] = useState(false);
  // VAL-003 — Validation définitive (Bureau/Admin)
  const [validatingReport, setValidatingReport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openFolder, setOpenFolder] = useState(null); // null, 'projects', 'searches', 'reports'
  const [stats, setStats] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProjectDetails, setShowProjectDetails] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedProjectForQuote, setSelectedProjectForQuote] = useState(null);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchesFilterStatus, setSearchesFilterStatus] = useState('ALL'); // ALL | CONVERTED | PENDING
  const [convertedSearches, setConvertedSearches] = useState(new Set());
  const [selectedSearch, setSelectedSearch] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchToConvert, setSearchToConvert] = useState(null);
  const [loadingSearches, setLoadingSearches] = useState(false);
  const [generatingPdfId, setGeneratingPdfId] = useState(null); // Recherche en cours de conversion
  const [newQuote, setNewQuote] = useState({
    title: '',
    amount_ht: '',
    tva: '20',
    valid_until: '',
    due_date: '',
    deadline: '',
    description: '',
    conditions: ''
  });
  const [newProject, setNewProject] = useState({
    name: '',
    client_id: '',
    status: 'RECHERCHE',
    priority: 'NORMAL',
    category: '',
    estimated_value: '',
    start_date: '',
    notes: ''
  });

  useEffect(() => {
    loadClients();
    loadProjects();
    loadSearches();
    loadStats();
    loadReports();
  }, []);

  // ⚡ Realtime — rafraîchir automatiquement quand projets/recherches/clients changent
  useEffect(() => {
    const handler = (e) => {
      const t = e.detail?.table;
      if (t === 'projects') loadProjects();
      if (t === 'searches') loadSearches();
      if (t === 'clients') loadClients();
    };
    window.addEventListener('realtime-update', handler);
    return () => window.removeEventListener('realtime-update', handler);
  }, []);

  // Ouvrir le modal de création quand on arrive sur les projets avec une recherche à convertir
  useEffect(() => {
    if (openFolder === 'projects' && searchToConvert) {
      console.log('Ouverture du modal pour recherche:', searchToConvert.id);
      const timer = setTimeout(() => {
        setShowCreateModal(true);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [openFolder, searchToConvert]);

  const loadProjects = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      setLoading(true);
      const response = await http.get(`${API}/projects`);
      setProjects(response.data.data || []);
    } catch (error) {
      console.error('Erreur chargement projets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReports = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      setReportsLoading(true);
      const response = await http.get(`${API}/mission-reports`);
      setReports(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur chargement comptes-rendus:', error);
      setReports([]);
    } finally {
      setReportsLoading(false);
    }
  };

  const handleDownloadReportPdf = async (report) => {
    if (!report?.id) return;
    try {
      setDownloadingReportPdf(true);
      const response = await http.get(`${API}/mission-reports/${report.id}/pdf`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeTitle = (report.mission_title || 'compte_rendu').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 30);
      link.download = `compte_rendu_${safeTitle}_${report.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur téléchargement PDF compte-rendu:', error);
      alert('Impossible de générer le PDF du compte-rendu');
    } finally {
      setDownloadingReportPdf(false);
    }
  };

  const handleUpdateReport = async () => {
    if (!editingReport?.id) return;
    try {
      setSavingReport(true);
      await http.patch(`${API}/mission-reports/${editingReport.id}`, editReportForm);
      setReports(prev => prev.map(r => r.id === editingReport.id ? { ...r, ...editReportForm } : r));
      setEditingReport(null);
      setEditReportForm({});
    } catch (err) {
      alert('Erreur lors de la sauvegarde : ' + (err?.response?.data?.detail || err.message));
    } finally {
      setSavingReport(false);
    }
  };

  // VAL-002 — Le Bureau/Admin demande une correction au technicien
  const handleRequestCorrection = async () => {
    const reason = (correctionReason || '').trim();
    if (!correctionTarget?.id || !reason || sendingCorrection) return;
    try {
      setSendingCorrection(true);
      const res = await http.patch(`${API}/mission-reports/${correctionTarget.id}/request-correction`, { reason });
      const updated = res.data || {};
      // Mise à jour immédiate des vues (liste + détail ouvert)
      setReports(prev => prev.map(r => r.id === correctionTarget.id ? { ...r, ...updated } : r));
      setSelectedReport(prev => (prev && prev.id === correctionTarget.id ? { ...prev, ...updated } : prev));
      setCorrectionTarget(null);
      setCorrectionReason('');
      await loadReports();
    } catch (err) {
      const status = err?.response?.status;
      const msg = status === 409
        ? "Ce compte-rendu ne peut plus être renvoyé en correction (état modifié). Veuillez rafraîchir."
        : "Impossible de demander une correction. Veuillez réessayer.";
      alert((err?.response?.data?.detail) || msg);
    } finally {
      setSendingCorrection(false);
    }
  };

  // VAL-003 — Le Bureau/Admin valide définitivement le compte-rendu
  const handleValidateReport = async (report) => {
    if (!report?.id || validatingReport) return;
    try {
      setValidatingReport(true);
      const res = await http.patch(`${API}/mission-reports/${report.id}/validate`, {});
      const updated = res.data || {};
      setReports(prev => prev.map(r => r.id === report.id ? { ...r, ...updated } : r));
      setSelectedReport(prev => (prev && prev.id === report.id ? { ...prev, ...updated } : prev));
      await loadReports();
    } catch (err) {
      const status = err?.response?.status;
      const msg = status === 409
        ? "Ce compte-rendu ne peut plus être validé (état modifié). Veuillez rafraîchir."
        : "Impossible de valider le compte-rendu. Veuillez réessayer.";
      alert((err?.response?.data?.detail) || msg);
    } finally {
      setValidatingReport(false);
    }
  };


  const loadSearches = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      setLoadingSearches(true);
      const response = await http.get(`${API}/searches?status=SHARED`);
      const searches = response.data.data || [];
      // Récupérer les infos des utilisateurs pour chaque recherche
      const searchesWithUsers = await Promise.all(
        searches.map(async (search) => {
          if (search.user_id) {
            try {
              const userResponse = await http.get(
                `${API}/users/${search.user_id}`,
                { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
              );
              return { ...search, users: userResponse.data };
            } catch (error) {
              console.error(`❌ Impossible de charger l'utilisateur ${search.user_id}:`, error.response?.status, error.response?.data);
              return search;
            }
          }
          console.warn('⚠️ Recherche sans user_id:', search.id);
          return search;
        })
      );
      
      // Initialiser convertedSearches avec les recherches qui ont déjà un project_id
      const converted = new Set(
        searchesWithUsers
          .filter(search => search.project_id)
          .map(search => search.id)
      );
      setConvertedSearches(converted);
      
      setSearches(searchesWithUsers);
    } catch (error) {
      console.error('Erreur chargement recherches:', error);
    } finally {
      setLoadingSearches(false);
    }
  };

  const loadStats = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await http.get(`${API}/projects/stats/dashboard`);
      setStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  const loadClients = async () => {
    if (!localStorage.getItem('token')) return;
    try {
      const response = await http.get(`${API}/clients`);
      setClients(response.data || []);
    } catch (error) {
      console.error('Erreur chargement clients:', error);
    }
  };

  const handleViewDetails = (search) => {
    setSelectedSearch(search);
    setIsDetailsModalOpen(true);
  };

  const handleEdit = (search) => {
    setSelectedSearch(search);
    setIsEditModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedSearch(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedSearch(null);
    loadSearches();
  };

  const handleSaveEdit = async (searchId, updatedData) => {
    try {
      await http.put(`${API}/searches/${searchId}`, updatedData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      loadSearches();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      throw error;
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!(await confirm('⚠️ Êtes-vous sûr de vouloir supprimer ce projet ?'))) return;
    
    try {
      await http.delete(`${API}/projects/${projectId}`);
      loadProjects();
      loadStats();
      alert('✅ Projet supprimé');
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('❌ Erreur lors de la suppression');
    }
  };

  const handleCreateQuote = (project) => {
    console.log('🔵 Création devis pour projet:', project);
    // Naviguer vers la page de création de devis avec les données du projet
    navigate('/bureau/devis', { 
      state: { 
        fromProject: true,
        project: project,
        client_id: project.client_id 
      } 
    });
  };

  const handleCreateQuoteSubmit = async () => {
    try {
      if (!newQuote.title || !newQuote.amount_ht) {
        alert('⚠️ Veuillez remplir au moins le titre et le montant HT');
        return;
      }

      const amountHT = parseFloat(newQuote.amount_ht);
      const tvaRate = parseFloat(newQuote.tva);
      const amountTTC = amountHT * (1 + tvaRate / 100);

      const quoteData = {
        client_id: selectedProjectForQuote.client_id,
        title: newQuote.title,
        description: `${newQuote.description || ''}\n\n--- Détails ---\nMontant HT: ${amountHT.toFixed(2)}€\nTVA: ${tvaRate}%\nMontant TTC: ${amountTTC.toFixed(2)}€\nValable jusqu'au: ${newQuote.valid_until || 'Non spécifié'}\nDélai: ${newQuote.deadline || 'Non spécifié'}\nConditions: ${newQuote.conditions || 'Aucune'}`,
        amount: amountTTC,
        status: 'DRAFT',
        items: [],
        ...(newQuote.due_date ? { due_date: newQuote.due_date } : {}),
      };

      const response = await http.post(`${API}/quotes`, quoteData);
      
      if (response.data) {
        // Mettre à jour le projet avec le quote_id
        await http.put(`${API}/projects/${selectedProjectForQuote.id}`, {
          quote_id: response.data.id,
          status: 'DEVIS_EN_COURS'
        });

        alert('✅ Devis créé avec succès !');
        setShowQuoteModal(false);
        loadProjects(); // Recharger les projets pour voir le devis lié
      }
    } catch (error) {
      console.error('Erreur création devis:', error);
      alert('❌ Erreur lors de la création du devis: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreateProject = async () => {
    try {
      // Validation : nom requis, client requis SAUF si conversion avec nom/prénom
      const hasClientInfo = searchToConvert && (searchToConvert.nom || searchToConvert.prenom);
      if (!newProject.name || (!newProject.client_id && !hasClientInfo)) {
        alert('Veuillez remplir au moins le nom' + (hasClientInfo ? '' : ' et le client'));
        return;
      }

      // Si c'est une conversion de recherche, utiliser l'endpoint de conversion
      if (searchToConvert) {
        console.log('Conversion de recherche en projet...', searchToConvert.id);
        const response = await http.post(
          `${API}/searches/${searchToConvert.id}/convert-to-project`,
          {
            project_data: {
              ...newProject,
              estimated_value: newProject.estimated_value ? parseFloat(newProject.estimated_value) : null
            }
          },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        
        console.log('Reponse conversion:', response.data);
        
        // Marquer comme converti seulement si le projet a été créé
        if (response.data.project || response.data.project_id) {
          setConvertedSearches(prev => new Set([...prev, searchToConvert.id]));
        }
        
        alert(`✅ ${response.data.message || 'Projet créé avec succès !'}`);
        setSearchToConvert(null); // Réinitialiser
      } else {
        // Création normale de projet
        console.log('Creation nouveau projet...');
        const response = await http.post(`${API}/projects`, {
          ...newProject,
          estimated_value: newProject.estimated_value ? parseFloat(newProject.estimated_value) : null
        });
        console.log('Projet cree:', response.data);
        alert('✅ Projet créé avec succès !');
      }

      setShowCreateModal(false);
      setNewProject({
        name: '',
        client_id: '',
        status: 'RECHERCHE',
        priority: 'NORMAL',
        category: '',
        estimated_value: '',
        start_date: '',
        notes: ''
      });
      
      // Recharger toutes les données
      await loadProjects();
      await loadStats();
      await loadSearches(); // Important: Recharger les recherches pour mettre à jour l'état
    } catch (error) {
      console.error('Erreur creation projet:', error);
      console.error('Details erreur:', error.response?.data);
      alert(`❌ ${error.response?.data?.detail || error.response?.data?.message || 'Erreur lors de la création du projet'}`);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'RECHERCHE': { bg: 'bg-blue-100', text: 'text-blue-800', label: '🔍 Recherche' },
      'DEVIS_EN_COURS': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: '📝 Devis en cours' },
      'DEVIS_VALIDE': { bg: 'bg-green-100', text: 'text-green-800', label: '✅ Devis validé' },
      'CHANTIER_EN_COURS': { bg: 'bg-orange-100', text: 'text-orange-800', label: '🏗️ Chantier en cours' },
      'FACTURE': { bg: 'bg-emerald-100', text: 'text-emerald-800', label: '💰 Facturé' },
      'CHANTIER_TERMINE': { bg: 'bg-purple-100', text: 'text-purple-800', label: '🎉 Terminé' },
      'ARCHIVE': { bg: 'bg-gray-100', text: 'text-gray-600', label: '📦 Archivé' }
    };
    const badge = badges[status] || badges['RECHERCHE'];
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getPriorityIcon = (priority) => {
    if (priority === 'URGENT') return <AlertCircle className="h-4 w-4 text-red-500" />;
    if (priority === 'HIGH') return <TrendingUp className="h-4 w-4 text-orange-500" />;
    return null;
  };

  // Vue Bureau ou Dossier
  if (openFolder === 'projects') {
    return (
      <div className="min-h-screen skyapp-page-bg p-6">
        {/* Header Dossier Projets */}
        <div className="mb-6">
          <button
            onClick={() => setOpenFolder(null)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5" />
            Retour au bureau
          </button>
          
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <FolderOpen className="h-8 w-8 text-blue-500" />
              Projets ({projects.length})
            </h1>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
            >
              <Plus className="h-5 w-5" />
              Nouveau Projet
            </button>
          </div>
        </div>

        {/* Barre de recherche dans Projets */}
        <div className="mb-4 max-w-xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher dans les projets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Liste des Projets */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {projects.filter(project => {
            if (!searchTerm) return true;
            const search = searchTerm.toLowerCase();
            const client = clients.find(c => c.id === project.client_id);
            const clientName = client ? `${client.nom} ${client.prenom}`.toLowerCase() : '';
            return (
              project.name?.toLowerCase().includes(search) ||
              project.project_number?.toLowerCase().includes(search) ||
              clientName.includes(search) ||
              project.description?.toLowerCase().includes(search)
            );
          }).map(project => {
            const client = clients.find(c => c.id === project.client_id);
            return (
              <div key={project.id} className="bg-white rounded-lg border-2 border-gray-200 hover:border-blue-500 transition p-4 shadow-sm">
                {/* En-tête projet */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-5 w-5 text-blue-500" />
                      <span className="text-xs font-mono text-gray-500">{project.project_number}</span>
                    </div>
                    <h3 className="font-bold text-gray-900 text-sm line-clamp-2">{project.name}</h3>
                    {client && (
                      <p className="text-xs text-gray-600 mt-1">
                        👤 {client.nom} {client.prenom}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleDeleteProject(project.id)}
                    className="text-gray-400 hover:text-red-500 transition"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* Statut */}
                <div className="mb-3">
                  {getStatusBadge(project.status)}
                </div>

                {/* Montant */}
                {project.estimated_value && (
                  <div className="text-sm text-gray-600 mb-3">
                    💰 {parseFloat(project.estimated_value).toLocaleString('fr-FR')}€
                  </div>
                )}

                {/* Timeline */}
                <div className="flex items-center gap-1 mb-3 text-xs">
                  <div className={`w-3 h-3 rounded-full ${project.search_id ? 'bg-green-500' : 'bg-gray-300'}`} title="Recherche"></div>
                  <div className="h-0.5 flex-1 bg-gray-200"></div>
                  <div className="w-3 h-3 rounded-full bg-blue-500" title="Projet (actuel)"></div>
                  <div className="h-0.5 flex-1 bg-gray-200"></div>
                  <div className={`w-3 h-3 rounded-full ${project.quote_id ? 'bg-green-500' : 'bg-gray-300'}`} title="Devis"></div>
                  <div className="h-0.5 flex-1 bg-gray-200"></div>
                  <div className={`w-3 h-3 rounded-full ${project.worksite_id ? 'bg-green-500' : 'bg-gray-300'}`} title="Chantier"></div>
                  <div className="h-0.5 flex-1 bg-gray-200"></div>
                  <div className={`w-3 h-3 rounded-full ${project.report_id ? 'bg-green-500' : 'bg-gray-300'}`} title="Rapport"></div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setSelectedProject(project);
                      setShowProjectDetails(true);
                    }}
                    className="flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 transition"
                    title="Voir détails et modifier"
                  >
                    <Eye className="h-3 w-3" />
                    Détails
                  </button>
                  {!project.quote_id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!project.client_id) {
                          alert('⚠️ Ce projet n\'a pas de client assigné. Veuillez d\'abord ajouter un client.');
                          return;
                        }
                        handleCreateQuote(project);
                      }}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition"
                    >
                      <FileText className="h-3 w-3" />
                      Créer Devis
                    </button>
                  )}
                  {project.quote_id && !project.worksite_id && (
                    <button
                      className="flex-1 px-3 py-2 bg-orange-500 text-white text-xs rounded hover:bg-orange-600 transition"
                    >
                      🏗️ Créer Chantier
                    </button>
                  )}
                  {project.worksite_id && !project.report_id && (
                    <button
                      className="flex-1 px-3 py-2 bg-purple-500 text-white text-xs rounded hover:bg-purple-600 transition"
                    >
                      📄 Générer Rapport
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {projects.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <FolderOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucun projet dans ce dossier</p>
          </div>
        )}

        {/* Modal Création Projet */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Plus className="h-6 w-6" />
                  Nouveau Projet
                </h2>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setSearchToConvert(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Nom du projet */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom du projet <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newProject.name}
                    onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: Rénovation cuisine M. Dupont"
                  />
                </div>

                {/* Client */}
                {searchToConvert && (searchToConvert.nom || searchToConvert.prenom) ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client (non récurrent)
                    </label>
                    <div className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-blue-50 text-gray-700">
                      👤 {searchToConvert.prenom || ''} {searchToConvert.nom || ''}
                      {searchToConvert.location && (
                        <span className="text-sm text-gray-500 ml-2">• {searchToConvert.location}</span>
                      )}
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      ℹ️ Ce client ne sera pas ajouté à votre liste de clients récurrents
                    </p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Client <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={newProject.client_id}
                      onChange={(e) => setNewProject({...newProject, client_id: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Sélectionner un client</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.nom} {client.prenom} {client.entreprise ? `- ${client.entreprise}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Status et Priorité */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Statut
                    </label>
                    <select
                      value={newProject.status}
                      onChange={(e) => setNewProject({...newProject, status: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="RECHERCHE">🔍 Recherche</option>
                      <option value="DEVIS_EN_COURS">📝 Devis en cours</option>
                      <option value="DEVIS_VALIDE">✅ Devis validé</option>
                      <option value="CHANTIER_EN_COURS">🏗️ Chantier en cours</option>
                      <option value="FACTURE">💰 Facturé</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priorité
                    </label>
                    <select
                      value={newProject.priority}
                      onChange={(e) => setNewProject({...newProject, priority: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="LOW">🟢 Basse</option>
                      <option value="NORMAL">🔵 Normale</option>
                      <option value="HIGH">🟠 Haute</option>
                      <option value="URGENT">🔴 Urgente</option>
                    </select>
                  </div>
                </div>

                {/* Catégorie */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Catégorie
                  </label>
                  <input
                    type="text"
                    value={newProject.category}
                    onChange={(e) => setNewProject({...newProject, category: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: Électricité, Plomberie, Général..."
                  />
                </div>

                {/* Valeur estimée et Date de début */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Montant estimé (€)
                    </label>
                    <input
                      type="number"
                      value={newProject.estimated_value}
                      onChange={(e) => setNewProject({...newProject, estimated_value: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date de début estimée
                    </label>
                    <input
                      type="date"
                      value={newProject.start_date}
                      onChange={(e) => setNewProject({...newProject, start_date: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={newProject.notes}
                    onChange={(e) => setNewProject({...newProject, notes: e.target.value})}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Notes supplémentaires..."
                  />
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="p-6 border-t border-gray-200 flex gap-3 justify-end sticky bottom-0 bg-white">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setSearchToConvert(null);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreateProject}
                  className="flex items-center gap-2 px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition"
                >
                  <Check className="h-5 w-5" />
                  Créer le projet
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Détails Projet */}
        {showProjectDetails && selectedProject && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Eye className="h-6 w-6" />
                  Détails du Projet
                </h2>
                <button
                  onClick={() => {
                    setShowProjectDetails(false);
                    setSelectedProject(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Informations principales */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500">Numéro de projet</label>
                      <p className="text-sm font-mono text-gray-900">{selectedProject.project_number}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Statut</label>
                      <div className="mt-1">{getStatusBadge(selectedProject.status)}</div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Priorité</label>
                      <p className="text-sm text-gray-900 flex items-center gap-1">
                        {getPriorityIcon(selectedProject.priority)}
                        {selectedProject.priority}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500">Catégorie</label>
                      <p className="text-sm text-gray-900">{selectedProject.category || 'Non définie'}</p>
                    </div>
                  </div>
                </div>

                {/* Nom du projet - Éditable */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nom du projet</label>
                  <input
                    type="text"
                    value={selectedProject.name}
                    onChange={(e) => setSelectedProject({...selectedProject, name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Client */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Client</label>
                  <select
                    value={selectedProject.client_id || ''}
                    onChange={(e) => setSelectedProject({...selectedProject, client_id: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Sélectionner un client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.nom} {client.prenom} {client.is_recurring === false ? '(Non récurrent)' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Statut - Éditable */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                  <select
                    value={selectedProject.status}
                    onChange={(e) => setSelectedProject({...selectedProject, status: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="RECHERCHE">🔍 Recherche</option>
                    <option value="DEVIS_EN_COURS">📝 Devis en cours</option>
                    <option value="DEVIS_VALIDE">✅ Devis validé</option>
                    <option value="CHANTIER_EN_COURS">🏗️ Chantier en cours</option>
                    <option value="CHANTIER_TERMINE">🎉 Chantier terminé</option>
                    <option value="FACTURE">💰 Facturé</option>
                    <option value="ARCHIVE">📦 Archivé</option>
                  </select>
                </div>

                {/* Priorité - Éditable */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priorité</label>
                  <select
                    value={selectedProject.priority}
                    onChange={(e) => setSelectedProject({...selectedProject, priority: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="LOW">Faible</option>
                    <option value="NORMAL">Normale</option>
                    <option value="HIGH">Haute</option>
                    <option value="URGENT">Urgente</option>
                  </select>
                </div>

                {/* Catégorie - Éditable */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
                  <input
                    type="text"
                    value={selectedProject.category || ''}
                    onChange={(e) => setSelectedProject({...selectedProject, category: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: Infiltration, Terrain, etc."
                  />
                </div>

                {/* Montant estimé - Éditable */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Montant estimé (€)</label>
                  <input
                    type="number"
                    value={selectedProject.estimated_value || ''}
                    onChange={(e) => setSelectedProject({...selectedProject, estimated_value: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="2000"
                  />
                </div>

                {/* Date de début - Éditable */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date de début estimée</label>
                  <input
                    type="date"
                    value={selectedProject.start_date || ''}
                    onChange={(e) => setSelectedProject({...selectedProject, start_date: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Timeline du cycle de vie */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Cycle de vie du projet</label>
                  <div className="flex items-center gap-1">
                    {/* Recherche/Infiltration */}
                    <button
                      onClick={async () => {
                        if (selectedProject.search_id) {
                          try {
                            const response = await http.get(
                              `${API}/searches/${selectedProject.search_id}`,
                              { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                            );
                            setShowProjectDetails(false);
                            setSelectedProject(null);
                            setOpenFolder('searches');
                            setSelectedSearch(response.data);
                            setIsDetailsModalOpen(true);
                          } catch (error) {
                            console.error('Erreur chargement recherche:', error);
                            alert('❌ Impossible de charger la recherche');
                          }
                        }
                      }}
                      disabled={!selectedProject.search_id}
                      className={`flex-1 flex flex-col items-center gap-2 p-3 rounded-lg transition ${
                        selectedProject.search_id 
                          ? 'bg-green-50 border-2 border-green-500 hover:bg-green-100 cursor-pointer' 
                          : 'bg-gray-50 border-2 border-gray-200 cursor-not-allowed'
                      }`}
                    >
                      <Search className={`h-5 w-5 ${selectedProject.search_id ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className="text-xs font-medium text-center">Recherche</span>
                    </button>
                    <div className="text-gray-400">→</div>
                    
                    {/* Projet (Toujours actif) */}
                    <div className="flex-1 flex flex-col items-center gap-2 p-3 rounded-lg bg-blue-50 border-2 border-blue-500">
                      <Briefcase className="h-5 w-5 text-blue-600" />
                      <span className="text-xs font-medium text-center">Projet</span>
                    </div>
                    <div className="text-gray-400">→</div>
                    
                    {/* Devis */}
                    <button
                      onClick={() => {
                        if (selectedProject.quote_id) {
                          setShowProjectDetails(false);
                          setSelectedProject(null);
                          setMenuSection('devis');
                          alert('📄 Redirection vers la section Devis');
                        }
                      }}
                      disabled={!selectedProject.quote_id}
                      className={`flex-1 flex flex-col items-center gap-2 p-3 rounded-lg transition ${
                        selectedProject.quote_id 
                          ? 'bg-green-50 border-2 border-green-500 hover:bg-green-100 cursor-pointer' 
                          : 'bg-gray-50 border-2 border-gray-200 cursor-not-allowed'
                      }`}
                    >
                      <FileText className={`h-5 w-5 ${selectedProject.quote_id ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className="text-xs font-medium text-center">Devis</span>
                    </button>
                    <div className="text-gray-400">→</div>
                    
                    {/* Chantier */}
                    <button
                      onClick={() => {
                        if (selectedProject.worksite_id) {
                          setShowProjectDetails(false);
                          setSelectedProject(null);
                          setMenuSection('chantiers');
                          alert('🏗️ Redirection vers la section Chantiers');
                        }
                      }}
                      disabled={!selectedProject.worksite_id}
                      className={`flex-1 flex flex-col items-center gap-2 p-3 rounded-lg transition ${
                        selectedProject.worksite_id 
                          ? 'bg-green-50 border-2 border-green-500 hover:bg-green-100 cursor-pointer' 
                          : 'bg-gray-50 border-2 border-gray-200 cursor-not-allowed'
                      }`}
                    >
                      <Briefcase className={`h-5 w-5 ${selectedProject.worksite_id ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className="text-xs font-medium text-center">Chantier</span>
                    </button>
                    <div className="text-gray-400">→</div>
                    
                    {/* Rapport */}
                    <button
                      onClick={() => {
                        if (selectedProject.report_id) {
                          alert('📄 Navigation vers les rapports à venir');
                          // TODO: Implémenter la navigation vers les rapports
                        }
                      }}
                      disabled={!selectedProject.report_id}
                      className={`flex-1 flex flex-col items-center gap-2 p-3 rounded-lg transition ${
                        selectedProject.report_id 
                          ? 'bg-green-50 border-2 border-green-500 hover:bg-green-100 cursor-pointer' 
                          : 'bg-gray-50 border-2 border-gray-200 cursor-not-allowed'
                      }`}
                    >
                      <FileText className={`h-5 w-5 ${selectedProject.report_id ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className="text-xs font-medium text-center">Rapport</span>
                    </button>
                  </div>
                </div>

                {/* Dates de création/modification */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-xs text-gray-600">
                    <div>
                      <span className="font-medium">Créé le:</span> {new Date(selectedProject.created_at).toLocaleString('fr-FR')}
                    </div>
                    {selectedProject.updated_at && (
                      <div>
                        <span className="font-medium">Modifié le:</span> {new Date(selectedProject.updated_at).toLocaleString('fr-FR')}
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowProjectDetails(false);
                      setSelectedProject(null);
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await http.put(
                          `${API}/projects/${selectedProject.id}`,
                          selectedProject,
                          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                        );
                        alert('✅ Projet modifié avec succès');
                        await loadProjects();
                        setShowProjectDetails(false);
                        setSelectedProject(null);
                      } catch (error) {
                        console.error('Erreur modification:', error);
                        alert('❌ Erreur lors de la modification');
                      }
                    }}
                    className="flex items-center gap-2 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                  >
                    <Check className="h-5 w-5" />
                    Enregistrer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (openFolder === 'searches') {
    return (
      <div className="min-h-screen skyapp-page-bg p-6">
        {/* Header Dossier Recherches */}
        <div className="mb-6">
          <button
            onClick={() => setOpenFolder(null)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5" />
            Retour au bureau
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Layers className="h-8 w-8 text-orange-500" />
            Recherches d'Infiltration ({searches.length})
          </h1>
          <p className="text-gray-600 mt-1">Recherches d'infiltration partagées par les techniciens</p>
        </div>

        {/* Spinner chargement initial */}
        {loadingSearches && (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="relative">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-100 border-t-orange-500"></div>
              <div className="absolute inset-0 rounded-full animate-ping opacity-20 bg-orange-400" style={{animationDuration:'1.5s'}}></div>
            </div>
            <p className="text-gray-500 font-medium">Chargement des recherches…</p>
          </div>
        )}

        {/* Barre de recherche + filtres */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="relative max-w-xl flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher dans les recherches..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Filtre statut */}
          <select
            value={searchesFilterStatus}
            onChange={(e) => setSearchesFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="ALL">Tous les statuts</option>
            <option value="CONVERTED">🎯 Converties en projet</option>
            <option value="PENDING">⏳ Non converties</option>
          </select>

          {searchesFilterStatus !== 'ALL' && (
            <button
              onClick={() => setSearchesFilterStatus('ALL')}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Réinitialiser
            </button>
          )}
        </div>

        {/* Liste des Recherches */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {searches.filter(search => {
            // Page exclusivement dédiée aux recherches d'infiltration (pas le terrain)
            const type = search.search_type?.toUpperCase();
            if (type && type !== 'INFILTRATION') return false;
            if (searchTerm) {
              const searchLower = searchTerm.toLowerCase();
              const matchesSearch = (
                search.location?.toLowerCase().includes(searchLower) ||
                search.search_type?.toLowerCase().includes(searchLower) ||
                search.description?.toLowerCase().includes(searchLower) ||
                search.nom?.toLowerCase().includes(searchLower) ||
                search.prenom?.toLowerCase().includes(searchLower) ||
                search.users?.first_name?.toLowerCase().includes(searchLower) ||
                search.users?.last_name?.toLowerCase().includes(searchLower)
              );
              if (!matchesSearch) return false;
            }
            if (searchesFilterStatus !== 'ALL') {
              const converted = !!search.project_id || convertedSearches.has(search.id);
              if (searchesFilterStatus === 'CONVERTED' && !converted) return false;
              if (searchesFilterStatus === 'PENDING' && converted) return false;
            }
            return true;
          }).map(search => {
            const searchType = search.search_type?.toUpperCase() || 'TERRAIN';
            const isInfiltration = searchType === 'INFILTRATION';
            const isConverted = !!search.project_id || convertedSearches.has(search.id);
            
            return (
              <div 
                key={search.id} 
                className={`group bg-white rounded-2xl border hover:shadow-2xl transition-all duration-300 overflow-hidden hover:-translate-y-1 ${
                  isInfiltration ? 'border-orange-200/70' : 'border-blue-200/70'
                }`}
              >
                {/* Barre d'accent SkyApp */}
                <div
                  className="h-1.5 w-full"
                  style={{
                    background: isInfiltration
                      ? 'linear-gradient(90deg, #FB923C 0%, #F97316 100%)'
                      : 'linear-gradient(90deg, #2563EB 0%, #7C3AED 100%)'
                  }}
                />

                <div className="p-5">
                {/* Header avec type et date */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-white shadow-sm ${
                      isInfiltration
                        ? 'bg-gradient-to-br from-orange-400 to-orange-500'
                        : 'bg-gradient-to-br from-blue-600 to-violet-600'
                    }`}>
                      {isInfiltration ? <Layers size={18} /> : <MapPin size={18} />}
                    </div>
                    <span className={`text-xs font-bold px-3 py-1.5 rounded-full shadow-sm ${
                      isInfiltration ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {isInfiltration ? 'Infiltration' : 'Terrain'}
                    </span>
                    <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-violet-100 text-violet-700 flex items-center gap-1.5 shadow-sm">
                      <User size={13} />
                      {search.users ? `${search.users.first_name} ${search.users.last_name}` : 'Technicien'}
                    </span>
                  </div>
                  <span className="text-xs font-medium text-gray-500 bg-gray-50 px-3 py-1.5 rounded-full whitespace-nowrap">
                    {new Date(search.shared_at || search.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>

                {/* Client info */}
                {(search.nom || search.prenom) && (
                  <div className={`mb-3 p-3 rounded-xl border ${
                    isInfiltration ? 'bg-orange-50/70 border-orange-100' : 'bg-blue-50/70 border-blue-100'
                  }`}>
                    <p className={`text-sm font-bold flex items-center gap-2 ${
                      isInfiltration ? 'text-orange-600' : 'text-blue-900'
                    }`}>
                      <User size={16} className={isInfiltration ? 'text-orange-500' : 'text-blue-600'} />
                      {search.prenom} {search.nom}
                    </p>
                  </div>
                )}

                {/* Adresse */}
                <div className="mb-3 bg-gray-50 p-3 rounded-xl border border-gray-100">
                  <h3 className="font-bold text-gray-900 text-sm flex items-start gap-2">
                    <MapPin size={16} className={`mt-0.5 flex-shrink-0 ${
                      isInfiltration ? 'text-orange-500' : 'text-blue-600'
                    }`} strokeWidth={2.5} />
                    <span>{search.location || 'Sans adresse'}</span>
                  </h3>
                </div>

                {/* Description */}
                {search.description && (
                  <div className="mb-3 px-1">
                    <p className="text-sm text-gray-600 italic line-clamp-2">{search.description}</p>
                  </div>
                )}

                {/* Observations */}
                {search.observations && cleanObservations(search.observations) && (
                  <div className="mb-4 p-3.5 bg-gradient-to-br from-amber-50 to-orange-50/50 border border-amber-200/70 rounded-xl">
                    <p className="text-xs font-bold text-amber-700 mb-1.5 flex items-center gap-1.5 uppercase tracking-wide">
                      <span className="text-base">📝</span>
                      Observations
                    </p>
                    <p className="text-sm text-gray-700 line-clamp-2 leading-relaxed">{cleanObservations(search.observations)}</p>
                  </div>
                )}

                {/* Boutons d'action */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => handleViewDetails(search)}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl transition-all text-sm font-bold shadow-md hover:shadow-lg transform hover:scale-105 text-white ${
                      isInfiltration 
                        ? 'bg-gradient-to-r from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600' 
                        : 'bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700'
                    }`}
                  >
                    <Eye size={16} strokeWidth={2.5} />
                    Détails
                  </button>
                  <button
                    onClick={() => !isConverted && handleEdit(search)}
                    disabled={isConverted}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl transition-all text-sm font-bold shadow-md hover:shadow-lg ${
                      isConverted
                        ? 'bg-gray-300 text-gray-400 cursor-not-allowed'
                        : 'bg-slate-700 hover:bg-slate-800 text-white transform hover:scale-105'
                    }`}
                    title={isConverted ? 'Projet déjà créé - modification désactivée' : 'Modifier la recherche'}
                  >
                    <Edit2 size={16} strokeWidth={2.5} />
                    Modifier
                  </button>
                  <button
                    onClick={async () => {
                      setGeneratingPdfId(search.id);
                      try {
                        const response = await fetch(`${API}/searches/${search.id}/pdf`, {
                          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                        });
                        if (!response.ok) {
                          const errorText = await response.text();
                          throw new Error(`Erreur génération PDF: ${response.status} - ${errorText}`);
                        }
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `recherche_${search.location || search.id}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      } catch (error) {
                        console.error('❌ ERREUR PDF:', error);
                        alert(`❌ Erreur lors de la génération du PDF: ${error.message}`);
                      } finally {
                        setGeneratingPdfId(null);
                      }
                    }}
                    disabled={generatingPdfId === search.id}
                    className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl transition-all text-sm font-bold shadow-md ${
                      generatingPdfId === search.id
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white cursor-wait scale-95'
                        : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 hover:shadow-lg transform hover:scale-105'
                    }`}
                    title="Télécharger en PDF"
                  >
                    {generatingPdfId === search.id ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
                        </svg>
                        En création…
                      </>
                    ) : (
                      <>
                        <Download size={16} strokeWidth={2.5} />
                        PDF
                      </>
                    )}
                  </button>
                </div>

                {/* Bouton Transformer en Projet / Projet Créé */}
                <button
                  onClick={() => {
                    if (isConverted) return; // Désactiver si déjà converti
                    
                    console.log('Bouton Transformer en Projet clique pour recherche:', search.id);
                    
                    // Stocker la recherche à convertir et pré-remplir le formulaire
                    setSearchToConvert(search);
                    
                    // Nettoyer les observations du formatage Markdown
                    const cleanedDescription = search.description || '';
                    const cleanedObservations = cleanObservations(search.observations || '');
                    
                    setNewProject({
                      name: search.location || 'Projet sans nom',
                      client_id: '',
                      status: 'RECHERCHE',
                      priority: 'NORMAL',
                      category: search.search_type === 'TERRAIN' ? 'Recherche terrain' : 'Infiltration',
                      estimated_value: '',
                      start_date: '',
                      notes: cleanedObservations || cleanedDescription || ''
                    });
                    
                    console.log('Redirection vers projets...');
                    // Redirection vers les projets (le useEffect ouvrira le modal automatiquement)
                    setOpenFolder('projects');
                  }}
                  disabled={isConverted}
                  className={`w-full py-3 px-4 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2.5 shadow-lg hover:shadow-xl text-white ${
                    isConverted
                      ? 'bg-green-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 via-violet-600 to-orange-400 hover:opacity-90 cursor-pointer transform hover:scale-105'
                  }`}
                >
                  {isConverted ? (
                    <>
                      <CheckCircle size={18} strokeWidth={2.5} />
                      Projet Créé
                    </>
                  ) : (
                    <>
                      <FolderOpen size={18} strokeWidth={2.5} />
                      Transformer en Projet
                    </>
                  )}
                </button>
                </div>
              </div>
            );
          })}
        </div>

        {searches.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucune recherche partagée</p>
          </div>
        )}

        {/* Modal Détails */}
        {isDetailsModalOpen && selectedSearch && (
          <SearchDetailsModal 
            search={selectedSearch} 
            onClose={handleCloseDetailsModal}
          />
        )}
        
        {/* Modal Édition - Utilise le même formulaire que la création */}
        {isEditModalOpen && selectedSearch && (
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl w-[98vw] h-[98vh] overflow-y-auto p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-200">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-900">Modifier la Recherche</h2>
                  <p className="text-sm text-gray-600 mt-1">Éditez les informations de la recherche</p>
                </div>
                <button
                  onClick={handleCloseEditModal}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="h-6 w-6 text-gray-600" />
                </button>
              </div>
              
              {/* Formulaire de recherche réutilisé */}
              <SearchForm 
                searchType={selectedSearch.search_type || 'TERRAIN'}
                initialSearch={selectedSearch}
                onSearchLoaded={() => console.log('✅ Recherche chargée pour édition')}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Vue Comptes-rendus de chantier
  if (openFolder === 'reports') {
    const filteredReports = reports.filter(r => {
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return (
        (r.mission_title || '').toLowerCase().includes(q) ||
        (r.client_name || '').toLowerCase().includes(q) ||
        (r.technician_name || '').toLowerCase().includes(q) ||
        (r.works_performed || '').toLowerCase().includes(q)
      );
    });
    return (
      <div className="min-h-screen skyapp-page-bg p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => { setOpenFolder(null); setSelectedReport(null); }}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-5 w-5" />
            Retour au bureau
          </button>

          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FileText className="h-8 w-8 text-green-500" />
            Comptes-rendus de chantier ({reports.length})
          </h1>
          <p className="text-gray-600 mt-1">Tous les comptes-rendus rédigés par les techniciens</p>
        </div>

        {/* Barre de recherche */}
        <div className="mb-4 max-w-xl">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un compte-rendu (chantier, client, technicien...)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {reportsLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-green-500" />
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">
              {reports.length === 0 ? 'Aucun compte-rendu pour le moment' : 'Aucun résultat'}
            </p>
            <p className="text-sm text-gray-400 mt-1">
              Les comptes-rendus rédigés par les techniciens apparaîtront ici
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredReports.map(report => {
              const created = report.created_at ? new Date(report.created_at) : null;
              const photoCount = (report.photos_before_urls?.length || 0) + (report.photos_after_urls?.length || 0);
              return (
                <div
                  key={report.id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 overflow-hidden flex flex-col"
                >
                  {/* Barre accent SkyApp */}
                  <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg, #2563EB 0%, #7C3AED 55%, #FB923C 100%)' }} />

                  <div className="p-5 flex-1 flex flex-col">
                    {/* En-tête */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #DBEAFE, #EDE9FE)' }}>
                        <FileText className="h-5 w-5" style={{ color: '#2563EB' }} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-bold text-gray-900 truncate text-sm leading-tight">
                          {report.mission_title || 'Chantier'}
                        </h3>
                        {report.client_name && (
                          <p className="text-xs text-gray-500 truncate mt-0.5 flex items-center gap-1">
                            <span className="text-gray-400">👤</span> {report.client_name}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Extrait travaux */}
                    {report.works_performed && (
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3 flex-1">{report.works_performed}</p>
                    )}

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {(() => { const m = getReportStatusMeta(report.report_status); return (
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${m.cls}`}>{m.label}</span>
                      ); })()}
                      {report.duration_hours != null && report.duration_hours !== '' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">{report.duration_hours}h</span>
                      )}
                      {photoCount > 0 && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 font-medium">📷 {photoCount} photo{photoCount > 1 ? 's' : ''}</span>
                      )}
                      {report.issues_encountered && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 font-medium">⚠️ Problème signalé</span>
                      )}
                    </div>

                    {/* Pied */}
                    <div className="text-xs text-gray-400 flex items-center justify-between mb-4 pt-3 border-t border-gray-100">
                      <span className="truncate flex items-center gap-1">🧑‍🔧 {report.technician_name}</span>
                      {created && (
                        <span className="flex-shrink-0 ml-2">{created.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      )}
                    </div>

                    {/* Boutons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedReport(report)}
                        className="flex-1 text-xs font-semibold py-2 px-3 rounded-xl bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 transition-colors"
                      >
                        Voir
                      </button>
                      {report.report_status !== 'validated' && (
                      <button
                        onClick={() => {
                          setEditingReport(report);
                          setEditReportForm({
                            works_performed: report.works_performed || '',
                            materials_used: report.materials_used || '',
                            duration_hours: report.duration_hours ?? '',
                            observations: report.observations || '',
                            issues_encountered: report.issues_encountered || '',
                          });
                        }}
                        className="flex-1 text-xs font-semibold py-2 px-3 rounded-xl border border-violet-200 text-violet-700 bg-violet-50 hover:bg-violet-100 transition-colors"
                      >
                        Modifier
                      </button>
                      )}
                      <button
                        onClick={() => handleDownloadReportPdf(report)}
                        disabled={downloadingReportPdf}
                        className="flex-1 text-xs font-semibold py-2 px-3 rounded-xl text-white transition-colors disabled:opacity-60"
                        style={{ background: 'linear-gradient(90deg, #2563EB, #7C3AED)' }}
                      >
                        {downloadingReportPdf ? '...' : '⬇ PDF'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Modal édition compte-rendu */}
        {editingReport && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setEditingReport(null)}>
            <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="p-5 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
                <div>
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#2563EB,#7C3AED)' }}>
                      <FileText className="h-4 w-4 text-white" />
                    </span>
                    Modifier le compte-rendu
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{editingReport.mission_title}</p>
                </div>
                <button onClick={() => setEditingReport(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Durée (heures)</label>
                  <input
                    type="number" min="0" step="0.5"
                    value={editReportForm.duration_hours}
                    onChange={e => setEditReportForm(p => ({ ...p, duration_hours: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                </div>
                {[
                  { key: 'works_performed', label: 'Travaux réalisés' },
                  { key: 'materials_used', label: 'Matériaux utilisés' },
                  { key: 'observations', label: 'Observations' },
                  { key: 'issues_encountered', label: 'Problèmes rencontrés' },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
                    <textarea
                      rows={3}
                      value={editReportForm[key]}
                      onChange={e => setEditReportForm(p => ({ ...p, [key]: e.target.value }))}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none"
                    />
                  </div>
                ))}
              </div>
              <div className="p-5 pt-0 flex gap-3">
                <button onClick={() => setEditingReport(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50">Annuler</button>
                <button
                  onClick={handleUpdateReport}
                  disabled={savingReport}
                  className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-60 transition-all"
                  style={{ background: 'linear-gradient(90deg,#2563EB,#7C3AED)' }}
                >
                  {savingReport ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* VAL-002 — Modale : demander une correction */}
        {correctionTarget && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => { if (!sendingCorrection) { setCorrectionTarget(null); setCorrectionReason(''); } }}>
            <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl" onClick={e => e.stopPropagation()}>
              <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Demander une correction
                </h2>
                <button onClick={() => { if (!sendingCorrection) { setCorrectionTarget(null); setCorrectionReason(''); } }} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-sm text-gray-600">Indiquez au technicien les éléments à corriger dans le compte rendu.</p>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Motif de la correction <span className="text-red-500">*</span></label>
                  <textarea
                    rows={4}
                    maxLength={2000}
                    value={correctionReason}
                    onChange={e => setCorrectionReason(e.target.value)}
                    placeholder="Exemple : merci de préciser la zone contrôlée et d’ajouter une photo de la façade nord."
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                  />
                  <p className="text-[11px] text-gray-400 mt-1 text-right">{correctionReason.length}/2000</p>
                </div>
              </div>
              <div className="p-5 pt-0 flex gap-3">
                <button
                  onClick={() => { setCorrectionTarget(null); setCorrectionReason(''); }}
                  disabled={sendingCorrection}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 disabled:opacity-60"
                >Annuler</button>
                <button
                  onClick={handleRequestCorrection}
                  disabled={sendingCorrection || !correctionReason.trim()}
                  className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold disabled:opacity-50 transition-all"
                  style={{ background: 'linear-gradient(90deg,#F59E0B,#D97706)' }}
                >{sendingCorrection ? 'Envoi...' : 'Envoyer la demande'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal détail compte-rendu */}
        {selectedReport && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedReport(null)}>
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 flex items-start justify-between sticky top-0 bg-white z-10">
                <div className="min-w-0">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <FileText className="h-6 w-6 text-green-600" />
                    Compte-rendu de chantier
                  </h2>
                  <p className="text-gray-500 mt-1 truncate">{selectedReport.mission_title || 'Chantier'}</p>
                  {(() => { const m = getReportStatusMeta(selectedReport.report_status); return (
                    <span className={`inline-block mt-2 text-xs px-2.5 py-1 rounded-full border font-semibold ${m.cls}`}>{m.label}</span>
                  ); })()}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {['submitted', 'resubmitted'].includes(selectedReport.report_status) && (
                    <button
                      onClick={() => handleValidateReport(selectedReport)}
                      disabled={validatingReport}
                      className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                      <CheckCircle className="h-4 w-4" />
                      <span className="hidden sm:inline">{validatingReport ? 'Validation...' : 'Valider'}</span>
                    </button>
                  )}
                  {['submitted', 'resubmitted'].includes(selectedReport.report_status) && (
                    <button
                      onClick={() => { setCorrectionTarget(selectedReport); setCorrectionReason(''); }}
                      className="flex items-center gap-2 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition-colors"
                    >
                      <AlertTriangle className="h-4 w-4" />
                      <span className="hidden sm:inline">Demander une correction</span>
                      <span className="sm:hidden">Correction</span>
                    </button>
                  )}
                  <button
                    onClick={() => handleDownloadReportPdf(selectedReport)}
                    disabled={downloadingReportPdf}
                    className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors"
                  >
                    {downloadingReportPdf ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    <span className="hidden sm:inline">PDF</span>
                  </button>
                  <button onClick={() => setSelectedReport(null)} className="text-gray-400 hover:text-gray-600">
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-5">
                {/* VAL-002 — Bandeau correction demandée */}
                {selectedReport.report_status === 'correction_requested' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <p className="text-sm font-bold text-amber-800 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" /> Correction demandée
                    </p>
                    {selectedReport.correction_reason && (
                      <p className="text-sm text-amber-900 mt-1 whitespace-pre-wrap">« {selectedReport.correction_reason} »</p>
                    )}
                    <p className="text-xs text-amber-700 mt-2">
                      {selectedReport.correction_requested_by_name ? `Demandée par ${selectedReport.correction_requested_by_name}` : 'Demandée'}
                      {selectedReport.correction_requested_at ? ` le ${new Date(selectedReport.correction_requested_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
                    </p>
                    <p className="text-xs text-amber-600 mt-1 italic">En attente du renvoi corrigé par le technicien.</p>
                  </div>
                )}
                {selectedReport.report_status === 'resubmitted' && selectedReport.report_resubmitted_at && (
                  <div className="bg-violet-50 border border-violet-200 rounded-xl p-3">
                    <p className="text-sm text-violet-800">
                      ✅ Compte-rendu corrigé et renvoyé le {new Date(selectedReport.report_resubmitted_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}.
                    </p>
                  </div>
                )}
                {selectedReport.report_status === 'validated' && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                    <p className="text-sm font-semibold text-green-800 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" /> Compte-rendu validé
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      {selectedReport.report_validated_by_name ? `Validé par ${selectedReport.report_validated_by_name}` : 'Validé'}
                      {selectedReport.report_validated_at ? ` le ${new Date(selectedReport.report_validated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
                    </p>
                  </div>
                )}

                {/* Métadonnées */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedReport.worksite_reference && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs font-semibold text-gray-500 mb-1">Réf. chantier</p>
                      <p className="text-sm font-medium text-gray-900">{selectedReport.worksite_reference}</p>
                    </div>
                  )}
                  {selectedReport.quote_number && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs font-semibold text-gray-500 mb-1">Réf. devis</p>
                      <p className="text-sm font-medium text-gray-900">{selectedReport.quote_number}</p>
                    </div>
                  )}
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs font-semibold text-gray-500 mb-1">Technicien</p>
                    <p className="text-sm font-medium text-gray-900">🧑‍🔧 {selectedReport.technician_name}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs font-semibold text-gray-500 mb-1">Date</p>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedReport.created_at ? new Date(selectedReport.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </p>
                  </div>
                  {selectedReport.client_name && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs font-semibold text-gray-500 mb-1">Client</p>
                      <p className="text-sm font-medium text-gray-900">{selectedReport.client_name}</p>
                    </div>
                  )}
                  {selectedReport.mission_start_date && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs font-semibold text-gray-500 mb-1">Date de début</p>
                      <p className="text-sm font-medium text-gray-900">{new Date(selectedReport.mission_start_date).toLocaleDateString('fr-FR')}</p>
                    </div>
                  )}
                  {selectedReport.mission_end_date && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs font-semibold text-gray-500 mb-1">Date de fin</p>
                      <p className="text-sm font-medium text-gray-900">{new Date(selectedReport.mission_end_date).toLocaleDateString('fr-FR')}</p>
                    </div>
                  )}
                  {selectedReport.mission_initial_days != null && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs font-semibold text-gray-500 mb-1">Durée initiale</p>
                      <p className="text-sm font-medium text-gray-900">{selectedReport.mission_initial_days} jour{selectedReport.mission_initial_days > 1 ? 's' : ''}</p>
                    </div>
                  )}
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs font-semibold text-gray-500 mb-1">Durée réelle</p>
                    <p className="text-sm font-medium text-gray-900">{selectedReport.mission_real_days != null ? `${selectedReport.mission_real_days} jour${selectedReport.mission_real_days > 1 ? 's' : ''}` : 'Chantier non clôturé'}</p>
                  </div>
                  {selectedReport.duration_hours != null && selectedReport.duration_hours !== '' && (
                    <div className="bg-gray-50 rounded-xl p-3">
                      <p className="text-xs font-semibold text-gray-500 mb-1">Durée saisie</p>
                      <p className="text-sm font-medium text-gray-900">{selectedReport.duration_hours} h</p>
                    </div>
                  )}
                  {Array.isArray(selectedReport.team_members) && selectedReport.team_members.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-3 col-span-2">
                      <p className="text-xs font-semibold text-gray-500 mb-1">Équipe</p>
                      <div className="space-y-0.5">
                        {selectedReport.team_members.map((m, i) => (
                          <p key={i} className="text-sm font-medium text-gray-900">
                            {m.name} <span className="text-xs font-normal text-gray-500">({m.role})</span>
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedReport.client_address && (
                    <div className="bg-gray-50 rounded-xl p-3 col-span-2">
                      <p className="text-xs font-semibold text-gray-500 mb-1">Adresse</p>
                      <p className="text-sm font-medium text-gray-900">📍 {selectedReport.client_address}</p>
                    </div>
                  )}
                </div>

                {/* Travaux réalisés */}
                {selectedReport.works_performed && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Travaux réalisés</h3>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap bg-green-50 rounded-xl p-4">{selectedReport.works_performed}</p>
                  </div>
                )}

                {/* Matériaux utilisés */}
                {selectedReport.materials_used && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Matériaux utilisés</h3>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-xl p-4">{selectedReport.materials_used}</p>
                  </div>
                )}

                {/* Observations */}
                {selectedReport.observations && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Observations</h3>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-xl p-4">{selectedReport.observations}</p>
                  </div>
                )}

                {/* Problèmes rencontrés */}
                {selectedReport.issues_encountered && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">Problèmes rencontrés</h3>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap bg-amber-50 rounded-xl p-4">{selectedReport.issues_encountered}</p>
                  </div>
                )}

                {/* Photos avant */}
                {Array.isArray(selectedReport.photos_before_urls) && selectedReport.photos_before_urls.length > 0 && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">📷 Photos avant ({selectedReport.photos_before_urls.length})</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {selectedReport.photos_before_urls.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-100 hover:opacity-90">
                          <img src={url} alt={`Avant ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Photos après */}
                {Array.isArray(selectedReport.photos_after_urls) && selectedReport.photos_after_urls.length > 0 && (
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">📸 Photos après ({selectedReport.photos_after_urls.length})</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {selectedReport.photos_after_urls.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-100 hover:opacity-90">
                          <img src={url} alt={`Après ${i + 1}`} loading="lazy" className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Vue Paramètres Entreprise
  if (openFolder === 'settings') {
    return <CompanySettings onBack={() => setOpenFolder(null)} />;
  }

  // Vue Bureau (par défaut)
  const bureauSearchActive = searchTerm.trim().length > 0;
  const bureauMatchingProjects = bureauSearchActive
    ? projects.filter(p => {
        const s = searchTerm.toLowerCase();
        return p.name?.toLowerCase().includes(s) ||
          p.project_number?.toLowerCase().includes(s) ||
          p.description?.toLowerCase().includes(s);
      }).slice(0, 5)
    : [];
  const bureauMatchingSearches = bureauSearchActive
    ? searches.filter(s => {
        const q = searchTerm.toLowerCase();
        return s.location?.toLowerCase().includes(q) ||
          s.description?.toLowerCase().includes(q);
      }).slice(0, 5)
    : [];
  const bureauNoResults = bureauSearchActive && bureauMatchingProjects.length === 0 && bureauMatchingSearches.length === 0;

  return (
    <div className="min-h-screen p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Briefcase className="h-8 w-8" />
          Mon Entreprise
        </h1>
        <p className="text-gray-600 mt-1">Bureau de gestion</p>
      </div>

      {/* Barre de recherche */}
      <div className="mb-6 max-w-2xl mx-auto relative">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un projet ou une recherche..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        {bureauSearchActive && (
          <div className="absolute z-20 top-full mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
            {bureauNoResults ? (
              <p className="px-4 py-3 text-sm text-gray-400">Aucun résultat</p>
            ) : (
              <>
                {bureauMatchingProjects.map(project => (
                  <button
                    key={`p-${project.id}`}
                    onClick={() => { setSearchTerm(''); setOpenFolder('projects'); setSelectedProject(project); setShowProjectDetails(true); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 border-b border-gray-50"
                  >
                    <FolderOpen className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{project.name || 'Projet sans nom'}</p>
                      <p className="text-xs text-gray-400">Projet · {project.project_number}</p>
                    </div>
                  </button>
                ))}
                {bureauMatchingSearches.map(search => (
                  <button
                    key={`s-${search.id}`}
                    onClick={() => { setSearchTerm(''); setOpenFolder('searches'); setSelectedSearch(search); setIsDetailsModalOpen(true); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 border-b border-gray-50"
                  >
                    <Search className="h-4 w-4 text-orange-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{search.location || 'Recherche'}</p>
                      <p className="text-xs text-gray-400 truncate">Recherche · {search.description || '—'}</p>
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* Bureau - Dossiers */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Dossier Projets */}
        <button
          onClick={() => setOpenFolder('projects')}
          className="bg-white rounded-xl border-2 border-gray-200 hover:border-blue-500 p-8 transition-all hover:shadow-xl group text-left"
        >
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-4">
              <FolderOpen className="h-24 w-24 text-blue-500 group-hover:scale-110 transition-transform" />
              <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs font-bold rounded-full w-8 h-8 flex items-center justify-center">
                {projects.length}
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">📁 Projets</h3>
            <p className="text-sm text-gray-600">Tous vos projets en cours</p>
          </div>
        </button>

        {/* Dossier Recherches Techniciens */}
        <button
          onClick={() => setOpenFolder('searches')}
          className="bg-white rounded-xl border-2 border-gray-200 hover:border-orange-500 p-8 transition-all hover:shadow-xl group text-left"
        >
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-4">
              <Search className="h-24 w-24 text-orange-500 group-hover:scale-110 transition-transform" />
              <div className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs font-bold rounded-full w-8 h-8 flex items-center justify-center">
                {searches.length}
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">🔍 Recherches</h3>
            <p className="text-sm text-gray-600">Recherches partagées</p>
          </div>
        </button>

        {/* Dossier Comptes-rendus chantier */}
        <button
          onClick={() => setOpenFolder('reports')}
          className="bg-white rounded-xl border-2 border-gray-200 hover:border-green-500 p-8 transition-all hover:shadow-xl group text-left"
        >
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-4">
              <FileText className="h-24 w-24 text-green-500 group-hover:scale-110 transition-transform" />
              <div className="absolute -top-1 -right-1 bg-green-500 text-white text-xs font-bold rounded-full w-8 h-8 flex items-center justify-center">
                {reports.length}
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">📋 Comptes-rendus</h3>
            <p className="text-sm text-gray-600">Comptes-rendus de chantier</p>
          </div>
        </button>

        {/* Bouton Paramètres Entreprise */}
        <button
          onClick={() => setOpenFolder('settings')}
          className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl border-2 border-indigo-500 p-8 transition-all hover:shadow-xl group text-left"
        >
          <div className="flex flex-col items-center text-center">
            <Settings className="h-24 w-24 text-white mb-4 group-hover:scale-110 group-hover:rotate-90 transition-all" />
            <h3 className="text-xl font-bold text-white mb-2">⚙️ Paramètres</h3>
            <p className="text-sm text-indigo-100">Informations légales</p>
          </div>
        </button>
      </div>

      {/* Modal Création Devis */}
      {showQuoteModal && selectedProjectForQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="h-6 w-6" />
                Créer un Devis - {selectedProjectForQuote.name}
              </h2>
              <button
                onClick={() => setShowQuoteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Informations du projet */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Projet
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-blue-700">N°:</span>
                    <span className="ml-2 font-medium">{selectedProjectForQuote.project_number}</span>
                  </div>
                  <div>
                    <span className="text-blue-700">Client:</span>
                    <span className="ml-2 font-medium">
                      {clients.find(c => c.id === selectedProjectForQuote.client_id)?.nom || '-'}
                    </span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-blue-700">Montant estimé:</span>
                    <span className="ml-2 font-medium">
                      {selectedProjectForQuote.estimated_value ? `${parseFloat(selectedProjectForQuote.estimated_value).toLocaleString('fr-FR')}€` : 'Non défini'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Formulaire devis */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Titre du devis */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre du devis <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Devis plomberie cuisine"
                    value={newQuote.title}
                    onChange={(e) => setNewQuote({...newQuote, title: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Montant HT */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Montant HT <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      placeholder="0.00"
                      value={newQuote.amount_ht}
                      onChange={(e) => setNewQuote({...newQuote, amount_ht: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <span className="absolute right-3 top-2.5 text-gray-500">€</span>
                  </div>
                </div>

                {/* TVA */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    TVA (%)
                  </label>
                  <select 
                    value={newQuote.tva}
                    onChange={(e) => setNewQuote({...newQuote, tva: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="20">20%</option>
                    <option value="10">10%</option>
                    <option value="5.5">5.5%</option>
                    <option value="0">0%</option>
                  </select>
                </div>

                {/* Date de validité */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Valable jusqu'au
                  </label>
                  <input
                    type="date"
                    value={newQuote.valid_until}
                    onChange={(e) => setNewQuote({...newQuote, valid_until: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Échéance paiement */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Échéance paiement
                    <span className="ml-1 text-xs text-gray-400">(date limite de règlement)</span>
                  </label>
                  <input
                    type="date"
                    value={newQuote.due_date || ''}
                    onChange={(e) => setNewQuote({...newQuote, due_date: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>

                {/* Délai de réalisation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Délai de réalisation
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: 2 semaines"
                    value={newQuote.deadline}
                    onChange={(e) => setNewQuote({...newQuote, deadline: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Description */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description des travaux
                  </label>
                  <textarea
                    rows="4"
                    placeholder="Décrivez les travaux à réaliser..."
                    value={newQuote.description}
                    onChange={(e) => setNewQuote({...newQuote, description: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Conditions */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Conditions particulières
                  </label>
                  <textarea
                    rows="3"
                    placeholder="Conditions de paiement, garanties, etc."
                    value={newQuote.conditions}
                    onChange={(e) => setNewQuote({...newQuote, conditions: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="p-6 border-t border-gray-200 flex gap-3 justify-end sticky bottom-0 bg-white">
              <button
                onClick={() => setShowQuoteModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateQuoteSubmit}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                <Check className="h-5 w-5" />
                Créer le devis
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Page d'acceptation d'invitation
const AcceptInvitationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    
    if (!token) {
      setError('Token d\'invitation manquant');
      setLoading(false);
      return;
    }

    // Vérifier le token et récupérer les infos d'invitation
    http.get(`${API}/invitations/${token}/validate`)
      .then(response => {
        setInvitation(response.data);
        setFormData(prev => ({ ...prev, email: response.data.email }));
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.detail || 'Invitation invalide ou expirée');
        setLoading(false);
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.email.toLowerCase() !== formData.confirmEmail.toLowerCase()) {
      setError('Les adresses email ne correspondent pas');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    const passwordError = validatePasswordStrength(formData.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');

      // Créer le compte et accepter l'invitation avec le nouvel endpoint
      const response = await http.post(`${API}/invitations/${invitation.id}/accept-and-register`, {
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName
      });

      // Connexion automatique
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        recordUserActivity(response.data.user?.id);
        window.location.href = '/bureau';
      } else {
        setError('Compte créé mais impossible de se connecter automatiquement');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'acceptation de l\'invitation');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Vérification de l'invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Invitation invalide</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => window.location.href = '/'} className="w-full">
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-6">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center pb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Mail className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold">Accepter l'invitation</CardTitle>
          <CardDescription className="mt-2">
            Vous êtes invité à rejoindre <strong>{invitation?.company_name}</strong> en tant que <strong>{invitation?.role}</strong>
          </CardDescription>
          {invitation?.invited_by && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-gray-700">
                <span className="font-medium">Invité par :</span> {invitation.invited_by}
              </p>
            </div>
          )}
        </CardHeader>

        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <Input
                type="email"
                name="invitation-email-display"
                autoComplete="off"
                value={formData.email}
                disabled
                className="bg-gray-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmer votre email
              </label>
              <Input
                type="email"
                name="invitation-confirm-email"
                autoComplete="off"
                value={formData.confirmEmail}
                onChange={(e) => setFormData({ ...formData, confirmEmail: e.target.value })}
                placeholder="Confirmez votre adresse email"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prénom
                </label>
                <Input
                  type="text"
                  name="invitation-firstname"
                  autoComplete="given-name"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  placeholder="Votre prénom"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom
                </label>
                <Input
                  type="text"
                  name="invitation-lastname"
                  autoComplete="family-name"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  placeholder="Votre nom"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mot de passe
              </label>
              <Input
                type="password"
                name="invitation-new-password"
                autoComplete="new-password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={`Minimum ${PASSWORD_MIN_LENGTH} caractères`}
                required
                minLength={PASSWORD_MIN_LENGTH}
              />
              <PasswordStrengthMeter password={formData.password} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirmer le mot de passe
              </label>
              <Input
                type="password"
                name="invitation-confirm-password"
                autoComplete="new-password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Confirmez votre mot de passe"
                required
                minLength={PASSWORD_MIN_LENGTH}
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Création du compte...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Accepter et créer mon compte
                </span>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Main App Component
function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const founderEmail = FOUNDER_EMAIL;
  const isFounder = (user?.is_fondateur === true) || ((user?.email || '').toLowerCase() === founderEmail);
  const isAdmin = isFounder || (user?.role === 'ADMIN');

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (savedToken && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        // Règle métier SkyApp : ne jamais restaurer une session dont la dernière
        // activité persistée dépasse SESSION_IDLE_TIMEOUT_MS (ex. Chrome fermé
        // puis rouvert >10 min plus tard). Vérifié AVANT de rendre les routes
        // protégées pour éviter tout flash de contenu.
        const expired = isSessionExpired(userData.id);
        if (expired) {
          clearAuthStorage();
          window.location.replace('/?session=inactivity');
          return;
        }
        setToken(savedToken);
        setUser(userData);
        // Reprise valide (page rouverte avant expiration) : prolonge la fenêtre
        // depuis maintenant plutôt que de garder l'ancien timestamp figé.
        recordUserActivity(userData.id);
      } catch (error) {
        localStorage.clear();
      }
    }
    setLoading(false);
  }, []);

  // Déconnexion automatique après 10 minutes d'inactivité
  useInactivityTimeout({
    isActive: !!user,
    userId: user?.id,
    timeoutMs: SESSION_IDLE_TIMEOUT_MS,
    onTimeout: () => {
      broadcastLogout('inactivity');
      forceLogout('inactivity');
    }
  });

  // Synchronisation multi-onglet : écoute les logouts déclenchés par d'autres onglets
  useEffect(() => {
    const cleanup = initAuthSync({
      onLogout: (reason) => {
        setUser(null);
        setToken(null);
        if (window.location.pathname !== '/') {
          // Param différencié pour afficher le bon message dans le banner
          const param = reason === 'inactivity' ? 'inactivity' : reason === 'account_deactivated' ? 'deactivated' : 'expired';
          window.location.href = `/?session=${param}`;
        }
      }
    });
    return cleanup;
  }, []);

  // Watchdog : surveiller la disparition du token (suppression manuelle, etc.)
  useEffect(() => {
    const checkToken = setInterval(() => {
      const currentToken = localStorage.getItem('token');
      if (!currentToken && user) {
        // Token disparu alors que l'app croit l'utilisateur connecté
        clearInterval(checkToken);
        setUser(null);
        setToken(null);
        broadcastLogout('token_missing');
        forceLogout('token_missing');
        if (window.location.pathname !== '/') {
          window.location.href = '/?session=expired';
        }
      }
    }, 2000);
    return () => clearInterval(checkToken);
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <AuthContext.Provider value={{ user, token }}>
        <BrowserRouter>
          <div className="App" translate="no">
            {/* Cosmic Animated Background */}
            <CosmicBackground />
            {/* Toast notifications (remplace alert() bloquant) */}
            <SonnerToaster richColors position="top-center" />
            
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/verifier-email" element={<VerifyEmailPage />} />
              <Route path="/auth/confirmation" element={<EmailConfirmedPage />} />
              <Route path="/changelog" element={<ChangelogPage />} />
              <Route path="/mentions-legales" element={<LegalPage />} />
              <Route path="/cgu" element={<LegalPage />} />
              <Route path="/cgv" element={<LegalPage />} />
              <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
              <Route 
                path="/role-selection" 
                element={user ? <RoleSelection /> : <Navigate to="/" />} 
              />
              <Route 
                path="/technicien" 
                element={user ? <TechnicienLayout /> : <Navigate to="/" />} 
              />
              <Route 
                path="/bureau/*" 
                element={user ? ((isAdmin || user?.role === 'BUREAU') ? <BureauLayout /> : <Navigate to="/role-selection" />) : <Navigate to="/" />} 
              />
              {/* Redirection par défaut vers /bureau/projets */}
              <Route 
                path="/bureau" 
                element={<Navigate to="/bureau/projets" replace />} 
              />
              <Route 
                path="/statistiques" 
                element={user ? (isAdmin ? <StatisticsLayout /> : <Navigate to="/role-selection" />) : <Navigate to="/" />} 
              />
              {/* Route fondateur retirée à la demande */}
              <Route 
                path="/admin" 
                element={user ? (isFounder ? <SupremeAdminLayout /> : <Navigate to="/role-selection" />) : <Navigate to="/" />} 
              />
            </Routes>

            {/* AI Orb - Orbe Blanche et Noire toujours active */}
            {user && <AIOrb />}
          </div>
        </BrowserRouter>
      </AuthContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
